import { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "./supabase";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import Landing from './LandingPage';
import { AppleHealthSection, PreventiveCareChecklist, HealthAuthNotice } from './components/Health';
import resetViewport from './viewportReset';
import { syncScreenTime, armScreenTime, disarmScreenTime, setupScreenTime, screenTimeStatus, setupUsageDisplay, rearmUsage, reconcileScreenTimeOwner, claimScreenTime } from './services/screenTime';
import { reconstructScreenTimeUsage, formatUsageMinutes, capCrossPlausible } from './lib/screenTimeUsage';
import { App as CapacitorApp } from '@capacitor/app';
import { GOAL_TEMPLATES, ONBOARDING_GOAL_TEMPLATES, TEMPLATE_GROUPS, inferGoalCategory, stHoursToMinutes, stMinutesToHours, customGoalBinding, CUSTOM_GOAL_UNITS, SPEND_CATEGORY_FILTERS } from './lib/goalTemplates';
import { reportError, stripQuery, addBreadcrumb, reportMessage } from './sentry';
// Same module api/notify-cron.js and api/coach.js use, so the Finance tab and Kai
// can never disagree about when a bill is due or whether it is paid.
import { billDaysUntil, localToday, currentMonth, isPaidThisMonth, annotateBills, billsNeedingAction, autopayEditFields, autopayStatusLine } from './lib/bills';
import { calcPillarScores, resolveEnrolled, defaultTrackers, PILLARS, TRACKERS, TRACKER_IDS, cushionBalance, habitTarget28, habitLogs28, HABIT_WINDOW_DAYS } from './lib/scoring';
import { normalizeInflowName, isCountableInflow, countableDeposits, classifyIncomeCadence, resolveIncomeType, irregularIncomeSummary, scheduledPaydays, createIncomeTypeSaver, readIncomeTypeOverride, paySetupDone, payLine, payScheduleChanged } from './lib/income';
import { fmtUSD, fmtUSDExact, fmtUSDCompact, sumMoney, SEVEN_EQUAL_COLUMNS } from './lib/money';
import { BALANCE_FRESH_WINDOW_MS } from './lib/freshness';
import { normalizeClientState, buildKaiContext, PATTERN_HONESTY_RULE } from './lib/kaiContext';
import { missedTodayHits } from './lib/habitTodayClaims';
import { activityClaimHits } from './lib/activityClaims';
import { WORKOUT_TYPES, shouldAskWorkoutDetails, buildWorkoutRow, habitTickDateForWorkout, workoutMeta, isWorkoutHabit, workoutSheetFor, workoutWritesForConfirmed } from './lib/workoutLog';
import { applyHabitTick } from './lib/habitTick';
import { habitNoteFor, setHabitNote, HABIT_NOTE_MAX } from './lib/habitNotes';
import { briefUpdatedSince } from './lib/briefStaleness';
import { buildWeekPlan, planToCommitments, weekdayIdx, WEEKDAY_SHORT } from './lib/weekPlan';
import { takeUpNext, briefHeadline, dayPadParts, upNextGroups } from './lib/homeLayout';
import { computeOneThing } from './lib/oneThing';
import { ageFromBirthYear, ageRangeOf, birthYearOptions, isValidBirthYear } from './lib/birthYear';
import { pillarIdForTab } from './lib/collapsingHeader';
import { settlePillars, relativeTime, snapshotPillar, ALL_INPUTS } from './lib/pillarReadiness';
import { useCollapsingHero } from './hooks/useCollapsingHero';
import { subscriptionsPlacement, SUBSCRIPTIONS_CARD_ID } from './lib/financeLayout';
import { billsSummary, billsDefaultOpen, subscriptionsSummary } from './lib/billsSummary';
import { detectRecurringCharges, recurringCandidates, subscriptionsMonthlyTotal, billFromPattern, billsToAutoMarkPaid, ordinal, isSubscriptionBill, billFromManual, removalRuleFor, subscriptionRow, possibleSubscriptions, billFromPossible, hiddenSubscriptions, billFromHidden, suggestMerchants, dismissPossibleRule, restoreAddsBill } from './lib/subscriptions';
import { localWeekStart } from './lib/weekWindow';
import { linkWorkoutGoal, linkWorkoutGoals, workoutGoalTarget, isWorkoutCountGoal } from './lib/goalLink';
import { countWorkoutSessions, gymHistoryOf } from './lib/workoutSessions';
import { createMoodSaver, localDayBoundsIso, moodLocalDate } from './lib/moodSave';
import { goalAdherence, budgetAdherence } from './lib/goalAdherence';
import { txLocalDay, txDayWord, txMatchesSpendGoal } from './lib/spend';
import { spendCategoryLabel } from './lib/spendLabels';
import { T, withAlpha, G, RADII, REDUCED_MOTION } from './theme';
import { commitLabel, fmt12h, fmtDueDay, textAddsDetail, statusMeta } from './lib/commitmentDisplay';
import Paywall from './Paywall';
import { shouldShowPaywall, subscriptionStatusLabel, paywallPresentation, makeRequirePro, PREVIEW_BANNER_COPY, needsCancelSubscriptionStep, CANCEL_FIRST_TITLE, CANCEL_FIRST_BODY, CANCEL_FIRST_PRIMARY, CANCEL_FIRST_CONTINUE, CANCEL_FIRST_SKIP, SUBSCRIPTION_STILL_ACTIVE_LINE } from './lib/paywall';
import { DEMO_PROFILES, demoDeltaDays, shiftDemoValue, normalizeDemoProfile } from './lib/demoProfiles';
import { applyPushIdentity } from './lib/pushIdentity';
import { showManageSubscriptions, logOutPurchases } from './lib/purchases';
import { greetingFor, hourInZone, isFirstRunHome, welcomeBrief, gettingStartedSteps, gettingStartedVisible } from './lib/firstRun';
import { LockSetup, PrivacyCover, announceLockChanged } from './components/AppLock';
import { isLockCapable, readLockState, resolveLockStateForUser, disableLock, markPrompted, clearLockState, LOCK_PASSED_EVENT } from './lib/appLock';
import { PENDING_ACTION_EVENT, readPendingAction, clearPendingAction, resolvePendingAction, PENDING_BANK_RECONNECT_KEY, BANK_RECONNECT_EVENT } from './lib/pushActions';
import { HABIT_TEMPLATES } from './lib/habitTemplates';
import { balanceAsOfLabel, BALANCE_SNAPSHOT_TOAST } from './lib/balanceAsOf';
import { bankSyncedAt, bankAttentionLine, BANK_ATTENTION_TITLE, BANK_ATTENTION_HINT } from './lib/plaidItemHealth';
import { containsCrisisLanguage } from './lib/crisisDetect';
import { BarChart, Bar as RBar, Cell, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie } from "recharts";

// Palette moved to src/theme.js so App.js, index.js and WaitlistPage can share
// it — one THEMES object, zero restated copies. See CLAUDE.md → Design tokens.

// Colour maps to the band, but colour is NEVER the only signal: every indicator
// below always renders its percentage, or a literal "—" when the pillar has no
// enrolled tracker. Thresholds: green >=70, amber 40-69, red <40.
const bandColor = (band) => band === 'good' ? T.success : band === 'fair' ? T.accent : band === 'poor' ? T.danger : T.textDim;
// placeholder (pillarReadiness): no complete score yet → "··", distinct from
// "—" (not tracked). Never 0%.
const pctText = (pct, placeholder = false) => placeholder ? "··" : pct === null ? "\u2014" : `${pct}%`;

// Compact four-up indicator used in the app header.
const PillarBar = ({ pillars, compact }) => (
  <div style={{display:"flex",gap:compact?8:12,alignItems:"center"}}>
    {pillars.map(p=>(
      <div key={p.id}
           data-testid={`pillar-${p.id}`}
           title={`${p.label}: ${p.pct===null?"not tracked":p.pct+"%"} (${p.trackedCount} of ${p.totalCount} trackers)`}
           style={{textAlign:"center",lineHeight:1.1,minWidth:compact?26:32}}>
        <div data-testid={`pillar-${p.id}-value`} style={{fontSize:compact?12:14,fontWeight:700,color:bandColor(p.band),fontFamily:"'DM Sans',sans-serif"}}>{pctText(p.pct, p.placeholder)}</div>
        <div data-testid={`pillar-${p.id}-label`} style={{fontSize:8,color:T.textMuted,letterSpacing:"0.05em",textTransform:"uppercase",fontWeight:600}}>{p.short}</div>
      </div>
    ))}
  </div>
);

// Compact "<Pillar> · <score>%" with a small ring: the top bar while a pillar
// tab's hero is scrolled past (useCollapsingHero). Same values as the hero.
const PillarPill = ({ pillar, onClick }) => {
  if (!pillar) return null;
  const r = 9, c = 2 * Math.PI * r;
  const dash = pillar.pct === null ? 0 : c * Math.min(100, Math.max(0, pillar.pct)) / 100;
  return (
    <button type="button" onClick={onClick} aria-label={`Back to top, ${pillar.label} ${pctText(pillar.pct, pillar.placeholder)}`} data-testid={`pillar-pill-${pillar.id}`}
      style={{display:"flex",alignItems:"center",gap:7,height:36,background:"transparent",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit"}}>
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r={r} fill="none" stroke={G.ringTrack} strokeWidth="3"/>
        {dash > 0 && <circle cx="12" cy="12" r={r} fill="none" stroke={bandColor(pillar.band)} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${dash} ${c + 4}`} transform="rotate(-90 12 12)"/>}
      </svg>
      <span style={{fontSize:13,fontWeight:700,color:T.text,whiteSpace:"nowrap"}}>{pillar.label} · <span style={{color:bandColor(pillar.band)}}>{pctText(pillar.pct, pillar.placeholder)}</span></span>
    </button>
  );
};

// ── PILLAR RINGS (redesign Phase 1, docs/REDESIGN.md) ────────────────────────
// Concentric Activity-style rings, one per pillar, geometry and colors from
// docs/redesign/scorify-redesign/chosen/HomeActivity. PRESENTATION ONLY: every
// value renders from the same pillars array calcPillarScores already produces
// (pct/band/trackedCount/totalCount via pctText) — see the parity table in
// docs/REDESIGN.md §3. A pct===null pillar draws an empty track and reads "—",
// never a zero-length arc labeled 0 (the enrollment honesty rule).
const RING_RADII = [128, 104, 80, 56];               // outer→inner, viewBox 300
const RING_CIRC = RING_RADII.map(r => 2 * Math.PI * r); // ≈ [804, 653, 503, 352]
const RING_CHIP_POS = [
  { left: 0, top: 0 }, { right: 0, top: 0 },
  { left: 0, bottom: 0 }, { right: 0, bottom: 0 },
];
const PillarRings = ({ pillars, selected, onSelect, onStart }) => {
  const sel = pillars[selected] || pillars[0];
  const selValueColor = sel?.band === 'poor' ? G.lowValue : T.text;
  // Welcome state in the center until at least one pillar has a real score.
  // A pillar still loading (placeholder, pillarReadiness) counts as present: "··" in the
  // center, never the Welcome / Not started copy, which is for trackers never turned on.
  const anyScored = (pillars || []).some((p) => p && (p.placeholder || (p.pct !== null && p.pct !== undefined)));
  return (
    <div style={{ position: "relative", margin: "6px auto 0", width: 342, height: 330, maxWidth: "100%" }}>
      {/* Soft halo so the rings blend into the ambient background, no card box */}
      <div aria-hidden="true" style={{ position: "absolute", left: 21, top: 15, width: 300, height: 300, borderRadius: "50%", background: G.ringHalo, pointerEvents: "none" }} />
      <div style={{ position: "absolute", left: 31, top: 25, width: 280, height: 280 }}>
        <svg width="280" height="280" viewBox="0 0 300 300" style={{ position: "absolute", left: 0, top: 0 }}>
          {pillars.map((p, i) => {
            const C0 = RING_CIRC[i];
            const dash = p.pct === null ? 0 : C0 * Math.min(100, Math.max(0, p.pct)) / 100;
            return (
              <g key={p.id}>
                <circle cx="150" cy="150" r={RING_RADII[i]} fill="none" stroke={G.ringTrack} strokeWidth="20" />
                {dash > 0 && (
                  <circle cx="150" cy="150" r={RING_RADII[i]} fill="none" stroke={G.ringColors[i]} strokeWidth="20"
                    strokeLinecap="round" strokeDasharray={`${dash} ${C0 + 50}`} transform="rotate(-90 150 150)"
                    opacity={selected === i ? 1 : 0.35}
                    style={REDUCED_MOTION ? undefined : { transition: "stroke-dasharray 0.6s ease, opacity 0.2s ease" }} />
                )}
                {/* Transparent wide hit ring ≈44pt arc, same pattern as the mockup */}
                <circle cx="150" cy="150" r={RING_RADII[i]} fill="none" stroke="transparent" strokeWidth="22"
                  style={{ cursor: "pointer", pointerEvents: "stroke" }} onClick={() => onSelect(i)} />
              </g>
            );
          })}
        </svg>
        <div style={{ position: "absolute", left: 95, top: 95, width: 90, height: 90, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none", textAlign: "center" }}>
          {anyScored ? (
            <>
              <span data-testid="ring-center-value" style={{ fontFamily: "'DM Serif Display',serif", fontSize: 38, lineHeight: 1, color: selValueColor }}>{pctText(sel?.pct ?? null, !!sel?.placeholder)}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{sel?.short}</span>
            </>
          ) : (
            <>
              <span data-testid="ring-center-value" style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, lineHeight: 1.1, color: T.text }}>Welcome</span>
              <span style={{ fontSize: 10, color: T.textMuted, marginTop: 3 }}>Tap a ring to start</span>
            </>
          )}
        </div>
      </div>
      {pillars.map((p, i) => {
        const isSel = selected === i;
        const started = !!p.placeholder || (p.pct !== null && p.pct !== undefined); // loading reads as started ("··")
        // Not started: one tap goes straight to that pillar's tab to begin (onStart).
        // Started: tap selects the ring to read its value, as before.
        return (
          <button key={p.id} type="button" onClick={() => (started || !onStart) ? onSelect(i) : onStart(p.id)}
            aria-label={started ? `Show ${p.short}` : `Start ${p.short}`} aria-pressed={isSel}
            data-testid={`pillar-${p.id}`}
            style={{
              position: "absolute", ...RING_CHIP_POS[i], width: 88, minHeight: 52, borderRadius: RADII.chip,
              border: G.hairline,
              background: isSel ? G.chipSelectedSurface : G.chipSurface,
              backdropFilter: G.blurChip, WebkitBackdropFilter: G.blurChip,
              boxShadow: isSel ? G.chipSelectedShadow : G.chipShadow,
              fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 1, cursor: "pointer", color: T.text, padding: 0,
            }}>
            <span data-testid={`pillar-${p.id}-label`} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600 }}>
              <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: G.ringColors[i] }} />
              {p.short}
            </span>
            {started ? (
              <span data-testid={`pillar-${p.id}-value`} style={{ fontFamily: "'DM Serif Display',serif", fontSize: 17, lineHeight: 1, color: p.band === 'poor' ? G.lowValue : T.text }}>{pctText(p.pct, p.placeholder)}</span>
            ) : (
              <span data-testid={`pillar-${p.id}-value`} style={{ fontSize: 10, fontWeight: 600, color: T.accent, lineHeight: 1 }}>Not started</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// One row per bucket. A tracker the user has not enrolled in renders "—" with a
// reason, visually distinct from an enrolled bucket sitting at zero.
const BucketRows = ({ buckets }) => (
  <div style={{display:"flex",flexDirection:"column",gap:8}}>
    {buckets.map(b=>{
      const off = b.state !== 'scored';
      return (
        <div key={b.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 14px",borderRadius:10,
                                background:off?T.overlayLight(0.02):T.overlayLight(0.05),
                                border:off?`1px dashed ${T.overlayLight(0.08)}`:"1px solid transparent"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,color:off?T.textMuted:T.text}}>{b.label}</div>
            {b.detail&&<div style={{fontSize:11,color:T.textDim,marginTop:2}}>{b.detail}</div>}
          </div>
          <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
            {off
              ? <div style={{fontSize:15,fontWeight:700,color:T.textDim}} title="Not tracked">{"\u2014"}</div>
              : <div style={{fontSize:16,fontWeight:800,color:b.earned>0?T.accent:T.textMuted}}>
                  {Math.round(b.earned)}<span style={{fontSize:10,color:T.textMuted,fontWeight:400}}> / {b.max}</span>
                </div>}
          </div>
        </div>
      );
    })}
  </div>
);

// PillarHeader was removed with redesign Phase 5: all three pillar-tab
// banners became the hero (ring + serif title) pattern, reading the same
// pillarById fields the old component read.

// Module-level native check (established idiom — LandingPage's isNativeApp,
// the inline checks below): the Capacitor bridge exists from first paint on
// iOS, so a module const cannot go stale.
const native = typeof window !== "undefined" && typeof window.Capacitor !== "undefined" && window.Capacitor.isNativePlatform();
// Screen Time status for a profiles.is_demo account: ready + tracking, so the
// Health card and the screen_time goal render the seeded screen_time_days rows.
// Display only — nothing reads it as a reason to arm, sync or report.
const DEMO_ST_STATUS = Object.freeze({ available: true, authorization: "approved", hasSelection: true, usageEnabled: true, hasUsageSelection: true, armedToday: true, usageArmedToday: true, needsRearm: false, rearmsToday: 0, demo: true });

const apiUrl = (path) => native ? "https://www.scorify.to" + path : path;
// Attaches the Supabase session token once, here, rather than at each of the 18
// call sites — plaid.js, coach.js and memory.js all need the same
// header as they get their own auth checks.
// Fails soft on purpose: /api/waitlist is legitimately anonymous, and demo mode
// has no session. The server-side 401 is what enforces auth, not this.
// Session recovery for authenticated /api/* calls. An access token can be stale
// (near expiry) or revoked; when a request that CARRIED a token comes back 401,
// the honest options are refresh-and-retry or hand off to sign-in — never
// silently degrade to "not tracked" (SCORIFY-APP-2). Shared across concurrent
// callers so a burst of 401s triggers ONE refresh, and one handoff at most.
let _refreshInFlight = null;
let _sessionHandoffDone = false;
async function refreshAccessTokenOnce() {
  if (!_refreshInFlight) {
    _refreshInFlight = supabase.auth.refreshSession()
      .then(({ data, error }) => (!error && data?.session?.access_token) ? data.session.access_token : null)
      .catch(() => null)
      .finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
}
async function handleSessionExpired() {
  if (_sessionHandoffDone) return;
  _sessionHandoffDone = true;
  // This is a SIGN-OUT path: purge user-scoped storage (the cache-first snapshot
  // included). The app-lock PIN is NOT cleared here — it is stored per user (keyed
  // by uid) and survives sign-out so the same user reuses it on return; a different
  // user gets their own. Purge FIRST — it sweeps scorify_* keys, and the expired
  // flag below must survive it.
  try { purgeUserStorage(); } catch {}
  // A flag the sign-in screen reads to explain why the user landed there.
  try { localStorage.setItem('scorify_session_expired', '1'); } catch {}
  // Local sign-out fires App.js's onAuthStateChange (null session), which routes
  // the tree to /login. scope 'local' so recovering THIS device never touches
  // the account's other devices.
  try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
}

// Time to FIRST BYTE (response headers), not to body end: the timer is cleared
// the moment fetch resolves, so a long body (the 125 KB Plaid cache, a coach
// reply) is never cut off mid-stream. Without any bound, a request that never
// answered left its caller's "fetching" flag true for the life of the page:
// plaidFetching stuck → permanent Money skeleton (2026-10-15). Callers with a
// slow first byte by design (coach/claude generation) pass a longer
// `timeoutMs`; a caller-supplied `signal` still aborts as before.
const API_TIMEOUT_MS = 20000;
const apiFetch = async (url, options = {}) => {
  const { timeoutMs = API_TIMEOUT_MS, signal: callerSignal, ...fetchOptions } = options;
  const fetchWithTimeout = async (init) => {
    const ctrl = new AbortController();
    let timedOut = false;
    const onCallerAbort = () => ctrl.abort(callerSignal?.reason);
    if (callerSignal) {
      if (callerSignal.aborted) onCallerAbort();
      else callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
    const timer = setTimeout(() => { timedOut = true; ctrl.abort(); }, timeoutMs);
    try {
      return await fetch(url, { ...init, signal: ctrl.signal });
    } catch (e) {
      if (timedOut) { const err = new Error(`API timeout after ${timeoutMs}ms`); err.name = "TimeoutError"; err.timeoutMs = timeoutMs; throw err; }
      throw e;
    } finally {
      clearTimeout(timer);
      callerSignal?.removeEventListener("abort", onCallerAbort);
    }
  };
  const headers = { ...(fetchOptions.headers || {}) };
  let hadSession = !!(headers.Authorization || headers.authorization);
  if (!hadSession) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
        hadSession = true;
        // A valid session again — re-arm the one-shot handoff for any future
        // expiry in this JS lifetime (the tree remounts to /login on handoff).
        _sessionHandoffDone = false;
      }
    } catch { /* no session — send unauthenticated and let the server decide */ }
  }
  // Path only, host and query stripped: this is what gets tagged on the Sentry
  // event. The BODY is never read or reported — /api/coach, /api/claude and
  // /api/plaid bodies carry coach messages, journal text and bank data, and
  // reading the response here would also consume the stream the caller needs.
  const endpoint = stripQuery(url).replace(/^https?:\/\/[^/]+/, "");
  try {
    let res = await fetchWithTimeout({ redirect: 'follow', ...fetchOptions, headers });
    // AUTHENTICATED 401 → the token is stale or revoked. Refresh once and retry;
    // if it still 401s while online, the session is genuinely gone → hand off to
    // sign-in. This is what stops a dead session from reading as "not tracked"
    // (SCORIFY-APP-2). An UNauthenticated 401 (anonymous /api/waitlist, demo
    // mode) is expected and left exactly as before. Bodies here are strings, so
    // the request is safe to replay.
    if (res.status === 401 && hadSession) {
      const fresh = await refreshAccessTokenOnce();
      if (fresh) {
        headers.Authorization = `Bearer ${fresh}`;
        res = await fetchWithTimeout({ redirect: 'follow', ...fetchOptions, headers });
      }
      // Still 401 after a refresh attempt, and we are demonstrably online (the
      // server just answered 401) — the session cannot be recovered. Offline is
      // NOT this case: a lost connection throws below, it does not return 401.
      if (res.status === 401 && navigator.onLine !== false) {
        await handleSessionExpired();
      }
    }
    // 401/403 are EXPECTED here for the anonymous/demo paths above, and the
    // server-side 401 is what enforces auth. Reporting them would bury the
    // real failures in noise. 429 is our OWN rate limiter answering by design:
    // every call site renders a friendly wait message (apiErrorMessage,
    // plaidRateLimited), so reporting it just filed working throttles as
    // errors (Sentry SCORIFY-APP-3, 21 events of designed behavior).
    // 402 subscription_required (coach.js / claude.js): the paywall is the
    // answer, not an error. apiFetch is module scope, so it tells the
    // dashboard through a window event (listener next to fetchSubscription).
    if (res.status === 402) {
      try { window.dispatchEvent(new CustomEvent("scorify:subscription-required")); } catch {}
    }
    if (!res.ok && res.status !== 401 && res.status !== 402 && res.status !== 403 && res.status !== 429) {
      reportError(new Error(`API ${res.status} ${endpoint}`), {
        op: "api.response", endpoint, status: res.status,
      });
    }
    return res;
  } catch (e) {
    // Network-level failure: DNS, offline, TLS, CORS — or our own first-byte
    // timeout (tagged separately so a slow server is not filed as "offline").
    // A caller-initiated abort is not a failure and is not reported.
    if (e?.name === "AbortError" && callerSignal?.aborted) throw e;
    reportError(e, { op: e?.name === "TimeoutError" ? "api.timeout" : "api.network", endpoint, timeout_ms: e?.name === "TimeoutError" ? e.timeoutMs : undefined });
    throw e;
  }
};

// Awaits a batch of background data-healing writes and reports any that failed.
//
// These repairs run during load, inside .map() callbacks that build state. They
// used to be fired and forgotten: `supabase.from('habits').update(...)` with no
// await and no .catch. That has three consequences, in increasing severity —
// the error is discarded; a network-level rejection becomes an unhandled
// promise rejection; and, worst, the write races the reads that follow it in
// loadData, so a heal can land AFTER a later read has already been taken and
// silently disappear on the next load, which presents as a UI bug rather than
// as a failed write.
//
// A failed heal is NOT surfaced to the user: these are idempotent repairs that
// simply re-run on the next load, not user actions, so the save toast would be
// noise. But they are logged, and they are settled before loadData continues.
const settleHeals = async (label, promises) => {
  if (promises.length === 0) return;
  const results = await Promise.allSettled(promises);
  results.forEach((r) => {
    if (r.status === "rejected") {
      console.error(`${label}: heal write threw —`, r.reason?.message || r.reason);
      reportError(r.reason, { op: "heal.threw", label, level: "warning" });
    } else if (r.value?.error) {
      console.error(`${label}: heal write rejected —`, r.value.error.message);
      reportError(new Error(r.value.error.message), { op: "heal.rejected", label, level: "warning" });
    }
  });
};



// Optional Health trackers: off by default, surfaced via the "Add a tracker" card.
// When off, the section is hidden, no data is collected, and Kai/briefing skip it.
const OPTIONAL_HEALTH_TRACKERS = ["medications", "preventive_care"];
const OPTIONAL_TRACKER_META = {
  medications: { label: "Medications", desc: "Track doses and refills for your medications." },
  preventive_care: { label: "Preventive Care", desc: "Track checkups and screenings so none slip." },
};

const INITIAL_SUPPLEMENTS = [];



const PHQ2 = [
  "Over the past 2 weeks, how often have you felt little interest or pleasure in doing things?",
  "Over the past 2 weeks, how often have you felt down, depressed, or hopeless?",
];
const GAD2 = [
  "Over the past 2 weeks, how often have you felt nervous, anxious, or on edge?",
  "Over the past 2 weeks, how often have you been unable to stop or control worrying?",
];
const PHQ_OPTS = ["Not at all","Several days","More than half the days","Nearly every day"];

const INITIAL_HABITS = [];

// Returns the current (or given) date as a local YYYY-MM-DD string.
// NEVER use toISOString().split('T')[0] for date comparisons — that returns
// UTC midnight which will be off by one day for users logging after 8 pm ET.
// Mirrors the unique index expression on public.goals:
//   create unique index goals_user_title_uniq
//     on public.goals (user_id, lower(trim(title))) where completed = false;
// The guard must normalise identically or it disagrees with the database in one
// of two ways: too loose lets "run 5k" through for the database to reject, too
// strict blocks an insert the database would have accepted.
// The browser's IANA zone, or null if the runtime cannot report one.
// Null is a real answer meaning "unknown" — it must never be turned into UTC.
// notify-cron skips users whose timezone is unknown rather than guessing, because
// guessing UTC puts a Pacific user's quiet hours at 1am-2pm local.
const browserTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
};

// Server-side limits (429 rate limited, 413 payload too large) carry a
// user-facing `message`. Without this the call sites fell through to their
// generic "connection issue" fallback, so a user who hit a daily cap was told
// nothing useful and would simply retry into the same wall.
const apiErrorMessage = (res, data) => {
  if (res.ok) return null;
  if (res.status === 429) {
    const mins = Math.ceil((Number(data?.retry_after_seconds) || 3600) / 60);
    return data?.message || `Too many requests. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`;
  }
  if (res.status === 413) return data?.message || "That request is too large to process.";
  // 503 = the rate limiter itself is unreachable. The AI routes fail closed
  // rather than spend against a $50/month ceiling without a quota check.
  if (res.status === 503) return data?.message || "Your coach is temporarily unavailable. Please try again in a minute.";
  return null;
};

const goalKey = (title) => (title || "").trim().toLowerCase();

// ─── GENERATED TEMPLATE TITLES ───────────────────────────────────────────────
// Quick-template goals get a title that carries the number ("Eating out under
// $300/mo") and follows the numeric field while — and only while — the title is
// still the untouched generated one. newGoal carries two transient fields that
// never reach the DB (addGoal enumerates its payload): `titleBase`, the bare
// template label needed to regenerate, and `autoTitle`, the last title the app
// generated. title === autoTitle means the title is still the app's to rewrite;
// the title input writes only `title`, so a single user keystroke diverges the
// pair and the title is the user's forever after.
const fmtGoalNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString("en-US") : String(v || "");
};
// fmtUSD / fmtUSDExact / fmtUSDCompact / sumMoney live in src/lib/money.js
// (2026-10-14): every amount on screen goes through them, and dollar totals are
// summed in integer cents, so a raw float ("$49.980000000000004") can never
// reach the screen.
// Whole-hour 12-hour label: 0 -> "12 AM", 6 -> "6 AM", 12 -> "12 PM", 18 -> "6 PM".
// The check-in picker is whole hours only, since the cron runs hourly (the chosen
// hour is when it actually arrives).
const fmtHour12 = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return "";
  const hr = ((n % 12) + 12) % 12 || 12;
  return `${hr} ${n % 24 < 12 ? "AM" : "PM"}`;
};
// Accessible on/off switch (role=switch + aria-checked) for the Notifications
// settings screen — the a11y audit flagged the app's onClick-div toggles.
const ToggleSwitch = ({ on, onChange, label }) => (
  <button type="button" role="switch" aria-checked={!!on} aria-label={label}
    onClick={() => onChange(!on)}
    style={{ width: 44, height: 26, borderRadius: 99, background: on ? T.success : "rgba(0,0,0,0.14)", position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0, border: "none", padding: 0 }}>
    <span style={{ position: "absolute", top: 4, left: on ? 22 : 4, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }} />
  </button>
);
const NOTIF_ROW = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 0" };
const NOTIF_LABEL = { fontSize: 14, fontWeight: 700, color: T.text };
const NOTIF_SUB = { fontSize: 12, color: T.textMuted, marginTop: 2, lineHeight: 1.4 };

// Pattern insight ("food for thought"): the observation Kai wrote, plus a
// tap-to-reveal "Why I noticed this" showing the code-written plain-language
// evidence (numbers, never a coefficient). Behind the app lock, never on the
// lock screen. `pattern` = { observation, evidence? }.
const PatternBlock = ({ pattern }) => {
  const [open, setOpen] = useState(false);
  if (!pattern?.observation) return null;
  return (
    <div style={{ marginTop: 12, padding: "12px 14px", background: withAlpha(T.accent, 0.06), border: `1px solid ${withAlpha(T.accent, 0.15)}`, borderRadius: 12 }}>
      <div style={{ fontSize: 10.5, color: T.accent, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>Food for thought</div>
      <div style={{ fontSize: 13.5, color: T.text, lineHeight: 1.55 }}>{pattern.observation}</div>
      {pattern.evidence && (
        <>
          <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{ background: "none", border: "none", color: T.accent, fontWeight: 700, fontSize: 11, cursor: "pointer", padding: 0, marginTop: 8, fontFamily: "inherit" }}>{open ? "Hide" : "Why I noticed this"}</button>
          {open && <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.55, marginTop: 6 }}>{pattern.evidence}</div>}
        </>
      )}
    </div>
  );
};
const genGoalTitle = (base, { period, unit, value, source_type }) => {
  if (source_type === "plaid_spend" && period === "month") return `${base} under $${fmtGoalNum(value)}/mo`;
  if (period === "week") return `${base}: ${fmtGoalNum(value)} ${unit}/wk`;
  if (unit === "$") return `${base}: $${fmtGoalNum(value)}`;
  return `${base}: ${fmtGoalNum(value)}${unit ? ` ${unit}` : ""}`;
};

// ─── GOAL INSERT PAYLOAD — THE one construction ──────────────────────────────
// Shared by BOTH goal-insert paths: addGoal (the modal) and persistOnboarding.
// Map 4.2 was exactly these two drifting apart — the modal learned to pass
// binding fields while onboarding's hand-rolled payload silently dropped them,
// so every onboarding goal was born untracked. If goals grow a column, add it
// here once and both paths carry it.
//
// goal_type is not listed (nothing sets it). start_value IS carried now:
// credit_payoff captures the balance-at-bind here, and the reduction resolver
// reads it. Passed through only when provided, so non-reduction goals still
// write null rather than a stray 0.
const buildGoalPayload = (g, userId) => ({
  user_id: userId,
  title: (g.title || "").trim(),
  category: g.category,
  current_value: parseFloat(g.current_value) || 0,
  target_value: parseFloat(g.target_value),
  unit: (g.unit || "").trim(),
  deadline: g.deadline || null,
  completed: false,
  // The accountability engine (resolveProgress / getGoalAutoValue) keys
  // entirely on these columns.
  source_type: g.source_type || null,
  source_config: g.source_config || null,
  period: g.period || null,
  period_target: (g.period_target !== undefined && g.period_target !== null && g.period_target !== "")
    ? parseFloat(g.period_target) : null,
  ...(g.start_value !== undefined && g.start_value !== null && g.start_value !== ""
    ? { start_value: parseFloat(g.start_value) } : {}),
});

// ─── ONBOARDING QUICK GOALS ──────────────────────────────────────────────────
// Module scope so persistOnboarding can look bindings up by title at insert
// time: a chip tap sets the title verbatim, so title === chip.title means the
// chip's bindings apply, and a user who edits the title into something else
// gets a manual goal — free text is never inferred into a binding, matching
// the Add Goal modal's rule.
//
// Bindings, chip by chip (resolvers: 'savings'/'weight'/'workout'/'plaid_spend'
// server-side in api/lib/goal-progress.js resolveProgress — shared by
// notify-cron and coach; 'workout'/'plaid_spend' client-side in
// getGoalAutoValue. The client's category/unit HEURISTICS are gone — removed
// 2026-09-10, parity P3 — so unbound chips are honestly manual on both sides):
// - The two savings chips carry source_type 'savings' — both sides read the
//   live savings balance (getGoalAutoValue's savings branch client-side,
//   resolveProgress's server-side; a matched pair).
// - "Pay off credit cards" is manual by nature (no debt-payoff resolver on
//   either side). Its old target of 100 "%" rendered "0 / 100 %"
//   meaninglessly — now unit $ with an EMPTY target the user fills with
//   their real balance (targetHint prompts; persistOnboarding skips goal
//   creation when the target is left empty).
// - "Lose 20 lbs" stays manual: the server 'weight' resolver compares an
//   ABSOLUTE goal weight against the log, but this chip is a DELTA — binding
//   it would compare bodyweight to 20 (parity P9, latent — nothing writes
//   this binding; resolve the semantics before anything does).
// - "Run a 5K" is a milestone; no resolver measures single-run distance, and
//   weekly_distance counts walking. Manual.
// - Alcohol-free / books / meditate: no streak/count resolver exists. Manual.
// One list, two views (src/lib/goalTemplates.js): onboarding shows the
// `onboarding: true` subset under its historical QUICK_GOALS name — the
// title strings are load-bearing (persistOnboarding + the goal step match
// chips by exact title), so the subset preserves them verbatim.
const QUICK_GOALS = ONBOARDING_GOAL_TEMPLATES;

// ─── ONBOARDING FOCUS ────────────────────────────────────────────────────────
// "What are you here to work on?" — the answer must DO something: it
// pre-enrolls that pillar's trackers, pre-selects habit chips, puts that
// pillar's goal templates first, and Kai leads with it in the first message.
//
// `trackers` is deliberately NOT the pillar's full tracker list: enrollment is
// explicit opt-in and an enrolled tracker with no data scores 0 against the
// pillar (enrolling `medications` for a "get in shape" user tanks their Health
// score on day one). Listed here are the trackers that can score from what
// onboarding itself collects; `plaidTrackers` join only when a bank actually
// connected, and defaultTrackers()' data-driven inferences are unioned in at
// completion.
const FOCUS_PRESETS = {
  finance:   { label: "Get my money in order", trackers: ["savings_cushion","spending_discipline"], plaidTrackers: ["debt_management","cashflow_trend"], habits: ["budget","track_spend"], goalCats: ["finance"] },
  health:    { label: "Get in better shape",   trackers: ["activity"],                              plaidTrackers: [],                                   habits: ["gym","steps"],          goalCats: ["fitness"] },
  habits:    { label: "Build better habits",   trackers: ["habits"],                                plaidTrackers: [],                                   habits: [],                        goalCats: ["personal"] },
  wellbeing: { label: "Get my head right",     trackers: ["mood","journal"],                        plaidTrackers: [],                                   habits: ["meditate","gratitude"],  goalCats: ["wellness"] },
  all:       { label: "All of it",             trackers: ["savings_cushion","spending_discipline","activity","habits","mood","journal"], plaidTrackers: ["debt_management","cashflow_trend"], habits: ["budget","gym","meditate"], goalCats: null },
};
const FOCUS_ORDER = ["finance","health","habits","wellbeing","all"];

// Hoisted so the greeting effect (component scope) and the modal (IIFE) agree
// on step names. welcome=name; focus=the new question; consent sits directly
// before Kai's first message on done.
// The lock step exists only on the native app: web has no Keychain and no
// biometrics, and the platform cannot change mid-session, so branching the
// array at module scope is safe.
// Bank BEFORE goal: money goals prefill from real balances and bind to
// auto-tracking, so the bank must be connected first. Goal is now the last
// data step before consent, so persistOnboarding runs on leaving GOAL (not
// bank) — see advanceFromGoal.
// [UIDIAG] once-per-session latch for the resume UI health report. Module
// scope so a Scorify remount (account switch) cannot re-arm it.
const uiDiagReported = { ui: false, refresh: false };
const OB_STEPS = ["welcome","coach","focus","body","habits","bank","goal","consent","checkin",...(isLockCapable()?["lock"]:[]),"done"];

// ─── CUSTOM GOAL SHAPES ──────────────────────────────────────────────────────
// Free-form goals are not all "reach a number by a date" — the data model
// already supports rate goals (period + period_target) but only templates
// could set those fields, so a custom "3 gym sessions a week" had to invent a
// fake target number. The shape picker (Add Goal modal + onboarding goal step,
// custom path only — template taps carry their own shape) asks what KIND of
// goal it is and shows only that shape's fields. All four go through
// buildGoalPayload; none is a new construction.
//
// Progress resolution per shape — all four are MANUAL (the Update button)
// unless a template binding is present, which the picker never touches:
// - target:    current_value vs target_value, as today.
// - recurring: period + period_target; the user maintains this period's count
//              via Update (nothing resets it — only bound goals derive counts
//              from data). Kai's pace logic reads period/period_target.
// - cap:       same fields, inverted meaning. source_config.manual_shape='cap'
//              is the inversion marker: GoalCard renders it inverted,
//              updateGoalProgress never auto-completes it, and notify-cron's
//              manual resolver flags it inverted so "under the limit" reads
//              on_track, not "behind".
// - milestone: target_value=1, current_value=0; Update marks it done. Manual
//              by definition — nothing can verify a one-time real-world event.
// NO streak shape on purpose: habits own streaks. The recurring shape offers
// "add it as a habit instead" for the no-endpoint case.
const GOAL_SHAPES = [
  { id: "target",    label: "Reach a number",     hint: "save $5,000, lose 20 lbs" },
  { id: "recurring", label: "Do it regularly",    hint: "3 gym sessions a week" },
  { id: "cap",       label: "Stay under a limit", hint: "2 takeout orders a week" },
  { id: "milestone", label: "Do it once",         hint: "run a 10K, take the trip" },
];

const localDateStr = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// ─── TRANSACTION WINDOWS & SPEND SEMANTICS ───────────────────────────────────
// THE one trailing-N-days window for Plaid transactions. String comparison
// against a LOCAL calendar date, matching how Plaid dates are stored — the
// alternative (`new Date(t.date) >= cutoff`) parses "YYYY-MM-DD" as UTC
// midnight and shifts the window edge for anyone west of UTC (map 3.7).
// Every 7/30-day transaction window in this file goes through these two.
const daysAgoStr = (n) => localDateStr(new Date(Date.now() - n * 86400000));
// THE local month key (YYYY-MM) for bucketing transactions by month.
// toISOString() is UTC: for a Pacific user it rolls to the next month at
// 4/5pm on the last day, landing boundary transactions in the wrong bucket
// (map 3.2). Plaid dates are local strings, so the key must be local too —
// same idiom as notify-cron's localMonthPosition, via localDateStr.
const localMonthStr = (d = new Date()) => localDateStr(d).slice(0, 7);
// normalizeInflowName lives in src/lib/income.js (2026-10-14) so the server
// groups deposits by the same key; it is imported above.
// Transaction day = authorized_datetime → datetime → authorized_date → date
// (src/lib/spend.js txLocalDay, device-local), never the bare posting date.
const txSince = (t, cutoffStr) => (txLocalDay(t) || "") >= cutoffStr;
// What counts as SPENDING. Transfers are money moving between the user's own
// accounts, and loan payments largely pay off card purchases that are already
// in the feed as spending — counting both sides double-counts (paying the
// Visa from checking showed as $6,620 of "spending" on top of the purchases
// themselves). Mirrors the EXCLUDED set in api/notify-cron.js resolveProgress.
// MATCHED PAIR with api/lib/spend.js — change both halves together. Primary
// categories always excluded from spend: inbound transfers and loan/credit-card
// payments (double-count guard). TRANSFER_OUT is NOT blanket-excluded — Zelle /
// Venmo / Cash App / Apple Cash payments to PEOPLE arrive as TRANSFER_OUT, and
// rent-by-check and ATM cash as TRANSFER_OUT_WITHDRAWAL; all count. Only the
// self-account TRANSFER_OUT detailed categories below are excluded (2026-09-22:
// a real account under-reported August spend by ~$2,961 — a $2,375 rent check
// plus cash and a Zelle — because primary TRANSFER_OUT was dropped wholesale).
const SPEND_EXCLUDED = new Set(["TRANSFER_IN", "LOAN_PAYMENTS"]);
const TRANSFER_OUT_SELF_DETAILED = new Set([
  "TRANSFER_OUT_ACCOUNT_TRANSFER",
  "TRANSFER_OUT_INVESTMENT_AND_RETIREMENT_FUNDS",
  "TRANSFER_OUT_SAVINGS",
]);
const spendPrimary = (t) => t.personal_finance_category?.primary || (t.category && t.category[0]) || "";
const isExcludedFromSpend = (t) => {
  if (SPEND_EXCLUDED.has(spendPrimary(t))) return true;
  const detailed = t.personal_finance_category?.detailed || "";
  return !!detailed && TRANSFER_OUT_SELF_DETAILED.has(detailed);
};
const isSpendTx = (t) => t.amount > 0 && !isExcludedFromSpend(t);

// No emoji anywhere (house rule). Icon fields carry legacy emoji (supplements
// were saved with a default pill icon by an older addSupp; demo seed had others), so
// icon rendering is emoji-safe: an emoji icon degrades to the name's initials,
// the non-emoji treatment the design uses elsewhere. New icons are never emoji.
const EMOJI_RE = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{200D}]/u;
const hasEmoji = (s) => !!s && EMOJI_RE.test(s);
const iconOrInitials = (icon, name) =>
  (icon && icon !== "·" && icon !== "" && !hasEmoji(icon)) ? icon : (name || "").slice(0, 2).toUpperCase();

// isCountableInflow lives in src/lib/income.js (2026-10-14): one decision for
// plaidDerivedIncome, freshIncome (Kai), the cadence classifier, and the cron.

// ─── USER-SCOPED LOCALSTORAGE ─────────────────────────────────────────────────
// All per-user localStorage keys use this prefix to prevent cross-user data leaks.
const userKey = (uid, key) => uid ? `scorify_${uid}_${key}` : null;

// CACHE-FIRST STARTUP: version of the on-device dashboard snapshot. Bump to
// invalidate old snapshots when the cached shape changes. The snapshot holds
// render/scoring DATA only (with staleness timestamps) — never tokens, never
// Plaid access tokens (those live server-side), never transient UI. It is
// keyed per user (userKey → scorify_<uid>_app_cache_vN), stamped with the uid,
// hydrated only when the uid matches, cleared by purgeUserStorage on sign-out,
// localStorage.clear on account deletion, and an other-user sweep on hydrate —
// so one account's financial/health data can never surface under another. It is
// one-account-only, like the PIN and Screen Time state (CLAUDE.md).
// v2: journal entry TEXT removed from the snapshot (review L7) — the most
// sensitive column has no place at rest in localStorage, and the overview
// renders without it (the wellness tab loads entries from the network). The
// bump makes the hydrate-time sweep delete every stored v1 snapshot, purging
// journal text already written to devices.
const APP_CACHE_VERSION = 2;

// Remove all scorify_/ls_ keys except scorify_theme. Called on sign-out.
const purgeUserStorage = () => {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if ((k.startsWith('scorify_') || k.startsWith('ls_')) && k !== 'scorify_theme' && k !== 'scorify_onesignal_ext') {
        toRemove.push(k);
      }
    }
    toRemove.forEach(k => localStorage.removeItem(k));
  } catch {}
};

// One-time migration: delete legacy unscoped keys from pre-scoped versions.
const migrateLegacyKeys = () => {
  try {
    if (localStorage.getItem('scorify_ls_migrated_v1')) return;
    const legacy = ['scorify_coach_msgs', 'scorify_coach_msgs_savedAt',
      'ls_coach_insight_text', 'ls_coach_insight_date',
      'scorify_ai_consent',
      // Google Calendar was removed; this key is kept in the purge list so the
      // stale token left behind on existing devices gets cleared on sign-out.
      'google_calendar_token'];
    legacy.forEach(k => localStorage.removeItem(k));
    // Also remove date-keyed legacy entries
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('ls_coach_morning_') || k.startsWith('ls_crisis_') ||
          k.startsWith('ls_weekly_') || k.startsWith('ls_visited_'))) {
        localStorage.removeItem(k);
      }
    }
    localStorage.setItem('scorify_ls_migrated_v1', '1');
  } catch {}
};
migrateLegacyKeys();

// ─── PILLAR SCORING ───────────────────────────────────────────────────────────
// Returns { total, pillars } where pillars shows per-category detail



const Bar = ({ value, max, color=T.accent, h=6 }) => (
  <div style={{background:T.overlayLight(0.07),borderRadius:99,height:h,overflow:"hidden"}}>
    <div style={{width:`${Math.min(100,(value/max)*100)}%`,background:color,height:"100%",borderRadius:99,transition:"width 0.8s ease"}}/>
  </div>
);

// target28: expected logs over the 28-day window (habitTarget28), so the
// "28-day rate" below is the SAME against-target ratio the Habits pillar
// scores — never logs ÷ 28, which capped a 5-times-a-week habit at 71%.
const HabitRing = ({ target, completions, streak, target28 }) => {
  const r = 45;
  const circ = 2 * Math.PI * r;
  const safeCompletions = completions || [];

  // Build Mon–Sun cells for the current calendar week. Each cell computes
  // its own local date string so logged status is always date-accurate.
  const jsDay = new Date().getDay(); // 0=Sun … 6=Sat
  const daysSinceMonday = jsDay === 0 ? 6 : jsDay - 1;
  const dayLabels = ['M','T','W','T','F','S','S'];
  const weekCells = dayLabels.map((label, i) => {
    const dayOffset = i - daysSinceMonday; // <0 past, 0 today, >0 future
    const isFuture = dayOffset > 0;
    const cellDate = new Date();
    cellDate.setDate(cellDate.getDate() + dayOffset);
    const cellDateStr = localDateStr(cellDate);
    const logged = !isFuture && safeCompletions.includes(cellDateStr);
    return { label, logged, isFuture };
  });
  const derivedCount = weekCells.filter(c => c.logged).length;
  const progress = target > 0 ? Math.min(derivedCount / target, 1) : 0;
  const dashOffset = circ * (1 - progress);

  // Best streak: walk last 28 days oldest→newest and count consecutive logged days.
  // Take max with streak prop to capture streaks older than 28 days.
  let bestS = 0, cur = 0;
  for (let k = 27; k >= 0; k--) {
    const d = new Date(); d.setDate(d.getDate() - k);
    if (safeCompletions.includes(localDateStr(d))) { cur++; if (cur > bestS) bestS = cur; }
    else cur = 0;
  }
  if (streak > bestS) bestS = streak;

  // 28-day consistency against the habit's own target (shared helper with
  // the scoring engine, so this and the pillar always agree).
  const totalLogs = habitLogs28({ completions: safeCompletions }, localDateStr());
  const need = target28 || Math.max(1, (target || 5) * 4);
  const pct = Math.min(100, Math.round((totalLogs / need) * 100));

  const bottomText = safeCompletions.length === 0
    ? "Just getting started"
    : `${bestS}-day best · ${pct}% of target, last ${HABIT_WINDOW_DAYS} days`;

  return (
    <div style={{display:"flex",alignItems:"center",gap:16,paddingTop:2}}>
      {/* Progress ring */}
      <div style={{position:"relative",width:90,height:90,flexShrink:0}}>
        <svg viewBox="0 0 110 110" width={90} height={90} style={{transform:"rotate(-90deg)",display:"block"}}>
          <circle cx={55} cy={55} r={r} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={8}/>
          {progress > 0 && (
            <circle cx={55} cy={55} r={r} fill="none" stroke={T.accent} strokeWidth={8}
              strokeDasharray={`${circ} ${circ}`} strokeDashoffset={dashOffset} strokeLinecap="round"/>
          )}
        </svg>
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",pointerEvents:"none"}}>
          <div style={{fontSize:22,fontWeight:900,color:T.text,lineHeight:1}}>{derivedCount}</div>
          <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>of {target}</div>
        </div>
      </div>
      {/* This-week breakdown */}
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:10,fontWeight:700,color:T.textDim,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:7}}>This Week</div>
        <div style={{display:"grid",gridTemplateColumns:SEVEN_EQUAL_COLUMNS,gap:3,marginBottom:8}}>
          {weekCells.map((c,i) => (
            <div key={i} style={{textAlign:"center"}}>
              <div style={{fontSize:9,color:T.textDim,fontWeight:600,marginBottom:3,lineHeight:1}}>{c.label}</div>
              <div style={{height:16,borderRadius:4,
                background: c.logged ? T.accent : T.overlayLight(0.06),
                border: c.logged ? "none" : `1px solid ${T.overlayLight(0.09)}`,
                opacity: c.isFuture ? 0.25 : 1,
                display:"flex",alignItems:"center",justifyContent:"center",
                transition:"background 0.2s"}}>
                {/* Drawn check, thumbs precedent: logged vs not must not be
                    color alone. White on the accent fill; empty cells stay
                    empty — the mark itself is the shape signal. */}
                {c.logged && (
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4.5 12.5l5 5L19.5 7" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{fontSize:10,color:T.textDim,lineHeight:1.4}}>{bottomText}</div>
      </div>
    </div>
  );
};

const Flame = ({ streak }) => {
  const color = streak>=10?T.danger:streak>=5?T.accent:streak>=2?T.success:T.textMuted;
  return (
    <div style={{display:"flex",alignItems:"center",gap:5}}>
      <span style={{fontSize:18,fontWeight:700,color:color,fontFamily:"'DM Serif Display',serif"}}>{streak}</span>
      <span style={{fontSize:10,color:T.textMuted,fontWeight:500,letterSpacing:"0.08em",textTransform:"uppercase"}}>day streak</span>
    </div>
  );
};

// ── DEMO PROFILES ────────────────────────────────────────────────────────────
// The three "Alex" profiles and their date shift live in src/lib/demoProfiles.js
// (shared with scripts/seed-demo-account.mjs, which seeds the App Review account
// from the same rows). Shifted once at module load, like before the move.
// Whether Plaid's INITIAL import may still be running. For the first hours
// after linking, /transactions/get returns a partial history, and any rate
// or total computed from it (savings rate, monthly income) is confidently
// wrong — a tester saw "a 62% savings rate" minutes after connecting. Ready
// when the history already spans ~a month (25+ days of transactions covers
// every 30-day window) OR the link is over 24 hours old. A legacy cache
// without connected_at never suppresses (better an old-style number than a
// permanent "still importing").
const plaidImportPendingCheck = (connected, connectedAtIso, txs) => {
  if (!connected) return false;
  let oldest = null;
  for (const t of (txs || [])) { const d = txLocalDay(t); if (d && (!oldest || d < oldest)) oldest = d; }
  if (oldest && (Date.now() - new Date(oldest + "T00:00:00").getTime()) >= 25 * 86400000) return false;
  if (!connectedAtIso) return false;
  return (Date.now() - new Date(connectedAtIso).getTime()) < 24 * 60 * 60 * 1000;
};

const _demoDeltaDays = demoDeltaDays();
const _shiftDemoValue = (v) => shiftDemoValue(v, _demoDeltaDays);
const PICKED_DEMO = normalizeDemoProfile(DEMO_PROFILES[Math.floor(Math.random() * DEMO_PROFILES.length)], _demoDeltaDays);


const calcCreditFactors = (debts, habits, monthlyIncome, creditDetails={}) => {
  const payHabit = habits.find(h=>h.id==="cardpay");
  const payStreak = payHabit?.streak||0;
  const payValue = Math.min(100, 60 + payStreak * 2);
  const payColor = payValue>=80?T.success:payValue>=60?T.accent:T.danger;
  const payDesc = payStreak>0 ? `${payStreak}-month on-time streak` : "No payment history logged yet";
  const ccBal = creditDetails.cc_balance>0 ? creditDetails.cc_balance : debts.filter(d=>(d.name||"").toLowerCase().includes("credit")||(parseFloat(d.apr)>15)).reduce((a,d)=>a+(d.balance||0),0);
  const ccLim = creditDetails.cc_limit>0 ? creditDetails.cc_limit : Math.max(ccBal*2, 5000);
  const utilPct = ccLim>0 ? Math.round((ccBal/ccLim)*100) : 0;
  const utilValue = Math.max(0, 100-utilPct);
  const utilColor = utilPct<=30?T.success:utilPct<=50?T.accent:T.danger;
  const utilDesc = ccBal>0 ? `$${ccBal.toLocaleString()} / $${ccLim.toLocaleString()} limit (${utilPct}%)` : "No credit card debt. Great!";
  const totalDebt = debts.reduce((a,d)=>a+(d.balance||0),0);
  const dti = monthlyIncome>0 ? Math.round((totalDebt/(monthlyIncome*12))*100) : 0;
  const dtiValue = Math.max(0, 100-dti);
  const dtiColor = dti<=20?T.success:dti<=50?T.accent:T.danger;
  const dtiDesc = totalDebt>0 ? `$${totalDebt.toLocaleString()} total debt (${dti}% DTI)` : "No debt. Excellent!";
  return [
    { label:"Payment History",    weight:35, value:payValue,  desc:payDesc,  color:payColor  },
    { label:"Credit Utilization", weight:30, value:utilValue, desc:utilDesc, color:utilColor },
    { label:"Debt-to-Income",     weight:20, value:dtiValue,  desc:dtiDesc,  color:dtiColor  },
    { label:"Credit Age",         weight:15,
      value: creditDetails.credit_age_years>=7?90:creditDetails.credit_age_years>=4?75:creditDetails.credit_age_years>=2?60:creditDetails.credit_age_years>0?45:72,
      desc: creditDetails.credit_age_years>0?`Avg account age: ${creditDetails.credit_age_years} yrs`:"Enter your credit age below",
      color: creditDetails.credit_age_years>=7?T.success:creditDetails.credit_age_years>=4?T.accent:creditDetails.credit_age_years>0?T.accent:T.textMuted },
    { label:"New Credit",         weight:10, value:90,        desc:"No hard inquiries (12mo)", color:T.accent },
  ];
};

// ─── GAMIFICATION ────────────────────────────────────────────────────────────
// Icons were deliberately removed 2026-03-10 ("remove all emojis for clean
// professional look"); the empty icon fields lingered for six months with zero
// consumers. Levels are text + color only — do not re-add icon slots.
const LEVELS = [
  { name:"Rookie",   minXP:0,    color:T.textMuted },
  { name:"Hustler",  minXP:500,  color:T.accent },
  { name:"Pro",      minXP:1500, color:T.accent },
  { name:"Elite",    minXP:3000, color:T.accent },
  { name:"Legend",   minXP:6000, color:T.success },
];
// Badge icons removed with the 2026-03-10 emoji purge; badges are label + desc.
const BADGES = [
  { id:"streak7",   label:"7-Day Streak",    desc:"Log 7 days in a row" },
  { id:"streak30",  label:"30-Day Streak",   desc:"Log 30 days in a row" },
  { id:"streak100", label:"100-Day Streak",  desc:"Log 100 days in a row" },
  { id:"hustler",   label:"Hustler",         desc:"Reached Hustler level" },
  { id:"pro",       label:"Pro",             desc:"Reached Pro level" },
  { id:"elite",     label:"Elite",           desc:"Reached Elite level" },
  { id:"legend",    label:"Legend",          desc:"Reached Legend level" },
  { id:"first_log", label:"First Log",       desc:"Logged your first activity" },
  { id:"week1",     label:"Week One",        desc:"Used Scorify for 7 days" },
];
const getLevel = (xp) => [...LEVELS].reverse().find(l => xp >= l.minXP) || LEVELS[0];
const getNextLevel = (xp) => LEVELS.find(l => l.minXP > xp) || null;
const XP_ACTIONS = { login:15, habit:10, mood:5, weight:5, supplement:3 };


// ─── GOAL CARD (reusable across tabs) ────────────────────────────────────────
function GoalCard({ g, onUpdate, onDelete, onEdit, autoValue, autoLabel, autoTarget, autoUnit, autoHasData, autoInverted, autoNoSource, autoStateLine, autoNote, onConnectSource }) {
  const isLiveTracked = autoLabel != null;
  const noSource = autoNoSource != null; // bound goal, but its data source is not connected
  const isSyncing = isLiveTracked && autoHasData === false && !noSource;
  // Spending cap — over target is the warning state. Bound caps signal via
  // the resolver (autoInverted); manual caps carry the marker on the row.
  const isInverted = autoInverted === true || g.source_config?.manual_shape === "cap";
  const displayVal = isSyncing ? 0 : (autoValue ?? g.current_value);
  const target = autoTarget ?? g.period_target ?? g.target_value;
  const unit = autoUnit ?? g.unit;
  const pct = target > 0 && !isSyncing ? Math.min(100, Math.round((displayVal / target) * 100)) : 0;
  const isOverBudget = isInverted && target > 0 && displayVal > target;
  const daysLeft = g.deadline ? Math.ceil((new Date(g.deadline) - new Date()) / (1000*60*60*24)) : null;
  const catColors = { fitness:T.success, finance:T.accent, health:T.accent, personal:T.accent };
  const col = isOverBudget ? T.danger : (catColors[g.category] || T.accent);
  const isDollar = unit === '$';
  const fmtVal = (n) => isDollar ? `$${Number(n).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:2})}` : `${Number(n).toLocaleString()} ${unit}`.trim();
  // G.tileBg, not black-alpha: over a glass card the dark tint stacked with
  // the glow and put muted text under AA (REDESIGN.md §2 rule).
  return (
    <div style={{background:G.tileBg,border:`1px solid ${col}22`,borderRadius:14,padding:"16px 18px",borderLeft:`3px solid ${col}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:col,background:`${col}18`,border:`1px solid ${col}30`,borderRadius:6,padding:"2px 8px",textTransform:"uppercase"}}>{g.category}</span>
            {isLiveTracked && <span style={{fontSize:10,color:noSource?T.textMuted:isOverBudget?T.danger:T.success,fontWeight:600}}>{noSource ? 'not connected' : isSyncing ? '⟳ syncing…' : `● ${autoLabel || 'auto-tracking'}`}</span>}
            {!isLiveTracked && <span style={{fontSize:10,color:T.textMuted,fontWeight:600}}>Manual: you update this</span>}
            {daysLeft !== null && <span style={{fontSize:11,color:daysLeft<=7?T.danger:daysLeft<=14?T.accent:T.textMuted}}>{daysLeft > 0 ? `${daysLeft}d left` : " Overdue"}</span>}
          </div>
          <div style={{fontSize:15,fontWeight:800}}>{g.title}</div>
        </div>
        <div style={{display:"flex",gap:6,flexShrink:0}}>
          {/* Edit is the ONLY affordance besides Delete on live-tracked goals
              (they hide Update by design) — without it a cap can never change. */}
          {onEdit && <button onClick={()=>onEdit(g)} style={{background:"transparent",border:`1px solid ${col}44`,color:col,borderRadius:8,padding:"4px 10px",fontSize:11,cursor:"pointer",fontWeight:700}}>Edit</button>}
          {!isLiveTracked && <button onClick={()=>onUpdate(g)} style={{background:`${col}18`,border:`1px solid ${col}44`,color:col,borderRadius:8,padding:"4px 10px",fontSize:11,cursor:"pointer",fontWeight:700}}>Update</button>}
          <button onClick={()=>{if(window.confirm(`Delete "${g.title}"?`))onDelete(g.id);}} aria-label={`Delete goal ${g.title}`} style={{background:"transparent",border:`1px solid ${T.overlayLight(0.08)}`,color:T.textMuted,borderRadius:8,padding:"11px 13px",fontSize:11,cursor:"pointer"}}>×</button>
        </div>
      </div>
      <div style={{background:T.overlayLight(0.07),borderRadius:99,height:8,overflow:"hidden",marginBottom:6}}>
        <div style={{width:`${pct}%`,background:`linear-gradient(90deg,${col},${col}88)`,height:"100%",borderRadius:99,transition:"width 0.8s"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
        {/* stateLine (screen_time) replaces the numeric line entirely: a
            threshold source has no totals, and a floor number rendered as
            "N / cap" would overstate success on an inverted cap. The line
            carries the honest state for BOTH hasData values. */}
        {autoStateLine
          ? <span style={{color:autoHasData===false?T.textMuted:isOverBudget?T.danger:T.text}}>{autoStateLine}</span>
          : noSource
          ? <span style={{color:T.textMuted}}>— / {fmtVal(target || 0)} · {onConnectSource
              ? <button onClick={onConnectSource} style={{background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",padding:0,fontFamily:"inherit"}}>{autoNoSource === "screen_time" ? "Allow Screen Time →" : "Connect your bank →"}</button>
              : <span>{autoNoSource === "screen_time" ? "allow Screen Time access to track this" : "connect your bank in Finances to track this"}</span>}</span>
          : isSyncing
          ? <span style={{color:T.textMuted}}>— / {fmtVal(target || 0)} · syncing…</span>
          : <span style={{color:T.textMuted}}>{fmtVal(displayVal)} / {fmtVal(target || 0)}</span>
        }
        {autoStateLine
          ? <span style={{fontWeight:800,color:isOverBudget?T.danger:autoHasData===false?T.textMuted:T.success}}>{isOverBudget ? 'Over limit' : autoHasData===false ? '—' : `≤ ${unit === 'min' && target >= 60 ? `${parseFloat(((target || 0) / 60).toFixed(1))}h` : fmtVal(target || 0)}`}</span>
          : isInverted
          ? <span style={{fontWeight:800,color:col}}>{isOverBudget ? 'Over limit' : pct+'%'}</span>
          : <span style={{fontWeight:800,color:pct>=100?T.success:col}}>{pct>=100?' Complete!':pct+'%'}</span>
        }
      </div>
      {/* Honest note (credit payoff): the balance has risen above the start,
          so the paid figure is clamped to 0 — say so instead of a fake 0%. */}
      {autoNote && <div style={{fontSize:11,color:T.warning,marginTop:6}}>{autoNote}</div>}
    </div>
  );
}

// ── COACH EMPTY STATES (shared by the Coach tab and the coach drawer) ────────
// Extracted after the drawer was found to have the same dead-screen gap the tab
// had (map entry A.2): both surfaces must render identical demo/consent states,
// so they render these components rather than each keeping a copy that can drift.
const CoachSampleConversation = () => (
  <div style={{display:"flex",flexDirection:"column",gap:12,animation:"coachFadeInUp 0.4s ease"}}>
    <div style={{alignSelf:"center",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.1em",background:T.overlayLight(0.06),borderRadius:99,padding:"3px 12px"}}>Sample conversation</div>
    <div style={{maxWidth:"82%",alignSelf:"flex-start",background:T.overlayLight(0.07),color:T.text,borderRadius:"4px 18px 18px 18px",padding:"13px 17px",fontSize:14,lineHeight:1.6}}>
      Morning. Your water streak hit 12 days and your mood has averaged 7.8 this week. The two track together in your data. One thing to watch: your credit utilization crept to 36%, and a payment before Friday keeps it off your report.
    </div>
    <div style={{maxWidth:"82%",alignSelf:"flex-end",background:T.accentFill,color:"#fff",borderRadius:"18px 4px 18px 18px",padding:"13px 17px",fontSize:14,lineHeight:1.6}}>
      What should I focus on today?
    </div>
    <div style={{maxWidth:"82%",alignSelf:"flex-start",background:T.overlayLight(0.07),color:T.text,borderRadius:"4px 18px 18px 18px",padding:"13px 17px",fontSize:14,lineHeight:1.6}}>
      Log your gym session. That 8 day streak is your longest yet, and your mood runs about 1.4 points higher on training days. It is the single highest leverage 45 minutes on your calendar.
    </div>
    <div style={{textAlign:"center",fontSize:12,color:T.textMuted,lineHeight:1.6,marginTop:6}}>
      This is a sample. Create an account and the coach reads your real habits, spending and health data.
    </div>
  </div>
);
const CoachConsentPrompt = ({ onEnable }) => (
  <div style={{textAlign:"center",marginTop:32,animation:"coachFadeInUp 0.4s ease",maxWidth:360,alignSelf:"center"}}>
    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:T.text,marginBottom:8}}>Your coach needs your OK first</div>
    <div style={{fontSize:13,color:T.textMuted,lineHeight:1.7,marginBottom:18}}>
      AI coaching sends what you track to Anthropic's Claude to generate responses: habits, goals, supplements, recovery streaks, XP, mood scores and wellbeing check-ins, health data like workouts, steps, sleep, screen time and weight, and your finances, including balances and recent transactions when your bank is connected. It also includes your medications for adherence only (never to assess them), tendencies Scorify computes from your own history, and commitments you make to the coach; Scorify stores a derived daily ledger to power these. Journal entries and mood notes are shared only if you turn that on separately. Nothing is sent until you enable it, and you can revoke it anytime.
    </div>
    <button onClick={onEnable} style={{background:T.accentFill,color:"#fff",border:"none",borderRadius:10,padding:"12px 24px",cursor:"pointer",fontWeight:600,fontSize:14,letterSpacing:"0.01em",fontFamily:"'DM Sans',sans-serif"}}>Enable AI Coaching</button>
  </div>
);

export default function LifeSync({ user, onSignOut, isDemo = false }) {
  const navigate = useNavigate();
  const isDemoMode = isDemo || !!window.__DEMO_MODE__;
  const DEMO = isDemoMode ? PICKED_DEMO : DEMO_PROFILES[0];
  const [tab, setTab] = useState("overview");
  // PERF: the nav highlight reads `tab` (urgent — updates the instant you tap),
  // but the heavy tab CONTENT is gated on this deferred copy. Under React 18's
  // concurrent root the content render becomes low-priority and INTERRUPTIBLE,
  // so a switch never blocks the main thread (the device bug: the tab
  // highlighted but the screen stayed frozen, taking 5-6 taps to change) and
  // rapid back-and-forth switching coalesces to the latest tab instead of
  // queuing a full render per tap. Content conditionals below use contentTab;
  // nav/highlight/FAB keep using tab.
  const contentTab = useDeferredValue(tab);
  // PERF: the Finances tab's Recharts SVGs are the single heaviest thing to
  // mount in the app, and mounting them in the same frame as the tab switch is
  // what makes Finances feel slow even with the content render deferred. Hold
  // the charts back one paint: the tab (numbers, cards) shows immediately, then
  // the charts fill in a frame later. Reset on leave so the next entry defers
  // again. Placeholders below preserve the chart height so nothing jumps.
  const [financeChartsReady, setFinanceChartsReady] = useState(false);
  useEffect(() => {
    if (contentTab !== "finances") { setFinanceChartsReady(false); return; }
    let r1, r2;
    r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setFinanceChartsReady(true)); });
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
  }, [contentTab]);

  const [dataLoaded, setDataLoaded] = useState(isDemo ? true : false);
  // ── OFFLINE ── navigator.onLine is a HINT, never a gate: `false` is trusted
  // (banner, toast wording, disabled composer), `true` is never treated as
  // proof of connectivity — the fetch result stays the ground truth for every
  // operation, so a lying `true` degrades to today's behaviour, never worse.
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const wasOffline = useRef(typeof navigator !== "undefined" && navigator.onLine === false);
  // Bumped on reconnect to re-run loadData (its effect depends on this), so an
  // offline cold launch's empty dashboard heals without killing the app.
  const [reloadTick, setReloadTick] = useState(0);
  // [UIDIAG] status of the last foreground data refresh (the Plaid resume
  // sync — the only data refresh a warm resume runs). Diagnostics only.
  const resumeRefreshRef = useRef({ status: "none", at: 0 });
  // Tracks the profiles row specifically, so the onboarding modal can never
  // render before we know what profiles.onboarding_complete actually says.
  const [profileLoaded, setProfileLoaded] = useState(isDemo ? true : false);
  // Paywall (src/lib/paywall.js, src/Paywall.jsx). `subscription` is what
  // GET /api/subscription last answered ({ paywallEnabled, entitled, reason,
  // expiresAt, willRenew }); null until it has, and a failed fetch leaves it
  // alone so nobody is blocked on a network error. paywallForced: a 402 from
  // coach/claude. freshOnboarding + firstScoreSeen: a new user sees their first
  // scores before the paywall; a returning one gets it at launch.
  const [subscription, setSubscription] = useState(null);
  const [paywallForced, setPaywallForced] = useState(false);
  const [freshOnboarding, setFreshOnboarding] = useState(false);
  const [firstScoreSeen, setFirstScoreSeen] = useState(false);
  // Soft paywall (2026-10-27): "Not now" puts this session in preview mode
  // (every tab viewable, banner under the header); a cold launch resets it.
  // requirePro guards the actions that write or spend: gated, it opens the
  // paywall and answers false. paywallGated is the server's answer alone, so
  // it is safe to read from handlers declared before the placement rule.
  const [paywallPreview, setPaywallPreview] = useState(false);
  const paywallGated = !isDemoMode && !!user && shouldShowPaywall(subscription);
  const requirePro = makeRequirePro({ isGated: () => paywallGated, openPaywall: () => setPaywallForced(true) });
  const [habits, setHabits] = useState(isDemo ? DEMO.habits : INITIAL_HABITS);
  const [showAdd, setShowAdd] = useState(false);
  const [logModal, setLogModal] = useState(null);
  const [logVal, setLogVal] = useState(1);
  const [justLogged, setJustLogged] = useState(null);
  // Workout details sheet after a Gym tick (src/lib/workoutLog.js): { habitId, date, type, duration, note }.
  const [workoutSheet, setWorkoutSheet] = useState(null);
  // Habit note editor: { id, date, text } (src/lib/habitNotes.js). Never prompted.
  const [habitNoteEdit, setHabitNoteEdit] = useState(null);
  // The Home header's local day: rolls at local midnight (timer) and on a
  // resume that crossed it (visibilitychange), so the pad never shows yesterday.
  const [headerDay, setHeaderDay] = useState(() => localDateStr());
  // A minute tick so "later today" and the after-noon rule stay honest without a state change.
  const [headerMinute, setHeaderMinute] = useState(() => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); });
  useEffect(() => { const t = setInterval(() => { const n = new Date(); setHeaderMinute(n.getHours() * 60 + n.getMinutes()); }, 60000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
    const t = setTimeout(() => setHeaderDay(localDateStr()), Math.max(1000, next - now));
    const onVis = () => { const d = localDateStr(); setHeaderDay(prev => prev === d ? prev : d); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", onVis); };
  }, [headerDay]);
  const habitPressTimer = useRef(null);
  const [workoutSheetSaving, setWorkoutSheetSaving] = useState(false);
  const [creditScore, setCreditScore] = useState(isDemo ? DEMO.creditScore : 0);
  const [monthlyIncome, setMonthlyIncome] = useState(isDemo ? DEMO.monthlyIncome : 0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(isDemo ? DEMO.monthlyExpenses : 0);
  const [savings, setSavings] = useState(isDemo ? DEMO.savings : 0);
  // Whether a finances row EXISTS — distinct from savings===0, which is also
  // the default when no row was ever written. The savings-bound goal branch
  // keys hasData on this so a missing row renders as unknown, never $0.
  const [hasFinancesRow, setHasFinancesRow] = useState(isDemo);
  // Whether the user has actually ENTERED a savings balance — distinct from
  // savings===0 (the default). Unknown must render as an empty state, never as
  // "$0" (invariant: unknown is never $0). True when the finances row carries a
  // non-null savings value, or the user saves one.
  const [savingsKnown, setSavingsKnown] = useState(isDemo);
  // Demo debts/bills come from the SAME profile as the transactions and
  // journal, so the numbers agree across screens (the old inline arrays here
  // said Rent $1,250 while the profile's transactions paid $1,400, and an
  // $18.5k student loan while the journal talked about $22k).
  const [debts, setDebts] = useState(isDemo ? (DEMO.debts || []) : []);
  const [bills, setBills] = useState(isDemo ? (DEMO.bills || []) : []);
  // "Other savings and assets" (gold/silver, cash, other bank/brokerage, other) — count
  // toward the savings cushion. prevSavingsBalance is last month's cushion balance (from
  // savings_snapshots), feeding the cushion's month-over-month growth credit.
  const [otherAssets, setOtherAssets] = useState([]);
  const [prevSavingsBalance, setPrevSavingsBalance] = useState(null);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [newAsset, setNewAsset] = useState({ type: 'cash', label: '', amount: '' });
  // Plan-with-Kai commitments (Up next card + commitments calendar). One fetch, all
  // statuses with a due date; the card and calendar derive their own views.
  const [commitments, setCommitments] = useState(isDemo ? (DEMO.commitments || []) : []);
  const [openCommitmentId, setOpenCommitmentId] = useState(null); // which Up-next row is expanded
  const [rescheduleDraft, setRescheduleDraft] = useState({ date: "", time: "" }); // reschedule editor
  const [commitCalMonth, setCommitCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [commitCalDay, setCommitCalDay] = useState(null); // selected day string in the commitments calendar
  const [showAllCommitments, setShowAllCommitments] = useState(false); // "See all" -> calendar
  // Sunday "Plan my week" (src/lib/weekPlan.js). plannerPending defers the
  // deterministic pre-fill until data has loaded (a cold-launch push tap).
  const [weekPlan, setWeekPlan] = useState(null);
  // Home declutter: the brief and the weekly recap start collapsed.
  const [briefExpanded, setBriefExpanded] = useState(false);
  const [recapExpanded, setRecapExpanded] = useState(false);
  const [plannerPending, setPlannerPending] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState("");
  // ONE commitments query, shared by loadData, the coach-reply refresh and the
  // resume refresh, so Up next, See all (calendar) and the day detail can never
  // show different vintages — they all read the `commitments` state above.
  // Device finding (build 19): the coach recorded a plan, the row landed, and
  // Up next said "Nothing scheduled" until a force quit, because only loadData
  // ever fetched this table.
  const reloadCommitments = useCallback(async () => {
    if (isDemo || !user?.id) return;
    try {
      const { data } = await supabase.from("coach_commitments")
        .select("id,text,status,due_date,due_time,metric_type,metric_ref,evidence,resolved_at,created_at,confirm_asked_at")
        .eq("user_id", user.id)
        .in("status", ["open", "needs_confirmation", "done", "missed", "dropped"])
        .order("due_date", { ascending: true })
        .limit(200);
      if (data) setCommitments(data);
    } catch (e) { console.warn("commitments refresh failed", e?.message); }
  }, [isDemo, user?.id]);
  const [medications, setMedications] = useState(isDemo ? DEMO.medications : []);
  // Fallback checkup dates ride the same date shift as the profiles (they
  // were authored against DEMO_AUTHORED_TODAY too); without it they all read
  // Overdue within weeks of authoring.
  const [checkups, setCheckups] = useState(isDemo ? (DEMO.checkups || _shiftDemoValue([
    { id: 'care1', name: 'Annual Physical', last_date: '', urgent: false, doctor: 'Dr. Martinez', type: 'Primary Care', next_date: '2026-04-15' },
    { id: 'care2', name: 'Dental Cleaning', last_date: '', urgent: false, doctor: 'Dr. Patel', type: 'Dentist', next_date: '2026-04-22' },
    { id: 'care3', name: 'Eye Exam', last_date: '', urgent: false, doctor: 'Dr. Chen', type: 'Optometrist', next_date: '2026-05-10' },
    { id: 'care4', name: 'Blood Work Follow-up', last_date: '2026-03-28', urgent: true, doctor: 'Dr. Martinez', type: 'Primary Care', next_date: '2026-03-30' },
  ])) : []);
  // Notification preferences (profiles columns). Defaults MATCH the DB defaults
  // (on by default; checkinHour null = the user has not chosen a check-in time,
  // so nothing is sent until they pick one). Held as one object for simple
  // load/save/cache.
  const [notifPrefs, setNotifPrefs] = useState({
    checkinHour: null, checkinEnabled: true, weeklyRecapEnabled: true,
    alertsMoney: true, alertsGoals: true, alertsHabits: true, alertsHealth: true,
    moodPatternsEnabled: true,
    eveningMoodEnabled: true, // ~8 PM one-tap mood prompt when none logged that day
    // Default true = current behavior (medication + merchant names reach Kai).
    // OFF strips medication names and merchant names from everything sent to Kai.
    shareSensitiveNames: true,
    checkinPromptSnoozedUntil: null,
  });
  // Optimistic-then-persist. profiles is UPDATE only (never upsert — username is
  // NOT NULL + UNIQUE, see CLAUDE.md). Maps camelCase state to the snake_case
  // columns the cron reads.
  const saveNotifPrefs = async (partial) => {
    const next = { ...notifPrefs, ...partial };
    setNotifPrefs(next);
    if (isDemoMode || !user?.id) return true;
    return await persist("your notification settings", supabase.from("profiles").update({
      checkin_hour: next.checkinHour,
      checkin_enabled: next.checkinEnabled,
      weekly_recap_enabled: next.weeklyRecapEnabled,
      alerts_money: next.alertsMoney,
      alerts_goals: next.alertsGoals,
      alerts_habits: next.alertsHabits,
      alerts_health: next.alertsHealth,
      mood_patterns_enabled: next.moodPatternsEnabled,
      evening_mood_enabled: next.eveningMoodEnabled,
      share_sensitive_names: next.shareSensitiveNames,
      checkin_prompt_snoozed_until: next.checkinPromptSnoozedUntil,
    }).eq("id", user.id));
  };
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [showEditFinances, setShowEditFinances] = useState(false);
  const openEditFinances = () => { setFinanceForm({income: monthlyIncome||"", expenses: monthlyExpenses||"", savings: savings||"", pay_amount: paycheckAmount||"", pay_freq: paySchedule.freq||"biweekly", pay_day1: String(paySchedule.day1||1), pay_day2: String(paySchedule.day2||15), pay_weekday: String(paySchedule.weekday||5), pay_start: paySchedule.startDate||""}); setShowEditFinances(true); };
  const [showAddDebt, setShowAddDebt] = useState(false);
  const [showAddBill, setShowAddBill] = useState(false);
  const [showAddMed, setShowAddMed] = useState(false);
  const [showAddCheckup, setShowAddCheckup] = useState(false);
  const [newDebt, setNewDebt] = useState({name:"",balance:"",monthly_payment:"",apr:""});
  const [newBill, setNewBill] = useState({name:"",amount:"",due_day:"",status:"upcoming",category:"other",autopay:false});
  const [editBill, setEditBill] = useState(null);
  const [debtMethod, setDebtMethod] = useState("avalanche"); // "avalanche" | "snowball"
  const [plaidDebtAprs, setPlaidDebtAprs] = useState({});
  const [debtBudget, setDebtBudget] = useState(0);
  const [cfMonth, setCfMonth] = useState(new Date().getMonth());
  const [paySchedule, setPaySchedule] = useState({ freq: "biweekly", day1: 1, day2: 15, weekday: 5, startDate: "" });
  // Irregular income (2026-10-14): the user's own word (finances.income_type_override,
  // 'regular' | 'irregular' | null) wins over the transaction-based detection.
  const [incomeTypeOverride, setIncomeTypeOverride] = useState(null);
  const [incomeTypeSaving, setIncomeTypeSaving] = useState(false);
  const incomeTypeSaverRef = useRef(null);
  // True only when a schedule has been PERSISTED (loaded from finances or just
  // saved). The local defaults above are indistinguishable from a saved
  // biweekly schedule, and the old "is it set up" test (paycheckAmount > 0 ||
  // startDate) checked the biweekly-only anchor and the optional amount — so a
  // saved semimonthly schedule (day1/day2 only) rendered PAY markers on the
  // calendar while the card above it still showed the Set Up empty state.
  const [payScheduleSaved, setPayScheduleSaved] = useState(false);
  // Payday set once: Edit reopens the settings; a dismissed "did it change?" stays dismissed this session.
  const [payEditOpen, setPayEditOpen] = useState(false);
  const [payChangeDismissed, setPayChangeDismissed] = useState(false);
  const [paycheckAmount, setPaycheckAmount] = useState(0);
  // freq: "weekly" | "biweekly" | "semimonthly" | "monthly"
  // semimonthly: day1 + day2 each month
  // monthly: day1 only
  // weekly/biweekly: weekday (1=Mon..5=Fri) + startDate to anchor
  const [showHealthBreakdown, setShowHealthBreakdown] = useState(false);
  const [showWellnessBreakdown, setShowWellnessBreakdown] = useState(false);
  const [showFinanceBreakdown, setShowFinanceBreakdown] = useState(false);
  const [showHabitsBreakdown, setShowHabitsBreakdown] = useState(false);
  const [newMed, setNewMed] = useState({name:"",dose:"",schedule:"Daily",refill_days:30});
  const [newCheckup, setNewCheckup] = useState({name:"",last_date:"",next_date:"",urgent:false});
  const [financeForm, setFinanceForm] = useState({income:"",expenses:"",savings:"",pay_amount:"",pay_freq:"biweekly",pay_day1:"1",pay_day2:"15",pay_weekday:"5",pay_start:""});
  const [creditDetails, setCreditDetails] = useState({ cc_balance:0, cc_limit:0, credit_age_years:0, num_accounts:0, hard_inquiries:0, family_size:1, has_student_loans:false, is_first_time_buyer:false, employer_has_401k:false });
  const [showEditCreditDetails, setShowEditCreditDetails] = useState(false);
  const [creditDetailForm, setCreditDetailForm] = useState({});
  // eslint-disable-next-line no-unused-vars
  const creditFactors = calcCreditFactors(debts, habits, monthlyIncome, creditDetails);
  const [showUpdateScore, setShowUpdateScore] = useState(false);
  const [newScoreInput, setNewScoreInput] = useState("");
  const [simActions] = useState({ paydown: 0, newCard: false, missedPayment: false, oldAccount: false });
  // ── WELLNESS STATE ──
  const [moodHistory, setMoodHistory] = useState(isDemo ? DEMO.moodHistory : []);
  const [todayMood, setTodayMood] = useState(null);
  const [todayNote, setTodayNote] = useState("");
  const [moodLogged, setMoodLogged] = useState(false);
  // The SAVED score for today (what the row holds), separate from the draft
  // `todayMood` the grid edits; the logged card shows this one (2026-10-15:
  // the card showed a 10 the user had tapped after logging a 9).
  const [savedMood, setSavedMood] = useState(null);
  const [moodSaving, setMoodSaving] = useState(false);
  const moodSaverRef = useRef(null); // one-per-day saver with the in-flight guard (src/lib/moodSave.js)
  // One-tap mood sheet (evening-mood push deep-link + a quick-log surface).
  const [showMoodSheet, setShowMoodSheet] = useState(false);
  const [moodSheetNote, setMoodSheetNote] = useState("");
  const [moodSheetNoteOpen, setMoodSheetNoteOpen] = useState(false);
  const [checkInStep, setCheckInStep] = useState(0); // 0=idle,1=mood,2=phq,3=gad,4=done
  const [phqAnswers, setPhqAnswers] = useState([null,null]);
  const [gadAnswers, setGadAnswers] = useState([null,null]);
  // Deterministic crisis-resource card (src/lib/crisisDetect.js). Set by the
  // check-in note and journal save paths for EVERY user: no AI, no consent
  // gate, works in demo. Shows 988 and the Crisis Text Line, nothing else.
  const [showCrisisResources, setShowCrisisResources] = useState(false);
  const [checkInResults, setCheckInResults] = useState(null);
  const [wellnessAiLoading, setWellnessAiLoading] = useState(false);
  const [wellnessMsg, setWellnessMsg] = useState(null);
  // ── JOURNAL STATE ──
  const [journalEntries, setJournalEntries] = useState(isDemo && DEMO.journalEntries ? DEMO.journalEntries : []);
  const [journalText, setJournalText] = useState("");
  const [journalMood, setJournalMood] = useState(null);
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalCoachEnabled, setJournalCoachEnabled] = useState(isDemo && DEMO.journalCoachEnabled ? DEMO.journalCoachEnabled : false);
  const [wellnessSubTab, setWellnessSubTab] = useState("overview");
  const [supplements, setSupplements] = useState(isDemo ? DEMO.supplements : INITIAL_SUPPLEMENTS);
  const [showAddSupp, setShowAddSupp] = useState(false);
  const [newSupp, setNewSupp] = useState({ name:"", dose:"", timing:"Morning" });
  const [suppJustLogged, setSuppJustLogged] = useState(null);
  const [workouts, setWorkouts] = useState(isDemo ? (DEMO.workouts || []) : []);
  const [dailyActivity, setDailyActivity] = useState([]);
  // Measured sleep nights (asleep_min set), for the Health pillar's Sleep tracker (2026-10-14).
  const [sleepNights, setSleepNights] = useState([]);
  const [healthLastSync, setHealthLastSync] = useState(null); // ISO string or null
  const [showAddWorkout, setShowAddWorkout] = useState(false);
  const [newWorkout, setNewWorkout] = useState({ type:"Lift", customType:"", name:"", duration_min:"", distance_mi:"", notes:"", date: localDateStr() });
  const [workoutError, setWorkoutError] = useState("");
  const [workoutDeleteConfirm, setWorkoutDeleteConfirm] = useState(null);
  const [username, setUsername] = useState(isDemo ? DEMO.username : "");
  const [coachName, setCoachName] = useState("Kai");
  const [obCoachName, setObCoachName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null); // base64 or data URL
  // ── GAMIFICATION STATE ──
  const [progress, setProgress] = useState(() => {
    if (isDemo && DEMO.habits?.length) {
      const maxStreak = Math.max(...DEMO.habits.map(h => h.streak || 0));
      return { xp: 1240, level:"Hustler", daily_streak: maxStreak, longest_streak: maxStreak, last_active_date: localDateStr(), badges:["first_log","streak7","hustler"] };
    }
    return { xp:0, level:"Rookie", daily_streak:0, longest_streak:0, last_active_date:null, badges:[] };
  });
  const [, setXpPopup] = useState(null); // { amount, reason }
  const [, setLevelUpPopup] = useState(null);

  // ── BODY STATS & PROFILE ──
  // height_in / sex / activity_level dropped from the default shape — no
  // longer collected or read; loaded DB rows may still carry them, unused.
  const [bodyStats, setBodyStats] = useState(isDemo && DEMO.bodyStats ? DEMO.bodyStats : { age:"", current_weight:"", goal_weight:"", goal_type:"fat_loss", goal_date:"", goal_label:"" });
  const [weightLog, setWeightLog] = useState(isDemo && DEMO.weightLog ? DEMO.weightLog : []);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState(isDemo && DEMO.bodyStats ? {...DEMO.bodyStats, age:String(DEMO.bodyStats.age||""), current_weight:String(DEMO.bodyStats.current_weight||""), goal_weight:String(DEMO.bodyStats.goal_weight||"")} : { age:"", current_weight:"", goal_weight:"", goal_type:"fat_loss", goal_date:"", goal_label:"" });
  const [logWeightVal, setLogWeightVal] = useState("");
  // Birth year (profiles.birth_year); age is computed from it, never stored (src/lib/birthYear.js).
  const [birthYear, setBirthYear] = useState(null);
  const [birthYearEstimated, setBirthYearEstimated] = useState(false);
  // ── GOALS STATE ──
  const [goals, setGoals] = useState(isDemo && DEMO.goals ? DEMO.goals : []);
  // Screen Time day records (screen_time_days) — facts only, synced from the
  // App Group by services/screenTime.js. Fed to getGoalAutoValue's
  // screen_time branch; absence of today's row is UNKNOWN, never success.
  const [screenTimeDays, setScreenTimeDays] = useState([]);
  // Native plugin status (authorization + app selection). Drives the goal
  // card's "Allow Screen Time" access-needed state — the screen-time twin of
  // Plaid's noSource/connect pattern.
  const [stStatus, setStStatus] = useState(null);
  // profiles.is_demo (the seeded App Store screenshot account, scripts/seed-demo.mjs).
  // Screen Time cannot authorize in the simulator, so for a demo account the
  // card + screen_time goal render from the seeded screen_time_days rows under
  // DEMO_ST_STATUS, and the native refresh (owner reconcile, arming, App Group
  // sync) never runs — display only. Real accounts never take this path.
  const [isDemoAccount, setIsDemoAccount] = useState(false);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [goalError, setGoalError] = useState("");
  // Inline save failures for the Profile tab fields and the bills list. These
  // writes previously discarded their error and updated local state anyway, so
  // the UI showed a saved value the database had rejected.
  const [profileError, setProfileError] = useState("");
  const [billError, setBillError] = useState("");
  const [disconnectError, setDisconnectError] = useState("");
  // One shared transient banner. Used for failed saves and for the messages the
  // API returns on 429/503/413, which previously had nowhere to be displayed.
  const [toast, setToast] = useState("");
  const [toastTone, setToastTone] = useState("error");
  // True when the timezone could not be persisted — the user gets no notifications
  // until it lands, so this is shown rather than left in the console.
  const [timezoneWarning, setTimezoneWarning] = useState(false);
  const [showUpdateGoal, setShowUpdateGoal] = useState(null); // goal object
  const [goalUpdateVal, setGoalUpdateVal] = useState("");
  const [newGoal, setNewGoal] = useState({ title:"", category:"fitness", current_value:"0", target_value:"", unit:"", deadline:"", shape:"target" });
  const [editGoalId, setEditGoalId] = useState(null); // non-null → Add Goal modal is in edit mode for this goal
  // Add Goal flow view: templates are the front door ('gallery'), a tap
  // opens the one-number 'confirm' screen, and 'custom' is the secondary
  // full form. Edit mode bypasses all three (renders the form directly).
  const [goalView, setGoalView] = useState("gallery");
  const [goalTpl, setGoalTpl] = useState(null); // the tapped GOAL_TEMPLATES entry, for confirm-screen labels
  const [recoveryTrackers, setRecoveryTrackers] = useState(isDemo && DEMO.recoveryTrackers ? DEMO.recoveryTrackers : []);
  const [showAddRecovery, setShowAddRecovery] = useState(false);
  const [habitTab, setHabitTab] = useState("templates"); // "templates" | "custom"
  const [customHabit, setCustomHabit] = useState({ label:"", target:"5", unit:"times/week" });
  // ── PLAID STATE ──
  const [plaidConnected, setPlaidConnected] = useState(isDemoMode ? true : false);
  const [plaidAccounts, setPlaidAccounts] = useState(isDemoMode ? DEMO.plaidAccounts : []);
  const [plaidTransactions, setPlaidTransactions] = useState(isDemoMode ? DEMO.plaidTransactions : []);
  // ISO string of when Plaid ACCOUNT BALANCES were last refreshed (separate
  // from the transactions/blob timestamp — they diverge). Gates Kai's payload
  // (12h) and the Finance-tab "as of" affordance (6h). Demo is always "fresh".
  const [plaidBalancesUpdatedAt, setPlaidBalancesUpdatedAt] = useState(isDemoMode ? new Date().toISOString() : null);
  // When the bank was LINKED (plaid_connections.created_at, via the API).
  // Feeds the initial-import guard: Plaid's first transaction pull is often
  // partial for hours after linking, so rates derived from transactions are
  // withheld from Kai and the briefing until the link is a day old or the
  // history clearly spans a month. Demo is always "ready".
  const [plaidConnectedAt, setPlaidConnectedAt] = useState(isDemoMode ? new Date(Date.now() - 30 * 86400000).toISOString() : null);
  const plaidConnectedAtRef = useRef(isDemoMode ? new Date(Date.now() - 30 * 86400000).toISOString() : null);
  // User-confirmed inflow classifications (income_rules rows). Keyed by
  // normalizeInflowName; read rules-first by plaidDerivedIncome. Never demo.
  const [incomeRules, setIncomeRules] = useState([]);
  // Subscription answers (recurring_rules) — the outflow mirror of incomeRules.
  const [recurringRules, setRecurringRules] = useState([]);
  // Subscriptions card: list open, row being edited, manual-add form.
  // Collapsible cards (2026-10-22): null = the default (open only when something
  // needs attention); a tap remembers the choice per device (localStorage).
  const readCardPref = (k) => { try { const v = user?.id ? localStorage.getItem(userKey(user.id, k)) : null; return v === "1" ? true : v === "0" ? false : null; } catch { return null; } };
  const [subsOpen, setSubsOpenState] = useState(() => readCardPref("subs_card_open"));
  const [billsOpen, setBillsOpenState] = useState(() => readCardPref("bills_card_open"));
  const setCardPref = (k, setter) => (next) => { setter(next); try { if (user?.id) localStorage.setItem(userKey(user.id, k), next ? "1" : "0"); } catch {} };
  const setSubsOpen = setCardPref("subs_card_open", setSubsOpenState);
  const setBillsOpen = setCardPref("bills_card_open", setBillsOpenState);
  const [subEdit, setSubEdit] = useState(null);
  const [subAdd, setSubAdd] = useState(null);
  const [subsHiddenOpen, setSubsHiddenOpen] = useState(false);
  const [forgetConfirm, setForgetConfirm] = useState(null); // matchName awaiting the Forget confirm
  const [possibleAdding, setPossibleAdding] = useState(null); // { matchName, name } while naming a Possible row
  const [plaidLoading, setPlaidLoading] = useState(false);
  const [plaidFetching, setPlaidFetching] = useState(false);
  // Refs mirror Plaid state and are updated synchronously so buildUserContext
  // always reads the latest fetched values, never a stale render-cycle closure.
  const plaidConnectedRef    = useRef(isDemoMode);
  const plaidAccountsRef     = useRef(isDemoMode ? DEMO.plaidAccounts : []);
  const plaidTransactionsRef = useRef(isDemoMode ? DEMO.plaidTransactions : []);
  const plaidBalancesUpdatedAtRef = useRef(isDemoMode ? new Date().toISOString() : null);
  const [plaidError, setPlaidError] = useState(false);
  // Distinct from plaidError: the bank connection expired and needs
  // re-authentication. Retrying cannot fix it, so it must not offer a retry.
  const [plaidReauth, setPlaidReauth] = useState(false);
  // Linked banks, token-free, from the server (multi-bank, 2026-10-13):
  // [{ item_id, institution_name, institution_id, connected_at, needs_reauth }].
  // Drives the grouped Accounts section, per-bank Disconnect/Reconnect, and
  // the "Connect another bank" button (hidden at 3).
  const [plaidItems, setPlaidItems] = useState([]);
  // Honest Refresh feedback (2026-09-16): a rate-limited or failed manual
  // refresh used to be silent for a connected user (the error banner was gated
  // on !plaidConnected). plaidRateLimited holds the 429 message; plaidRefreshMsg
  // is the transient button result of a MANUAL refresh ('updated' or
  // 'balance_stale' — transactions refreshed but the balance did not).
  const [plaidRateLimited, setPlaidRateLimited] = useState(null); // { message } | null
  const [plaidRefreshMsg, setPlaidRefreshMsg] = useState(null);   // 'updated' | 'balance_stale' | null
  const plaidRefreshMsgTimer = useRef(null);
  const [plaidRetry, setPlaidRetry] = useState(0);
  const [plaidTxLimit, setPlaidTxLimit] = useState(15);
  const [financeSubTab, setFinanceSubTab] = useState("overview");
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [editHabit, setEditHabit] = useState(null); // habit object being edited
  const [deleteHabitId, setDeleteHabitId] = useState(null);
  const [newRecovery, setNewRecovery] = useState({ substance:"alcohol", custom:"", start_date: localDateStr() });

  // ── DELETE ACCOUNT STATE ──
  // showCancelSubStep: the cancel-your-subscription-first step before the two
  // confirmations (src/lib/paywall.js needsCancelSubscriptionStep). deleteDone:
  // the account is gone; shown only when the Apple subscription is still
  // active so the Settings line is read before the redirect.
  const [showCancelSubStep, setShowCancelSubStep] = useState(false);
  const [deleteDone, setDeleteDone] = useState(false);
  const [showDeleteConfirm1, setShowDeleteConfirm1] = useState(false);
  const [showDeleteConfirm2, setShowDeleteConfirm2] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── DATA EXPORT STATE ──
  const [exportLoading, setExportLoading] = useState(false);

  // ── MONEY-GOAL AUTO-TRACK UPGRADE ──
  // When a bank is connected with manual $ goals already present, offer ONCE
  // to switch each to automatic tracking. Never silent (the offer is a modal);
  // dismissing sets a per-user localStorage flag so it does not nag again.
  const [moneyUpgrade, setMoneyUpgrade] = useState(null); // candidate goals, or null
  const moneyUpgradeShown = useRef(false);

  // ── AI CONSENT STATE ──
  const [aiConsent, setAiConsent] = useState(() => { try { const k = userKey(user?.id, 'ai_consent'); return k && localStorage.getItem(k) === "true"; } catch { return false; } });
  const [showAiConsentModal, setShowAiConsentModal] = useState(false);

  // ── APP LOCK STATE (native only) ──
  // Mirrors the device-local Keychain record for display; the record itself
  // is the source of truth and never touches Supabase or localStorage.
  const [appLockEnabled, setAppLockEnabled] = useState(false);
  const [showAppLockModal, setShowAppLockModal] = useState(false);
  const [appLockChangingPin, setAppLockChangingPin] = useState(false);
  useEffect(() => {
    if (!isLockCapable() || isDemo) return;
    // Ownership-checked read: a record left by a reinstall or another
    // account resolves to null (and is cleared), so the Profile row can
    // never show Change PIN against a stale record.
    const refresh = () => resolveLockStateForUser(user?.id).then((s) => setAppLockEnabled(!!s?.enabled));
    refresh();
    // LockGate's one-time setup prompt (and anything else outside this
    // component) announces changes on this event; without it the Profile row
    // would keep showing the state read at mount.
    window.addEventListener('scorify-lock-changed', refresh);
    return () => window.removeEventListener('scorify-lock-changed', refresh);
  }, [isDemo]);

  // Auto-link goals to real data
  // Sum of live Plaid balances matching pred, or null when unavailable/stale
  // (12h rule via plaidBalancesUpdatedAt). MATCHED PAIR with plaidBalanceSum
  // in api/lib/goal-progress.js. Returns null (not 0) so callers distinguish
  // "no fresh balance" from a real zero.
  const plaidBalanceSum = (pred) => {
    if (!plaidConnected) return null;
    const ts = balanceFreshAt; // Plaid's clock (bankSyncedAt below), not our write time
    if (!ts || Date.now() - new Date(ts).getTime() >= 12 * 60 * 60 * 1000) return null;
    const matched = (plaidAccounts || []).filter(pred);
    if (matched.length === 0) return null;
    return matched.reduce((s, a) => s + (Number(a.balances?.current) || 0), 0);
  };
  const liveCreditBalance = () => (plaidAccounts || []).filter(a => a.type === 'credit').reduce((s, a) => s + (Number(a.balances?.current) || 0), 0);
  // Stored balances are a snapshot. The time that matters is when PLAID last
  // pulled from the bank (plaid_synced_at per Item, from /item/get; the
  // OLDEST across the user's banks, src/lib/plaidItemHealth.js), not when our
  // sync wrote the cache: that write stamp advanced daily over two-day-old
  // Wells Fargo data (2026-10-29). Items never status-checked (an older cache)
  // leave the time unknown: no label, and the freshness gates fall back to the
  // write time until the next sync. Demo mode shows nothing (no bank snapshot).
  const bankSync = bankSyncedAt(plaidItems);
  const balanceFreshAt = bankSync.known ? bankSync.at : plaidBalancesUpdatedAt;
  const balanceFreshAtRef = useRef(null);
  useEffect(() => { balanceFreshAtRef.current = balanceFreshAt; }, [balanceFreshAt]);
  const balanceAsOf = isDemoMode ? null : balanceAsOfLabel(bankSync.known ? bankSync.at : null);
  // Banks that need the user: an Item error from Plaid, or no Plaid refresh in
  // 24h. Drives the "needs attention" banner (Money tab, Home card) and the
  // per-bank Reconnect; Kai sees the same Items as stale through bank_synced_at.
  const bankAttentionItems = isDemoMode ? [] : (plaidItems || []).filter(i => i && i.needs_attention);
  // Credit-payoff target must never exceed the balance that exists, or the goal
  // can never reach 100% (device: target 4000 vs start_value 3861.30). Cap at
  // the live balance (paying down PART is fine; targeting more than exists is
  // not), and default to the full balance when no valid target was entered.
  const capCreditTarget = (entered, balance) => {
    const t = parseFloat(entered);
    return (isFinite(t) && t > 0) ? Math.min(t, balance) : balance;
  };

  // A manual $ goal's likely binding, inferred from its title: debt/payoff
  // words → credit payoff, else a savings-balance goal.
  const moneyGoalKind = (g) => /debt|credit|card|loan|pay ?off|owe/i.test(g.title || "") ? 'credit' : 'savings';
  // Switch a manual money goal to automatic tracking (explicit user action,
  // never silent). Credit payoff captures the live balance NOW as start_value.
  const convertGoalToAuto = async (g, kind) => {
    // Credit: cap the target at the live balance, same as the create paths — a
    // manual goal upgraded here kept its old target (device: roccovasq+1 had
    // target 4000 vs start 3861.30, unreachable). start_value anchors the
    // reduction resolver.
    const ccBal = kind === 'credit' ? liveCreditBalance() : 0;
    const changes = kind === 'credit'
      ? { source_type: 'credit_payoff', start_value: ccBal, target_value: capCreditTarget(g.target_value, ccBal), current_value: 0, unit: '$' }
      : { source_type: 'savings', unit: '$' };
    const ok = await persist('that goal', supabase.from('goals').update(changes).eq('id', g.id));
    if (ok) {
      setGoals(prev => prev.map(x => x.id === g.id ? { ...x, ...changes } : x));
      setMoneyUpgrade(prev => {
        const next = (prev || []).filter(x => x.id !== g.id);
        // Last one converted: close the offer for good. Returning [] would leave
        // an empty-but-truthy array that kept anyModalOpen true and stranded the
        // tab bar (device: build 1.1(7), tapping the last Yes hid the nav until
        // force quit). Null closes it and marks the one-time offer done.
        if (next.length === 0) {
          try { if (user?.id) localStorage.setItem(userKey(user.id, 'money_upgrade_done'), '1'); } catch {}
          return null;
        }
        return next;
      });
    }
  };
  // Offer once: bank connected, manual $ goals present, not previously shown.
  useEffect(() => {
    if (isDemoMode || !user?.id || !plaidConnected || moneyUpgradeShown.current) return;
    try { if (localStorage.getItem(userKey(user.id, 'money_upgrade_done'))) return; } catch {}
    const manual = goals.filter(g => !g.completed && !g.source_type && g.unit === '$' && g.source_config?.manual_shape !== 'cap' && !g.period);
    if (manual.length > 0) { moneyUpgradeShown.current = true; setMoneyUpgrade(manual); }
  }, [plaidConnected, goals, user?.id, isDemoMode]);
  const dismissMoneyUpgrade = () => {
    try { if (user?.id) localStorage.setItem(userKey(user.id, 'money_upgrade_done'), '1'); } catch {}
    setMoneyUpgrade(null);
  };

  // A weekly workout-count goal is LINKED to the Gym / Workout habit: its
  // title and target follow the habit's saved target (src/lib/goalLink.js).
  // Every renderer and the Kai payload go through this; the stored row is
  // untouched. Server parity: fetchUserBundle in api/lib/coach-run.js.
  const viewGoal = (g) => linkWorkoutGoal(g, habits);
  const getGoalAutoValue = (g) => {
    // Workout-count / distance goals — weekly_distance reads daily_activity (walking+running combined)
    if (g.source_type === 'workout') {
      const cfg = g.source_config || {};
      // MATCHED PAIR — the fitness AVAILABILITY GUARD is a three-way contract:
      // this branch, buildContext's fitness gate in api/coach.js, and
      // resolveProgress's workout branch in api/notify-cron.js (parity P1
      // added the cron's). One rule everywhere; change all three or none.
      //
      // The previous premise was "a sync ran and zero source='healthkit' workout rows
      // exist, therefore access was denied". That is false. It equally describes every
      // user who grants access and simply does not record workouts on a watch — passive
      // step/distance tracking fills daily_activity for them while workouts stays empty
      // forever. Those users were pinned in the unavailable state permanently, and
      // logging a workout by hand could not clear it because manual rows are
      // source='manual'.
      //
      // Rows in EITHER table, from ANY source, mean data is reaching the app. The window
      // is a fixed trailing 30 days, deliberately NOT the goal period: "is data flowing"
      // and "how much did you do this week" are different questions, and keying presence
      // to the period would relapse into the unavailable state every Monday morning
      // before the first sync of the week lands.
      const presenceCutoff = localDateStr(new Date(Date.now() - 30 * 86400000));
      const hasFitnessData =
        dailyActivity.some(d => d.date && d.date >= presenceCutoff) ||
        workouts.some(w => w.date && w.date >= presenceCutoff);
      // Both genuinely empty AND a sync has run — the real revoked-access signature.
      // Never synced and no rows is just an empty log, and 0 is the honest answer.
      if (!hasFitnessData && healthLastSync) {
        return { value: 0, label: 'from Apple Health', target: isWorkoutCountGoal(g) ? workoutGoalTarget(g, habits) : (g.period_target ?? g.target_value), unit: '', hasData: false };
      }
      const period = g.period || 'week';
      const now = new Date();
      const todayStr = localDateStr(now);
      let cutoff;
      if (period === 'week') {
        // THE week (src/lib/weekWindow.js): Monday-start in the user's local
        // calendar — the same window the habit card (weekCount) and the
        // cron's habitWeekCount use. This used to be a ROLLING trailing 7
        // days, so a gym log on Saturday still counted toward "this week"
        // here on Thursday while the habit card had dropped it on Monday:
        // 2/4 on the goal, 1/5 on the habit, same two logs (2026-10-15).
        // MATCHED PAIR: resolveProgress in api/lib/goal-progress.js reads the
        // same helper — change both or neither.
        cutoff = localWeekStart(todayStr);
      } else if (period === 'month') {
        cutoff = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      } else {
        cutoff = todayStr; // 'day' — today only
      }
      // Rows dated after today (clock skew, a future-dated manual entry) never count.
      const inWindow = (d) => typeof d === 'string' && d >= cutoff && d.slice(0, 10) <= todayStr;
      if (cfg.metric === 'weekly_distance') {
        // MATCHED PAIR: api/notify-cron.js resolveProgress's weekly_distance
        // branch applies this exact rule (it used to sum daily_activity only,
        // dropping manual workouts from Kai's view) — change both or neither.
        //
        // Two sources, deduplicated by source rather than by date.
        //
        // daily_activity is Apple's passive whole-day distance. A workout recorded on
        // the watch (workouts.source='healthkit') happened during that day and its
        // distance is ALREADY inside the daily_activity figure, so adding healthkit
        // workout rows would double-count every watch-tracked run. Manual rows are the
        // opposite case: nothing else in the system knows about them, so without adding
        // them a hand-logged run could never count toward a distance goal at all.
        //
        // Hence: all daily_activity distance, plus distance from non-healthkit workouts
        // only. Residual overlap is possible and accepted — if the user carried their
        // phone on a run and then also logged it by hand, the passive miles and the
        // manual miles both land in the sum. Nothing in the data distinguishes that from
        // a treadmill run the phone never saw, and under-counting a real workout is the
        // worse failure of the two.
        const actMeters = dailyActivity
          .filter(d => inWindow(d.date))
          .reduce((s, d) => s + (Number(d.distance_m) || 0), 0);
        const manualMeters = workouts
          .filter(w => inWindow(w.date) && w.source !== 'healthkit')
          .reduce((s, w) => s + (Number(w.distance_m) || 0), 0);
        const miles = (actMeters + manualMeters) / 1609.34;
        return { value: parseFloat(miles.toFixed(1)), label: 'daily distance, plus logged workouts', target: g.period_target ?? g.target_value, unit: 'mi', hasData: true };
      }
      if (cfg.metric === 'weekly_steps') {
        // MATCHED PAIR: resolveProgress's weekly_steps branch in
        // api/lib/goal-progress.js. Steps exist only in daily_activity
        // (Apple's passive count); workouts carry none.
        const steps = dailyActivity.filter(d => inWindow(d.date)).reduce((s, d) => s + (Number(d.steps) || 0), 0);
        return { value: steps, label: 'from Apple Health steps', target: g.period_target ?? g.target_value, unit: 'steps', hasData: true };
      }
      // MATCHED PAIR: resolveProgress's count-goal branch in
      // api/lib/goal-progress.js applies this exact rule — change both or neither.
      //
      // Count goals count SESSIONS (src/lib/workoutSessions.js, 2026-10-27): every
      // workouts row in the window, a HealthKit/manual twin of one session collapsed,
      // plus one per Gym / Workout habit day with no row. Ticking the habit used to
      // count for nothing here, so a user who trains and ticks the box daily still saw
      // 0/4; then the fix counted distinct DAYS, so Muay Thai plus a lift on one day
      // read 1/4. A tick on a day already logged is still one workout, not two.
      //
      // Nothing is written to `workouts`. A checkbox must not manufacture rows in a table
      // that records real sessions: that would leave null-duration phantoms behind,
      // poison any future average-duration stat, and require un-ticking to delete a row.
      //
      // The habit is identified by TEMPLATE ID ('gym', stored in the habits.name column),
      // never by display name. saveEditHabit only ever updates label/icon/target/unit and
      // keys the update by name, so the slug survives any rename — matching on the label
      // "Gym / Workout" would silently break the moment someone renamed it. Only this one
      // habit counts; no other template feeds a workout count.
      const gymHabit = habits.find(h => h.id === 'gym' && h.active);
      const sessions = countWorkoutSessions({
        workouts: workouts.filter(w => inWindow(w.date)),
        gymHistory: gymHistoryOf(gymHabit).filter(d => inWindow(d)),
      });
      const count = sessions.count;
      // Label earns its keep on the Home widget, where "3 / 4" can sit next to two
      // visible workout cards. Only claim habits when they actually added a session.
      const countLabel = sessions.fromHabits > 0 ? 'from workouts + habits' : 'from your workouts';
      // Target from the Gym / Workout habit when there is one (goalLink.js).
      return { value: count, label: countLabel, target: workoutGoalTarget(g, habits), unit: '', hasData: true };
    }
    // Savings-BOUND goals (source_type 'savings' — the onboarding chips).
    // MATCHED PAIR with resolveProgress's savings branch in
    // api/lib/goal-progress.js: both read the live savings balance; hasData
    // mirrors the server's `finances != null` (a missing finances row renders
    // as unknown, never $0). This is an explicit BINDING branch, not the
    // removed P3 heuristic: it keys on source_type the user opted into, never
    // on category/unit/title guesses. Added 2026-09-10 when the P3 removal
    // was found to have orphaned row 7's client half — without it the app
    // rendered stale current_value under a modal disclosure promising
    // "tracks automatically from your savings balance".
    // MATCHED PAIR with resolveProgress's savings branch in
    // api/lib/goal-progress.js — change both or neither. Reach-a-total-balance
    // goal: live Plaid savings balance (savings-subtype depository accounts)
    // when connected and fresh, else the user-entered finances.savings. A
    // stale Plaid balance falls back to the manual figure, never a drifted
    // reading.
    if (g.source_type === 'savings') {
      const plaidSavings = plaidBalanceSum(a => a.type === 'depository' && a.subtype === 'savings');
      const value = plaidSavings != null ? plaidSavings : savings;
      // hasData keys on savingsKnown, NOT on the finances row existing: a row
      // saved with savings left blank stores NULL, and unknown must never
      // render as $0 progress (review M3). MATCHED PAIR with resolveProgress's
      // savings branch in api/lib/goal-progress.js (manual is null-aware there).
      return { value, label: plaidSavings != null ? 'from your bank' : 'from your savings', target: g.target_value, unit: '$', hasData: plaidSavings != null || savingsKnown };
    }
    // MATCHED PAIR with resolveProgress's credit_payoff branch in
    // api/lib/goal-progress.js — change both or neither. Reduction goal:
    // paid = start_value (balance at bind) minus live credit balance, clamped
    // [0, target]; overBy = how far the balance has risen ABOVE the start, so
    // the card can say "balance is up $N since you started" instead of a fake
    // 0%. Balance stale (>12h) or no anchor -> hasData:false (shows stale).
    if (g.source_type === 'credit_payoff') {
      const start = g.start_value != null ? Number(g.start_value) : null;
      const target = Number(g.target_value || 0);
      const bal = plaidBalanceSum(a => a.type === 'credit');
      if (bal == null || start == null) {
        return { value: 0, label: null, target, unit: '$', hasData: false, noSource: !plaidConnected ? 'plaid' : undefined };
      }
      const paid = Math.max(0, Math.min(start - bal, target));
      const overBy = Math.max(0, bal - start);
      return { value: paid, label: 'from your bank', target, unit: '$', hasData: true,
        note: overBy > 0 ? `Balance is up $${Math.round(overBy).toLocaleString()} since you started` : undefined };
    }
    // MATCHED PAIR with resolveProgress's plaid_spend branch in
    // api/lib/goal-progress.js — change both or neither. Same local-month
    // window, same SPEND_EXCLUDED members, same source_config
    // category/detailed filters, and the same legacy-category fallback
    // (added both sides 2026-09-10, parity P6 — server sites read it via
    // lib/spend.js's spendCategory): a legacy-shape transfer is excluded
    // from the cap everywhere or nowhere.
    if (g.source_type === 'plaid_spend') {
      const cfg = g.source_config || {};
      const now = new Date();
      // Local month prefix matches rest of file (localDateStr uses local getMonth/getDate, not UTC)
      const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const sum = plaidTransactions.filter(t => {
        const txDay = txLocalDay(t);
        if (!txDay || !txDay.startsWith(yearMonth)) return false;
        // THE shared test (src/lib/spend.js) — the server resolver's own, so
        // category, detailed and scope ('everyday' custom caps) can't drift.
        return txMatchesSpendGoal(t, cfg);
      }).reduce((acc, t) => acc + t.amount, 0);
      return {
        value: Math.round(sum * 100) / 100,
        label: cfg.scope === 'everyday' ? (cfg.include_bills ? 'spending + bills, from your bank' : 'everyday spending, from your bank') : 'from Plaid',
        target: g.period_target ?? g.target_value,
        unit: '$',
        hasData: plaidConnected, // true even when sum=0 (real zero is valid)
        // "syncing" must mean a connection EXISTS and data is loading, nothing
        // else. With no bank connected a cap showed "syncing" forever with no
        // explanation and no way out. noSource makes the renderers say so and
        // offer the connect control instead.
        noSource: !plaidConnected ? 'plaid' : undefined,
        inverted: true,
      };
    }
    // MATCHED PAIR with resolveProgress's screen_time branch in
    // api/lib/goal-progress.js — change both or neither. Screen Time is a
    // THRESHOLD-EVENT source: there are no minute totals (Apple's report
    // extension cannot export them), only delivered callbacks. Derivation,
    // both sides: cap_crossed_at = OVER; closed_at (natural day end) with no
    // crossing = UNDER; today-in-progress = under so far; NO row for the day
    // = hasData:false, never success. `value` is the known USAGE FLOOR
    // (0 → heartbeat → midpoint → cap), which understates real usage by
    // design — so the renderers show stateLine, never a bare number that
    // would overstate success on an inverted cap.
    if (g.source_type === 'screen_time') {
      const todayStr = localDateStr();
      const row = screenTimeDays.find(d => d.date === todayStr);
      const capMin = Number(row?.cap_minutes ?? g.period_target ?? g.target_value ?? 0);
      const fmtT = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      // Access-needed beats EVERYTHING, row or no row. A screen_time_days row
      // means "we wrote something for today" (the on-open sync upserts the cap)
      // — NOT "monitoring is running". Conflating the two let a wiped Family
      // Controls authorization (notDetermined / denied / no selection — a
      // reinstall or an OS update, SCORIFY-APP-6) render as "monitoring active"
      // over a stale empty row, hiding the repair control in exactly the state
      // it exists for. So the auth + selection check runs FIRST, independent of
      // the row: an unready source is the actionable noSource state (GoalCard's
      // noSource branch renders the Allow control), never a stateLine.
      const stNotReady = native && stStatus && (!stStatus.available || stStatus.authorization !== "approved" || !stStatus.hasSelection);
      if (stNotReady) {
        return { value: 0, label: 'from Screen Time', target: capMin, unit: 'min', hasData: false, inverted: true, noSource: 'screen_time' };
      }
      if (!row) {
        // Ready (approved + selected) but no row for today yet: honest "not
        // running, open the app", never a fabricated number.
        return { value: 0, label: 'from Screen Time', target: capMin, unit: 'min', hasData: false, inverted: true,
          stateLine: 'No data today. Tracking is not running. Open the app on your iPhone.' };
      }
      // OVER comes from the cap window's delivered crossing OR from the usage
      // ladder standing at or past the cap (2026-10-17). The ladder counts from
      // the usage window and survives a cap edit; a mid-day cap edit re-arms the
      // cap window, whose threshold then only counts from that moment (Oct 13:
      // 8h → 5h at 9:08 PM after ~8.5h of use; no crossing could fire). A
      // clamped, non-overcounted ladder is a true floor, so ladder >= cap is
      // usage >= cap. MATCHED PAIR: ladderAtCap in api/lib/goal-progress.js.
      const usage = reconstructScreenTimeUsage(row);
      const usageMin = usage && !usage.overcount ? usage.minutes : null;
      const capNum = Number(row.cap_minutes) || 0;
      const ladderOver = capNum > 0 && usageMin != null && usageMin >= capNum;
      const crossed = !!row.cap_crossed_at || ladderOver;
      // MATCHED PAIR with the resolver's cap-armed guard: cap_minutes 0/null
      // means the cap events were never registered on this row's window (a
      // usage-only re-arm after a reinstall, before the goal's cap was
      // restored — device 2026-09-16). No watcher was running, so the day
      // can never read as under; the self-healing arm effect restores the
      // cap on open and the row heals on the next sync.
      if (!crossed && !Number(row.cap_minutes)) {
        return { value: 0, label: 'from Screen Time', target: capMin, unit: 'min', hasData: false, inverted: true,
          stateLine: 'Your limit was not being tracked today. Tracking restarts automatically.' };
      }
      // PROGRESS uses the usage LADDER (2026-09-27): the same reconstruction
      // the Health card shows, so the goal bar reads "3h 30m of 8h so far"
      // instead of sitting empty until the cap window's midpoint event. Like
      // the old event floor, the ladder is a stepped floor — between steps it
      // can understate usage by up to 30 minutes (the old floor's gaps were
      // half the cap), so OVER still comes only from the cap window's own
      // delivered event, never from the ladder reaching the cap. Overcounted
      // ladders (the iOS false-fire class, clamped at read) fall back to the
      // event floor. Crossed value stays >= capMin + 1 so both renderers key
      // "over" on value > target.
      const eventFloor = crossed ? capMin + 1
        : row.midpoint_at ? Math.round(capMin / 2)
        : row.heartbeat_at ? (row.heartbeat_minutes || 5)
        : 0;
      const floor = crossed ? Math.max(capMin + 1, usageMin || 0)
        : (usageMin != null ? usageMin : eventFloor);
      // PARTIAL-DAY coverage: armed more than 15 minutes after the day's
      // local midnight means usage before arming was UNSEEN — the arming
      // afternoon of day one, or an app-open re-arm after a broken chain.
      // A partial day can be OVER (a crossing is proof regardless) but can
      // NEVER close into a confirmed under-day: that would render unknown
      // as success. Chained windows open at 00:00:30, so every normal day
      // is full. MATCHED PAIR: screenTimeDayStatus in api/lib/goal-progress.js
      // applies the same 15-minute rule for the retrospective derivation.
      // Base on the CAP window's own start (usage/cap split): a mid-day cap
      // edit re-arms only the cap window, and the goal counts from that new
      // arm. Falls back to armed_at for pre-split rows.
      const capArmedAt = row.cap_armed_at || row.armed_at;
      const partial = capArmedAt && (new Date(capArmedAt) - new Date(row.date + 'T00:00:00')) > 15 * 60 * 1000;
      // State line: usage-first when the ladder is measuring, with honest
      // coverage notes; no internal jargon ("monitoring" is banned copy).
      const usagePart = usageMin != null ? `${formatUsageMinutes(usageMin)} of ${formatUsageMinutes(capMin)} so far` : null;
      return {
        value: floor, label: 'from Screen Time', target: capMin, unit: 'min', hasData: true, inverted: true, partial,
        stateLine: crossed ? (row.cap_crossed_at ? `Limit crossed at ${fmtT(row.cap_crossed_at)}` : `Over your limit (${formatUsageMinutes(usageMin)} of ${formatUsageMinutes(capMin)} seen)`)
          : row.cap_warning_at ? (usagePart ? `${usagePart} · close to your limit` : `Close to your limit today`)
          : partial ? (usagePart ? `${usagePart} · limit watched since ${fmtT(capArmedAt)}` : `Counting since ${fmtT(capArmedAt)} · earlier today not counted`)
          : (usagePart ? `${usagePart} · under your limit` : `Under your limit so far`),
      };
    }
    // MANUAL goals get NO heuristics — removed 2026-09-10 (parity P3). This
    // block used to layer guesses over current_value for manual goals (savings
    // balance for finance-$/"save" titles, weight-log LOSS delta for lbs goals,
    // habit streak for fitness-days) — client-side ONLY: Kai's payload
    // (buildCoachUserData) and notify-cron's manual resolver both read the
    // stored column, so every heuristic value was a client/server disagreement
    // by construction. Worse, any returned label makes GoalCard treat the goal
    // as live-tracked and HIDE the Update button, freezing current_value at
    // whatever the heuristic papered over ($3,200 of typed emergency-fund
    // progress rendered as $0 "from your savings" on an account with no
    // finances row; a gain-weight goal rendered 0 because the delta assumed
    // loss). Live tracking is explicit opt-in via a real source_type resolved
    // on BOTH sides (workout/plaid_spend above, savings via the goal chips) —
    // do not re-add a guess here without a matching server resolver.
    return null;
  };
  const [showLogWeight, setShowLogWeight] = useState(false);
  // ── COACH STATE ──
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachMsgs, setCoachMsgs] = useState(() => {
    try {
      const uid = user?.id;
      if (!uid) return [];
      const k = userKey(uid, 'coach_msgs');
      const kAt = userKey(uid, 'coach_msgs_savedAt');
      const saved = localStorage.getItem(k);
      const savedAt = localStorage.getItem(kAt);
      if (saved && savedAt) {
        const ageMs = Date.now() - parseInt(savedAt);
        if (ageMs > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(k);
          localStorage.removeItem(kAt);
          return [];
        }
        return JSON.parse(saved);
      }
      return [];
    } catch { return []; }
  });
  const [coachInput, setCoachInput] = useState("");
  const [coachLoading, setCoachLoading] = useState(false);
  const coachInFlight = useRef(false);
  const [coachPulse, setCoachPulse] = useState(true);
  // Hide the FAB while scrolling DOWN (the user is reading), reveal on scroll up,
  // when scrolling stops, and at the top/bottom of any page. This also covers the
  // short-viewport case, which shrinking/repositioning cannot, since a short page
  // has no scroll range to move content out from under a fixed element.
  const [fabHidden, setFabHidden] = useState(false);
  const scrollRef = useRef(null);
  // Partial-load honesty (src/lib/pillarReadiness.js): which inputs have
  // loaded or failed this session, and each pillar's last COMPLETE score
  // (hydrated from the launch snapshot). A pillar recomputes only when all of
  // its inputs are in; otherwise the last complete value stands.
  const [inputsLoaded, setInputsLoaded] = useState(() => new Set());
  const [inputsFailed, setInputsFailed] = useState(() => new Set());
  const inputsLoadedRef = useRef(new Set());
  const [lastCompletePillars, setLastCompletePillars] = useState({});
  const markInput = (key, status) => {
    if (status === "loaded") {
      inputsLoadedRef.current.add(key);
      setInputsLoaded(prev => prev.has(key) ? prev : new Set([...prev, key]));
      setInputsFailed(prev => prev.has(key) ? new Set([...prev].filter(k => k !== key)) : prev);
    } else if (!inputsLoadedRef.current.has(key)) {
      setInputsFailed(prev => prev.has(key) ? prev : new Set([...prev, key]));
    }
  };
  // Collapsing hero on the pillar tabs (one hook, four tabs): the hero row
  // carries heroRef; the top bar swaps to PillarPill while collapsed.
  const heroPillarId = pillarIdForTab(contentTab);
  // heroRef is a callback ref (the hook re-attaches to each tab's hero element).
  const { collapsed: heroCollapsed, heroRef } = useCollapsingHero({ scrollRef, enabled: !!heroPillarId, tabKey: contentTab });
  const coachBottomRef = useRef(null);
  const coachInputRef = useRef(null);
  // ── DAILY BRIEFING STATE ──
  const [briefingJson, setBriefingJson] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingDate, setBriefingDate] = useState(null);
  const [briefingDbId, setBriefingDbId] = useState(null);
  const [briefingFeedback, setBriefingFeedback] = useState(null);
  // Generation failed AND no cached briefing under 24h could be restored —
  // renders the needs-a-connection card instead of any invented content.
  const [briefingError, setBriefingError] = useState(false);
  // null = never chosen; the engine infers a one-time default so no existing
  // user drops to 0%. Once saved, the stored list is authoritative.
  const [enabledTrackers, setEnabledTrackers] = useState(null);
  // { finance:[{week_start,earned,possible,pct}], ... } — per pillar, not composite
  const [scoreHistory, setScoreHistory] = useState({});
  const [briefingDismissed, setBriefingDismissed] = useState(false);
  // Weekly recap (cron-generated, kind='recap' in ai_briefings). Shown as its
  // own card; the daily check-in reuses the briefing card (same JSON shape).
  const [recapJson, setRecapJson] = useState(null);
  const [recapDismissed, setRecapDismissed] = useState(false);
  const lastPlaidCall = useRef(0);
  // Use Plaid-derived income when connected, fall back to manual
  const plaidDerivedIncome = useMemo(() => {
    if (!plaidConnected || plaidTransactions.length === 0) return 0;
    const cutoff30 = daysAgoStr(30);
    const depositoryAccountIds = plaidAccounts
      .filter(a => a.type === "depository")
      .map(a => a.account_id);
    // Income classification is DELIBERATELY loose here. Restricting this to
    // personal_finance_category INCOME was tried and reverted the same day: in
    // real data, paychecks deposited by phone arrive as
    // TRANSFER_IN_DEPOSIT ("MOBILE DEPOSIT" $3,080/$3,200/$7,500...), and
    // Plaid cannot tell a mobile paycheck from any other deposit — the strict
    // filter scored a paid user as having zero income.
    //
    // Safe to exclude (and excluded): LOAN_PAYMENTS credits — the card-side
    // inflow of a payment whose purchases are already counted as spending.
    // NOT excluded, known-ambiguous: TRANSFER_IN_DEPOSIT (may be a paycheck),
    // and TRANSFER_IN_FROM_APPS (self-transfers only when the counterparty
    // matches the account holder — the cache carries counterparties, but we
    // hold no reliable legal name to match against, so no silent guess).
    //
    // income_rules is the user-confirmed layer on top (the "real fix" the
    // paragraph above always pointed at): rules are consulted FIRST by
    // normalized name — a confirmed 'income' name counts regardless of Plaid
    // category, a confirmed 'not_income' name is excluded, and unconfirmed
    // names keep the loose default above, so a user who never answers gets
    // byte-identical numbers to the pre-rules behavior.
    const ruleByName = new Map(incomeRules.map(r => [r.match_name, r.classification]));
    const income = plaidTransactions
      .filter(t => {
        const isDeposit = t.amount < 0;
        const isDepository = depositoryAccountIds.includes(t.account_id);
        const isRecent = txSince(t, cutoff30);
        if (!isDeposit || !isDepository || !isRecent) return false;
        return isCountableInflow(t, ruleByName);
      })
      .reduce((a, t) => a + Math.abs(t.amount), 0);
    return income;
  }, [plaidConnected, plaidTransactions, plaidAccounts, incomeRules]);
  // Recurring-inflow candidates awaiting a confirmation answer. Detection runs
  // over the WHOLE cache (not the 30-day income window — a qualifying pair may
  // straddle it), on deposits into depository accounts only. LOAN_PAYMENTS
  // credits are never candidates (card-payment inflows, never income) and
  // INCOME-categorized rows are auto-trusted (Plaid already knows — asking
  // "is your paycheck income?" would be noise). A candidate is a normalized
  // name with two occurrences ≥$200 each, amounts within 25%, dates 5–35 days
  // apart — one honest band rather than snapped cadences, because real pay
  // gaps (19 days in live data) match no textbook cadence.
  // ── SUBSCRIPTIONS (2026-10-20, src/lib/subscriptions.js) ── detected
  // recurring charges, the ask-once candidates, and the monthly total.
  const recurringDetected = useMemo(() => {
    let zone; try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { zone = undefined; }
    return plaidConnected ? detectRecurringCharges(plaidTransactions, { zone }) : [];
  }, [plaidTransactions, plaidConnected]);
  const recurringAsk = useMemo(() => recurringCandidates(recurringDetected, { rules: recurringRules, bills }), [recurringDetected, recurringRules, bills]);
  const subscriptionsTotal = useMemo(() => subscriptionsMonthlyTotal({ bills, detected: recurringDetected, rules: recurringRules }), [bills, recurringDetected, recurringRules]);
  const subscriptionBills = useMemo(() => bills.filter(isSubscriptionBill), [bills]);
  // Looser "Possible subscriptions" and the "Hidden (N)" not-a-subscription list.
  const possibleSubs = useMemo(() => plaidConnected ? possibleSubscriptions(plaidTransactions, { today: localDateStr(), bills, rules: recurringRules, detected: recurringDetected }) : [], [plaidConnected, plaidTransactions, bills, recurringRules, recurringDetected]);
  const hiddenSubs = useMemo(() => hiddenSubscriptions(recurringRules, plaidTransactions, { bills }), [recurringRules, plaidTransactions, bills]);
  const incomeCandidates = useMemo(() => {
    if (isDemoMode || !user || !plaidConnected || plaidTransactions.length === 0) return [];
    const answered = new Set(incomeRules.map(r => r.match_name));
    const depositoryIds = new Set(plaidAccounts.filter(a => a.type === "depository").map(a => a.account_id));
    const groups = new Map();
    for (const t of plaidTransactions) {
      if (!(t.amount < 0) || Math.abs(t.amount) < 200) continue;
      if (!depositoryIds.has(t.account_id)) continue;
      const cat = t.personal_finance_category?.primary || "";
      if (cat === "LOAN_PAYMENTS" || cat === "INCOME") continue;
      const key = normalizeInflowName(t.name);
      if (!key || answered.has(key)) continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ date: txLocalDay(t), amt: Math.abs(t.amount), name: (t.name || "").trim() });
    }
    const out = [];
    for (const [key, rows] of groups) {
      const gaps = [];
      let avg = 0;
      for (let i = 0; i < rows.length; i++) for (let j = i + 1; j < rows.length; j++) {
        const a = rows[i], b = rows[j];
        const ratio = Math.max(a.amt, b.amt) / Math.min(a.amt, b.amt);
        const gap = Math.abs((new Date(a.date) - new Date(b.date)) / 86400000);
        if (ratio <= 1.25 && gap >= 5 && gap <= 35) { gaps.push(gap); avg = (a.amt + b.amt) / 2; }
      }
      if (!gaps.length) continue;
      gaps.sort((x, y) => x - y);
      const medGap = gaps[Math.floor(gaps.length / 2)];
      const cadence = medGap <= 10 ? "roughly weekly" : medGap <= 17 ? "roughly every two weeks" : medGap <= 23 ? "roughly every few weeks" : "roughly monthly";
      out.push({ matchName: key, displayName: rows[rows.length - 1].name, avg, cadence });
    }
    return out;
  }, [isDemoMode, user, plaidConnected, plaidTransactions, plaidAccounts, incomeRules]);
  // ── INCOME CADENCE (2026-10-14) ──────────────────────────────────────────
  // Every countable deposit in the cache (not just 30 days), classified into
  // regular / irregular / unknown; the override wins. Irregular earners get the
  // honest view (this month vs their own typical range) and NEVER a payday.
  const allCountableDeposits = useMemo(() => {
    if (!plaidConnected || plaidTransactions.length === 0) return [];
    const ruleByName = new Map(incomeRules.map(r => [r.match_name, r.classification]));
    return countableDeposits(plaidTransactions, { depositoryAccountIds: plaidAccounts.filter(a => a.type === "depository").map(a => a.account_id), ruleByName });
  }, [plaidConnected, plaidTransactions, plaidAccounts, incomeRules]);
  const incomeProfile = useMemo(() => classifyIncomeCadence(allCountableDeposits), [allCountableDeposits]);
  const incomeType = useMemo(() => resolveIncomeType(incomeProfile.type, incomeTypeOverride), [incomeProfile, incomeTypeOverride]);
  const irregularSummary = useMemo(() => {
    if (incomeType.type !== "irregular") return null;
    const sinceDate = plaidTransactions.reduce((m, t) => { const d = txLocalDay(t); return d && (!m || d < m) ? d : m; }, null);
    return irregularIncomeSummary(allCountableDeposits, { today: localDateStr(), sinceDate });
  }, [incomeType, allCountableDeposits, plaidTransactions]);
  const plaidDerivedExpenses = useMemo(() => {
    if (!plaidConnected || plaidTransactions.length === 0) return 0;
    const cutoff30 = daysAgoStr(30);
    return plaidTransactions
      .filter(t => isSpendTx(t) && txSince(t, cutoff30))
      .reduce((a, t) => a + t.amount, 0);
  }, [plaidConnected, plaidTransactions]);
  // Irregular earners (2026-10-14): the Money score's income is their TYPICAL
  // month (median of complete months), not the last 30 days, so a lean stretch
  // between client payments is not scored as overspending.
  const effectiveIncome = (incomeType.type === "irregular" && irregularSummary?.typical?.median > 0)
    ? irregularSummary.typical.median
    : (plaidConnected && plaidDerivedIncome > 0 ? Math.round(plaidDerivedIncome) : monthlyIncome);
  const effectiveExpenses = plaidConnected && plaidDerivedExpenses > 0 ? Math.round(plaidDerivedExpenses) : monthlyExpenses;

  const suppStreak = supplements.length > 0
    ? Math.round(supplements.reduce((a, s) => a + (s.streak || 0), 0) / supplements.length)
    : 0;
  const otherAssetsTotal = otherAssets.reduce((a, x) => a + (Number(x.amount) || 0), 0);
  // Goal-adherence inputs (src/lib/goalAdherence.js): history per goal from
  // the goal's creation on, device-local calendar. Server parity:
  // api/lib/pillars-server.js builds the same context from the cron bundle.
  const _adhCtx = { today: localDateStr(), screenTimeDays, transactions: plaidTransactions, workouts, habits };
  const financeData = {
    totalDebt: debts.reduce((a, d) => a + (d.balance||0), 0),
    income: effectiveIncome,
    incomeType: incomeType.type,
    savings: savings,
    otherAssets: otherAssetsTotal,          // manual "Other savings and assets" → cushion
    prevSavingsBalance: prevSavingsBalance,  // last month's cushion balance → growth credit
    bills: bills,                            // → Bills On Time bucket
    // Category budgets (monthly spend-cap goals) → Budget Adherence scores against these
    // when set; otherwise it falls back to month-over-month.
    budgets: goals.filter(g => g.source_type === 'plaid_spend' && g.period === 'month' && Number(g.period_target || g.target_value) > 0)
      .map(g => ({ limit: Number(g.period_target || g.target_value), primary: g.source_config?.category || null, detailed: g.source_config?.detailed_categories || [], label: g.title })),
    // Budget Adherence (2026-10-17): past complete months within the cap +
    // this month's pace, from the goal's creation on (shared with the server).
    budgetAdherence: budgetAdherence(goals, _adhCtx),
    effectiveExpenses: effectiveExpenses,
    plaidConnected: plaidConnected,
    plaidAccounts: plaidAccounts,
    plaidTransactions: plaidTransactions,
    overdueCheckups: checkups.filter(ch => ch.urgent).length,
    lowRefillMeds: medications.filter(m => {
      const asNeededPat = /as.?needed|prn|when needed/i;
      if (asNeededPat.test(m.schedule || '')) return false;
      if (!m.refill_date) return false;
      const _today = new Date(); _today.setHours(0,0,0,0);
      const _refill = new Date(m.refill_date);
      const _daysLeft = Math.ceil((_refill - _today)/(1000*60*60*24));
      return _daysLeft <= 7; // includes past-due (negative) values
    }).length,
    suppBonus: Math.min(3, Math.floor(suppStreak / 7)),
    medications,
    supplements,
    checkups,
    // Activity scoring (active days in the last 7) — a day counts via a
    // movement-habit completion, a workout row, or steps over the floor.
    workouts,
    dailyActivity,
    // Health pillar rebuild (2026-10-14): steps are graded against a step goal
    // (none of the goal templates sets one today, so this is null and the
    // scorer uses the user's own 28-day median), every active health/fitness
    // goal becomes a tracker scored by its RESOLVED progress (same resolver the
    // goal cards use), and measured sleep nights become the Sleep tracker.
    stepGoal: null,
    sleepNights,
    healthGoals: linkWorkoutGoals(goals, habits).filter(g => g && !g.completed && (g.source_type === 'workout' || g.source_type === 'screen_time' || g.category === 'health' || g.category === 'fitness')).map(g => {
      const a = getGoalAutoValue(g);
      if (a) return { id: g.id, adherence: goalAdherence(g, _adhCtx), title: g.title, source_type: g.source_type, period: g.period || null, unit: a.unit || g.unit || '', value: Number(a.value) || 0, target: Number(a.target ?? g.period_target ?? g.target_value) || 0, hasData: a.hasData !== false, inverted: !!a.inverted };
      // Manual goals: current_value against target_value; a manual cap is inverted.
      return { id: g.id, adherence: goalAdherence(g, _adhCtx), title: g.title, source_type: g.source_type || 'manual', period: g.period || null, unit: g.unit || '', value: Number(g.current_value) || 0, target: Number(g.period_target ?? g.target_value) || 0, hasData: true, inverted: g.source_config?.manual_shape === 'cap' };
    }),
    todayStr: localDateStr(),
    moodHistory,
    journalEntries,
  };
  // There is no composite score. Each pillar reports earned / possible over the
  // trackers the user actually enrolled in; an unenrolled tracker is absent from
  // both sides of that fraction, so it can neither cost points nor cap a pillar.
  const enrolledSet = resolveEnrolled(enabledTrackers, { habits, data: financeData });
  const pillarScores = calcPillarScores({ habits, data: financeData, enrolled: enrolledSet, templates: HABIT_TEMPLATES });
  // Settled, not raw: a pillar whose inputs are still loading keeps its last
  // complete score (pillarReadiness). Demo and signed-out previews are whole.
  const settledPillars = settlePillars({ computed: pillarScores.pillars, loaded: (isDemo || !user) ? new Set(ALL_INPUTS) : inputsLoaded, failed: inputsFailed, previous: lastCompletePillars });
  const pillars = settledPillars.pillars;
  const pillarsPending = settledPillars.pending;
  const lastCompleteSig = Object.values(settledPillars.lastComplete).map(p => `${p.id}:${p.pct}:${p.earned}:${p.possible}:${p.computedAt}`).join('|');
  const prevCompleteSig = Object.values(lastCompletePillars).map(p => `${p.id}:${p.pct}:${p.earned}:${p.possible}:${p.computedAt}`).join('|');
  useEffect(() => { if (lastCompleteSig !== prevCompleteSig) setLastCompletePillars(settledPillars.lastComplete); }, [lastCompleteSig]); // eslint-disable-line react-hooks/exhaustive-deps
  // First-run Home: days since signup + whether any pillar has a real score drive the
  // greeting→brief→rings layout and the Getting-started card (src/lib/firstRun.js).
  const accountAgeDaysHome = user?.created_at ? Math.max(0, Math.floor((Date.now() - new Date(user.created_at).getTime()) / 86400000)) : (isDemoMode ? 999 : null);
  const anyPillarScoredHome = pillars.some(p => p.pct !== null && p.pct !== undefined);
  const isFirstRun = isFirstRunHome({ accountAgeDays: accountAgeDaysHome, anyPillarScored: anyPillarScoredHome, isDemo: isDemoMode });
  // Which ring/chip is selected on the Home rings (redesign Phase 1).
  // Pure presentation state — never feeds scoring or persistence.
  const [selectedPillar, setSelectedPillar] = useState(0);
  const pillarById = Object.fromEntries(pillars.map(p => [p.id, p]));
  // Demo mode runs the same engine on seeded data — no hardcoded breakdown.

  // ── DATA-SANITY MONITORS ────────────────────────────────────────────────────
  // Silent-failure guards for "impossible value" cases: flag a suspicious
  // number and report it once, rather than presenting a wrong figure as fact
  // (the rent/Zelle exclusion bug dropped August spend in half with no error).
  // Client-side because these values are derived here. Each fires once per
  // session, and only past thresholds tuned to stay quiet on healthy accounts.
  const [spendSanityFlag, setSpendSanityFlag] = useState(null);
  const dataSanityReported = useRef(new Set());
  useEffect(() => {
    if (isDemoMode || !plaidConnected) { setSpendSanityFlag(null); return; }
    // Fresh data only (2026-10-23): never judge the launch snapshot. SCORIFY-APP-B
    // fired "balance not refreshed" against the cached copy 40s after a good
    // refresh. The Plaid input marks loaded once this session's fetch settles.
    if (!inputsLoaded.has("plaid_cache")) return;
    const txns = plaidTransactions || [];
    if (txns.length < 20) return; // not enough history to judge
    const ageDays = plaidConnectedAt ? (Date.now() - new Date(plaidConnectedAt).getTime()) / 86400000 : 999;
    if (ageDays < 7) return; // still inside the initial-import window
    const reportOnce = (key, msg, ctx) => {
      if (dataSanityReported.current.has(key)) return;
      dataSanityReported.current.add(key);
      try { reportError(new Error(msg), ctx); } catch {}
    };
    const ym = (d) => (d || "").slice(0, 7);
    const now = new Date();
    const curYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevYM = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
    // Compare LIKE FOR LIKE (APP-B, 2026-09-29): the current month is only
    // partial, so both sides are summed over the SAME day-of-month range —
    // month-to-date vs the prior month through the same day. Comparing a
    // partial September ($2777 through the 23rd) against a full August ($5579)
    // fired a false >50%-drop warning every day of every month.
    const dayCap = now.getDate();
    const monthSpendToDay = (m) => txns.filter(t => {
      const txDay = txLocalDay(t);
      if (!isSpendTx(t) || ym(txDay) !== m) return false;
      const dom = parseInt((txDay || "").slice(8, 10), 10);
      return Number.isFinite(dom) && dom <= dayCap;
    }).reduce((a, t) => a + t.amount, 0);
    const curSpend = monthSpendToDay(curYM), prevSpend = monthSpendToDay(prevYM);
    let flag = null;
    // (1) Spend dropped >50% for the same day range month over month — the
    // transfer-exclusion signature. Small elapsed-days floor so day 1-2 noise
    // (tiny samples on both sides) does not trip it.
    if (prevSpend >= 500 && now.getDate() >= 7 && curSpend < prevSpend * 0.5) {
      flag = { kind: "drop" };
      reportOnce("spend_drop", `data-sanity: month-to-date spend dropped >50% vs same range last month (${prevYM} $${Math.round(prevSpend)} to ${curYM} $${Math.round(curSpend)}, through day ${dayCap})`, { op: "sanity.spend_drop", level: "warning" });
    }
    // (2) Connected account with exactly zero spend or income over 30 days.
    if (plaidDerivedExpenses === 0) {
      flag = flag || { kind: "zero" };
      reportOnce("zero_spend", "data-sanity: connected account with 20+ transactions but $0 derived spend over 30 days", { op: "sanity.zero_spend", level: "warning" });
    }
    if (plaidDerivedIncome === 0) {
      reportOnce("zero_income", "data-sanity: connected account with 20+ transactions but $0 derived income over 30 days", { op: "sanity.zero_income", level: "warning" });
    }
    setSpendSanityFlag(flag);
    // (3) Balance not refreshed in >24h while the user is active (in the app now).
    if (plaidBalancesUpdatedAt) {
      const balAgeH = (Date.now() - new Date(plaidBalancesUpdatedAt).getTime()) / 3600000;
      if (balAgeH > 24) {
        reportOnce("stale_balance", `data-sanity: Plaid balance not refreshed in ${Math.round(balAgeH)}h for an active user`, { op: "sanity.stale_balance", level: "warning" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoMode, plaidConnected, plaidTransactions, plaidDerivedIncome, plaidDerivedExpenses, plaidBalancesUpdatedAt, plaidConnectedAt, inputsLoaded]);

  // Screen-time overcount alert: reconstructScreenTimeUsage flags a floor that
  // exceeds elapsed wall-clock (a false threshold fire). Report it once so the
  // silent-failure monitors see it; the card already shows a recheck state.
  const screenTimeOvercountReported = useRef(false);
  const screenTimeBurstReported = useRef(false);
  const screenTimeChainBreakReported = useRef(false);
  // (restart alert is now gated once-per-day via localStorage, not a session ref)
  useEffect(() => {
    if (isDemoMode || !native) return;
    if (!inputsLoaded.has("screen_time")) return; // fresh rows only, never the launch snapshot
    const todayRow = (screenTimeDays || []).find(d => d.date === localDateStr());
    const u = reconstructScreenTimeUsage(todayRow);
    if (u?.overcount && !screenTimeOvercountReported.current) {
      screenTimeOvercountReported.current = true;
      try { reportError(new Error('data-sanity: screen-time usage floor exceeded elapsed wall-clock (likely false threshold fire)'), { op: 'sanity.screentime_overcount', level: 'warning' }); } catch {}
    }
    // Premature-burst crossing: a cap_crossed_at that is impossible against the
    // cap window's arm time (iOS-26 false fire, 2026-09-29). Report once so the
    // burst is visible in Sentry even on field builds whose extension predates
    // the write-side skip; the resolver already ignores it for display.
    if (todayRow && todayRow.cap_crossed_at && !capCrossPlausible(todayRow) && !screenTimeBurstReported.current) {
      screenTimeBurstReported.current = true;
      try { reportError(new Error('data-sanity: screen-time cap_crossed_at implausible vs arm time (premature threshold burst)'), { op: 'sanity.screentime_burst', level: 'warning' }); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenTimeDays, isDemoMode, inputsLoaded]);

  // ── APP-VISIBLE SIGNAL (for the lock's Face ID circle) ──────────────────────
  // The lock overlay (App.js LockGate, z1100+) sits above Scorify, and Scorify
  // shows its OWN crimson boot/data cover (PrivacyCover z290) while profile/data
  // load. If the unlock circle starts while that cover is still up, it reveals
  // crimson onto crimson (invisible), and when the cover then drops the app pops
  // in as a hard blink (device: two cold launches, one animated, one blink,
  // identical LOCKSEQ — the difference was load timing). So announce when the
  // cover is actually GONE and a frame has painted; LockScreen holds the circle
  // until then. profileLoaded is the gate for a returning (locked) user; it
  // flips true when their profile+data cover drops. (Onboarding users are not
  // the locked-cold-launch case and are covered by their own sheet.)
  const appCoverShown = !isDemoMode && !profileLoaded;
  useEffect(() => {
    if (appCoverShown) { try { window.__scorifyAppVisible = false; } catch {} return; }
    const raf = requestAnimationFrame(() => {
      try {
        window.__scorifyAppVisible = true;
        window.dispatchEvent(new Event('scorify-app-visible'));
        // LAUNCH TIMING: content actually on screen (boot/data cover gone).
        // Logged once, relative to the earliest webview JS, for cold-launch
        // measurement from Xcode's console.
        if (!window.__scorifyAppVisibleLogged && window.__scorifyBoot) {
          window.__scorifyAppVisibleLogged = true;
          const appVisibleMs = Date.now() - window.__scorifyBoot;
          console.log('[launch] app visible (content painted):', appVisibleMs, 'ms since boot');
        }
      } catch {}
    });
    return () => cancelAnimationFrame(raf);
  }, [appCoverShown]);

  // ── TAB-SWITCH TIMING (measure before/after any tab-perf change) ────────────
  // From the tap (tab state change) to the new tab's content actually on screen
  // (two rAFs after contentTab settles). Logged to Xcode's console so the
  // current cost is measurable on-device before deciding whether the heavier
  // "keep tabs mounted" refactor is worth its stale-data risk.
  const tabSwitchStart = useRef(0);
  const tabTimingInited = useRef(false);
  useEffect(() => {
    if (!tabTimingInited.current) { tabTimingInited.current = true; return; }
    tabSwitchStart.current = performance.now();
  }, [tab]);
  useEffect(() => {
    const t0 = tabSwitchStart.current;
    if (!t0) return;
    tabSwitchStart.current = 0;
    const r1 = requestAnimationFrame(() => requestAnimationFrame(() => {
      try {
        const ms = Math.round(performance.now() - t0);
        console.log('[perf] tab switch ->', contentTab, ':', ms, 'ms');
      } catch {}
    }));
    return () => cancelAnimationFrame(r1);
  }, [contentTab]);

  // ── NOTIFICATION TAP-THROUGH ────────────────────────────────────────────────
  // A tapped push used to just foreground the app with its message lost. App.js's
  // OneSignal click handler stashes the notification's full text in localStorage
  // (survives the lock screen and a cold launch) and dispatches an event; here we
  // reopen the coach drawer seeded with that text as Kai's opening line, ready
  // for a reply. Runs once the app is interactive; while a lock is up the drawer
  // simply opens behind it and is revealed on unlock. Opened is already marked
  // by App.js's open-report.
  useEffect(() => {
    if (isDemoMode || !user) return;
    const openFromPending = (pending) => {
      if (!pending || !pending.message) return;
      // Freshness guard: only a recent tap reopens the drawer, and the pending
      // is cleared either way so a stale one never resurfaces on a later launch.
      if (pending.ts && (Date.now() - pending.ts) > 30 * 60 * 1000) {
        try { localStorage.removeItem('scorify_pending_coach'); } catch {}
        return;
      }
      try { localStorage.removeItem('scorify_pending_coach'); } catch {}
      setCoachMsgs([{ text: pending.message, isCoach: true, role: "assistant" }]);
      setCoachOpen(true);
    };
    try {
      const raw = localStorage.getItem('scorify_pending_coach');
      if (raw) openFromPending(JSON.parse(raw)); // cold-launch / already-stashed
    } catch {}
    const onEvent = (e) => openFromPending(e.detail); // warm: app already open
    window.addEventListener('scorify-coach-notification', onEvent);
    return () => window.removeEventListener('scorify-coach-notification', onEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isDemoMode]);

  // ── Scroll to top on tab change ─────────────────────────────────────────────
  // Must target scrollRef (the C.app div) — the actual scroll container.
  // window.scrollTo / body.scrollTop don't work in Capacitor WKWebView when
  // html,body have height:100% and overflow:hidden (the app div scrolls, not the body).
  useEffect(() => {
    console.log('[Scorify] tab changed to:', tab, '— resetting scroll');
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setFabHidden(false); // a fresh tab starts at the top — never inherit a hidden FAB
  }, [tab]);
  // [UIDIAG] tab-change breadcrumb (tab ids only) for the resume health check.
  useEffect(() => { addBreadcrumb("ui", "tab change", { tab }); }, [tab]);

  // ── FAB hide-on-scroll ──────────────────────────────────────────────────────
  // Listener on scrollRef (the C.app container), NOT window — the body doesn't
  // scroll in the WKWebView. Direction is thresholded so jitter can't toggle it.
  // The pill reveals ONLY on scroll-up and at the top of the page (2026-10-13):
  // the old idle timer re-showed it 180ms after scrolling stopped, i.e. right
  // over whatever the user had just scrolled to (the Habits "Log today"
  // button, device screenshot), and the at-bottom reveal did the same at the
  // end of the page. setFabHidden with an unchanged value is a no-op in React,
  // so per-event calls don't re-render.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const DIR_THRESHOLD = 10; // ignore direction flips smaller than this (px)
    const EDGE = 6;           // treat within this of the top as "at the top"
    let lastY = el.scrollTop;
    const onScroll = () => {
      const y = el.scrollTop;
      const delta = y - lastY;
      if (y <= EDGE) {
        setFabHidden(false);
        lastY = y;
      } else if (Math.abs(delta) >= DIR_THRESHOLD) {
        setFabHidden(delta > 0); // down → hide, up → reveal
        lastY = y;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); };
  }, []);

  // ── SUPABASE: Load user data on mount ──────────────────────────────────────
  useEffect(() => {
    if (!user || isDemo) return;
    // CACHE-FIRST: hydrate the last snapshot for THIS user so the real screen
    // renders instantly (cover drops now, not after the network), then loadData
    // below refreshes in place. Guarded by uid so another account never sees it;
    // other accounts' snapshots are swept here (account-switch cleanup). The app
    // lock still renders over everything, so cached data is never shown before
    // unlock. Staleness rides along in the cached timestamps.
    try {
      const cacheKey = userKey(user.id, `app_cache_v${APP_CACHE_VERSION}`);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('scorify_') && k.includes('_app_cache_v') && k !== cacheKey) localStorage.removeItem(k);
      }
      const raw = cacheKey && localStorage.getItem(cacheKey);
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap && snap.v === APP_CACHE_VERSION && snap.uid === user.id && snap.d) {
          const d = snap.d, S = (v, set) => { if (v !== undefined) set(v); };
          S(d.coachName, setCoachName); S(d.username, setUsername); S(d.avatarUrl, setAvatarUrl); S(d.focusPillar, setFocusPillar); S(d.notifPrefs, setNotifPrefs);
          S(d.aiConsent, setAiConsent); S(d.enabledTrackers, setEnabledTrackers);
          S(d.bodyStats, setBodyStats); S(d.goals, setGoals); S(d.habits, setHabits);
          S(d.moodHistory, setMoodHistory);
          S(d.monthlyIncome, setMonthlyIncome); S(d.monthlyExpenses, setMonthlyExpenses); S(d.savings, setSavings);
          S(d.hasFinancesRow, setHasFinancesRow); S(d.savingsKnown, setSavingsKnown); S(d.creditDetails, setCreditDetails); S(d.creditScore, setCreditScore);
          S(d.debts, setDebts); S(d.bills, setBills); S(d.medications, setMedications); S(d.checkups, setCheckups);
          S(d.supplements, setSupplements); S(d.recoveryTrackers, setRecoveryTrackers);
          S(d.dailyActivity, setDailyActivity); S(d.workouts, setWorkouts); S(d.healthLastSync, setHealthLastSync);
          S(d.plaidConnected, setPlaidConnected); S(d.plaidAccounts, setPlaidAccounts); S(d.plaidTransactions, setPlaidTransactions);
          S(d.plaidBalancesUpdatedAt, setPlaidBalancesUpdatedAt); S(d.plaidConnectedAt, setPlaidConnectedAt);
          S(d.incomeRules, setIncomeRules); S(d.scoreHistory, setScoreHistory); S(d.progress, setProgress);
          if (d.pillarSnapshot && typeof d.pillarSnapshot === 'object') setLastCompletePillars(d.pillarSnapshot);
          // Only profileLoaded: it drops the crimson cover and renders the cached
          // dashboard now. dataLoaded is left for the real network load below, so
          // the server-writing effects gated on it (savePillarScores, the cache
          // re-write, screen-time refresh) run against FRESH data, never the
          // cached copy — no stale score written back, no cache rewritten from
          // itself.
          setProfileLoaded(true);
        }
      }
    } catch { /* corrupt/absent snapshot — fall through to the normal network load */ }
    const loadData = async () => {
      // Anything not loaded 20s in counts as timed out: its pillar keeps the
      // last complete score and says "Last updated …" instead of waiting forever.
      const loadTimeout = setTimeout(() => { for (const k of ALL_INPUTS) if (!inputsLoadedRef.current.has(k)) markInput(k, "failed"); }, 20000);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      // Reset per-user claims so a sign-out/sign-in can't block legitimate adds.
      habitIdsRef.current = new Set();
      goalTitlesRef.current = new Set();
      // Load habits
      const { data: habitsData, error: habitsErr } = await supabase
        .from("habits")
        .select("*")
        .eq("user_id", user.id);
      markInput("habits", habitsErr ? "failed" : "loaded");
      if (habitsData && habitsData.length > 0) {
        habitsData.forEach(h => habitIdsRef.current.add(h.name));
        const today = new Date();
        const dow = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
        startOfWeek.setHours(0,0,0,0);
        // Local date strings anchored to load time in the user's timezone.
        const todayStr = localDateStr();
        const yesterdayStr = localDateStr(new Date(Date.now() - 86400000));
        // Collected rather than fired-and-forgotten; awaited after the map.
        const habitHeals = [];
        const mapped = habitsData.map(h => {
          // last_week_reset is a DATE ('YYYY-MM-DD', written by localDateStr).
          // Compare it as a local date string: new Date('YYYY-MM-DD') parses as
          // UTC midnight, which is BEFORE local Monday 00:00 everywhere west of
          // UTC — so every load read "new week" and zeroed the weekly count.
          const isNewWeek = !h.last_week_reset || String(h.last_week_reset).slice(0, 10) < localDateStr(startOfWeek);
          // Streak is broken if last log was not today and not yesterday
          const streakBroken = h.last_completed && h.last_completed !== todayStr && h.last_completed !== yesterdayStr;
          const streak = streakBroken ? 0 : (h.streak || 0);
          // Silently reset in DB if broken
          if (streakBroken && h.streak > 0) {
            habitHeals.push(supabase.from('habits').update({ streak: 0 }).eq('id', h.id));
          }
          // Migrate old positional history (array of 0/1) to date-keyed completions (array of date strings).
          // Conservative: only preserve the most recent log date — old positional data is date-unreliable.
          // Concurrent writes from web+iOS produce the same result, so no lock is needed.
          const rawHistory = h.history ? JSON.parse(h.history) : null;
          let completions;
          if (!rawHistory || rawHistory.length === 0) {
            completions = [];
          } else if (typeof rawHistory[0] === 'string') {
            completions = rawHistory; // already new format
          } else {
            // Old format — migrate using last_completed as the only reliable anchor.
            // todayStr/yesterdayStr are anchored to load time (local timezone).
            if (h.last_completed === todayStr) completions = [todayStr];
            else if (h.last_completed === yesterdayStr) completions = [yesterdayStr];
            else completions = [];
            habitHeals.push(supabase.from('habits').update({ history: JSON.stringify(completions) }).eq('id', h.id));
          }

          // ── DATA HEALING: fix UTC-shifted last_completed dates ──────────────
          // Old code used toISOString().split('T')[0] (UTC). Users west of UTC
          // who logged in the evening got last_completed = "tomorrow local".
          // If last_completed equals localTomorrow, it was written by old code
          // during an evening log — shift it back 1 day to the correct local date.
          // Concurrent writes from web+iOS produce the same result (idempotent).
          let lastCompleted = h.last_completed || null;
          const localTomorrow = localDateStr(new Date(Date.now() + 86400000));
          if (lastCompleted && lastCompleted !== todayStr && lastCompleted !== yesterdayStr) {
            if (lastCompleted === localTomorrow) {
              // Definitely UTC drift: old evening log stored "tomorrow local". Shift back.
              const fixedDateShifted = localDateStr(new Date(new Date(lastCompleted + 'T12:00:00').getTime() - 86400000));
              completions = completions.map(d => d === lastCompleted ? fixedDateShifted : d);
              lastCompleted = fixedDateShifted;
              habitHeals.push(supabase.from('habits').update({
                last_completed: lastCompleted,
                history: JSON.stringify(completions),
              }).eq('id', h.id));
            }
          }

          return {
            id: h.name,
            streak,
            weekCount: isNewWeek ? 0 : (h.week_count || 0),
            completions,
            active: true,
            _dbId: h.id,
            createdAt: h.created_at || null,
            // Restore custom habit fields if stored
            ...(h.label ? { label: h.label, icon: h.icon, target: h.target, unit: h.unit } : {}),
            lastLoggedDate: lastCompleted,
            lastLoggedAt: h.last_logged_at || null,
            notes: h.notes && typeof h.notes === "object" ? h.notes : {},
            // MATCHED PAIR with loggedOnLocalDate in api/notify-cron.js —
            // done-today is DERIVED from last_completed vs the local calendar
            // date, never read from the stored `completed` flag (written true
            // once, never reset — the original client/server-disagree bug).
            // Change this comparison and the server helper together or not
            // at all.
            completed: lastCompleted === todayStr,
          };
        });
        await settleHeals("habits", habitHeals);
        setHabits(mapped);
      }

      // Load mood history — the MOST RECENT 56 rows, then reversed back to
      // chronological order. This was `ascending: true` + limit(56), which is
      // the OLDEST 56 rows: past day 56 of mood logging, every chart, calendar,
      // average, trend and insight would have computed from the user's first
      // two months forever while new moods never appeared. Every consumer of
      // moodHistory (moodTrend, computeInsights, buildUserContext,
      // buildCoachUserData, the crisis effect — all use slice(-N)) assumes
      // oldest→newest, so the reverse below is load-bearing, not cosmetic.
      const { data: moodData, error: moodErr } = await supabase
        .from("moods")
        .select("*")
        .eq("user_id", user.id)
        .order("logged_at", { ascending: false })
        .limit(56);
      markInput("moods", moodErr ? "failed" : "loaded");
      if (moodData && moodData.length > 0) {
        const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
        const mapped = [...moodData].reverse().map(m => ({
          day: days[new Date(m.logged_at).getDay()],
          score: m.score,
          // LOCAL calendar date of the instant (was the UTC slice: a 9:44 PM
          // Pacific log read as the next day everywhere this list is used).
          date: moodLocalDate(m.logged_at),
          loggedAt: m.logged_at || null,
          note: m.note || "",
        }));
        setMoodHistory(mapped);
      }
      // Always separately check if mood logged today (regardless of limit)
      // Use local date boundaries to handle all timezones correctly
      const { startIso: todayStartIso, endIso: todayEndIso } = localDayBoundsIso(new Date());
      const { data: todayMoodRows } = await supabase
        .from("moods")
        .select("id, score, note")
        .eq("user_id", user.id)
        .gte("logged_at", todayStartIso)
        .lte("logged_at", todayEndIso)
        .order("logged_at", { ascending: false })
        .limit(1);
      if (todayMoodRows && todayMoodRows.length > 0) {
        // Today's LATEST row by local date is the saved score the card shows;
        // its note pre-fills the box so an edit starts from what was saved.
        setTodayMood(todayMoodRows[0].score);
        setSavedMood(todayMoodRows[0].score);
        setTodayNote(todayMoodRows[0].note || "");
        setMoodLogged(true);
      }

      // Load finances
      const { data: finData, error: finErr } = await supabase
        .from("finances").select("*").eq("user_id", user.id).maybeSingle();
      markInput("finances", finErr ? "failed" : "loaded");
      if (finData) {
        setHasFinancesRow(true);
        if (finData.credit_score) setCreditScore(finData.credit_score);
        if (finData.monthly_income) setMonthlyIncome(finData.monthly_income);
        if (finData.monthly_expenses) setMonthlyExpenses(finData.monthly_expenses);
        if (finData.savings) setSavings(finData.savings);
        // Known when the column is a real value (0 included), unknown when null.
        setSavingsKnown(finData.savings != null && finData.savings !== "");
        if (finData.pay_freq) { setPaySchedule({ freq: finData.pay_freq, day1: finData.pay_day1||1, day2: finData.pay_day2||15, weekday: finData.pay_weekday||5, startDate: finData.pay_start||"" }); setPayScheduleSaved(true); }
        if (finData.pay_amount) setPaycheckAmount(finData.pay_amount);
        setIncomeTypeOverride(readIncomeTypeOverride(finData));
        incomeTypeSaverRef.current = null; // the saver re-seeds from the stored value on the next tap
        setFinanceForm({ income: finData.monthly_income||"", expenses: finData.monthly_expenses||"", savings: finData.savings||"", pay_amount: finData.pay_amount||"", pay_freq: finData.pay_freq||"biweekly", pay_day1: String(finData.pay_day1||1), pay_day2: String(finData.pay_day2||15), pay_weekday: String(finData.pay_weekday||5), pay_start: finData.pay_start||"" });
        setCreditDetails({
          cc_balance: finData.cc_balance||0,
          cc_limit: finData.cc_limit||0,
          credit_age_years: finData.credit_age_years||0,
          num_accounts: finData.num_accounts||0,
          hard_inquiries: finData.hard_inquiries||0,
          family_size: finData.family_size||1,
          has_student_loans: finData.has_student_loans||false,
          is_first_time_buyer: finData.is_first_time_buyer||false,
          employer_has_401k: finData.employer_has_401k||false,
        });
      }

      // Load debts
      const { data: debtsData, error: debtsDataErr } = await supabase.from("debts").select("*").eq("user_id", user.id).order("created_at");
      markInput("debts", debtsDataErr ? "failed" : "loaded");
      if (debtsData) setDebts(debtsData);

      // Load "Other savings and assets" (count toward the cushion).
      const { data: assetsData } = await supabase.from("other_assets").select("*").eq("user_id", user.id).order("created_at");
      if (assetsData) setOtherAssets(assetsData);

      // Last month's cushion balance (for month-over-month growth credit): the most
      // recent snapshot from a PRIOR month. Current month is written separately on load.
      const curMonthStr = localDateStr().slice(0, 7);
      const { data: snapRows } = await supabase.from("savings_snapshots")
        .select("year_month, balance").eq("user_id", user.id)
        .lt("year_month", curMonthStr).order("year_month", { ascending: false }).limit(1);
      setPrevSavingsBalance(snapRows && snapRows[0] ? Number(snapRows[0].balance) : null);

      // Load bills
      const { data: billsData, error: billsDataErr } = await supabase.from("bills").select("*").eq("user_id", user.id).order("due_day");
      markInput("bills", billsDataErr ? "failed" : "loaded");
      if (billsData) setBills(billsData);

      // Load commitments (plan-with-Kai): every status with a due date, for the Up
      // next card and the commitments calendar. Same query as reloadCommitments.
      await reloadCommitments();

      // Load medications
      const { data: medsData, error: medsDataErr } = await supabase.from("medications").select("*").eq("user_id", user.id).order("created_at");
      markInput("medications", medsDataErr ? "failed" : "loaded");
      if (medsData) setMedications(medsData);

      // Load checkups
      const { data: checkupsData, error: checkupsDataErr } = await supabase.from("checkups").select("*").eq("user_id", user.id).order("created_at");
      markInput("checkups", checkupsDataErr ? "failed" : "loaded");
      if (checkupsData) setCheckups(checkupsData);

      // Load supplements from db
      const { data: suppData, error: suppDataErr } = await supabase.from("supplements").select("*").eq("user_id", user.id).order("created_at");
      markInput("supplements", suppDataErr ? "failed" : "loaded");
      if (suppData && suppData.length > 0) {
        // Use LOCAL date (not UTC) to avoid timezone causing stale resets
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
        const suppHeals = [];
        const mappedSupps = suppData.map(s => {
          const takenToday = s.last_taken_date === today ? s.taken_today : false;
          // Derive streak: break it if last_taken_date is older than yesterday
          const yesterday = new Date(now);
          yesterday.setDate(now.getDate() - 1);
          const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth()+1).padStart(2,"0")}-${String(yesterday.getDate()).padStart(2,"0")}`;
          const streakBroken = s.last_taken_date && s.last_taken_date !== today && s.last_taken_date !== yesterdayStr;
          const streak = streakBroken ? 0 : (s.streak || 0);
          // Silently reset in DB if broken (same pattern as habits)
          if (s.taken_today && s.last_taken_date !== today) {
            suppHeals.push(supabase.from("supplements").update({ taken_today: false }).eq("id", s.id));
          }
          if (streakBroken && s.streak > 0) {
            suppHeals.push(supabase.from("supplements").update({ streak: 0 }).eq("id", s.id));
          }
          // No per-day history exists anywhere (supplements stores only streak +
          // last_taken_date; there is no log table). The old Array(28).fill(0)
          // "history" here fed a heatmap that rendered 28 fabricated blanks on
          // every load (map 6.5) — the heatmap is removed rather than fed.
          return { ...s, takenToday, streak };
        });
        await settleHeals("supplements", suppHeals);
        setSupplements(mappedSupps);
      }

      // Load workouts
      // Limit 60: covers any full month for workout-count goals; display list slices to 7
      const { data: workoutsData, error: workoutsDataErr } = await supabase.from("workouts").select("id,type,name,duration_min,notes,date,distance_m,source,created_at,start_at,end_at").eq("user_id", user.id).order("date", { ascending: false }).limit(60);
      markInput("workouts", workoutsDataErr ? "failed" : "loaded");
      if (workoutsData) setWorkouts(workoutsData);

      // Load daily activity (weekly_distance goals + activity scoring's step floor)
      const { data: dailyActData, error: dailyActDataErr } = await supabase.from("daily_activity").select("date,steps,distance_m").eq("user_id", user.id).order("date", { ascending: false }).limit(60);
      markInput("daily_activity", dailyActDataErr ? "failed" : "loaded");
      if (dailyActData) setDailyActivity(dailyActData);
      // Sleep nights for the Health pillar (scoreSleep): absent when unsynced, never zero.
      const { data: sleepRows, error: sleepRowsErr } = await supabase.from("sleep_nights").select("date,asleep_min").eq("user_id", user.id).order("date", { ascending: false }).limit(14);
      markInput("sleep", sleepRowsErr ? "failed" : "loaded");
      if (Array.isArray(sleepRows)) setSleepNights(sleepRows);

      // Load recovery trackers
      const { data: recoveryData } = await supabase.from("recovery_trackers").select("*").eq("user_id", user.id).order("created_at");
      if (recoveryData) setRecoveryTrackers(recoveryData);

      // Load journal entries
      const { data: journalData, error: journalDataErr } = await supabase.from("journal_entries").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
      markInput("journal", journalDataErr ? "failed" : "loaded");
      if (journalData) setJournalEntries(journalData);

      // Load journal coach setting
      const { data: journalSettings } = await supabase.from("journal_settings").select("coach_enabled").eq("user_id", user.id).maybeSingle();
      if (journalSettings) setJournalCoachEnabled(journalSettings.coach_enabled || false);

      // Load goals
      const { data: goalsData, error: goalsDataErr } = await supabase.from("goals").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
      markInput("goals", goalsDataErr ? "failed" : "loaded");
      if (goalsData) {
        // Active goals only — the index is partial, so a completed goal does not
        // reserve its title and must not be claimed here either.
        goalsData.forEach(g => { if (!g.completed && g.title) goalTitlesRef.current.add(goalKey(g.title)); });
        setGoals(goalsData);
      }

      // Load Screen Time day records (last 14 local days) — the client half
      // of the screen_time resolver reads these.
      const { data: stData, error: stDataErr } = await supabase.from("screen_time_days").select("*").eq("user_id", user.id).order("date", { ascending: false }).limit(14);
      markInput("screen_time", stDataErr ? "failed" : "loaded");
      if (stData) setScreenTimeDays(stData);

      // Load body stats
      const { data: bsData } = await supabase.from("body_stats").select("*").eq("user_id", user.id).maybeSingle();
      if (bsData) { setBodyStats(bsData); setProfileForm(bsData); }

      // Load weight log
      const { data: wlData } = await supabase.from("weight_log").select("*").eq("user_id", user.id).order("logged_at", { ascending: true });
      if (wlData) setWeightLog(wlData);

      // Load income rules (confirmed inflow classifications — tiny table)
      const { data: irData } = await supabase.from("income_rules").select("*").eq("user_id", user.id);
      if (irData) setIncomeRules(irData);
      const { data: rrData } = await supabase.from("recurring_rules").select("*").eq("user_id", user.id);
      if (rrData) setRecurringRules(rrData);

      // Load user progress. Newest-wins: the daily-login award (mount effect →
      // award_xp RPC) usually commits BEFORE this late SELECT is even sent, so
      // the row read here is normally post-award — but if this response arrives
      // after an award already updated state, keep the newer row.
      const { data: progData } = await supabase.from("user_progress").select("*").eq("user_id", user.id).maybeSingle();
      if (progData) {
        setProgress(prev => (prev.updated_at && progData.updated_at && String(prev.updated_at) > String(progData.updated_at)) ? prev : { ...progData, badges: progData.badges || [] });
      }

      // Load profile.
      // NOTE: `goal_type` is NOT a column on public.profiles — it lives on body_stats.
      // Selecting it made this whole query fail, which left `profile` null and
      // re-opened onboarding on every single launch. Do not add it back here.
      const { data: profile, error: profileLoadErr } = await supabase
        .from("profiles")
        .select("username, display_name, avatar_url, onboarding_complete, coach_name, ai_consent_granted, timezone, health_last_sync_at, focus_pillar, focus_note, checkin_hour, checkin_enabled, weekly_recap_enabled, alerts_money, alerts_goals, alerts_habits, alerts_health, mood_patterns_enabled, evening_mood_enabled, share_sensitive_names, checkin_prompt_snoozed_until, birth_year, birth_year_estimated")
        .eq("id", user.id)
        .maybeSingle();
      if (profileLoadErr) console.error("profile load failed", profileLoadErr);

      // Read enrollment on its own so a missing column (pre-migration) degrades
      // to "never chosen" — which falls back to inference — instead of nulling
      // `profile` and re-opening onboarding for everyone.
      try {
        const { data: trackerRow, error: trackerErr } = await supabase
          .from("profiles").select("enabled_trackers").eq("id", user.id).maybeSingle();
        if (trackerErr) console.warn("enabled_trackers unavailable — using inferred defaults:", trackerErr.message);
        else setEnabledTrackers(Array.isArray(trackerRow?.enabled_trackers) ? trackerRow.enabled_trackers : null);
      } catch (e) { console.warn("enabled_trackers read failed — using inferred defaults"); }
      // Own read, same reason as enabled_trackers: a missing column must not null the profile.
      try {
        const { data: demoRow } = await supabase.from("profiles").select("is_demo").eq("id", user.id).maybeSingle();
        setIsDemoAccount(demoRow?.is_demo === true);
      } catch { setIsDemoAccount(false); }
      if (profile) {
        // display_name wins — username is the unique auto handle, display_name is what the user typed.
        if (profile.display_name || profile.username) setUsername(profile.display_name || profile.username);
        setBirthYear(isValidBirthYear(profile.birth_year) ? Number(profile.birth_year) : null);
        setBirthYearEstimated(profile.birth_year_estimated === true);
        if (profile.avatar_url) setAvatarUrl(profile.avatar_url);
        if (profile.coach_name) setCoachName(profile.coach_name);
        setFocusPillar(profile.focus_pillar || null);
        setFocusNote(profile.focus_note || null);
        setHealthLastSync(profile.health_last_sync_at || null);
        // Notification prefs — nulls degrade to the on-by-default posture (a
        // pre-migration row reads all-null; treat that as defaults, checkin_hour
        // null = not chosen).
        setNotifPrefs({
          checkinHour: profile.checkin_hour == null ? null : Number(profile.checkin_hour),
          checkinEnabled: profile.checkin_enabled !== false,
          weeklyRecapEnabled: profile.weekly_recap_enabled !== false,
          alertsMoney: profile.alerts_money !== false,
          alertsGoals: profile.alerts_goals !== false,
          alertsHabits: profile.alerts_habits !== false,
          alertsHealth: profile.alerts_health !== false,
          moodPatternsEnabled: profile.mood_patterns_enabled !== false,
          eveningMoodEnabled: profile.evening_mood_enabled !== false,
          shareSensitiveNames: profile.share_sensitive_names !== false,
          checkinPromptSnoozedUntil: profile.checkin_prompt_snoozed_until || null,
        });
        const dbConsent = profile.ai_consent_granted === true;
        setAiConsent(dbConsent);
        try {
          const ck = userKey(user.id, 'ai_consent');
          if (ck) { if (dbConsent) localStorage.setItem(ck, "true"); else localStorage.removeItem(ck); }
        } catch {}
        // Capture IANA timezone and sync to DB if missing or changed
        // Backfill for users whose row predates the write in writeProfile.
        // A silent failure here means the cron skips this user forever, so the
        // error is retried once and then surfaced rather than logged and dropped.
        if (user && user.id) {
          const tz = browserTimezone();
          if (tz && profile && profile.timezone !== tz) {
            // writeProfile, not a raw update: the raw update matched 0 rows for
            // a user whose profiles row was never created, returned no error,
            // and the backfill read as done while the cron kept skipping them —
            // and null timezone means SKIPPED, not defaulted: 38 of 42 profiles
            // had timezone null when this was audited, i.e. most users had
            // never been eligible for a single notification. writeProfile
            // verifies a row was touched and creates the row when missing.
            let lastErr = null; let lastRow = null;
            for (let attempt = 1; attempt <= 2; attempt++) {
              try {
                const { error, row } = await writeProfile({ timezone: tz });
                if (!error && row) { lastErr = null; lastRow = row; break; }
                lastErr = error || new Error("timezone write touched no row");
              } catch (e) { lastErr = e; }
              if (attempt === 1) await new Promise(r => setTimeout(r, 500));
            }
            if (lastErr || !lastRow) {
              console.error("timezone write failed after retry — notifications will be skipped for this user:", lastErr?.message || lastErr);
              setTimezoneWarning(true);
            } else {
              setTimezoneWarning(false);
            }
          } else if (tz && profile && profile.timezone === tz) {
            setTimezoneWarning(false);
          }
        }
        // Source of truth is profiles.onboarding_complete. The ref is only a
        // within-session guard so a re-render can't reopen a flow just dismissed.
        //
        // openOnboardingFlow, NOT a bare setOnboardingStep(0): loadData
        // re-runs on every resume (returning from Plaid Link refreshes the
        // Supabase token, onAuthStateChange hands App.js a NEW user object,
        // and this effect keys on user identity). The unconditional step
        // reset here is what threw a mid-onboarding user back to the welcome
        // step after connecting their bank (TestFlight 1.1 (6), debug_log
        // 2026-09-22 02:40: continuous LOCKSEQ clock, no remount — an
        // in-place reset). The flow now opens once per session and resumes
        // from the persisted step on a genuine remount.
        if (!profile.onboarding_complete && !onboardingDone.current) {
          openOnboardingFlow();
        } else if (profile.onboarding_complete) {
          onboardingDone.current = true;
        }
      } else if (!profileLoadErr && !onboardingDone.current) {
        // Query succeeded and matched no row — genuinely a brand new user.
        // On a query error we show nothing rather than guessing.
        openOnboardingFlow();
      }
      setProfileLoaded(true);

      // Load score history
      const { data: scoreHistData } = await supabase
        .from("score_history")
        .select("pillar, week_start, earned, possible")
        .eq("user_id", user.id)
        .order("week_start", { ascending: true });
      if (scoreHistData && scoreHistData.length > 0) {
        const byPillar = {};
        for (const r of scoreHistData) {
          if (!r.pillar || !r.possible) continue;
          (byPillar[r.pillar] ||= []).push({
            week_start: r.week_start,
            pct: Math.round((Number(r.earned) / Number(r.possible)) * 100),
          });
        }
        setScoreHistory(byPillar);
      }

      clearTimeout(loadTimeout);
      setDataLoaded(true);
    };
    loadData();
    // reloadTick: bumped by the reconnect listener so an offline launch's
    // empty state re-loads the moment signal returns. loadData is idempotent
    // (setters plus idempotent heals), so a re-run is always safe.
  }, [user, reloadTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // CACHE-FIRST: persist the dashboard snapshot (DATA only, timestamps intact,
  // no tokens) whenever it changes after the first load, debounced. Read back on
  // the next cold launch by the hydrate block above. Quota/serialization
  // failures are swallowed — the network load still runs. Never in demo.
  useEffect(() => {
    if (isDemoMode || !user?.id || !dataLoaded) return;
    const t = setTimeout(() => {
      try {
        const snap = { v: APP_CACHE_VERSION, uid: user.id, savedAt: Date.now(), d: {
          coachName, avatarUrl, notifPrefs, aiConsent, enabledTrackers,
          username, // the greeting's name and the avatar initials, shown before the network
          // journalEntries deliberately absent (review L7): entry text never
          // sits at rest in localStorage; the wellness tab loads it live.
          bodyStats, goals, habits, moodHistory,
          monthlyIncome, monthlyExpenses, savings, hasFinancesRow, savingsKnown, creditDetails, creditScore,
          debts, bills, medications, checkups, supplements, recoveryTrackers,
          dailyActivity, workouts, healthLastSync,
          plaidConnected, plaidAccounts, plaidTransactions, plaidBalancesUpdatedAt, plaidConnectedAt,
          incomeRules, scoreHistory, progress,
          // Each pillar's last complete score + computed_at, shown at launch until the inputs are in.
          pillarSnapshot: Object.fromEntries(Object.entries(lastCompletePillars).map(([k, v]) => [k, snapshotPillar(v)])),
        } };
        localStorage.setItem(userKey(user.id, `app_cache_v${APP_CACHE_VERSION}`), JSON.stringify(snap));
      } catch { /* quota/serialize — skip; last snapshot (or none) stands */ }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, coachName, avatarUrl, notifPrefs, aiConsent, enabledTrackers,
      bodyStats, goals, habits, moodHistory,
      monthlyIncome, monthlyExpenses, savings, hasFinancesRow, savingsKnown, creditDetails, creditScore,
      debts, bills, medications, checkups, supplements, recoveryTrackers,
      dailyActivity, workouts, healthLastSync,
      plaidConnected, plaidAccounts, plaidTransactions, plaidBalancesUpdatedAt, plaidConnectedAt,
      incomeRules, scoreHistory, progress]);

  // ── PLAID: cache-first, free sync when cache is stale (>15m) or manual retry ──
  // /transactions/sync is free (no paid /accounts/balance/get), so app-open syncs
  // whenever the last sync is >15 min old; the server throttles the real Plaid call
  // to the same 15 min so rapid opens can't spam Plaid.
  useEffect(() => {
    if (!user || isDemo) return;

    const STALE_MS = 15 * 60 * 1000; // 15 minutes

    const fetchPlaid = async () => {
      setPlaidFetching(true);
      setPlaidRateLimited(null); // a fresh attempt clears any prior rate-limit notice
      // [UIDIAG] This mount fetch IS the refresh in the fresh-context resume
      // shape (iOS killed the WebView; the page reloaded; no visibilitychange
      // fires, so the resume sync listener below never runs). The resume
      // health check used to read only that listener's stamp and reported
      // every fresh-context resume as "not_triggered" (SCORIFY-APP-13, 4/4
      // events) while this path was mid-flight. Stamp it here too.
      resumeRefreshRef.current = { status: "started", at: Date.now() };
      addBreadcrumb("resume", "refresh start", { kind: "plaid_mount" });
      const stamp = (status, extra) => { resumeRefreshRef.current = { status, at: Date.now() }; addBreadcrumb("resume", "refresh end", { kind: "plaid_mount", status, ...(extra || {}) }); };
      try {
        // 1. Serve cache immediately so UI populates on first render
        let cacheAge = Infinity;
        try {
          const cacheRes = await apiFetch(apiUrl("/api/plaid"), {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "get_cache", userId: user.id }),
          });
          // A non-ok response now means the cache could not be READ, not that it
          // is empty — the server used to collapse both into 200 { cache: null }.
          // Falling through to the live get_transactions below is the correct
          // recovery either way, but the two are logged differently so a
          // persistent cache outage is visible rather than looking like a user
          // who has never linked a bank.
          if (!cacheRes.ok) {
            console.error("plaid cache unreadable", cacheRes.status, "— falling back to live fetch");
          } else {
            const cacheData = await cacheRes.json();
            // Per-bank list rides on the cache (or top-level when the cache is
            // empty); needs_reauth is persisted server-side, so the broken bank
            // is known on a cold load without a live sync.
            const cachedItems = cacheData.cache?.items ?? cacheData.items;
            if (Array.isArray(cachedItems)) { setPlaidItems(cachedItems); setPlaidReauth(cachedItems.some(i => i.needs_reauth)); }
            if (cacheData.cache?.accounts?.length > 0) {
              setPlaidConnected(true);                          plaidConnectedRef.current = true;
              setPlaidAccounts(cacheData.cache.accounts);       plaidAccountsRef.current = cacheData.cache.accounts;
              setPlaidTransactions(cacheData.cache.transactions || []); plaidTransactionsRef.current = cacheData.cache.transactions || [];
              setPlaidBalancesUpdatedAt(cacheData.cache.balances_updated_at || null); plaidBalancesUpdatedAtRef.current = cacheData.cache.balances_updated_at || null;
              setPlaidConnectedAt(cacheData.cache.connected_at || null); plaidConnectedAtRef.current = cacheData.cache.connected_at || null;
              if (cacheData.cache.updated_at) {
                cacheAge = Date.now() - new Date(cacheData.cache.updated_at).getTime();
              }
            }
          }
        } catch (e) {
          console.error("plaid cache request failed —", e?.message, "— falling back to live fetch");
        }

        // 2. Decide whether to call get_transactions (returns accounts+balances for free)
        const isManualRetry = plaidRetry > 0;
        const cacheIsFresh = cacheAge < STALE_MS;

        // Normal mount: skip if cache is < 15 minutes old (last sync is recent)
        if (!isManualRetry && cacheIsFresh) { stamp("ok", { reason: "cache_fresh" }); setPlaidFetching(false); return; }
        // Manual retry: 30-second guard to prevent mashing
        if (isManualRetry && Date.now() - lastPlaidCall.current < 30000) { stamp("ok", { reason: "retry_guard" }); setPlaidFetching(false); return; }

        lastPlaidCall.current = Date.now();
        // get_transactions returns accounts with balances — no separate $0.10 Balance call needed
        const txRes = await apiFetch(apiUrl("/api/plaid"), {
          method: "POST", headers: { "Content-Type": "application/json" },
          // force only on an explicit user retry — the server's 1-hour cache
          // guard otherwise prevents a 20-call Plaid fan-out per page load.
          body: JSON.stringify({ action: "get_transactions", userId: user.id, force: isManualRetry }),
        });
        if (txRes.ok) {
          const txData = await txRes.json();
          const freshAccounts = txData.accounts || [];
          const freshTx = txData.transactions || [];
          if (freshAccounts.length > 0) {
            setPlaidConnected(true);               plaidConnectedRef.current = true;
            setPlaidAccounts(freshAccounts);        plaidAccountsRef.current = freshAccounts;
          }
          const prevBalancesAt = plaidBalancesUpdatedAtRef.current;
          if (txData.balances_updated_at !== undefined) { setPlaidBalancesUpdatedAt(txData.balances_updated_at); plaidBalancesUpdatedAtRef.current = txData.balances_updated_at; }
          if (txData.connected_at !== undefined) { setPlaidConnectedAt(txData.connected_at); plaidConnectedAtRef.current = txData.connected_at; }
          setPlaidTransactions(freshTx);           plaidTransactionsRef.current = freshTx;
          // Cache is now written server-side by get_transactions
          setPlaidError(false);
          // Reauth is PER BANK now: a 200 with one failed Item among two is only
          // visible in `items` (the old unconditional false hid it).
          if (Array.isArray(txData.items)) { setPlaidItems(txData.items); setPlaidReauth(txData.items.some(i => i.needs_reauth)); }
          else setPlaidReauth(!!txData.reauth_required);
          stamp("ok");
          // Visible result of a DELIBERATE tap: "Updated", or an honest
          // "balance_stale" when the transactions refreshed but the balance
          // did not (its 6/day bucket was spent or the live call failed). A
          // green check over a stale balance is the silent-wrong-number bug.
          if (isManualRetry) {
            // balance_refresh_reason distinguishes WHY the balance did not
            // move (2026-09-28: a silently failing balance call read as
            // "refresh limit" for six days). rate_limited keeps the limit
            // wording; failed says so plainly; reauth_required raises the
            // reconnect banner instead of a footnote. Older servers send
            // only balance_refresh_skipped — fall back to the limit wording.
            const reason = txData.balance_refresh_skipped ? (txData.balance_refresh_reason || "rate_limited") : null;
            if (reason === "reauth_required") {
              setPlaidReauth(true);
              setPlaidRefreshMsg(null);
            } else if (!reason && prevBalancesAt && txData.balances_updated_at
              && new Date(prevBalancesAt).getTime() === new Date(txData.balances_updated_at).getTime()) {
              // The bank handed back the SAME snapshot: say so instead of a
              // green "Updated" that implies a live pull (2026-10-28).
              setPlaidRefreshMsg(null);
              showToast(BALANCE_SNAPSHOT_TOAST, "info");
            } else {
              setPlaidRefreshMsg(reason || "updated");
            }
            if (plaidRefreshMsgTimer.current) clearTimeout(plaidRefreshMsgTimer.current);
            plaidRefreshMsgTimer.current = setTimeout(() => setPlaidRefreshMsg(null), 5000);
          }
        } else {
          // There was no else here at all. apiFetch does not reject on a non-2xx,
          // so a 400 from Plaid fell straight through: plaidError was never set,
          // the error banner never rendered, and the Finance tab simply showed
          // stale or empty data with no explanation.
          const body = await txRes.json().catch(() => ({}));
          stamp(`failed_http_${txRes.status}`);
          if (txRes.status === 429) {
            // Rate limited — an EXPECTED outcome, not a Sentry error. force
            // skips caches, never limits (by design), so a burst of manual
            // taps hits this; surface it instead of the old silent no-op.
            setPlaidRateLimited({ message: body?.message || "Too many refreshes. Please wait a moment and try again." });
          } else if (txRes.status === 401 || txRes.status === 403) {
            // AUTH-LAYER, not a Plaid item (SCORIFY-APP-2, 2026-09-29). A
            // 401/403 here is our own requireAuth rejecting an expired or
            // revoked Supabase session — NOT ITEM_LOGIN_REQUIRED, which Plaid
            // surfaces as requires_reauth on a 400 and which the branch below
            // handles with the reconnect banner. apiFetch already refreshed
            // the token and retried, and if it still 401'd while online handed
            // off to sign-in — so this outcome is transient and already owned.
            // Do NOT report it as a plaid failure or raise the generic error
            // banner; that just adds noise for a session being signed out.
            console.warn("plaid get_transactions auth", txRes.status, "— session refresh/handoff owns this");
          } else {
            console.error("plaid get_transactions failed", txRes.status, body?.error_code || "", body?.error || "");
            // apiFetch already reported the status; this adds Plaid's error_code,
            // which is a fixed enum and the thing that actually identifies the
            // fault. The body itself carries account data and is never sent.
            reportError(new Error(`plaid get_transactions ${txRes.status} ${body?.error_code || "unknown"}`), {
              op: "plaid.get_transactions", status: txRes.status,
              error_code: body?.error_code, requires_reauth: !!body?.requires_reauth,
            });
            if (body?.requires_reauth) {
              // ITEM_LOGIN_REQUIRED. Retrying this forever cannot succeed — the
              // bank has invalidated the connection and it must be re-established.
              setPlaidReauth(true);
              setPlaidError(false);
            } else {
              setPlaidError(true);
            }
          }
        }
      } catch (e) {
        console.error("plaid request threw —", e?.message);
        stamp(e?.name === "TimeoutError" ? "failed_timeout" : "failed_throw");
        reportError(e, { op: "plaid.sync" });
        setPlaidError(true);
      } finally {
        setPlaidFetching(false);
        // The finance pillar's Plaid input: served (cache or fresh, or no bank
        // at all) = loaded; a failed fetch with nothing served = failed.
        markInput("plaid_cache", (plaidConnectedRef.current || /^ok/.test(resumeRefreshRef.current?.status || "")) ? "loaded" : "failed");
      }
    };
    fetchPlaid();
  }, [user, plaidRetry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── RECONNECT ── one listener set drives everything offline-related: the
  // banner state, the loadData re-run, and the Plaid retry. The 'online'
  // event is NOT delivered while iOS keeps the WebView suspended in the
  // background — events during suspension are lost, not queued — so the
  // visibilitychange re-check on foreground synthesizes the transition a
  // backgrounded reconnect would otherwise miss.
  useEffect(() => {
    if (isDemo) return;
    const sync = () => {
      const off = typeof navigator !== "undefined" && navigator.onLine === false;
      setIsOffline(off);
      if (off === wasOffline.current) return;
      wasOffline.current = off;
      if (!off) {
        // Back online: reload everything loadData owns. Plaid re-fires
        // through the existing manual-retry counter, but only when nothing
        // ever loaded — a connected account with data visible keeps its
        // manual "Tap to retry" instead of forcing a billed Plaid call on
        // every signal blip.
        setReloadTick(t => t + 1);
        if (!plaidConnectedRef.current) { lastPlaidCall.current = 0; setPlaidRetry(n => n + 1); }
      }
    };
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PLAID: free sync on foreground/resume for CONNECTED users ───────────────
  // The mount effect covers a cold launch; this covers resuming a warm app (iOS
  // keeps the WebView suspended, so the mount effect does not re-run). /transactions/
  // sync is free, so we refresh whenever the last sync is >15 min old; the server
  // throttles the real Plaid call to the same 15 min as a backstop against rapid
  // opens. Never calls the paid /accounts/balance/get.
  useEffect(() => {
    if (isDemo) return;
    const SYNC_AFTER_MS = 15 * 60 * 1000;
    const run = async () => {
      if (document.visibilityState !== "visible") return;
      if (!user || !plaidConnectedRef.current) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      // balances_updated_at advances at each sync, so it is our "last synced" clock.
      const lastMs = plaidBalancesUpdatedAtRef.current ? new Date(plaidBalancesUpdatedAtRef.current).getTime() : 0;
      if (lastMs && Date.now() - lastMs < SYNC_AFTER_MS) return; // client 15-min gate
      if (Date.now() - lastPlaidCall.current < 30000) return;    // anti-mash
      lastPlaidCall.current = Date.now();
      // [UIDIAG] diagnostics only — the resume health check reads this status.
      resumeRefreshRef.current = { status: "started", at: Date.now() };
      addBreadcrumb("resume", "refresh start", { kind: "plaid_sync" });
      try {
        const res = await apiFetch(apiUrl("/api/plaid"), {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "sync", userId: user.id }),
        });
        if (!res.ok) { // keep showing cached data
          resumeRefreshRef.current = { status: `failed_http_${res.status}`, at: Date.now() };
          addBreadcrumb("resume", "refresh end", { kind: "plaid_sync", status: res.status });
          return;
        }
        const d = await res.json();
        if (Array.isArray(d.accounts) && d.accounts.length) { setPlaidAccounts(d.accounts); plaidAccountsRef.current = d.accounts; setPlaidConnected(true); plaidConnectedRef.current = true; }
        if (Array.isArray(d.transactions)) { setPlaidTransactions(d.transactions); plaidTransactionsRef.current = d.transactions; }
        if (d.balances_updated_at !== undefined) { setPlaidBalancesUpdatedAt(d.balances_updated_at); plaidBalancesUpdatedAtRef.current = d.balances_updated_at; }
        resumeRefreshRef.current = { status: "ok", at: Date.now() };
        addBreadcrumb("resume", "refresh end", { kind: "plaid_sync", status: "ok" });
      } catch { /* offline/transient — keep cached data */
        resumeRefreshRef.current = { status: "failed_throw", at: Date.now() };
        addBreadcrumb("resume", "refresh end", { kind: "plaid_sync", status: "threw" });
      }
    };
    document.addEventListener("visibilitychange", run);
    window.addEventListener("focus", run);
    return () => {
      document.removeEventListener("visibilitychange", run);
      window.removeEventListener("focus", run);
    };
  }, [user, isDemo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Award daily login XP whenever progress loads and it's a new day
  useEffect(() => { if (user && progress.xp !== undefined) awardDailyLogin(); }, [progress.last_active_date, user]);

  // Snapshot this month's cushion balance so NEXT month can award growth credit. Upsert
  // (idempotent per month); the balance tracks the latest known value this month and
  // freezes once the month passes. Runs when the cushion inputs settle.
  useEffect(() => {
    if (isDemo || !user || !dataLoaded) return;
    const bal = cushionBalance({ plaidConnected, plaidAccounts, savings, otherAssets: otherAssets.reduce((a, x) => a + (Number(x.amount) || 0), 0) });
    const ym = localDateStr().slice(0, 7);
    supabase.from("savings_snapshots").upsert(
      { user_id: user.id, year_month: ym, balance: bal, updated_at: new Date().toISOString() },
      { onConflict: "user_id,year_month" }
    ).then(({ error }) => { if (error) console.error("savings snapshot upsert failed", error.message); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dataLoaded, plaidConnected, savings, otherAssets]);

  // ── MIDNIGHT SUPPLEMENT RESET ───────────────────────────────────────────────
  useEffect(() => {
    const getMsUntilMidnight = () => {
      const now = new Date();
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
      return midnight - now;
    };
    let timer;
    const scheduleReset = () => {
      timer = setTimeout(async () => {
        // Reset state
        setSupplements(prev => prev.map(s => ({ ...s, takenToday: false })));
        // Reset in Supabase. Awaited with an explicit catch: this fires while the
        // app is idle at midnight, so an un-awaited rejection here surfaced as a
        // bare unhandled rejection with no context. A failure is not toasted —
        // the user is asleep, and the reset self-heals on the next load via the
        // last_taken_date comparison in loadData — but it must be logged.
        if (user) {
          try {
            const { error } = await supabase.from("supplements")
              .update({ taken_today: false }).eq("user_id", user.id);
            if (error) console.error("midnight supplement reset rejected —", error.message);
          } catch (e) {
            console.error("midnight supplement reset threw —", e?.message);
          }
        }
        // Reschedule for next midnight
        scheduleReset();
      }, getMsUntilMidnight());
    };
    scheduleReset();
    return () => clearTimeout(timer);
  }, [user]);

  // ── SUPABASE: Save mood when logged ────────────────────────────────────────
  // Refetch fitness state when a HealthKit sync lands. App.js fires the syncs
  // on session start, in parallel with loadData — on a fresh account loadData
  // read workouts/daily_activity BEFORE the first upserts landed while the
  // (fast) workouts sync had already stamped health_last_sync_at, so the
  // dashboard held empty arrays + a truthy sync stamp all session. That is
  // byte-for-byte the "revoked access" signature getGoalAutoValue's guard
  // checks, so a correctly-bound workout goal rendered "syncing…" forever
  // while the Health card (which runs its own queries when its tab mounts,
  // later) happily showed the miles. Same queries as loadData, so the two
  // reads cannot drift.
  useEffect(() => {
    if (!user?.id || isDemoMode) return;
    const onSynced = async () => {
      try {
        const [{ data: w }, { data: a }, { data: prof }] = await Promise.all([
          supabase.from("workouts").select("id,type,name,duration_min,notes,date,distance_m,source,created_at,start_at,end_at").eq("user_id", user.id).order("date", { ascending: false }).limit(60),
          supabase.from("daily_activity").select("date,steps,distance_m").eq("user_id", user.id).order("date", { ascending: false }).limit(60),
          supabase.from("profiles").select("health_last_sync_at").eq("id", user.id).maybeSingle(),
        ]);
        if (w) setWorkouts(w);
        if (a) setDailyActivity(a);
        if (prof?.health_last_sync_at) setHealthLastSync(prof.health_last_sync_at);
      } catch (e) { console.warn("health resync refresh failed", e?.message); }
    };
    window.addEventListener("scorify-health-synced", onSynced);
    return () => window.removeEventListener("scorify-health-synced", onSynced);
  }, [user?.id]);

  // ── SCREEN TIME: keep today's window armed, sync day records on open ──────
  // Arming is idempotent (the plugin no-ops when today is armed under the
  // same cap) and the extension keeps FUTURE days alive by chaining at each
  // natural close (device-proven 2026-09-14) — this effect exists so a
  // broken chain (phone died overnight, iOS killed monitoring) self-heals on
  // the next open instead of silently producing unknown days forever. The
  // delayed re-sync catches the freshly-armed window's intervalDidStart
  // record, which lands a few seconds AFTER the first sync read the group.
  const stGoal = goals.find(g => g.source_type === "screen_time" && !g.completed);
  const stGoalKey = stGoal ? `${stGoal.id}:${stGoal.period_target}` : "";
  // The arm/rearm-then-sync sequence, extracted so BOTH mount and a
  // foreground RESUME can run it. Warm resume (app switched away and back,
  // no kill) never re-runs loadData, so before this the whole screen-time
  // refresh only happened on cold launch — a day could close and chain
  // overnight and never reach Supabase until a force-quit (device-confirmed
  // 2026-09-15). Idempotent: arm no-ops when today is already armed at the
  // same cap, and rearmUsage is gated on !armedToday, so calling this on
  // every foreground cannot churn windows.
  const runScreenTimeRefresh = useCallback(async (trigger = "mount") => {
    if (!user?.id || isDemoMode || !native) return;
    // Demo account: display-only, no plugin call of any kind (see isDemoAccount).
    if (isDemoAccount) { setStStatus(DEMO_ST_STATUS); return; }
    // OWNERSHIP FIRST — before status is read, arming happens, or records are
    // synced. The App Group is device-wide and unowned, so a device where a
    // PREVIOUS account set up Screen Time would otherwise show and sync that
    // account's arming/usage as this one's (device report 2026-09-22:
    // "watching since 7:30 PM" from an earlier setup). reconcile resets a
    // foreign account's state (this user starts clean) and adopts/claims for
    // this account, so everything below operates on this account's own state.
    await reconcileScreenTimeOwner(user.id);
    // Status FIRST, and arm only when the device is actually ready —
    // authorization approved and an app selection saved. An unauthorized
    // device (fresh install, Settings revoke, OS update resetting Family
    // Controls) is an expected state the goal card surfaces via
    // noSource:'screen_time', never an error (SCORIFY-APP-6).
    const status = await screenTimeStatus();
    setStStatus(status);
    // ── SCREEN-TIME SILENT-FAILURE MONITORS ──────────────────────────────────
    // (1) Chain broke: the user wants tracking (a cap goal or the usage ladder),
    // iOS permission is still approved and a selection still exists, yet today
    // is not armed — tracking stopped without the user turning it off or
    // revoking permission. Report once (the re-arm just below heals it). This is
    // the detector for the build-6 "not tracking" class.
    const wantsTracking = (!!stGoalKey && status?.hasSelection) || (status?.usageEnabled && status?.hasUsageSelection);
    if (status?.available && status.authorization === "approved" && wantsTracking && !status.armedToday && !screenTimeChainBreakReported.current) {
      screenTimeChainBreakReported.current = true;
      try { reportError(new Error("screen-time chain broke: enabled + authorized + selection present but not armed today"), { op: "sanity.screentime_chain_break", level: "warning", trigger }); } catch {}
    }
    // (2) Restarted more than once today for a non-version reason — each restart
    // loses the day's earlier usage, so this should be rare. Legitimate causes
    // are already excluded by construction: with the usage/cap split a goal edit
    // re-arms only the CAP window (rearmWindow increments only for usage
    // restarts), an account switch calls reset() which clears the counter, and a
    // fresh permission grant re-arms an UNarmed window (wasArmed false, not
    // counted). What remains is genuinely repeated usage-losing restarts.
    // Report at most ONCE per user per LOCAL DAY — the old per-session ref fired
    // up to once per launch (SCORIFY-APP-A: 7 events on 2026-09-22).
    if ((status?.rearmsToday || 0) > 1) {
      try {
        const k = userKey(user.id, "st_restart_alerted");
        const today = localDateStr();
        if (k && localStorage.getItem(k) !== today) {
          localStorage.setItem(k, today);
          reportError(new Error(`screen-time restarted ${status.rearmsToday} times today (non-version) — each loses earlier usage`), { op: "sanity.screentime_restarts", level: "warning", rearms: status.rearmsToday });
        }
      } catch {}
    }
    // Cap goal and usage ladder are INDEPENDENT windows (usage/cap split): arm
    // each on its own, never else-if — a cap-goal user with the ladder also on
    // needs BOTH armed, and the old else-if left the usage window unarmed for
    // them. armScreenTime / rearmUsage each no-op when their own window already
    // covers today at the current version, so this never churns a healthy chain.
    if (stGoalKey && status?.available && status.authorization === "approved" && status.hasSelection) {
      const capMinutes = Number(stGoalKey.split(":")[1]) || 0;
      // armScreenTime force-re-arms the CAP window on a version bump itself.
      if (capMinutes >= 15) await armScreenTime(capMinutes);
    }
    if (status?.available && status.usageEnabled && status.hasUsageSelection && status.authorization === "approved" && (!status.usageArmedToday || status.needsRearm)) {
      // Re-arm the USAGE window when its own window is missing (chain broke, or
      // a cap-goal user never had a usage window) OR when a shipped
      // counting-logic fix means the armed window is stale (needsRearm) — so the
      // fix applies on next launch with no user action. Gated on usageArmedToday
      // (this window specifically), not armedToday, so a cap-goal user's armed
      // cap window can't suppress arming the separate usage window.
      await rearmUsage();
    }
    await syncScreenTime(user.id);
  }, [user?.id, isDemoMode, isDemoAccount, stGoalKey]);

  useEffect(() => {
    if (!user?.id || isDemoMode || !native || !dataLoaded) return;
    // Demo account: never sync — an App Group read would upsert over the seeded rows.
    if (isDemoAccount) { setStStatus(DEMO_ST_STATUS); return; }
    let lateSync;
    runScreenTimeRefresh().then(() => {
      // The delayed re-sync catches the freshly-armed window's
      // intervalDidStart record, which lands a few seconds AFTER the first
      // sync read the App Group.
      lateSync = setTimeout(() => syncScreenTime(user.id), 8000);
    });
    // Warm resume: iOS foregrounds the WebView without re-running loadData,
    // so this is the ONLY trigger for a resume. Use Capacitor's NATIVE
    // appStateChange, not the web `visibilitychange` — the latter does not
    // reliably fire inside a WKWebView when backgrounding via the app
    // switcher (device-confirmed 2026-09-15: a background→foreground round
    // trip produced NO second sync). appStateChange is the native
    // foreground/background bridge event and is the documented correct
    // signal on iOS/Android. Idempotent refresh (arm no-ops at same cap,
    // rearm gated on !armedToday) so firing on every activation is safe.
    let removeListener;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) runScreenTimeRefresh("appstate");
    }).then((handle) => {
      removeListener = () => handle.remove();
    }).catch(() => { /* listener registration failed: the mount refresh still ran */ });
    return () => { clearTimeout(lateSync); if (removeListener) removeListener(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isDemoMode, isDemoAccount, dataLoaded, stGoalKey, runScreenTimeRefresh]);

  // Refetch day records when a sync lands — same pattern as
  // scorify-health-synced, so the goal card can never show a stale vintage.
  useEffect(() => {
    if (!user?.id || isDemoMode) return;
    const onStSynced = async () => {
      try {
        const { data } = await supabase.from("screen_time_days").select("*").eq("user_id", user.id).order("date", { ascending: false }).limit(14);
        if (data) setScreenTimeDays(data);
      } catch (e) { console.warn("screen time resync refresh failed", e?.message); }
    };
    window.addEventListener("scorify-screentime-synced", onStSynced);
    return () => window.removeEventListener("scorify-screentime-synced", onStSynced);
  }, [user?.id, isDemoMode]);

  // Returns true when the mood write landed — logMood gates its local state on
  // this, so a failed save no longer renders as logged.
  const saveMood = async (score, note) => {
    if (!user) return true; // demo: local-only by design
    if (!requirePro("mood")) return false;
    // ONE mood per local day, saved once (src/lib/moodSave.js, 2026-10-15):
    // nine taps in five seconds used to write nine rows, because this was a
    // read-then-write with no in-flight guard. Repeat taps now join the save
    // in flight; a later save the same day updates today's row; extra
    // same-day rows left by a race are removed, newest kept.
    if (!moodSaverRef.current || moodSaverRef.current.uid !== user.id) {
      const uid = user.id;
      moodSaverRef.current = { uid, save: createMoodSaver({
        findToday: async () => {
          const { startIso, endIso } = localDayBoundsIso(new Date());
          const { data } = await supabase.from("moods").select("id, note").eq("user_id", uid)
            .gte("logged_at", startIso).lte("logged_at", endIso).order("logged_at", { ascending: false });
          return data || [];
        },
        insert: (row) => persist("your mood", supabase.from("moods").insert([{ user_id: uid, ...row }])),
        update: (id, patch) => persist("your mood", supabase.from("moods").update(patch).eq("id", id).eq("user_id", uid)),
        remove: (ids) => persist("your mood", supabase.from("moods").delete().in("id", ids).eq("user_id", uid)),
      }) };
    }
    const { ok, mode, existing } = await moodSaverRef.current.save(score, note);
    if (!ok) return false;
    // XP only for a NEW day's log (an edit is not a second log).
    if (mode === "inserted") awardXP("mood");
    setSavedMood(score);
    // Journal bridge (ledger 5.4 / PLAN "N4"). Two fixes, one non-fix:
    // - The premise "the note never reaches journal_entries" is FALSE in live
    //   data: every mood note since the bridge shipped (2026-05-09) has a
    //   journal row; the 10 unjournaled notes all predate it. Verified 2026-09-10.
    // - Real defect 1: the insert's error was discarded — a transient failure
    //   silently dropped the journal copy. persistRow now toasts and reports.
    // - Real defect 2: the bridge ran on the UPDATE path too, so re-logging
    //   today's mood with the note still in the box inserted a DUPLICATE
    //   journal entry. Skipped now when the note matches what today's mood row
    //   already carried (a changed or newly added note still journals).
    const trimmed = (note || "").trim();
    if (trimmed && trimmed !== (existing?.note || "").trim()) {
      const journalEntry = {
        user_id: user.id,
        text: trimmed,
        mood_score: score,
        created_at: new Date().toISOString(),
        date: localDateStr(),
      };
      const data = await persistRow("your journal note", supabase.from("journal_entries").insert([journalEntry]).select().single());
      if (data) setJournalEntries(prev => [data, ...prev]);
      // A failed journal copy does not unwind the mood log — the mood row
      // landed and the note itself is preserved on it; the toast says the
      // journal copy failed.
    }
    return true;
  };

  // ── SUPABASE: Save habit log ────────────────────────────────────────────────
  const saveHabit = async (habitId, streak, weekCount, completions, extraMeta, opts = {}) => {
    if (!user) return;
    const today = localDateStr();
    // opts.lastCompleted: a Health-side tick for a day up to 2 days back keeps the newest date.
    const lastCompleted = opts.lastCompleted && opts.lastCompleted > today ? today : (opts.lastCompleted || today);
    const dow = new Date().getDay();
    const startOfWeek = new Date();
    startOfWeek.setDate(new Date().getDate() - (dow === 0 ? 6 : dow - 1));
    startOfWeek.setHours(0,0,0,0);
    // Trim to last 90 days before persisting
    const cutoff90 = new Date(); cutoff90.setDate(cutoff90.getDate() - 90);
    const cutoff90Str = localDateStr(cutoff90);
    const historyJson = JSON.stringify((completions || []).filter(d => d >= cutoff90Str));
    const { data: existing } = await supabase
      .from("habits")
      .select("id, last_week_reset")
      .eq("user_id", user.id)
      .eq("name", habitId)
      .maybeSingle();
    // Local date-string compare — see the loader: new Date('YYYY-MM-DD') is UTC midnight.
    const isNewWeek = !existing?.last_week_reset || String(existing.last_week_reset).slice(0, 10) < localDateStr(startOfWeek);
    const payload = {
      completed: true,
      streak,
      week_count: weekCount,
      history: historyJson,
      last_completed: lastCompleted,
      last_logged_at: new Date().toISOString(), // the log INSTANT (briefStaleness)
      last_week_reset: isNewWeek ? localDateStr(startOfWeek) : existing?.last_week_reset,
    };
    // Persist label/icon/target/unit — passed explicitly to avoid stale closure
    if (extraMeta) {
      if (extraMeta.label) payload.label = extraMeta.label;
      if (extraMeta.icon)  payload.icon  = extraMeta.icon;
      if (extraMeta.target) payload.target = extraMeta.target;
      if (extraMeta.unit)  payload.unit  = extraMeta.unit;
    }
    if (existing) {
      await persist("that habit", supabase.from("habits").update(payload).eq("id", existing.id));
    } else {
      await persist("that habit", supabase.from("habits").insert([{ user_id: user.id, name: habitId, completed: true, ...payload }]));
    }
    awardXP("habit");
  };

  // ── GOALS CRUD ────────────────────────────────────────────────────────────────
  const addRecovery = async () => {
    if (!user) return;
    const label = newRecovery.substance === "custom" ? newRecovery.custom.trim() : {
      alcohol:"Alcohol", nicotine:"Nicotine / Vaping", drugs:"Drugs",
      gambling:"Gambling", social:"Social Media / Phone"
    }[newRecovery.substance];
    if (!label) return;
    const icon = { alcohol:"", nicotine:"", drugs:"", gambling:"", social:"", custom:"" }[newRecovery.substance] || "";
    // Gated on the write landing (map 5.4): failure keeps the modal open with
    // what the user typed, and persistRow has already toasted why.
    const data = await persistRow("that tracker", supabase.from("recovery_trackers")
      .insert([{ user_id: user.id, label, icon, start_date: newRecovery.start_date, best_streak: 0 }])
      .select().single());
    if (!data) return;
    setRecoveryTrackers(p => [...p, data]);
    setShowAddRecovery(false);
    setNewRecovery({ substance:"alcohol", custom:"", start_date: localDateStr() });
  };

  const resetRecovery = async (tracker) => {
    const currentStreak = Math.floor((new Date() - new Date(tracker.start_date)) / (1000*60*60*24));
    const newBest = Math.max(tracker.best_streak || tracker.streak || 0, currentStreak);
    const today = localDateStr();
    // No modal here — this is the card's reset button. Gating (map 5.5) is
    // about the toast: a failed reset used to look identical to success while
    // the streak quietly kept its old start date in the DB.
    const data = await persistRow("that tracker", supabase.from("recovery_trackers")
      .update({ start_date: today, best_streak: newBest })
      .eq("id", tracker.id).select().single());
    if (!data) return;
    setRecoveryTrackers(p => p.map(t => t.id === tracker.id ? data : t));
  };

  const deleteRecovery = async (id) => {
    if (!(await persist("that tracker", supabase.from("recovery_trackers").delete().eq("id", id)))) return;
    setRecoveryTrackers(p => p.filter(t => t.id !== id));
  };

  // Answer an income-candidate prompt. One row per normalized name, forever —
  // the card disappears for that name on either answer. State is gated on the
  // write landing, so a rejected insert leaves the card up instead of silently
  // "confirming" a rule that does not exist.
  const confirmIncomeRule = async (matchName, classification) => {
    if (!user || isDemoMode) return;
    if (!(await persist("your income setting", supabase.from("income_rules")
      .insert([{ user_id: user.id, match_name: matchName, classification }])))) return;
    setIncomeRules(p => [...p, { user_id: user.id, match_name: matchName, classification }]);
  };

  // Answer a recurring-charge prompt, once per merchant ever (recurring_rules).
  // "Add to bills" inserts the bill (due_day from the pattern, match_name so a
  // matching charge auto-marks it paid) and then the rule; both gated on the
  // write landing. Never adds anything without this tap.
  const answerRecurring = async (p, decision) => {
    if (!user || isDemoMode) return;
    if (decision === "bill" || decision === "recurring") {
      // Monthly → a monthly bill; weekly ("Yes, it's recurring") → a weekly bill.
      const data = await persistRow("that bill", supabase.from("bills").insert([billFromPattern(p, user.id)]).select().single());
      if (!data) return;
      setBills(b => [...b, data]);
    }
    await saveRecurringRule(p.matchName, decision, { typical_amount: p.typicalAmount, usual_day: p.usualDay, ...(decision === "not_bill" ? { dismissed_from: "prompt" } : {}) });
  };
  // One recurring_rules row per merchant (upsert: a later answer replaces an
  // earlier one, e.g. "Not a subscription" on a bill first added as one).
  const saveRecurringRule = async (matchName, decision, extra = {}) => {
    if (!user || !matchName) return false;
    const rule = { user_id: user.id, match_name: matchName, decision, dismissed_from: null, ...extra };
    if (!(await persist("your answer", supabase.from("recurring_rules").upsert([rule], { onConflict: "user_id,match_name" })))) return false;
    setRecurringRules(r => [...r.filter(x => x.match_name !== matchName), rule]);
    return true;
  };
  // Subscriptions list: edit amount/day, remove as "Not a subscription", add one.
  const saveSubscriptionEdit = async (b, { amount, day, month }) => {
    const weekly = b.cadence === "weekly";
    const d = parseInt(day, 10);
    const changes = { amount: Math.round((parseFloat(amount) || 0) * 100) / 100,
      ...(weekly ? { weekday: Math.min(Math.max(Number.isFinite(d) ? d : 0, 0), 6) } : { due_day: Math.min(Math.max(Number.isFinite(d) ? d : 1, 1), 31) }),
      ...(b.cadence === "yearly" ? { due_month: Math.min(Math.max(parseInt(month, 10) || 1, 1), 12) } : {}) };
    if (!(await persist("that subscription", supabase.from("bills").update(changes).eq("id", b.id)))) return false;
    setBills(p => p.map(x => x.id === b.id ? { ...x, ...changes } : x));
    return true;
  };
  const removeSubscription = async (b) => {
    if (!(await persist("that subscription", supabase.from("bills").delete().eq("id", b.id)))) return;
    setBills(p => p.filter(x => x.id !== b.id));
    // Remembered for detected/linked ones only (removalRuleFor): a hand-entered
    // bill that was never linked has nothing for the detector to block.
    const r = removalRuleFor(b, user?.id);
    if (r) await saveRecurringRule(r.match_name, "not_bill", { typical_amount: r.typical_amount, usual_day: r.usual_day, dismissed_from: "subscriptions" });
  };
  const addManualSubscription = async (form) => {
    if (!user || !String(form.name || "").trim() || !(parseFloat(form.amount) > 0)) return false;
    const data = await persistRow("that subscription", supabase.from("bills").insert([billFromManual(form, user.id)]).select().single());
    if (!data) return false;
    setBills(p => [...p, data]);
    // Answered too (the picked merchant, else the typed name's key), so it is
    // not asked about again and an earlier "not a subscription" no longer stands.
    await saveRecurringRule(form.matchName || normalizeInflowName(form.name), form.cadence === "weekly" ? "recurring" : "bill");
    return true;
  };
  // "Add" on a possible subscription: monthly, on its last charge's day, linked
  // to the merchant (so it auto-marks paid).
  const addPossibleSubscription = async (p, name) => {
    if (!user) return false;
    const data = await persistRow("that subscription", supabase.from("bills").insert([billFromPossible(p, user.id, name)]).select().single());
    if (!data) return false;
    setBills(b => [...b, data]);
    await saveRecurringRule(p.matchName, "bill", { typical_amount: p.amount, usual_day: p.usualDay });
    return true;
  };
  // "Not a subscription" on a Possible row: a not_bill answer from Possible
  // (Apple keyed per amount), which moves it to Hidden.
  const dismissPossibleSubscription = async (p) => {
    if (!user) return;
    const r = dismissPossibleRule(p, user.id);
    await saveRecurringRule(r.match_name, "not_bill", { typical_amount: r.typical_amount, usual_day: r.usual_day, dismissed_from: "possible" });
    setPossibleAdding(x => x?.matchName === p.matchName ? null : x);
  };
  // Restore from Hidden: delete the not_bill answer. Dismissed from Possible →
  // it returns to Possible; removed from the confirmed list (or "Not a bill" on
  // a detected charge) → re-added as a subscription at its last amount and day.
  const restoreHiddenSubscription = async (h) => {
    if (!user) return;
    if (!(await persist("that subscription", supabase.from("recurring_rules").delete().eq("user_id", user.id).eq("match_name", h.matchName)))) return;
    setRecurringRules(r => r.filter(x => x.match_name !== h.matchName));
    if (!restoreAddsBill(h)) return;
    // Amount, cadence, day and the merchant link come from the real charges.
    const data = await persistRow("that subscription", supabase.from("bills").insert([billFromHidden(h, user.id, plaidTransactions)]).select().single());
    if (data) setBills(b => [...b, data]);
  };
  // Forget: delete the answer outright, so detection / Possible can offer the
  // merchant again, fresh. Confirmed inline first (forgetConfirm).
  const forgetHiddenSubscription = async (h) => {
    if (!user) return;
    if (!(await persist("that answer", supabase.from("recurring_rules").delete().eq("user_id", user.id).eq("match_name", h.matchName)))) return;
    setRecurringRules(r => r.filter(x => x.match_name !== h.matchName));
    setForgetConfirm(null);
  };

  // Auto-mark paid (client half; api/lib/plaid-sync.js runs the same helper
  // after a server sync): a bill added from a detected pattern whose matching
  // charge is in the loaded transactions is stamped paid for this month.
  // Manual mark-paid is untouched; a stamped bill is never re-matched.
  useEffect(() => {
    if (!user || isDemoMode || !plaidTransactions.length || !bills.some(b => b.match_name)) return;
    let zone; try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { zone = undefined; }
    const month = currentMonth();
    const due = billsToAutoMarkPaid(bills, plaidTransactions, { month, zone });
    if (!due.length) return;
    (async () => {
      for (const b of due) {
        const stamp = b.paidStamp || month; // yearly: this year's due month
        const ok = await persist("that bill", supabase.from("bills").update({ paid_for_month: stamp, status: "paid" }).eq("id", b.id));
        if (ok) setBills(p => p.map(x => x.id === b.id ? { ...x, paid_for_month: stamp, status: "paid" } : x));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plaidTransactions, bills, user?.id, isDemoMode]);

  // Save the birth year (an edit clears the "estimated" flag). Gated on the write.
  const saveBirthYear = async (year) => {
    const y = year === "" || year == null ? null : Number(year);
    if (y != null && !isValidBirthYear(y)) return false;
    if (user && !(await persist("your birth year", supabase.from("profiles").update({ birth_year: y, birth_year_estimated: false }).eq("id", user.id)))) return false;
    setBirthYear(y); setBirthYearEstimated(false);
    return true;
  };
  const birthYearSelect = (value, onChange, { label = "Birth year", helper = null } = {}) => (
    <div>
      <div style={{fontSize:11,color:T.textMuted,marginBottom:5,fontWeight:600}}>{label}</div>
      <select value={value ?? ""} onChange={e=>onChange(e.target.value)} aria-label={label} style={{...C.inp,width:"100%"}}>
        <option value="">Prefer not to say</option>
        {birthYearOptions().map(y=><option key={y} value={y}>{y}</option>)}
      </select>
      {helper && <div style={{fontSize:11,color:T.textMuted,marginTop:5,lineHeight:1.5}}>{helper}</div>}
    </div>
  );

  // ── GOAL EDIT (the Add Goal modal in edit mode) ────────────────────────────
  // Editable: target, title, deadline. NOT editable: the binding (changing a
  // goal's data source mid-life is a different feature), category (it routes
  // which tab renders the goal), unit (bound goals' units are the binding's
  // semantics; re-denominating a manual goal would falsify current_value), and
  // current_value (that is Update's job — editing must not rewrite progress).
  //
  // Title ownership without a DB column: genGoalTitle("") yields exactly the
  // value-suffix (" under $300/mo", ": 10 mi/wk", ...), so a stored title that
  // ends with the OLD target's suffix is still the generated one — recover
  // titleBase from the prefix and arm the same live regeneration the creation
  // flow uses. Any other title is the user's and is never touched. (This is
  // the rule from the template-title work; autoTitle is modal state only.)
  const openGoalEditor = (gRaw) => {
    // The linked view: a workout-count goal with a gym habit opens with the
    // habit's target and is flagged habitLinked (target read-only below).
    const g = viewGoal(gRaw);
    // Screen-time caps are STORED in minutes but PRESENTED in hours
    // everywhere (template name, card); the edit draft converts on the way
    // in, saveGoalEdits converts back on the way out — the create path's
    // exact pair (stHoursToMinutes/stMinutesToHours).
    const stEdit = g.source_type === "screen_time";
    const oldVal = g.period ? (g.period_target ?? g.target_value) : g.target_value;
    const suffix = genGoalTitle("", { period: g.period, unit: g.unit, value: oldVal, source_type: g.source_type });
    const isGenerated = !!suffix && typeof g.title === "string" && g.title.endsWith(suffix) && g.title.length > suffix.length;
    setNewGoal({
      title: g.title || "",
      category: g.category || "personal",
      current_value: String(g.current_value ?? 0),
      target_value: g.target_value != null ? String(stEdit ? stMinutesToHours(g.target_value) : g.target_value) : "",
      unit: g.unit || "",
      deadline: g.deadline || "",
      source_type: g.source_type || undefined,
      source_config: g.source_config || undefined,
      period: g.period || undefined,
      period_target: g.period_target != null ? String(stEdit ? stMinutesToHours(g.period_target) : g.period_target) : undefined,
      titleBase: isGenerated ? g.title.slice(0, -suffix.length) : undefined,
      autoTitle: isGenerated ? g.title : undefined,
      habitLinked: g.linked_habit === 'gym',
      // Shape derived from the row (never editable in edit mode — changing a
      // goal's shape is a re-scope, not an edit).
      shape: g.source_type === "plaid_spend" || g.source_type === "screen_time" || g.source_config?.manual_shape === "cap" ? "cap"
        : g.source_config?.manual_shape === "milestone" ? "milestone"
        : g.period ? "recurring" : "target",
    });
    setEditGoalId(g.id);
    setGoalError("");
    setShowAddGoal(true);
  };

  const closeGoalModal = () => {
    // An abandoned edit must not leak its draft into the next "+ Add Goal".
    if (editGoalId) {
      setEditGoalId(null);
      setNewGoal({ title:"", category:"fitness", current_value:"0", target_value:"", unit:"", deadline:"", shape:"target" });
    }
    setGoalError("");
    setShowAddGoal(false);
    setGoalView("gallery"); // next open always starts at the front door
    setGoalTpl(null);
  };

  const saveGoalEdits = async () => {
    const goal = goals.find(g => g.id === editGoalId);
    if (!goal) { closeGoalModal(); return; }
    if (!requirePro("goal")) return;
    const title = newGoal.title.trim();
    if (!title || !newGoal.target_value) return;
    const oldKey = goalKey(goal.title);
    const key = goalKey(title);
    if (key !== oldKey && (goals.some(g => g.id !== goal.id && !g.completed && goalKey(g.title) === key) || goalTitlesRef.current.has(key))) {
      setGoalError(`You already have a goal called "${title}". Rename it, or update the one you have.`);
      return;
    }
    // Same construction creation uses (buildGoalPayload) so number parsing and
    // trimming cannot drift; the UPDATE then takes only the editable columns.
    // current_value and completed are deliberately not written: progress and
    // history survive a target change untouched. Bindings are likewise not
    // editable — with ONE sanctioned transition: the savings toggle
    // (savings ↔ manual, both directions, explicit opt-in — the P3
    // replacement). current_value survives a bind untouched by design: the
    // savings resolver never reads it, so the user's typed progress is
    // parked, not discarded, and resurfaces the moment they unbind.
    const payload = buildGoalPayload(newGoal, user?.id);
    const changes = { title: payload.title, target_value: payload.target_value, period_target: payload.period_target, deadline: payload.deadline };
    // A habit-linked workout goal: the target lives on the Gym habit, so the
    // goal's own period_target (the no-habit fallback) is never overwritten
    // from here, and a still-generated title stays generated (it is derived
    // at render time from the habit's target).
    if (newGoal.habitLinked) {
      delete changes.target_value;
      delete changes.period_target;
      if (newGoal.title === newGoal.autoTitle) delete changes.title;
    }
    // Screen-time edit drafts hold HOURS (openGoalEditor converted on the
    // way in) — convert back to storage minutes, same bounds as creation.
    if (goal.source_type === "screen_time") {
      const hours = parseFloat(newGoal.period_target ?? newGoal.target_value);
      if (isNaN(hours) || hours < 0.5 || hours > 24) { setGoalError("Enter a daily limit between 0.5 and 24 hours."); return; }
      const capMinutes = stHoursToMinutes(hours);
      changes.target_value = capMinutes;
      changes.period_target = capMinutes;
    }
    const oldSrc = goal.source_type || null;
    const newSrc = newGoal.source_type === "savings" ? "savings" : (newGoal.source_type || null);
    const savingsToggle = (oldSrc === null && newSrc === "savings") || (oldSrc === "savings" && newSrc === null);
    if (savingsToggle) changes.source_type = newSrc;
    if (user) {
      const ok = await persist("your goal", supabase.from("goals").update(changes).eq("id", goal.id));
      if (!ok) return; // write rejected — modal stays open, local state untouched
    }
    if (key !== oldKey && !goal.completed) {
      goalTitlesRef.current.delete(oldKey);
      goalTitlesRef.current.add(key);
    }
    setGoals(p => p.map(g => g.id === goal.id ? { ...g, ...changes } : g));
    // A cap edit on a screen-time goal re-arms today's window so the new
    // thresholds apply from now (the plugin stops and re-arms; the
    // extension RE-STAMPS openedAt so the edited day reads as partial —
    // the daemon's usage accumulator resets with the window — and only a
    // natural-end close counts, so the stop artifact cannot fake a close).
    if (goal.source_type === "screen_time" && changes.period_target && changes.period_target !== goal.period_target) {
      armScreenTime(Number(changes.period_target));
    }
    setEditGoalId(null);
    setNewGoal({ title:"", category:"fitness", current_value:"0", target_value:"", unit:"", deadline:"", shape:"target" });
    setGoalError("");
    setShowAddGoal(false);
  };

  // The connect-control handler for a screen-time goal whose authorization
  // or app selection has gone missing (fresh install, Settings revoke, OS
  // update resetting Family Controls). Mirrors connectPlaid's role in the
  // noSource pattern: request access, re-pick, re-arm, refresh status so
  // the card heals. A decline just refreshes status — the card stays
  // honestly in its access-needed state, nothing throws.
  // Setup flow for the ambient usage card (authorization → select-all
  // picker → enable + arm the ladder), then sync so the first record lands.
  const enableUsageDisplay = async () => {
    const result = await setupUsageDisplay();
    if (result.ok) {
      // Restore the cap goal's config after the usage-path re-arm:
      // setUsageEnabled rebuilds config from whatever the App Group holds,
      // and after a reinstall that can be nothing — a 600-min cap came back
      // as 0 (device 2026-09-16), leaving the day's cap events unarmed.
      // arm() rewrites config from the goal and re-arms; it pre-checks the
      // cap selection and returns no_selection as an expected state (the
      // goal card's repair control owns that path).
      const g = goals.find(x => x.source_type === "screen_time" && !x.completed);
      const cap = Number(g?.period_target) || 0;
      if (cap >= 15) await armScreenTime(cap);
      await claimScreenTime(user?.id);
    }
    setStStatus(await screenTimeStatus());
    if (result.ok && user?.id) {
      syncScreenTime(user.id);
      setTimeout(() => syncScreenTime(user.id), 8000);
    }
  };

  const repairScreenTime = async () => {
    const g = goals.find(x => x.source_type === "screen_time" && !x.completed);
    if (!g) return;
    const setup = await setupScreenTime();
    if (setup.ok) {
      const cap = Number(g.period_target) || 0;
      if (cap >= 15) await armScreenTime(cap);
      if (user?.id) syncScreenTime(user.id);
    }
    setStStatus(await screenTimeStatus());
  };

  // Gallery tap → load the template preset into the draft, open the
  // one-number confirm screen. Full reset first (the old bottom-chip rule):
  // every template-owned field is overwritten so a stale number can never
  // ride under a freshly-swapped binding. Screen-time templates set the
  // screen_time_bind flag — addGoal's existing native block (authorization
  // → picker → arm, hours → minutes) runs at save, unchanged.
  const openTemplateConfirm = (t) => {
    if (t.screenTime) {
      setNewGoal({
        title: t.title, category: t.category, current_value: "0",
        target_value: t.target_value, unit: "min", deadline: "",
        period: "day", period_target: t.period_target,
        shape: "cap", screen_time_bind: true, fromTemplate: true,
      });
    } else {
      // Credit payoff prefills the target from the live balance when a bank is
      // connected (default = full balance, editable down to a partial payoff).
      const ccPrefill = t.source_type === 'credit_payoff' && plaidConnected && liveCreditBalance() > 0
        ? String(Math.round(liveCreditBalance())) : t.target_value;
      const gen = ccPrefill ? genGoalTitle(t.title, { period: t.period, unit: t.unit, value: t.period ? t.period_target : ccPrefill, source_type: t.source_type }) : t.title;
      setNewGoal({
        title: gen, autoTitle: ccPrefill ? gen : undefined, titleBase: ccPrefill ? t.title : undefined,
        category: t.category, current_value: "0",
        target_value: ccPrefill, unit: t.unit, deadline: "",
        source_type: t.source_type, source_config: t.source_config,
        period: t.period, period_target: t.period_target,
        shape: t.source_type === "plaid_spend" ? "cap" : t.period ? "recurring" : "target",
        fromTemplate: true,
      });
    }
    setGoalTpl(t);
    setGoalError("");
    setGoalView("confirm");
  };

  // Custom-form auto-track: the binding the draft's shape + unit map to, or
  // null (manual / opted out / not the custom path). Pure mapping lives in
  // goalTemplates.js customGoalBinding; `autoTrack === false` is the user's
  // "Track automatically" switched off.
  const customBindingOption = (g) => (!editGoalId && g && !g.fromTemplate && !g.source_type && !g.screen_time_bind)
    ? customGoalBinding({ shape: g.shape || "target", unit: g.unit, includeBills: !!g.includeBills, spendFilter: g.spendFilter || null, native })
    : null;
  const customDraftBinding = (g) => (g && g.autoTrack !== false) ? customBindingOption(g) : null;
  const applyCustomBinding = (g, b) => {
    const amount = (g.period_target !== undefined && g.period_target !== null && g.period_target !== "") ? g.period_target : g.target_value;
    return {
      ...g,
      source_type: b.source_type || undefined,
      source_config: b.source_config || undefined,
      screen_time_bind: !!b.screen_time_bind,
      period: b.period || undefined,
      period_target: b.period ? amount : undefined,
      target_value: amount,
      unit: b.unit,
    };
  };

  const addGoal = async () => {
    const title = newGoal.title.trim();
    if (!title || !newGoal.target_value) return;
    if (!requirePro("goal")) return;
    // Custom path auto-track (customGoalBinding): the draft takes the binding's
    // source/period/unit, so everything below saves it like a template goal.
    const bind = customDraftBinding(newGoal);
    const draft = bind ? applyCustomBinding(newGoal, bind) : newGoal;

    // Claim the title on the ref FIRST, synchronously. `goals` is React state and
    // is stale until the next render, so checking it alone let a double-tap pass
    // the guard twice before either insert landed — that is how one goal ended up
    // with 8 rows inserted 90ms apart.
    const key = goalKey(title);
    if (goals.some(g => !g.completed && goalKey(g.title) === key) || goalTitlesRef.current.has(key)) {
      setGoalError(`You already have a goal called "${title}". Rename it, or update the one you have.`);
      return;
    }

    // Screen Time binding: authorization + app picker + arming must ALL
    // succeed BEFORE the goal exists — a bound goal with no monitoring
    // would render unknown forever. The entered number is HOURS; stored as
    // MINUTES with period 'day' (the resolvers' unit on both sides of the
    // matched pair). Runs before the title claim so a failed setup leaves
    // nothing to release.
    let stOverride = null;
    if (draft.screen_time_bind) {
      const hours = parseFloat(draft.period_target ?? draft.target_value);
      if (isNaN(hours) || hours < 0.5 || hours > 24) { setGoalError("Enter a daily limit between 0.5 and 24 hours."); return; }
      const capMinutes = stHoursToMinutes(hours);
      const setup = await setupScreenTime();
      if (!setup.ok) {
        setGoalError(setup.reason === "denied" ? "Screen Time access was declined, so the goal wasn't created. You can allow it in Settings and try again."
          : setup.reason === "no_selection" ? "No apps picked. Choose at least one app to limit and try again."
          : "Screen Time isn't available on this device.");
        return;
      }
      const armed = await armScreenTime(capMinutes);
      if (!armed?.armed) { setGoalError("Couldn't start Screen Time monitoring. Please try again."); return; }
      // Stamp this account as the owner of the device's Screen Time state, so
      // it can never surface under a different account on a shared device.
      await claimScreenTime(user?.id);
      // source_config carries the PICKER COUNTS, nothing more: the
      // FamilyActivitySelection tokens are opaque device-scoped blobs that
      // never leave the App Group, and the cap itself lives in
      // period_target (per day, in the row's own cap_minutes stamp) —
      // neither resolver half reads this config. Counts are the
      // representable part, recorded so the goal row says what was picked.
      // category enforced here too, in case a stale draft carried one.
      stOverride = { source_type: "screen_time", source_config: { apps: setup.apps || 0, categories: setup.categories || 0, web_domains: setup.webDomains || 0 }, period: "day", period_target: capMinutes, target_value: capMinutes, unit: "min", category: "health" };
    }

    goalTitlesRef.current.add(key);
    setGoalError("");

    // Shared with persistOnboarding — see buildGoalPayload. The binding
    // passthrough history (map 4.1/4.2) lives on the helper.
    // Category is never ASKED anymore: templates carry it (fromTemplate),
    // the screen-time override forces health, and the custom path INFERS it
    // from the picked unit — it must still be SET because assembleSignals
    // maps it to the cron signal's pillar tag and it routes tab placement.
    const payload = {
      ...buildGoalPayload(draft, user?.id),
      ...(draft.fromTemplate ? {} : { category: bind?.category || inferGoalCategory(draft.unit) }),
      ...(stOverride || {}),
    };
    // Credit payoff binds to the live bank when one is connected with a real
    // balance: start_value = balance at bind, progress derived from it (never
    // current_value). No bank -> manual $ goal with the connect-to-track
    // label. Same rule as persistOnboarding.
    const ccBalance = liveCreditBalance();
    if (draft.source_type === 'credit_payoff') {
      if (plaidConnected && ccBalance > 0) {
        payload.source_type = 'credit_payoff';
        payload.start_value = ccBalance;
        payload.target_value = capCreditTarget(draft.target_value, ccBalance);
        payload.current_value = 0;
        payload.unit = '$';
      } else {
        payload.source_type = null; // manual fallback
        payload.unit = '$';
      }
    } else if (draft.fromTemplate && parseFloat(draft.current_value) > 0) {
      // The optional "already saved something?" line (other money templates):
      // current_value is the progress the manual resolver reads, and
      // start_value records the baseline ("Save $5,000" at 5125/5000).
      payload.start_value = parseFloat(draft.current_value);
    }
    if (user) {
      const { data, error } = await supabase.from("goals").insert([payload]).select().single();
      // Release the claim on failure so a corrected retry can go through.
      if (error || !data) {
        goalTitlesRef.current.delete(key);
        // The arm above succeeded but the goal row didn't: without a goal,
        // nothing would ever disarm — the extension would chain windows
        // forever for a goal that does not exist.
        if (stOverride) disarmScreenTime();
        console.error("addGoal failed", error);
        // 23505 = unique_violation on goals_user_title_uniq. Reachable even with
        // the ref guard: another device, or a goal completed earlier that is
        // still in the table. Show the reason instead of a raw Postgres string,
        // and leave the modal open so the title can be edited.
        setGoalError(error?.code === "23505"
          ? `You already have a goal called "${title}". Rename it, or update the one you have.`
          : "Could not save that goal. Please try again.");
        return;
      }
      setGoals(p => [data, ...p]);
      awardXP("habit");
    } else {
      setGoals(p => [{ ...payload, id: "g_" + Date.now() }, ...p]);
    }
    setNewGoal({ title:"", category:"fitness", current_value:"0", target_value:"", unit:"", deadline:"", shape:"target" });
    setGoalError("");
    setShowAddGoal(false);
    setGoalView("gallery"); // a saved goal must not leave a stale confirm screen for the next open
    setGoalTpl(null);
  };

  const updateGoalProgress = async (goal, newVal) => {
    const v = parseFloat(newVal);
    if (isNaN(v)) return;
    // A cap is inverted: REACHING the limit is the bad outcome, so it must
    // never auto-complete the way a target does.
    const completed = goal.source_config?.manual_shape === "cap" ? false : v >= goal.target_value;
    const updated = { ...goal, current_value: v, completed };
    // Gated: a rejected update used to run setGoals/awardXP/modal-close anyway,
    // so the UI showed progress (and granted XP) for a write that never landed.
    // Failure keeps the Update modal open with the typed value. No user =
    // demo mode, local-only by design.
    if (user && !(await persist("your goal progress", supabase.from("goals").update({ current_value: v, completed }).eq("id", goal.id)))) return;
    // The index only covers active goals, so completing frees the title and
    // dropping back below target reclaims it.
    syncGoalClaim(goal.title, completed);
    setGoals(p => p.map(g => g.id === goal.id ? updated : g));
    if (completed) awardXP("habit");
    setShowUpdateGoal(null);
    setGoalUpdateVal("");
  };

  const deleteGoal = async (id) => {
    const gone = goals.find(g => g.id === id);
    // The claim release is gated too: freeing the title while the row survives
    // in the database would let the same goal be added again as a duplicate.
    if (user && !(await persist("that goal", supabase.from("goals").delete().eq("id", id)))) return;
    if (gone?.title) goalTitlesRef.current.delete(goalKey(gone.title)); // release the claim
    setGoals(p => p.filter(g => g.id !== id));
    // Deleting the screen-time goal is the disarm affordance: config flips
    // off (so the extension stops chaining) and all monitors stop. Recorded
    // days stay — history is history.
    if (gone?.source_type === "screen_time") disarmScreenTime();
  };

  // ── GAMIFICATION: Award XP + update streak ────────────────────────────────
  // xp / daily_streak / longest_streak / last_active_date are computed ONLY by
  // the award_xp RPC (supabase/migrations/20260910_award_xp_atomic.sql), from
  // the row's own committed values. This function used to read `progress` from
  // the render closure and upsert the WHOLE row — on every mount the
  // daily-login effect fired before loadData's user_progress SELECT resolved,
  // so the upsert was computed from the initial {xp:0,last_active_date:null}
  // state and clobbered the real row to xp=15/streak=1 (live table 2026-09-10:
  // every user at 15–35 xp including daily users since July). Do not reintroduce
  // any client-side read-modify-write of those four columns.
  //
  // level + badges stay derived HERE (getLevel + the badge rules — the single
  // implementation), but always from the RPC's returned row, never from state;
  // they are cache columns and self-heal on the next award if their write loses
  // a race or is interrupted.
  const awardXP = async (action, { oncePerDay = false } = {}) => {
    if (!user || isDemo) return;
    const amount = XP_ACTIONS[action] || 5;
    const today = localDateStr();
    const { data, error } = await supabase.rpc("award_xp", { p_amount: amount, p_today: today, p_once_per_day: oncePerDay });
    if (error || !data?.row) {
      console.error("save failed: your progress", error);
      reportError(new Error(`save failed: your progress — ${error?.message || "no row returned"}`), {
        op: "db.persist", what: "your progress", code: error?.code,
      });
      showToast(typeof navigator !== "undefined" && navigator.onLine === false
        ? "You're offline. Your progress wasn't saved. Try again when you're back."
        : "Couldn't save your progress. Check your connection; the change may not be there when you return.");
      return;
    }
    const row = data.row;
    const granted = data.granted === true;

    const newLevel = getLevel(row.xp || 0);
    const prevLevel = getLevel((row.xp || 0) - (granted ? amount : 0));
    const currentBadges = Array.isArray(row.badges) ? row.badges : [];
    const newBadges = [...currentBadges];
    if (!newBadges.includes("first_log")) newBadges.push("first_log");
    if (row.daily_streak >= 7   && !newBadges.includes("streak7"))   newBadges.push("streak7");
    if (row.daily_streak >= 30  && !newBadges.includes("streak30"))  newBadges.push("streak30");
    if (row.daily_streak >= 100 && !newBadges.includes("streak100")) newBadges.push("streak100");
    if (newLevel.name === "Hustler" && !newBadges.includes("hustler")) newBadges.push("hustler");
    if (newLevel.name === "Pro"     && !newBadges.includes("pro"))     newBadges.push("pro");
    if (newLevel.name === "Elite"   && !newBadges.includes("elite"))   newBadges.push("elite");
    if (newLevel.name === "Legend"  && !newBadges.includes("legend"))  newBadges.push("legend");
    if (newLevel.name !== row.level || newBadges.length !== currentBadges.length) {
      await persist("your progress", supabase.from("user_progress").update({ level: newLevel.name, badges: newBadges }).eq("user_id", user.id));
    }

    const updated = { ...row, level: newLevel.name, badges: newBadges };
    // Newest-wins: a slower response from an earlier award (or loadData's
    // SELECT) must not overwrite a newer row already in state.
    setProgress(prev => (prev.updated_at && row.updated_at && String(prev.updated_at) > String(row.updated_at)) ? prev : updated);

    if (granted) {
      setXpPopup({ amount, reason: action });
      setTimeout(() => setXpPopup(null), 2000);
      if (newLevel.name !== prevLevel.name) {
        setLevelUpPopup(newLevel);
        setTimeout(() => setLevelUpPopup(null), 4000);
      }
    }
  };

  // Award daily login XP once per day. The closure check is only a cheap skip —
  // the real once-per-day guard is p_once_per_day inside the RPC, which no-ops
  // (granted:false) when the row is already stamped today, so firing this from
  // stale mount state is harmless.
  const awardDailyLogin = async () => {
    if (!user || isDemo) return;
    // Offline, the award can only fail — and it used to fire an unprompted
    // "Couldn't save your progress" toast at cold launch before the user
    // touched anything. Skip it: the reconnect reload refreshes progress,
    // which re-fires this effect, and award_xp is once-per-day server-side.
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    const today = localDateStr();
    if (progress.last_active_date === today) return; // already got it today
    await awardXP("login", { oncePerDay: true });
  };

  // ── SUPABASE: Save body stats ─────────────────────────────────────────────
  const saveBodyStats = async (form) => {
    if (!user) return;
    // height_in / sex / activity_level are no longer written: their features
    // (BMI/BMR/TDEE) are gone, nothing reads them, and the old "male"/
    // "moderate" fallbacks fabricated data nobody entered. Columns remain in
    // the table for existing rows, deliberately untouched.
    const payload = {
      user_id: user.id,
      // age is no longer written (2026-10-23): it is computed from profiles.birth_year.
      current_weight: parseFloat(form.current_weight)||null,
      goal_weight: parseFloat(form.goal_weight)||null,
      goal_type: form.goal_type||"fat_loss",
      goal_date: form.goal_date||null,
      goal_label: form.goal_label||null,
      updated_at: new Date().toISOString(),
    };
    // Gated through persist (audit W9/W10): this failed console-only, so an
    // Edit Profile save could vanish with no user-visible sign.
    if (await persist("your body stats", supabase.from("body_stats").upsert(payload, { onConflict: "user_id" }))) setBodyStats(payload);
    if (form.birth_year !== undefined && String(form.birth_year ?? "") !== String(birthYear ?? "")) await saveBirthYear(form.birth_year);
  };

  const logWeight = async () => {
    if (!user || !logWeightVal) return;
    if (!requirePro("weight")) return;
    const w = parseFloat(logWeightVal);
    if (!w) return;
    // Gated (map 5.6): failure keeps the modal open with the typed weight.
    const data = await persistRow("your weight", supabase.from("weight_log").insert([{ user_id: user.id, weight: w }]).select().single());
    if (!data) return;
    setWeightLog(p => [...p, data]);
    await saveBodyStats({ ...bodyStats, current_weight: w });
    setBodyStats(p => ({ ...p, current_weight: w }));
    awardXP("weight");
    setLogWeightVal(""); setShowLogWeight(false);
  };

  // ── SUPABASE: Save finances ────────────────────────────────────────────────
  const saveCreditDetails = async (form) => {
    if (!user) return;
    const payload = {
      cc_balance: parseFloat(form.cc_balance)||0,
      cc_limit: parseFloat(form.cc_limit)||0,
      credit_age_years: parseFloat(form.credit_age_years)||0,
      num_accounts: parseInt(form.num_accounts)||0,
      hard_inquiries: parseInt(form.hard_inquiries)||0,
      family_size: parseInt(form.family_size)||1,
      has_student_loans: !!form.has_student_loans,
      is_first_time_buyer: !!form.is_first_time_buyer,
      employer_has_401k: !!form.employer_has_401k,
      updated_at: new Date().toISOString(),
    };
    await persist("your finances", supabase.from("finances").upsert({ user_id: user.id, ...payload }, { onConflict: "user_id" }));
    setCreditDetails(payload);
  };

  // The user's own word on how they are paid. Written on its own (not through
  // saveFinances) so a toggle never re-submits the whole form.
  // Serialized + confirmed (2026-10-14): taps queue behind each other, the
  // shown value is optimistic, the CONFIRMED value is read from the row the
  // server returns, and a failed save toasts and reverts. See
  // createIncomeTypeSaver in src/lib/income.js for the bug this closes.
  const chooseIncomeType = (choice) => {
    if (!user) return;
    if (!incomeTypeSaverRef.current) {
      incomeTypeSaverRef.current = createIncomeTypeSaver({
        initial: incomeTypeOverride,
        write: async (next) => {
          const { data, error } = await supabase.from("finances")
            .upsert({ user_id: user.id, income_type_override: next, updated_at: new Date().toISOString() }, { onConflict: "user_id" })
            .select("income_type_override").single();
          if (error) { reportSaveFailure("your income type", error); return { ok: false, error }; }
          setHasFinancesRow(true);
          return { ok: true, stored: data?.income_type_override ?? null };
        },
        onState: ({ value, saving }) => { setIncomeTypeOverride(value); setIncomeTypeSaving(saving); },
      });
    }
    incomeTypeSaverRef.current.choose(choice);
  };
  const saveFinances = async (income, expenses, sav, payForm) => {
    if (!user) return;
    const inc = parseFloat(income)||0;
    const exp = parseFloat(expenses)||0;
    const savAmt = parseFloat(sav)||0;
    const paycheckAmt = parseFloat(payForm?.pay_amount)||0;
    const newSchedule = payForm ? { freq: payForm.pay_freq, day1: parseInt(payForm.pay_day1)||1, day2: parseInt(payForm.pay_day2)||15, weekday: parseInt(payForm.pay_weekday)||5, startDate: payForm.pay_start||"" } : paySchedule;
    if (payForm) { setPaySchedule(newSchedule); setPaycheckAmount(paycheckAmt); setPayScheduleSaved(true); }
    // Blank savings is written as NULL, never 0, so "unknown" survives a reload
    // as unknown (invariant: unknown is never $0). An entered value (0 included)
    // is a real, known figure.
    const savEntered = sav != null && String(sav).trim() !== "";
    const { error } = await supabase.from("finances").upsert(
      { user_id: user.id, monthly_income: inc, monthly_expenses: exp, savings: savEntered ? savAmt : null, pay_amount: paycheckAmt, pay_freq: newSchedule.freq, pay_day1: newSchedule.day1, pay_day2: newSchedule.day2, pay_weekday: newSchedule.weekday, pay_start: newSchedule.startDate, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    );
    // The error used to be discarded here: with no pay_amount column in the
    // table (added 2026-10-14) every save was rejected and the screen still
    // showed the form closing as if it had saved.
    if (error) reportSaveFailure("your finances", error);
    if (!error) {
      setHasFinancesRow(true);
      setMonthlyIncome(inc);
      setMonthlyExpenses(exp);
      setSavings(savAmt);
      setSavingsKnown(savEntered);
      // Auto-sync finance savings goals so Overview + Finances tab stay in sync.
      //
      // The DB write and syncGoalClaim used to live INSIDE this setGoals updater.
      // A React state updater must be pure: StrictMode double-invokes updaters in
      // development, so every save fired two identical UPDATEs per goal and
      // mutated goalTitlesRef twice. The write was also un-awaited, so its error
      // was discarded and a rejection became an unhandled promise rejection.
      //
      // Both side effects now happen out here, where they can be awaited and
      // reported; the updater is left pure.
      const isFinanceSavingsGoal = (g) =>
        g.category === "finance" && (g.unit === "$" || g.title.toLowerCase().includes("save"));
      const affected = goals.filter(isFinanceSavingsGoal)
        .map(g => ({ ...g, completed: savAmt >= g.target_value }));

      setGoals(prev => prev.map(g =>
        isFinanceSavingsGoal(g)
          ? { ...g, current_value: savAmt, completed: savAmt >= g.target_value }
          : g
      ));

      for (const g of affected) {
        await persist("your savings goal", supabase.from("goals")
          .update({ current_value: savAmt, completed: g.completed, updated_at: new Date().toISOString() })
          .eq("id", g.id));
        syncGoalClaim(g.title, g.completed); // auto-complete frees the title too
      }
    } else {
      console.error("saveFinances error:", error);
    }
  };

  const addDebt = async () => {
    if (!newDebt.name.trim() || !user) return;
    // Gated (map 5.7): failure keeps the modal open with what the user typed.
    const data = await persistRow("that debt", supabase.from("debts").insert([{ user_id: user.id, name: newDebt.name, balance: parseFloat(newDebt.balance)||0, monthly_payment: parseFloat(newDebt.monthly_payment)||0, apr: newDebt.apr }]).select().single());
    if (!data) return;
    setDebts(p => [...p, data]);
    setNewDebt({name:"",balance:"",monthly_payment:"",apr:""}); setShowAddDebt(false);
  };

  // Confirmed deletes gate their local removal on the write landing.
  //
  // persist() already returns true/false and toasts on failure; these call sites
  // used to discard that and filter the row out regardless, so an RLS denial or
  // an offline device looked exactly like success and the item came back on the
  // next load. The user has explicitly confirmed a delete by this point, which
  // makes a silent no-op worse than an error: they believe it is gone.
  //
  // The handlers that already guarded on `user` keep that guard: no user means
  // demo mode, where the write is skipped and local state updates on its own.
  //
  // Caveat: this catches errors, not zero-row deletes. A .delete() that matches
  // nothing is not an error in PostgREST, so an RLS policy that silently filters
  // the row still reads as success. Closing that needs .select() on the delete
  // and a row-count check in persist(), which touches all 24 write sites.
  const removeDebt = async (id) => {
    if (!(await persist("that debt", supabase.from("debts").delete().eq("id", id)))) return;
    setDebts(p => p.filter(d => d.id !== id));
  };

  // "Other savings and assets" — amount is OPTIONAL (null keeps it listed without a
  // value, counts 0 toward the cushion). Gated on the write landing, like addDebt.
  const addAsset = async () => {
    if (!user) { setShowAddAsset(false); return; }
    const amt = newAsset.amount === '' ? null : (parseFloat(newAsset.amount) || 0);
    const data = await persistRow("that asset", supabase.from("other_assets").insert([{ user_id: user.id, type: newAsset.type, label: newAsset.label.trim() || null, amount: amt }]).select().single());
    if (!data) return;
    setOtherAssets(p => [...p, data]);
    setNewAsset({ type: 'cash', label: '', amount: '' }); setShowAddAsset(false);
  };
  const removeAsset = async (id) => {
    if (!(await persist("that asset", supabase.from("other_assets").delete().eq("id", id)))) return;
    setOtherAssets(p => p.filter(a => a.id !== id));
  };

  const addBill = async () => {
    if (!newBill.name.trim() || !user) return;
    // Gated (map 5.8): failure keeps the modal open with what the user typed.
    const data = await persistRow("that bill", supabase.from("bills").insert([{ user_id: user.id, name: newBill.name, amount: parseFloat(newBill.amount)||0, due_day: parseInt(newBill.due_day)||1, status: newBill.status, category: newBill.category||"other", autopay: newBill.autopay||false }]).select().single());
    if (!data) return;
    setBills(p => [...p, data]);
    setNewBill({name:"",amount:"",due_day:"",status:"upcoming"}); setShowAddBill(false);
  };

  const removeBill = async (id) => {
    if (!(await persist("that bill", supabase.from("bills").delete().eq("id", id)))) return;
    setBills(p => p.filter(b => b.id !== id));
  };
  const updateBill = async (bill) => {
    // Setting status to "paid" in the edit dialog stamps the current cycle;
    // setting it to anything else clears the stamp. status still is not what
    // decides paid-ness — paid_for_month is — but the two must not disagree, or
    // the dropdown would appear to do nothing.
    const updates = { name: bill.name, amount: parseFloat(bill.amount)||0, due_day: parseInt(bill.due_day)||1, status: bill.status, category: bill.category||"other", ...autopayEditFields(bill), paid_for_month: bill.status==="paid"?currentMonth():null };
    if (user) await persist("that bill", supabase.from("bills").update(updates).eq("id", bill.id));
    setBills(p => p.map(b => b.id === bill.id ? { ...b, ...updates } : b));
    setEditBill(null);
  };
  // Writes the CURRENT local billing cycle rather than a permanent flag. status
  // is still set so older reads and the status filter UI keep working, but
  // paid_for_month is the field that decides whether a bill counts as paid —
  // and because it is compared against the current month, it expires on its own
  // at rollover instead of sticking forever.
  const markBillPaid = async (id) => {
    const month = currentMonth();
    if (user) {
      // Confirm the write landed BEFORE touching local state. The previous version
      // updated setBills unconditionally, so a rejected update left the bill
      // rendering as paid for the rest of the session while paid_for_month was
      // never written — meaning the bill_due cron would still fire for a bill the
      // user believed they had settled. .select() is required: without it
      // PostgREST returns no rows and a no-op update is indistinguishable from a
      // successful one.
      const { data, error } = await supabase
        .from("bills").update({ status: "paid", paid_for_month: month })
        .eq("id", id).select("id");
      if (error || !data || data.length === 0) {
        console.error("markBillPaid failed", error);
        setBillError("Couldn't mark that bill paid. Check your connection and try again.");
        return;
      }
      setBillError("");
    }
    setBills(p => p.map(b => b.id === id ? { ...b, status: "paid", paid_for_month: month } : b));
  };

  const addMed = async () => {
    if (!newMed.name.trim() || !user) return;
    if (!enrolledSet.has("medications")) return; // tracker off: collect nothing
    const cycleDays = parseInt(newMed.refill_days) || 30;
    const refillDate = new Date();
    refillDate.setDate(refillDate.getDate() + cycleDays);
    const refillDateStr = localDateStr(refillDate);
    // Gated (map 5.13 shape): failure keeps the modal open with what the user typed.
    const data = await persistRow("that medication", supabase.from("medications").insert([{
      user_id: user.id,
      name: newMed.name,
      dose: newMed.dose,
      schedule: newMed.schedule,
      refill_days: cycleDays,
      refill_date: refillDateStr,
    }]).select().single());
    if (!data) return;
    setMedications(p => [...p, data]);
    // Scheduled meds only: an as-needed med has no daily adherence to score,
    // and auto-enrolling on it would recreate the as-needed inversion.
    if (!/as.?needed|prn|when needed/i.test(data.schedule || "")) {
      ensureTrackerEnrolled("medications", "Medications now count toward your Health score.");
    }
    setNewMed({name:"",dose:"",schedule:"Daily",refill_days:30});
    setShowAddMed(false);
  };

  const removeMed = async (id) => {
    if (!(await persist("that medication", supabase.from("medications").delete().eq("id", id)))) return;
    setMedications(p => p.filter(m => m.id !== id));
  };

  const markMedTaken = async (id) => {
    const med = medications.find(m => m.id === id);
    if (!med) return;
    const today = localDateStr();
    // Don't double-log same day
    if (med.last_taken_date === today) return;
    // If we've passed the refill_date, reset the cycle from today
    const now = new Date();
    now.setHours(0,0,0,0);
    const refill = med.refill_date ? new Date(med.refill_date) : null;
    const isPastRefill = refill && now >= refill;
    // New refill date = today + cycle days (reset the clock)
    const cycleDays = med.refill_days || 30;
    const newRefillDate = new Date();
    newRefillDate.setDate(newRefillDate.getDate() + cycleDays);
    const newRefillDateStr = localDateStr(newRefillDate);
    const updates = isPastRefill
      ? { last_taken_date: today, refill_date: newRefillDateStr }
      : { last_taken_date: today };
    await persist("that medication", supabase.from("medications").update(updates).eq("id", id));
    setMedications(p => p.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  const addCheckup = async () => {
    if (!newCheckup.name.trim() || !user) return;
    if (!enrolledSet.has("preventive_care")) return; // tracker off: collect nothing
    // Gated (map 5.14): the insert's persist() boolean was discarded, so a
    // rejected insert still refetched (showing nothing new) and closed the
    // modal. Failure now keeps the modal open with what the user typed. The
    // refetch stays best-effort — the insert is already confirmed; a failed
    // refetch just leaves the list stale until the next load.
    // No next_date: the checkups table has no such column (id, user_id, name,
    // last_date, urgent, created_at), so including it made every add 400
    // (write-column audit 2026-10-15). The form still collects it for the
    // in-memory row; it is not persisted until a migration adds the column.
    if (!(await persist("that check-up", supabase.from("checkups").insert([{ user_id: user.id, name: newCheckup.name, last_date: newCheckup.last_date, urgent: newCheckup.urgent }])))) return;
    const { data: updated } = await supabase.from("checkups").select("*").eq("user_id", user.id).order("created_at");
    if (updated) setCheckups(updated);
    ensureTrackerEnrolled("preventive_care", "Preventive care now counts toward your Health score.");
    setNewCheckup({name:"",last_date:"",next_date:"",urgent:false}); setShowAddCheckup(false);
  };

  // ── SUPABASE: Save credit score ─────────────────────────────────────────────
  const saveCreditScore = async (score) => {
    if (!user) return;
    await persist("your credit score", supabase.from("finances").upsert(
      { user_id: user.id, credit_score: score, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    ));
  };

  // ── SUPABASE: Save per-pillar score history weekly ──────────────────────────
  // One row per pillar per week, upserted on (user_id, week_start, pillar).
  //
  // The old version read-then-inserted a single composite row and was called
  // from an effect keyed on the score itself, which changes repeatedly as data
  // loads. With no unique index behind it that produced 3364 duplicate rows —
  // 1148 in a single user-week. The ref claims the week synchronously, and the
  // upsert makes a duplicate impossible even if two tabs race.
  // Persisting enrollment is what makes opt-in explicit. Until this is written
  // the engine falls back to inferring from existing data, which is the very
  // behaviour that produced the as-needed-medication inversion — so the write
  // matters as much as the toggle.
  const toggleTracker = async (id) => {
    const current = new Set(enrolledSet);
    const turningOn = !current.has(id);
    if (current.has(id)) current.delete(id); else current.add(id);
    const next = TRACKER_IDS.filter(t => current.has(t)); // stable order
    setEnabledTrackers(next);
    if (!user || isDemoMode) return;
    // Internal audit of optional-tracker enable/disable (user, tracker, on/off, time).
    if (OPTIONAL_HEALTH_TRACKERS.includes(id)) {
      supabase.from("tracker_events").insert([{ user_id: user.id, tracker: id, enabled: turningOn }])
        .then(({ error }) => { if (error) console.error("tracker_events insert failed", error.message); });
    }
    // writeProfile so a rowless user's enrollment write cannot 0-row silently —
    // and as a side effect every enrollment toggle also stamps timezone, which
    // is one more path out of the null-timezone notification blackout.
    const { error, row } = await writeProfile({ enabled_trackers: next });
    // Local state stays applied either way so the toggle still works in-session;
    // pre-migration it simply will not persist across reloads.
    if (error || !row) {
      console.error("toggleTracker did not persist (is enabled_trackers migrated?)", error);
      reportError(new Error(`toggleTracker: ${error?.message || "no row touched"}`), { op: "db.toggleTracker", code: error?.code });
    }
  };

  // Enroll-on-explicit-add: adding a supplement / scheduled med / check-up IS
  // the opt-in for its tracker, so the corresponding bucket must not sit at
  // "Not tracked" while the user logs data into it (the Creatine bug —
  // enabled_trackers was written once at onboarding and adds never touched it).
  // Add-only, never removes; un-enrolling stays a manual toggle. Same persisted
  // write path as toggleTracker, same keep-local-state-on-failure stance.
  const ensureTrackerEnrolled = async (id, toastMsg) => {
    if (enrolledSet.has(id)) return;
    const current = new Set(enrolledSet); current.add(id);
    const next = TRACKER_IDS.filter(t => current.has(t)); // stable order
    setEnabledTrackers(next);
    if (toastMsg) showToast(toastMsg, "success");
    if (!user || isDemoMode) return;
    const { error, row } = await writeProfile({ enabled_trackers: next });
    if (error || !row) {
      console.error("ensureTrackerEnrolled did not persist", error);
      reportError(new Error(`ensureTrackerEnrolled: ${error?.message || "no row touched"}`), { op: "db.ensureTrackerEnrolled", code: error?.code });
    }
  };

  const savePillarScores = async (rows, financeSettled) => {
    if (!user || isDemoMode) return;
    // MONDAY-start, matching every other week boundary in the app (map 3.1 —
    // this was the one Sunday-start writer, so score_history rows were keyed
    // one day off from the week the scoring describes).
    const now = new Date();
    const dow = now.getDay(); // 0=Sun..6=Sat
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
    const weekStr = localDateStr(monday);
    // The ref guards RENDER-LOOP repeats, not corrections (map 7.2: the plain
    // week key blocked any better save all week). The key carries snapshot
    // QUALITY — whether finance data had settled when it was taken — so the
    // early save (before the Plaid effect resolves) is replaced by the settled
    // one, while a settled save is final for the week and can never be
    // overwritten by a worse one (the worse save is simply skipped).
    const best = `${weekStr}:1`;
    const key = `${weekStr}:${financeSettled ? 1 : 0}`;
    if (savedWeekRef.current === best || savedWeekRef.current === key) return;
    savedWeekRef.current = key;
    const payload = rows
      .filter(p => p.pct !== null)          // a pillar with no enrolled tracker is not a data point
      .map(p => ({ user_id: user.id, week_start: weekStr, pillar: p.id,
                   earned: p.earned, possible: p.possible,
                   // When the app last scored this pillar (migration 20261018):
                   // the check-in's PILLARS line quotes the app's own Money
                   // score "as of" this stamp (api/lib/pillars-server.js).
                   updated_at: new Date().toISOString() }));
    if (payload.length === 0) return;
    const { error } = await supabase.from("score_history")
      .upsert(payload, { onConflict: "user_id,week_start,pillar" });
    if (error) { savedWeekRef.current = null; console.error("savePillarScores failed", error); }
  };

  // Snapshot on load, and AGAIN when Plaid resolves: the load-time run fires
  // before the Plaid effect has fetched anything (map 7.2), so a connected
  // user's finance pillar would be snapshotted from empty data. When
  // plaidConnected flips true the pillars have already recomputed in the same
  // render, and the quality key lets the settled save replace the early one.
  // Manual-finance users never flip, and their load-time snapshot was already
  // computed from their real (non-Plaid) figures.
  useEffect(() => {
    if (user && dataLoaded) savePillarScores(pillars.filter(p => p.fresh), plaidConnected);
  }, [user, dataLoaded, plaidConnected]); // eslint-disable-line react-hooks/exhaustive-deps


  const tmpl = id => {
    const preset = HABIT_TEMPLATES.find(t => t.id === id);
    const h = habits.find(h => h.id === id);
    if (preset) {
      // The ROW wins over the preset for label/target/unit. saveEditHabit
      // writes those three to the row and loadData reads them back, but this
      // resolver used to return the bare preset for any template id, so an
      // edited Gym target (5 → 4) or sleep label stayed at the preset in the
      // card, the edit modal, the ring and the score (2026-10-15). Icon and
      // scorePerStreak are not editable and stay with the preset.
      if (!h) return preset;
      return {
        ...preset,
        ...(h.label ? { label: h.label } : {}),
        ...(Number(h.target) > 0 ? { target: Number(h.target) } : {}),
        ...(h.unit ? { unit: h.unit } : {}),
      };
    }
    // Custom habit — data stored on the habit object itself
    if (h && h.label) return { id, label: h.label, icon: h.icon||"·", target: h.target||5, unit: h.unit||"times/week", scorePerStreak: 2, custom: true };
    return null;
  };
  const hdata = id => habits.find(h => h.id === id);

  // TIMEZONE NOTE: All date strings here MUST come from localDateStr().
  // Never use .toISOString().split('T')[0] — that's UTC and will cause
  // off-by-one bugs for users west of UTC who log in the evening.
  // Synchronous once-per-day claim for logHabit. The `lastLoggedDate === today`
  // guard reads React state, which does not update until the next render, so a
  // double-tap on the log button could pass it twice — and with XP now an
  // atomic DB-side increment (map 5.9), a doubled award ADDS twice instead of
  // being absorbed by the old clobber. Same pattern as habitIdsRef in addHabit.
  const logClaimRef = useRef(new Set());
  const logHabit = (id, count, opts = {}) => {
    const today = localDateStr();
    const habit = habits.find(h => h.id === id);
    if (!habit || habit.lastLoggedDate === today) return;
    if (!requirePro("habit")) return;
    const claim = `${id}|${today}`;
    if (logClaimRef.current.has(claim)) return;
    logClaimRef.current.add(claim);
    // Compute the new values HERE and keep the setHabits updater pure. The DB
    // write + XP award used to run INSIDE the updater callback — a React state
    // updater must be pure (StrictMode double-invokes them in development, so
    // every log fired saveHabit twice: a duplicate habit UPDATE and a doubled
    // XP award). Same fix as saveFinances' goals sync — see the comment there.
    const t = tmpl(id);
    // THE tick math (src/lib/habitTick.js), shared with the Health-side tick.
    const cutoff90 = new Date(); cutoff90.setDate(cutoff90.getDate() - 90);
    const next = applyHabitTick(habit, { date: today, count, target: t.target, weekStart: localWeekStart(today), keepSince: localDateStr(cutoff90) })
      // "Log more" on an already-logged day: completions unchanged, count still adds.
      || { weekCount: Math.min(habit.weekCount + count, t.target * 2), streak: habit.streak + 1, completions: habit.completions || [], lastLoggedDate: today };
    const { weekCount: newCount, streak: newStreak, completions: trimmed } = next;
    saveHabit(id, newStreak, newCount, trimmed, { label:habit.label, icon:habit.icon, target:habit.target, unit:habit.unit });
    setHabits(prev => prev.map(h => h.id === id
      ? { ...h, weekCount: newCount, streak: newStreak, completions: trimmed, lastLoggedDate: today, lastLoggedAt: new Date().toISOString() }
      : h));
    setJustLogged(id);
    setTimeout(() => setJustLogged(null), 2200);
    setLogModal(null); setLogVal(1);
    // Gym: one quick sheet for the workout's details (type, duration, note).
    // Skip keeps just the tick; a workout row already on today (a watch sync,
    // Health's "+ Log workout") means no sheet. `silent` = ticked from Health.
    if (!opts.silent && shouldAskWorkoutDetails({ habitId: id, workouts, date: today })) {
      setWorkoutSheet({ habitId: id, date: today, type: "Lift", duration: "", note: "" });
    }
  };

  // Tick the Gym habit for a workout's date (Health's "+ Log workout"): today or
  // up to 2 days back fills that day's history so the streak reflects it; a
  // day already logged is a no-op; no Gym habit, nothing to tick. Silent: no
  // details sheet (the workout row is the details).
  const tickHabitForDate = (id, date) => {
    const habit = habits.find(h => h.id === id && h.active !== false);
    if (!habit) return;
    if (!requirePro("habit")) return;
    const today = localDateStr();
    const t = tmpl(id);
    const cutoff90 = new Date(); cutoff90.setDate(cutoff90.getDate() - 90);
    // `today`: a tick for a past date recomputes the streak from the full
    // history (src/lib/habitTick.js) instead of incrementing the stored one.
    const next = applyHabitTick(habit, { date, target: t?.target || habit.target || 5, weekStart: localWeekStart(today), keepSince: localDateStr(cutoff90), today });
    if (!next) return;
    saveHabit(id, next.streak, next.weekCount, next.completions, { label:habit.label, icon:habit.icon, target:habit.target, unit:habit.unit }, { lastCompleted: next.lastLoggedDate });
    setHabits(prev => prev.map(h => h.id === id ? { ...h, ...next, lastLoggedAt: new Date().toISOString() } : h));
    if (date === today) { setJustLogged(id); setTimeout(() => setJustLogged(null), 2200); }
  };

  // THE workouts insert (src/lib/workoutLog.js buildWorkoutRow): the tick sheet
  // and Health's "+ Log workout" both land here. Returns the saved row or null
  // (persistRow has already toasted why).
  const saveWorkoutRow = async (fields) => {
    if (!user) return null;
    if (!requirePro("workout")) return null;
    const data = await persistRow("that workout", supabase.from("workouts").insert([buildWorkoutRow({ ...fields, userId: user.id })]).select("id,type,name,duration_min,notes,date,distance_m,source,created_at,start_at,end_at").single());
    if (!data) return null;
    setWorkouts(prev => [data, ...prev].slice(0, 60));
    return data;
  };
  // One note per logged day, saved on the habit row (gated on the write).
  const saveHabitNote = async (id, date, text) => {
    const habit = habits.find(h => h.id === id);
    if (!habit) return false;
    const cutoff90 = new Date(); cutoff90.setDate(cutoff90.getDate() - 90);
    const notes = setHabitNote(habit.notes, date, text, { keepSince: localDateStr(cutoff90) });
    if (user) {
      const q = habit._dbId ? supabase.from("habits").update({ notes }).eq("id", habit._dbId) : supabase.from("habits").update({ notes }).eq("user_id", user.id).eq("name", id);
      if (!(await persist("that note", q))) return false;
    }
    setHabits(prev => prev.map(h => h.id === id ? { ...h, notes } : h));
    return true;
  };
  const openHabitNote = (h) => {
    const d = localDateStr(); if (h.lastLoggedDate !== d) return;
    // Workout habits: the details sheet, after the fact (2026-10-22). It edits
    // the day's workouts row when one exists, else creates it on Save.
    if (isWorkoutHabit(h.id)) { setWorkoutSheet(workoutSheetFor({ habitId: h.id, date: d, workouts, habitNote: habitNoteFor(h.notes, d) })); return; }
    setHabitNoteEdit({ id: h.id, date: d, text: habitNoteFor(h.notes, d) || "" });
  };
  const saveWorkoutSheet = async () => {
    if (!workoutSheet || workoutSheetSaving) return;
    if (!requirePro("workout")) return;
    setWorkoutSheetSaving(true);
    try {
      if (workoutSheet.editId) {
        const row = buildWorkoutRow({ userId: user?.id, type: workoutSheet.type, durationMin: workoutSheet.duration, note: workoutSheet.note, date: workoutSheet.date });
        const changes = { type: row.type, duration_min: row.duration_min, notes: row.notes };
        if (!user || await persist("that workout", supabase.from("workouts").update(changes).eq("id", workoutSheet.editId))) {
          setWorkouts(prev => prev.map(w => w.id === workoutSheet.editId ? { ...w, ...changes } : w));
          setWorkoutSheet(null);
        }
        return;
      }
      const data = await saveWorkoutRow({ type: workoutSheet.type, durationMin: workoutSheet.duration, note: workoutSheet.note, date: workoutSheet.date });
      if (!data) return; // failure keeps the sheet and what was typed
      // The note now lives on the workout row; a plain habit note for that day is cleared.
      const h = habits.find(x => x.id === workoutSheet.habitId);
      if (h && habitNoteFor(h.notes, workoutSheet.date)) await saveHabitNote(h.id, workoutSheet.date, "");
      setWorkoutSheet(null);
    } finally { setWorkoutSheetSaving(false); }
  };

  const addHabit = async id => {
    // Guard on the ref FIRST and claim the id synchronously. The old guard read
    // `habits` (React state), which does not update until the next render — so a
    // double-tap, or the onboarding loop, could pass the check twice before either
    // insert landed and write the same template twice.
    if (habits.find(h => h.id === id) || habitIdsRef.current.has(id)) return;
    habitIdsRef.current.add(id);
    const today = localDateStr();
    const emptyHistory = JSON.stringify([]);
    const t = HABIT_TEMPLATES.find(t => t.id === id);
    const extraCols = t ? { label: t.label, icon: t.icon, target: t.target, unit: t.unit } : {};
    if (user) {
      // last_completed is NULL at creation — adding a habit is not doing it.
      // Writing `today` here made a brand-new habit read as done-today
      // everywhere that derives from last_completed: the client marked it
      // completed at next load, notify-cron's loggedOnLocalDate counted it in
      // habitsDoneToday, and after a reload the card showed "Done for Today"
      // for a habit never logged once — which also blocked its first real log
      // that day (logHabit early-returns when lastLoggedDate === today).
      const { data, error } = await supabase.from("habits")
        .insert([{ user_id: user.id, name: id, streak: 0, week_count: 0, history: emptyHistory, last_completed: null, last_week_reset: today, completed: false, ...extraCols }])
        .select().single();
      // Release the claim only on failure, so a retry is possible but a success
      // stays claimed for the rest of the session.
      if (error || !data) { habitIdsRef.current.delete(id); console.error("addHabit failed", error); }
      else setHabits(p => p.some(h => h.id === id) ? p : [...p, { id, streak:0, weekCount:0, completions:[], active:true, _dbId: data.id, ...extraCols }]);
    } else {
      setHabits(p => p.some(h => h.id === id) ? p : [...p, { id, streak:0, weekCount:0, completions:[], active:true, ...extraCols }]);
    }
    setShowAdd(false);
  };

  const addCustomHabit = async () => {
    if (!customHabit.label.trim()) return;
    const id = "custom_" + Date.now();
    const today = localDateStr();
    const emptyHistory = JSON.stringify([]);
    const target = parseInt(customHabit.target) || 5;
    const newH = { id, label: customHabit.label.trim(), target, unit: customHabit.unit, streak:0, weekCount:0, completions:[], active:true };
    if (user) {
      // Same as addHabit: born unlogged, last_completed null.
      // Gated (map 5.12 shape): failure keeps the modal open with the typed
      // label/target so Save is retryable.
      const data = await persistRow("that habit", supabase.from("habits")
        .insert([{ user_id: user.id, name: id, streak: 0, week_count: 0, history: emptyHistory, last_completed: null, last_week_reset: today, completed: false, label: newH.label, target, unit: newH.unit }])
        .select().single());
      if (!data) return;
      setHabits(p => [...p, { ...newH, _dbId: data.id }]);
    } else {
      setHabits(p => [...p, newH]);
    }
    setCustomHabit({ label:"", target:"5", unit:"times/week" });
    setHabitTab("templates");
    setShowAdd(false);
  };

  const saveEditHabit = async () => {
    if (!editHabit || !editHabit.label.trim()) return;
    const target = parseInt(editHabit.target) || 5;
    const updates = { label: editHabit.label.trim(), target, unit: editHabit.unit };
    if (user) {
      await persist("that habit", supabase.from("habits").update(updates).eq("user_id", user.id).eq("name", editHabit.id));
    }
    setHabits(p => p.map(h => h.id === editHabit.id ? { ...h, ...updates } : h));
    setEditHabit(null);
  };

  const confirmDeleteHabit = async (id) => {
    const ok = !user || await persist("that habit", supabase.from("habits").delete().eq("user_id", user.id).eq("name", id));
    // Dismiss the modal either way: persist() has already raised a toast naming
    // what failed, and leaving the dialog open with no explanation reads as a
    // frozen button. The habit itself only disappears if the write landed.
    setDeleteHabitId(null);
    if (!ok) return;
    habitIdsRef.current.delete(id); // release the claim so it can be added again
    setHabits(p => p.filter(h => h.id !== id));
  };

  const takeSupp = async (id) => {
    const newStreak = (supplements.find(s=>s.id===id)?.streak||0) + 1;
    // Use local date to avoid UTC timezone mismatch
    const _now = new Date();
    const todayDate = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,"0")}-${String(_now.getDate()).padStart(2,"0")}`;
    // Gated (audit W6 sibling): a rejected update used to bump the streak and
    // grant XP anyway — a supplement "taken" that the DB never recorded.
    if (user && !(await persist("that supplement", supabase.from("supplements").update({ streak: newStreak, taken_today: true, last_taken_date: todayDate }).eq("id", id)))) return;
    setSupplements(prev => prev.map(s => {
      if (s.id !== id || s.takenToday) return s;
      return { ...s, takenToday: true, streak: newStreak };
    }));
    setSuppJustLogged(id);
    setTimeout(() => setSuppJustLogged(null), 2200);
    awardXP("supplement");
  };

  const addSupp = async () => {
    if (!newSupp.name.trim()) return;
    if (user) {
      // Gated (audit W6): the silent-insert-cluster member map 5.4-5.14 never
      // listed. Failure keeps the modal open with what the user typed.
      const data = await persistRow("that supplement", supabase.from("supplements").insert([{ user_id: user.id, name: newSupp.name.trim(), dose: newSupp.dose.trim(), timing: newSupp.timing, streak: 0, taken_today: false }]).select().single());
      if (!data) return;
      setSupplements(prev => [...prev, { ...data, takenToday: false }]);
    } else {
      const id = "supp_" + Date.now();
      setSupplements(prev => [...prev, { id, name: newSupp.name.trim(), dose: newSupp.dose.trim(), timing: newSupp.timing, streak: 0, takenToday: false }]);
    }
    ensureTrackerEnrolled("supplements", "Supplements now count toward your Health score.");
    setNewSupp({ name:"", dose:"", timing:"Morning" });
    setShowAddSupp(false);
  };

  const removeSupp = async (id) => {
    if (user && !(await persist("that supplement", supabase.from("supplements").delete().eq("id", id)))) return;
    setSupplements(prev => prev.filter(s => s.id !== id));
  };

  const addWorkout = async () => {
    if (!user) return;
    setWorkoutError("");
    const type = String(newWorkout.type || "").trim();
    if (!type) { setWorkoutError("Please pick a workout type."); return; }
    // Distance is entered in miles and stored in meters (buildWorkoutRow), the
    // unit healthSync writes to workouts.distance_m.
    if (String(newWorkout.distance_mi).trim() !== "") {
      const mi = parseFloat(newWorkout.distance_mi);
      if (!Number.isFinite(mi) || mi < 0) { setWorkoutError("Distance must be a positive number of miles."); return; }
    }
    const date = newWorkout.date || localDateStr();
    // ONE workout log (2026-10-22): the same insert the Gym tick sheet uses.
    const data = await saveWorkoutRow({ type, name: newWorkout.name, durationMin: newWorkout.duration_min, distanceMi: newWorkout.distance_mi, note: newWorkout.notes, date });
    if (!data) { setWorkoutError("Could not save that workout. Please try again."); return; }
    // Logging from Health also ticks the Gym habit for that date (today or up
    // to 2 days back), with no second prompt. Older backdated workouts count
    // toward the goal and the Health score through the day-dedupe only.
    const tickDate = habitTickDateForWorkout({ date, today: localDateStr() });
    if (tickDate) tickHabitForDate("gym", tickDate);
    setNewWorkout({ type:"Lift", customType:"", name:"", duration_min:"", distance_mi:"", notes:"", date: localDateStr() });
    setWorkoutError("");
    setShowAddWorkout(false);
  };

  const deleteWorkout = async (id) => {
    const ok = !user || await persist("that workout", supabase.from("workouts").delete().eq("id", id));
    // Same as confirmDeleteHabit: the inline confirm/cancel row is dismissed
    // either way, the row itself only goes if the write landed.
    setWorkoutDeleteConfirm(null);
    if (!ok) return;
    setWorkouts(prev => prev.filter(w => w.id !== id));
  };

  const submitNewScore = () => {
    const s = parseInt(newScoreInput);
    if (isNaN(s) || s < 300 || s > 850) return;
    setCreditScore(s);
    saveCreditScore(s);
    setNewScoreInput(""); setShowUpdateScore(false);
  };

  // eslint-disable-next-line no-unused-vars
  const simScore = () => {
    let s = creditScore;
    const util = Math.max(0, 2400 - simActions.paydown * 100);
    if (util / 5000 < 0.1) s += 45;
    else if (util / 5000 < 0.3) s += 25;
    if (simActions.newCard) s -= 8;
    if (simActions.missedPayment) s -= 90;
    if (simActions.oldAccount) s -= 15;
    return Math.min(850, Math.max(300, s));
  };

  const scLabel = (s) => s>=800?"Exceptional":s>=740?"Very Good":s>=670?"Good":s>=580?"Fair":"Poor";
  const scColor = (s) => s>=740?T.success:s>=670?T.accent:s>=580?T.accent:T.danger;

  const logMood = async () => {
    if (!todayMood || moodSaving) return; // a tap while saving is ignored (the saver also dedupes)
    const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const todayStr = localDateStr();
    const todayDay = days[new Date().getDay()];
    const score = todayMood;
    setMoodSaving(true);
    try {
      // Gated on the write landing — this used to update moodHistory and flip
      // moodLogged before the fire-and-forgotten save resolved, so a rejected
      // write still rendered as logged for the rest of the session.
      if (!(await saveMood(score, todayNote))) return;
      setMoodHistory(prev => {
        // Replace today's entry if it exists, otherwise append
        const withoutToday = prev.filter(m => m.date !== todayStr);
        return [...withoutToday, { day: todayDay, score, date: todayStr, loggedAt: new Date().toISOString(), note: todayNote || "" }];
      });
      setTodayMood(score);
      setMoodLogged(true);
    } finally { setMoodSaving(false); }
  };

  // One-tap mood log from the mood sheet: a tapped score saves (with the optional
  // note if the field is open) and the sheet closes. Gated on the write landing.
  const logMoodQuick = async (score) => {
    if (moodSaving) return;
    const note = moodSheetNoteOpen ? moodSheetNote.trim() : "";
    setMoodSaving(true);
    try {
      if (!(await saveMood(score, note || null))) return;
      const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
      const todayStr = localDateStr();
      const todayDay = days[new Date().getDay()];
      setMoodHistory(prev => [...prev.filter(m => m.date !== todayStr), { day: todayDay, score, date: todayStr, loggedAt: new Date().toISOString(), note: note || "" }]);
      setTodayMood(score); setMoodLogged(true);
      setShowMoodSheet(false); setMoodSheetNoteOpen(false); setMoodSheetNote("");
    } finally { setMoodSaving(false); }
  };

  const finishCheckIn = async () => {
    const phqTotal = phqAnswers.reduce((a,b) => a + (b||0), 0);
    const gadTotal = gadAnswers.reduce((a,b) => a + (b||0), 0);
    // "below_threshold" (1-2), not "mild": PHQ-2/GAD-2 validation defines a
    // single >=3 screening cutpoint; a graded "mild" band implies a precision
    // the two-item instruments do not have (docs/SCORING-AUDIT.md item 4).
    const phqRisk  = phqTotal >= 3 ? "elevated" : phqTotal >= 1 ? "below_threshold" : "low";
    const gadRisk  = gadTotal >= 3 ? "elevated" : gadTotal >= 1 ? "below_threshold" : "low";
    setCheckInResults({ phqTotal, gadTotal, phqRisk, gadRisk, mood: todayMood });
    // Deterministic crisis check on the free-text note: every user, before
    // and regardless of any AI branch below.
    if (containsCrisisLanguage(todayNote)) setShowCrisisResources(true);
    setCheckInStep(4);
    // Demo has no Supabase session, so /api/claude would 401. Matches the
    // isDemoMode guard generateBriefing already uses — demo stays self-contained.
    if (isDemoMode) {
      setWellnessMsg("Thanks for checking in. Your mood and stress answers are logged here in the demo. In the full app your coach responds with a personal reflection based on your recent patterns.");
      return;
    }
    if (!requirePro("checkin")) return;
    if (!aiConsent) { setShowAiConsentModal(true); return; }
    setWellnessAiLoading(true);
    try {
      const res = await apiFetch(apiUrl("/api/claude"), {
        timeoutMs: 90000, // model generation: slow first byte by design
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          max_tokens: 500,
          system: `You are ${coachName}, the coach inside the Scorify app. The user just finished a mental wellness check-in. Write 2 to 3 short sentences in a calm, plain, matter-of-fact voice. Briefly reflect what their result shows, then offer one small, concrete idea they could try today. Do not diagnose, and do not frame this as therapy, treatment, or emotional support. Do not gush or open with praise ("Great job", "I'm so proud", "Amazing"). No exclamation points. If it fits naturally, you can mention they can talk with you in the Coach tab, but do not force it. House copy rule: never use em dashes, en dashes, or double hyphens in your reply. Use a comma, period, or colon instead; hyphens only inside compound words.`,
          messages: [{ role: "user", content: `My mood today: ${todayMood}/10. PHQ-2 score: ${phqTotal}/6 (${phqRisk === "below_threshold" ? "below screening threshold" : phqRisk} depression indicators). GAD-2 score: ${gadTotal}/6 (${gadRisk === "below_threshold" ? "below screening threshold" : gadRisk} anxiety indicators). My note: "${todayNote || "no note"}". Please give me a short, plain response.` }]
        })
      });
      const d = await res.json();
      const limitMsg = apiErrorMessage(res, d);
      setWellnessMsg(limitMsg || d.content?.map(b => b.text||"").join("") || "Thank you for checking in. Remember, small steps every day add up.");
    } catch { setWellnessMsg("Thanks for checking in today. Whatever you're feeling is valid. Take it one moment at a time. "); }
    setWellnessAiLoading(false);
  };

  const resetCheckIn = () => { setCheckInStep(0); setPhqAnswers([null,null]); setGadAnswers([null,null]); setCheckInResults(null); setWellnessMsg(null); setTodayNote(""); /* keep todayMood + moodLogged if already logged today */ };

  // ── JOURNAL HELPERS ──────────────────────────────────────────────────────────
  const saveJournalEntry = async () => {
    if (!journalText.trim()) return;
    if (!requirePro("journal")) return;
    setJournalSaving(true);
    const entry = {
      user_id: user?.id || "demo",
      text: journalText.trim(),
      mood_score: journalMood || null,
      created_at: new Date().toISOString(),
      date: localDateStr(),
    };
    // Deterministic crisis check, before the save so a failed insert cannot
    // swallow it. Every user, no AI involved.
    if (containsCrisisLanguage(entry.text)) setShowCrisisResources(true);
    if (user) {
      const { data, error } = await supabase.from("journal_entries").insert([entry]).select().single();
      // The textarea is only cleared on a CONFIRMED save. This used to clear
      // unconditionally, so a rejected insert (offline, RLS, constraint)
      // destroyed the entry — journal text is the least recoverable data in
      // the app, written once and gone. On failure the text stays in the box,
      // the user sees why, and Save remains tappable to retry.
      if (error || !data) {
        console.error("journal save failed", error);
        reportError(new Error(`journal save failed — ${error?.message || "no row returned"}`), { op: "db.journal", code: error?.code });
        showToast("Couldn't save your entry. Your text is still here. Check your connection and try again.");
        setJournalSaving(false);
        return;
      }
      setJournalEntries(prev => [data, ...prev]);
    } else {
      setJournalEntries(prev => [{ ...entry, id: "j_" + Date.now() }, ...prev]);
    }
    setJournalText("");
    setJournalMood(null);
    setJournalSaving(false);
  };

  const deleteJournalEntry = async (id) => {
    if (user && !(await persist("that entry", supabase.from("journal_entries").delete().eq("id", id)))) return;
    setJournalEntries(prev => prev.filter(e => e.id !== id));
  };

  const toggleJournalCoach = async (val) => {
    setJournalCoachEnabled(val);
    if (user) {
      await persist("that setting", supabase.from("journal_settings").upsert({ user_id: user.id, coach_enabled: val }, { onConflict: "user_id" }));
    }
  };

  // ── ONBOARDING HELPERS ──────────────────────────────────────────────────────
  const requestPushPermission = async () => {
    if (!window.Capacitor?.isNativePlatform?.()) return;
    try {
      const OneSignal = window.plugins?.OneSignal || require('onesignal-cordova-plugin').default;
      await OneSignal.Notifications.requestPermission(true);
      // Guarded optIn (see App.js ensurePushPermission): permission and
      // subscription opt-in are separate v5 states; optIn() cannot re-prompt
      // here because it only prompts when permission is undetermined.
      const granted = await OneSignal.Notifications.getPermissionAsync?.();
      if (granted) { try { OneSignal.User?.pushSubscription?.optIn?.(); } catch {} }
      if (user?.id) {
        await OneSignal.login(String(user.id));
        // Same device-alias record App.js keeps for account-switch detection.
        try { localStorage.setItem('scorify_onesignal_ext', String(user.id)); } catch {}
      }
    } catch (e) {
      console.log('OneSignal permission request error', e);
    }
  };

  const handleSignOut = async () => {
    // No OneSignal.logout() here — routine sign-out KEEPS the alias. Clearing
    // it on every sign-out churned identity records across reinstalls until
    // five orphans absorbed every push (Sept 2026 delivery outage). The alias
    // moves on account switch (App.js linkOneSignalUser compares the stored
    // device alias) and is cleared on account deletion, which still logs out.
    // The device's push subscription is opted OUT in App.js handleSignOut
    // (src/lib/pushIdentity.js), so a signed-out phone stops receiving pushes.
    purgeUserStorage();
    if (onSignOut) onSignOut();
  };

  // "Export your data" — the access right promised in the privacy policy §7.
  // /api/export-data assembles everything the server holds (all user tables,
  // account info, coach memories); this delivers the resulting JSON as a file.
  //
  // Delivery is Filesystem.writeFile → Share.share on native, NOT
  // navigator.share with a File. The Web Share path failed on device with the
  // server returning 200: WKWebView's canShare({files}) answers true, but the
  // actual share rejects — the awaited fetch consumes the tap's transient user
  // activation (WebKit NotAllowedError), and file-backed Web Share from a bare
  // WKWebView is unreliable regardless. Native plugins have neither problem.
  // The web keeps the plain blob download link.
  //
  // Failure modes are distinct on purpose: a share that fails is not a build
  // that fails. A fetched-but-undelivered export is kept (10 min) so retrying
  // a failed share does not re-hit the API. Read-only end to end — nothing
  // here mutates state the rest of the app derives from.
  const exportRetryRef = useRef(null); // { json, at } — survives a failed delivery, not a failed build
  const exportMyData = async () => {
    if (exportLoading || isDemoMode) return;
    setExportLoading(true);
    try {
      // ── PHASE 1: build (server) — skipped when retrying a failed delivery ──
      let exportJson = null;
      const cached = exportRetryRef.current;
      if (cached && Date.now() - cached.at < 10 * 60 * 1000) exportJson = cached.json;
      if (!exportJson) {
        let res;
        try {
          res = await apiFetch(apiUrl("/api/export-data"), { method: "POST", timeoutMs: 60000 });
        } catch (netErr) {
          // apiFetch throws only on network-level failure and has already
          // reported it; here the connection wording is actually true.
          showToast("Couldn't reach the server to build your export. Check your connection and try again.");
          return;
        }
        exportJson = await res.json().catch(() => null);
        if (!res.ok || !exportJson || exportJson.error) {
          showToast(apiErrorMessage(res, exportJson) || exportJson?.error || "Couldn't build your export. Please try again.");
          return;
        }
      }

      // ── PHASE 2: deliver (client) — the server has already succeeded ──
      const filename = `scorify-export-${localDateStr()}.json`;
      const jsonStr = JSON.stringify(exportJson, null, 2);
      try {
        if (window.Capacitor?.isNativePlatform?.()) {
          await Filesystem.writeFile({ path: filename, data: jsonStr, directory: Directory.Cache, encoding: Encoding.UTF8 });
          const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
          try {
            await Share.share({ title: "Scorify data export", url: uri });
          } catch (shareErr) {
            // Cancelling the share sheet is a choice, not a failure. The iOS
            // plugin rejects with "Share canceled"; web fallbacks use AbortError.
            if (shareErr?.name === "AbortError" || /cancel/i.test(shareErr?.message || "")) {
              exportRetryRef.current = null;
              return;
            }
            throw shareErr;
          }
        } else {
          const blob = new Blob([jsonStr], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
      } catch (deliverErr) {
        // A silent client-side failure after a server 200 was invisible until
        // it hit a user; report it so the pattern pages us, not them.
        exportRetryRef.current = { json: exportJson, at: Date.now() };
        console.error("export delivery failed", deliverErr);
        reportError(deliverErr, { op: "export.deliver" });
        showToast("Your export was built, but the file couldn't be shared. Tap Export My Data to retry; it won't rebuild.");
        return;
      }

      exportRetryRef.current = null;
      if (exportJson.complete === false) {
        showToast("Export saved, but coach memories were temporarily unavailable. Export again later for a complete copy.");
      }
    } catch (err) {
      // Unforeseen only — both phases catch their own failures above.
      console.error("export failed", err);
      reportError(err, { op: "export.failed" });
      showToast("Couldn't complete your export. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  // Header avatar. Was wired straight to handleSignOut, so a single tap signed
  // the user out with no confirmation — the tooltip was the only warning, and
  // tooltips never appear on touch. Sign out now lives only in the Profile tab
  // button.
  //
  // The avatar owns the two tabs that used to live behind the bottom nav's More
  // button. It toggles on both layouts now that the menu is anchored to the
  // avatar itself rather than rendered inside the mobile-only nav block.
  const openAvatarMenu = () => setShowMoreMenu(m => !m);

  // Wraps a Supabase write so a rejection is SEEN rather than discarded.
  //
  // Every one of these writes previously ended at `await supabase.from(...)`,
  // throwing the { error } away. An RLS denial, a constraint violation, or an
  // offline device produced exactly the same result as a successful save: the
  // optimistic local state update ran, the UI showed the change, and the value
  // was gone on the next load with nothing having been reported.
  //
  // `what` is a human-readable noun that lands in the toast, so the message
  // names the thing that failed instead of a generic "something went wrong".
  //
  // Returns true on success so a caller CAN gate its local state on the write.
  // Most call sites still update optimistically and simply surface the toast —
  // reverting 20-odd independent optimistic updates is a larger behavioural
  // change than this pass is taking on. The toast at least means the user is
  // told, rather than silently handed a value the database rejected.
  const toastTimer = useRef(null);
  // tone: "error" (the default — the toast is primarily the failure channel)
  // or "success" for the rare positive notice (e.g. auto-enrolling a tracker).
  // Colour must differ, or a good-news toast reads as a failed save.
  const showToast = (msg, tone = "error") => {
    setToast(msg);
    setToastTone(tone);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 6000);
  };
  // Shared failure path for persist/persistRow. `what` is a fixed UI label
  // ("your mood", "that bill"), never user content, so it is safe to tag. The
  // row payload is deliberately not sent.
  const reportSaveFailure = (what, error) => {
    console.error(`save failed: ${what}`, error);
    // reportError itself drops transport-shaped failures while the device is
    // provably offline (see sentry.js) — no gate needed here.
    // A network failure is weather, not a bug (2026-10-23): warning level with
    // one fingerprint, so a flaky connection never floods the error list.
    // (Hand writes are not queued: the user sees the toast and taps again.)
    const transport = /load failed|failed to fetch|networkerror|network request failed|timed? ?out/i.test(String(error?.message || ""));
    reportError(new Error(`save failed: ${what} — ${error?.message || "no row returned"}`), {
      op: "db.persist", what, code: error?.code, ...(transport ? { level: "warning", fingerprint: ["save-transport-failure"] } : {}),
    });
    // "Couldn't save" is wrong when the truth is "you're offline" — say which.
    // navigator.onLine === false is the trustworthy direction of that flag;
    // when it claims online but the request still died, keep the hedged
    // wording, because "you're offline" would then be the lie.
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    showToast(offline
      ? `You're offline. ${what.charAt(0).toUpperCase() + what.slice(1)} wasn't saved. Try again when you're back.`
      : `Couldn't save ${what}. Check your connection; the change may not be there when you return.`);
  };
  const persist = async (what, query) => {
    const { error } = await query;
    if (error) { reportSaveFailure(what, error); return false; }
    return true;
  };
  // Row-returning sibling of persist(), for handlers that need the inserted or
  // updated row back (`.select().single()` queries) to build local state.
  // Same failure handling; returns the row, or null on failure. Note the
  // `!data` check is CORRECT here and would be wrong in persist(): a query
  // without .select() legitimately returns null data, but a .single() that
  // returns nothing means the write did not land.
  const persistRow = async (what, query) => {
    const { data, error } = await query;
    if (error || !data) { reportSaveFailure(what, error); return null; }
    return data;
  };

  // ── Commitment actions (Up next card) ──────────────────────────────────────
  // Manual changes write to coach_commitments immediately so Kai and the cron see
  // them at once; local state is gated on the write landing (persist returns bool),
  // per the silent-success rule. A manually completed item leaves needs_confirmation,
  // so it is never nagged about.
  const updateCommitment = async (id, changes) => {
    if (isDemo || !user) { setCommitments(cs => cs.map(c => c.id === id ? { ...c, ...changes } : c)); return true; }
    const ok = await persist("that plan", supabase.from("coach_commitments")
      .update({ ...changes, updated_at: new Date().toISOString() }).eq("id", id).eq("user_id", user.id));
    if (ok) setCommitments(cs => cs.map(c => c.id === id ? { ...c, ...changes } : c));
    return ok;
  };
  const nowIso = () => new Date().toISOString();
  // A workout-type commitment that DID happen (Yes on the confirm card, or Mark
  // done) lands the workout (2026-10-24): the commitment's local date (never a
  // future day) gets a workouts row, type from the title, when none exists, and
  // the Gym habit is ticked under the Health rule (today or up to 2 days back;
  // a past-date tick recomputes the streak from history). Runs only after the
  // commitment write landed; demo/no user does nothing. Planner commitments
  // carry metric_type 'workout' and take the same path.
  const landWorkoutForCommitment = async (c) => {
    if (!c || isDemo || !user) return;
    const plan = workoutWritesForConfirmed({ commitment: c, workouts, today: localDateStr() });
    if (!plan) return;
    if (plan.row) await saveWorkoutRow({ type: plan.row.type, note: plan.row.note, date: plan.row.date });
    if (plan.tickDate) tickHabitForDate("gym", plan.tickDate);
  };
  const markCommitmentDone = async (id) => {
    const c = commitments.find(x => x.id === id);
    const ok = await updateCommitment(id, { status: "done", resolved_at: nowIso(), evidence: "You marked it done" });
    if (ok) await landWorkoutForCommitment(c);
    return ok;
  };
  const removeCommitment = (id) => updateCommitment(id, { status: "dropped", resolved_at: nowIso() });
  // Yes lands the workout too; No changes nothing else (it just resolves as missed).
  const confirmCommitment = async (id, didHappen) => {
    const c = commitments.find(x => x.id === id);
    const ok = await updateCommitment(id, didHappen
      ? { status: "done", resolved_at: nowIso(), evidence: "You confirmed" }
      : { status: "missed", resolved_at: nowIso(), evidence: "You said it didn't happen" });
    if (ok && didHappen) await landWorkoutForCommitment(c);
    return ok;
  };
  // Rescheduling reopens the plan and clears the one-time flags so it can be
  // reminded and (if needed) confirmed again on the new date.
  const rescheduleCommitment = (id, dueDate, dueTime) => updateCommitment(id, {
    status: "open", due_date: dueDate || null, due_time: dueTime || null,
    resolved_at: null, confirm_asked_at: null, reminder_sent_at: null, evidence: null,
  });
  // Status display: each status is distinct by SHAPE + color, not color alone (open and
  // missed are both reddish in the theme). open = hollow accent ring; the rest are solid
  // (amber / green / red / gray). statusDot/statusPill are the one source of truth, used
  // on the calendar grid, its legend, the day detail, and the Up next card.
  const STATUS_TONE = { accent: T.accent, warning: T.warning, success: T.success, danger: T.danger, dim: T.textDim };
  const statusStyle = (status) => { const meta = statusMeta(status); return { ...meta, color: STATUS_TONE[meta.tone] || T.accent }; };
  const statusDot = (status, size = 10) => {
    const s = statusStyle(status);
    return <span style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, boxSizing: "border-box", ...(s.hollow ? { border: `2px solid ${s.color}`, background: "transparent" } : { background: s.color }) }} />;
  };
  const statusPill = (status) => {
    const s = statusStyle(status);
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: s.color, background: withAlpha(s.color, 0.14), borderRadius: 999, padding: "3px 9px", flexShrink: 0 }}>
        {statusDot(status, 7)}{s.label}
      </span>
    );
  };
  const daysFromToday = (dstr) => {
    if (!dstr) return null;
    const a = new Date(`${localDateStr()}T00:00:00`), b = new Date(`${dstr}T00:00:00`);
    if (isNaN(a) || isNaN(b)) return null;
    return Math.round((b - a) / 86400000);
  };

  // Writes fields onto the caller's profiles row and CONFIRMS the row was touched.
  //
  // Why not upsert(): profiles.username is NOT NULL + UNIQUE, and PostgREST upsert
  // issues INSERT ... ON CONFLICT, so Postgres runs the NOT NULL check on the
  // proposed tuple *before* resolving the conflict. Any upsert whose payload omits
  // username therefore failed with "null value in column username violates not-null
  // constraint" — even for users who already had a row. That is what silently broke
  // both "Skip setup" and the final save.
  //
  // Returns { error } and treats "0 rows updated" as a failure, so the caller can
  // only dismiss the flow after the write is verified to have landed.
  const writeProfile = async (fields) => {
    // Timezone is stamped on every profile write, which is what closes the gap
    // for brand-new users: the row is CREATED here during onboarding, so waiting
    // for a dashboard mount to capture it left a window in which a user could
    // grant AI consent and be picked up by the cron with timezone still null.
    const tz = browserTimezone();
    if (tz) fields = { ...fields, timezone: tz };
    const { data, error } = await supabase
      .from("profiles").update(fields).eq("id", user.id).select("id, onboarding_complete");
    if (error) return { error, row: null };
    if (data && data.length > 0) return { error: null, row: data[0] };
    // No row matched — brand new user whose profiles row was never created.
    // username is NOT NULL + UNIQUE, so seed a unique handle rather than the display name.
    const base = (user.email ? user.email.split("@")[0] : "user").replace(/[^a-zA-Z0-9_.-]/g, "").slice(0, 20) || "user";
    const seeded = { id: user.id, username: `${base}_${user.id.slice(0, 8)}`, ...fields };
    const { data: insData, error: insError } = await supabase
      .from("profiles").insert(seeded).select("id, onboarding_complete");
    if (insError) return { error: insError, row: null };
    if (!insData || insData.length === 0) return { error: new Error("profile insert returned no row"), row: null };
    return { error: null, row: insData[0] };
  };

  // Persists EVERYTHING onboarding collected. Runs once, when leaving the bank
  // step — before the consent step and Kai's first message, so the greeting has
  // real saved data behind it. Returns true on success; the caller does not
  // advance on failure. (Formerly `completeOnboarding`, which ran at the final
  // button and also dismissed the flow — dismissal is now finishOnboarding.)
  const persistOnboarding = async () => {
    if (!user) return false;
    // Save profile: display name, coach name, focus baseline, tracker
    // enrollment, and mark complete.
    // goal_type is NOT written here — public.profiles has no goal_type column.
    // It is persisted on body_stats.goal_type below.
    const resolvedCoachName = obCoachName.trim() || "Kai";
    const profileFields = { coach_name: resolvedCoachName, onboarding_complete: true };
    if (obName.trim()) {
      // display_name only. username is UNIQUE across all users, so writing a
      // first name there collides as soon as two people share a name.
      profileFields.display_name = obName.trim();
    }
    // Focus baseline — profiles columns, not Mem0: a stable fact for every
    // coach payload, not something to retrieve semantically. The note stays on
    // our servers; it reaches Anthropic only inside consent-gated coach calls.
    profileFields.focus_pillar = obFocus;
    profileFields.focus_note = obFocusNote.trim() || null;
    // The focus answer DOES something: explicit tracker enrollment. Preset
    // trackers (see FOCUS_PRESETS for why not the pillar's full list), the
    // Plaid-only pair when a bank actually connected, plus defaultTrackers()'
    // inferences from what onboarding collected.
    if (obFocus && FOCUS_PRESETS[obFocus]) {
      const preset = FOCUS_PRESETS[obFocus];
      const enrolled = new Set(preset.trackers);
      if (obPlaidDone) preset.plaidTrackers.forEach(t => enrolled.add(t));
      defaultTrackers({
        habits: obHabits.map(id => ({ id })),
        data: { plaidConnected: obPlaidDone, effectiveExpenses: obExpenses, savings: obSavings },
      }).forEach(t => enrolled.add(t));
      profileFields.enabled_trackers = Array.from(enrolled);
    }
    const { error: profileErr, row: profileRow } = await writeProfile(profileFields);
    if (profileErr || !profileRow?.onboarding_complete) {
      console.error("onboarding profile write failed", profileErr);
      alert("Couldn't save your profile. Please try again.");
      return false; // don't advance — let the user retry
    }
    // Local state only after the DB write is confirmed, so a failed save can't
    // leave the UI showing a name/coach the database does not have.
    setCoachName(resolvedCoachName);
    if (obName.trim()) setUsername(obName.trim());
    setFocusPillar(obFocus);
    setFocusNote(obFocusNote.trim() || null);
    if (profileFields.enabled_trackers) setEnabledTrackers(profileFields.enabled_trackers);
    // Save body stats. height_in / sex / activity_level are no longer
    // collected (orphaned when BMI/BMR/calorie targets were removed) — the
    // columns remain in the table for existing rows, deliberately unwritten.
    const bsPayload = { user_id: user.id, current_weight: parseFloat(obWeight)||null, goal_weight: parseFloat(obGoalWeight)||null, goal_type: obGoalType, updated_at: new Date().toISOString() };
    // body_stats is the ONLY home for goal_type, so a silent failure here loses
    // the "What brings you here?" answer. Log it and keep local state truthful.
    const { error: bsErr } = await supabase.from("body_stats").upsert(bsPayload, { onConflict: "user_id" });
    if (obBirthYear) await saveBirthYear(obBirthYear);
    if (bsErr) console.error("onboarding body_stats write failed", bsErr);
    else setBodyStats(bsPayload);
    // Save selected habits (de-duped — see the toggle above)
    for (const id of Array.from(new Set(obHabits))) {
      await addHabit(id);
    }
    // Save first goal
    const obTitle = obGoalTitle.trim();
    const obKey = goalKey(obTitle);
    // Milestones have no numeric target (they save as 0/1), so the
    // target-required guard exempts them — custom-shape milestones only;
    // every template chip carries its own target.
    const chip = QUICK_GOALS.find(q => q.title === obTitle);
    const hasRequiredTarget = obGoalTarget || (!chip && obGoalShape === "milestone");
    if (obTitle && hasRequiredTarget && !goalTitlesRef.current.has(obKey)) {
      goalTitlesRef.current.add(obKey); // claim before the await, as in addGoal
      // Untouched chip title → that chip's bindings; edited title → the
      // custom path, where the SHAPE decides the fields. Same construction
      // addGoal uses (buildGoalPayload) — map 4.2 stays closed.
      // Credit payoff binds to the live bank ONLY when one is connected with a
      // real balance; otherwise it degrades to a manual $ goal (the "connect
      // to track automatically" case). start_value anchors the reduction.
      const ccBind = chip?.source_type === 'credit_payoff';
      const ccStart = ccBind ? liveCreditBalance() : 0;
      const ccAuto = ccBind && plaidConnected && ccStart > 0;
      const shapeFields = ccBind ? (ccAuto ? {
        source_type: 'credit_payoff', source_config: {},
        start_value: ccStart, target_value: String(capCreditTarget(obGoalTarget, ccStart)),
        current_value: "0", unit: "$", deadline: obGoalDeadline,
      } : {
        // No bank: manual $ goal, self-updated. current_value carries any
        // already-paid head-start (moneyStart).
        source_type: null, source_config: {},
        target_value: obGoalTarget, current_value: obGoalCurrent || "0", unit: "$", deadline: obGoalDeadline,
      }) : chip ? {
        source_type: chip.source_type, source_config: chip.source_config,
        period: chip.period, period_target: chip.period_target,
        // moneyStart chips accept an optional head-start (already saved/paid),
        // same as the Add Goal confirm screen; everything else starts at 0.
        target_value: obGoalTarget, current_value: (chip.moneyStart && obGoalCurrent) ? obGoalCurrent : "0", unit: obGoalUnit, deadline: obGoalDeadline,
      } : obGoalShape === "milestone" ? {
        target_value: "1", current_value: "0", unit: "", deadline: obGoalDeadline,
        source_config: { manual_shape: "milestone" },
      } : (obGoalShape === "recurring" || obGoalShape === "cap") ? {
        period: obGoalPeriod, period_target: obGoalTarget,
        target_value: obGoalTarget, current_value: "0", unit: "", deadline: null,
        source_config: { manual_shape: obGoalShape },
      } : {
        target_value: obGoalTarget, current_value: "0", unit: obGoalUnit, deadline: obGoalDeadline,
      };
      const gPayload = buildGoalPayload({
        // Chip carries its own category; a custom goal infers it from the
        // picked unit, exactly like the Add Goal redesign (inferGoalCategory),
        // so the cron pillar mapping stays honest without asking a category.
        title: obTitle, category: chip ? obGoalCategory : inferGoalCategory(obGoalUnit), ...shapeFields,
      }, user.id);
      const { data, error } = await supabase.from("goals").insert([gPayload]).select().single();
      if (error || !data) {
        goalTitlesRef.current.delete(obKey);
        // Non-blocking: onboarding should finish even if the first goal collides.
        console.error("onboarding goal insert failed", error);
      } else setGoals(p => [data, ...p]);
    }
    // Save manual finances if Plaid skipped
    if (obSkippedPlaid && (obIncome || obExpenses || obSavings)) {
      await saveFinances(obIncome, obExpenses, obSavings, null);
    }
    // Seed the weight chart with the starting weight (body_stats holds the value,
    // weight_log holds the series). Only when there is no history to duplicate.
    const startWeight = parseFloat(obWeight);
    if (startWeight && weightLog.length === 0) {
      // persistRow, not a discarded destructure (audit W10): this seed is what
      // makes the weight log and the Your Goal card render at all — a silent
      // failure left a new user with a body-profile section that never
      // appeared. Still non-blocking: onboarding finishes either way, but the
      // user now sees the toast and Sentry sees the write.
      const wlRow = await persistRow("your starting weight", supabase.from("weight_log")
        .insert([{ user_id: user.id, weight: startWeight }]).select().single());
      if (wlRow) setWeightLog([wlRow]);
    }
    return true;
  };

  // Bank step → goal step. Bank now comes BEFORE goal, so nothing persists
  // here — the goal is not set yet. Navigation only.
  const advanceFromBank = () => {
    setOnboardingStep(OB_STEPS.indexOf("goal"));
  };

  // Goal step → consent step, persisting once on the way through (goal is the
  // last data step now). Backing up and returning does not re-persist — the
  // guard keeps it single-shot and every underlying write has its own dedup.
  const advanceFromGoal = async () => {
    if (!obPersisted.current) {
      const ok = await persistOnboarding();
      if (!ok) return;
      obPersisted.current = true;
    }
    setOnboardingStep(OB_STEPS.indexOf("consent"));
  };

  const finishOnboarding = () => {
    // Leaving while Kai is still writing is allowed, but not silent: the
    // greeting lands in the coach chat regardless (fetchCoachMessage appends
    // to coachMsgs), so say where it will be and pulse the coach button.
    if (obGreetingState === "loading") {
      showToast(`${coachName || "Kai"}'s first note will be waiting in the coach chat in a moment.`);
      setCoachPulse(true);
    }
    onboardingDone.current = true;
    setFreshOnboarding(true);
    setShowOnboarding(false);
    clearOnboardingResume();
    requestPushPermission();
  };

  // ONE consent-grant write, shared by the onboarding consent step and the
  // Profile-tab modal so the two can never diverge. writeProfile (not a raw
  // update) for the 0-row-new-user trap documented at the modal.
  const grantAiConsent = async () => {
    if (!user) return false;
    const { error, row } = await writeProfile({ ai_consent_granted: true, ai_consent_date: new Date().toISOString() });
    if (error || !row) {
      console.error("consent write failed", error);
      return false;
    }
    setAiConsent(true);
    try { const ck = userKey(user?.id, 'ai_consent'); if (ck) localStorage.setItem(ck, "true"); } catch {}
    return true;
  };

  const skipOnboarding = async () => {
    if (!user) return;
    // Same verified write as persistOnboarding — the flow is only dismissed
    // once profiles.onboarding_complete is confirmed true in the database.
    const { error, row } = await writeProfile({ onboarding_complete: true });
    if (error || !row?.onboarding_complete) {
      console.error("skip onboarding write failed", error);
      alert("Couldn't save. Please try again.");
      return;
    }
    onboardingDone.current = true;
    setFreshOnboarding(true);
    setShowOnboarding(false);
    clearOnboardingResume();
    requestPushPermission();
    // Skipping setup skips the lock step too. The signup flag suppresses
    // LockGate's one-time offer while onboarding owns it; with onboarding
    // abandoned and no Keychain record written, release the flag so the next
    // launch makes the offer instead of never asking.
    if (isLockCapable()) {
      readLockState(user?.id).then((s) => {
        if (!s) { try { localStorage.removeItem('scorify_lock_prompt_done'); } catch {} }
      });
    }
  };

  // ── REAL DATA INSIGHTS ENGINE ────────────────────────────────────────────────
  const computeInsights = () => {
    const results = [];
    const validMoods = moodHistory.filter(d => d.score !== null && d.score !== undefined);

    if (validMoods.length < 3) {
      return [{
        icon: "", type: "info", color: T.textMuted,
        text: "Log your mood for at least 3 days to start seeing personalized patterns.",
        recommendation: "Go to Mind → Log Mood to get started.",
        tag: null,
      }];
    }

    const today = new Date();
    const recentMoodAvg = validMoods.slice(-7).reduce((a,b)=>a+b.score,0) / Math.min(validMoods.slice(-7).length, 7);

    // ── Mood trend: last 7 vs prior 7 ──────────────────────────────────────
    const trend = moodTrend();
    if (Math.abs(trend) >= 0.5) {
      results.push({
        icon: trend > 0 ? "" : "",
        type: trend > 0 ? "positive" : "warning",
        color: trend > 0 ? T.success : T.danger,
        tag: trend > 0 ? "Improving" : "Declining",
        text: `Your mood ${trend > 0 ? "improved" : "dropped"} by ${Math.abs(trend).toFixed(1)} pts over the last 7 days compared to the prior week.`,
        recommendation: trend > 0
          ? "You're on an upswing. Identify what changed and double down on it."
          : "Try adding one positive habit this week: a short walk, journaling, or better sleep.",
      });
    }

    // ── Habit mood correlations ─────────────────────────────────────────────
    const habitCorrelations = [];
    if (validMoods.some(m => m.date) && habits.length > 0) {
      habits.forEach(h => {
        const t = tmpl(h.id);
        if (!t || !h.completions) return;
        const datesWithLog = [];
        const datesWithout = [];
        for (let i = 0; i < 28; i++) {
          const d = new Date(today); d.setDate(today.getDate() - (27 - i));
          const dateStr = localDateStr(d);
          const moodEntry = validMoods.find(m => m.date === dateStr);
          if (moodEntry) {
            if (h.completions.includes(dateStr)) datesWithLog.push(moodEntry.score);
            else datesWithout.push(moodEntry.score);
          }
        }
        if (datesWithLog.length >= 2 && datesWithout.length >= 2) {
          const avgWith    = datesWithLog.reduce((a,b)=>a+b,0) / datesWithLog.length;
          const avgWithout = datesWithout.reduce((a,b)=>a+b,0) / datesWithout.length;
          const diff = +(avgWith - avgWithout).toFixed(1);
          if (Math.abs(diff) >= 0.5) {
            habitCorrelations.push({ t, diff, avgWith, avgWithout, sampleSize: datesWithLog.length });
          }
        }
      });
    }

    // Sort correlations: strongest positive first, then strongest negative
    const positiveCorr = habitCorrelations.filter(c => c.diff > 0).sort((a,b) => b.diff - a.diff);
    const negativeCorr = habitCorrelations.filter(c => c.diff < 0).sort((a,b) => a.diff - b.diff);

    positiveCorr.slice(0, 3).forEach(({ t, diff, avgWith, avgWithout, sampleSize }) => {
      const pct = Math.round((diff / avgWithout) * 100);
      results.push({
        icon: t.icon || "",
        type: "positive",
        color: T.accent,
        tag: `+${pct}% mood boost`,
        text: `When you log ${t.label}, your mood averages ${avgWith.toFixed(1)}/10 vs ${avgWithout.toFixed(1)}/10 on days you skip it, a ${diff.toFixed(1)} pt difference across ${sampleSize} logged days.`,
        recommendation: `Prioritize ${t.label} on days you feel low. Your data shows it consistently lifts your mood.`,
      });
    });

    negativeCorr.slice(0, 1).forEach(({ t, diff, avgWith, avgWithout }) => {
      results.push({
        icon: t.icon || "",
        type: "neutral",
        color: T.accent,
        tag: "Worth noting",
        text: `Your mood is slightly lower on ${t.label} days (${avgWith.toFixed(1)} vs ${avgWithout.toFixed(1)}). This often reflects exertion; the effort is still building long-term resilience.`,
        recommendation: `Try logging your mood a day after ${t.label} to capture recovery benefits.`,
      });
    });

    // ── Day-of-week patterns ────────────────────────────────────────────────
    const byDay = {};
    validMoods.forEach(m => {
      if (!byDay[m.day]) byDay[m.day] = [];
      byDay[m.day].push(m.score);
    });
    const dayAvgs = Object.entries(byDay)
      .filter(([,v]) => v.length >= 2)
      .map(([day, scores]) => ({ day, avg: scores.reduce((a,b)=>a+b,0)/scores.length }))
      .sort((a,b) => b.avg - a.avg);
    if (dayAvgs.length >= 3) {
      const best = dayAvgs[0];
      const worst = dayAvgs[dayAvgs.length - 1];
      if (best.avg - worst.avg >= 1.0) {
        results.push({
          icon: "",
          type: "pattern",
          color: T.accent,
          tag: "Weekly pattern",
          text: `${best.day}s are your best mood days (avg ${best.avg.toFixed(1)}/10) while ${worst.day}s are your toughest (avg ${worst.avg.toFixed(1)}/10).`,
          recommendation: `Schedule demanding tasks on ${best.day}s and protect ${worst.day}s for recovery, light activity, or social connection.`,
        });
      }
    }

    // ── Medication adherence ────────────────────────────────────────────────
    // Derived from last_taken_date — the ONLY intake record that exists. The
    // medications table has no taken_today and no streak column, and there is
    // no medication log table anywhere, so the previous
    // `med.taken_today && med.streak >= 5` branch could never fire while
    // `!med.taken_today` (undefined) fired for EVERY med EVERY day, including
    // seconds after logging. This is the same comparison the med card and
    // notify-cron's loggedOnLocalDate already use: last_taken_date against the
    // user's local calendar date.
    //
    // The streak insight was DELETED, not rewritten: a streak needs per-day
    // history and only a single date is recorded. Do not reintroduce it without
    // a medication log table, and do not add a sticky taken_today boolean —
    // that pattern was just removed from habits and supplements.
    if (medications.length > 0 && validMoods.length >= 5) {
      const medTodayStr = localDateStr();
      const asNeededPat = /as.?needed|prn|when needed/i;
      medications.forEach(med => {
        // As-needed meds have no daily expectation and no log button — without
        // this exemption they read "Missed today" forever. Same regex as the
        // med card and financeData.
        if (asNeededPat.test(med.schedule || '')) return;
        const takenToday = med.last_taken_date === medTodayStr;
        if (takenToday) {
          results.push({
            icon: "",
            type: "positive",
            color: T.accent,
            tag: "Logged today",
            text: `You've logged ${med.name} today. Your current mood average is ${recentMoodAvg.toFixed(1)}/10. Consistent adherence supports a stable baseline.`,
            recommendation: "Keep your medication time consistent. Same time daily improves absorption and reduces missed doses.",
          });
        } else {
          results.push({
            icon: "!",
            type: "warning",
            color: T.danger,
            tag: "Missed today",
            text: `${med.name} hasn't been logged today. Missing doses can directly affect your mood, energy, and focus.`,
            recommendation: "Set a phone alarm for the same time each day. Pair it with an existing routine like morning coffee.",
          });
        }
      });
    }

    // ── Supplements ─────────────────────────────────────────────────────────
    if (supplements.length > 0) {
      const consistentSupps = supplements.filter(s => s.streak >= 5);
      // takenToday (derived at load, updated by takeSupp) — NOT the raw DB
      // taken_today the {...s} spread also left on the object, which takeSupp
      // never touches: reading the raw field made a supplement taken this
      // session still count as "missed" here. Read-site fix only; the column
      // and its midnight reset are correct.
      const missedSupps = supplements.filter(s => !s.takenToday && s.streak < 2);
      if (consistentSupps.length > 0) {
        results.push({
          icon: "",
          type: "positive",
          color: T.success,
          tag: "Consistent",
          text: `${consistentSupps.map(s=>s.name).join(", ")} logged for ${Math.min(...consistentSupps.map(s=>s.streak))}+ consecutive days. Supplement consistency compounds over weeks, not days.`,
          recommendation: "Track how you feel after 30 days of consistent use. That's when most supplements show measurable effects.",
        });
      }
      if (missedSupps.length > 0 && consistentSupps.length === 0) {
        results.push({
          icon: "",
          type: "neutral",
          color: T.accent,
          tag: "Inconsistent",
          text: `${missedSupps.map(s=>s.name).join(", ")} ${missedSupps.length > 1 ? "haven't" : "hasn't"} been logged consistently. Inconsistent supplementation limits its effectiveness.`,
          recommendation: "Stack your supplements with a habit you already do daily, like breakfast or brushing your teeth.",
        });
      }
    }

    // ── Financial stress & mood ─────────────────────────────────────────────
    const totalDebt = debts.reduce((a,d) => a+(d.balance||0), 0);
    if (totalDebt > 5000 && recentMoodAvg < 6) {
      results.push({
        icon: "",
        type: "warning",
        color: T.accent,
        tag: "Financial stress",
        text: `You have $${totalDebt.toLocaleString()} in debt and your mood this week averages ${recentMoodAvg.toFixed(1)}/10. Financial stress is one of the top predictors of low mood.`,
        recommendation: "Even paying $25 extra/month toward debt reduces cognitive load. Check the Money tab for a payoff plan.",
      });
    }

    // ── Low mood alert ──────────────────────────────────────────────────────
    const last5 = validMoods.slice(-5);
    if (last5.length === 5 && last5.every(m => m.score <= 5)) {
      results.push({
        icon: "",
        type: "alert",
        color: T.danger,
        tag: "Attention needed",
        text: `Your last 5 mood logs: ${last5.map(m=>m.score).join(", ")}. Persistent low mood lasting more than 2 weeks is worth discussing with a professional.`,
        recommendation: "Resources are in the section below. The Crisis Text Line (text HOME to 741741) is free and available 24/7.",
      });
    }

    // ── High performer ──────────────────────────────────────────────────────
    const last5h = validMoods.slice(-5);
    if (last5h.length >= 5 && last5h.every(m => m.score >= 7)) {
      results.push({
        icon: "",
        type: "positive",
        color: T.success,
        tag: "On a roll",
        text: `${last5h.length} consecutive mood logs at 7 or above. That's a strong run. Your current habits and routines are clearly working.`,
        recommendation: "Write down what you're doing differently this week. You'll want to reference it when things get harder.",
      });
    }

    // ── Body stats ──────────────────────────────────────────────────────────
    if (bodyStats?.current_weight && bodyStats?.goal_weight) {
      const diff = +(bodyStats.current_weight - bodyStats.goal_weight).toFixed(1);
      if (Math.abs(diff) >= 5) {
        results.push({
          icon: diff > 0 ? "" : "✓",
          type: diff > 0 ? "neutral" : "positive",
          color: diff > 0 ? T.accent : T.success,
          tag: diff > 0 ? `${diff} lbs to goal` : "Goal reached",
          text: diff > 0
            ? `You're ${diff} lbs above your goal weight of ${bodyStats.goal_weight} lbs. Weight is one of many health signals — it works alongside mood, sleep, and activity.`
            : `You've reached or passed your weight goal of ${bodyStats.goal_weight} lbs. Great work maintaining your body stats.`,
          recommendation: diff > 0
            ? "Focus on consistency over speed — logging workouts and water daily adds up over weeks."
            : "Shift focus to maintaining and building other pillars like sleep and stress management.",
        });
      }
    }

    // ── Overdue checkups ────────────────────────────────────────────────────
    const urgentCheckups = checkups.filter(c => c.urgent);
    if (urgentCheckups.length > 0) {
      results.push({
        icon: "",
        type: "warning",
        color: T.accent,
        tag: `${urgentCheckups.length} overdue`,
        text: `Overdue checkup${urgentCheckups.length > 1 ? "s" : ""}: ${urgentCheckups.map(c=>c.name).join(", ")}. Preventive care catches issues early and reduces long-term health anxiety.`,
        recommendation: "Schedule one checkup this week. Most doctor appointments can be booked online in under 5 minutes.",
      });
    }

    // ── Habit streak momentum ───────────────────────────────────────────────
    const topStreaks = [...habits].filter(h => h.streak >= 5).sort((a,b) => b.streak - a.streak).slice(0,2);
    if (topStreaks.length > 0 && results.filter(r=>r.type==="positive").length < 2) {
      const names = topStreaks.map(h => { const t = tmpl(h.id); return t ? `${t.label} (${h.streak}-day streak)` : null; }).filter(Boolean);
      results.push({
        icon: "·",
        type: "positive",
        color: T.accent,
        tag: "Momentum",
        text: `Your strongest habit streaks: ${names.join(" · ")}. Streaks this long indicate genuine behavior change, not just motivation.`,
        recommendation: "Protect these streaks at all costs — even logging a partial day keeps the chain alive.",
      });
    }

    // ── Default ─────────────────────────────────────────────────────────────
    if (results.length === 0) {
      results.push({
        icon: "", type: "info", color: T.textMuted, tag: null,
        text: "Keep logging daily — personalized insights appear as your data builds up.",
        recommendation: "Log mood, habits, and medications consistently for at least a week to unlock correlations.",
      });
    }

    return results.slice(0, 4);
  };

  const avgMood = () => { const ago28=new Date();ago28.setDate(ago28.getDate()-28);const ds28=localDateStr(ago28); const valid = moodHistory.filter(d=>d.score!==null&&d.date&&d.date>=ds28); return valid.length ? (valid.reduce((a,b)=>a+b.score,0)/valid.length).toFixed(1) : "—"; };
  const moodTrend = () => { const valid = moodHistory.filter(d=>d.score!==null&&d.date); if (valid.length < 7) return 0; const last7 = valid.slice(-7), prev7 = valid.slice(-14,-7); if (prev7.length < 7) return 0; const avgL = last7.reduce((a,b)=>a+b.score,0)/7, avgP = prev7.reduce((a,b)=>a+b.score,0)/7; return +(avgL - avgP).toFixed(1); };
  // Mood bands read through theme tokens only (accessibility pass 2026-09-12):
  // the old hardcoded #d98c2b (2.7:1) and #7a9b6e (3.1:1) failed WCAG AA as
  // text. success moodMid warning danger keeps the good-to-bad gradient.
  const moodColor = (s) => s>=9?T.success:s>=7?T.moodMid:s>=5?T.warning:T.danger;

  // ── DAILY BRIEFING HELPERS ────────────────────────────────────────────────────
  const BRIEFING_CACHE_KEY = user ? `scorify_briefing_v2_${user.id}` : null;

  // Load cached briefing on mount — 24-hour TTL
  useEffect(() => {
    if (!BRIEFING_CACHE_KEY) return;
    try {
      const saved = localStorage.getItem(BRIEFING_CACHE_KEY);
      if (saved) {
        const { json, date, dbId, feedback } = JSON.parse(saved);
        if (Date.now() - new Date(date).getTime() < 24 * 3600 * 1000) {
          setBriefingJson(json);
          setBriefingDate(date);
          if (dbId) setBriefingDbId(dbId);
          if (feedback) setBriefingFeedback(feedback);
        }
      }
    } catch {}
  }, [user?.id]); // eslint-disable-line

  // Pre-generated check-in / recap: the cron writes the day's briefing to
  // ai_briefings so a push tap opens it with NO Generate step. On mount (and on
  // a briefing-push tap) load the latest rows — a check-in fills the briefing
  // card (same JSON shape), a recent recap fills the recap card. The DB is
  // authoritative, so this can overwrite the 24h localStorage cache above.
  useEffect(() => {
    if (isDemoMode || !user?.id) return;
    let cancelled = false;
    const loadLatest = async () => {
      try {
        const { data } = await supabase.from("ai_briefings")
          .select("id, briefing_json, kind, created_at")
          .eq("user_id", user.id).order("created_at", { ascending: false }).limit(4);
        if (cancelled || !Array.isArray(data)) return;
        const latestBrief = data.find(r => (r.kind === "checkin" || r.kind === "briefing" || !r.kind) && r.briefing_json);
        // Only adopt a FRESH briefing (<24h), matching the cache TTL — otherwise a
        // days-old row would overwrite the empty-state "generate" prompt.
        if (latestBrief && (Date.now() - new Date(latestBrief.created_at).getTime()) < 24 * 3600 * 1000) {
          setBriefingJson(latestBrief.briefing_json);
          setBriefingDate(latestBrief.created_at);
          setBriefingDbId(latestBrief.id);
          setBriefingError(false); setBriefingDismissed(false);
          try { if (BRIEFING_CACHE_KEY) localStorage.setItem(BRIEFING_CACHE_KEY, JSON.stringify({ json: latestBrief.briefing_json, date: latestBrief.created_at, dbId: latestBrief.id })); } catch {}
        }
        // Recap only if fresh (within ~2 days of its Sunday send).
        const latestRecap = data.find(r => r.kind === "recap" && r.briefing_json);
        if (latestRecap && (Date.now() - new Date(latestRecap.created_at).getTime()) < 2 * 86400000) {
          setRecapJson(latestRecap.briefing_json);
          setRecapDismissed(false);
        }
      } catch { /* leave the cached briefing in place */ }
    };
    loadLatest();
    const onTap = (e) => { setTab("overview"); loadLatest(); if (e?.detail?.kind === "recap") setRecapExpanded(true); if (e?.detail?.plan) setPlannerPending(true); };
    window.addEventListener("scorify-briefing-notification", onTap);
    // Cold-launch case: a stashed pending-briefing flag from a tap that launched
    // the app. Consume it once dataLoaded so the overview is ready.
    try {
      const rawPending = localStorage.getItem("scorify_pending_briefing");
      if (rawPending) {
        localStorage.removeItem("scorify_pending_briefing");
        // Only honor a FRESH tap (review L4): the mount can race the click
        // handler, and a stale flag consumed on a later launch would yank the
        // user to the overview for no visible reason.
        try {
          const p = JSON.parse(rawPending);
          if (p?.ts && Date.now() - p.ts < 5 * 60 * 1000) { setTab("overview"); if (p.kind === "recap") setRecapExpanded(true); if (p.plan) setPlannerPending(true); }
        } catch {}
      }
    } catch {}
    return () => { cancelled = true; window.removeEventListener("scorify-briefing-notification", onTap); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isDemoMode]);

  // Auto-generate the brief on first open of a NEW account so it is never a blank,
  // below-the-fold card. Once per session, only when AI consent is already granted (so
  // this never auto-pops the consent modal) and nothing is already loaded/loading. A
  // new account with no consent shows the deterministic welcomeBrief instead.
  const briefAutoTriedRef = useRef(false);
  useEffect(() => {
    if (briefAutoTriedRef.current) return;
    if (isDemoMode || !user?.id || !dataLoaded) return;
    if (!isFirstRun || !aiConsent || pillarsPending) return;
    if (briefingJson || briefingLoading || briefingDismissed) return;
    briefAutoTriedRef.current = true;
    generateBriefing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isDemoMode, dataLoaded, isFirstRun, aiConsent, briefingJson, briefingLoading, briefingDismissed, pillarsPending]);

  // Evening-mood push deep-link: open the one-tap mood sheet. Warm case via the
  // window event; cold-launch case via the stashed flag (consumed once, fresh only).
  useEffect(() => {
    if (isDemoMode) return;
    const open = () => { setTab("wellness"); setShowMoodSheet(true); };
    window.addEventListener("scorify-mood-notification", open);
    try {
      const raw = localStorage.getItem("scorify_pending_mood");
      if (raw) {
        localStorage.removeItem("scorify_pending_mood");
        const p = JSON.parse(raw);
        if (p?.ts && Date.now() - p.ts < 30 * 60 * 1000) open();
      }
    } catch {}
    return () => window.removeEventListener("scorify-mood-notification", open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isDemoMode]);

  // Push action buttons ("Mark done" / "Mark paid", src/lib/pushActions.js):
  // the tap stashed a pending action (App.js). Consumed once auth has
  // resolved, data is loaded and the app lock is passed; gated users go
  // through requirePro; anything older than 12 hours is dropped. Warm case
  // via the window event, cold launch via the stash.
  const [lockPassed, setLockPassed] = useState(() => { try { return !native || window.__scorifyUnlocked === true; } catch { return !native; } });
  const [pendingActionTick, setPendingActionTick] = useState(0);
  useEffect(() => {
    const sync = () => { try { setLockPassed(!native || window.__scorifyUnlocked === true); } catch {} };
    const bump = () => setPendingActionTick(t => t + 1);
    sync();
    window.addEventListener(LOCK_PASSED_EVENT, sync);
    window.addEventListener(PENDING_ACTION_EVENT, bump);
    return () => { window.removeEventListener(LOCK_PASSED_EVENT, sync); window.removeEventListener(PENDING_ACTION_EVENT, bump); };
  }, []);
  useEffect(() => {
    if (isDemoMode || !user?.id || !dataLoaded || !lockPassed) return;
    const pending = readPendingAction();
    if (!pending) return;
    const r = resolvePendingAction(pending, { today: localDateStr(), month: currentMonth(), habits, bills, gated: paywallGated, now: Date.now() });
    clearPendingAction();
    if (r.kind === 'gated') { requirePro(pending.action); return; }
    if (r.kind === 'drop') return;
    if (r.kind === 'notice') { setTab(r.tab); showToast(r.message, "info"); return; }
    if (r.kind === 'habit_log') {
      if (!r.alreadyDone) logHabit(r.habitId, 1); // no-op when already logged today
      showToast(r.toast, "success");
      return;
    }
    if (r.kind === 'bill_paid') {
      (async () => { if (!r.alreadyPaid) await markBillPaid(r.billId); showToast(r.toast, "success"); })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isDemoMode, dataLoaded, lockPassed, pendingActionTick]);

  // Bank reconnect push (api/lib/bank-reconnect.js): the tap opens the Money
  // tab and, once the bank list is loaded and the app lock is passed, the
  // reconnect flow for that Item (Plaid Link in update mode). Warm case via
  // the window event, cold launch via the stash (fresh for 30 minutes). An
  // Item no longer linked just leaves the user on the Money tab.
  const [pendingReconnectItemId, setPendingReconnectItemId] = useState(null);
  useEffect(() => {
    if (isDemoMode) return;
    const open = (p) => { if (!p) return; setTab("finances"); setFinanceSubTab("overview"); setPendingReconnectItemId(p.itemId || null); };
    const onEvent = (e) => open(e?.detail);
    window.addEventListener(BANK_RECONNECT_EVENT, onEvent);
    try {
      const raw = localStorage.getItem(PENDING_BANK_RECONNECT_KEY);
      if (raw) {
        localStorage.removeItem(PENDING_BANK_RECONNECT_KEY);
        const p = JSON.parse(raw);
        if (p?.ts && Date.now() - p.ts < 30 * 60 * 1000) open(p);
      }
    } catch {}
    return () => window.removeEventListener(BANK_RECONNECT_EVENT, onEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isDemoMode]);
  useEffect(() => {
    if (!pendingReconnectItemId || isDemoMode || !user?.id || !dataLoaded || !lockPassed) return;
    const item = (plaidItems || []).find(i => i && i.item_id === pendingReconnectItemId);
    if (!item) return; // items still loading, or the bank was unlinked: the Money tab is open
    setPendingReconnectItemId(null);
    reconnectPlaidItem(item, item.institution_name || "Your bank");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReconnectItemId, plaidItems, dataLoaded, lockPassed, isDemoMode, user?.id]);

  const buildUserContext = (lastBriefing = null, sleepNights = [], findings = [], commitments = [], doneTodayById = null) => {
    const _pillars = pillars;
    const pillarPayload = Object.fromEntries(_pillars.map(p => [p.id, {
      pct: p.pct, earned: p.earned, possible: p.possible,
      tracked: p.trackedCount, total: p.totalCount,
    }]));
    // Always read from refs — freshest Plaid data, never a stale render closure
    const _conn  = plaidConnectedRef.current;
    const _accts = plaidAccountsRef.current;
    const _txs   = plaidTransactionsRef.current;

    // ── Finance ──────────────────────────────────────────────────────────────
    // Balances past BALANCE_FRESH_WINDOW_MS are not vouchable — the briefing
    // gets null (renders unknown), never a stale figure. One shared window with
    // buildCoachUserData (chat) and the cron, from src/lib/freshness.js, so the
    // same balance cannot be fresh in one Kai path and stale in another. Read
    // via ref, same as the accounts.
    const _balTs   = balanceFreshAtRef.current; // Plaid's clock (bankSyncedAt), not our write time
    const balFresh = !!_balTs && (Date.now() - new Date(_balTs).getTime()) < BALANCE_FRESH_WINDOW_MS;
    // Initial-import guard, same rule as buildCoachUserData: a bank linked
    // hours ago has partial history, so 30-day money flows are withheld.
    const _importPending = plaidImportPendingCheck(_conn, plaidConnectedAtRef.current, _txs);
    const plaidCreditAccts = _conn && balFresh ? _accts.filter(a => a.type === "credit") : [];
    const plaidCreditBal   = plaidCreditAccts.reduce((a, acc) => a + (acc.balances?.current  || 0), 0);
    const plaidCreditLimit = plaidCreditAccts.reduce((a, acc) => a + (acc.balances?.limit    || 0), 0);
    const creditUtil  = plaidCreditLimit > 0 ? Math.round((plaidCreditBal / plaidCreditLimit) * 100) : null;
    const checkingBal = _conn && balFresh ? _accts.filter(a => a.subtype === "checking").reduce((a, acc) => a + (acc.balances?.current || 0), 0) : null;
    const availBal    = _conn && balFresh ? _accts.filter(a => a.type === "depository").reduce((a, acc) => a + (acc.balances?.available ?? acc.balances?.current ?? 0), 0) : null;
    // Stale Plaid suppresses only the Plaid-derived sum; the manual savings
    // figure is user-entered, not a balance snapshot, and stays honest.
    const savingsBal  = _conn && balFresh
      ? (_accts.filter(a => a.subtype === "savings").reduce((a, acc) => a + (acc.balances?.current || 0), 0) || savings || null)
      : (savings || null);
    const _cut30 = daysAgoStr(30);
    const _depIds = _conn ? _accts.filter(a => a.type === "depository").map(a => a.account_id) : [];
    // Same rule as plaidDerivedIncome (see its comment): loose on TRANSFER_IN
    // because mobile-deposited paychecks arrive as TRANSFER_IN_DEPOSIT; only
    // loan-payment credits are safely excludable.
    // Same income_rules layer + countability decision as plaidDerivedIncome via
    // the shared isCountableInflow. freshIncome previously applied NEITHER — so a
    // confirmed rule was honored on Home but ignored in Kai's payload, and app
    // transfers were counted here regardless. One helper closes both gaps.
    const freshRuleByName = new Map(incomeRules.map(r => [r.match_name, r.classification]));
    const freshIncome = _conn && _txs.length > 0 ? _txs.filter(t =>
      t.amount < 0 && _depIds.includes(t.account_id) && txSince(t, _cut30) && isCountableInflow(t, freshRuleByName)
    ).reduce((a, t) => a + Math.abs(t.amount), 0) : 0;
    const freshExpenses = _conn && _txs.length > 0 ? _txs.filter(t =>
      isSpendTx(t) && txSince(t, _cut30)
    ).reduce((a, t) => a + t.amount, 0) : 0;
    // Top spending category (exclude transfers/income)
    const catTotals = {};
    if (_conn) {
      _txs.filter(t => isSpendTx(t) && txSince(t, _cut30)).forEach(t => {
        const friendly = spendCategoryLabel(t);
        catTotals[friendly] = (catTotals[friendly] || 0) + t.amount;
      });
    }
    const topCatEntry = Object.entries(catTotals).sort((a, b) => b[1] - a[1])[0];
    const topSpendCategory = topCatEntry ? { name: topCatEntry[0], amount: Math.round(topCatEntry[1]) } : null;

    // ── Habits ────────────────────────────────────────────────────────────────
    const todayStr     = localDateStr();
    const activeHabits = habits.filter(h => h.active !== false);
    // isNew: created within the last 2 days. The briefing prompt is required
    // to frame these as a fresh start, never as "missed" — a habit created
    // during onboarding minutes ago has no logs because it just began.
    const _habitIsNew = (h) => !!h.createdAt && (Date.now() - new Date(h.createdAt).getTime()) < 2 * 86400000;
    // doneTodayById: done-today read from the habits table at generation time
    // (generateBriefing), so the brief never describes a stale in-memory
    // snapshot (2026-10-20: "You missed Gym, Read, and Drink water today" over
    // three habits logged today). Falls back to state when the read failed.
    const _doneToday = (h) => (doneTodayById && doneTodayById.has(h.id)) ? doneTodayById.get(h.id) : h.lastLoggedDate === todayStr;
    const completedToday = activeHabits
      .filter(h => _doneToday(h))
      .map(h => ({ name: tmpl(h.id)?.label || h.id, streak: h.streak || 0, isNew: _habitIsNew(h), noteToday: journalCoachEnabled ? habitNoteFor(h.notes, todayStr) : null }));
    const missedToday = activeHabits
      .filter(h => !_doneToday(h))
      .map(h => ({ name: tmpl(h.id)?.label || h.id, streak: h.streak || 0, isNew: _habitIsNew(h) }));

    // ── Health ────────────────────────────────────────────────────────────────
    // Sensitive-names consent (item 4b): when off, medication + merchant names are
    // stripped before anything reaches Kai. Default on = current behavior.
    const _shareNames = notifPrefs?.shareSensitiveNames !== false;
    const _now = new Date(); _now.setHours(0, 0, 0, 0);
    // Optional trackers that are OFF are invisible to Kai/the briefing — no mention.
    const upcomingCare = enrolledSet.has("preventive_care") ? checkups
      .filter(c => c.next_date && new Date(c.next_date) >= _now)
      .sort((a, b) => new Date(a.next_date) - new Date(b.next_date))
      .slice(0, 4)
      .map(c => ({ name: c.name, date: c.next_date })) : [];
    const medsDueToday = enrolledSet.has("medications") ? medications
      .filter(m => m.schedule !== "As needed" && m.last_taken_date !== todayStr)
      .map(m => _shareNames ? m.name : "a medication") : [];

    // Mood is now unified to the shared shape (_ctx.mood below) — raw recent list
    // plus labeled averages and staleness — the same shape chat and the cron use.

    // ── Shared context (Phase 2) ──────────────────────────────────────────────
    // Every domain the briefing was missing (sleep, steps, workouts, screen time,
    // goals, itemized debts, supplements, recovery, XP, mental health, focus) now
    // comes from the SAME shapers chat and the cron use (src/lib/kaiContext.js),
    // so coverage and shaping no longer diverge. mood is unified to the shared
    // shape (raw recent list + labeled averages) too. Free-text (journal, mood
    // notes) stays behind journalCoachEnabled, exactly as the coach-chat payload.
    const _nowMs = Date.now();
    const _zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const _goalLines = linkWorkoutGoals(goals, habits).filter(g => !g.completed).map(g => {
      const a = getGoalAutoValue(g);
      const tgt = a.target ?? g.period_target ?? g.target_value;
      if (a.hasData === false) return `  - ${g.title} (${g.category || "goal"}): progress unavailable right now, do NOT treat as zero`;
      const pct = tgt > 0 ? Math.round((a.value / tgt) * 100) : null;
      return `  - ${g.title} (${g.category || "goal"}): ${a.value}/${tgt}${a.unit ? " " + a.unit : ""}${pct != null ? `, ${pct}%` : ""}`;
    });
    const _ctx = buildKaiContext(normalizeClientState({
      today: todayStr, nowMs: _nowMs, zone: _zone, healthLastSyncAt: healthLastSync || null,
      profile: { focusPillar, focusNote },
      habits: activeHabits.map(h => { const t = tmpl(h.id); return { name: t?.label || h.id, label: t?.label || null, streak: h.streak || 0, weekCount: h.weekCount, target: t?.target || 5, unit: t?.unit || null, loggedToday: _doneToday(h), noteToday: journalCoachEnabled ? habitNoteFor(h.notes, todayStr) : null, ageDays: h.createdAt ? Math.floor((_nowMs - new Date(h.createdAt).getTime()) / 86400000) : null }; }),
      supplements: supplements.map(s => ({ name: s.name, streak: s.streak, takenToday: s.takenToday })),
      medications: enrolledSet.has("medications") ? medications.map(m => ({ name: _shareNames ? m.name : "a medication", schedule: m.schedule, lastTakenDate: m.last_taken_date })) : [],
      recovery: recoveryTrackers.map(r => ({ label: r.label, startDate: r.start_date })),
      progress: progress ? { xp: progress.xp, level: progress.level, dailyStreak: progress.daily_streak, longestStreak: progress.longest_streak, badges: progress.badges } : null,
      moods: moodHistory.filter(m => m.score != null).slice(-14).map(m => ({ score: m.score, date: m.date, note: journalCoachEnabled ? (m.note || null) : null })),
      mentalHealth: checkInResults ? { phqTotal: checkInResults.phqTotal, gadTotal: checkInResults.gadTotal } : null,
      journalEntries: journalCoachEnabled ? journalEntries.map(e => ({ date: e.date, text: e.text, moodScore: e.mood_score })) : [],
      sleepNights: (sleepNights || []).map(n => ({ date: n.date, asleepMin: n.asleep_min, inBedMin: n.in_bed_min, sleepStartAt: n.sleep_start_at })),
      dailyActivity: dailyActivity.map(d => ({ date: d.date, steps: d.steps, distanceM: d.distance_m })),
      workouts: workouts.map(w => ({ type: w.type, date: w.date, distanceM: w.distance_m, durationMin: w.duration_min, note: w.notes })),
      screenTimeDays: screenTimeDays,
      debts: debts.map(d => ({ name: d.name, balance: d.balance, rate: d.apr, monthlyPayment: d.monthly_payment })),
      bills: annotateBills(bills, _zone),
      goalLines: _goalLines,
      findings,
      commitments,
      moodPatternsEnabled: notifPrefs?.moodPatternsEnabled !== false,
      finance: { income: effectiveIncome || monthlyIncome || 0 },
    }));

    return {
      userName: username || "User",
      accountAgeDays: user?.created_at ? Math.max(0, Math.floor((Date.now() - new Date(user.created_at).getTime()) / 86400000)) : null,
      pillars: pillarPayload,
      finance: {
        connected:         _conn,
        importPending:     _importPending,
        availableBalance:  availBal    != null  ? Math.round(availBal)    : null,
        checking:          checkingBal != null  ? Math.round(checkingBal) : null,
        savings:           savingsBal  != null  ? Math.round(savingsBal)  : null,
        creditUsed:        plaidCreditBal > 0   ? Math.round(plaidCreditBal) : null,
        creditUtilization: creditUtil,
        moneyIn30d:        _conn && !_importPending && freshIncome   > 0 ? Math.round(freshIncome)                  : null,
        moneyOut30d:       _conn && !_importPending && freshExpenses > 0 ? Math.round(freshExpenses)                 : null,
        netSaved30d:       _conn && !_importPending && freshIncome   > 0 ? Math.round(freshIncome - freshExpenses)   : null,
        topSpendCategory:  _importPending ? null : topSpendCategory,
      },
      habits: { completedToday, missedToday },
      health: { upcomingCare, medsDueToday },
      mood: _ctx.mood,
      sleep: _ctx.sleep,
      activity: _ctx.activity,
      screenTime: _ctx.screenTime,
      supplements: _ctx.supplements,
      recovery: _ctx.recovery,
      progress: _ctx.progress,
      goals: _ctx.goals,
      debts: _ctx.debts,
      bills: _ctx.bills,
      findings: _ctx.findings,
      commitments: _ctx.commitments,
      mentalHealth: _ctx.mentalHealth,
      journal: _ctx.journal,
      focus: _ctx.focus,
      lastBriefing,
    };
  };

  const DEMO_BRIEFING_JSON = {
    greeting: `Good afternoon, Alex`,
    focus: [
      "Log your gym session today. Your 8 day streak is on the line.",
      "Make a credit card payment to drop utilization below 30%.",
      "Schedule your Adderall XR pickup. The refill is due in 18 days.",
    ],
    momentum: "Water habit at 12 days straight and mood averaged 7.8 this week.",
    watchOut: "Credit utilization at 36%. A payment today keeps your score clean.",
    oneThing: "Log your workout. Your data shows mood runs 1.4 points higher on gym days.",
  };

  // Week planner: build the pre-fill once data is in (deterministic, no AI).
  useEffect(() => {
    if (!plannerPending || !dataLoaded) return;
    setPlannerPending(false);
    const hasGym = habits.some(h => h.id === "gym" && h.active !== false);
    const countGoal = hasGym ? null : goals.find(g => !g.completed && isWorkoutCountGoal(g));
    setPlanError("");
    setWeekPlan(buildWeekPlan({
      today: localDateStr(),
      habits: habits.map(h => { const t = tmpl(h.id); return { id: h.id, label: t?.label || h.label || h.id, target: t?.target ?? h.target, history: h.completions || [], weekCount: h.weekCount, active: h.active }; }),
      workouts,
      workoutTarget: countGoal ? Number(countGoal.period_target ?? countGoal.target_value) : null,
      zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerPending, dataLoaded]);
  const toggleplanDay = (key, day) => setWeekPlan(p => p && ({ ...p, items: p.items.map(it => it.key !== key ? it : { ...it, days: it.days.includes(day) ? it.days.filter(d => d !== day) : [...it.days, day].sort() }) }));
  const setPlanTime = (key, time) => setWeekPlan(p => p && ({ ...p, items: p.items.map(it => it.key !== key ? it : { ...it, time: time || null }) }));
  // Save: one commitment per planned day (source 'plan'), skipping a day that
  // already has the same open plan. Gated on the insert landing.
  const saveWeekPlan = async () => {
    if (!weekPlan || planSaving) return;
    const rows = planToCommitments(weekPlan, { userId: user?.id, existing: commitments });
    if (!rows.length) { setWeekPlan(null); return; }
    if (isDemo || !user) { setCommitments(cs => [...cs, ...rows.map((r, i) => ({ ...r, id: `plan_${Date.now()}_${i}`, created_at: new Date().toISOString() }))]); setWeekPlan(null); return; }
    setPlanSaving(true); setPlanError("");
    try {
      const { error } = await supabase.from("coach_commitments").insert(rows);
      if (error) { reportError(new Error(`week plan insert failed — ${error.message}`), { op: "db.week_plan", code: error.code }); setPlanError("Couldn't save your plan. Please try again."); return; }
      await reloadCommitments();
      setWeekPlan(null);
      showToast(`Planned ${rows.length} for the week. They're in Up next.`, "success");
    } finally { setPlanSaving(false); }
  };
  const briefingInFlightRef = useRef(false);
  const generateBriefing = async () => {
    if (isDemoMode) {
      setBriefingJson(DEMO_BRIEFING_JSON);
      setBriefingDate(new Date().toISOString());
      return;
    }
    if (!requirePro("briefing")) return;
    if (!aiConsent) { setShowAiConsentModal(true); return; }
    // Never a brief from partial data (pillarReadiness): wait for the inputs.
    if (pillarsPending) { showToast("Still loading your data. Try again in a moment.", "info"); return; }
    // One generation at a time: a double tap on Refresh ("Updated since this
    // brief") lands before the loading render and would call the model twice.
    if (briefingInFlightRef.current) return;
    briefingInFlightRef.current = true;
    setBriefingLoading(true);
    setBriefingError(false);
    try {
      // Fetch previous briefing for continuity
      let lastBriefing = null;
      if (user) {
        const { data: prev } = await supabase
          .from("ai_briefings")
          .select("briefing_json")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (prev?.briefing_json) lastBriefing = prev.briefing_json;
      }
      // Sleep is the one briefing domain the client holds no state for; fetch the
      // recent nights here so the shared shaper can include it (Phase 2 parity).
      let sleepNights = [];
      if (user) {
        const { data: sn } = await supabase
          .from("sleep_nights")
          .select("date, asleep_min, in_bed_min, sleep_start_at, sleep_end_at")
          .eq("user_id", user.id)
          .order("date", { ascending: false })
          .limit(14);
        if (Array.isArray(sn)) sleepNights = sn;
      }
      // Phase 3 correlation findings (ledger:* keys), surface-gated in the shaper.
      let findings = [];
      if (user) {
        const { data: fr } = await supabase
          .from("pattern_insights")
          .select("pattern_key, direction, strength, involves_mood, last_detected_at, snapshot")
          .eq("user_id", user.id)
          .like("pattern_key", "ledger:%");
        if (Array.isArray(fr)) findings = fr;
      }
      // Phase 4 commitments: open to follow up + recently resolved to acknowledge.
      let commitments = [];
      if (user) {
        const { data: cr } = await supabase
          .from("coach_commitments")
          .select("text, status, source, due_date, evidence, created_at, resolved_at")
          .eq("user_id", user.id)
          .in("status", ["open", "done"])
          .order("updated_at", { ascending: false })
          .limit(25);
        if (Array.isArray(cr)) commitments = cr;
      }
      // Habits done today, read fresh (see buildUserContext doneTodayById). Same
      // derivation as the loader: last_completed or history carries today's
      // local date.
      let doneTodayById = null;
      if (user) {
        const { data: hr, error: hrErr } = await supabase.from("habits").select("name, last_completed, history").eq("user_id", user.id);
        if (!hrErr && Array.isArray(hr)) {
          const _today = localDateStr();
          doneTodayById = new Map(hr.map(r => {
            let hist = r.history;
            if (typeof hist === "string") { try { hist = JSON.parse(hist); } catch { hist = []; } }
            const inHist = Array.isArray(hist) && hist.some(d => String(d).slice(0, 10) === _today);
            return [r.name, String(r.last_completed || "").slice(0, 10) === _today || inHist];
          }));
        }
      }
      const userCtx = buildUserContext(lastBriefing, sleepNights, findings, commitments, doneTodayById);
      const res = await apiFetch(apiUrl("/api/claude"), {
        timeoutMs: 90000, // model generation: slow first byte by design
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          max_tokens: 700,
          system: `You are the Scorify AI coach, a holistic life coach and personal organizer who has read the user's whole file. You receive a userContext JSON with the sections below. Use ONLY what is present: a section with available false, or an empty list, means that data is not reaching the app right now, which is NOT a zero and must never be reported as one.
- pillars: the four pillar scores (each a percentage of only the trackers the user opted into, so a pillar may read "not tracked", which is not a zero).
- finance: balances, 30-day money in, money out, net saved, credit used and utilization, top spending category.
- habits: completedToday and missedToday arrays, each habit with its streak, an isNew flag and, when the user chose to share it, noteToday in their own words (draw on it for context, never quote it back at length).
- health: upcomingCare appointments and medsDueToday.
- mood: recentAvg over the last 7 days, trend versus the prior week, last7Scores, loggedToday, newestDate, and a stale flag with an asOf note when the latest entry is three or more days old.
- sleep: status is "measured", "schedule-only", or "none"; when measured it carries avgLabel and typicalBedtime. Never describe schedule-only time-in-bed as sleep.
- activity: steps7d, miles7d, and workouts7d, all over the TRAILING 7 days, plus workoutList (type, date, miles, minutes and the user's own note on a workout, which you may draw on but never quote at length), not "this week": a this-week workout count comes only from the workouts goal in goals, and without one say "in the last 7 days"; carries an asOf note when the last Health sync is stale. Steps are a count and miles are distance; never fuse the two.
- screenTime: a stepped floor shown with an "at least" label; never quote it as an exact total.
- supplements, recovery (days clean per item), and progress (xp, level, login streak, badges).
- goals: live-resolved progress lines; spending caps are limits where lower is better.
- debts: itemized balances with a known total, DTI, and high-interest flags.
- bills: upcoming bills, each with dueWeekday + dueDate (the ABSOLUTE local due day, already computed), daysUntil, amount, an autopay flag, and paidThisMonth. When you mention a bill's timing, use dueWeekday/dueDate exactly (e.g. "due Saturday"); never recompute "today"/"tomorrow" from daysUntil yourself.
- findings: correlation tendencies from the user's own recent weeks (findings.items, each with evidence, direction, and confidence). Present only when something cleared the bar.
- commitments: things the user committed to. commitments.open are still open (each with dueDate and overdue); commitments.justResolved were just completed (with evidence).
- mentalHealth: PHQ-2 and GAD-2 totals when a recent check-in exists.
- journal: personal reflections, present only when the user chose to share them; draw on them for context, never quote long passages back.
- focus: the user's stated focus from signup.

Your job is to synthesize across the domains you can see, not react to a single metric. Look for patterns that span multiple pillars. Connect things: "your mood is below your weekly average and you have not logged your gym habit today" or "you have a dentist appointment in 4 days and have not logged your medication today." Reference upcoming appointments by name and date when they are within 14 days.

Respond ONLY with a valid JSON object — no markdown fences, no explanation, just raw JSON:

{
  "greeting": "<short personalized greeting using their first name and one real observation>",
  "focus": ["<specific action item 1>", "<specific action item 2>", "<specific action item 3 if warranted>"],
  "momentum": "<one sentence on a genuine positive pattern you see across their data>",
  "watchOut": "<one sentence on the most important risk or gap right now — cross-domain if possible — or null>",
  "oneThing": "<the single most impactful action they can take in the next 24 hours, specific and concrete>"
}

RULES:
1. Use ONLY values present in userContext. Never invent numbers or assume data not present.
2. Omit any field topic if its data is null or missing — do not mention it.
3. Reference habits by name (from completedToday / missedToday arrays).
4. Reference upcoming appointments by name and date if within 14 days.
5. Reference medications by name if they are in medsDueToday.
6. Tone: direct, warm, and specific — like a coach who has read your whole file. No generic cheerleading, no filler phrases like "great job" or "keep it up."
7. Each field is 1-2 sentences maximum.
8. NEVER say the user is debt-free if finance.creditUsed is greater than 0.
9. For focus array: prioritize the weakest pillars but pull in health appointments and missed habits regardless of pillar score.
10. House copy rule: never use em dashes, en dashes, or double hyphens in any field value. Use a comma, period, or colon instead; hyphens only inside compound words.
11. NEW ACCOUNT / NEW ITEMS. If accountAgeDays is 3 or less, this is a welcome briefing: name what they have set up, say what you will watch for, and give one easy first step. Never say "missed", "behind", "at risk", or "haven't logged" about anything. Independently of account age, any habit with isNew true was created in the last two days: invite its first log, never count it as missed.
12. If finance.importPending is true, the bank was connected within the last day and transactions are still importing: never state money in, money out, net saved, a savings rate, or a top spending category. If you mention the bank at all, say the transaction picture is still filling in. Balances are current and fine to use.
13. If mood.stale is true, the most recent mood is at least three days old (its date is mood.newestDate): do not present the average as today's mood, and if you reference mood at all, note it is from the last entry on that date. The same holds for activity.stale: say the step and distance figures are as of the last sync.
14. Never report a zero or missing value as a fact about the user. No steps, no sleep, no screen time, no workouts means the data did not reach the app, not that the user did nothing. Say the data is not currently reaching the app if it is relevant.
15. Sleep: only sleep.status "measured" is real sleep. Never narrate schedule-only time-in-bed as sleep, and never claim to know when or how long they slept when status is not "measured".
16. Screen time is a stepped floor: say "at least" or "about", never an exact figure.
17. Medications and recovery are adherence and streaks only. Acknowledge logging a med or a day clean as a win; never assess a medication itself (dosage, timing, side effects, whether it is working). If mentalHealth shows phqTotal 3 or more or gadTotal 3 or more, lead with warmth and gently suggest professional support; do not open with productivity coaching.
18. Bills: a bill with autopay true, or paidThisMonth true, needs no action. Never tell the user to go pay a bill that pays itself or is already paid. A bill due soon that is NOT on autopay and NOT yet paid is worth flagging, especially alongside tight cashflow.
19. Connecting the dots: findings.items are CONFIRMED tendencies with evidence — when present, you may weave at most ONE in with its numbers (these fields open behind the app lock). When findings is empty you may still connect the dots as an OBSERVATION from the specific facts in the payload ("your two highest mood days were both workout days"), never as a proven pattern or a cause. Follow the tiers exactly: ${PATTERN_HONESTY_RULE}
20. Commitments: acknowledge a commitments.justResolved item once, warmly and briefly. You may gently note a commitments.open item, especially if overdue, but never nag and never scold a missed one. Do not invent commitments that are not in the data.`,
          messages: [{ role: "user", content: JSON.stringify(userCtx) }],
        }),
      });
      // Must come before the parse below. On a non-2xx, d.content is undefined,
      // so `|| "{}"` produced valid JSON, JSON.parse succeeded, and
      // setBriefingJson({}) rendered an EMPTY briefing card — the fallback
      // briefing sitting in the catch never ran, because nothing ever threw.
      // Throwing here routes a failed request to that fallback, which is what it
      // was written for.
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = apiErrorMessage(res, body);
        console.error("briefing request failed", res.status, body?.error || "");
        // Surface the limiter's own wording when it sent one; otherwise stay
        // quiet — the catch below restores the cached briefing or shows the
        // needs-a-connection card.
        if (msg) showToast(msg);
        throw new Error(`briefing request failed: ${res.status}`);
      }
      const d = await res.json();
      const raw = d.content?.map(b => b.text || "").join("") || "{}";
      const cleaned = raw.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "").trim();
      let json = JSON.parse(cleaned);
      // Same check as the check-in audit (habitClaimIssues): a field saying a
      // habit logged today was missed today is dropped (no regeneration here).
      {
        const _items = habits.filter(h => h.active !== false).map(h => ({ name: h.id, label: tmpl(h.id)?.label || null, loggedToday: doneTodayById?.has(h.id) ? doneTodayById.get(h.id) : h.lastLoggedDate === localDateStr() }));
        // Also a walk/run the data cannot back (activityClaims): steps are all-day movement.
        const _actCtx = { workouts, today: localDateStr() };
        const _bad = (v) => typeof v === "string" && (missedTodayHits(v, _items).length > 0 || activityClaimHits(v, _actCtx).length > 0);
        const next = { ...json };
        for (const [k, v] of Object.entries(next)) {
          if (k === "greeting") continue;
          if (Array.isArray(v)) next[k] = v.filter(x => !_bad(x));
          else if (_bad(v)) next[k] = null;
        }
        json = next;
      }
      const now = new Date().toISOString();
      setBriefingJson(json);
      setBriefingDate(now);
      setBriefingFeedback(null);
      // Persist to Supabase
      let dbId = null;
      if (user) {
        // Error no longer discarded (audit W11). No toast on purpose — the
        // briefing itself rendered fine and a toast about its rating row would
        // read as a briefing failure; the visible consequence is the feedback
        // control not rendering (it is hidden when there is no row to attach a
        // rating to, rather than shown dead), and Sentry carries the cause.
        const { data: saved, error: briefErr } = await supabase
          .from("ai_briefings")
          .insert({ user_id: user.id, briefing_json: json, created_at: now })
          .select("id")
          .single();
        if (briefErr || !saved?.id) {
          console.error("ai_briefings insert failed", briefErr);
          reportError(new Error(`ai_briefings insert failed — ${briefErr?.message || "no row returned"}`), { op: "db.briefing", code: briefErr?.code });
        } else { dbId = saved.id; setBriefingDbId(saved.id); }
      }
      // Cache locally for 24h
      if (BRIEFING_CACHE_KEY) {
        try { localStorage.setItem(BRIEFING_CACHE_KEY, JSON.stringify({ json, date: now, dbId, feedback: null })); } catch {}
      }
    } catch (e) {
      // No fabricated fallback (deleted 2026-09-12). The old catch rendered an
      // INVENTED briefing ("You showed up today") in the real briefing card —
      // fake Kai output whether the cause was no signal or a server 500, and a
      // direct violation of the honest-data principle. Failure now restores
      // the cached briefing when one is under 24h (real content, honestly
      // stamped with its original age by the "Xh ago" label), and otherwise
      // shows the needs-a-connection card with a retry.
      console.error("Briefing generation error:", e);
      let restored = false;
      if (BRIEFING_CACHE_KEY) {
        try {
          const saved = localStorage.getItem(BRIEFING_CACHE_KEY);
          if (saved) {
            const { json, date, dbId, feedback } = JSON.parse(saved);
            if (Date.now() - new Date(date).getTime() < 24 * 3600 * 1000) {
              setBriefingJson(json);
              setBriefingDate(date);
              setBriefingDbId(dbId || null);
              setBriefingFeedback(feedback || null);
              restored = true;
            }
          }
        } catch { /* unreadable cache — fall through to the error card */ }
      }
      if (!restored) setBriefingError(true);
    }
    briefingInFlightRef.current = false;
    setBriefingLoading(false);
  };

  const refreshBriefing = () => {
    // The cache is deliberately NOT cleared up front any more: it is the
    // fallback if regeneration fails, and a successful generate overwrites it.
    setBriefingJson(null); setBriefingDate(null); setBriefingDbId(null); setBriefingFeedback(null); setBriefingError(false);
    generateBriefing();
  };

  const saveBriefingFeedback = async (vote) => {
    setBriefingFeedback(vote);
    if (BRIEFING_CACHE_KEY) {
      try {
        const saved = localStorage.getItem(BRIEFING_CACHE_KEY);
        if (saved) { const p = JSON.parse(saved); localStorage.setItem(BRIEFING_CACHE_KEY, JSON.stringify({ ...p, feedback: vote })); }
      } catch {}
    }
    if (user && briefingDbId) {
      await persist("your feedback", supabase.from("ai_briefings").update({ feedback: vote }).eq("id", briefingDbId));
    }
  };



  // ── PLAID HELPERS ─────────────────────────────────────────────────────────
  const loadPlaidScript = () => new Promise((resolve, reject) => {
    if (window.Plaid) return resolve();
    const s = document.createElement("script");
    s.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });

  // Returns true ONLY when a bank was actually linked (token exchange landed).
  // Cancelling Plaid Link, a failed link-token call, or any error returns
  // false. The onboarding bank step keys "✓ Bank connected via Plaid" on this
  // return value — it used to ignore it (there was nothing to ignore; the
  // function returned undefined and swallowed its own "exited"), so a cancel
  // still claimed a connected bank on the done screen (map 5.13 / latch L4).
  const connectPlaid = async () => {
    // Demo: a clean pointer instead of a silently dead button.
    if (!user?.id) { if (isDemoMode) showToast("This is demo data. Create a free account to connect your own bank."); return false; }
    if (!requirePro("plaid")) return false;
    setPlaidLoading(true);
    let connected = false;
    try {
      const isNative = typeof window !== "undefined" && typeof window.Capacitor !== "undefined" && window.Capacitor.isNativePlatform();
      const res = await apiFetch(apiUrl("/api/plaid"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "create_link_token", userId: user.id }),
      });
      const { link_token, error } = await res.json();
      if (!link_token) { alert(error || "Failed to create link token"); setPlaidLoading(false); return false; }
      let publicToken, linkMeta = null;
      if (isNative) {
        const PlaidPlugin = window.Capacitor.Plugins.PlaidPlugin;
        const result = await PlaidPlugin.open({ token: link_token });
        publicToken = result.publicToken;
        linkMeta = result.metadata || null;
      } else {
        await loadPlaidScript();
        ({ publicToken, linkMeta } = await new Promise((resolve, reject) => {
          const handler = window.Plaid.create({
            token: link_token,
            onSuccess: (token, metadata) => resolve({ publicToken: token, linkMeta: metadata || null }),
            onExit: () => reject(new Error("exited")),
          });
          handler.open();
        }));
      }
      // The bank's name from Link's metadata (multi-bank, 2026-10-13). Web gives
      // institution.{name, institution_id}; the native SDK gives
      // institution.{name, id}. Stored on the connection row so the Accounts
      // section can group by bank; a missing name is backfilled server-side.
      const inst = linkMeta?.institution || null;
      const institution = inst ? { institution_id: inst.institution_id || inst.id || null, name: inst.name || null } : null;
      setPlaidFetching(true);
      const exchRes = await apiFetch(apiUrl("/api/plaid"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "exchange_token", userId: user.id, publicToken, institution }),
      });
      if (exchRes.ok) {
        connected = true;
        const exch = await exchRes.json().catch(() => ({}));
        if (Array.isArray(exch.items)) setPlaidItems(exch.items);
        // New connection: get_balance needed because Plaid may not have transactions ready yet
        const balRes = await apiFetch(apiUrl("/api/plaid"), {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "get_balance", userId: user.id, reason: "new-connection" }),
        });
        if (balRes.ok) {
          const balData = await balRes.json();
          const accts = balData.accounts || [];
          if (Array.isArray(balData.items)) { setPlaidItems(balData.items); setPlaidReauth(balData.items.some(i => i.needs_reauth)); }
          setPlaidAccounts(accts);        plaidAccountsRef.current = accts;
          setPlaidConnected(true);        plaidConnectedRef.current = true;
          // The cache_balances write that stood here was removed 2026-10-14:
          // get_balance already runs the sync that fills the cache, and this
          // call posted transactions: [] on top of it (a wipe of every bank's
          // history). The server now ignores empty fields too.
        }
        const txRes = await apiFetch(apiUrl("/api/plaid"), {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "get_transactions", userId: user.id }),
        });
        if (txRes.ok) {
          const txData = await txRes.json();
          const freshTx = txData.transactions || [];
          setPlaidTransactions(freshTx);  plaidTransactionsRef.current = freshTx;
          if (Array.isArray(txData.items)) setPlaidItems(txData.items);
          // get_transactions now caches server-side, including accounts
        }
      } else {
        // The exchange was refused AFTER the bank authorized Link. 409 is the
        // 3-bank cap (a clean message, not an error); anything else is shown
        // with the server's wording so the user knows the link did not land.
        const body = await exchRes.json().catch(() => ({}));
        if (exchRes.status === 409) showToast(body.message || "You can connect up to 3 banks. Disconnect one to add another.");
        else alert("Failed to connect bank: " + (body.message || body.error || `server returned ${exchRes.status}`));
      }
    } catch(e) {
      if (e.message !== "exited") alert("Failed to connect bank: " + e.message);
    } finally {
      setPlaidFetching(false);
      setPlaidLoading(false);
    }
    return connected;
  };

  const disconnectPlaid = async () => {
    // Demo: a clean pointer instead of a silently dead button.
    if (isDemoMode || !user?.id) { showToast("This is demo data. Create a free account to connect your own bank."); return; }
    setPlaidLoading(true);
    setDisconnectError("");
    try {
      // apiFetch is a thin wrapper over fetch, and fetch does NOT reject on a
      // non-2xx response — it only rejects on network failure, CORS, or abort.
      // So the previous `catch {}` produced two different wrong outcomes:
      //
      //   server 500  -> fetch RESOLVES, catch never runs, the three state
      //                  clears below execute anyway, and the UI reports the
      //                  bank as disconnected while plaid_connections still
      //                  holds the row. This is the dangerous one: the user is
      //                  told their financial link is severed when it is not.
      //   offline     -> fetch rejects, catch swallows it, state is not cleared
      //                  and nothing is said. A silent no-op.
      //
      // res.ok is therefore the check that matters; the catch only covers the
      // transport failure.
      const res = await apiFetch(apiUrl("/api/plaid"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "disconnect", userId: user.id }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        console.error("plaid disconnect rejected", res.status, detail);
        setDisconnectError("Couldn't disconnect your bank. Your account is still linked — please try again.");
        return;
      }
      setPlaidConnected(false);
      setPlaidAccounts([]);
      setPlaidTransactions([]);
      setPlaidItems([]);
      setPlaidReauth(false);
    } catch (e) {
      console.error("plaid disconnect failed", e);
      setDisconnectError("Couldn't reach the server. Your bank is still linked — please try again.");
    } finally {
      setPlaidLoading(false);
      setPlaidFetching(false);
    }
  };

  // ── PER-BANK DISCONNECT / RECONNECT (multi-bank, 2026-10-13) ─────────────
  // The server removes ONE Item and returns the pruned cache + the remaining
  // banks in the same response, so local state is replaced from the server's
  // answer rather than guessed (silent-success rule: nothing updates unless
  // the write landed). The all-banks disconnectPlaid above stays for
  // "Disconnect all".
  const applyBankState = (data) => {
    const items = Array.isArray(data?.items) ? data.items : [];
    setPlaidItems(items);
    setPlaidReauth(items.some(i => i.needs_reauth));
    if (Array.isArray(data?.accounts)) { setPlaidAccounts(data.accounts); plaidAccountsRef.current = data.accounts; }
    if (Array.isArray(data?.transactions)) { setPlaidTransactions(data.transactions); plaidTransactionsRef.current = data.transactions; }
    if (items.length === 0) {
      setPlaidConnected(false); plaidConnectedRef.current = false;
      setPlaidAccounts([]); plaidAccountsRef.current = [];
      setPlaidTransactions([]); plaidTransactionsRef.current = [];
    }
  };
  const disconnectPlaidItem = async (item, label, { confirm = true } = {}) => {
    if (isDemoMode || !user?.id) { showToast("This is demo data. Create a free account to connect your own bank."); return false; }
    if (!item?.item_id) return false;
    if (confirm && !window.confirm(`Disconnect ${label}? Its accounts and transactions will be removed from Scorify. ${plaidItems.length > 1 ? "Your other banks stay connected." : ""}`.trim())) return false;
    setPlaidLoading(true);
    setDisconnectError("");
    try {
      const res = await apiFetch(apiUrl("/api/plaid"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "disconnect", userId: user.id, itemId: item.item_id }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        console.error("plaid disconnect(item) rejected", res.status, detail);
        setDisconnectError(`Couldn't disconnect ${label}. It is still linked, please try again.`);
        return false;
      }
      applyBankState(await res.json().catch(() => ({})));
      return true;
    } catch (e) {
      console.error("plaid disconnect(item) failed", e);
      setDisconnectError(`Couldn't reach the server. ${label} is still linked, please try again.`);
      return false;
    } finally {
      setPlaidLoading(false);
    }
  };
  // ── PLAID UPDATE MODE (2026-10-13) ─────────────────────────────────────────
  // Link opens on the EXISTING Item (the server builds the token from that
  // Item's access_token; the client never sees it). On success there is no
  // public token to exchange and no new connection row: update_complete clears
  // the Item's flags, drops deselected accounts, and syncs that Item only, so
  // a reconnect keeps the bank's history and "Add accounts" flows new
  // accounts into the same bank card.
  //   mode 'add_accounts' -> account selection; 'reconnect' -> re-auth only.
  const openPlaidLinkToken = async (link_token) => {
    const isNative = typeof window !== "undefined" && typeof window.Capacitor !== "undefined" && window.Capacitor.isNativePlatform();
    if (isNative) {
      const result = await window.Capacitor.Plugins.PlaidPlugin.open({ token: link_token });
      return { publicToken: result?.publicToken || null, linkMeta: result?.metadata || null };
    }
    await loadPlaidScript();
    return new Promise((resolve, reject) => {
      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: (token, metadata) => resolve({ publicToken: token || null, linkMeta: metadata || null }),
        onExit: () => reject(new Error("exited")),
      });
      handler.open();
    });
  };
  const updatePlaidItem = async (item, label, mode) => {
    if (isDemoMode || !user?.id) { showToast("This is demo data. Create a free account to connect your own bank."); return false; }
    if (!item?.item_id) return false;
    setPlaidLoading(true);
    setDisconnectError("");
    let done = false;
    try {
      const tokRes = await apiFetch(apiUrl("/api/plaid"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "create_link_token", userId: user.id, itemId: item.item_id, mode }),
      });
      const tok = await tokRes.json().catch(() => ({}));
      if (!tokRes.ok || !tok.link_token) { showToast(tok.message || tok.error || `Couldn't open ${label} for updating. Please try again.`); return false; }
      const { linkMeta } = await openPlaidLinkToken(tok.link_token);
      // The accounts the user left selected (account selection only); the
      // server drops any the user deselected, with their transactions.
      const selectedAccountIds = mode === "add_accounts" && Array.isArray(linkMeta?.accounts)
        ? linkMeta.accounts.map(a => a?.id || a?.account_id).filter(Boolean) : null;
      setPlaidFetching(true);
      const res = await apiFetch(apiUrl("/api/plaid"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_complete", userId: user.id, itemId: item.item_id, mode, selectedAccountIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showToast(data.message || `${label} was updated at the bank, but the refresh did not finish. Pull to refresh in a moment.`); return false; }
      applyBankState(data);
      if (data.balances_updated_at !== undefined) { setPlaidBalancesUpdatedAt(data.balances_updated_at); plaidBalancesUpdatedAtRef.current = data.balances_updated_at; }
      setPlaidError(false);
      done = true;
      showToast(mode === "add_accounts" ? `${label} accounts updated.` : `${label} reconnected.`, "success");
    } catch (e) {
      if (e?.message !== "exited") { console.error("plaid update mode failed", e); showToast(`Couldn't update ${label}: ${e?.message || "unknown error"}`); }
    } finally {
      setPlaidFetching(false);
      setPlaidLoading(false);
    }
    return done;
  };
  // Reconnect ONE expired bank IN PLACE: update mode keeps the Item, its
  // account ids and its transaction history (the old disconnect-and-relink
  // issued new ids and dropped the history).
  const reconnectPlaidItem = (item, label) => updatePlaidItem(item, label, "reconnect");
  const addPlaidAccounts = (item, label) => updatePlaidItem(item, label, "add_accounts");

  // ── COACH HELPERS ─────────────────────────────────────────────────────────
  const buildCoachUserData = () => {
    const todayStr = localDateStr();
    // TODAY'S CHECK-IN (1B): the prose body Kai sent this morning, so a reply
    // from the card continues that conversation. Body only (never journal).
    const todayCheckin = (briefingJson && typeof briefingJson.body === "string" && briefingJson.body.trim())
      ? { date: briefingDate || null, headline: briefingJson.headline || null, body: briefingJson.body } : null;
    // Sensitive-names consent (item 4b): strip medication + merchant names when off.
    const _shareNames = notifPrefs?.shareSensitiveNames !== false;
    const importPending = plaidImportPendingCheck(plaidConnected, plaidConnectedAt, plaidTransactions);
    const ageDaysOf = (iso) => iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null;
    return {
      todayCheckin,
      profile: { username, goal_type: bodyStats?.goal_type, age_range: (()=>{ const r = ageRangeOf(ageFromBirthYear(birthYear)); return r === "unknown" ? null : r; })(), coachName,
                 // Stated focus baseline from onboarding — stable facts Kai
                 // leads with. Only ever sent inside consent-gated calls.
                 focusPillar, focusNote,
                 // Days since signup: Kai's new-account rule keys on this so a
                 // ten-minute-old account is welcomed, never audited.
                 accountAgeDays: ageDaysOf(user?.created_at),
                 pillars: Object.fromEntries(pillars.map(p => [p.id, p.pct])) },
      habits: habits.map(h => { const t = tmpl(h.id); return { id: h.id, label: t?.label || h.id, streak: h.streak, weekCount: h.weekCount, target: t?.target || 5, loggedToday: h.lastLoggedDate === todayStr, noteToday: journalCoachEnabled ? habitNoteFor(h.notes, todayStr) : null, ageDays: ageDaysOf(h.createdAt) }; }),
      // goals deliberately NOT sent: coach.js fetches and resolves them
      // server-side through api/lib/goal-progress.js (the cron's resolver),
      // so Kai sees live progress for bound goals instead of the stale
      // current_value column this payload used to carry. Same convention as
      // the healed habit/supplement columns — the stale value is not sent,
      // so it cannot be read by accident.
      // importPending: the bank was linked hours ago and Plaid's initial
      // history may be partial. Transaction-DERIVED figures are withheld
      // (manual entries pass through; the user stated those themselves), and
      // the flag tells coach.js to say the picture is still filling in.
      finances: { importPending, incomeType: incomeType.type, incomeIrregular: irregularSummary ? { mtd: irregularSummary.mtd, typical: irregularSummary.typical, tracking: irregularSummary.tracking } : null, monthlyIncome: importPending ? (monthlyIncome || null) : (effectiveIncome || monthlyIncome), monthlyExpenses: importPending ? (monthlyExpenses || null) : (effectiveExpenses || monthlyExpenses), cashflow: importPending ? ((monthlyIncome || 0) - (monthlyExpenses || 0)) : ((effectiveIncome || monthlyIncome || 0) - (effectiveExpenses || monthlyExpenses || 0)), savings, cushionBalance: Math.round(cushionBalance(financeData)), cushionGrew: (typeof prevSavingsBalance === "number" && cushionBalance(financeData) > prevSavingsBalance), debts: debts.map(d => ({ name: d.name, balance: d.balance, rate: d.apr, monthly_payment: d.monthly_payment })), bankAccounts: plaidConnected ? (() => {
        // Kai must not quote a stale balance — send name/type with balance null
        // so it knows the account exists but has no vouchable number, rather than
        // reasoning on a stale figure or losing the account. One shared window
        // (src/lib/freshness.js) with the briefing and the cron.
        const balFresh = balanceFreshAt && (Date.now() - new Date(balanceFreshAt).getTime()) < BALANCE_FRESH_WINDOW_MS;
        return plaidAccounts.map(a => ({ name: a.name, type: a.type, bank: a.institution_name || null, balance: balFresh ? a.balances?.current : null }));
      })() : null, recentSpending: plaidConnected && !importPending && plaidTransactions.length > 0 ? (() => { const cut30 = daysAgoStr(30); const cats = {}; plaidTransactions.filter(t => txSince(t, cut30) && isSpendTx(t)).forEach(t => { const c = spendCategoryLabel(t); cats[c] = (cats[c]||0) + t.amount; }); return Object.entries(cats).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([c,v])=>({ category:c, total:Math.round(v) })); })() : null },
      // Transfers and loan payments stay VISIBLE here but labeled: this is
      // Kai's view of recent activity, and "user moved $500 to savings" is
      // real context — the harm was Kai reading a card payment as spending,
      // not Kai seeing it. Sums elsewhere still exclude via isSpendTx.
      recentTransactions: plaidConnected && plaidTransactions.length > 0
        ? plaidTransactions
            .filter(t => t.amount > 0)
            .slice(0, 10)
            .map(t => ({ name: _shareNames ? t.name : spendCategoryLabel(t), amount: Math.round(t.amount), date: txLocalDay(t), when: txDayWord(t, localDateStr()), pending: !!t.pending, category: spendCategoryLabel(t), kind: isSpendTx(t) ? "spending" : "transfer_or_payment_not_spending" }))
        : null,
      supplements: supplements.map(s => ({ name: s.name, streak: s.streak, takenToday: s.takenToday })),
      // Phase 2 coverage: medications (adherence only), recovery streaks, and
      // XP/level — shaped server-side by api/lib/chat-context.js. Meds carry only
      // name/schedule/last-taken; the chat prompt forbids clinical assessment.
      medications: enrolledSet.has("medications") ? medications.map(m => ({ name: _shareNames ? m.name : "a medication", schedule: m.schedule, lastTakenDate: m.last_taken_date })) : [],
      recovery: recoveryTrackers.map(r => ({ label: r.label, startDate: r.start_date })),
      progress: progress ? { xp: progress.xp, level: progress.level, dailyStreak: progress.daily_streak, longestStreak: progress.longest_streak, badges: progress.badges } : null,
      // Mood NOTES are journal-grade free text and ride the SAME consent as
      // journal entries: the privacy policy promises free-text is shared only
      // when the user chooses, and this field was bypassing the toggle —
      // scores went out with notes attached in every coach payload. With the
      // toggle off, only the numeric score and date ship. (The check-in flow
      // sending a note to /api/claude is different — that is the user asking,
      // per message, and stays.)
      moods: moodHistory.filter(m => m.score !== null).slice(-14).map(m => ({ score: m.score, date: m.date, note: journalCoachEnabled ? (m.note || null) : null })),
      bodyStats: bodyStats?.current_weight ? { current_weight: bodyStats.current_weight, goal_weight: bodyStats.goal_weight, goal_type: bodyStats.goal_type } : null,
      weightLog: weightLog.slice(-7),
      scoreHistory,
      mentalHealth: checkInResults ? { phqTotal: checkInResults.phqTotal, gadTotal: checkInResults.gadTotal, phqRisk: checkInResults.phqRisk, gadRisk: checkInResults.gadRisk } : null,
      journalEntries: journalCoachEnabled && journalEntries.length > 0
        ? journalEntries.slice(0, 5).map(e => ({ date: e.date, text: e.text.slice(0, 200), mood_score: e.mood_score }))
        : null,
    };
  };

  // ── MEM0 MEMORY HELPERS ───────────────────────────────────────────────────
  const retrieveMemories = async (query) => {
    if (!user?.id) return [];
    try {
      const res = await apiFetch(apiUrl("/api/memory"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "retrieve", userId: user.id, query }),
      });
      const data = await res.json();
      return Array.isArray(data.memories) ? data.memories : [];
    } catch { return []; }
  };

  const saveMemories = async (messages) => {
    if (!user?.id) return;
    try {
      await apiFetch(apiUrl("/api/memory"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "save", userId: user.id, messages }),
      });
    } catch {}
  };

  const BACKGROUND_MODES = new Set(["morning_briefing","weekly_review","crisis_check","open_greeting"]);
  const fetchCoachMessage = async (mode, userMsg = "", history = [], cacheKey = null, memories = []) => {
    // Demo never reaches /api/coach, whatever the local consent flag says:
    // the consent modal is browsable in demo and could flip aiConsent on,
    // after which openCoach's greeting fetch would fire a real, 401-doomed
    // request. Every demo coach surface renders canned content instead.
    if (isDemoMode) return null;
    // Soft paywall: a tap on Ask Coach opens the paywall; a background fetch
    // (greeting, briefing modes) is nobody's tap and stays silent.
    if (!requirePro("coach", { silent: BACKGROUND_MODES.has(mode) })) return null;
    if (!aiConsent) { if (!BACKGROUND_MODES.has(mode)) setShowAiConsentModal(true); return null; }
    // Ref-based lock prevents concurrent calls (state-based coachLoading is async/batched)
    if (coachInFlight.current) return null;
    coachInFlight.current = true;
    setCoachLoading(true);
    let content = null;
    try {
      const res = await apiFetch(apiUrl("/api/coach"), {
        timeoutMs: 90000, // model generation + tool loop: slow first byte by design
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userData: buildCoachUserData(), mode, userMessage: userMsg, conversationHistory: history, memories, userId: user?.id }),
      });
      // apiFetch is a thin fetch wrapper and fetch does not reject on a non-2xx,
      // so this check is what catches a failed call — the catch below only ever
      // sees transport failures. Without it, a 429/503/500 fell through
      // `if (data.content)` with no else and the user got nothing at all: the
      // spinner simply stopped. That silence is also what hid every rate-limit
      // response, since those bodies carry `message` and no `content`.
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // apiErrorMessage covers 429/413/503 and returns null for anything else,
        // so a 500 falls through to a generic line rather than leaking the raw
        // upstream error string into the transcript.
        const msg = apiErrorMessage(res, body)
          || "Your coach isn't available right now. Please try again in a moment.";
        console.error("coach request failed", res.status, "mode:", mode, body?.error || "");
        // Background modes run unprompted, so an error bubble in the transcript
        // would be noise for something the user never asked for. They still log.
        // 402: the paywall opened (apiFetch event); a bubble under it is noise.
        if (!BACKGROUND_MODES.has(mode) && res.status !== 402) {
          setCoachMsgs(prev => [...prev, { text: msg, isCoach: true, isError: true }]);
        }
      } else {
        const data = await res.json();
        if (data.content) {
          setCoachMsgs(prev => [...prev, { text: data.content, isCoach: true, role: "assistant" }]);
          if (cacheKey) { try { localStorage.setItem(cacheKey, data.content); } catch {} }
          // /api/coach sets commitmentsChanged when record_commitment inserted or
          // refined a row this turn: refetch so Up next shows the plan at once.
          if (data.commitmentsChanged) reloadCommitments();
          // Unprompted arrivals must be findable: crisis_check and
          // weekly_review land silently in the transcript (no push, no
          // toast), so pulse the FAB the way onboarding's pending greeting
          // does. openCoach clears it. Not for morning_briefing, which only
          // fires on the coach tab itself, nor open_greeting (drawer already
          // open).
          if (mode === "crisis_check" || mode === "weekly_review") setCoachPulse(true);
          content = data.content;
        } else {
          // 200 with no content — shouldn't happen, but must not be silent.
          console.error("coach returned 200 with no content, mode:", mode);
          if (!BACKGROUND_MODES.has(mode)) {
            setCoachMsgs(prev => [...prev, { text: "Your coach didn't send a reply. Please try again.", isCoach: true, isError: true }]);
          }
        }
      }
    } catch (e) {
      console.error("coach request threw —", e?.message);
      setCoachMsgs(prev => [...prev, { text: "Having trouble connecting. Check your connection and try again.", isCoach: true, isError: true }]);
    }
    coachInFlight.current = false;
    setCoachLoading(false);
    return content;
  };

  // Demo has no user and no consent, so a send would silently null out in
  // fetchCoachMessage (background gate) or 401 — either way a spinner with
  // nothing behind it. Answer with a labeled sample instead.
  const DEMO_COACH_REPLY = "This is a sample of the coach. Create an account and connect your data, and I'll answer using your real habits, spending and health trends.";
  const sendCoachMsg = async () => {
    const msg = coachInput.trim();
    if (!msg || coachLoading) return;
    // Soft paywall: before the message lands in the thread, so a gated tap
    // opens the paywall with the draft still in the box.
    if (!requirePro("coach")) return;
    setCoachInput("");
    setCoachMsgs(prev => [...prev, { text: msg, isCoach: false, role: "user" }]);
    if (isDemoMode) { setCoachMsgs(prev => [...prev, { text: DEMO_COACH_REPLY, isCoach: true, role: "assistant" }]); return; }
    const history = coachMsgs.filter(m => m.role).map(m => ({ role: m.role, content: m.text }));
    const memories = await retrieveMemories(msg);
    const response = await fetchCoachMessage("chat", msg, history, null, memories);
    if (response) saveMemories([{ role: "user", content: msg }, { role: "assistant", content: response }]);
  };

  // Shared handler for tab prompt chips and drawer suggestion chips
  const sendCoachPrompt = async (prompt) => {
    if (coachLoading) return;
    if (!requirePro("coach")) return;
    setCoachMsgs(prev => [...prev, { text: prompt, isCoach: false, role: "user" }]);
    if (isDemoMode) { setCoachMsgs(prev => [...prev, { text: DEMO_COACH_REPLY, isCoach: true, role: "assistant" }]); return; }
    const history = coachMsgs.filter(m => m.role).map(m => ({ role: m.role, content: m.text }));
    const memories = await retrieveMemories(prompt);
    const response = await fetchCoachMessage("chat", prompt, history, null, memories);
    if (response) saveMemories([{ role: "user", content: prompt }, { role: "assistant", content: response }]);
  };

  // loadCoachInsight was deleted here 2026-09-07: it made a real /api/coach
  // daily_insight call (one paid Anthropic request per user per day, cached in
  // localStorage) whose result was stored in state that NO JSX ever rendered.
  // The briefing card replaced the insight card at some point and the fetch
  // outlived the UI. If a daily-insight surface returns, the server mode
  // (api/coach.js "daily_insight") still exists — rebuild the client side
  // against it rather than resurrecting the old state wiring.

  // Open coach drawer
  // Coach sheet closed: refetch commitments once. Backstop for the
  // commitmentsChanged flag (older API, or a change the reply did not flag).
  const coachWasOpen = useRef(false);
  useEffect(() => {
    if (coachOpen) { coachWasOpen.current = true; return; }
    if (coachWasOpen.current) { coachWasOpen.current = false; reloadCommitments(); }
  }, [coachOpen, reloadCommitments]);
  // App resume: a reminder tap or a plan made on another device lands here.
  // Native appStateChange, not visibilitychange (unreliable in WKWebView, see
  // the Screen Time resume comment).
  useEffect(() => {
    if (isDemo || !user?.id) return;
    let handle;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => { if (isActive) reloadCommitments(); })
      .then((h) => { handle = h; }).catch(() => {});
    return () => { try { handle?.remove(); } catch {} };
  }, [isDemo, user?.id, reloadCommitments]);

  const openCoach = async () => {
    setCoachOpen(true);
    setCoachPulse(false);
    if (coachMsgs.length === 0) {
      const history = [];
      await fetchCoachMessage("open_greeting", "", history);
    }
    setTimeout(() => coachInputRef.current?.focus(), 350);
  };

  // Scroll coach to bottom
  useEffect(() => { coachBottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [coachMsgs, coachLoading]);
  useEffect(() => {
    if (coachMsgs.length === 0 || !user?.id) return;
    try {
      localStorage.setItem(userKey(user.id, 'coach_msgs'), JSON.stringify(coachMsgs.slice(-20)));
      localStorage.setItem(userKey(user.id, 'coach_msgs_savedAt'), Date.now().toString());
    } catch {}
  }, [coachMsgs]);

  // Crisis detection — check for 3+ consecutive low mood days
  useEffect(() => {
    if (!user || isDemoMode) return;
    const validMoods = moodHistory.filter(m => m.score !== null && m.score !== undefined);
    if (validMoods.length < 3) return;
    const last3 = validMoods.slice(-3);
    const allLow = last3.every(m => m.score <= 4);
    if (allLow) {
      const cacheKey = userKey(user.id, "crisis_" + localDateStr());
      if (!localStorage.getItem(cacheKey)) {
        // Non-empty user message required: "" fell through coach.js's mode
        // ternary into messages[0].content and Anthropic 400'd the whole call
        // (SCORIFY-APP-1). The server now owns the real prompt per mode; this
        // text is a belt-and-braces floor for old-server/new-client skew.
        fetchCoachMessage("crisis_check", "Check in with me.", [], cacheKey);
        localStorage.setItem(cacheKey, "shown");
      }
    }
  }, [moodHistory, user]);

  // Weekly review — trigger on Sundays
  useEffect(() => {
    if (!user || isDemoMode) return;
    const today = new Date();
    if (today.getDay() !== 0) return; // Only Sunday
    const cacheKey = userKey(user.id, "weekly_" + localDateStr(today));
    if (cacheKey && !localStorage.getItem(cacheKey) && tab === "overview") {
      // Same SCORIFY-APP-1 rule as crisis_check above: never send "" as the
      // user message. This call fired every Sunday and 400'd upstream since
      // at least 2026-09-06 — no user ever saw a weekly review from it.
      fetchCoachMessage("weekly_review", "Give me my weekly review of the past week.", [], cacheKey);
      localStorage.setItem(cacheKey, "shown");
    }
  }, [tab, user]);

  const coachSuggestions = (() => {
    const todayStr = localDateStr();
    const suggestions = [];
    const topStreak = [...habits].sort((a, b) => b.streak - a.streak)[0];
    if (topStreak && topStreak.streak >= 3) {
      const t = tmpl(topStreak.id);
      suggestions.push(`How do I protect my ${t?.label || topStreak.id} streak?`);
    }
    const atRisk = habits.find(h => h.streak > 2 && h.lastLoggedDate !== todayStr);
    if (atRisk) {
      const t = tmpl(atRisk.id);
      suggestions.push(`Help me keep my ${t?.label || atRisk.id} streak alive`);
    }
    const cashflow = (effectiveIncome || monthlyIncome || 0) - (effectiveExpenses || monthlyExpenses || 0);
    if (cashflow < 0) suggestions.push("My expenses exceed income — where do I start cutting?");
    else if (savings > 0) suggestions.push(`I have $${savings.toLocaleString()} saved — best move right now?`);
    const activeGoal = goals.find(g => !g.completed);
    if (activeGoal) suggestions.push(`Am I on track with my ${activeGoal.title} goal?`);
    const defaults = ["What pattern are you seeing?", "Where should I focus today?", "How's my progress overall?", "What's hurting my score?"];
    defaults.forEach(d => { if (suggestions.length < 4) suggestions.push(d); });
    return suggestions.slice(0, 4);
  })();

  const coachTabPrompts = (() => {
    const todayStr = localDateStr();
    const topHabit = [...habits].sort((a, b) => b.streak - a.streak)[0];
    const topLabel = topHabit ? (tmpl(topHabit.id)?.label || topHabit.id) : null;
    const atRisk = habits.find(h => h.streak > 0 && h.lastLoggedDate !== todayStr);
    const atRiskLabel = atRisk ? (tmpl(atRisk.id)?.label || atRisk.id) : null;
    const cashflow = (effectiveIncome || monthlyIncome || 0) - (effectiveExpenses || monthlyExpenses || 0);
    const topDebt = [...debts].sort((a, b) => (b.balance || 0) - (a.balance || 0))[0];
    const weightGap = bodyStats?.current_weight && bodyStats?.goal_weight ? Math.abs(bodyStats.current_weight - bodyStats.goal_weight) : null;
    return [
      { label: "Patterns", prompts: [
        "What's the strongest pattern in my data?",
        topLabel && topHabit.streak >= 5 ? `My ${topLabel} streak is ${topHabit.streak} days. What does that say about me?` : "When am I most on track?",
        "What's secretly hurting my progress?",
      ]},
      { label: "Focus", prompts: [
        "What's the #1 thing to fix this week?",
        atRiskLabel ? `Help me keep my ${atRiskLabel} streak going today` : "Which habit has the biggest ripple effect?",
        "Where am I close to a breakthrough?",
      ]},
      { label: "Money", prompts: [
        cashflow < 0 ? `I'm spending $${Math.abs(cashflow).toLocaleString()} more than I earn — where do I cut first?` : `How can I make the most of my $${cashflow.toLocaleString()}/mo cashflow?`,
        topDebt ? `Best strategy for my ${topDebt.name} debt ($${(topDebt.balance || 0).toLocaleString()})` : "Am I on track with my financial goals?",
        "How does my mood affect my spending?",
      ]},
      { label: "Health", prompts: [
        weightGap !== null ? `I'm ${weightGap} lbs from my goal — realistic timeline?` : "What health habit should I add next?",
        supplements.length > 0 ? `Are my supplements actually making a difference?` : "How is my sleep affecting everything?",
        "Am I recovering well?",
      ]},
    ];
  })();
  const [coachTabCategory, setCoachTabCategory] = useState(0);
  // ── ONBOARDING STATE ──
  const onboardingDone = useRef(false);
  const obScrollRef = useRef(null);
  const obOverlayRef = useRef(null);
  // Template ids already inserted (or currently inserting) for this user. `habits`
  // is React state and is stale until the next render, so it cannot guard against
  // two inserts fired in the same tick. This ref is written before the await.
  const habitIdsRef = useRef(new Set());
  // Same problem, same fix, for goals. This ref claims a title before the await
  // so a fast double-tap cannot get two inserts past the check.
  //
  // Holds goalKey(title) — lower(trim()) — for ACTIVE goals only, matching the
  // partial index. A completed goal releases its title: finishing "Run 5 miles a
  // week" in August must not stop the same goal being set again in September,
  // which is exactly the pattern recurring goals follow.
  const goalTitlesRef = useRef(new Set());
  // Claims "week:quality" synchronously so the score writer runs at most twice
  // per week (once early, once when Plaid settles) instead of once per render —
  // and never lets a worse snapshot replace a settled one. See savePillarScores.
  const savedWeekRef = useRef(null);

  // Keep the claim in step when a goal's completed flag flips either way.
  const syncGoalClaim = (title, completed) => {
    const key = goalKey(title);
    if (!key) return;
    if (completed) goalTitlesRef.current.delete(key);
    else goalTitlesRef.current.add(key);
  };
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  // Entering the onboarding lock step: drop any keyboard left up by a text
  // field from an earlier step (WKWebView never dismisses it when a focused
  // input unmounts). LockSetup blurs on its own mount too; this also covers
  // the already-enabled branch, which renders no LockSetup.
  useEffect(() => {
    if (OB_STEPS[onboardingStep] !== "lock") return;
    try { const el = document.activeElement; if (el && el !== document.body && el.blur) el.blur(); } catch {}
  }, [onboardingStep]);

  // ── ONBOARDING STEP RESUME ──────────────────────────────────────────────
  // The flow opens ONCE per mounted session; loadData re-runs (token refresh
  // on resume, reconnect reloads) must never reset a step the user is
  // standing on. The current step also persists per user, so a genuine
  // webview remount (OAuth bank handoff, memory jettison) resumes exactly
  // where the user was instead of starting over.
  const obFlowOpened = useRef(false);
  const openOnboardingFlow = () => {
    setShowOnboarding(true);
    if (obFlowOpened.current) return; // already open this session — keep the user's step
    obFlowOpened.current = true;
    let saved = NaN;
    try { saved = parseInt(localStorage.getItem(userKey(user?.id, 'ob_step')) || '', 10); } catch {}
    setOnboardingStep(Number.isFinite(saved) ? Math.min(Math.max(saved, 0), OB_STEPS.length - 1) : 0);
  };
  useEffect(() => {
    if (!user?.id || !showOnboarding) return;
    try { localStorage.setItem(userKey(user.id, 'ob_step'), String(onboardingStep)); } catch {}
  }, [onboardingStep, showOnboarding, user?.id]);
  const clearOnboardingResume = () => {
    try { if (user?.id) localStorage.removeItem(userKey(user.id, 'ob_step')); } catch {}
  };
  const [obName, setObName] = useState("");
  // goal_type now defaults neutral and is chosen via explicit chips on the
  // body step (the old welcome-grid overloaded it with 8 values, 5 of which
  // the Your Goal card could not even render).
  const [obGoalType, setObGoalType] = useState("general_fitness");
  const [obBirthYear, setObBirthYear] = useState("");
  const [obWeight, setObWeight] = useState("");
  const [obGoalWeight, setObGoalWeight] = useState("");
  // "What are you here to work on?" — key into FOCUS_PRESETS, or null (skippable).
  const [obFocus, setObFocus] = useState(null);
  const [obFocusNote, setObFocusNote] = useState("");
  const [obHabits, setObHabits] = useState([]);
  const [obGoalTitle, setObGoalTitle] = useState("");
  const [obGoalCategory, setObGoalCategory] = useState("fitness");
  const [obGoalTarget, setObGoalTarget] = useState("");
  const [obGoalUnit, setObGoalUnit] = useState("");
  // Custom-goal shape (see GOAL_SHAPES). Only applies when no template chip is
  // matched at persist time — chips carry their own shape via bindings.
  const [obGoalShape, setObGoalShape] = useState("target");
  const [obGoalPeriod, setObGoalPeriod] = useState("week");
  const [obGoalDeadline, setObGoalDeadline] = useState("");
  // Optional head-start for money templates (moneyStart) — mirrors the Add
  // Goal confirm screen's "Already saved or paid something?" line.
  const [obGoalCurrent, setObGoalCurrent] = useState("");
  // Template-first flow: null = gallery of chips + "write your own"; a chip
  // object = its one-number confirm. Mirrors the Add Goal redesign's
  // gallery/confirm split (goalTemplates.js, commit 0305776c).
  const [obGoalTpl, setObGoalTpl] = useState(null);
  const [obIncome, setObIncome] = useState("");
  const [obExpenses, setObExpenses] = useState("");
  const [obSavings, setObSavings] = useState("");
  const [obPlaidDone, setObPlaidDone] = useState(false);
  const [obSkippedPlaid, setObSkippedPlaid] = useState(false);
  // Onboarding check-in hour: no preselected default (null). Only persisted when
  // the user actually picks and taps Set; Skip leaves checkin_hour null so
  // nothing is sent until they choose (from the Home prompt or Notifications).
  const [obCheckinHour, setObCheckinHour] = useState(null);
  // Onboarding ending: persistence happens ONCE when leaving the bank step
  // (ref-guarded — every underlying write already dedupes via its own claim),
  // so the consent step and Kai's greeting have real saved data behind them.
  const obPersisted = useRef(false);
  const obGreetingReq = useRef(false);
  const [obGreeting, setObGreeting] = useState(null);      // Kai's first message text
  const [obGreetingState, setObGreetingState] = useState("idle"); // idle|loading|done|failed
  // Persisted focus baseline (mirrors profiles.focus_pillar / focus_note) —
  // loaded in loadData, written at persistOnboarding, sent in every coach payload.
  const [focusPillar, setFocusPillar] = useState(null);
  const [focusNote, setFocusNote] = useState(null);

  // Kai's first message, on the onboarding done step. Effect rather than the
  // grant button handler because setAiConsent has not flushed when the button's
  // own closure runs — fetchCoachMessage would read stale consent and silently
  // return null (its background gate). The effect sees fresh state. Declined
  // Dismiss the iOS keyboard on every onboarding step change: after typing a
  // name it stayed up over the focus step (which has no text input at all),
  // with the form-accessory bar floating over the sheet. Steps that DO need
  // the keyboard still work — the sheet lays out above it via kbInset and its
  // content scrolls (see the kbInset effect below).
  useEffect(() => {
    if (!showOnboarding) return;
    const el = document.activeElement;
    if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) el.blur();
  }, [onboardingStep, showOnboarding]);

  // The missing half of the keyboard fix. kbInset correctly shrinks the sheet
  // above the keyboard (visualViewport fires fine and the inset is on the
  // right element — the overlay), but WKWebView only auto-scrolls the WINDOW
  // to reveal a focused field, which the inset effect deliberately undoes to
  // keep the fixed sheet in place, and it never scrolls an inner overflow
  // container. So a field low in the sheet (the focus step's note, the goal
  // step's custom fields) stayed below the fold, reading as "covered by the
  // keyboard". Scroll the sheet's own container: once when the field focuses,
  // and again when the inset lands (the keyboard finishes animating after
  // focusin fires, shrinking the container a second time).
  const obFocusedField = useRef(null);

  useEffect(() => {
    if (!showOnboarding) return;
    const onFocusIn = (e) => {
      const el = e.target;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
        obFocusedField.current = el;
        setTimeout(() => {
          try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch {}
        }, 350);
      }
    };
    const onFocusOut = () => { obFocusedField.current = null; };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => { document.removeEventListener("focusin", onFocusIn); document.removeEventListener("focusout", onFocusOut); };
  }, [showOnboarding]);

  // consent never enters here, so the focus note cannot reach Anthropic.
  useEffect(() => {
    if (!showOnboarding || !aiConsent) return;
    if (OB_STEPS[onboardingStep] !== "done") return;
    if (obGreetingReq.current) return;
    obGreetingReq.current = true;
    setObGreetingState("loading");
    // The done screen never spins with nothing behind it: the static summary
    // renders regardless, this only decides whether Kai's message tops it.
    // 30s, not 12: a cold Vercel lambda plus Claude generation regularly runs
    // past 12s, and the first device pass showed exactly that failure — the
    // timeout hid the loading line, the user left, and the greeting landed
    // silently in the coach drawer. The summary is already on screen, so a
    // longer wait costs nothing; a LATE arrival still renders (the display
    // keys on obGreeting, not on this state).
    const timer = setTimeout(() => {
      setObGreetingState(s => (s === "loading" ? "failed" : s));
      reportError(new Error("onboarding greeting timed out"), { op: "onboarding.greeting", stage: "timeout" });
    }, 30000);
    (async () => {
      const content = await fetchCoachMessage("open_greeting");
      clearTimeout(timer);
      if (content) { setObGreeting(content); setObGreetingState("done"); }
      else {
        setObGreetingState("failed");
        // Which branch nulled: consent gate, in-flight lock, non-ok, or empty
        // 200 — fetchCoachMessage logged the specifics; this ties it to the
        // onboarding surface so the next device pass names the culprit.
        reportError(new Error("onboarding greeting returned null"), { op: "onboarding.greeting", stage: "null" });
      }
    })();
    return () => clearTimeout(timer);
  }, [showOnboarding, aiConsent, onboardingStep]);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── [UIDIAG] RESUME UI HEALTH CHECK — diagnostics only, no behavior ────────
  // Intermittent device bug (2026-10-09): after ~11h in the background the app
  // sometimes reopens half-laid-out (blank areas, content pushed to the
  // bottom, odd nav pill) until force quit, with no Sentry event. ~3s after a
  // resume that followed >30 min in the background, read the DOM and report
  // ONCE per session if it looks degraded. Reads only; never changes state.
  //
  // Two resume shapes, both covered:
  //   warm  — same JS context; appStateChange / visibilitychange fire.
  //   fresh — iOS killed the WebView process while backgrounded; Capacitor
  //           reloads the page (webViewWebContentProcessDidTerminate →
  //           webView.reload()), so NO resume event fires in the new context.
  //           The background timestamp is therefore persisted (localStorage,
  //           a bare timestamp, no user data) and read on mount. This is the
  //           likelier shape after many hours and the one the in-memory
  //           version of this check would have missed entirely.
  const uiDiagStateRef = useRef({});
  uiDiagStateRef.current = { tab, contentTab, isMobile, financeChartsReady, dataLoaded };
  useEffect(() => {
    if (isDemo) return;
    const MIN_BG_MS = 30 * 60 * 1000;
    const BG_KEY = "scorify_diag_bg_at";
    const NAV_IDS = ["overview", "habits", "wellness", "finances", "health"];
    let bgAt = null;
    let timer = null;
    const readBg = () => { try { const v = parseInt(localStorage.getItem(BG_KEY) || "", 10); return Number.isFinite(v) ? v : null; } catch { return null; } };
    const writeBg = (v) => { try { v == null ? localStorage.removeItem(BG_KEY) : localStorage.setItem(BG_KEY, String(v)); } catch {} };
    const navType = () => { try { return performance.getEntriesByType("navigation")[0]?.type || "unknown"; } catch { return "unknown"; } };

    const runCheck = (bgMs, source, attempt = 0) => {
      try {
        const st = uiDiagStateRef.current;
        // Fresh context: data is still loading for the first seconds; checking
        // a skeleton would be a false alarm. Wait for dataLoaded (max ~20s).
        if (source === "fresh_context" && !st.dataLoaded && attempt < 8) {
          timer = setTimeout(() => runCheck(bgMs, source, attempt + 1), 2500);
          return;
        }
        const w = window.innerWidth, h = window.innerHeight;

        // (a) NAV. Two views of each pill: React's (data-nav-state) and the
        // painted one (computed background). Degraded when the active count
        // is not exactly one, the active pill is not `tab`, or paint and
        // state disagree (two states sharing a background, or one state
        // painted two ways). Unvisited-today pills are listed, not flagged —
        // that soft tint is by design and resets at local midnight, which an
        // 11h background usually crosses (the likely "Health highlighted").
        let nav = "n/a", navActive = "none", unvisited = "none";
        const btns = Array.from(document.querySelectorAll("[data-nav-tab]"));
        if (!NAV_IDS.includes(st.tab)) nav = "offnav"; // profile/coach live in the avatar menu
        else if (btns.length) {
          const byState = {};
          btns.forEach(b => {
            const stt = b.getAttribute("data-nav-state") || "?";
            (byState[stt] = byState[stt] || []).push({ id: b.getAttribute("data-nav-tab"), bg: window.getComputedStyle(b).backgroundColor });
          });
          const act = byState.active || [];
          navActive = act.map(x => x.id).join(",") || "none";
          unvisited = (byState.unvisited || []).map(x => x.id).join(",") || "none";
          const bgsOf = (k) => new Set((byState[k] || []).map(x => x.bg));
          const paintMismatch = Object.keys(byState).some(k => bgsOf(k).size > 1)
            || [...bgsOf("active")].some(bg => bgsOf("idle").has(bg) || bgsOf("unvisited").has(bg));
          if (act.length !== 1) nav = `active_${act.length}`;
          else if (act[0].id !== st.tab) nav = "mismatch";
          else if (paintMismatch) nav = "paint_mismatch";
          else nav = "ok";
        }

        // (b) CONTENT. Container height, where its first painted descendant
        // starts, and what is actually under three points down the screen's
        // centre line. A probe that lands on a container (page, scroller,
        // body, ambient layer) rather than a card means that part of the
        // screen is blank — the "large blank areas" symptom directly.
        let content = "ok", firstTop = null, pgH = null, blank = 0;
        const pg = document.querySelector('[data-diag="tab-content"]');
        const sc = scrollRef.current;
        if (!pg) content = "missing";
        else {
          const r = pg.getBoundingClientRect();
          pgH = Math.round(r.height);
          let first = null;
          const walker = document.createTreeWalker(pg, NodeFilter.SHOW_ELEMENT);
          for (let n = walker.nextNode(); n; n = walker.nextNode()) {
            const rr = n.getBoundingClientRect();
            if (rr.height > 0 && rr.width > 0) { first = n; break; }
          }
          if (first) firstTop = Math.round(first.getBoundingClientRect().top);
          const containers = new Set([pg, sc, document.body, document.documentElement, pg.parentElement].filter(Boolean));
          [0.3, 0.5, 0.7].forEach(f => {
            const el = document.elementFromPoint(Math.round(w / 2), Math.round(h * f));
            if (!el || containers.has(el) || el.getAttribute("aria-hidden") === "true") blank++;
          });
          if (r.height < 40 || !first) content = "near_zero_height";
          else if (firstTop > h * 0.6) content = "first_child_low";
          else if (blank >= 2) content = `blank_probes_${blank}`;
        }

        // (c) REFRESH — the resume Plaid sync, the only data refresh a warm
        // resume runs. not_triggered = connected, data past its 15-min gate,
        // yet no sync fired on this resume. Offline is its own state, not a
        // failure.
        const rr = resumeRefreshRef.current;
        let refresh = rr.status;
        if (rr.status === "started") refresh = "pending";
        // A refresh still in flight is not a failure yet: the Plaid cache
        // read alone averages several seconds. Re-check up to three times
        // (15 s more) before reporting it as "pending".
        if (refresh === "pending" && attempt < 11) {
          timer = setTimeout(() => runCheck(bgMs, source, Math.max(attempt + 1, 8)), 5000);
          return;
        }
        else if (rr.status === "none" || rr.at < Date.now() - bgMs - 5000) {
          const lastMs = plaidBalancesUpdatedAtRef.current ? new Date(plaidBalancesUpdatedAtRef.current).getTime() : 0;
          if (!plaidConnectedRef.current) refresh = "not_connected";
          else if (typeof navigator !== "undefined" && navigator.onLine === false) refresh = "offline";
          else if (lastMs && Date.now() - lastMs <= 15 * 60 * 1000) refresh = "not_needed";
          else refresh = "not_triggered";
        }

        const tabLag = st.tab !== st.contentTab;
        const navBad = nav !== "ok" && nav !== "n/a" && nav !== "offnav";
        const uiDegraded = navBad || content !== "ok" || tabLag;
        const refreshBad = refresh === "pending" || refresh === "not_triggered" || refresh.startsWith("failed");
        const vv = window.visualViewport;
        const se = document.scrollingElement;
        addBreadcrumb("resume", "ui-health check", { uiDegraded, refreshBad, nav, content, refresh, tabLag, source });
        if (!uiDegraded && !refreshBad) return;
        // UI degradation and a failed refresh are different problems: separate
        // fingerprints and separate once-per-session latches, so a transient
        // Plaid 5xx after a long background cannot use up the UI report.
        const which = uiDegraded ? "ui" : "refresh";
        if (uiDiagReported[which]) return;
        uiDiagReported[which] = true;
        reportMessage(uiDegraded ? "ui-health: degraded after resume" : "ui-health: refresh did not complete after resume", {
          level: "warning",
          fingerprint: [uiDegraded ? "ui-health-degraded-after-resume" : "ui-health-refresh-after-resume"],
          op: "ui.resume_health",
          check_nav: nav,
          check_content: content,
          check_refresh: refresh,
          check_tab_lag: tabLag ? "yes" : "no",
          backgrounded_min: Math.round(bgMs / 60000),
          resume_source: source,
          nav_type: navType(),
          since_boot_s: Math.round(performance.now() / 1000),
          active_tab: st.tab,
          content_tab: st.contentTab,
          nav_active: navActive,
          nav_unvisited: unvisited,
          is_mobile: st.isMobile ? "yes" : "no",
          data_loaded: st.dataLoaded ? "yes" : "no",
          finance_charts_ready: st.financeChartsReady ? "yes" : "no",
          viewport: `${w}x${h}`,
          visual_viewport: vv ? `${Math.round(vv.width)}x${Math.round(vv.height)}@${Math.round(vv.offsetTop)} s${vv.scale}` : "none",
          doc_scroll_top: se ? Math.round(se.scrollTop) : "none",
          app_scroll: sc ? `${Math.round(sc.scrollTop)}/${sc.clientHeight}/${sc.scrollHeight}` : "none",
          content_height: pgH,
          first_visible_top: firstTop,
          blank_probes: blank,
        });
      } catch { /* diagnostics must never break the app */ }
    };

    const onBackground = () => { if (bgAt == null) { bgAt = Date.now(); writeBg(bgAt); } };
    const onForeground = (source) => {
      if (bgAt == null) return; // the other signal already handled this resume
      const bgMs = Date.now() - bgAt;
      bgAt = null; writeBg(null);
      addBreadcrumb("resume", "resume start", { source, bgMin: Math.round(bgMs / 60000) });
      if (bgMs < MIN_BG_MS) return;
      clearTimeout(timer);
      timer = setTimeout(() => runCheck(bgMs, source), 3000);
    };
    // Fresh context (see above): a persisted background stamp with no matching
    // foreground means the page reloaded while backgrounded. Only within the
    // first minute of this context, so a stale stamp from a crash cannot fire
    // hours later. Always cleared.
    const stale = readBg();
    writeBg(null);
    if (stale != null && performance.now() < 60 * 1000) {
      const bgMs = Date.now() - stale;
      addBreadcrumb("resume", "resume start", { source: "fresh_context", bgMin: Math.round(bgMs / 60000), navType: navType() });
      if (bgMs >= MIN_BG_MS) timer = setTimeout(() => runCheck(bgMs, "fresh_context"), 3000);
    }
    // Both warm signals, because neither is reliable alone in WKWebView (see
    // the Screen Time resume comment): whichever arrives first wins.
    const onVis = () => { document.visibilityState === "visible" ? onForeground("visibility") : onBackground(); };
    document.addEventListener("visibilitychange", onVis);
    let handle;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => { isActive ? onForeground("appstate") : onBackground(); })
      .then((hd) => { handle = hd; }).catch(() => {});
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
      try { handle?.remove(); } catch {}
    };
  }, [isDemo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SUBSCRIPTION / PAYWALL ─────────────────────────────────────────────────
  // The server decides (api/lib/entitlement.js); this only mirrors its answer.
  // GET after auth and on every resume; POST after a purchase or restore so
  // the client never waits on the RevenueCat webhook. A failed fetch keeps the
  // previous answer (or none) and the next resume retries.
  const fetchSubscription = useCallback(async (method = "GET") => {
    if (!user?.id || isDemoMode) return null;
    try {
      const res = await apiFetch(apiUrl("/api/subscription"), { method });
      if (!res.ok) return null;
      const d = await res.json().catch(() => null);
      if (!d || typeof d.entitled !== "boolean") return null;
      setSubscription(d);
      return d;
    } catch { return null; }
  }, [user?.id, isDemoMode]);
  useEffect(() => {
    if (!user?.id || isDemoMode) return;
    fetchSubscription();
    // Both warm signals, as elsewhere in this file: neither is reliable alone in WKWebView.
    const onVis = () => { if (document.visibilityState === "visible") fetchSubscription(); };
    document.addEventListener("visibilitychange", onVis);
    let handle;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => { if (isActive) fetchSubscription(); })
      .then((h) => { handle = h; }).catch(() => {});
    // A 402 from coach.js / claude.js (dispatched by apiFetch): open the
    // paywall now, whatever placement rule would otherwise apply.
    const onRequired = () => { setPaywallForced(true); fetchSubscription(); };
    window.addEventListener("scorify:subscription-required", onRequired);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("scorify:subscription-required", onRequired);
      try { handle?.remove(); } catch {}
    };
  }, [user?.id, isDemoMode, fetchSubscription]);
  // New user: the paywall waits until the first scores have settled and been
  // on screen a few seconds after the onboarding done screen.
  useEffect(() => {
    if (!freshOnboarding || firstScoreSeen || !dataLoaded || pillarsPending) return;
    const t = setTimeout(() => setFirstScoreSeen(true), 4000);
    return () => clearTimeout(t);
  }, [freshOnboarding, firstScoreSeen, dataLoaded, pillarsPending]);
  const paywallMode = paywallPresentation({
    ready: paywallGated && profileLoaded && onboardingDone.current && !showOnboarding,
    requested: paywallForced, preview: paywallPreview, settled: !freshOnboarding || firstScoreSeen,
  });
  const paywallVisible = paywallMode === "full";
  const previewBannerVisible = paywallMode === "banner";
  // Not now: back to wherever the user was, in preview mode.
  const dismissPaywall = () => { setPaywallPreview(true); setPaywallForced(false); };
  // Both delete entry points (Profile, the Paywall link) start here: the
  // latest /api/subscription answer decides whether the cancel-first step
  // comes before the confirmations. A failed fetch falls back to the last
  // answer; no answer at all skips the step.
  const openDeleteAccount = async () => {
    const latest = (await fetchSubscription()) || subscription;
    if (needsCancelSubscriptionStep({ native, subscription: latest })) setShowCancelSubStep(true);
    else setShowDeleteConfirm1(true);
  };

  // Arriving from the auth screen, any zoom/pan left over from a focused field
  // survives the unmount — the webview state is not React state. Clear it once.
  useEffect(() => { resetViewport(); }, []);

  // Backstop for any field we missed: when the last open modal closes, put the
  // viewport back. Every text field in the app lives inside one of these, so a
  // stray focus-zoom cannot outlive the sheet that caused it.
  // Comprehensive: EVERY role="dialog" in the app is represented here. It gates
  // the viewport reset (below), and it hides the FAB + lets the sheet layer sit
  // above the bottom tab bar (see FAB render and C.overlay z-index). A modal
  // missing from this list would leave the FAB floating over it and the tab bar
  // painting over its footer — the device bug this list closes. Keep it in sync
  // when a modal is added.
  const anyModalOpen = showAdd || showAddBill || showAddCheckup || showAddDebt ||
    showAddGoal || showAddMed || showAddRecovery || showAddSupp || showAddWorkout ||
    showAiConsentModal || showEditCreditDetails || showEditFinances || showEditProfile ||
    showLogWeight || showOnboarding || showUpdateGoal ||
    showUpdateScore || coachOpen || !!editBill || !!editHabit ||
    checkInStep > 0 || !!logModal || !!deleteHabitId || showDeleteConfirm1 || showCancelSubStep || deleteDone ||
    showDeleteConfirm2 || (moneyUpgrade?.length > 0) || showAppLockModal || showCrisisResources ||
    showNotifSettings || paywallVisible;
  const prevModalOpen = useRef(false);
  useEffect(() => {
    if (prevModalOpen.current && !anyModalOpen) resetViewport();
    prevModalOpen.current = anyModalOpen;
  }, [anyModalOpen]);

  // SAFETY NET: the tab bar + FAB hide off DOM TRUTH (a [role=dialog] actually
  // on screen), not the anyModalOpen flag. A modal exit that forgets to clear
  // its state (device: the upgrade offer's Yes path left an empty-but-truthy
  // array) can then never strand the user without navigation — the observer
  // sees the dialog gone and restores the nav even if the flag is stuck.
  const [modalOnScreen, setModalOnScreen] = useState(false);
  const stuckFlagReported = useRef(false);
  useEffect(() => {
    let pending = 0;
    const check = () => { pending = 0; setModalOnScreen(!!document.querySelector('[role="dialog"]')); };
    const schedule = () => { if (!pending) pending = requestAnimationFrame(check); };
    check();
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (pending) cancelAnimationFrame(pending); };
  }, []);
  // Report a stuck flag: anyModalOpen true but nothing rendered, persisting past
  // a transient close. Once per session; the safety net above already keeps the
  // nav usable, this just surfaces the bug to fix.
  useEffect(() => {
    if (!anyModalOpen || modalOnScreen) return;
    const t = setTimeout(() => {
      if (anyModalOpen && !document.querySelector('[role="dialog"]') && !stuckFlagReported.current) {
        stuckFlagReported.current = true;
        try { reportError(new Error('anyModalOpen true but no dialog rendered — a modal exit left its flag set'), { op: 'sanity.stuck_modal_flag', level: 'warning' }); } catch {}
      }
    }, 800);
    return () => clearTimeout(t);
  }, [anyModalOpen, modalOnScreen]);

  // Scroll-bleed containment — ONE shared fix for every modal and sheet in the
  // app. Was per-screen (onboarding only); now a single document-level touch
  // handler covers every dialog by construction, because every modal/sheet
  // renders with role="dialog". No per-modal ref wiring: closest() finds the
  // enclosing dialog, and the pan is cancelled unless it lands inside a
  // scrollable element within that dialog. Two hard-won facts (onboarding
  // device pass 2026-09-19) still shape it:
  //   1. The page that scrolls behind a sheet is the C.app div, NOT #root —
  //      #root's only child is the 100vh C.app div, so a lock on #root did
  //      nothing. We contain at the touch layer instead of locking overflow.
  //   2. Freezing an ancestor's overflow breaks WebKit's native focused-field
  //      reveal with Keyboard.resize=none (keyboard covered the input the
  //      moment #root went overflow:hidden). So NO scroller's overflow is
  //      touched here while a field may focus.
  // Any pan a dialog's own scroller cannot consume is cancelled before iOS can
  // chain it to the page; the scrollers carry overscrollBehavior:contain
  // (C.mbox + the sheet scrollers) so hitting their ends cannot chain either.
  // React's synthetic touch/wheel handlers are passive, so this is attached
  // natively with passive:false. closest() gates the common no-modal case to a
  // single native call, so normal page scrolling pays almost nothing.
  useEffect(() => {
    // A pan is allowed if some element under the finger can consume it: a
    // scroller on EITHER axis (a horizontal chip row scrolls on X, a sheet
    // body on Y), so both overflows are checked.
    const canScroll = (el) => {
      if (!el || el.nodeType !== 1) return false;
      const s = window.getComputedStyle(el);
      if (el.scrollHeight > el.clientHeight && (s.overflowY === "auto" || s.overflowY === "scroll")) return true;
      if (el.scrollWidth  > el.clientWidth  && (s.overflowX === "auto" || s.overflowX === "scroll")) return true;
      return false;
    };
    const contain = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const dialog = t.closest('[role="dialog"]');
      if (!dialog) return;                       // not in a modal — leave page scroll alone
      // Controls that own their touch gesture (range sliders, and anything
      // opted in with data-drag) must never be cancelled or they can't be
      // dragged inside a dialog.
      if (t.closest('input[type="range"],[data-drag]')) return;
      let node = t;
      while (node && node !== dialog) {
        if (canScroll(node)) return;             // an inner scroller (x or y) will consume the pan
        node = node.parentElement;
      }
      if (canScroll(dialog)) return;             // the dialog itself is the scroller
      if (e.cancelable) e.preventDefault();      // nothing can consume it — block the bleed
    };
    document.addEventListener("touchmove", contain, { passive: false });
    document.addEventListener("wheel", contain, { passive: false });
    return () => {
      document.removeEventListener("touchmove", contain);
      document.removeEventListener("wheel", contain);
    };
  }, []);

  // ── SOFT KEYBOARD INSET ────────────────────────────────────────────────────
  // Capacitor Keyboard.resize is "none", so the webview keeps its full height and
  // the keyboard simply covers the bottom of the screen. visualViewport tells us
  // how much is covered; fixed-position sheets subtract it so they end above the
  // keyboard instead of behind it.
  const [kbInset, setKbInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let settle;
    // Reference height for the occlusion math. window.innerHeight is NOT
    // stable while the keyboard moves: the OBKB traces show WKWebView
    // transiently resizing the layout viewport to the keyboard-visible area
    // (innerHeight 932→519→932) on every keyboard show after the session's
    // first, despite Keyboard.resize=none. Sampling innerHeight live inside
    // that transient made covered read 0 with the keyboard up — the refocus
    // dip. So the reference is captured only while NO field is focused (the
    // keyboard provably down): with baseH pinned to the pre-keyboard height,
    // the transient sample computes the same correct inset as the settled
    // one, and the sheet is placed right on the first frame instead of
    // dip-then-heal. Real dismissals are unaffected: vv.height returns to
    // full height, so covered goes to 0 regardless of baseH.
    let baseH = window.innerHeight;
    const typing = () => {
      const el = document.activeElement;
      return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    };
    const measure = () => {
      // vv.height shrinks when the page is ZOOMED just as it does when the
      // keyboard covers it. Without the scale guard a stray focus-zoom pinned
      // kbInset at a nonzero value that never returned to 0, because the zoom
      // outlives the keyboard. Only trust the measurement at scale 1.
      const scale = vv.scale || 1;
      if (!typing()) baseH = window.innerHeight;
      const covered = scale > 1.01 ? 0 : baseH - vv.height - vv.offsetTop;
      setKbInset(covered > 80 ? Math.round(covered) : 0);
      // iOS scrolls the whole webview to reveal a focused input, which drags a
      // fixed sheet up behind the status bar. Put it back — the sheet handles
      // keeping the input visible by scrolling its own content.
      if (showOnboarding && vv.offsetTop > 0) window.scrollTo(0, 0);
    };
    const update = () => {
      measure();
      // Settle re-check: one more measurement after the burst of viewport
      // events stops. With baseH the event-time samples are already correct
      // (see above), so this is the fallback for the one remaining race the
      // OBKB traces show: an UNFOCUSED event can land mid-transient (one
      // fired 34ms before focusin, innerHeight already collapsed) and pin
      // baseH to the shrunken height. By settle time the layout viewport is
      // back to full height and no field-focus race is possible, so this
      // measurement is authoritative. The window-resize listener below
      // matters for the same reason — innerHeight growing back fires only a
      // window resize, never a visualViewport event.
      clearTimeout(settle);
      settle = setTimeout(measure, 250);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      clearTimeout(settle);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [showOnboarding]);
  // Second half of the focused-field reveal (see the focusin listener above):
  // re-scroll when the inset lands, because the keyboard finishes animating
  // after focusin fired and shrinks the sheet a second time.
  useEffect(() => {
    if (!showOnboarding || kbInset === 0) return;
    const el = obFocusedField.current;
    if (el) { try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch {} }
  }, [kbInset, showOnboarding]);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // ── FLOATING COACH BUTTON GEOMETRY ─────────────────────────────────────────
  // The button is position:fixed, so it sits over the scroll container and used to
  // cover the Habits "Log" buttons, the Health medication tracker, and the Wellness
  // mood card. The scroll container's bottom padding is derived from these values
  // (offset + size + a breathing gap) so content can always scroll clear of it.
  // Change the button's position here, not at the button.
  const FAB_SIZE = 52;
  // 118 clears the mobile bottom nav with the same ~22px gap. The nav trimmed in
  // two passes: fixed bottom padding 12→8 and button vertical padding 8→6 (nav
  // 108→100, FAB 130→122), then button padding 6→4 (nav 100→96, FAB 122→118).
  // Desktop (28) has no bottom nav, so it is unchanged. FAB_CLEARANCE is derived,
  // so the C.app scroll padding follows automatically.
  const FAB_OFFSET = isMobile ? 118 : 28;
  const FAB_GAP = 16;
  const FAB_CLEARANCE = FAB_OFFSET + FAB_SIZE + FAB_GAP;

  const C = {
    // Colors come from the THEMES object at the top of this file, via T.
    // The palette is documented ONCE, in CLAUDE.md → "Design tokens" — never
    // restate hex values in comments; a stale restated palette is exactly how
    // the docs and the shipped design drifted apart.
    app:{height:"100vh",overflowY:"auto",WebkitOverflowScrolling:"touch",background:T.bg,position:"relative",zIndex:0,color:T.text,fontFamily:"'DM Sans',sans-serif",paddingBottom:isMobile?`calc(${FAB_CLEARANCE}px + env(safe-area-inset-bottom, 0px))`:FAB_CLEARANCE},
    // Glass, not opaque cream (soft-glass pass): content scrolling under the
    // sticky header fades through the blur instead of clipping against a
    // hard cream edge. Matches the tab bar's chrome treatment; fixed chrome
    // over scrolling content, so the capped blurFixed, never blurCard. Falls
    // back to solid T.surface under prefers-reduced-transparency via G.
    hdr:{background:G.chromeSurface,borderBottom:G.hairline,backdropFilter:G.blurFixed,WebkitBackdropFilter:G.blurFixed,padding:isMobile?"0 16px":"0 28px",paddingTop:isMobile?"env(safe-area-inset-top, 0px)":0,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,minHeight:isMobile?52:58,position:"sticky",top:0,zIndex:50},
    logo:{fontSize:20,fontWeight:700,color:T.text,letterSpacing:"-0.02em",fontFamily:"'DM Serif Display',serif",textShadow:G.chromeTextShadow},
    nav:{display:isMobile?"none":"flex",gap:0,overflowX:"auto",flex:1,alignSelf:"stretch"},
    navB:(a)=>({background:a?withAlpha(T.accent,0.12):"transparent",color:a?T.accent:T.overlayLight(0.45),border:a?`1px solid ${withAlpha(T.accent,0.3)}`:"1px solid transparent",padding:"6px 12px",cursor:"pointer",fontSize:13,fontWeight:a?600:400,borderRadius:6,transition:"all .2s",whiteSpace:"nowrap",display:"flex",alignItems:"center",letterSpacing:"0.01em",margin:"0 2px"}),
    pg:{padding:isMobile?"16px 16px 0":"0 24px",maxWidth:1100,marginLeft:"auto",marginRight:"auto"},
    g:(cols="repeat(auto-fit,minmax(300px,1fr))")=>({display:"grid",gridTemplateColumns:isMobile?"1fr":cols,gap:isMobile?12:16}),
    card:{background:T.surface,border:`1px solid ${T.overlayLight(0.08)}`,borderRadius:16,padding:isMobile?20:28,boxShadow:"none"},
    cTitle:{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",color:T.overlayLight(0.45),marginBottom:14},
    row:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 0",borderBottom:`1px solid ${T.overlayLight(0.07)}`},
    btn:(col=T.accentFill)=>({background:col,color:"#fff",border:"none",borderRadius:10,padding:isMobile?"11px 20px":"9px 18px",cursor:"pointer",fontWeight:600,fontSize:13,minHeight:isMobile?44:undefined,letterSpacing:"0.01em",transition:"opacity 0.15s"}),
    ghost:{background:"transparent",border:`1px solid ${T.overlayLight(0.14)}`,color:T.textMuted,borderRadius:8,padding:"10px 16px",minHeight:isMobile?44:undefined,cursor:"pointer",fontSize:12,fontWeight:500},
    inp:{background:T.overlayLight(0.06),border:`1px solid ${T.overlayLight(0.12)}`,borderRadius:10,padding:"10px 14px",color:T.text,fontSize:16,outline:"none"},
    bub:(r)=>({alignSelf:r==="user"?"flex-end":"flex-start",background:r==="user"?T.accentFill:T.overlayLight(0.07),border:r==="user"?"none":`1px solid ${T.overlayLight(0.08)}`,borderRadius:r==="user"?"16px 16px 4px 16px":"16px 16px 16px 4px",padding:"10px 14px",maxWidth:"82%",fontSize:13,lineHeight:1.6,color:T.text,boxShadow:"none"}),
    overlay:{position:"fixed",inset:0,background:G.scrim,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100,backdropFilter:G.scrimBlur,WebkitBackdropFilter:G.scrimBlur},
    // maxHeight + overflow added when the Add Goal modal's shape picker made
    // it taller than a phone screen: a centered flex child with no height cap
    // clips at BOTH edges with no way to scroll (the Edit Profile modal had
    // already solved this inline with the same two properties). Applies to
    // every mbox modal; content shorter than 85vh renders exactly as before.
    mbox:{background:G.modalSurface,border:G.hairline,borderRadius:RADII.card,padding:28,paddingBottom:"calc(28px + env(safe-area-inset-bottom, 0px))",width:380,maxWidth:"92vw",maxHeight:"85vh",overflowY:"auto",WebkitOverflowScrolling:"touch",overscrollBehavior:"contain",boxShadow:`${G.shadow}, 0 24px 64px rgba(20,14,12,0.35)`,backdropFilter:G.blurCard,WebkitBackdropFilter:G.blurCard},
  };

  // Warm-glass card for REDESIGNED screens only (Phase 1: Home). Un-redesigned
  // tabs keep C.card; recipe lives in theme.js (G/RADII), never restated here.
  // In-scroll cards may carry the full blur — they move WITH the content
  // behind them; only FIXED chrome uses G.blurFixed (docs/REDESIGN.md §4).
  const GC = {
    card:{background:G.cardSurface,border:G.hairline,borderRadius:RADII.card,padding:isMobile?20:28,boxShadow:G.shadow,backdropFilter:G.blurCard,WebkitBackdropFilter:G.blurCard},
    // Lightest tier — prompt/nudge cards that should feel like part of the
    // page (faint wash, near-boxless), vs. the content cards above which
    // keep more presence. Same blur the card tier already carried.
    wash:{background:G.washSurface,border:G.hairline,borderRadius:RADII.card,padding:isMobile?20:28,boxShadow:G.washShadow,backdropFilter:G.blurCard,WebkitBackdropFilter:G.blurCard},
  };

  // Optional date field with a Clear control. iOS date inputs open the picker
  // on tap and, once a date is set, offer no way back to empty — so every
  // optional date gets an explicit Clear that stores "" (no date). Also
  // carries the overflow fix (border-box + WebkitAppearance) so the native
  // control never spills past the right edge. onChange receives the new value
  // ("" when cleared); saving with it cleared writes no date.
  const dateField = (value, onChange, extra = {}) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input type="date" value={value || ""} onChange={e => onChange(e.target.value)}
        style={{ ...C.inp, flex: 1, minWidth: 0, boxSizing: "border-box", WebkitAppearance: "none", ...extra }} />
      {value ? (
        <button type="button" onClick={() => onChange("")}
          style={{ flexShrink: 0, background: "transparent", border: `1px solid ${T.border}`, color: T.textMuted, borderRadius: 8, padding: "9px 12px", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
          Clear
        </button>
      ) : null}
    </div>
  );

  const TABS=[{id:"overview",label:"Overview",icon:"◈"},{id:"profile",label:"Profile",icon:"◯"},{id:"habits",label:"Habits",icon:"·"},{id:"finances",label:"Money",icon:"◎"},{id:"health",label:"Health",icon:"◉"},{id:"wellness",label:"Mind",icon:"◯"},{id:"coach",label:"Coach",icon:"·"}];
  // Tabs that have meaningful daily check-in content worth reminding about
  // The two tabs the mobile bottom nav has no room for. They lived behind its
  // "More" button until that was removed; the header avatar owns them now.
  const AVATAR_MENU = [{id:"profile",label:"Profile"},{id:"coach",label:"Coach"}];
  const avatarMenuActive = AVATAR_MENU.some(t => t.id === tab);
  const REMIND_TABS = new Set(["habits","wellness","health","finances"]);
  const todayKey = userKey(user?.id, "visited_" + localDateStr());
  const [visitedTabs, setVisitedTabs] = useState(() => {
    try { return todayKey ? new Set(JSON.parse(localStorage.getItem(todayKey)||"[]")) : new Set(); }
    catch { return new Set(); }
  });
  const markTabVisited = (id) => {
    setVisitedTabs(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      try { if (todayKey) localStorage.setItem(todayKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  // Load morning briefing when coach tab first opened — cached once per day
  useEffect(() => {
    if (tab === "coach" && coachMsgs.length === 0 && !coachLoading) {
      const todayStr = localDateStr();
      const cacheKey = userKey(user?.id, "coach_morning_" + todayStr);
      if (!cacheKey) return;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setCoachMsgs([{ text: cached, isCoach: true, role: "assistant" }]);
      } else {
        fetchCoachMessage("morning_briefing", "", [], cacheKey);
      }
    }
  }, [tab]);

  // Mark overview as visited on first render
  useEffect(() => { markTabVisited("overview"); }, []);
  // Local-midnight reset for the unvisited-today tab highlight: todayKey carries
  // the local date, so when the app is open across midnight (or the account
  // changes) this re-seeds visitedTabs from the new day's stored set (empty at
  // the rollover) and re-marks the tabs the user is actually on. Highlights thus
  // reset at local midnight without a reload.
  useEffect(() => {
    try { setVisitedTabs(new Set(todayKey ? JSON.parse(localStorage.getItem(todayKey) || "[]") : [])); }
    catch { setVisitedTabs(new Set()); }
    markTabVisited("overview");
    if (tab) markTabVisited(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayKey]);
  // PWA meta tags
  useEffect(() => {
    const setMeta = (name, content, prop=false) => {
      let el = document.querySelector(prop ? `meta[property="${name}"]` : `meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); prop ? el.setAttribute("property",name) : el.setAttribute("name",name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("theme-color", T.surface);
    setMeta("apple-mobile-web-app-capable","yes");
    setMeta("apple-mobile-web-app-status-bar-style","black-translucent");
    setMeta("apple-mobile-web-app-title","Scorify");
    setMeta("mobile-web-app-capable","yes");
    const vp = document.querySelector('meta[name="viewport"]');
    if (vp) vp.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
  }, []);

  if (!user && !isDemoMode) {
    return (
      <Landing
        onSignIn={() => navigate('/login')}
        onViewDemo={() => navigate('/demo')}
        onJoinWaitlist={async (email) => {
          const trimmed = (email || "").trim().toLowerCase();
          if (!trimmed) return;
          await apiFetch(apiUrl("/api/waitlist"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: trimmed }),
          }).catch(() => {});
        }}
      />
    );
  }

  // Ambient warm glow on Home only (round 6): a position:fixed underlay
  // starting 120px below the viewport top — iOS carries scroll-container
  // backgrounds with the content regardless of backgroundAttachment
  // (-webkit-overflow-scrolling:touch quirk), which is how faint pink kept
  // reaching the status bar; a fixed element with top:120px physically
  // cannot paint there on any engine at any scroll position. zIndex:-1
  // under C.app's own stacking context (position:relative+zIndex:0) puts it
  // above the cream background and below every card. Painted gradients
  // only, no live filter:blur; G.ambient is "none" under
  // prefers-reduced-transparency.
  return (
    <div style={C.app} ref={scrollRef}>
      {(contentTab==="overview"||contentTab==="habits"||contentTab==="wellness"||contentTab==="finances"||contentTab==="health"||contentTab==="profile") && G.ambient !== "none" && (
        <div aria-hidden="true" style={{position:"fixed",top:120,left:0,right:0,bottom:0,zIndex:-1,pointerEvents:"none",backgroundImage:G.ambient,backgroundRepeat:"no-repeat"}}/>
      )}
      {/* role="alert": the toast is the app's failure channel, and without a
          live region VoiceOver users never hear a failed save — the silent-
          success bug class all over again, for them only. */}
      {toast&&(
        <div role={toastTone==="error"?"alert":"status"} onClick={()=>setToast("")} style={{position:"fixed",left:12,right:12,bottom:88,zIndex:9999,padding:"11px 14px",borderRadius:10,background:withAlpha(toastTone==="success"?T.success:toastTone==="info"?T.text:T.danger,0.96),color:"#fff",fontSize:12.5,fontWeight:600,lineHeight:1.45,cursor:"pointer",boxShadow:"0 6px 20px rgba(0,0,0,0.35)"}}>
          {toast}
        </div>
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&family=DM+Serif+Display:ital@0;1&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        html,body{background:${T.bg} !important;overscroll-behavior:none;height:100%;overflow:hidden;}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-track{background:${T.surface}}
        ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.12);border-radius:2px}
        @keyframes pulse-dot{0%{box-shadow:0 0 0 0 ${withAlpha(T.accent,0.6)}}50%{box-shadow:0 0 0 5px ${withAlpha(T.accent,0)}}100%{box-shadow:0 0 0 0 ${withAlpha(T.accent,0)}}}
        @keyframes pop{0%{transform:scale(1)}50%{transform:scale(1.12)}100%{transform:scale(1)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes coachDotPulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.6);opacity:0.55}}
        @keyframes coachPulse{0%,100%{box-shadow:0 4px 20px ${withAlpha(T.accentFill,0.45)}}50%{box-shadow:0 4px 30px ${withAlpha(T.accentFill,0.75)},0 0 0 8px ${withAlpha(T.accentFill,0.1)}}}
        @keyframes coachSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
        @keyframes coachFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes coachFadeInUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes coachTypingDot{0%,80%,100%{transform:scale(0.6);opacity:0.3}40%{transform:scale(1);opacity:1}}
        @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
        @media (prefers-reduced-motion: reduce){
          *,*::before,*::after{animation-duration:0.01ms !important;animation-iteration-count:1 !important;transition-duration:0.01ms !important;}
        }
        .popped{animation:pop 0.3s ease}
        input::placeholder{color:${T.textDim}}
        textarea::placeholder{color:${T.textDim}}
        button:hover{opacity:0.88}`}</style>

      {/* HEADER */}
      <div style={C.hdr}>
        {/* Logo */}
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,paddingRight:8}}>
          <img src="/logo.png" alt="Scorify" style={{height:32,width:32,borderRadius:8,objectFit:"cover"}} />
          <span style={C.logo}>Scorify</span>
        </div>

        {/* Tabs inline */}
        <div style={C.nav}>
          {TABS.map(t=>{
            const unvisited = REMIND_TABS.has(t.id) && !visitedTabs.has(t.id) && tab!==t.id;
            return (
              <button key={t.id} style={{...C.navB(tab===t.id), position:"relative"}} aria-current={tab===t.id?"page":undefined}
                onClick={()=>{ if(t.id!==tab){ setTab(t.id); markTabVisited(t.id); } }}>
                {t.label}
                {unvisited && (
                  <span aria-hidden="true" style={{
                    position:"absolute", top:10, right:4,
                    width:7, height:7, borderRadius:"50%",
                    background:T.accent,
                    boxShadow:"0 0 0 0 rgba(249,115,22,0.7)",
                    animation:"pulse-dot 1.8s ease-in-out infinite"
                  }}/>
                )}
              </button>
            );
          })}
        </div>

        {/* Pillar indicators + Avatar */}
        <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0,paddingLeft:8}}>
          {/* Hidden on Home only: the rings carry the same values there
              (and the same data-testids — exactly one carrier at a time). */}
          {/* Fixed 36px so swapping the four mini scores for the compact pill never moves the bar. */}
          {contentTab!=="overview" && (
            <div style={{textAlign:"right",lineHeight:1.2,height:36,display:"flex",alignItems:"center"}}>
              {heroCollapsed && heroPillarId && pillarById[heroPillarId]
                ? <PillarPill pillar={pillarById[heroPillarId]} onClick={()=>{ const el = scrollRef.current; if (!el) return; let reduced = false; try { reduced = !!window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch {} el.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" }); }}/>
                : <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:1}}>
                    <PillarBar pillars={pillars} compact={isMobile}/>
                    {pillarsPending && <span aria-live="polite" style={{fontSize:9,color:T.textMuted,letterSpacing:"0.04em"}}>Updating…</span>}
                  </div>}
            </div>
          )}
          <div style={{position:"relative"}}>
            {/* The avatar carries the active state the removed More button used
                to show: accent ring while Profile or Coach is the current tab. */}
            {/* A real button: on mobile this menu is the ONLY route to the
                Coach and Profile tabs, and as a bare img/div onClick it was
                invisible to VoiceOver entirely. The 44px button box also fixes
                the 36px touch target; the visual stays the 36px avatar. */}
            <button onClick={openAvatarMenu} aria-label="Account menu" aria-haspopup="true" aria-expanded={showMoreMenu}
              style={{width:44,height:44,margin:-4,padding:0,background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" style={{width:36,height:36,borderRadius:"50%",objectFit:"cover",border:`2px solid ${avatarMenuActive?T.accent:T.overlayLight(0.08)}`}} title={username}/>
                : <span style={{width:36,height:36,borderRadius:"50%",background:T.accentFill,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:12,color:"#fff",letterSpacing:"0.03em",boxShadow:avatarMenuActive?`0 0 0 2px ${T.bg}, 0 0 0 4px ${T.accent}`:"none"}}
                    title={username}>
                    {(username||"Me").slice(0,2).toUpperCase()}
                  </span>
              }
            </button>
            {/* Anchored under the avatar rather than to the bottom nav, where it
                used to render — it opened at the far corner from the tap. The
                backdrop is a sibling so an outside tap closes it. */}
            {showMoreMenu && (
              <>
                <div style={{position:"fixed",inset:0,zIndex:1}} onClick={()=>setShowMoreMenu(false)}/>
                <div style={{
                  position:"absolute", top:"calc(100% + 10px)", right:0, zIndex:2,
                  background:G.modalSurface,
                  backdropFilter:G.blurCard, WebkitBackdropFilter:G.blurCard,
                  border:G.hairline,
                  borderRadius:14,
                  padding:"6px 0",
                  minWidth:170,
                  boxShadow:`${G.shadow}, 0 16px 48px rgba(20,14,12,0.3)`,
                  animation:"fadeUp 0.16s ease",
                }}>
                  {AVATAR_MENU.map(t=>(
                    <button key={t.id} aria-current={tab===t.id?"page":undefined} onClick={()=>{ if(t.id!==tab){ setTab(t.id); markTabVisited(t.id); } setShowMoreMenu(false); }}
                      style={{width:"100%",background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:12,padding:"11px 18px",color:tab===t.id?T.accent:T.text,fontWeight:tab===t.id?600:400,fontSize:14,letterSpacing:"0.01em",fontFamily:"inherit",textAlign:"left"}}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Preview mode banner (soft paywall): every tab is viewable, the
          writes and Kai calls go through requirePro, and this reopens the
          paywall. Same slot as the offline banner below. */}
      {previewBannerVisible && (
        <button type="button" onClick={()=>setPaywallForced(true)}
          style={{display:"block",width:"100%",background:withAlpha(T.accent,0.1),border:"none",borderBottom:`1px solid ${withAlpha(T.accent,0.25)}`,padding:"8px 16px",textAlign:"center",fontSize:12.5,fontWeight:600,color:T.text,fontFamily:"inherit",cursor:"pointer"}}>
          {PREVIEW_BANNER_COPY}
        </button>
      )}

      {/* Offline banner — said once, here, in the demo-banner slot. Driven by
          the reconnect listener (navigator.onLine, trusted only when false);
          individual failures still speak through the save toast, so a lying
          `true` costs nothing but this banner. */}
      {isOffline && !isDemoMode && (
        <div style={{background:withAlpha(T.warning,0.1),borderBottom:`1px solid ${withAlpha(T.warning,0.25)}`,padding:"8px 24px",textAlign:"center",fontSize:12.5,fontWeight:600,color:T.text}}>
          You're offline. Showing what's already loaded. Changes won't save until you're back.
        </div>
      )}

      {isDemo && (
        <div style={{background:withAlpha(T.warning,0.08),borderBottom:`1px solid ${withAlpha(T.warning,0.2)}`,padding:"10px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:13,color:T.text,fontWeight:500}}>Demo mode. Viewing @{username}'s account. Data is not real.</span>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>window.location.href="/login?mode=signup"} style={{background:T.accentFill,color:"#fff",border:"none",borderRadius:8,padding:"6px 16px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"inherit"}}>Create Account →</button>
            <button onClick={()=>window.location.href="/"} style={{background:"transparent",border:`1px solid ${T.overlayLight(0.15)}`,color:T.text,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12}}>← Back</button>
          </div>
        </div>
      )}
      <div style={C.pg} data-diag="tab-content">

        {/* ── OVERVIEW ── */}
        {contentTab==="overview"&&(
          <div style={{display:"flex",flexDirection:"column",gap:18}}>

            {/* ── GREETING (redesign Phase 1) ── Lives in the scroll content,
                not the sticky header: the header keeps the avatar menu (the
                only mobile route to Coach/Profile) untouched. The date line is
                deliberately NOT a "synced X ago" claim — no single sync fact
                backs one (docs/REDESIGN.md §1). */}
            {/* Compact date pad (2026-10-22): crimson band with the month, the
                day number, the weekday, one faint stacked page behind; a real
                button that opens the commitments calendar. To its right the
                greeting and "One thing: …" (Kai's oneThing, else the first Up
                next item, else nothing). Theme tokens only. */}
            {(()=>{
              const pad = dayPadParts(headerDay);
              let z=null; try { z=Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}
              const greeting = greetingFor(hourInZone(new Date(), z), username);
              const annotatedForHeader = z ? annotateBills(bills, z) : [];
              // "One thing" is LIVE (src/lib/oneThing.js): current commitments, bills,
              // weekly goals and habits, re-evaluated on every state change and each minute.
              const weeklyGoals = linkWorkoutGoals(goals, habits).filter(g => g && !g.completed && (g.period || (g.source_type === 'workout' ? 'week' : null)) === 'week').map(g => {
                const a = getGoalAutoValue(g);
                const gymDone = habits.some(h => h.id === 'gym' && h.active !== false && h.lastLoggedDate === headerDay);
                return { title: g.title, current: a?.value ?? g.current_value, target: a?.target ?? g.period_target ?? g.target_value, unit: a?.unit ?? g.unit,
                  inverted: !!a?.inverted, hasData: a ? a.hasData !== false : true,
                  loggedToday: g.source_type === 'workout' ? (workouts.some(w => String(w.date || '').slice(0, 10) === headerDay) || gymDone) : false };
              });
              const oneThing = computeOneThing({
                todayStr: headerDay, nowMinutes: headerMinute, commitments, bills: annotatedForHeader, goals: weeklyGoals,
                habits: habits.map(h => ({ label: tmpl(h.id)?.label || h.label || h.id, loggedToday: h.lastLoggedDate === headerDay, active: h.active })),
                commitLabel, fmt12h,
              });
              const PAD_W = 66, PAD_H = 78;
              return (
                <div style={{padding:"12px 2px 0",order:-4,display:"flex",alignItems:"center",gap:14}}>
                  <div style={{position:"relative",width:PAD_W+4,height:PAD_H+4,flexShrink:0}}>
                    {/* the stacked page behind */}
                    <div aria-hidden="true" style={{position:"absolute",left:4,top:4,width:PAD_W,height:PAD_H,borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,opacity:0.6}}/>
                    {pad && (
                      <button onClick={()=>{ setCommitCalDay(null); setShowAllCommitments(true); }} aria-label={pad.aria}
                        style={{position:"absolute",left:0,top:0,width:PAD_W,height:PAD_H,borderRadius:10,background:T.surface,border:`1px solid ${T.borderStrong}`,padding:0,cursor:"pointer",fontFamily:"inherit",overflow:"hidden",display:"flex",flexDirection:"column",alignItems:"stretch",boxShadow:`0 2px 6px ${T.overlayLight(0.08)}`}}>
                        <span style={{display:"block",background:T.accent,color:"#fff",fontSize:10,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",lineHeight:"18px",textAlign:"center"}}>{pad.month}</span>
                        <span style={{display:"block",fontFamily:"'DM Serif Display',serif",fontSize:30,lineHeight:"38px",color:T.text,textAlign:"center",letterSpacing:"-0.01em"}}>{pad.day}</span>
                        <span style={{display:"block",fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:T.textMuted,lineHeight:"18px",textAlign:"center"}}>{pad.weekday}</span>
                      </button>
                    )}
                  </div>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:26,lineHeight:1.1,color:T.text,letterSpacing:"-0.01em",overflow:"hidden",textOverflow:"ellipsis"}}>{greeting}</div>
                    {oneThing && (
                      <div style={{fontSize:13,color:T.textMuted,marginTop:6,lineHeight:1.45,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {oneThing.prefix && <span style={{color:T.accent,fontWeight:700}}>One thing: </span>}{oneThing.text}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ── GETTING STARTED (Kai) ── three first actions that tick off from
                real data and the card disappears once all three are done or after
                7 days (gettingStartedVisible). Sits between the brief and the rings
                for new users (order -2). */}
            {(()=>{
              if (isDemoMode) return null;
              const habitLogged = habits.some(h => (Array.isArray(h.completions) && h.completions.length > 0) || h.lastLoggedDate);
              const moodEver = moodHistory.some(m => m.date) || moodLogged;
              const planMade = commitments.length > 0;
              const steps = gettingStartedSteps({ habitLogged, moodLogged: moodEver, planMade });
              if (!gettingStartedVisible({ steps, accountAgeDays: accountAgeDaysHome })) return null;
              const act = { habit: ()=>{ setTab("habits"); markTabVisited("habits"); }, mood: ()=>setShowMoodSheet(true), plan: ()=>setTab("coach") };
              const doneCount = steps.filter(s=>s.done).length;
              return (
                <div style={{...GC.card,order:-2}}>
                  <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:4}}>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:T.text}}>Getting started</div>
                    <div style={{fontSize:12,color:T.textMuted}}>with {coachName||"Kai"}</div>
                  </div>
                  <div style={{fontSize:12,color:T.textMuted,marginBottom:12}}>{doneCount} of {steps.length} done — your scores start filling in as you go.</div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {steps.map(s=>(
                      <button key={s.id} type="button" onClick={s.done?undefined:act[s.id]} disabled={s.done}
                        style={{display:"flex",alignItems:"center",gap:10,width:"100%",textAlign:"left",background:G.tileBg,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",cursor:s.done?"default":"pointer",fontFamily:"inherit",minHeight:48}}>
                        <span aria-hidden="true" style={{width:18,height:18,borderRadius:"50%",flexShrink:0,border:s.done?"none":`2px solid ${T.borderStrong}`,background:s.done?T.success:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {s.done && <span style={{width:9,height:9,borderRadius:"50%",background:"#fff"}}/>}
                        </span>
                        <span style={{flex:1,fontSize:14,fontWeight:600,color:s.done?T.textMuted:T.text,textDecoration:s.done?"line-through":"none"}}>{s.label}</span>
                        {!s.done && <span style={{fontSize:13,color:T.accent,flexShrink:0}}>→</span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── PILLAR RINGS ── replaces the "Your Pillars" card. Values:
                the same `pillars` array (calcPillarScores) the old rows and
                the header PillarBar read — parity table, docs/REDESIGN.md §3. */}
            <div style={{order:-1}}>
              <PillarRings pillars={pillars} selected={selectedPillar} onSelect={setSelectedPillar}
                onStart={(id)=>{ const t={finance:"finances",health:"health",habits:"habits",wellbeing:"wellness"}[id]; if(t){ setTab(t); markTabVisited(t); } }}/>
              {(()=>{
                const sel = pillars[selectedPillar] || pillars[0];
                if (!sel) return null;
                const loading = !!sel.placeholder;
                const notStarted = sel.pct===null && !loading;
                const tabFor = { finance:"finances", health:"health", habits:"habits", wellbeing:"wellness" }[sel.id];
                return (
                  <div style={{margin:"10px 32px 0",display:"flex",flexDirection:"column",alignItems:"center",gap:2,textAlign:"center"}}>
                    <span style={{fontSize:13,color:T.textMuted}}>
                      {loading ? "Updating…" : notStarted ? "Not started yet" : `${sel.trackedCount} of ${sel.totalCount} trackers`}
                    </span>
                    <button onClick={()=>{ if(tabFor){ setTab(tabFor); markTabVisited(tabFor); } }}
                      style={{minHeight:44,display:"flex",alignItems:"center",background:"transparent",border:"none",cursor:"pointer",fontSize:14,fontWeight:600,color:T.accent,fontFamily:"inherit",padding:"0 12px"}}>
                      {notStarted ? `Start ${sel.short}` : `Open ${sel.short}`} →
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* ── UP NEXT ── plan-with-Kai commitments + due-soon bills. Today /
                This week / Needs an answer / Due soon; top 3, then See all opens
                the commitments calendar. All colors from the theme, no emoji. */}
            {(() => {
              const todayStr = localDateStr();
              let zone = null; try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { zone = null; }
              const annotated = zone ? annotateBills(bills, zone) : [];
              // THE grouping (src/lib/homeLayout.js upNextGroups), shared with the header's "One thing" fallback.
              const { needAnswer, today: todayItems, week: weekItems, bills: dueSoonBills } = upNextGroups({ commitments, annotatedBills: annotated, todayStr, daysFromToday });
              const totalCount = todayItems.length + weekItems.length + needAnswer.length + dueSoonBills.length;

              const openPanel = (c) => { setOpenCommitmentId(c.id); setRescheduleDraft({ date: c.due_date || "", time: c.due_time || "" }); };

              // Expandable action panel for an open/this-week commitment row.
              const actionPanel = (c) => (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => { markCommitmentDone(c.id); setOpenCommitmentId(null); }} style={{ ...C.btn(T.success), minHeight: 40, padding: "0 14px", fontSize: 13 }}>Mark done</button>
                    <button onClick={() => { removeCommitment(c.id); setOpenCommitmentId(null); }} style={{ ...C.ghost, minHeight: 40, padding: "0 14px", fontSize: 13 }}>Remove</button>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input type="date" value={rescheduleDraft.date} onChange={e => setRescheduleDraft(d => ({ ...d, date: e.target.value }))} style={{ ...C.inp, minHeight: 40, padding: "0 10px", width: "auto", flex: "1 1 140px" }} />
                    <input type="time" value={rescheduleDraft.time} onChange={e => setRescheduleDraft(d => ({ ...d, time: e.target.value }))} style={{ ...C.inp, minHeight: 40, padding: "0 10px", width: "auto", flex: "1 1 110px" }} />
                    <button onClick={() => { rescheduleCommitment(c.id, rescheduleDraft.date, rescheduleDraft.time); setOpenCommitmentId(null); }} style={{ ...C.btn(), minHeight: 40, padding: "0 14px", fontSize: 13 }}>Save</button>
                  </div>
                </div>
              );

              const commitRow = (c, trailing) => {
                const open = openCommitmentId === c.id;
                const t = fmt12h(c.due_time);
                return (
                  <div key={c.id} style={{ padding: "11px 0", borderBottom: `1px solid ${T.border}` }}>
                    <button onClick={() => open ? setOpenCommitmentId(null) : openPanel(c)}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, width: "100%", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0, textAlign: "left" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        {statusDot(c.status)}
                        <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 15, color: T.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{commitLabel(c)}</span>
                          <span style={{ fontSize: 12, color: T.textMuted }}>{[trailing, t].filter(Boolean).join(" · ") || "from your chat with Kai"}</span>
                        </span>
                      </span>
                      <span style={{ fontSize: 13, color: T.accent, flexShrink: 0 }}>{open ? "Close" : "Edit"}</span>
                    </button>
                    {open && actionPanel(c)}
                  </div>
                );
              };

              const needRow = (c) => (
                <div key={c.id} style={{ padding: "11px 0", borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {statusDot(c.status)}
                    <div style={{ fontSize: 15, color: T.text, fontWeight: 600 }}>Did {commitLabel(c)} happen?</div>
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{c.due_date ? `planned for ${fmtDueDay(c.due_date, localDateStr()).replace(/^(Today|Tomorrow|Yesterday)$/, w => w.toLowerCase())}` : "from your chat with Kai"}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button onClick={() => confirmCommitment(c.id, true)} style={{ ...C.btn(T.success), minHeight: 40, padding: "0 18px", fontSize: 13 }}>Yes</button>
                    <button onClick={() => confirmCommitment(c.id, false)} style={{ ...C.ghost, minHeight: 40, padding: "0 18px", fontSize: 13 }}>No</button>
                  </div>
                </div>
              );

              const billRow = (b) => (
                <button key={b.id} onClick={() => { setTab("finances"); markTabVisited("finances"); }}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, width: "100%", background: "transparent", border: "none", borderBottom: `1px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit", padding: "11px 0", textAlign: "left" }}>
                  <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 15, color: T.text, fontWeight: 600 }}>{b.name}</span>
                    <span style={{ fontSize: 12, color: T.textMuted }}>bill · {b.daysUntil === 0 ? "due today" : `due in ${b.daysUntil} day${b.daysUntil === 1 ? "" : "s"}`}{b.amount ? ` · $${Number(b.amount).toLocaleString()}` : ""}</span>
                  </span>
                  <span style={{ fontSize: 13, color: T.accent, flexShrink: 0 }}>View</span>
                </button>
              );

              const section = (title, nodes) => nodes.length ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textDim, marginBottom: 2 }}>{title}</div>
                  {nodes}
                </div>
              ) : null;

              // Home declutter (2026-10-20): at most 3 rows (src/lib/homeLayout.js); See all has the rest.
              const { needAnswer: nNeed, today: nToday, week: nWeek, bills: nBills } = takeUpNext({ needAnswer, today: todayItems, week: weekItems, bills: dueSoonBills });

              return (
                <div style={GC.card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div style={C.cTitle}>Up next</div>
                    {commitments.some(c => c.due_date) && (
                      <button onClick={() => { setCommitCalDay(null); setShowAllCommitments(true); }} style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: T.accent, padding: "4px 0" }}>See all →</button>
                    )}
                  </div>
                  {totalCount === 0 ? (
                    <div style={{ color: T.textMuted, fontSize: 14, marginTop: 8 }}>Nothing scheduled. Tell Kai a plan and it'll show up here.</div>
                  ) : (
                    <>
                      {section("Needs an answer", nNeed.map(needRow))}
                      {section("Today", nToday.map(c => commitRow(c, null)))}
                      {section("This week", nWeek.map(c => commitRow(c, fmtDueDay(c.due_date, todayStr))))}
                      {section("Due soon", nBills.map(billRow))}
                    </>
                  )}
                </div>
              );
            })()}

            {/* ── COMMITMENTS CALENDAR ── the "See all" destination. A month grid of
                every commitment with a due date, colored by status; tap a day for the
                text, status, and what resolved it. Date changes move the entry (derived
                from state each render) so there are never duplicates. */}
            {showAllCommitments && (() => {
              const y = commitCalMonth.getFullYear(), m = commitCalMonth.getMonth();
              const firstDay = new Date(y, m, 1).getDay();
              const daysInMonth = new Date(y, m + 1, 0).getDate();
              const monthPrefix = `${y}-${String(m + 1).padStart(2, "0")}`;
              const byDay = {};
              commitments.forEach(c => { if (c.due_date && c.due_date.startsWith(monthPrefix)) { const day = parseInt(c.due_date.slice(8, 10), 10); (byDay[day] = byDay[day] || []).push(c); } });
              const todayStr = localDateStr();
              const dayStr = (day) => `${monthPrefix}-${String(day).padStart(2, "0")}`;
              const selItems = commitCalDay ? commitments.filter(c => c.due_date === commitCalDay) : [];
              const monthLabel = commitCalMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
              const shiftMonth = (delta) => { setCommitCalMonth(new Date(y, m + delta, 1)); setCommitCalDay(null); };

              return (
                <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: T.bg, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
                  <div style={{ maxWidth: 560, margin: "0 auto", padding: "16px", paddingTop: "calc(16px + env(safe-area-inset-top, 0px))", paddingBottom: "calc(48px + env(safe-area-inset-bottom, 0px))" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                      <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: T.text, margin: 0 }}>Commitments</h2>
                      <button onClick={() => setShowAllCommitments(false)} style={{ ...C.ghost, minHeight: 44, padding: "0 16px", fontSize: 14 }}>Done</button>
                    </div>

                    <div style={GC.card}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <button onClick={() => shiftMonth(-1)} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.accent, fontSize: 22, minHeight: 44, minWidth: 44, fontFamily: "inherit" }}>‹</button>
                        <span style={{ fontSize: 16, fontWeight: 600, color: T.text }}>{monthLabel}</span>
                        <button onClick={() => shiftMonth(1)} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.accent, fontSize: 22, minHeight: 44, minWidth: 44, fontFamily: "inherit" }}>›</button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
                        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                          <div key={i} style={{ textAlign: "center", fontSize: 11, color: T.textDim, fontWeight: 600, paddingBottom: 4 }}>{d}</div>
                        ))}
                        {Array(firstDay).fill(0).map((_, i) => <div key={`e${i}`} />)}
                        {Array(daysInMonth).fill(0).map((_, i) => {
                          const day = i + 1, ds = dayStr(day), items = byDay[day] || [];
                          const isToday = ds === todayStr, isSel = ds === commitCalDay;
                          const dots = [...new Set(items.map(c => c.status))].slice(0, 4);
                          return (
                            <button key={day} onClick={() => setCommitCalDay(isSel ? null : ds)}
                              style={{ aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, border: isSel ? `2px solid ${T.accent}` : `1px solid ${T.border}`, borderRadius: 10, background: isToday ? withAlpha(T.accent, 0.08) : T.surface, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                              <span style={{ fontSize: 13, color: T.text, fontWeight: isToday ? 700 : 500 }}>{day}</span>
                              <span style={{ display: "flex", gap: 3, minHeight: 8 }}>
                                {dots.map((s, k) => { const ss = statusStyle(s); return <span key={k} style={{ width: 8, height: 8, borderRadius: "50%", boxSizing: "border-box", ...(ss.hollow ? { border: `1.5px solid ${ss.color}`, background: "transparent" } : { background: ss.color }) }} />; })}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                        {["open", "needs_confirmation", "done", "missed", "dropped"].map((k) => {
                          const ss = statusStyle(k);
                          return (
                            <span key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textMuted }}>
                              {statusDot(k, 9)}{ss.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {commitCalDay && (
                      <div style={{ ...GC.card, marginTop: 14 }}>
                        <div style={C.cTitle}>{new Date(`${commitCalDay}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
                        {selItems.length === 0 ? (
                          <div style={{ color: T.textMuted, fontSize: 14, marginTop: 8 }}>Nothing planned this day.</div>
                        ) : selItems.map(c => {
                          const resolvedNote = c.status === "done" || c.status === "missed" ? (c.evidence || (c.status === "done" ? "Completed" : "Not completed")) : null;
                          return (
                            <div key={c.id} style={{ padding: "11px 0", borderBottom: `1px solid ${T.border}` }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 15, color: T.text, fontWeight: 600 }}>{commitLabel(c)}{c.due_time ? ` · ${fmt12h(c.due_time)}` : ""}</span>
                                {statusPill(c.status)}
                              </div>
                              {textAddsDetail(c) && (
                                <div style={{ fontSize: 13, color: T.textMuted, marginTop: 3 }}>{c.text}</div>
                              )}
                              {resolvedNote && <div style={{ fontSize: 13, color: T.textMuted, marginTop: 3 }}>{resolvedNote}</div>}
                              {c.status === "needs_confirmation" && (
                                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                  <button onClick={() => confirmCommitment(c.id, true)} style={{ ...C.btn(T.success), minHeight: 40, padding: "0 18px", fontSize: 13 }}>Yes</button>
                                  <button onClick={() => confirmCommitment(c.id, false)} style={{ ...C.ghost, minHeight: 40, padding: "0 18px", fontSize: 13 }}>No</button>
                                </div>
                              )}
                              {c.status === "open" && (
                                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                  <button onClick={() => markCommitmentDone(c.id)} style={{ ...C.btn(T.success), minHeight: 40, padding: "0 14px", fontSize: 13 }}>Mark done</button>
                                  <button onClick={() => removeCommitment(c.id)} style={{ ...C.ghost, minHeight: 40, padding: "0 14px", fontSize: 13 }}>Remove</button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ── AI COACH CARD (merged briefing + insight; glass, Phase 1) ── */}
            {!briefingDismissed&&(
              <div style={{...GC.card,padding:isMobile?"16px 18px":"20px 24px",position:"relative",overflow:"hidden",order:isFirstRun?-3:0}}>
                {/* Header row */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"baseline",gap:10}}>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,color:T.accent,letterSpacing:"-0.01em",lineHeight:1.2}}>{coachName||"Kai"}</div>
                    <div style={{fontSize:12,color:T.textMuted}}>Daily brief</div>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {briefingJson&&briefingDate&&<span style={{fontSize:10,color:T.textDim}}>{(()=>{const m=Math.floor((Date.now()-new Date(briefingDate).getTime())/60000);return m<60?`${m}m ago`:`${Math.floor(m/60)}h ago`;})()}</span>}
                    {briefingJson&&<button onClick={refreshBriefing} title="Refresh" aria-label="Refresh briefing" style={{background:T.overlayLight(0.06),border:"none",color:T.textMuted,borderRadius:8,padding:"4px 10px",cursor:"pointer",fontSize:12}}>↻</button>}
                    <button onClick={()=>setBriefingDismissed(true)} aria-label="Dismiss briefing" style={{background:"transparent",border:"none",color:T.textMuted,cursor:"pointer",fontSize:20,lineHeight:1,padding:"10px 12px"}}>×</button>
                  </div>
                </div>
                {/* Something logged after this brief was generated (briefStaleness):
                    say so, never auto-regenerate. Refresh regenerates once —
                    refreshBriefing clears the brief, so this line and its button
                    are gone until the new one lands. */}
                {briefingJson&&!briefingLoading&&briefUpdatedSince(briefingDate,{habits,workouts,moods:moodHistory})&&(
                  <div style={{fontSize:12,color:T.textMuted,marginBottom:10}}>
                    Updated since this brief ·{" "}
                    <button onClick={refreshBriefing} style={{background:"none",border:"none",padding:0,color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Refresh</button>
                  </div>
                )}
                {/* No briefing yet — manual trigger, or the honest failure
                    state (never invented content; see generateBriefing) */}
                {!briefingJson&&!briefingLoading&&(
                  <div style={{marginTop:10}}>
                    {isFirstRun ? (
                      // Brand-new account: a short welcome, never a blank card. (When AI
                      // consent is on, the auto-generate effect replaces this with Kai's
                      // real welcome brief; this stands in meanwhile and without consent.)
                      (()=>{ const w = welcomeBrief(username); return (
                        <>
                          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:17,color:T.text,marginBottom:10,letterSpacing:"-0.01em"}}>{w.title}</div>
                          {w.lines.map((l,i)=>(<p key={i} style={{margin:"0 0 8px",fontSize:13,color:T.textMuted,lineHeight:1.6}}>{l}</p>))}
                          <button onClick={generateBriefing} style={{...C.btn(T.accent),fontSize:13,minHeight:isMobile?44:undefined,width:"100%",marginTop:6}}>Generate today's brief</button>
                        </>
                      ); })()
                    ) : briefingError ? (
                      <>
                        <p style={{margin:"0 0 14px",fontSize:13,color:T.textMuted,lineHeight:1.6}}>Your briefing needs a connection.</p>
                        <button onClick={generateBriefing} style={{...C.btn(T.accent),fontSize:13,minHeight:isMobile?44:undefined,width:"100%"}}>Tap to retry</button>
                      </>
                    ) : (
                      <>
                        <p style={{margin:"0 0 14px",fontSize:13,color:T.textMuted,lineHeight:1.6}}>Tap to generate today's briefing, personalized to your habits, finances &amp; health.</p>
                        <button onClick={generateBriefing} style={{...C.btn(T.accent),fontSize:13,minHeight:isMobile?44:undefined,width:"100%"}}>Generate today's briefing</button>
                      </>
                    )}
                  </div>
                )}
                {/* Loading skeleton */}
                {briefingLoading&&(
                  <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:10}}>
                    {[100,80,60,80,50].map((w,i)=>(
                      <div key={i} style={{height:12,width:`${w}%`,borderRadius:6,background:`linear-gradient(90deg,${T.overlayLight(0.04)} 25%,${T.overlayLight(0.08)} 50%,${T.overlayLight(0.04)} 75%)`,backgroundSize:"200% 100%",animation:`shimmer 1.5s ${i*0.15}s infinite`}}/>
                    ))}
                  </div>
                )}
                {/* Collapsed (Home declutter, 2026-10-20): the headline (check-in
                    rows) or greeting (client briefs) plus Read more, which
                    expands in place. "Updated since this brief" stays above. */}
                {briefingJson&&!briefingLoading&&!briefExpanded&&(
                  <div style={{marginTop:4,borderTop:`1px solid ${T.border}`,paddingTop:12}}>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:17,color:T.text,letterSpacing:"-0.01em",lineHeight:1.35}}>{briefHeadline(briefingJson)}</div>
                    <button onClick={()=>setBriefExpanded(true)} aria-expanded={false} style={{minHeight:40,background:"none",border:"none",padding:0,color:T.accent,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Read more</button>
                  </div>
                )}
                {/* Structured briefing */}
                {briefingJson&&!briefingLoading&&briefExpanded&&(
                  <div style={{marginTop:4,borderTop:`1px solid ${T.border}`,paddingTop:14}}>
                    {briefingJson.headline&&<div style={{fontFamily:"'DM Serif Display',serif",fontSize:17,color:T.text,marginBottom:10,letterSpacing:"-0.01em",lineHeight:1.35}}>{briefingJson.headline}</div>}
                    {/* Greeting */}
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:17,color:T.text,marginBottom:14,letterSpacing:"-0.01em"}}>{briefingJson.greeting}</div>
                    {/* PROSE CHECK-IN (1B, 2026-10-14): when the row carries `body`,
                        the card is the body as plain prose plus a quiet chip row of
                        must items. No section labels, no emoji; the only red is a
                        bill due today or a crossed cap (must_items[].urgent). Rows
                        without `body` (older builds of the cron, the client-side
                        generator) fall through to the sections below. */}
                    {typeof briefingJson.body==="string"&&briefingJson.body.trim()?(
                      <>
                        {briefingJson.body.split(/\n{2,}/).map((para,i)=>(
                          <p key={i} style={{margin:"0 0 12px",fontSize:14,color:T.text,lineHeight:1.65}}>{para.trim()}</p>
                        ))}
                        {Array.isArray(briefingJson.must_items)&&briefingJson.must_items.length>0&&(
                          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:4}}>
                            {briefingJson.must_items.slice(0,4).map((m,i)=>{
                              const tone=m.urgent?T.danger:T.textMuted;
                              return (
                                <span key={i} style={{fontSize:11,fontWeight:600,color:tone,background:withAlpha(tone,0.08),border:`1px solid ${withAlpha(tone,0.25)}`,borderRadius:20,padding:"3px 10px"}}>
                                  {String(m.label||"").replace(/^(bill coming due|commitment due today|cap at or over its limit|behind pace):\s*/i,"")}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ):(<>
                    {/* Today's Focus */}
                    <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Today's Focus</div>
                    {(briefingJson.focus||[]).map((item,i)=>(
                      <div key={i} style={{display:"flex",gap:8,marginBottom:6,paddingLeft:4}}>
                        <span style={{color:T.accent,flexShrink:0,fontSize:10,marginTop:3}}>◆</span>
                        <span style={{fontSize:13,color:T.textMuted,lineHeight:1.6}}>{item}</span>
                      </div>
                    ))}
                    {/* Momentum */}
                    {briefingJson.momentum&&(
                      <div style={{marginTop:12}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Momentum</div>
                        <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6}}>{briefingJson.momentum}</div>
                      </div>
                    )}
                    {/* Watch Out */}
                    {briefingJson.watchOut&&(
                      <div style={{marginTop:12}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Watch Out</div>
                        <div style={{fontSize:13,color:T.danger,lineHeight:1.6}}>{briefingJson.watchOut}</div>
                      </div>
                    )}
                    {/* One Thing */}
                    {briefingJson.oneThing&&(
                      <div style={{marginTop:12}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.accent,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>One Thing</div>
                        <div style={{fontSize:13,color:T.accent,fontWeight:600,lineHeight:1.6}}>{briefingJson.oneThing}</div>
                      </div>
                    )}
                    {/* Pattern insight ("food for thought") woven into the check-in. */}
                    {briefingJson.pattern?.observation && <PatternBlock pattern={briefingJson.pattern}/>}
                    </>)}
                    {/* Feedback + Talk to coach */}
                    <div style={{marginTop:16,paddingTop:12,borderTop:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                      {/* Rendered only when a briefing row exists to attach the
                          rating to (audit W11) — a control that silently writes
                          nowhere is worse than an absent one. Drawn thumbs, not
                          emoji: the documented exception to the no-emoji rule
                          (see the scorify skill's design principles) — this is
                          a control carrying information type and color alone
                          cannot, not decoration. White glyph on a filled
                          circle; T.success up, T.accent down; unselected dims. */}
                      {briefingDbId && (
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:11,color:T.textDim}}>Helpful?</span>
                        <button aria-label="Helpful" aria-pressed={briefingFeedback==="up"} onClick={()=>saveBriefingFeedback("up")} style={{width:44,height:44,background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                          <span style={{width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:T.success,opacity:briefingFeedback==="up"?1:0.4,transition:"opacity 0.15s"}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M3.8 11.2h1.7a1 1 0 0 1 1 1v6.6a1 1 0 0 1-1 1H3.8a1 1 0 0 1-1-1v-6.6a1 1 0 0 1 1-1Z M9 18.8v-6.9a2 2 0 0 1 .5-1.3l3.9-5.7a1.8 1.8 0 0 1 3.2 1.3l-.6 3h3.2a2 2 0 0 1 1.95 2.45l-1.15 4.9a2.4 2.4 0 0 1-2.34 1.85H10a1 1 0 0 1-1-.6Z" stroke={T.bg} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          </span>
                        </button>
                        <button aria-label="Not helpful" aria-pressed={briefingFeedback==="down"} onClick={()=>saveBriefingFeedback("down")} style={{width:44,height:44,background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                          <span style={{width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:T.accent,opacity:briefingFeedback==="down"?1:0.4,transition:"opacity 0.15s"}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{transform:"rotate(180deg)"}}>
                            <path d="M3.8 11.2h1.7a1 1 0 0 1 1 1v6.6a1 1 0 0 1-1 1H3.8a1 1 0 0 1-1-1v-6.6a1 1 0 0 1 1-1Z M9 18.8v-6.9a2 2 0 0 1 .5-1.3l3.9-5.7a1.8 1.8 0 0 1 3.2 1.3l-.6 3h3.2a2 2 0 0 1 1.95 2.45l-1.15 4.9a2.4 2.4 0 0 1-2.34 1.85H10a1 1 0 0 1-1-.6Z" stroke={T.bg} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          </span>
                        </button>
                      </div>
                      )}
                      <button onClick={()=>setTab("coach")} style={{minHeight:44,padding:"0 18px",display:"flex",alignItems:"center",borderRadius:22,background:T.accentFill,color:"#fff",border:"none",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>
                        Reply to {coachName||"Kai"}
                      </button>
                    </div>
                    <button onClick={()=>setBriefExpanded(false)} aria-expanded={true} style={{minHeight:40,marginTop:6,background:"none",border:"none",padding:0,color:T.accent,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Show less</button>
                  </div>
                )}
              </div>
            )}

            {/* ── WEEKLY RECAP CARD ── cron-generated (kind='recap'); opened by
                tapping the Sunday recap push, or shown here when fresh. ── */}
            {/* Home declutter (2026-10-20): a one-line summary ("Your week" +
                headline) that expands in place on tap; a recap push tap opens
                it expanded. */}
            {recapJson && !recapDismissed && !recapExpanded && (
              <div style={{...GC.card,padding:"6px 8px 6px 18px",display:"flex",alignItems:"center",gap:6}}>
                <button onClick={()=>setRecapExpanded(true)} aria-expanded={false}
                  style={{flex:1,minWidth:0,minHeight:44,display:"flex",alignItems:"center",gap:8,background:"transparent",border:"none",cursor:"pointer",padding:0,fontFamily:"inherit",textAlign:"left"}}>
                  <span style={{fontFamily:"'DM Serif Display',serif",fontSize:16,color:T.text,flexShrink:0}}>Your week</span>
                  <span style={{fontSize:13,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>{recapJson.headline || "Your weekly recap"}</span>
                  <span aria-hidden="true" style={{marginLeft:"auto",color:T.accent,fontSize:16,flexShrink:0}}>›</span>
                </button>
                <button onClick={()=>setRecapDismissed(true)} aria-label="Dismiss recap" style={{background:"transparent",border:"none",color:T.textMuted,cursor:"pointer",fontSize:20,lineHeight:1,padding:"10px 12px"}}>×</button>
              </div>
            )}
            {recapJson && !recapDismissed && recapExpanded && (
              <div style={{...GC.card}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <button onClick={()=>setRecapExpanded(false)} aria-expanded={true} style={{background:"transparent",border:"none",cursor:"pointer",padding:0,fontFamily:"inherit",textAlign:"left"}}>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:16,color:T.text,lineHeight:1.2}}>Your week</div>
                    <div style={{fontSize:11,color:T.accent,fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginTop:2}}>Weekly Recap</div>
                  </button>
                  <button onClick={()=>setRecapDismissed(true)} aria-label="Dismiss recap" style={{background:"transparent",border:"none",color:T.textMuted,cursor:"pointer",fontSize:20,lineHeight:1,padding:"10px 12px"}}>×</button>
                </div>
                {recapJson.headline && <div style={{fontFamily:"'DM Serif Display',serif",fontSize:17,color:T.text,marginBottom:12,letterSpacing:"-0.01em",lineHeight:1.35}}>{recapJson.headline}</div>}
                {recapJson.learning ? (
                  <>
                    <div style={{fontSize:13.5,color:T.text,lineHeight:1.6,marginBottom:10}}>{recapJson.learning}</div>
                    {recapJson.focusNextWeek && <div style={{fontSize:12.5,color:T.textMuted,lineHeight:1.55}}><span style={{color:T.accent,fontWeight:700}}>Next week: </span>{recapJson.focusNextWeek}</div>}
                  </>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {[["Went well",recapJson.wentWell,T.success],["Slipped",recapJson.slipped,T.warning],["Next week",recapJson.focusNextWeek,T.accent]].filter(([,v])=>v).map(([label,v,col])=>(
                      <div key={label} style={{fontSize:12.5,color:T.textMuted,lineHeight:1.55}}><span style={{color:col,fontWeight:700}}>{label}: </span>{v}</div>
                    ))}
                  </div>
                )}
                {/* Pattern as its own block with the "Why I noticed this" evidence.
                    Defensive against a legacy string value from an older recap row. */}
                {recapJson.pattern && <PatternBlock pattern={typeof recapJson.pattern === "string" ? { observation: recapJson.pattern } : recapJson.pattern}/>}
                {/* The recap ends with planning the week (2026-10-20). */}
                <button onClick={()=>setPlannerPending(true)} style={{...C.btn(T.accent),width:"100%",marginTop:14}}>Plan my week</button>
              </div>
            )}

            {/* ── DAILY CHECK-IN TIME PROMPT ── Existing users (and anyone who
                skipped it in onboarding) pick their check-in time here. Shows
                only when they have not chosen one and the snooze has elapsed;
                native only, where the push is actually delivered. ── */}
            {native && !isDemoMode && user && notifPrefs.checkinEnabled && notifPrefs.checkinHour==null &&
              (!notifPrefs.checkinPromptSnoozedUntil || new Date(notifPrefs.checkinPromptSnoozedUntil) < new Date()) && (
              <div style={{...GC.wash, display:"flex", flexDirection:"column", gap:10}}>
                <div>
                  <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:4}}>Pick when {coachName||"Kai"} checks in</div>
                  <div style={{fontSize:13,color:T.textMuted,lineHeight:1.5}}>A short daily note at the time you choose, with the one thing that matters most that day.</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setShowNotifSettings(true)} style={{...C.btn(T.accent),fontSize:13,flex:1}}>Choose a time</button>
                  <button onClick={()=>saveNotifPrefs({ checkinPromptSnoozedUntil: new Date(Date.now() + 5*86400000).toISOString() })} style={{...C.ghost,fontSize:13,padding:"8px 16px"}}>Not now</button>
                </div>
              </div>
            )}

            {/* ── WEEKLY CHECK-IN CARD ── In-app only (never a push). Shows once
                a week: hidden as soon as a mood/check-in was logged in the last
                7 days, so it never nags someone who is already checking in. */}
            {(() => {
              const weekAgoStr = localDateStr(new Date(Date.now() - 7 * 86400000));
              const doneThisWeek = (moodHistory || []).some(d => d && d.score != null && d.date && d.date >= weekAgoStr);
              if (doneThisWeek) return null;
              return (
                <div style={{...GC.wash, display:"flex", alignItems:"center", justifyContent:"space-between", gap:14}}>
                  <div>
                    <div style={{fontSize:14, fontWeight:700, color:T.text}}>Weekly check-in</div>
                    <div style={{fontSize:12, color:T.textMuted, marginTop:3, lineHeight:1.5}}>Two minutes: your mood and a quick mental health screen.</div>
                  </div>
                  <button style={{...C.btn(T.accent), fontSize:13, whiteSpace:"nowrap"}} onClick={()=>{ setTab("wellness"); setCheckInStep(1); }}>Start</button>
                </div>
              );
            })()}

            {/* ── TODAY (redesign Phase 1) ── subsumes the old Top Streaks
                card: SAME top-4-by-streak data, now with a check-off. Done
                state derives exactly as the habits tab does (lastLoggedDate
                vs local today — NEVER habits.completed), and the tap goes
                through logHabit(id), the one claim-guarded write path. */}
            {habits.length > 0 && (
              <div style={{...GC.card, padding:isMobile?"8px 20px 12px":"10px 28px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",minHeight:48}}>
                  <span style={{fontSize:16,fontWeight:600,color:T.text}}>Today</span>
                  <button onClick={()=>{ setTab("habits"); markTabVisited("habits"); }} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:14,fontWeight:600,color:T.accent,fontFamily:"inherit",minHeight:44,display:"flex",alignItems:"center",padding:0}}>All habits</button>
                </div>
                {[...habits].sort((a,b)=>b.streak-a.streak).slice(0,4).map(h=>{
                  const t=tmpl(h.id);
                  const doneToday = h.lastLoggedDate === localDateStr();
                  return (
                    <div key={h.id} style={{display:"flex",alignItems:"center",gap:14,padding:"10px 0",borderTop:`1px solid ${G.divider}`}}>
                      <button aria-label={doneToday?`${t?.label||h.id} done today`:`Mark ${t?.label||h.id} done`}
                        onClick={()=>{ if(!doneToday) logHabit(h.id); }}
                        style={{flexShrink:0,width:44,height:44,borderRadius:"50%",
                          border:doneToday?"none":`1.5px solid ${T.overlayLight(0.25)}`,
                          background:doneToday?T.success:T.overlayLight(0.04),
                          display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",
                          cursor:doneToday?"default":"pointer"}}>
                        {doneToday && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>}
                      </button>
                      <span style={{flexGrow:1,display:"flex",flexDirection:"column"}}>
                        <span style={{fontSize:16,fontWeight:500,color:T.text}}>{t?.label||h.id}</span>
                        <span style={{fontSize:13,color:doneToday?T.success:T.textMuted}}>
                          {doneToday?`Done${h.streak>0?` · ${h.streak} day streak`:""}`:(h.streak>0?`${h.streak} day streak`:"Not logged yet")}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={C.g()}>
              <div style={GC.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div style={C.cTitle}>Financial Snapshot</div>
                  <button style={{...C.ghost,padding:"4px 12px",fontSize:11}} onClick={openEditFinances}>Edit</button>
                </div>
                {(()=>{
                  if (plaidConnected && plaidAccounts.length > 0) {
                    const depAccts = plaidAccounts.filter(a=>a.type==="depository");
                    const creditAccts = plaidAccounts.filter(a=>a.type==="credit");
                    // TWO DELIBERATELY DIFFERENT BASES — not a bug:
                    // • availBal (available ?? current) is the spendable figure. It
                    //   feeds the "Available Balance" hero + the account chips, so
                    //   Home matches the Finance tab hero (:6592), which also uses
                    //   available ?? current. Available nets out pending holds.
                    // • depBal (current) is what you OWN, holds included. It feeds
                    //   net worth below, matching the Finance Net Worth card (:6337),
                    //   which sums current. Net worth on `available` would understate
                    //   it by the held amount and disagree with that card.
                    // Same account, two honest numbers for two different questions.
                    const availBal = depAccts.reduce((a,acc)=>a+(acc.balances?.available ?? acc.balances?.current ?? 0),0);
                    const depBal = depAccts.reduce((a,acc)=>a+(acc.balances?.current||0),0);
                    const creditUsed = creditAccts.reduce((a,acc)=>a+(acc.balances?.current||0),0);
                    const nw = depBal - creditUsed;
                    // The SAME derivations scoring and the finance tab read
                    // (plaidDerivedIncome/Expenses: 30d window, transfers and
                    // loan payments excluded). This card used to re-derive by
                    // sign alone, so a $2,000 credit-card payment showed as
                    // $2,000 of "spending" here while every other surface
                    // excluded it. Display-only change: effectiveIncome /
                    // effectiveExpenses — what the Money pillar actually
                    // scores from — were already built on these derivations.
                    const autoIncome = plaidDerivedIncome;
                    const autoSpending = plaidDerivedExpenses;
                    const cashflow = autoIncome - autoSpending;
                    return (
                      <>
                        {bankAttentionItems.length > 0 && (
                          <div style={{marginBottom:14,padding:"10px 12px",borderRadius:10,background:withAlpha(T.warning,0.08),border:`1px solid ${withAlpha(T.warning,0.25)}`}}>
                            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:6}}>{BANK_ATTENTION_TITLE}</div>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              {bankAttentionItems.map(it => (
                                <button key={it.item_id} onClick={()=>reconnectPlaidItem(it, it.institution_name || "your bank")} disabled={plaidLoading}
                                  style={{...C.btn(T.accent),fontSize:12,padding:"7px 12px"}}>
                                  {plaidLoading ? "Working..." : `Reconnect ${it.institution_name || "bank"}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div style={{marginBottom:14}}>
                          <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Available Balance</div>
                          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:26,fontWeight:400,color:T.success,letterSpacing:"-0.01em",lineHeight:1,marginBottom:8}}>${availBal.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                          {balanceAsOf && <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>{balanceAsOf}</div>}
                          <Bar value={availBal} max={Math.max(availBal*1.5,10000)} color={T.success} h={5}/>
                        </div>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                          {depAccts.map(a=>(
                            <div key={a.account_id} style={{fontSize:11,fontWeight:600,color:T.overlayLight(0.6),background:T.overlayLight(0.06),border:`1px solid ${T.overlayLight(0.09)}`,borderRadius:8,padding:"4px 10px",whiteSpace:"nowrap"}}>
                              {a.name.split(" ").slice(-1)[0]} · ${(a.balances?.available ?? a.balances?.current ?? 0).toLocaleString(undefined,{maximumFractionDigits:0})}
                            </div>
                          ))}
                          {creditUsed>0&&<div style={{fontSize:11,fontWeight:600,color:"rgba(220,50,50,0.75)",background:"rgba(220,50,50,0.07)",border:"1px solid rgba(220,50,50,0.15)",borderRadius:8,padding:"4px 10px",whiteSpace:"nowrap"}}>Credit · -${creditUsed.toLocaleString(undefined,{maximumFractionDigits:0})}</div>}
                        </div>
                        {autoIncome > 0 && (
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:14,paddingBottom:14,borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                            {[["Income (30d)",`$${Math.round(autoIncome).toLocaleString()}`,T.success],["Spending (30d)",`$${Math.round(autoSpending).toLocaleString()}`,T.danger],["Cashflow",cashflow>=0?`+$${Math.round(cashflow).toLocaleString()}`:`-$${Math.round(Math.abs(cashflow)).toLocaleString()}`,cashflow>=0?T.success:T.danger]].map(([l,v,col])=>(
                              <div key={l} style={{textAlign:"center"}}>
                                <div style={{fontSize:16,fontWeight:800,color:col}}>{v}</div>
                                <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{l}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                          <div style={{fontSize:11,color:T.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Savings on Hand</div>
                          <button style={{...C.ghost,fontSize:10,padding:"2px 8px"}} onClick={openEditFinances}>Edit</button>
                        </div>
                        {savingsKnown ? (
                          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,fontWeight:400,color:T.accent,marginBottom:6}}>${savings.toLocaleString()}</div>
                        ) : (
                          <button onClick={openEditFinances} style={{display:"block",textAlign:"left",background:"none",border:"none",padding:0,margin:"0 0 8px",fontSize:12,color:T.textMuted,lineHeight:1.5,cursor:"pointer",fontFamily:"inherit"}}>Not added yet. <span style={{color:T.accent,fontWeight:700}}>Add your savings balance</span>: cash, emergency fund, or anything outside your bank.</button>
                        )}
                        <div style={{fontSize:11,color:T.textMuted,display:"flex",justifyContent:"space-between",paddingTop:10,borderTop:`1px solid ${T.overlayLight(0.05)}`}}>
                          <span>Net Worth</span>
                          <span style={{fontWeight:700,color:nw>=0?T.success:"rgba(220,50,50,0.8)"}}>{fmtUSD(nw)}</span>
                        </div>
                      </>
                    );
                  }
                  if (monthlyIncome===0 && monthlyExpenses===0 && savings===0) {
                    return (
                      <div style={{textAlign:"center",padding:"20px 0"}}>
                        <div style={{fontSize:13,color:T.textMuted,marginBottom:6,lineHeight:1.6}}>Connect your bank for live data, or enter your numbers manually.</div>
                        <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap",marginTop:12}}>
                          <button style={{...C.btn(T.accent),fontSize:12}} onClick={connectPlaid}>Connect Bank</button>
                          <button style={{...C.ghost,fontSize:12,padding:"8px 16px"}} onClick={openEditFinances}>Enter Manually</button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:18}}>
                        {[["Income",`$${(effectiveIncome||monthlyIncome).toLocaleString()}`,T.success],["Expenses",`$${(effectiveExpenses||monthlyExpenses).toLocaleString()}`,T.danger],["Cashflow",`$${Math.max(0,(effectiveIncome||monthlyIncome)-(effectiveExpenses||monthlyExpenses)).toLocaleString()}`,T.accent]].map(([l,v,col])=>(
                          <div key={l} style={{textAlign:"center"}}><div style={{fontSize:18,fontWeight:800,color:col}}>{v}</div><div style={{fontSize:11,color:T.textMuted}}>{l}/mo</div></div>
                        ))}
                      </div>
                      <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Savings on Hand</div>
                      {savingsKnown ? (
                        <>
                          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,fontWeight:400,color:T.accent,marginBottom:8}}>${savings.toLocaleString()}</div>
                          <Bar value={savings} max={Math.max(savings*2,5000)} color={T.accent} h={5}/>
                        </>
                      ) : (
                        <button onClick={openEditFinances} style={{display:"block",textAlign:"left",background:"none",border:"none",padding:0,margin:"0 0 4px",fontSize:12,color:T.textMuted,lineHeight:1.5,cursor:"pointer",fontFamily:"inherit"}}>Not added yet. <span style={{color:T.accent,fontWeight:700}}>Add your savings balance</span>: cash, emergency fund, or anything outside your bank.</button>
                      )}
                      <button onClick={connectPlaid} style={{marginTop:14,width:"100%",background:"transparent",border:`1px solid ${withAlpha(T.accent,0.2)}`,borderRadius:8,padding:"8px",fontSize:11,fontWeight:600,color:T.accent,cursor:"pointer"}}>Connect Bank for Live Data →</button>
                    </>
                  );
                })()}
              </div>
            </div>
            <div style={GC.card}>
              <div style={C.cTitle}>Priority Actions</div>
              <div style={C.g()}>
                {(()=>{
                  const actions = [];
                  // Low medication refills
                  medications.forEach(m=>{
                    const _asNeeded=/as.?needed|prn|when needed/i;
                    if(_asNeeded.test(m.schedule||''))return;
                    if(!m.refill_date)return;
                    const _t=new Date();_t.setHours(0,0,0,0);
                    const _r=new Date(m.refill_date);
                    const _dl=Math.ceil((_r-_t)/(1000*60*60*24));
                    if(_dl<=0)actions.push({icon:"",text:`${m.name} refill overdue`,color:T.danger,action:"View",go:"health"});
                    else if(_dl<=7)actions.push({icon:"",text:`${m.name} refill in ${_dl} day${_dl===1?"":"s"}`,color:T.danger,action:"View",go:"health"});
                  });
                  // Overdue bills
                  bills.filter(b=>b.status==="overdue").forEach(b=>{
                    actions.push({icon:"",text:`${b.name} bill is overdue: $${(b.amount||0).toLocaleString()}`,color:T.danger,action:"View",go:"finances"});
                  });
                  // Overdue checkups
                  checkups.filter(ch=>ch.urgent).slice(0,2).forEach(ch=>{
                    actions.push({icon:"",text:`${ch.name} is overdue`,color:T.accent,action:"View",go:"health"});
                  });
                  // Habit streaks at risk — only streaks WORTH protecting (5+
                  // days), matching notify-cron's streak-milestone/at-risk floor.
                  // A 1-day "streak" is noise, not something to defend.
                  const todayDateStr = localDateStr();
                  const topStreak = [...habits].filter(h=>h.active&&h.streak>=5&&h.lastLoggedDate!==todayDateStr).sort((a,b)=>b.streak-a.streak)[0];
                  if (topStreak) {
                    const topT = tmpl(topStreak.id);
                    actions.push({icon:"",text:`Log ${topT?.label||topStreak.id} to keep your ${topStreak.streak}-day streak`,color:T.accent,action:"Log now",go:"habits"});
                  }
                  // Low savings alert — only when a savings balance is actually
                  // known (unknown must not read as $0 and trip a false alert).
                  if (savingsKnown && monthlyIncome>0 && savings < monthlyIncome) {
                    actions.push({icon:"",text:`Savings ($${savings.toLocaleString()}) below 1 month income. Consider boosting`,color:T.success,action:"View",go:"finances"});
                  }
                  // High debt-to-income
                  const totalDebt = debts.reduce((a,d)=>a+(d.balance||0),0);
                  if (monthlyIncome>0 && totalDebt > monthlyIncome*12) {
                    actions.push({icon:"",text:`Debt ($${totalDebt.toLocaleString()}) exceeds annual income. Focus on payoff`,color:T.accent,action:"View",go:"finances"});
                  }
                  // Upcoming bills in next 3 days — via the shared calendar-day math
                  // (local tz, month rollover, paid_for_month), not a raw due_day-today
                  // subtraction, which disagreed near month boundaries and read status.
                  const _billZone = (()=>{ try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } })();
                  billsNeedingAction(annotateBills(bills, _billZone), 3).forEach(b=>{
                    const when = b.daysUntil===0?"today":b.daysUntil===1?"tomorrow":`in ${b.daysUntil} days`;
                    actions.push({icon:"",text:`${b.name} due ${when}: $${(b.amount||0).toLocaleString()}`,color:T.accent,action:"View",go:"finances"});
                  });
                  // No data state
                  if (actions.length===0) {
                    actions.push({icon:"",text:"Everything looks good! Keep building your streaks.",color:T.success,action:"View habits",go:"habits"});
                  }
                  return actions.slice(0,5).map((a,i)=>(
                    <div key={i} style={{background:G.tileBg,borderRadius:12,padding:"12px 16px",display:"flex",alignItems:"center",gap:12,border:`1px solid ${G.divider}`}}>
                      <span style={{fontSize:22}}>{a.icon}</span>
                      <div style={{flex:1,fontSize:13,fontWeight:600}}>{a.text}</div>
                      <button onClick={()=>setTab(a.go)} style={{background:"transparent",border:`1px solid ${a.color}`,color:a.color,borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>{a.action}</button>
                    </div>
                  ));
                })()}
              </div>
            </div>

          {/* Goals overview widget — THE one Add Goal entry point in the app.
              Six scattered openers (two Finance sections, two Health, two
              Profile) were removed: headings like SAVINGS GOALS looked like
              filters but all opened the same untyped modal, so the button
              under Savings could mint a spending cap. Always rendered now —
              a gated card would leave new users with no way to add a goal. */}
          {(
            <div style={GC.card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={C.cTitle}> Active Goals</div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setTab("profile")} style={{...C.ghost,fontSize:11,padding:"4px 12px"}}>View all →</button>
                  <button onClick={()=>setShowAddGoal(true)} style={{...C.btn(T.accent),fontSize:11,padding:"5px 14px"}}>+ Add Goal</button>
                </div>
              </div>
              {goals.filter(g=>!g.completed).length === 0 && (
                <div style={{fontSize:13,color:T.textMuted,textAlign:"center",padding:"10px 0"}}>No active goals yet.</div>
              )}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {linkWorkoutGoals(goals, habits).filter(g=>!g.completed).slice(0,3).map(g=>{
                  const auto = getGoalAutoValue(g);
                  // Same unknown-state test GoalCard uses, so the two renderers cannot
                  // disagree about one goal: this widget used to print the resolver's
                  // placeholder value:0 as a confident "0 / 5" on the same data that made
                  // the Health tab say "syncing…". A value we know we do not have must
                  // never render as a number.
                  const noSource = auto?.noSource != null;
                  const isSyncing = auto?.label != null && auto?.hasData === false && !noSource;
                  const displayVal = isSyncing ? 0 : (auto?.value ?? g.current_value);
                  const resolvedTarget = auto?.target ?? g.period_target ?? g.target_value;
                  const pct = resolvedTarget > 0 && !isSyncing ? Math.min(100, Math.round((displayVal / resolvedTarget) * 100)) : 0;
                  const daysLeft = g.deadline ? Math.ceil((new Date(g.deadline)-new Date())/(1000*60*60*24)) : null;
                  const catColors = { fitness:T.success, finance:T.accent, health:T.accent, personal:T.accent };
                  // Cap-style goals (spending caps, screen-time limits, manual
                  // caps): crossing the limit is the BAD outcome, so it must read
                  // as over — danger color, "Over limit" — never as 100% success.
                  // Same rule as GoalCard so the two renderers cannot disagree.
                  const isInverted = auto?.inverted === true || g.source_config?.manual_shape === "cap";
                  const isOverBudget = isInverted && resolvedTarget > 0 && !isSyncing && displayVal > resolvedTarget;
                  const col = isOverBudget ? T.danger : (catColors[g.category] || T.accent);
                  return (
                    <div key={g.id} style={{background:G.tileBg,borderRadius:12,padding:"12px 16px",border:`1px solid ${G.divider}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:11,fontWeight:700,color:col,background:`${col}18`,border:`1px solid ${col}30`,borderRadius:6,padding:"2px 8px",textTransform:"uppercase"}}>{g.category}</span>
                          <span style={{fontSize:13,fontWeight:700}}>{g.title}</span>
                        </div>
                        <div style={{fontSize:12,fontWeight:800,color:col}}>{isOverBudget ? "Over limit" : (auto?.stateLine && auto?.hasData === false ? "—" : `${pct}%`)}</div>
                      </div>
                      <div style={{background:T.overlayLight(0.07),borderRadius:99,height:6,overflow:"hidden",marginBottom:4}}>
                        <div style={{width:`${pct}%`,background:`linear-gradient(90deg,${col},${col}99)`,height:"100%",borderRadius:99,transition:"width 0.8s"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textMuted}}>
                        <span style={{display:"flex",alignItems:"center",gap:4}}>
                          {/* stateLine (screen_time): the threshold source has no
                              totals — same rule as GoalCard, no bare numbers. */}
                          {auto?.stateLine
                            ? <>{auto.stateLine}</>
                            : noSource
                            ? <>— / {g.unit==="$"?"$":""}{Number(resolvedTarget||0).toLocaleString()} {g.unit!=="$"?g.unit:""} · <button onClick={auto?.noSource==="screen_time"?repairScreenTime:connectPlaid} style={{background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:11,cursor:"pointer",padding:0,fontFamily:"inherit"}}>{auto?.noSource==="screen_time"?"Allow Screen Time →":"Connect your bank →"}</button></>
                            : isSyncing
                            ? <>— / {g.unit==="$"?"$":""}{Number(resolvedTarget||0).toLocaleString()} {g.unit!=="$"?g.unit:""} · syncing…</>
                            : <>{g.unit==="$"?"$":""}{Number(displayVal).toLocaleString()} / {g.unit==="$"?"$":""}{Number(resolvedTarget).toLocaleString()} {g.unit!=="$"?g.unit:""}</>}
                          {auto?.label&&!isSyncing&&<span style={{color:T.textMuted,fontSize:10}}>· {auto.label}</span>}
                        </span>
                        {daysLeft !== null && <span style={{color:daysLeft<=7?T.danger:daysLeft<=30?T.accent:T.textMuted}}>{daysLeft > 0 ? `${daysLeft}d left` : "Overdue!"}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
    </div>
        )}

        {/* ── HABITS ── */}
        {contentTab==="habits"&&(
          <div style={{display:"flex",flexDirection:"column",gap:18}}>
            {/* ── HERO (Phase 2, TabHabits layout · settled tokens) ──
                Small pillar ring + serif title replaces the banner. VALUES
                are the exact expressions the banner read: pillarById.habits
                pct/band/buckets detail (pctText renders null as an em dash,
                never 0%). Ring color = the Habits pillar ring color. */}
            {(()=>{
              const p = pillarById.habits;
              const C0 = 2*Math.PI*38;
              const dash = p.pct===null ? 0 : C0*Math.min(100,Math.max(0,p.pct))/100;
              return (
                <>
                <div ref={heroRef} style={{display:"flex",alignItems:"center",gap:18,padding:"8px 2px 0",transformOrigin:"left top",willChange:"transform, opacity"}}>
                  <div style={{position:"relative",width:92,height:92,flexShrink:0}}>
                    <div aria-hidden="true" style={{position:"absolute",left:6,top:6,width:80,height:80,borderRadius:"50%",background:G.ringHalo}}/>
                    <svg width="92" height="92" viewBox="0 0 92 92" style={{position:"absolute",left:0,top:0}} aria-hidden="true">
                      <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringTrack} strokeWidth="10"/>
                      {dash>0 && <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringColors[2]} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${C0+20}`} transform="rotate(-90 46 46)"/>}
                    </svg>
                    <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Serif Display',serif",fontSize:22,color:p.band==='poor'?G.lowValue:T.text}}>{pctText(p.pct, p.placeholder)}</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                    <span style={{fontFamily:"'DM Serif Display',serif",fontSize:32,lineHeight:1.05,color:T.text}}>Habits</span>
                    {p.stale && p.computedAt && <span style={{fontSize:11,color:T.textMuted}}>Last updated {relativeTime(p.computedAt)}{p.pending ? " · updating…" : ""}</span>}
                    {/* The "Debt load & overdue checkups holding your score back"
                        line that stood here was copied from the Money hero and
                        read Money/Health inputs; removed 2026-10-13. */}
                    <span style={{fontSize:14,color:T.textMuted}}>{p.placeholder ? "Updating…" : p.pct===null ? "No habits yet" : `${habits.length} habit${habits.length===1?"":"s"} · ${p.pct}% average`}</span>
                    {p.pct!==null && (
                      <button onClick={()=>setShowHabitsBreakdown(s=>!s)} aria-expanded={showHabitsBreakdown} style={{minHeight:32,display:"flex",alignItems:"center",background:"transparent",border:"none",padding:0,cursor:"pointer",fontSize:14,fontWeight:600,color:T.accent,fontFamily:"inherit"}}>{showHabitsBreakdown?"Hide breakdown":"See breakdown"}</button>
                    )}
                  </div>
                </div>
                {/* ── HABITS BREAKDOWN (2026-10-13) ── the Money/Health/Mind
                    "See breakdown" pattern, but one row per HABIT rather than
                    per tracker (the pillar has a single tracker, so BucketRows
                    would be one uninformative row). Every number here comes
                    from the same helpers scoreHabits uses. */}
                {showHabitsBreakdown && p.pct!==null && (()=>{
                  const todayStr = localDateStr();
                  return (
                    <div style={GC.card}>
                      <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Last {HABIT_WINDOW_DAYS} days, against each habit's own target</div>
                      <div style={{fontSize:12,color:T.textMuted,lineHeight:1.55,marginBottom:10}}>Each habit scores its logs over the last {HABIT_WINDOW_DAYS} days against its own target: a 5-times-a-week habit needs 20 logs, a daily one 28, a monthly one its monthly count. Your Habits score is the <strong style={{color:T.text}}>average</strong> across your habits. Streaks are shown for motivation and do not affect the score.</div>
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {habits.map(h=>{
                          const t=tmpl(h.id);
                          if(!t) return null;
                          const need=habitTarget28(h, HABIT_TEMPLATES);
                          const logs=habitLogs28(h, todayStr);
                          const ratio=Math.min(1, need>0 ? logs/need : 0);
                          const hp=Math.round(ratio*100);
                          const loggedToday=h.lastLoggedDate===todayStr;
                          const nextPct=Math.round(Math.min(1,(logs+1)/Math.max(1,need))*100);
                          const left=Math.max(0, need-logs);
                          const moves = ratio>=1 ? `At target for the last ${HABIT_WINDOW_DAYS} days.`
                            : !loggedToday ? `Logging today takes this to ${nextPct}%.`
                            : `Logged today. ${left} more log${left===1?"":"s"} in the window reaches 100%.`;
                          return (
                            <div key={h.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 14px",borderRadius:10,background:T.overlayLight(0.05),border:"1px solid transparent"}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{t.label}</div>
                                <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{logs} of {need} logs in {HABIT_WINDOW_DAYS} days · target {t.target} {t.unit}</div>
                                <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{moves}</div>
                              </div>
                              <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                                <div style={{fontSize:16,fontWeight:800,color:hp>0?T.accent:T.textMuted}}>{hp}<span style={{fontSize:10,color:T.textMuted,fontWeight:400}}>%</span></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5,marginTop:10}}>Habits score = the average of these: {p.pct}% across {habits.length} habit{habits.length===1?"":"s"}.</div>
                    </div>
                  );
                })()}
              </>
              );
            })()}

            {/* Header row */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <h2 style={{fontSize:20,fontWeight:600}}>Active habits</h2>
              <button onClick={()=>setShowAdd(true)} style={{minHeight:44,padding:"0 18px",display:"flex",alignItems:"center",borderRadius:22,border:G.hairline,background:G.chipSelectedSurface,backdropFilter:G.blurChip,WebkitBackdropFilter:G.blurChip,boxShadow:G.chipShadow,color:T.accent,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Add habit</button>
            </div>

            {/* Habit cards */}
            <div style={C.g()}>
              {habits.length === 0 && (
                <div style={{...GC.card,textAlign:"center",padding:"40px 24px"}}>
                  <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text,marginBottom:8}}>Build your first habit</div>
                  <div style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.65}}>Small daily actions compound into big results.</div>
                  <button style={{...C.btn(T.accent),fontSize:14,padding:"12px 28px"}} onClick={()=>setShowAdd(true)}>Add a habit</button>
                </div>
              )}
              {habits.map(h=>{
                const t=tmpl(h.id);
                if (!t) return null;
                const pct=Math.min(h.weekCount/t.target,1);
                const done=pct>=1;
                // "+N pts" (streak × scorePerStreak) was removed 2026-10-13: it fed
                // nothing once the score moved to logs ÷ target over 28 days.
                const logged=justLogged===h.id;
                return(
                  <div key={h.id} className={logged?"popped":""} style={{...GC.card,border:done?`1px solid ${withAlpha(T.success,0.55)}`:GC.card.border,transition:"border 0.3s"}}
                    // Long-press (or right-click) opens the note, only once today is logged so it never fights the tap that logs it.
                    onTouchStart={()=>{ clearTimeout(habitPressTimer.current); habitPressTimer.current = setTimeout(()=>openHabitNote(h), 550); }}
                    onTouchEnd={()=>clearTimeout(habitPressTimer.current)} onTouchMove={()=>clearTimeout(habitPressTimer.current)} onTouchCancel={()=>clearTimeout(habitPressTimer.current)}
                    onContextMenu={e=>{ if (h.lastLoggedDate === localDateStr()) { e.preventDefault(); openHabitNote(h); } }}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        {t.icon && t.icon !== "·" && <span style={{fontSize:28}}>{t.icon}</span>}
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <div style={{fontSize:18,fontWeight:600}}>{t.label}</div>
                            {t.custom && <span style={{fontSize:9,fontWeight:700,color:T.accent,background:withAlpha(T.accent,0.15),border:`1px solid ${withAlpha(T.accent,0.2)}`,borderRadius:4,padding:"1px 5px"}}>CUSTOM</span>}
                          </div>
                          <div style={{fontSize:13,color:T.textMuted}}>{t.target} {t.unit}</div>
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"flex-start",gap:6}}>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:13,color:T.textMuted,whiteSpace:"nowrap"}}><span style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:h.streak>0?T.success:T.textDim}}>{h.streak}</span> day streak</div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:2}}>
                          <button onClick={()=>setEditHabit({...t,...h})} aria-label={`Edit ${t.label}`} title="Edit" style={{background:withAlpha(T.accent,0.15),border:`1px solid ${withAlpha(T.accent,0.2)}`,borderRadius:6,padding:"8px 9px",cursor:"pointer",color:T.accent,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {/* Feather edit-2, inline to match the bottom-nav icon set exactly (24 viewBox, currentColor, 1.8 stroke, round caps/joins). currentColor inherits T.accent. */}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,display:"block"}}><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                          </button>
                          <button onClick={()=>setDeleteHabitId(h.id)} aria-label={`Delete ${t.label}`} title="Delete" style={{background:withAlpha(T.danger,0.1),border:`1px solid ${withAlpha(T.danger,0.2)}`,borderRadius:6,padding:"8px 9px",cursor:"pointer",color:T.danger,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {/* Feather trash-2, same style; currentColor inherits T.danger. Opens the confirm modal (deleteHabitId), it does not delete on tap. */}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,display:"block"}}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <HabitRing target={t.target} completions={h.completions} streak={h.streak} target28={habitTarget28(h, HABIT_TEMPLATES)}/>
                    {/* Footer is LEFT-aligned (2026-10-13): the floating "Ask Kai"
                        pill is fixed to the bottom-RIGHT corner, and a
                        right-aligned "Log today" landed exactly under it
                        whenever the user stopped scrolling on this card. */}
                    <div style={{marginTop:10,display:"flex",justifyContent:"flex-start"}}>
                      {(()=>{
                        const todayStr = localDateStr();
                        const loggedToday = h.lastLoggedDate === todayStr;
                        if (loggedToday) {
                          const todayRow = isWorkoutHabit(h.id) ? workouts.find(w => String(w.date || "").slice(0, 10) === todayStr) : null;
                          const note = todayRow ? [todayRow.type, todayRow.duration_min ? `${todayRow.duration_min} min` : null, todayRow.notes ? `“${todayRow.notes}”` : null].filter(Boolean).join(" · ") : habitNoteFor(h.notes, todayStr);
                          const editing = habitNoteEdit && habitNoteEdit.id === h.id;
                          return (
                            <div style={{display:"flex",flexDirection:"column",gap:8,flex:1,minWidth:0}}>
                              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                                <div style={{background:withAlpha(T.success,0.10),border:`1px solid ${withAlpha(T.success,0.25)}`,borderRadius:22,padding:"9px 16px",fontSize:13,color:T.success,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                                  ✓ Done for today
                                </div>
                                {/* Optional note, never prompted: "+ note" after a tick (or long-press). */}
                                {!editing && (note
                                  ? <button onClick={()=>openHabitNote(h)} style={{background:"none",border:"none",padding:"6px 0",color:T.textMuted,fontSize:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"100%"}}>{todayRow ? note : `“${note}”`} <span style={{color:T.accent,fontWeight:700}}>Edit</span></button>
                                  : <button onClick={()=>openHabitNote(h)} style={{background:"none",border:"none",padding:"6px 0",color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>{isWorkoutHabit(h.id) ? "+ details" : "+ note"}</button>)}
                              </div>
                              {editing && (
                                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                                  <input value={habitNoteEdit.text} maxLength={HABIT_NOTE_MAX} placeholder="One line about today (optional)" aria-label={`Note for ${t.label}`} autoFocus
                                    onChange={e=>setHabitNoteEdit(x=>({...x,text:e.target.value}))}
                                    onKeyDown={async e=>{ if (e.key==="Enter") { if (await saveHabitNote(h.id, habitNoteEdit.date, habitNoteEdit.text)) setHabitNoteEdit(null); } if (e.key==="Escape") setHabitNoteEdit(null); }}
                                    style={{...C.inp,flex:1,minWidth:0,padding:"8px 10px",fontSize:13}}/>
                                  <button style={{...C.btn(T.accent),fontSize:12,padding:"8px 12px"}} onClick={async()=>{ if (await saveHabitNote(h.id, habitNoteEdit.date, habitNoteEdit.text)) setHabitNoteEdit(null); }}>Save</button>
                                  <button style={{...C.ghost,fontSize:12,padding:"8px 10px"}} onClick={()=>setHabitNoteEdit(null)}>Cancel</button>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return <button onClick={()=>{setLogModal(h.id);setLogVal(1);}} style={{minHeight:44,padding:"0 20px",display:"flex",alignItems:"center",borderRadius:22,background:T.accentFill,color:"#fff",border:"none",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>{done?"Log more":"Log today"}</button>;
                      })()}
                    </div>
                    {logged&&<div style={{marginTop:10,background:withAlpha(T.success,0.10),borderRadius:10,padding:"7px 10px",fontSize:12,color:T.success,fontWeight:700,textAlign:"center",animation:"fadeUp 0.3s ease"}}> Logged! Streak &amp; pillar score updated </div>}
                  </div>
                );
              })}
            </div>

            {/* ── RECOVERY STREAKS ── */}
            <div style={GC.card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div>
                  <div style={C.cTitle}> Recovery Streaks</div>
                  <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>Track what you're working to quit. Every day counts.</div>
                </div>
                <button style={{...C.btn(T.accent),fontSize:12,padding:"7px 14px"}} onClick={()=>setShowAddRecovery(true)}>+ Add</button>
              </div>

              {recoveryTrackers.length === 0 ? (
                <div style={{textAlign:"center",padding:"28px 0"}}>
                  <div style={{fontSize:36,marginBottom:10}}></div>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:6}}>Start a Recovery Streak</div>
                  <div style={{fontSize:12,color:T.textMuted,marginBottom:16}}>Track sobriety, quitting smoking, reducing screen time, or any habit you're fighting. No judgment — just progress.</div>
                  <button style={C.btn(T.accent)} onClick={()=>setShowAddRecovery(true)}>+ Start Tracking</button>
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:12,marginTop:12}}>
                  {recoveryTrackers.map(tracker => {
                    const days = Math.floor((new Date() - new Date(tracker.start_date)) / (1000*60*60*24));
                    const best = tracker.best_streak || 0;
                    const milestoneColor = days >= 365 ? T.accent : days >= 90 ? T.success : days >= 30 ? T.accent : days >= 7 ? T.accent : T.textMuted;
                    const nextMilestone = days < 1 ? 1 : days < 3 ? 3 : days < 7 ? 7 : days < 14 ? 14 : days < 30 ? 30 : days < 60 ? 60 : days < 90 ? 90 : days < 180 ? 180 : days < 365 ? 365 : null;
                    const pct = nextMilestone ? Math.round((days / nextMilestone) * 100) : 100;
                    return (
                      <div key={tracker.id} style={{background:G.tileBg,border:`1px solid ${milestoneColor}33`,borderRadius:14,padding:"16px 18px",borderLeft:`3px solid ${milestoneColor}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                          <div style={{display:"flex",alignItems:"center",gap:12}}>
                            <span style={{fontSize:28}}>{tracker.icon}</span>
                            <div>
                              <div style={{fontSize:15,fontWeight:800}}>{tracker.label}</div>
                              <div style={{fontSize:11,color:T.textMuted}}>Best: {best} days</div>
                            </div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:32,fontWeight:900,color:milestoneColor,lineHeight:1}}>{days}</div>
                            <div style={{fontSize:10,color:T.textMuted,fontWeight:700,letterSpacing:0.5}}>DAYS CLEAN</div>
                          </div>
                        </div>

                        {/* Milestone badges */}
                        <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                          {[1,3,7,14,30,90,180,365].map(m => {
                            const achieved = days >= m;
                            const lbl = m===365?"1 Year":m===180?"6 Mo":m===90?"90 Days":m===30?"30 Days":m===14?"2 Weeks":m===7?"1 Week":m===3?"3 Days":"1 Day";
                            return (
                              <div key={m} style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:99,
                                background: achieved ? `${milestoneColor}22` : "rgba(0,0,0,0.06)",
                                color: achieved ? milestoneColor : T.textDim,
                                border:`1px solid ${achieved ? milestoneColor+"55" : "rgba(0,0,0,0.08)"}`}}>
                                {achieved ? "✓ " : ""}{lbl}
                              </div>
                            );
                          })}
                        </div>

                        {/* Progress to next milestone */}
                        {nextMilestone && (
                          <div style={{marginBottom:12}}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textMuted,marginBottom:4}}>
                              <span>Next: {nextMilestone===365?"1 year":nextMilestone===14?"2 weeks":nextMilestone===7?"1 week":`${nextMilestone} ${nextMilestone===1?"day":"days"}`}</span>
                              <span style={{color:milestoneColor,fontWeight:700}}>{pct}%</span>
                            </div>
                            <Bar value={days} max={nextMilestone} color={milestoneColor} h={6}/>
                          </div>
                        )}

                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div style={{fontSize:11,color:T.textDim}}>
                            Since {new Date(tracker.start_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                          </div>
                          <div style={{display:"flex",gap:8}}>
                            <button onClick={()=>{if(window.confirm(`Reset your ${days}-day ${tracker.label} streak? The counter goes back to 0 and this cannot be undone.${days>best?` Your ${days}-day best will be saved.`:""}`))resetRecovery(tracker);}}
                              style={{fontSize:11,fontWeight:700,padding:"5px 12px",borderRadius:8,background:"rgba(248,113,113,0.1)",border:"1px solid rgba(248,113,113,0.3)",color:T.danger,cursor:"pointer"}}>
                              Reset
                            </button>
                            <button onClick={()=>{if(window.confirm(`Delete the "${tracker.label}" tracker? Your ${days}-day streak and ${best}-day best will be lost. This cannot be undone.`))deleteRecovery(tracker.id);}}
                              style={{fontSize:11,padding:"5px 10px",borderRadius:8,background:"transparent",border:`1px solid ${T.overlayLight(0.08)}`,color:T.textMuted,cursor:"pointer"}}>
                              ×
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{marginTop:16,padding:"10px 14px",background:G.tileBg,border:`1px solid ${G.divider}`,borderRadius:10,fontSize:11,color:T.textMuted,lineHeight:1.6}}>
                 Need support? <strong style={{color:T.accent}}>SAMHSA Helpline: 1-800-662-4357</strong> — free &amp; confidential, 24/7.
              </div>
            </div>

            {/* Leaderboard */}
            <div style={GC.card}>
              <div style={C.cTitle}>Your Streak Leaderboard</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {[...habits].sort((a,b)=>b.streak-a.streak).map((h,i)=>{
                  const t=tmpl(h.id);
                  const medals=["1","2","3"];
                  const barColor=i===0?T.accent:i===1?T.textMuted:T.warning;
                  return(
                    <div key={h.id} style={{display:"flex",alignItems:"center",gap:14,background:G.tileBg,borderRadius:12,padding:"12px 16px"}}>
                      <span style={{fontSize:20,width:28}}>{medals[i]||`#${i+1}`}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>{t.label}</div>
                        <Bar value={h.streak} max={Math.max(8,...habits.map(x=>x.streak))} color={barColor} h={4}/>
                      </div>
                      <Flame streak={h.streak}/>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        )}

        {/* ── FINANCES ── */}
        {contentTab==="finances"&&(()=>{
          const cleanName = n => (n||"").replace(/[^\x20-\x7E]/g,"").trim();
          // fmtUSD is module-level now (sign outside the $) — shared with the
          // Home net-worth line so the two never diverge on negatives.
          // localDateStr, not toISOString(): Plaid transaction dates are LOCAL
          // calendar dates, and a UTC boundary string shifts the window edge by a
          // day for anyone west of UTC — at 17:00 in Los Angeles it is already
          // tomorrow in UTC, so the oldest day silently drops out of the window
          // and the 7- and 30-day spend totals under-report.
          const thirtyDaysAgo = daysAgoStr(30);
          const sevenDaysAgo = daysAgoStr(7);
          const thisWeekTx = plaidTransactions.filter(t => txSince(t, sevenDaysAgo) && isSpendTx(t));
          const thisWeekSpent = thisWeekTx.reduce((a,t) => a + t.amount, 0);
          const thisMonthTx = plaidTransactions.filter(t => txSince(t, thirtyDaysAgo));
          // ONE income/expense derivation, shared with the Home snapshot, Kai and
          // the Money pillar — the isSpendTx / getGoalAutoValue pattern applied to
          // cashflow. This card used to carry its OWN moneyIn (t.amount<0 minus
          // SPEND_EXCLUDED, no account restriction), which diverged two ways: it
          // DROPPED mobile-deposited paychecks (TRANSFER_IN_DEPOSIT — the exact
          // failure plaidDerivedIncome's comment documents) while COUNTING a
          // credit-card refund as income (no depository filter). Result: $3,027
          // here vs $6,641 on Home for the same 30 days. plaidDerivedIncome is the
          // canonical figure (30d, depository-only, loose on TRANSFER_IN so mobile
          // paychecks count, with the income_rules confirmation layer on top);
          // plaidDerivedExpenses is byte-identical to the old moneyOut (both 30d,
          // amount>0, minus {TRANSFER_OUT,TRANSFER_IN,LOAN_PAYMENTS}) — which is
          // why spending already agreed across both tabs. netSaved stays in-out.
          // CAVEAT: TRANSFER_IN_FROM_APPS (Venmo/Zelle, incl. self-transfers) is
          // still counted by the loose default; removing that over-count through
          // the income_rules confirmation flow is logged in docs/PLAN.md Phase A.
          const moneyIn  = plaidDerivedIncome;
          const moneyOut = plaidDerivedExpenses;
          const netSaved = moneyIn - moneyOut;
          const netWorth = sumMoney(plaidAccounts, acc => (acc.type === "credit" ? -1 : 1) * (acc.balances?.current || 0));
          // Spending by Category. This summed the ENTIRE Plaid cache with no
          // date filter at all — the card said "30 days" while showing $24,493
          // of all-time totals (441 transactions back to June), with loan
          // payments as the top "category". Windowed + spend-filtered now.
          const cats = {};
          thisMonthTx.filter(isSpendTx).forEach(t => {
            const label = spendCategoryLabel(t);
            cats[label] = (cats[label]||0) + t.amount;
          });
          const catSorted = Object.entries(cats).sort((a,b)=>b[1]-a[1]);
          const catMax = catSorted[0]?.[1] || 1;
          const catTotal = catSorted.reduce((a,[,v])=>a+v,0) || 1;
          const visibleTx = plaidTransactions.slice(0, plaidTxLimit);
          let insight = null;
          if (catSorted.length > 0 && moneyIn > 0) {
            const [topCat, topAmt] = catSorted[0];
            const pct = Math.round(topAmt / moneyIn * 100);
            insight = `You spent ${pct}% of your income on ${topCat.toLowerCase()} this month, your highest category.`;
          } else if (catSorted.length > 0) {
            insight = `${catSorted[0][0]} is your top spending category at ${fmtUSD(catSorted[0][1])} over the last 30 days.`;
          }
          const FIN_SUB_TABS = [
            {id:"overview",  label:"Overview"},
            {id:"spending",  label:"Spending"},
            {id:"bills",     label:"Bills & Goals"},
            {id:"accounts",  label:"Accounts"},
          ];

          return (
          <div style={{display:"flex",flexDirection:"column",gap:18}}>

            {/* ── HERO (Phase 4, TabFinance layout · settled tokens) ──
                Small pillar ring + serif title replaces the banner. VALUES
                are the exact fields the banner's PillarHeader read:
                pillarById.finance via pctText (null renders as an em dash,
                never 0%). "See breakdown" toggles the SAME
                showFinanceBreakdown + BucketRows. */}
            {(()=>{
              const fp = pillarById.finance;
              const C0 = 2*Math.PI*38;
              const dash = fp.pct===null ? 0 : C0*Math.min(100,Math.max(0,fp.pct))/100;
              return (
                <>
                  <div ref={heroRef} style={{display:"flex",alignItems:"center",gap:18,padding:"8px 2px 0",transformOrigin:"left top",willChange:"transform, opacity"}}>
                    <div style={{position:"relative",width:92,height:92,flexShrink:0}}>
                      <div aria-hidden="true" style={{position:"absolute",left:6,top:6,width:80,height:80,borderRadius:"50%",background:G.ringHalo}}/>
                      <svg width="92" height="92" viewBox="0 0 92 92" style={{position:"absolute",left:0,top:0}} aria-hidden="true">
                        <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringTrack} strokeWidth="10"/>
                        {dash>0 && <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringColors[0]} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${C0+20}`} transform="rotate(-90 46 46)"/>}
                      </svg>
                      <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Serif Display',serif",fontSize:22,color:fp.band==='poor'?G.lowValue:T.text}}>{pctText(fp.pct, fp.placeholder)}</span>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                      <span style={{fontFamily:"'DM Serif Display',serif",fontSize:32,lineHeight:1.05,color:T.text}}>Money</span>
                    {fp.stale && fp.computedAt && <span style={{fontSize:11,color:T.textMuted}}>Last updated {relativeTime(fp.computedAt)}{fp.pending ? " · updating…" : ""}</span>}
                      <span style={{fontSize:14,color:T.textMuted}}>{fp.placeholder ? "Updating…" : fp.pct===null ? "No score yet" : `${fp.trackedCount} of ${fp.totalCount} trackers · ${fp.earned} of ${fp.possible} points`}</span>
                      {fp.pct===null && (()=>{
                        // WHY there is no Money score (2026-10-14): each enabled tracker
                        // that cannot score says what it needs; a bills tracker left off
                        // while bills exist is the common onboarding-order gap.
                        const reasons = [...new Set(fp.buckets.filter(b=>b.state!=="scored").map(b=>b.detail).filter(d=>d && d!=="Not tracked"))];
                        const billsOff = bills.length>0 && !enrolledSet.has("bills_timeliness");
                        return (
                          <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5,marginTop:2}}>
                            {reasons.length ? reasons.slice(0,2).join(". ") : "Connect a bank, or add your monthly income and expenses"}.
                            {billsOff && <> You have {bills.length} bill{bills.length===1?"":"s"} tracked: <button onClick={()=>toggleTracker("bills_timeliness")} style={{background:"none",border:"none",padding:0,color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>turn on Bills On Time</button> to score them.</>}
                          </div>
                        );
                      })()}
                      <button onClick={()=>setShowFinanceBreakdown(s=>!s)} aria-expanded={showFinanceBreakdown} style={{minHeight:32,display:"flex",alignItems:"center",background:"transparent",border:"none",padding:0,cursor:"pointer",fontSize:14,fontWeight:600,color:T.accent,fontFamily:"inherit"}}>{showFinanceBreakdown?"Hide breakdown":"See breakdown"}</button>
                    </div>
                  </div>
                  {showFinanceBreakdown && (
                    <div style={GC.card}>
                      <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Trackers</div>
                      <div style={{fontSize:12,color:T.textMuted,lineHeight:1.55,marginBottom:10}}>Your Money score is mostly about <strong style={{color:T.text}}>behavior</strong> \u2014 cash flow (spending less than you earn), budget adherence, paying bills on time, and debt. A savings cushion is a small part, measured in <strong style={{color:T.text}}>months of expenses</strong>, and even a little saved this month earns credit. A $0 cushion is a starting point, not a mark against you.</div>
                      <BucketRows buckets={fp.buckets}/>
                      <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5,marginTop:10}}>Trackers shown as {"\u2014"} are not enabled. They are left out of this pillar's score entirely, so turning one off never costs you points.</div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Sub-tab segmented control (glass, mockup layout) */}
            <div style={{padding:4,borderRadius:24,border:G.hairline,background:G.chipSurface,backdropFilter:G.blurChip,WebkitBackdropFilter:G.blurChip,boxShadow:G.chipShadow,display:"flex",gap:2}}>
              {FIN_SUB_TABS.map(st=>(
                <button key={st.id} onClick={()=>setFinanceSubTab(st.id)} aria-pressed={financeSubTab===st.id}
                  style={{flexGrow:1,minHeight:40,padding:"0 6px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:20,fontSize:12.5,fontWeight:600,whiteSpace:"nowrap",border:"none",cursor:"pointer",fontFamily:"inherit",
                    background:financeSubTab===st.id?T.accentFill:"transparent",
                    color:financeSubTab===st.id?"#fff":T.textMuted}}>
                  {st.label}
                </button>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:-8}}>
              <button
                onClick={()=>{ lastPlaidCall.current=0; setPlaidRetry(n=>n+1); }}
                disabled={plaidFetching}
                style={{background:"none",border:`1px solid ${plaidRefreshMsg==="updated"?withAlpha(T.success,0.4):withAlpha(T.accent,0.2)}`,borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:600,color:plaidRefreshMsg==="updated"?T.success:T.accent,cursor:"pointer",opacity:plaidFetching?0.4:1}}>
                {plaidFetching ? "Refreshing..." : plaidRefreshMsg==="updated" ? "Updated ✓" : plaidRefreshMsg==="balance_stale" ? "Refreshed" : "Refresh"}
              </button>
            </div>
            {/* Honest result of a manual refresh — shown even when connected
                (the old error banner below was gated on !plaidConnected, so a
                connected user's rate-limit or partial refresh was silent). */}
            {!isDemoMode && !plaidFetching && plaidRateLimited && (
              <div style={{fontSize:11,color:T.warning,textAlign:"right",marginTop:4}}>
                {plaidRateLimited.message}
              </div>
            )}
            {!isDemoMode && !plaidFetching && !plaidRateLimited && (plaidRefreshMsg==="rate_limited"||plaidRefreshMsg==="balance_stale") && (
              <div style={{fontSize:11,color:T.textMuted,textAlign:"right",marginTop:4}}>
                Transactions updated. Balances are on today&rsquo;s refresh limit and did not change.
              </div>
            )}
            {!isDemoMode && !plaidFetching && !plaidRateLimited && plaidRefreshMsg==="failed" && (
              <div style={{fontSize:11,color:T.warning,textAlign:"right",marginTop:4}}>
                Transactions updated. The balance update did not go through; it will retry on your next refresh.
              </div>
            )}

            {/* Loading skeleton */}
            {!isDemoMode && plaidFetching && (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {[1,2,3].map(i=>(
                  <div key={i} style={{...GC.card,height:76,background:`linear-gradient(90deg,${T.overlayLight(0.04)} 25%,${T.overlayLight(0.08)} 50%,${T.overlayLight(0.04)} 75%)`,backgroundSize:"200% 100%",animation:"shimmer 1.5s infinite",border:`1px solid ${T.overlayLight(0.06)}`}}/>
                ))}
              </div>
            )}

            {/* Plaid error */}
            {/* Expired connection. Deliberately offers no retry: the bank has
                invalidated this link, so every retry returns the same error. The
                only real fix is reconnecting, which is why this points at
                Disconnect rather than at a button that cannot work. */}
            {!isDemoMode && !plaidFetching && plaidReauth && (()=>{
              // Per-bank (multi-bank, 2026-10-13): name the broken bank(s) and
              // reconnect ONLY those Items; the healthy banks are never touched.
              const broken = plaidItems.filter(i => i.needs_reauth);
              const nameOf = (it) => it.institution_name || (plaidItems.length > 1 ? `Bank ${plaidItems.indexOf(it) + 1}` : "Your bank");
              const title = broken.length === 0 ? "Your bank needs to be reconnected"
                : broken.length === 1 ? `${nameOf(broken[0])} needs to be reconnected`
                : `${broken.map(nameOf).join(" and ")} need to be reconnected`;
              return (
              <div style={{...GC.card,padding:"16px 14px",fontSize:12.5,lineHeight:1.6,color:T.text}}>
                <div style={{fontWeight:700,color:T.accent,marginBottom:6}}>{title}</div>
                The bank ended this connection, which usually happens after a password change,
                a new security check, or the bank&rsquo;s periodic re-approval. Its balances and
                transactions will stay out of date until it is reconnected.{plaidItems.length > 1 ? " Your other banks are not affected." : ""}
                {broken.length > 0 ? broken.map(it => (
                  <button key={it.item_id}
                    style={{...C.btn(T.accent),width:"100%",marginTop:12,padding:"11px",fontSize:13}}
                    disabled={plaidLoading}
                    onClick={()=>reconnectPlaidItem(it, nameOf(it))}>
                    {plaidLoading ? "Working..." : `Reconnect ${nameOf(it)}`}
                  </button>
                )) : (
                  // No per-bank detail (an older response): the single-bank path.
                  <button
                    style={{...C.btn(T.accent),width:"100%",marginTop:12,padding:"11px",fontSize:13}}
                    disabled={plaidLoading}
                    onClick={async()=>{ setPlaidReauth(false); await disconnectPlaid(); connectPlaid(); }}>
                    {plaidLoading ? "Working..." : "Reconnect bank"}
                  </button>
                )}
                <div style={{marginTop:8,color:T.textMuted}}>
                  Or use <strong style={{color:T.text}}>Disconnect</strong> under that bank in the Accounts section below, then link it again.
                </div>
              </div>
              );
            })()}
            {/* Bank needs attention (2026-10-29, src/lib/plaidItemHealth.js): an
                Item error from Plaid, or no Plaid refresh from the bank in 24h.
                The reauth banner above already covers ITEM_LOGIN_REQUIRED Items. */}
            {!isDemoMode && !plaidFetching && bankAttentionItems.some(i => !i.needs_reauth) && (()=>{
              const list = bankAttentionItems.filter(i => !i.needs_reauth);
              const nameOf = (it) => it.institution_name || (plaidItems.length > 1 ? `Bank ${plaidItems.indexOf(it) + 1}` : "Your bank");
              return (
              <div style={{...GC.card,padding:"16px 14px",fontSize:12.5,lineHeight:1.6,color:T.text}}>
                <div style={{fontWeight:700,color:T.accent,marginBottom:6}}>{BANK_ATTENTION_TITLE}</div>
                {list.map(it => <div key={it.item_id}>{bankAttentionLine(it, nameOf(it))}</div>)}
                <div style={{color:T.textMuted,marginTop:4}}>{BANK_ATTENTION_HINT}</div>
                {list.map(it => (
                  <button key={it.item_id}
                    style={{...C.btn(T.accent),width:"100%",marginTop:12,padding:"11px",fontSize:13}}
                    disabled={plaidLoading}
                    onClick={()=>reconnectPlaidItem(it, nameOf(it))}>
                    {plaidLoading ? "Working..." : `Reconnect ${nameOf(it)}`}
                  </button>
                ))}
              </div>
              );
            })()}
            {/* Generic refresh failure. No longer gated on !plaidConnected: a
                connected user whose refresh fails must see it, not silently
                keep stale data. The wording adapts — a connected user still has
                their last-saved data to fall back on. */}
            {!isDemoMode && !plaidFetching && plaidError && !plaidReauth && (
              <div style={{...GC.card,textAlign:"center",padding:"18px 0",color:T.textMuted,fontSize:12}}>
                {plaidConnected ? "Couldn't refresh — showing your last saved data." : "Unable to load bank data."}{" "}
                <button style={{color:T.accent,cursor:"pointer",background:"none",border:"none",font:"inherit",padding:"10px 8px"}} onClick={()=>{ setPlaidError(false); lastPlaidCall.current=0; setPlaidRetry(n=>n+1); }}>Tap to retry.</button>
              </div>
            )}

            {/* Income confirmation — one candidate at a time, once per name ever.
                Answering either way writes an income_rules row and the card is
                gone for that name; a user who never answers keeps today's
                numbers untouched. Never rendered in demo (guarded here AND in
                the incomeCandidates derivation). */}
            {!isDemoMode && user && incomeCandidates.length > 0 && (()=>{
              const c = incomeCandidates[0];
              return (
                <div style={{...GC.card,padding:"16px 18px"}}>
                  <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Is this your income?</div>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:2}}>{cleanName(c.displayName)}</div>
                  <div style={{fontSize:12.5,color:T.textMuted,marginBottom:12}}>~{fmtUSD(Math.round(c.avg))}{incomeType.type==="irregular" ? " per deposit" : ` · ${c.cadence}`}</div>
                  <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5,marginBottom:12}}>
                    Your bank labels this a transfer, so we can&rsquo;t tell on our own. Confirming keeps your income number right; you&rsquo;ll only be asked once.
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button style={{...C.btn(T.accent),flex:1,fontSize:12,padding:"9px 0"}} onClick={()=>confirmIncomeRule(c.matchName,"income")}>Yes, it&rsquo;s income</button>
                    <button style={{...C.ghost,flex:1,fontSize:12,padding:"9px 0"}} onClick={()=>confirmIncomeRule(c.matchName,"not_income")}>No, it&rsquo;s not</button>
                  </div>
                </div>
              );
            })()}

            {/* ── SUBSCRIPTIONS (2026-10-20) ── the monthly total of detected
                recurring charges, and "Recurring charges we found": each
                merchant asked ONCE (recurring_rules), mirroring the income
                card. Add to bills (monthly) uses the pattern's usual day and
                auto-marks paid when the matching charge lands. Nothing is
                added without a tap. Never rendered in demo mode.
                Placement (src/lib/financeLayout.js): the full card on Bills &
                Goals only; Overview gets one compact line that opens it. */}
            {!isDemoMode && user && (plaidConnected || subscriptionBills.length > 0) && subscriptionsPlacement(financeSubTab) === "compact" && (
              <button onClick={()=>{
                  setFinanceSubTab("bills");
                  // Scroll once Bills & Goals has rendered the card.
                  setTimeout(()=>{ try { document.getElementById(SUBSCRIPTIONS_CARD_ID)?.scrollIntoView({ behavior:"smooth", block:"start" }); } catch {} }, 60);
                }}
                style={{...GC.card,padding:"12px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",cursor:"pointer",fontFamily:"inherit",textAlign:"left",border:GC.card.border}}>
                <span style={{fontSize:13,color:T.textMuted,fontWeight:600}}>Subscriptions</span>
                <span style={{fontSize:14,fontWeight:700,color:T.text}}>{fmtUSDExact(subscriptionsTotal)}<span style={{fontSize:12,color:T.textMuted,fontWeight:500}}>/mo</span> <span aria-hidden="true" style={{color:T.accent}}>→</span></span>
              </button>
            )}
            {!isDemoMode && user && (plaidConnected || subscriptionBills.length > 0) && subscriptionsPlacement(financeSubTab) === "full" && (()=>{
              const WD = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
              const today = localDateStr(), month = currentMonth();
              const subsCardOpen = subsOpen ?? subscriptionsSummary({ total: subscriptionsTotal, toReview: recurringAsk.length }).defaultOpen;
              const suggestions = subAdd && !subAdd.matchName ? suggestMerchants(plaidTransactions, subAdd.name, { today }) : [];
              const MONTHS3 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              const monthInput = (value, onChange) => <select value={value} onChange={e=>onChange(e.target.value)} aria-label="Month" style={{...C.inp,padding:"8px 10px",fontSize:13}}>{MONTHS3.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select>;
              const dayInput = (cadence, value, onChange) => cadence === "weekly"
                ? <select value={value} onChange={e=>onChange(e.target.value)} style={{...C.inp,padding:"8px 10px",fontSize:13}}>{WD.map((w,i)=><option key={w} value={i}>{w}</option>)}</select>
                : <input type="number" min="1" max="31" value={value} onChange={e=>onChange(e.target.value)} placeholder="Day" style={{...C.inp,padding:"8px 10px",fontSize:13,width:80}}/>;
              return (
              <div id={SUBSCRIPTIONS_CARD_ID} style={{...GC.card,padding:"16px 18px",scrollMarginTop:80}}>
                {/* The whole card collapses to one line (open by default only with charges to review). */}
                <button onClick={()=>setSubsOpen(!subsCardOpen)} aria-expanded={subsCardOpen}
                  style={{display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",background:"transparent",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",minHeight:40,marginBottom:subsCardOpen?4:0,gap:8}}>
                  {subsCardOpen
                    ? <span style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em"}}>Subscriptions</span>
                    : <span style={{fontSize:13,color:T.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{subscriptionsSummary({ total: subscriptionsTotal, toReview: recurringAsk.length }).line}</span>}
                  <span style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                    {subsCardOpen && <span style={{fontSize:18,fontWeight:800,color:T.text}}>{fmtUSDExact(subscriptionsTotal)}<span style={{fontSize:12,color:T.textMuted,fontWeight:500}}>/mo</span></span>}
                    <span aria-hidden="true" style={{color:T.accent,fontSize:16,transform:subsCardOpen?"rotate(90deg)":"none",transition:"transform 0.15s"}}>›</span>
                  </span>
                </button>
                {/* Detected-but-unanswered charges: their own prompt, above the list. */}
                {subsCardOpen && recurringAsk.length > 0 && (
                  <>
                    <div style={{fontSize:13,fontWeight:700,color:T.text,margin:"8px 0 2px"}}>Recurring charges we found</div>
                    <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5,marginBottom:6}}>You'll only be asked once about each.</div>
                    {recurringAsk.slice(0,5).map(p=>(
                      <div key={p.matchName} style={{padding:"10px 0",borderTop:`1px solid ${T.border}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10}}>
                          <div style={{fontSize:14,fontWeight:700,color:T.text,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cleanName(p.displayName)}</div>
                          <div style={{fontSize:13,fontWeight:700,color:T.text,flexShrink:0}}>~{fmtUSDExact(p.typicalAmount)}</div>
                        </div>
                        <div style={{fontSize:12,color:T.textMuted,marginTop:2,marginBottom:8}}>
                          {p.cadence==="weekly" ? `Weekly, usually ${WD[p.weekday]}` : `Monthly, usually around the ${ordinal(p.usualDay)}`}
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          {p.cadence==="monthly"
                            ? <button style={{...C.btn(T.accent),flex:1,fontSize:12,padding:"9px 0"}} onClick={()=>answerRecurring(p,"bill")}>Add to bills</button>
                            : <button style={{...C.btn(T.accent),flex:1,fontSize:12,padding:"9px 0"}} onClick={()=>answerRecurring(p,"recurring")}>Yes, it&rsquo;s recurring</button>}
                          <button style={{...C.ghost,flex:1,fontSize:12,padding:"9px 0"}} onClick={()=>answerRecurring(p,"not_bill")}>Not a bill</button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {subsCardOpen && (
                  <div style={{marginTop:recurringAsk.length?12:4}}>
                    {subscriptionBills.length === 0 && <div style={{fontSize:12,color:T.textMuted,padding:"6px 0"}}>No confirmed subscriptions yet.</div>}
                    {subscriptionBills.map(b=>{
                      const r = subscriptionRow(b, { today, month });
                      const editing = subEdit && subEdit.id === b.id;
                      return (
                        <div key={b.id} style={{padding:"10px 0",borderTop:`1px solid ${T.border}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10}}>
                            <div style={{fontSize:14,fontWeight:700,color:T.text,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cleanName(b.name)}</div>
                            <div style={{fontSize:13,fontWeight:700,color:T.text,flexShrink:0}}>{fmtUSDExact(Number(b.amount)||0)}<span style={{fontSize:11,color:T.textMuted,fontWeight:500}}>{b.cadence==="weekly"?"/wk":b.cadence==="yearly"?"/yr":"/mo"}</span></div>
                          </div>
                          <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>
                            {b.cadence==="weekly" ? `Every ${WD[b.weekday]||"week"}` : b.cadence==="yearly" ? `Yearly, ${MONTHS3[(b.due_month||1)-1]} ${b.due_day}` : `Usually the ${ordinal(b.due_day)}`}
                            {r.nextDate ? ` · Next ${fmtDueDay(r.nextDate, today)}` : ""}
                            {r.paidThisMonth===true ? <span style={{color:T.success,fontWeight:700}}> · Paid this month</span> : r.paidThisMonth===false ? " · Not charged yet this month" : ""}
                            {r.paidThisYear===true ? <span style={{color:T.success,fontWeight:700}}> · Paid this year</span> : r.paidThisYear===false ? " · Not charged yet this year" : ""}
                          </div>
                          {editing ? (
                            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:8}}>
                              <input type="number" value={subEdit.amount} onChange={e=>setSubEdit(x=>({...x,amount:e.target.value}))} style={{...C.inp,padding:"8px 10px",fontSize:13,width:100}} aria-label="Amount"/>
                              {b.cadence==="yearly" && monthInput(subEdit.month, v=>setSubEdit(x=>({...x,month:v})))}
                              {dayInput(b.cadence, subEdit.day, v=>setSubEdit(x=>({...x,day:v})))}
                              <button style={{...C.btn(T.accent),fontSize:12,padding:"8px 14px"}} onClick={async()=>{ if (await saveSubscriptionEdit(b, subEdit)) setSubEdit(null); }}>Save</button>
                              <button style={{...C.ghost,fontSize:12,padding:"8px 12px"}} onClick={()=>setSubEdit(null)}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{display:"flex",gap:14,marginTop:6}}>
                              <button onClick={()=>setSubEdit({ id:b.id, amount:String(b.amount ?? ""), day:String(b.cadence==="weekly" ? (b.weekday ?? 0) : (b.due_day ?? "")), month:String(b.due_month ?? 1) })} style={{background:"none",border:"none",padding:0,color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                              <button onClick={()=>removeSubscription(b)} style={{background:"none",border:"none",padding:0,color:T.textMuted,fontWeight:600,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Not a subscription</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Manual add: counts in the total and shows on the bill
                        calendar; auto-marks paid ONLY with a merchant picked
                        from recent transactions (match_name). */}
                    {subAdd ? (
                      <div style={{padding:"12px 0 0",borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:8}}>
                        {/* Typing suggests merchants from the last 90 days of charges;
                            picking one fills amount, usual day and the merchant
                            link (so it auto-marks paid). Retyping drops the link. */}
                        <input value={subAdd.name} onChange={e=>setSubAdd(x=>({...x,name:e.target.value,matchName:null}))} placeholder="Name (e.g. Netflix)" style={{...C.inp,width:"100%"}} aria-label="Subscription name"/>
                        {suggestions.length > 0 && (
                          <div style={{display:"flex",flexDirection:"column",gap:4}}>
                            {suggestions.map(m=>(
                              <button key={m.matchName} onClick={()=>setSubAdd(x=>({...x,name:cleanName(m.displayName),amount:String(m.amount),day:String(m.usualDay),cadence:"monthly",matchName:m.matchName}))}
                                style={{display:"flex",justifyContent:"space-between",gap:8,textAlign:"left",background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,color:T.text}}>
                                <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cleanName(m.displayName)}</span>
                                <span style={{color:T.textMuted,flexShrink:0}}>{fmtUSDExact(m.amount)} · {fmtDueDay(m.lastDate, today)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {subAdd.matchName && <div style={{fontSize:11,color:T.success}}>{"\u25CF"} Linked to your {cleanName(subAdd.name)} charges, so it marks itself paid.</div>}
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                          <input type="number" value={subAdd.amount} onChange={e=>setSubAdd(x=>({...x,amount:e.target.value}))} placeholder="Amount" style={{...C.inp,padding:"8px 10px",fontSize:13,width:100}}/>
                          <div style={{display:"flex",gap:4}}>
                            {[["monthly","Monthly"],["weekly","Weekly"],["yearly","Yearly"]].map(([v,l])=>(
                              <button key={v} onClick={()=>setSubAdd(x=>({...x,cadence:v,day:v==="weekly"?"5":"1",month:x.month||String(new Date().getMonth()+1)}))} aria-pressed={subAdd.cadence===v}
                                style={{padding:"8px 10px",borderRadius:10,border:`1px solid ${subAdd.cadence===v?T.accent:T.border}`,background:subAdd.cadence===v?withAlpha(T.accent,0.1):T.surfaceAlt,color:subAdd.cadence===v?T.accent:T.textMuted,fontSize:12,fontWeight:subAdd.cadence===v?700:500,cursor:"pointer"}}>{l}</button>
                            ))}
                          </div>
                          {subAdd.cadence==="yearly" && monthInput(subAdd.month||"1", v=>setSubAdd(x=>({...x,month:v})))}
                          {dayInput(subAdd.cadence, subAdd.day, v=>setSubAdd(x=>({...x,day:v})))}
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button style={{...C.btn(T.accent),flex:1,fontSize:12,padding:"9px 0"}} disabled={!subAdd.name.trim()||!(parseFloat(subAdd.amount)>0)} onClick={async()=>{ if (await addManualSubscription(subAdd)) setSubAdd(null); }}>Add subscription</button>
                          <button style={{...C.ghost,flex:1,fontSize:12,padding:"9px 0"}} onClick={()=>setSubAdd(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={()=>setSubAdd({ name:"", amount:"", cadence:"monthly", day:"1", matchName:null })} style={{display:"block",width:"100%",marginTop:8,background:"transparent",border:`1px dashed ${T.borderStrong}`,color:T.textMuted,borderRadius:10,padding:"10px",fontSize:13,fontWeight:600,cursor:"pointer"}}>+ Add subscription</button>
                    )}
                    {/* Possible subscriptions: a looser net (src/lib/subscriptions.js
                        possibleSubscriptions), each with a plain Add. */}
                    {possibleSubs.length > 0 && (
                      <div style={{marginTop:16}}>
                        <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:2}}>Possible subscriptions</div>
                        {possibleSubs.slice(0,8).map(p=>(
                          <div key={p.matchName} style={{padding:"9px 0",borderTop:`1px solid ${T.border}`}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                              <div style={{minWidth:0}}>
                                <div style={{fontSize:13.5,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cleanName(p.displayName)}</div>
                                <div style={{fontSize:12,color:T.textMuted}}>{fmtUSDExact(p.amount)} · last {fmtDueDay(p.lastDate, today)}</div>
                              </div>
                              {possibleAdding?.matchName !== p.matchName && (
                                <div style={{display:"flex",gap:6,flexShrink:0}}>
                                  <button onClick={()=>dismissPossibleSubscription(p)} aria-label={`Not a subscription: ${cleanName(p.displayName)} ${fmtUSDExact(p.amount)}`} style={{...C.ghost,fontSize:12,padding:"7px 10px"}}>Not a subscription</button>
                                  <button onClick={()=>setPossibleAdding({ matchName: p.matchName, name: cleanName(p.displayName) })} style={{...C.btn(T.accent),fontSize:12,padding:"7px 14px"}}>Add</button>
                                </div>
                              )}
                            </div>
                            {/* Add opens with the name editable: "App Store subscription" → "Crunchyroll". */}
                            {possibleAdding?.matchName === p.matchName && (
                              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:8}}>
                                <input value={possibleAdding.name} onChange={e=>setPossibleAdding(x=>({...x,name:e.target.value}))} aria-label="Subscription name"
                                  onFocus={e=>{ if (p.reason==="app_store") e.target.select(); }} style={{...C.inp,flex:"1 1 160px",padding:"8px 10px",fontSize:13}}/>
                                <button style={{...C.btn(T.accent),fontSize:12,padding:"8px 14px"}} onClick={async()=>{ if (await addPossibleSubscription(p, possibleAdding.name)) setPossibleAdding(null); }}>Add</button>
                                <button style={{...C.ghost,fontSize:12,padding:"8px 12px"}} onClick={()=>setPossibleAdding(null)}>Cancel</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Hidden: everything answered "Not a subscription" / "Not a bill". */}
                    {hiddenSubs.length > 0 && (
                      <div style={{marginTop:12}}>
                        <button onClick={()=>setSubsHiddenOpen(o=>!o)} aria-expanded={subsHiddenOpen} style={{background:"none",border:"none",padding:"6px 0",color:T.textMuted,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Hidden ({hiddenSubs.length}) {subsHiddenOpen?"▾":"▸"}</button>
                        {subsHiddenOpen && hiddenSubs.map(h=>(
                          <div key={h.matchName} style={{borderTop:`1px solid ${T.border}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"8px 0"}}>
                            <div style={{minWidth:0}}>
                              <div style={{fontSize:13,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.matchName.startsWith("APPLE|") ? `Apple ${fmtUSDExact(h.amount)}` : cleanName(h.displayName)}</div>
                              <div style={{fontSize:11.5,color:T.textMuted}}>{fmtUSDExact(h.amount)} · usually the {ordinal(h.usualDay)}</div>
                            </div>
                            {forgetConfirm !== h.matchName && (
                              <div style={{display:"flex",gap:6,flexShrink:0}}>
                                <button onClick={()=>setForgetConfirm(h.matchName)} style={{background:"none",border:"none",padding:"6px 4px",color:T.textMuted,fontWeight:600,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Forget</button>
                                <button onClick={()=>restoreHiddenSubscription(h)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 12px",color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Restore</button>
                              </div>
                            )}
                          </div>
                          {forgetConfirm === h.matchName && (
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",padding:"0 0 8px"}}>
                              <span style={{fontSize:12,color:T.textMuted,flex:"1 1 200px"}}>Scorify will suggest this again if it looks like a subscription.</span>
                              <button onClick={()=>forgetHiddenSubscription(h)} style={{...C.btn(T.accent),fontSize:12,padding:"6px 12px"}}>Forget</button>
                              <button onClick={()=>setForgetConfirm(null)} style={{...C.ghost,fontSize:12,padding:"6px 10px"}}>Cancel</button>
                            </div>
                          )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })()}

            {/* ── OVERVIEW SUB-TAB — Bento Grid ── */}
            {financeSubTab==="overview"&&(()=>{
              // ── 6-month bar chart data from transactions ──
              // Local month keys + the shared spend test (map 3.2): UTC keys
              // shifted boundary transactions a month for Pacific users, and
              // sign-only sums counted transfers and card payments as spending.
              const sixMonthData = Array.from({length:6},(_,i)=>{
                const d = new Date(); d.setMonth(d.getMonth()-5+i);
                const ym = localMonthStr(d);
                const label = d.toLocaleDateString("en-US",{month:"short"});
                const total = plaidTransactions.filter(t=>isSpendTx(t)&&(txLocalDay(t)||"").startsWith(ym)).reduce((a,t)=>a+t.amount,0);
                return {month:label,amount:Math.round(total)};
              });
              // ── Donut chart top-5 categories ──
              const DONUT_PALETTE = [T.accent,T.success,T.danger,"#2980b9","#8e44ad"];
              const top5 = catSorted.slice(0,5).map(([name,value],i)=>({name,value:Math.round(value),fill:DONUT_PALETTE[i]}));
              // ── Net worth trend (estimate: this month vs last month spending delta) ──
              // Local keys + isSpendTx here too — moneyOut (the comparison's
              // other side) is already an isSpendTx sum, so the sign-only
              // lastMonthOut compared apples to a bigger basket of oranges.
              const lastMonth = new Date(); lastMonth.setMonth(lastMonth.getMonth()-1);
              const lastMonthStr = localMonthStr(lastMonth);
              const lastMonthSpendTx = plaidTransactions.filter(t=>isSpendTx(t)&&(txLocalDay(t)||"").startsWith(lastMonthStr));
              const lastMonthOut = lastMonthSpendTx.reduce((a,t)=>a+t.amount,0);
              const lastMonthTxCount = lastMonthSpendTx.length;
              const thisMonthTxCount = plaidTransactions.filter(t=>isSpendTx(t)&&(txLocalDay(t)||"").startsWith(localMonthStr())).length;
              const dayOfMonth = new Date().getDate();
              const trendPct = (lastMonthTxCount >= 15 && thisMonthTxCount >= 5 && lastMonthOut >= 500 && dayOfMonth >= 15 && Math.abs(((moneyOut - lastMonthOut) / lastMonthOut) * 100) <= 150)
                ? Math.round(((moneyOut - lastMonthOut) / lastMonthOut) * 100)
                : null;
              // ── Shared bento card style ──
              const bentoCard = (extra={}) => ({
                background:T.surfaceAlt,
                borderRadius:16,
                padding:"20px 22px",
                border:`1px solid ${T.overlayLight(0.06)}`,
                transition:"transform 200ms ease, box-shadow 200ms ease",
                ...extra,
              });
              // eslint-disable-next-line no-unused-vars
              const hoverStyle = "transform:translateY(-2px);box-shadow:0 8px 32px rgba(0,0,0,0.4)";
              // connect-only view
              if (!plaidConnected) return (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  {/* Trust strip */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:isMobile?16:28,flexWrap:"wrap",padding:"14px 20px",background:T.overlayLight(0.03),border:`1px solid ${T.overlayLight(0.06)}`,borderRadius:12}}>
                    {[
                      {icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>, text:"Bank-level encryption"},
                      {icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, text:"Read-only access"},
                      {icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, text:"Used by 8,000+ apps"},
                      {icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>, text:"We can never move your money"},
                    ].map(t=>(
                      <div key={t.text} style={{display:"flex",alignItems:"center",gap:6}}>
                        {t.icon}
                        <span style={{fontSize:11,color:T.overlayLight(0.45),fontWeight:500}}>{t.text}</span>
                      </div>
                    ))}
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontSize:11,color:T.overlayLight(0.25)}}>Bank-level security</span>
                    </div>
                  </div>

                  {/* Connect Bank hero card */}
                  <div style={{...bentoCard({borderLeft:`3px solid ${T.accent}`,padding:"32px 28px"}),display:"flex",flexDirection:isMobile?"column":"row",justifyContent:"space-between",alignItems:isMobile?"flex-start":"center",gap:16}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:T.accent,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>Bank Integration</div>
                      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:28,color:T.text,marginBottom:8,lineHeight:1.2}}>Connect your bank account</div>
                      <div style={{fontSize:13,color:T.overlayLight(0.5),lineHeight:1.6,maxWidth:360}}>Link your bank to unlock real-time balances, spending trends, category breakdowns, and AI-powered financial insights.</div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:isMobile?"flex-start":"center",gap:10,flexShrink:0}}>
                      <button onClick={connectPlaid} disabled={plaidLoading} style={{fontSize:14,padding:"12px 28px",borderRadius:12,border:"none",background:T.accentFill,color:"#fff",cursor:"pointer",fontWeight:700,opacity:plaidLoading?0.6:1,transition:"opacity 200ms ease,transform 200ms ease",letterSpacing:"0.02em",width:"100%"}}>
                        {plaidLoading?"Connecting...":"Connect Bank"}
                      </button>
                      {/* Plaid trust badge */}
                      <div style={{display:"flex",alignItems:"center",gap:6,justifyContent:"center"}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.overlayLight(0.3)} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        <span style={{fontSize:11,color:T.overlayLight(0.3),fontWeight:500}}>Bank-level security</span>
                        <span style={{fontSize:10,color:T.overlayLight(0.2)}}>·</span>
                        <span style={{fontSize:11,color:T.overlayLight(0.3)}}>Read-only · Encrypted</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
              return (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>

                  {/* Plaid trust badge — demo only */}
                  {isDemoMode && (
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:G.tileBg,border:`1px solid ${withAlpha(T.success,0.25)}`,borderRadius:10,flexWrap:"wrap",gap:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <div style={{width:8,height:8,borderRadius:"50%",background:T.success,flexShrink:0}}/>
                        <span style={{fontSize:12,color:T.success,fontWeight:600}}>Bank connected via Plaid</span>
                        <span style={{fontSize:11,color:T.textMuted}}>· Read-only · We can never move your money</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        <span style={{fontSize:11,fontWeight:800,color:T.textMuted,letterSpacing:"0.06em"}}>PLAID</span>
                      </div>
                    </div>
                  )}

                  <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5}}>
                    Not financial advice. Data from Plaid is read-only. We cannot move your money.
                  </div>

                  {/* ROW 1 — Available Balance hero */}
                  {(()=>{
                    const depositAccounts = plaidAccounts.filter(a=>a.type!=="credit");
                    const creditAccounts = plaidAccounts.filter(a=>a.type==="credit");
                    // "Available Balance" must sum the AVAILABLE balance (current
                    // minus holds), matching the correct computation at :4483.
                    // The ?? current fallback keeps accounts whose `available`
                    // Plaid leaves null from dropping to 0.
                    const availBal = depositAccounts.reduce((a,acc)=>a+(acc.balances?.available ?? acc.balances?.current ?? 0),0);
                    // Checking/Savings are a BREAKDOWN of the hero, so they read the
                    // SAME field (available ?? current) — mirror :6592 exactly, or a
                    // null-`available` account counts in the hero but not the chip and
                    // they stop reconciling. (Was `current`, which showed 1462 under a
                    // 1377 hero for the same account — the $85 pending gap, unexplained.)
                    // NOTE: a deposit subtype that is neither checking nor savings
                    // (money market, CD, HSA) is summed into the hero but appears in
                    // NEITHER chip, so for those accounts Checking+Savings won't fully
                    // reconcile to the hero. Acceptable for now; revisit if such an
                    // account shows up.
                    const checkingBal = depositAccounts.filter(a=>a.subtype==="checking").reduce((a,acc)=>a+(acc.balances?.available ?? acc.balances?.current ?? 0),0);
                    const savingsBal = depositAccounts.filter(a=>a.subtype==="savings").reduce((a,acc)=>a+(acc.balances?.available ?? acc.balances?.current ?? 0),0);
                    const creditUsed = creditAccounts.reduce((a,acc)=>a+(acc.balances?.current||0),0);
                    return (
                      <div style={{...bentoCard({borderLeft:`3px solid ${T.accent}`,padding:"24px 26px"})}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>Available Balance</div>
                        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:isMobile?36:48,fontWeight:400,color:T.success,lineHeight:1,marginBottom:12,letterSpacing:"-0.02em"}}>
                          {fmtUSD(availBal)}
                        </div>
                        {/* Snapshot time, always shown (2026-10-28; it used to appear
                            only past 6h, which read as live the rest of the day).
                            Show the number: the user knows their own balance and
                            can judge. Nothing when the time is unknown. */}
                        {balanceAsOf && (
                          <div style={{fontSize:11,color:T.textMuted,marginBottom:10}}>{balanceAsOf}</div>
                        )}
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:10}}>
                          {/* Show the deposit breakdown only when BOTH subtypes are
                              present. With one subtype the single chip just restates the
                              hero sitting directly above it (available ?? current now
                              matches), so it adds nothing. Credit Used below is separate —
                              it is NOT part of the hero — and shows independently. */}
                          {checkingBal>0&&savingsBal>0&&<span style={{fontSize:11,fontWeight:600,color:T.overlayLight(0.7),background:T.overlayLight(0.07),padding:"3px 10px",borderRadius:99,border:`1px solid ${T.overlayLight(0.1)}`}}>Checking {fmtUSD(checkingBal)}</span>}
                          {checkingBal>0&&savingsBal>0&&<span style={{fontSize:11,fontWeight:600,color:T.overlayLight(0.7),background:T.overlayLight(0.07),padding:"3px 10px",borderRadius:99,border:`1px solid ${T.overlayLight(0.1)}`}}>Savings {fmtUSD(savingsBal)}</span>}
                          {creditUsed>0&&<span style={{fontSize:11,fontWeight:600,color:"rgba(220,50,50,0.8)",background:"rgba(220,50,50,0.07)",padding:"3px 10px",borderRadius:99,border:"1px solid rgba(220,50,50,0.15)"}}>Credit Used {fmtUSD(creditUsed)}</span>}
                        </div>
                        {trendPct!==null&&(
                          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                            <span style={{fontSize:12,fontWeight:600,color:trendPct<=0?T.success:"rgba(220,50,50,0.9)",background:trendPct<=0?"rgba(93,202,165,0.1)":"rgba(220,50,50,0.1)",padding:"3px 10px",borderRadius:99}}>
                              {trendPct<=0?"-":"+"}{ Math.abs(trendPct)}% spending vs last month
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* THIS WEEK card */}
                  {plaidConnected && (
                    <div style={bentoCard()}>
                      <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>This Week</div>
                      {thisWeekSpent > 0 ? (
                        <>
                          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:isMobile?28:36,fontWeight:400,color:T.text,lineHeight:1,marginBottom:8,letterSpacing:"-0.02em"}}>
                            {fmtUSD(thisWeekSpent)}
                          </div>
                          <div style={{fontSize:12,color:T.overlayLight(0.5)}}>
                            {thisWeekTx.length} {thisWeekTx.length === 1 ? "transaction" : "transactions"} · last 7 days
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:isMobile?28:36,fontWeight:400,color:T.success,lineHeight:1,marginBottom:8,letterSpacing:"-0.02em"}}>
                            $0
                          </div>
                          <div style={{fontSize:12,color:T.overlayLight(0.5)}}>
                            No transactions in the last 7 days
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* ROW 2 — Three metric cards */}
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)",gap:12}}>
                    {[
                      {label:"Money In", val:moneyIn, color:T.success, bg:"rgba(93,202,165,0.06)", border:"rgba(93,202,165,0.15)"},
                      {label:"Money Out", val:moneyOut, color:"rgba(220,50,50,0.9)", bg:"rgba(220,50,50,0.06)", border:"rgba(220,50,50,0.15)"},
                      {label:"Net Saved", val:netSaved, color:netSaved>=0?T.success:"rgba(220,50,50,0.9)", bg:netSaved>=0?"rgba(93,202,165,0.06)":"rgba(220,50,50,0.06)", border:netSaved>=0?"rgba(93,202,165,0.15)":"rgba(220,50,50,0.15)"},
                    ].map(m=>(
                      <div key={m.label} style={{background:m.bg,borderRadius:14,padding:"18px 16px",border:`1px solid ${m.border}`,transition:"transform 200ms ease,box-shadow 200ms ease",cursor:"default",gridColumn:m.label==="Net Saved"&&isMobile?"span 2":"auto"}}
                        onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.35)";}}
                        onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
                        <div style={{fontSize:10,fontWeight:700,color:T.overlayLight(0.45),textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>{m.label}</div>
                        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:isMobile?20:26,fontWeight:400,color:m.color,lineHeight:1,letterSpacing:"-0.01em"}}>{fmtUSD(m.val)}</div>
                        <div style={{fontSize:11,color:T.overlayLight(0.3),marginTop:6}}>last 30 days</div>
                      </div>
                    ))}
                  </div>

                  {/* ROW 3 — Charts */}
                  {(sixMonthData.some(d=>d.amount>0) || top5.length>0) && (
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>

                      {/* Bar chart — 6-Month Spending Trend */}
                      <div style={bentoCard()}>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:16}}>6-Month Spending</div>
                        {spendSanityFlag?.kind==="drop" && (
                          <div style={{fontSize:11,color:T.warning,lineHeight:1.5,marginTop:-8,marginBottom:12}}>This month looks unusually low. Some payments may still be importing, or transfers may be excluded. Pull to refresh to recheck.</div>
                        )}
                        {sixMonthData.some(d=>d.amount>0) ? (
                          <div role="img" aria-label="Bar chart: total spending per month, last 6 months">
                          {financeChartsReady ? (
                          <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={sixMonthData} barCategoryGap="30%" margin={{top:4,right:0,left:-24,bottom:0}}>
                              <XAxis dataKey="month" tick={{fill:T.overlayLight(0.4),fontSize:11}} axisLine={false} tickLine={false}/>
                              <YAxis tick={{fill:T.overlayLight(0.3),fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>v>=1000?`$${(v/1000).toFixed(0)}k`:`$${v}`}/>
                              <RTooltip
                                contentStyle={{background:T.surfaceAlt,border:`1px solid ${withAlpha(T.accent,0.25)}`,borderRadius:8,padding:"8px 12px"}}
                                labelStyle={{color:T.overlayLight(0.6),fontSize:11,marginBottom:4}}
                                formatter={v=>["$"+v.toLocaleString(),"Spending"]}
                                itemStyle={{color:T.accent,fontWeight:700,fontSize:13}}
                                cursor={{fill:withAlpha(T.accent,0.06)}}
                              />
                              <RBar dataKey="amount" radius={[4,4,0,0]}>
                                {sixMonthData.map((_,i)=>(
                                  <Cell key={i} fill={i===5?T.accent:withAlpha(T.accent,0.3)}/>
                                ))}
                              </RBar>
                            </BarChart>
                          </ResponsiveContainer>
                          ) : <div style={{height:160}}/>}
                          </div>
                        ) : (
                          <div style={{height:160,display:"flex",alignItems:"center",justifyContent:"center",color:T.overlayLight(0.3),fontSize:12}}>No data yet</div>
                        )}
                      </div>

                      {/* Donut chart — Spending by Category */}
                      <div style={bentoCard()}>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:16}}>By Category</div>
                        {top5.length>0 ? (
                          <>
                            <div role="img" aria-label="Donut chart: spending by category this month">
                            {financeChartsReady ? (
                            <ResponsiveContainer width="100%" height={130}>
                              <PieChart>
                                <Pie
                                  data={top5} cx="50%" cy="50%"
                                  innerRadius={38} outerRadius={58}
                                  paddingAngle={3} dataKey="value"
                                  onMouseEnter={(_,i)=>setHoveredSlice(i)}
                                  onMouseLeave={()=>setHoveredSlice(null)}
                                  stroke="none"
                                >
                                  {top5.map((entry,i)=>(
                                    <Cell key={i} fill={entry.fill} opacity={hoveredSlice===null||hoveredSlice===i?1:0.45}
                                      style={{cursor:"pointer",transition:"opacity 200ms ease"}}/>
                                  ))}
                                </Pie>
                                <RTooltip
                                  contentStyle={{background:T.surfaceAlt,border:`1px solid ${withAlpha(T.accent,0.25)}`,borderRadius:8,padding:"7px 12px"}}
                                  formatter={(v,n)=>["$"+v.toLocaleString(),n]}
                                  itemStyle={{color:T.text,fontSize:12}}
                                  labelStyle={{display:"none"}}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                            ) : <div style={{height:130}}/>}
                            </div>
                            <div style={{display:"flex",flexDirection:"column",gap:5,marginTop:4}}>
                              {top5.map((c,i)=>(
                                <div key={c.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11,opacity:hoveredSlice===null||hoveredSlice===i?1:0.45,transition:"opacity 200ms ease"}}>
                                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                                    <div style={{width:8,height:8,borderRadius:2,background:c.fill,flexShrink:0}}/>
                                    <span style={{color:T.overlayLight(0.65)}}>{c.name}</span>
                                  </div>
                                  <span style={{fontWeight:700,color:T.text}}>${c.value.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div style={{height:130,display:"flex",alignItems:"center",justifyContent:"center",color:T.overlayLight(0.3),fontSize:12}}>No spending data</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ROW 4 — Recent transactions (compact 5) */}
                  {plaidTransactions.length>0 && (
                    <div style={bentoCard()}>
                      <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14}}>Recent Transactions</div>
                      <div style={{display:"flex",flexDirection:"column"}}>
                        {plaidTransactions.slice(0,5).map((t,i)=>{
                          const isDeposit=t.amount<0;
                          const catLabel=(t.personal_finance_category?.primary||(t.category&&t.category[0])||"Other").replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
                          const txDay=txLocalDay(t); const dateStr=txDay?new Date(txDay+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})+(t.pending?" · pending":""):"";
                          return (
                            <div key={t.transaction_id||i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:i<4?`1px solid ${T.overlayLight(0.04)}`:"none"}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:500,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cleanName(t.merchant_name||t.name||"Unknown")}</div>
                                <div style={{fontSize:11,color:T.overlayLight(0.35),marginTop:1}}>{catLabel} · {dateStr}</div>
                              </div>
                              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:15,fontWeight:400,color:isDeposit?T.success:T.overlayLight(0.7),marginLeft:14,flexShrink:0}}>
                                {isDeposit?"+":""}{fmtUSD(Math.abs(t.amount),2)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button onClick={()=>setFinanceSubTab("spending")} style={{marginTop:12,background:"none",border:"none",color:T.accent,fontSize:12,fontWeight:600,cursor:"pointer",padding:0,textAlign:"left",letterSpacing:"0.02em"}}>
                        View all transactions in Spending tab
                      </button>
                    </div>
                  )}

                  {/* ROW 5 — AI Insight */}
                  {insight && (
                    <div style={{padding:"13px 18px",background:withAlpha(T.accent,0.06),border:`1px solid ${withAlpha(T.accent,0.18)}`,borderRadius:12,fontSize:13,color:T.overlayLight(0.75),lineHeight:1.65}}>
                      <span style={{color:T.accent,fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.08em",marginRight:8}}>AI Insight</span>{insight}
                    </div>
                  )}

                </div>
              );
            })()}

            {/* ── SPENDING SUB-TAB ── */}
            {financeSubTab==="spending"&&(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {!plaidConnected && (
                  <div style={{...GC.card,textAlign:"center",padding:"32px 0"}}>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:12}}>Connect your bank to see spending data</div>
                    <button onClick={connectPlaid} disabled={plaidLoading} style={{fontSize:13,padding:"8px 20px",borderRadius:10,border:"none",background:T.accentFill,color:"#fff",cursor:"pointer",fontWeight:700}}>
                      {plaidLoading?"Connecting...":"Connect Bank"}
                    </button>
                  </div>
                )}
                {(plaidConnected && catSorted.length > 0) && (
                  <div style={GC.card}>
                    <div style={{...C.cTitle,marginBottom:14}}>Spending by Category</div>
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      {catSorted.map(([cat,amt])=>(
                        <div key={cat}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4}}>
                            <span style={{color:T.text,fontWeight:500}}>{cat}</span>
                            {/* Share of TOTAL 30-day spending. Was amt/catMax — a share of
                                the LARGEST category, which made the top row read "100%" no
                                matter what fraction of spending it actually was. The bar
                                below keeps catMax as a relative visual scale on purpose. */}
                            <span style={{fontWeight:700,color:T.text}}>{fmtUSD(amt)} <span style={{color:T.textMuted,fontWeight:400,fontSize:12}}>({Math.round(amt/catTotal*100)}%)</span></span>
                          </div>
                          <div style={{height:6,borderRadius:99,background:T.overlayLight(0.08)}}>
                            <div style={{height:"100%",borderRadius:99,background:T.accent,width:`${Math.min(100,amt/catMax*100)}%`,transition:"width 0.6s ease"}}/>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${T.overlayLight(0.06)}`,display:"flex",justifyContent:"space-between",fontSize:13}}>
                      <span style={{color:T.textMuted}}>Total spending (30 days)</span>
                      <span style={{fontWeight:800,color:"rgba(220,50,50,0.9)"}}>{fmtUSD(catSorted.reduce((a,v)=>a+v[1],0))}</span>
                    </div>
                  </div>
                )}
                {(plaidConnected && plaidTransactions.length > 0) && (
                  <div style={GC.card}>
                    <div style={{...C.cTitle,marginBottom:14}}>Recent Transactions</div>
                    <div style={{display:"flex",flexDirection:"column"}}>
                      {visibleTx.map((t,i)=>{
                        const isDeposit = t.amount < 0;
                        const catLabel = (t.personal_finance_category?.primary||(t.category&&t.category[0])||"Other").replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
                        const txDay = txLocalDay(t); const dateStr = txDay ? new Date(txDay+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})+(t.pending?" · pending":"") : "";
                        return (
                          <div key={t.transaction_id||i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<visibleTx.length-1?`1px solid ${T.overlayLight(0.05)}`:"none"}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:600,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cleanName(t.merchant_name||t.name||"Unknown")}</div>
                              <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>{catLabel} · {dateStr}</div>
                            </div>
                            <div style={{fontSize:14,fontWeight:700,color:isDeposit?T.success:T.text,marginLeft:12,flexShrink:0}}>
                              {isDeposit?"+":""}{fmtUSD(Math.abs(t.amount),2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {plaidTransactions.length > plaidTxLimit && (
                      <button onClick={()=>setPlaidTxLimit(plaidTransactions.length)} style={{marginTop:12,width:"100%",padding:"8px",background:"transparent",border:`1px solid ${T.overlayLight(0.1)}`,borderRadius:8,color:T.textMuted,fontSize:12,cursor:"pointer",fontWeight:600}}>
                        View all {plaidTransactions.length} transactions
                      </button>
                    )}
                    {plaidTxLimit > 15 && (
                      <button onClick={()=>setPlaidTxLimit(15)} style={{marginTop:6,width:"100%",padding:"6px",background:"transparent",border:"none",color:T.textMuted,fontSize:11,cursor:"pointer"}}>Show less</button>
                    )}
                    <div style={{fontSize:11,color:T.textMuted,marginTop:12,textAlign:"center",lineHeight:1.6,padding:"0 8px"}}>
                      {plaidTransactions.length < 30 ? "Transaction history builds over time. Plaid may take a few days to fully sync your historical data. " : "Transactions typically appear 1–3 days after purchase. "}
                      <span style={{color:T.overlayLight(0.3)}}>Powered by Plaid.</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── BILLS & GOALS SUB-TAB ── */}
            {financeSubTab==="bills"&&(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Bills — collapses to one line; open by default only when something is due soon or overdue. */}
                {(()=>{
                  let zone = null; try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}
                  const annotatedBills = zone ? annotateBills(bills, zone) : [];
                  const summary = billsSummary(annotatedBills);
                  const open = billsOpen ?? billsDefaultOpen(annotatedBills);
                  return (
                <div style={GC.card}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:open?12:0,gap:10}}>
                    <button onClick={()=>setBillsOpen(!open)} aria-expanded={open} style={{flex:1,minWidth:0,minHeight:40,display:"flex",alignItems:"center",gap:8,background:"transparent",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                      {open ? <span style={C.cTitle}>Bills</span> : <span style={{fontSize:13,color:summary.overdue?T.danger:T.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{summary.line}</span>}
                      <span aria-hidden="true" style={{color:T.accent,fontSize:16,transform:open?"rotate(90deg)":"none",transition:"transform 0.15s",flexShrink:0}}>›</span>
                    </button>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      {bills.filter(b=>b.status==="overdue").length>0&&<span style={{color:T.danger,fontWeight:700,fontSize:11}}>{bills.filter(b=>b.status==="overdue").length} overdue</span>}
                      <button onClick={()=>setShowAddBill(true)} style={{background:"transparent",border:`1px solid ${withAlpha(T.accent,0.3)}`,color:T.accent,borderRadius:8,padding:"5px 11px",cursor:"pointer",fontSize:12,fontWeight:700}}>+ Add Bill</button>
                    </div>
                  </div>
                  {open && (<>
                  {billError&&(
                    <div style={{marginBottom:12,padding:"9px 12px",borderRadius:8,background:withAlpha(T.danger,0.1),border:`1px solid ${T.danger}`,color:T.danger,fontSize:12,fontWeight:600}}>{billError}</div>
                  )}
                  {(()=>{
                    // Shared with the server via src/lib/bills.js. The old inline math
                    // hardcoded 31 for the rollover and overstated by 3 days every February.
                    const {y:ty,m:tm,d:td} = localToday();
                    const thisMonth = currentMonth();
                    const dueIn = (b)=>billDaysUntil(b,ty,tm,td) ?? Infinity;
                    const sorted = [...bills].sort((a,b)=>dueIn(a)-dueIn(b));
                    return sorted.map(b=>{
                      const daysUntil = dueIn(b);
                      const paid = b.cadence==="yearly" ? subscriptionRow(b, { today: localDateStr(), month: thisMonth }).paidThisYear : isPaidThisMonth(b, thisMonth);
                      const urgentColor = b.autopay===true ? (paid?T.success:T.textMuted) : b.status==="overdue"?T.danger:daysUntil<=3&&!paid?T.accent:T.textMuted;
                      // Autopay (2026-10-22): no due countdown, no Pay button — it charges itself.
                      const daysLabel = b.autopay===true ? autopayStatusLine(b, { paid }) : paid?"Paid":b.status==="overdue"?"Overdue":daysUntil===0?"Due today":daysUntil===1?"Due tomorrow":`Due in ${daysUntil}d`;
                      return (
                        <div key={b.id} style={{padding:"10px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontSize:14,fontWeight:600}}>{b.name}</span>
                              {b.autopay&&<span style={{fontSize:9,background:withAlpha(T.success,0.1),color:T.success,border:`1px solid ${withAlpha(T.success,0.3)}`,borderRadius:4,padding:"1px 5px",fontWeight:700}}>AUTO</span>}
                            </div>
                            <div style={{fontSize:11,color:urgentColor,marginTop:2,fontWeight:daysUntil<=3&&!paid&&b.autopay!==true?700:400}}>{daysLabel} · {b.cadence==="weekly" ? `Every ${["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][b.weekday] || "week"}` : b.cadence==="yearly" ? `Yearly · ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][(b.due_month||1)-1]} ${b.due_day}` : `Day ${b.due_day}`}</div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontWeight:700,fontSize:14,color:paid?T.success:b.status==="overdue"?T.danger:T.text}}>${(b.amount||0).toLocaleString()}</span>
                            {!paid&&b.autopay!==true&&<button onClick={()=>markBillPaid(b.id)} style={{fontSize:10,background:withAlpha(T.success,0.1),border:`1px solid ${withAlpha(T.success,0.3)}`,color:T.success,borderRadius:6,padding:"3px 7px",cursor:"pointer",fontWeight:700}}>Pay</button>}
                            <button onClick={()=>setEditBill({...b})} aria-label={`Edit bill ${b.name}`} title="Edit" style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",padding:"2px 4px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {/* Feather edit-2, inline to match the nav + habit-pill glyphs (24 viewBox, currentColor, 1.8 stroke). Was an empty button — an invisible, unlabeled control (UI audit 2026-09-16). */}
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,display:"block"}}><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                            </button>
                            <button onClick={()=>{if(window.confirm(`Delete "${b.name}"?`))removeBill(b.id);}} aria-label={`Delete bill ${b.name}`} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:16,padding:"12px 14px"}}>×</button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                  {bills.length===0&&<div style={{textAlign:"center",padding:"20px 0",color:T.textMuted,fontSize:13}}>No bills added yet.</div>}
                  {bills.length>0&&(
                    <div style={{marginTop:10,fontSize:12,color:T.textMuted}}>Monthly total: <span style={{color:T.text,fontWeight:700}}>{fmtUSDExact(sumMoney(bills, b=>b.amount))}</span></div>
                  )}
                  </>)}
                </div>
                  );
                })()}

                {/* Payday Settings */}
                {(()=>{
                  const freqLabels = { weekly:"Weekly", biweekly:"Bi-weekly", semimonthly:"Twice a month", monthly:"Monthly" };
                  const weekdayLabels = { "1":"Monday","2":"Tuesday","3":"Wednesday","4":"Thursday","5":"Friday" };
                  const freq = paySchedule.freq || "biweekly";
                  const freqLabel = freqLabels[freq] || freq;
                  let paydayLabel = "";
                  if (freq==="semimonthly") paydayLabel = `${paySchedule.day1 ? `${paySchedule.day1}${["th","st","nd","rd","th","th","th","th","th","th","th","th","th","th","th","th","th","th","th","th","th","st","nd","rd","th","th","th","th","th","th","th","st"][paySchedule.day1]}` : "1st"} & ${paySchedule.day2 ? `${paySchedule.day2}${["th","st","nd","rd","th","th","th","th","th","th","th","th","th","th","th","th","th","th","th","th","th","st","nd","rd","th","th","th","th","th","th","th","st"][paySchedule.day2]}` : "15th"} of month`;
                  else if (freq==="monthly") paydayLabel = `Day ${paySchedule.day1 || 1} of month`;
                  else paydayLabel = `Every ${weekdayLabels[String(paySchedule.weekday||"5")] || "Friday"}${freq==="biweekly" ? " (every 2 weeks)" : ""}`;
                  const paychecksPerMonth = freq==="weekly"?4.33:freq==="biweekly"?2.17:freq==="semimonthly"?2:1;
                  const monthlyFromPaycheck = paycheckAmount > 0 ? Math.round(paycheckAmount * paychecksPerMonth) : 0;
                  const isSetup = payScheduleSaved || paycheckAmount > 0 || !!paySchedule.startDate;
                  // "I'm paid irregularly" / "I have a regular paycheck": the override
                  // (2026-10-14). Detection can be wrong, so the user's word wins.
                  const overrideRow = (
                    <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${T.overlayLight(0.06)}`}}>
                      <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>How you're paid</div>
                      <div style={{display:"flex",gap:6}}>
                        {[["regular","I have a regular paycheck"],["irregular","I'm paid irregularly"]].map(([val,lbl])=>{
                          const on = incomeType.type===val;
                          return (
                            <button key={val} onClick={()=>chooseIncomeType(val)} disabled={incomeTypeSaving} aria-pressed={on} style={{flex:1,minHeight:40,padding:"8px 10px",borderRadius:10,border:`1px solid ${on?T.accent:T.overlayLight(0.12)}`,background:on?withAlpha(T.accent,0.12):"transparent",color:on?T.accent:T.text,fontSize:12,fontWeight:on?700:500,cursor:"pointer",fontFamily:"inherit"}}>{lbl}</button>
                          );
                        })}
                      </div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:6}}>
                        {incomeTypeSaving ? "Saving…" : incomeType.source==="override" ? "Set by you." : incomeType.source==="detected" ? `Detected from your deposits (${incomeProfile.reason}).` : "Not enough deposit history yet to detect a pattern; defaulting to a regular paycheck."}
                      </div>
                    </div>
                  );
                  // ── PAYDAY SET ONCE (2026-10-20, src/lib/income.js) ── answered →
                  // one quiet line + the stats; Edit reopens the settings below; a
                  // material change (no paycheck for 2x the usual gap) asks once.
                  const sm = irregularSummary || { mtd: 0, typical: null, tracking: null };
                  const trackLabel = sm.tracking==="above" ? "Tracking above a typical month" : sm.tracking==="below" ? "Tracking below a typical month" : sm.tracking==="on_pace" ? "About on pace for a typical month" : null;
                  const trackColor = sm.tracking==="above" ? T.success : sm.tracking==="below" ? T.warning : T.textMuted;
                  const statRow = (label, value, color=T.text, big=false) => (
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                      <span style={{fontSize:13,color:T.textMuted}}>{label}</span>
                      <span style={{fontSize:big?15:13,fontWeight:big?800:700,color}}>{value}</span>
                    </div>
                  );
                  if (paySetupDone({ incomeType, payScheduleSaved }) && !payEditOpen) {
                    const chg = payScheduleChanged({ deposits: allCountableDeposits, today: localDateStr(), type: incomeType.type, freq, paycheckAmount });
                    let dismissedFor = null;
                    try { const k = user?.id ? userKey(user.id, "pay_change_dismissed") : null; dismissedFor = k ? localStorage.getItem(k) : null; } catch {}
                    const askChange = chg.changed && dismissedFor !== chg.lastDate && !payChangeDismissed;
                    const dismissChange = () => { setPayChangeDismissed(true); try { const k = user?.id ? userKey(user.id, "pay_change_dismissed") : null; if (k && chg.lastDate) localStorage.setItem(k, chg.lastDate); } catch {} };
                    return (
                      <div style={{...GC.card,padding:"14px 18px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                          <span style={{fontSize:13,color:T.textMuted}}>{payLine({ incomeType, paySchedule })}</span>
                          <button onClick={()=>setPayEditOpen(true)} style={{background:"none",border:"none",padding:"6px 0",color:T.accent,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                        </div>
                        {askChange && (
                          <div style={{marginTop:10,padding:"10px 12px",borderRadius:10,background:T.surfaceAlt,border:`1px solid ${T.border}`}}>
                            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>Did your pay schedule change?</div>
                            <div style={{display:"flex",gap:8}}>
                              <button style={{...C.btn(T.accent),flex:1,fontSize:12,padding:"8px 0"}} onClick={()=>{ dismissChange(); setPayEditOpen(true); }}>Yes, update it</button>
                              <button style={{...C.ghost,flex:1,fontSize:12,padding:"8px 0"}} onClick={dismissChange}>No</button>
                            </div>
                          </div>
                        )}
                        <div style={{display:"flex",flexDirection:"column",marginTop:6}}>
                          {incomeType.type === "irregular" ? (<>
                            {statRow("Received this month", fmtUSD(sm.mtd), T.success, true)}
                            {statRow("Typical month", sm.typical ? `${fmtUSD(sm.typical.low)} to ${fmtUSD(sm.typical.high)}` : "Needs 3 full months of history")}
                            {trackLabel && <div style={{fontSize:13,fontWeight:700,color:trackColor,padding:"8px 0 0"}}>{trackLabel}</div>}
                          </>) : (<>
                            {paycheckAmount > 0 && statRow("Paycheck amount", `$${paycheckAmount.toLocaleString()}`, T.success, true)}
                            {monthlyFromPaycheck > 0 && statRow("Monthly income", `~$${monthlyFromPaycheck.toLocaleString()}`, T.success, true)}
                          </>)}
                        </div>
                      </div>
                    );
                  }
                  // Editing (or not answered yet): the full settings, with Done when editing.
                  const doneBtn = payEditOpen ? (
                    <button onClick={()=>setPayEditOpen(false)} style={{display:"block",width:"100%",marginTop:12,background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",minHeight:40}}>Done</button>
                  ) : null;
                  if (incomeType.type === "irregular") {
                    // IRREGULAR MODE: no schedule, no payday, no "next paycheck".
                    return (
                      <div style={GC.card}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                          <div style={C.cTitle}>Income</div>
                          <span style={{fontSize:11,color:T.textMuted}}>Paid irregularly</span>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:10}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                            <span style={{fontSize:13,color:T.textMuted}}>Received this month</span>
                            <span style={{fontSize:15,fontWeight:800,color:T.success}}>{fmtUSD(sm.mtd)}</span>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                            <span style={{fontSize:13,color:T.textMuted}}>Typical month</span>
                            <span style={{fontSize:13,fontWeight:700,color:T.text}}>{sm.typical ? `${fmtUSD(sm.typical.low)} to ${fmtUSD(sm.typical.high)}` : "Needs 3 full months of history"}</span>
                          </div>
                          {sm.typical && <div style={{fontSize:11,color:T.textMuted,marginTop:-4}}>From your last {sm.typical.months} full months; the middle half of them.</div>}
                          {trackLabel && <div style={{fontSize:13,fontWeight:700,color:trackColor,padding:"4px 0"}}>{trackLabel}</div>}
                        </div>
                        {overrideRow}
                        {doneBtn}
                      </div>
                    );
                  }
                  return (
                    <div style={GC.card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                        <div style={C.cTitle}>Payday Settings</div>
                        <button onClick={openEditFinances} style={{background:"transparent",border:`1px solid ${withAlpha(T.accent,0.3)}`,color:T.accent,borderRadius:8,padding:"5px 11px",cursor:"pointer",fontSize:12,fontWeight:700}}>Edit</button>
                      </div>
                      {!isSetup ? (
                        <div style={{textAlign:"center",padding:"16px 0"}}>
                          <div style={{fontSize:13,color:T.textMuted,marginBottom:12}}>Set your pay schedule to see income on the calendar.</div>
                          <button onClick={openEditFinances} style={{...C.btn(T.accent),fontSize:13,padding:"9px 20px"}}>Set Up Payday →</button>
                        </div>
                      ) : (
                        <div style={{display:"flex",flexDirection:"column",gap:10}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                            <span style={{fontSize:13,color:T.textMuted}}>Frequency</span>
                            <span style={{fontSize:13,fontWeight:700,color:T.text}}>{freqLabel}</span>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                            <span style={{fontSize:13,color:T.textMuted}}>Pay date</span>
                            <span style={{fontSize:13,fontWeight:700,color:T.text}}>{paydayLabel}</span>
                          </div>
                          {paycheckAmount > 0 && (
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                              <span style={{fontSize:13,color:T.textMuted}}>Paycheck amount</span>
                              <span style={{fontSize:15,fontWeight:800,color:T.success}}>${paycheckAmount.toLocaleString()}</span>
                            </div>
                          )}
                          {monthlyFromPaycheck > 0 && (
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0"}}>
                              <span style={{fontSize:13,color:T.textMuted}}>Monthly income</span>
                              <span style={{fontSize:15,fontWeight:800,color:T.success}}>~${monthlyFromPaycheck.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {overrideRow}
                      {doneBtn}
                    </div>
                  );
                })()}

                {/* Cash Flow Calendar */}
                {(()=>{
                  const fullMonths = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                  const today = new Date();
                  const year = today.getFullYear();
                  const month = cfMonth;
                  const firstDay = new Date(year,month,1).getDay();
                  const daysInMonth = new Date(year,month+1,0).getDate();
                  // PAY days come from src/lib/income.js (2026-10-14): only when the user
                  // actually set a schedule up, and never for an irregular earner. The
                  // old inline version ran on the biweekly default and painted invented
                  // paydays on the 1st/15th/29th for everyone.
                  const paydays = scheduledPaydays(paySchedule, year, month, { setUp: payScheduleSaved || paycheckAmount > 0 || !!paySchedule.startDate, incomeType: incomeType.type });
                  const perPaycheck = paycheckAmount || 0;
                  const calMonth = currentMonth(); // hoisted out of the 31-day map
                  const billsByDay = {};
                  bills.forEach(b=>{
                    // Weekly bills (subscriptions) land on every matching weekday.
                    // Yearly bills only in their own month.
                    const days = b.cadence==="weekly"
                      ? Array.from({length:daysInMonth},(_,i)=>i+1).filter(dd=>new Date(year,month,dd).getDay()===Number(b.weekday))
                      : b.cadence==="yearly"
                      ? (Number(b.due_month)===month+1 && b.due_day ? [Math.min(b.due_day,daysInMonth)] : [])
                      : (b.due_day ? [Math.min(b.due_day,daysInMonth)] : []);
                    days.forEach(day=>{ if (!billsByDay[day]) billsByDay[day]=[]; billsByDay[day].push(b); });
                  });
                  return (
                    <div style={GC.card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                        <div style={C.cTitle}>Cash Flow Calendar</div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <button onClick={()=>setCfMonth(m=>(m+11)%12)} aria-label="Previous month" style={{...C.ghost,padding:"8px 14px",fontSize:14}}>‹</button>
                          <span style={{fontSize:13,fontWeight:700,color:T.text,minWidth:80,textAlign:"center"}}>{fullMonths[month]}</span>
                          <button onClick={()=>setCfMonth(m=>(m+1)%12)} aria-label="Next month" style={{...C.ghost,padding:"8px 14px",fontSize:14}}>›</button>
                        </div>
                      </div>
                      {/* Seven columns that can never widen (minmax(0,1fr)): a long
                          amount truncates inside its cell instead of stretching the
                          Sunday column and breaking the grid (device bug, 2026-10-14). */}
                      <div style={{display:"grid",gridTemplateColumns:SEVEN_EQUAL_COLUMNS,gap:3,marginBottom:8}}>
                        {["S","M","T","W","T","F","S"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:10,color:T.textMuted,fontWeight:700,padding:"4px 0"}}>{d}</div>)}
                        {Array(firstDay).fill(null).map((_,i)=><div key={"e"+i}/>)}
                        {Array(daysInMonth).fill(null).map((_,i)=>{
                          const day=i+1;
                          const isToday=today.getDate()===day&&today.getMonth()===month;
                          const dayBills=billsByDay[day]||[];
                          const isPay=paydays.includes(day);
                          const hasOverdue=dayBills.some(b=>b.status==="overdue");
                          const hasPaid=dayBills.length>0&&dayBills.every(b=>isPaidThisMonth(b,calMonth));
                          const allAutopay=dayBills.length>0&&dayBills.every(b=>b.autopay===true); // charges itself: muted, never "upcoming" accent
                          const totalDue=sumMoney(dayBills, b=>b.amount); // integer cents: 24.99 + 24.99 = 49.98 exactly
                          return (
                            <div key={day} title={dayBills.map(b=>`${b.name}: ${fmtUSDExact(b.amount)}${b.autopay===true?" (autopay)":""}`).join(" | ")} style={{minHeight:42,minWidth:0,overflow:"hidden",borderRadius:8,padding:"4px",background:isToday?withAlpha(T.accent,0.15):hasPaid?withAlpha(T.success,0.05):hasOverdue?"rgba(248,113,113,0.1)":dayBills.length>0?"rgba(96,165,250,0.08)":"transparent",border:`1px solid ${isToday?T.accent:hasPaid?withAlpha(T.success,0.2):hasOverdue?"rgba(248,113,113,0.3)":dayBills.length>0?"rgba(96,165,250,0.2)":"transparent"}`,cursor:dayBills.length>0?"help":"default"}}>
                              <div style={{fontSize:11,fontWeight:isToday?800:500,color:isToday?T.accent:T.textMuted,textAlign:"right"}}>{day}</div>
                              {isPay&&<div style={{fontSize:7,background:withAlpha(T.success,0.2),color:T.success,borderRadius:3,padding:"1px 3px",fontWeight:700,textAlign:"center",marginTop:1}}><div>PAY</div>{perPaycheck>0&&<div style={{fontSize:6,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{fmtUSDCompact(perPaycheck)}</div>}</div>}
                              {dayBills.length>0&&<div style={{fontSize:8,color:hasOverdue?T.danger:hasPaid?T.success:allAutopay?T.textMuted:T.accent,fontWeight:700,textAlign:"center",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{fmtUSDCompact(totalDue)}</div>}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{display:"flex",gap:12,fontSize:11,flexWrap:"wrap"}}>
                        {[[T.accent,"Today"],[T.success,"Paid"],[T.danger,"Overdue"],[T.accent,"Upcoming"],[T.textMuted,"Autopay"],...(paydays.length?[[T.success,"PAY = Payday"]]:[])].map(([col,lbl])=>(
                          <div key={lbl} style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:8,height:8,borderRadius:"50%",background:col}}/><span style={{color:T.textMuted}}>{lbl}</span></div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Spending Caps — plaid_spend goals are CAPS, not savings targets.
                    They used to fall through the savings filter (category finance /
                    unit $) and render as "$0 / $0 · 0%" because that card reads
                    target_value, which is null for cap goals — the cap lives in
                    period_target. GoalCard + getGoalAutoValue already carry the
                    correct semantics (inverted: under-cap is good, over is danger),
                    exactly as the Health tab uses them. */}
                {(
                  <div style={GC.card}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                      <div style={C.cTitle}>Spending Caps</div>
                    </div>
                    {!goals.some(g=>!g.completed&&g.source_type==='plaid_spend') && (
                      <div style={{fontSize:13,color:T.textMuted,textAlign:"center",padding:"12px 0"}}>No spending caps yet. Add one from the Home tab.</div>
                    )}
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      {goals.filter(g=>!g.completed&&g.source_type==='plaid_spend').map(g=>{
                        const auto = getGoalAutoValue(g);
                        return <GoalCard key={g.id} g={viewGoal(g)} onEdit={openGoalEditor} onUpdate={(g)=>{setShowUpdateGoal(g);setGoalUpdateVal(String(g.current_value));}} onDelete={deleteGoal} autoValue={auto?.value} autoLabel={auto?.label} autoTarget={auto?.target} autoUnit={auto?.unit} autoHasData={auto?.hasData} autoInverted={auto?.inverted} autoNoSource={auto?.noSource} autoStateLine={auto?.stateLine} autoNote={auto?.note} onConnectSource={auto?.noSource==="plaid"?connectPlaid:auto?.noSource==="screen_time"?repairScreenTime:undefined}/>;
                      })}
                    </div>
                  </div>
                )}

                {/* Savings Goals — target goals only; caps render above */}
                {(()=>{
                  // Category IS the router now: finance goals render here,
                  // fitness/health in Health. The old unit/$-or-title-keyword
                  // heuristics are gone — a fitness goal titled "save my knees"
                  // has no business in a finance card.
                  const savingsGoals = goals.filter(g=>!g.completed&&g.source_type!=='plaid_spend'&&g.category==="finance");
                  return (
                    <div style={GC.card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                        <div style={C.cTitle}>Savings Goals</div>
                      </div>
                      {savingsGoals.length===0?(
                        <div style={{fontSize:13,color:T.textMuted,textAlign:"center",padding:"12px 0"}}>No savings goals yet. Add one from the Home tab.</div>
                      ):(
                        <div style={{display:"flex",flexDirection:"column",gap:10}}>
                          {/* GoalCard, NOT custom markup — routes savings-bound goals
                              (source_type 'savings') through getGoalAutoValue's live
                              balance instead of the frozen current_value, and hides
                              Update on live-tracked goals. Same renderer as the
                              Spending Caps section directly above and the other four.
                              The old "X months left · save $/mo" pacing line is dropped
                              here on purpose; see docs/PLAN.md Phase D to bring it back
                              generically for every deadline goal. */}
                          {savingsGoals.map(g=>{
                            const auto = getGoalAutoValue(g);
                            return <GoalCard key={g.id} g={viewGoal(g)} onEdit={openGoalEditor} onUpdate={(g)=>{setShowUpdateGoal(g);setGoalUpdateVal(String(g.current_value));}} onDelete={deleteGoal} autoValue={auto?.value} autoLabel={auto?.label} autoTarget={auto?.target} autoUnit={auto?.unit} autoHasData={auto?.hasData} autoInverted={auto?.inverted} autoNoSource={auto?.noSource} autoStateLine={auto?.stateLine} autoNote={auto?.note} onConnectSource={auto?.noSource==="plaid"?connectPlaid:auto?.noSource==="screen_time"?repairScreenTime:undefined}/>;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Debt Tracker */}
                {(()=>{
                  const plaidCreditAccounts = plaidAccounts.filter(a=>a.type==="credit");
                  const allDebts = [
                    ...plaidCreditAccounts.map(a=>({ id:"plaid_"+a.account_id, name:cleanName(a.name), balance:a.balances?.current||0, monthly_payment:0, apr:"", isPlaid:true })),
                    ...debts.map(d=>({...d, isPlaid:false})),
                  ];
                  const totalDebt = allDebts.reduce((a,d)=>a+(d.balance||0),0);
                  const totalPmt  = debts.reduce((a,d)=>a+(d.monthly_payment||0),0);
                  return (
                    <div style={GC.card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                        <div style={C.cTitle}>Debt Tracker</div>
                        <button onClick={()=>setShowAddDebt(true)} style={{...C.ghost,fontSize:11,padding:"4px 12px"}}>+ Add Manual Debt</button>
                      </div>
                      {allDebts.length===0&&<div style={{textAlign:"center",padding:"20px 0",color:T.textMuted,fontSize:13}}>No debts found. Connect bank or add manually.</div>}
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        {allDebts.map(d=>(
                          <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${T.overlayLight(0.05)}`}}>
                            <div>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <div style={{fontSize:13,fontWeight:600}}>{d.name}</div>
                                {d.isPlaid&&<span style={{fontSize:9,background:withAlpha(T.accent,0.1),color:T.accent,border:`1px solid ${withAlpha(T.accent,0.2)}`,borderRadius:4,padding:"1px 5px",fontWeight:700}}>LINKED</span>}
                              </div>
                              <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>{d.isPlaid?"Credit card balance":`${d.apr||"—"} APR · $${(d.monthly_payment||0).toLocaleString()}/mo`}</div>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{fontSize:15,fontWeight:800,color:"rgba(220,50,50,0.9)"}}>${(d.balance||0).toLocaleString()}</div>
                              {!d.isPlaid&&<button onClick={()=>{if(window.confirm(`Remove "${d.name}" ($${(d.balance||0).toLocaleString()})?`))removeDebt(d.id);}} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:16}}>x</button>}
                            </div>
                          </div>
                        ))}
                      </div>
                      {allDebts.length>0&&(
                        <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.overlayLight(0.06)}`,display:"flex",justifyContent:"space-between",fontSize:13}}>
                          <span style={{color:T.textMuted}}>Total debt</span>
                          <span style={{fontWeight:800,color:"rgba(220,50,50,0.9)"}}>${totalDebt.toLocaleString()}{totalPmt>0&&<span style={{color:T.textMuted,fontWeight:400,fontSize:11}}> · ${totalPmt.toLocaleString()}/mo payments</span>}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Debt Payoff Planner */}
                {(debts.length > 0 || plaidAccounts.filter(a=>a.type==="credit").length > 0) && (()=>{
                  const plaidCreditAccounts = plaidAccounts.filter(a=>a.type==="credit");
                  const allDebts = [
                    ...plaidCreditAccounts.map(a=>({
                      id:"plaid_"+a.account_id,
                      name:cleanName(a.name),
                      balance:a.balances?.current||0,
                      monthly_payment: Math.max(25, Math.round((a.balances?.current||0)*0.02)),
                      apr: plaidDebtAprs[a.account_id] || "20",
                      isPlaid:true
                    })),
                    ...debts.map(d=>({...d, isPlaid:false})),
                  ];
                  const minPayments = allDebts.reduce((a,d)=>a+(d.monthly_payment||0),0);
                  const activeBudget = debtBudget > 0 ? debtBudget : minPayments;
                  const sliderMax = Math.max(minPayments * 5, 2000);
                  const calcPayoff = (debts, method, totalBudget) => {
                    let ds = debts.map(d=>({...d,bal:d.balance||0,rate:parseFloat((d.apr||"0").replace("%",""))/100/12,pmt:d.monthly_payment||25})).filter(d=>d.bal>0);
                    const minTotal = ds.reduce((a,d)=>a+d.pmt,0);
                    const extraBudget = Math.max(0, (totalBudget||minTotal) - minTotal);
                    if (method==="avalanche") ds.sort((a,b)=>b.rate-a.rate);
                    else ds.sort((a,b)=>a.bal-b.bal);
                    if (ds.length > 0) ds[0].pmt += extraBudget;
                    const results=[];
                    let month=0;
                    const maxMonths=360;
                    while (ds.some(d=>d.bal>0)&&month<maxMonths) {
                      month++;
                      let extra=ds.filter(d=>d.bal<=0).reduce((a,d)=>a+d.pmt,0);
                      const active=ds.filter(d=>d.bal>0);
                      // eslint-disable-next-line no-loop-func
                      active.forEach((d,i)=>{ const interest=d.bal*d.rate; const payment=(i===0?d.pmt+extra:d.pmt); d.bal=Math.max(0,d.bal+interest-payment); if (d.bal===0&&!results.find(r=>r.name===d.name)) results.push({name:d.name,month,label:month<12?`${month}mo`:`${Math.floor(month/12)}y ${month%12}m`}); });
                    }
                    const totalMonths=month;
                    return {results,totalMonths};
                  };
                  const av=calcPayoff(allDebts,"avalanche",activeBudget);
                  const sw=calcPayoff(allDebts,"snowball",activeBudget);
                  const cur=debtMethod==="avalanche"?av:sw;
                  const totalDebt=allDebts.reduce((a,d)=>a+(d.balance||0),0);
                  const totalPmt=allDebts.reduce((a,d)=>a+(d.monthly_payment||0),0);
                  return (
                    <div style={GC.card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                        <div style={C.cTitle}>Debt Payoff Planner</div>
                        <div style={{display:"flex",gap:6}}>
                          {["avalanche","snowball"].map(m=>(
                            <button key={m} onClick={()=>setDebtMethod(m)} style={{fontSize:11,padding:"3px 10px",borderRadius:8,border:`1px solid ${debtMethod===m?T.accent:T.overlayLight(0.1)}`,background:debtMethod===m?withAlpha(T.accent,0.1):"transparent",color:debtMethod===m?T.accent:T.textMuted,cursor:"pointer",fontWeight:700,textTransform:"capitalize"}}>{m==="avalanche"?"Avalanche":"Snowball"}</button>
                          ))}
                        </div>
                      </div>
                      <div style={{marginBottom:16,marginTop:12}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                          <span style={{fontSize:12,color:T.textMuted}}>Monthly payment</span>
                          <span style={{fontSize:15,fontWeight:800,color:T.accent}}>${activeBudget.toLocaleString()}/mo</span>
                        </div>
                        <input
                          type="range"
                          min={Math.max(25, minPayments)}
                          max={sliderMax}
                          step={25}
                          value={activeBudget}
                          onChange={e=>setDebtBudget(Number(e.target.value))}
                          style={{width:"100%",accentColor:T.accent}}
                        />
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.textDim,marginTop:3}}>
                          <span>Min ${Math.max(25,minPayments).toLocaleString()}/mo</span>
                          <span>${sliderMax.toLocaleString()}/mo</span>
                        </div>
                        {activeBudget > minPayments && (()=>{
                          const minResult = calcPayoff(allDebts, debtMethod, minPayments);
                          const monthsSaved = minResult.totalMonths - cur.totalMonths;
                          if (monthsSaved > 0) return (
                            <div style={{fontSize:11,color:T.success,fontWeight:600,marginTop:6}}>
                              Paying ${(activeBudget - minPayments).toLocaleString()} extra/mo saves {monthsSaved < 12 ? monthsSaved + "mo" : Math.floor(monthsSaved/12) + "y " + monthsSaved % 12 + "m"}
                            </div>
                          );
                          return null;
                        })()}
                      </div>
                      <div style={{fontSize:12,color:T.textMuted,marginBottom:14}}>
                        {debtMethod==="avalanche"?"Pay highest APR debt first. It saves the most in interest.":"Pay smallest balance first. Quick wins build momentum."}
                        {av.totalMonths < sw.totalMonths && debtMethod==="snowball" && <span style={{color:T.accent,marginLeft:6}}>Avalanche saves {sw.totalMonths-av.totalMonths} months</span>}
                      </div>
                      {plaidCreditAccounts.length > 0 && (
                        <div style={{fontSize:11,color:T.textMuted,background:withAlpha(T.accent,0.06),border:`1px solid ${withAlpha(T.accent,0.12)}`,borderRadius:8,padding:"8px 12px",marginBottom:12}}>
                          Balances pulled live from your connected bank.
                          Enter your actual APR below for accurate payoff estimates.
                        </div>
                      )}
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:16}}>
                        {[["Total Debt","$"+totalDebt.toLocaleString(),"rgba(220,50,50,0.9)"],["Monthly Payments","$"+totalPmt.toLocaleString()+"/mo",T.accent],["Debt-Free In",cur.totalMonths<12?cur.totalMonths+"mo":Math.floor(cur.totalMonths/12)+"y "+cur.totalMonths%12+"m",T.success]].map(([lbl,val,col])=>(
                          <div key={lbl} style={{background:G.tileBg,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
                            <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>{lbl}</div>
                            <div style={{fontSize:17,fontWeight:800,color:col}}>{val}</div>
                          </div>
                        ))}
                      </div>
                      {cur.results.map((r,i)=>{
                        const debt=allDebts.find(d=>d.name===r.name)||{};
                        return (
                          <div key={r.name} style={{marginBottom:12}}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:5}}>
                              <span style={{fontWeight:600}}><span style={{color:T.textMuted,marginRight:6}}>#{i+1}</span>{r.name}</span>
                              <span style={{color:T.success,fontWeight:700}}>Paid off in {r.label}</span>
                            </div>
                            <div style={{height:7,borderRadius:99,background:T.overlayLight(0.08)}}>
                              <div style={{height:"100%",borderRadius:99,background:"linear-gradient(90deg,#4ade80,#60a5fa)",width:`${100-(r.month/cur.totalMonths)*100+10}%`,maxWidth:"100%",transition:"width 0.6s ease"}}/>
                            </div>
                            <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>Balance: ${(debt.balance||0).toLocaleString()} · ${(debt.monthly_payment||0).toLocaleString()}/mo · {debt.apr||"—"} APR{debt.isPlaid&&balanceAsOf?` · ${balanceAsOf}`:""}</div>
                            {debt.isPlaid && (
                              <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4}}>
                                <span style={{fontSize:11,color:T.textMuted}}>APR:</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={plaidDebtAprs[debt.id?.replace("plaid_","")] ?? "20"}
                                  onChange={e=>setPlaidDebtAprs(p=>({...p,[debt.id?.replace("plaid_","")]:e.target.value}))}
                                  style={{width:52,fontSize:16,background:T.overlayLight(0.08),border:`1px solid ${withAlpha(T.accent,0.3)}`,borderRadius:6,padding:"2px 6px",color:T.accent,fontWeight:700}}
                                />
                                <span style={{fontSize:11,color:T.textMuted}}>% — edit to match your card</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── ACCOUNTS SUB-TAB ── */}
            {financeSubTab==="accounts"&&(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Other savings and assets (manual) — counts toward the Savings Cushion. */}
                {(()=>{
                  const ASSET_LABELS={gold_silver:"Gold / Silver",cash:"Cash",bank_brokerage:"Other bank / brokerage",other:"Other"};
                  const exp=effectiveExpenses||monthlyExpenses||0;
                  const months=exp>0?(cushionBalance(financeData)/exp):null;
                  return (
                    <div style={GC.card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                        <div style={C.cTitle}>Other savings &amp; assets</div>
                        <button onClick={()=>setShowAddAsset(true)} style={{...C.ghost,fontSize:11,padding:"4px 12px"}}>+ Add</button>
                      </div>
                      {otherAssets.length===0?(
                        <div style={{fontSize:13,color:T.textMuted,lineHeight:1.5}}>Savings outside your linked bank — gold or silver, cash, another bank or brokerage. Add what you have; it counts toward your cushion.</div>
                      ):(
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {otherAssets.map(a=>(
                            <div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
                              <div style={{minWidth:0}}>
                                <div style={{fontSize:14,fontWeight:600,color:T.text}}>{a.label||ASSET_LABELS[a.type]||"Other"}</div>
                                {a.label&&<div style={{fontSize:12,color:T.textMuted}}>{ASSET_LABELS[a.type]||"Other"}</div>}
                              </div>
                              <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
                                <span style={{fontSize:14,fontWeight:700,color:T.text}}>{a.amount==null?"—":fmtUSD(Number(a.amount))}</span>
                                <button onClick={()=>removeAsset(a.id)} aria-label="Remove asset" style={{background:"transparent",border:"none",color:T.textMuted,cursor:"pointer",fontSize:13,minHeight:44,padding:"0 4px"}}>Remove</button>
                              </div>
                            </div>
                          ))}
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:4}}>
                            <span style={{fontSize:13,color:T.textMuted}}>Cushion{months!=null?` · ${months.toFixed(1)} months of expenses`:""}</span>
                            <span style={{fontSize:16,fontWeight:800,color:T.text}}>{fmtUSD(cushionBalance(financeData))}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {!plaidConnected && (
                  <div style={{...GC.card,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:T.text}}>No bank connected</div>
                      <div style={{fontSize:12,color:T.textMuted,marginTop:3}}>Link your bank to see account balances and utilization</div>
                    </div>
                    <button onClick={connectPlaid} disabled={plaidLoading} style={{fontSize:13,padding:"8px 20px",borderRadius:10,border:"none",background:T.accentFill,color:"#fff",cursor:"pointer",fontWeight:700,opacity:plaidLoading?0.6:1,flexShrink:0}}>
                      {plaidLoading?"Connecting...":"Connect Bank"}
                    </button>
                  </div>
                )}
                {!plaidFetching && plaidConnected && (
                  <>
                    {/* Net Worth */}
                    <div style={GC.card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={C.cTitle}>Net Worth</div>
                          <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>Assets minus liabilities</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:28,fontWeight:900,color:netWorth>=0?T.success:"rgba(220,50,50,0.9)"}}>{fmtUSD(netWorth)}</div>
                          {balanceAsOf && <div style={{fontSize:11,color:T.textMuted}}>{balanceAsOf}</div>}
                        </div>
                      </div>
                      <div style={{marginTop:12,display:"flex",gap:20,fontSize:12}}>
                        <div><span style={{color:T.textMuted}}>Assets: </span><span style={{fontWeight:700,color:T.success}}>{fmtUSD(plaidAccounts.filter(a=>a.type!=="credit").reduce((a,acc)=>a+(acc.balances?.current||0),0))}</span></div>
                        <div><span style={{color:T.textMuted}}>Liabilities: </span><span style={{fontWeight:700,color:"rgba(220,50,50,0.9)"}}>{fmtUSD(plaidAccounts.filter(a=>a.type==="credit").reduce((a,acc)=>a+(acc.balances?.current||0),0))}</span></div>
                      </div>
                    </div>

                    {/* Plaid connected badge */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:withAlpha(T.success,0.06),border:`1px solid ${withAlpha(T.success,0.2)}`,borderRadius:10,flexWrap:"wrap",gap:8,marginBottom:4}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <div style={{width:8,height:8,borderRadius:"50%",background:T.success,flexShrink:0}}/>
                        <span style={{fontSize:12,color:T.success,fontWeight:600}}>Bank connected via Plaid</span>
                        <span style={{fontSize:11,color:T.overlayLight(0.3)}}>· Read-only · We can never move your money</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        <span style={{fontSize:11,fontWeight:800,color:T.overlayLight(0.4),letterSpacing:"0.06em"}}>PLAID</span>
                      </div>
                    </div>

                    {/* Account cards, ONE CARD PER BANK (multi-bank, 2026-10-13).
                        Every cached account carries item_id + institution_name
                        from the sync stamp; plaidItems is the token-free bank
                        list from the server. Accounts cached before stamping
                        (item_id null) fall into the only bank when there is
                        one, else an "Other accounts" group until the next sync
                        restamps them. Each bank has its own Disconnect and,
                        when its link is broken, its own Reconnect. */}
                    {(()=>{
                      const renderAccount = (a) => {
                        const bal = a.balances?.current || 0;
                        const avail = a.balances?.available;
                        const limit = a.balances?.limit;
                        const isCredit = a.type==="credit";
                        const util = isCredit && limit ? Math.round((bal/limit)*100) : null;
                        const utilColor = util===null?null:util<30?T.success:util<70?T.accent:"rgba(220,50,50,0.9)";
                        return (
                          <div key={a.account_id} style={{padding:"14px",background:T.overlayLight(0.04),borderRadius:12,border:`1px solid ${T.overlayLight(0.06)}`}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom: isCredit&&limit?10:0}}>
                              <div>
                                <div style={{fontSize:14,fontWeight:600,color:T.text}}>{cleanName(a.name)}</div>
                                <div style={{fontSize:11,color:T.textMuted,marginTop:2,textTransform:"capitalize"}}>{a.subtype||a.type}</div>
                              </div>
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:17,fontWeight:800,color:isCredit?"rgba(220,50,50,0.9)":T.accent}}>{fmtUSD(bal,2)}</div>
                                {!isCredit&&avail!=null&&<div style={{fontSize:11,color:T.textMuted,marginTop:1}}>Avail {fmtUSD(avail,2)}</div>}
                                {isCredit&&limit&&<div style={{fontSize:11,color:T.textMuted,marginTop:1}}>Limit {fmtUSD(limit,2)}</div>}
                                {balanceAsOf&&<div style={{fontSize:11,color:T.textDim,marginTop:1}}>{balanceAsOf}</div>}
                              </div>
                            </div>
                            {isCredit && limit && (
                              <>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                                  <span style={{color:T.textMuted}}>Utilization</span>
                                  <span style={{fontWeight:700,color:utilColor}}>{util}%{util<30?" · Good":util<70?" · Moderate":" · High"}</span>
                                </div>
                                <div style={{height:6,borderRadius:99,background:T.overlayLight(0.08)}}>
                                  <div style={{height:"100%",borderRadius:99,background:utilColor,width:`${Math.min(100,util)}%`,transition:"width 0.6s ease"}}/>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      };
                      const known = new Set(plaidItems.map(i=>i.item_id));
                      const labelOf = (it, idx) => it.institution_name || (plaidItems.length > 1 ? `Bank ${idx+1}` : "Connected bank");
                      const groups = plaidItems.map((it, idx) => ({ key: it.item_id, item: it, label: labelOf(it, idx), accounts: plaidAccounts.filter(a => a.item_id === it.item_id) }));
                      const orphan = plaidAccounts.filter(a => !a.item_id || !known.has(a.item_id));
                      if (orphan.length) {
                        if (groups.length === 1 && groups[0].accounts.length === 0) groups[0].accounts = orphan;
                        else groups.push({ key: "other", item: null, label: groups.length ? "Other accounts" : "Connected bank", accounts: orphan });
                      }
                      // plaidItems can lag the connected flag (an older cache); treat a
                      // connected user with no item list as one bank.
                      const bankCount = Math.max(plaidItems.length, plaidConnected ? 1 : 0);
                      const MAX_BANKS = 3;
                      return (
                        <>
                          {groups.map(g => (
                            <div key={g.key} style={GC.card}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:14}}>
                                <div style={{minWidth:0}}>
                                  <div style={C.cTitle}>{g.label}</div>
                                  {g.item?.needs_reauth
                                    ? <div style={{fontSize:11,color:T.warning,fontWeight:600,marginTop:2}}>Needs reconnecting</div>
                                    : g.item?.needs_attention
                                    ? <div style={{fontSize:11,color:T.warning,fontWeight:600,marginTop:2}}>Needs attention</div>
                                    : <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{g.accounts.length} account{g.accounts.length===1?"":"s"}</div>}
                                </div>
                                {g.item && (
                                  <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                                    {(g.item.needs_reauth || g.item.needs_attention) ? (
                                      <button onClick={()=>reconnectPlaidItem(g.item, g.label)} disabled={plaidLoading} style={{...C.btn(T.accent),fontSize:12,padding:"7px 12px"}}>Reconnect</button>
                                    ) : (
                                      <button onClick={()=>addPlaidAccounts(g.item, g.label)} disabled={plaidLoading} aria-label={`Add accounts at ${g.label}`} style={{background:"transparent",border:`1px solid ${withAlpha(T.accent,0.3)}`,borderRadius:10,color:T.accent,fontSize:12,fontWeight:600,padding:"7px 12px",cursor:"pointer",fontFamily:"inherit",opacity:plaidLoading?0.5:1}}>Add accounts</button>
                                    )}
                                    <button onClick={()=>disconnectPlaidItem(g.item, g.label)} disabled={plaidLoading} aria-label={`Disconnect ${g.label}`} style={{background:"transparent",border:`1px solid ${withAlpha(T.danger,0.3)}`,borderRadius:10,color:T.danger,fontSize:12,fontWeight:600,padding:"7px 12px",cursor:"pointer",fontFamily:"inherit",opacity:plaidLoading?0.5:1}}>Disconnect</button>
                                  </div>
                                )}
                              </div>
                              {/* NEW_ACCOUNTS_AVAILABLE (ITEM webhook): a quiet prompt, no
                                  push. Cleared when an update-mode session completes. */}
                              {g.item?.new_accounts_available && !g.item.needs_reauth && (
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"9px 12px",marginBottom:12,borderRadius:10,background:withAlpha(T.accent,0.06),border:`1px solid ${withAlpha(T.accent,0.18)}`}}>
                                  <div style={{fontSize:12,color:T.text}}>New accounts available at {g.label}</div>
                                  <button onClick={()=>addPlaidAccounts(g.item, g.label)} disabled={plaidLoading} style={{background:"none",border:"none",color:T.accent,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:"4px 0",minHeight:32}}>Add them</button>
                                </div>
                              )}
                              {g.accounts.length > 0
                                ? <div style={{display:"flex",flexDirection:"column",gap:12}}>{g.accounts.map(renderAccount)}</div>
                                : <div style={{fontSize:12,color:T.textMuted}}>Accounts will appear after the first sync.</div>}
                            </div>
                          ))}

                          {/* Add / remove banks */}
                          <div style={{paddingTop:4,display:"flex",flexDirection:"column",gap:8}}>
                            {disconnectError&&(
                              <div style={{padding:"9px 12px",borderRadius:8,background:withAlpha(T.danger,0.1),border:`1px solid ${T.danger}`,color:T.danger,fontSize:12,fontWeight:600}}>{disconnectError}</div>
                            )}
                            {bankCount < MAX_BANKS ? (
                              <button onClick={connectPlaid} disabled={plaidLoading} style={{width:"100%",padding:"10px",background:"transparent",border:`1px solid ${withAlpha(T.accent,0.3)}`,borderRadius:10,color:T.accent,fontSize:13,cursor:"pointer",fontWeight:600,fontFamily:"inherit",opacity:plaidLoading?0.5:1}}>
                                {plaidLoading ? "Working..." : "Connect another bank"}
                              </button>
                            ) : (
                              <div style={{fontSize:11,color:T.textMuted,textAlign:"center"}}>You can connect up to {MAX_BANKS} banks. Disconnect one to add another.</div>
                            )}
                            {bankCount >= 2 && (
                              <button onClick={()=>{ if (window.confirm("Disconnect all banks? Every linked account and its transactions will be removed from Scorify.")) disconnectPlaid(); }} disabled={plaidLoading} style={{background:"none",border:"none",color:T.textMuted,fontSize:12,cursor:"pointer",fontFamily:"inherit",padding:"6px",minHeight:32}}>
                                Disconnect all banks
                              </button>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

          </div>
          );
        })()}

        {/* ── HEALTH ── */}
        {contentTab==="health"&&(
          <div style={{display:"flex",flexDirection:"column",gap:18}}>

            {/* ── HERO (Phase 5, TabHealth layout · settled tokens) ──
                Small pillar ring + serif title replaces the banner. VALUES
                are the exact fields the banner's PillarHeader read:
                pillarById.health via pctText (null renders as an em dash,
                never 0%). "See breakdown" toggles the SAME
                showHealthBreakdown + BucketRows. */}
            {(()=>{
              const hp = pillarById.health;
              const C0 = 2*Math.PI*38;
              const dash = hp.pct===null ? 0 : C0*Math.min(100,Math.max(0,hp.pct))/100;
              return (
                <>
                  <div ref={heroRef} style={{display:"flex",alignItems:"center",gap:18,padding:"8px 2px 0",transformOrigin:"left top",willChange:"transform, opacity"}}>
                    <div style={{position:"relative",width:92,height:92,flexShrink:0}}>
                      <div aria-hidden="true" style={{position:"absolute",left:6,top:6,width:80,height:80,borderRadius:"50%",background:G.ringHalo}}/>
                      <svg width="92" height="92" viewBox="0 0 92 92" style={{position:"absolute",left:0,top:0}} aria-hidden="true">
                        <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringTrack} strokeWidth="10"/>
                        {dash>0 && <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringColors[1]} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${C0+20}`} transform="rotate(-90 46 46)"/>}
                      </svg>
                      <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Serif Display',serif",fontSize:22,color:hp.band==='poor'?G.lowValue:T.text}}>{pctText(hp.pct, hp.placeholder)}</span>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                      <span style={{fontFamily:"'DM Serif Display',serif",fontSize:32,lineHeight:1.05,color:T.text}}>Health</span>
                    {hp.stale && hp.computedAt && <span style={{fontSize:11,color:T.textMuted}}>Last updated {relativeTime(hp.computedAt)}{hp.pending ? " · updating…" : ""}</span>}
                      <span style={{fontSize:14,color:T.textMuted}}>{hp.placeholder ? "Updating…" : hp.pct===null ? "Not tracked yet" : hp.trackedCount < 2 ? `Based on ${hp.trackedCount} tracker · ${hp.earned} of ${hp.possible} points` : `${hp.trackedCount} of ${hp.totalCount} trackers · ${hp.earned} of ${hp.possible} points`}</span>
                      <button onClick={()=>setShowHealthBreakdown(s=>!s)} aria-expanded={showHealthBreakdown} style={{minHeight:32,display:"flex",alignItems:"center",background:"transparent",border:"none",padding:0,cursor:"pointer",fontSize:14,fontWeight:600,color:T.accent,fontFamily:"inherit"}}>{showHealthBreakdown?"Hide breakdown":"See breakdown"}</button>
                    </div>
                  </div>
                  {showHealthBreakdown && (
                    <div style={GC.card}>
                      <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Trackers</div>
                      <div style={{fontSize:12,color:T.textMuted,lineHeight:1.55,marginBottom:10}}>Your Health score grades your <strong style={{color:T.text}}>steps</strong> against your own goal or your usual day (a workout or a logged movement habit counts as a full day), counts each <strong style={{color:T.text}}>health goal</strong> by its live progress (a screen-time limit that is over scores low, a workouts goal scores its share), and includes <strong style={{color:T.text}}>sleep</strong> when it is synced. Medications, supplements and check-ups are optional and never cost points when off.</div>
                      <BucketRows buckets={hp.buckets}/>
                      <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5,marginTop:10}}>Trackers shown as {"\u2014"} are not enabled or have no data yet. They are left out of this pillar's score entirely, so turning one off never costs you points.{hp.trackedCount < 2 ? " With one tracker scoring, the percentage is that tracker alone." : ""}</div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Health empty state — only while the Medications tracker is ON
                (2026-10-17): with it off, the only medication entry point is
                the ADD A TRACKER toggle below, so a card that says "Add a
                medication" would contradict the user's choice. */}
            {!isDemoMode && enrolledSet.has("medications") && medications.length === 0 && supplements.length === 0 && (
              <div style={{...GC.card,textAlign:"center",padding:"40px 24px"}}>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text,marginBottom:8}}>Start tracking your health</div>
                <div style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.65}}>Log medications and supplements to boost your Health score.</div>
                <button style={{...C.btn(T.accent),fontSize:14,padding:"12px 28px"}} onClick={()=>setShowAddMed(true)}>Add a medication</button>
              </div>
            )}
            {/* Section order is deliberate (2026-09-08): Apple Health first,
                then supplements, goals, workouts, and medications LAST — the
                tab led with the medication tracker, which made a health tab
                read as a pill reminder. */}
            <AppleHealthSection
              user={user}
              isDemo={isDemo}
            />
            {/* ── SCREEN TIME — ambient usage display, NOT a goal ──────────
                Always-visible on iOS once enabled. Deliberately nothing
                goal-shaped here: no progress bar (a bar implies a target),
                no percentage, no Edit/×. The number is a stepped FLOOR from
                30-min threshold crossings, clamped by wall-clock — the
                caption says exactly what it is. The cap GOAL card (Health &
                Fitness Goals list) is a separate surface reading the same
                day record. Native-only: screen time cannot exist on web,
                and a permanently-unknown card would be noise. */}
            {native && !isDemoMode && stStatus?.available && (() => {
              const todayRow = screenTimeDays.find(d => d.date === localDateStr());
              const usage = reconstructScreenTimeUsage(todayRow);
              const ladderOn = stStatus.usageEnabled && stStatus.hasUsageSelection && stStatus.authorization === "approved";
              // The USAGE window's own start (usage/cap split): a cap edit
              // re-arms only the cap window, so the usage card's "counting
              // since" must not move with it. Falls back to armed_at (pre-split).
              const usageArmedAt = todayRow?.usage_armed_at || todayRow?.armed_at;
              const armedLate = usageArmedAt && (new Date(usageArmedAt) - new Date(todayRow.date + "T00:00:00")) > 15 * 60 * 1000;
              const steps = Math.min(Number(todayRow?.usage_steps_fired) || 0, 32);
              return (
                <div style={{...GC.card, marginBottom: 20}}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom: usage ? 10 : 8}}>
                    <div style={C.cTitle}>Screen Time</div>
                    <div style={{fontSize:11, color:T.textMuted}}>today</div>
                  </div>
                  {usage?.overcount ? (
                    <div style={{fontSize:13, color:T.warning, lineHeight:1.6}}>
                      Today's screen time reading looks off, so we're not showing a number. Your Settings &gt; Screen Time total is the accurate one. This rechecks automatically.
                    </div>
                  ) : (usage && (usage.minutes > 0 || ladderOn)) ? (
                    // Show a NUMBER only when there is a real one (minutes > 0)
                    // or we are actively tracking (ladderOn, where "0m · as of
                    // the last 30-min mark" is the honest "armed, under one step
                    // so far"). A 0-minute row while NOT tracking is UNKNOWN, not
                    // zero (invariant) — e.g. a new account on an already-
                    // authorized device inherited a 0-step row; it falls through
                    // to the "Not tracking yet" entry state below, never "0m".
                    <>
                      <div style={{fontSize:34, fontWeight:800, letterSpacing:"-0.02em", color:T.text, fontFamily:"'DM Serif Display',serif"}}>
                        {formatUsageMinutes(usage.minutes)}
                      </div>
                      {/* The number is preserved when the ladder was revoked
                          after arming (auth/selection off — a reinstall or OS
                          update), same as the stale Plaid balance we keep with an
                          "as of" label. But the LIVE caption ("as of the last
                          30-min mark" / "watching since…") is the false claim — it
                          implies the number is still advancing — so it renders ONLY
                          with ladderOn true. Otherwise: the number stays, an honest
                          not-tracking caption replaces it, and the repair link acts. */}
                      {/* Three honest states, not two. The bug (TestFlight
                          1.1 (6), 2026-09-22): "Not tracking right now" was
                          shown over a session the extension had genuinely
                          armed at 7:32 and was still firing — this card only
                          renders a number when today's row has fired steps, so
                          monitoring WAS on. The caption trusted usageEnabled +
                          hasUsageSelection (ladder-config flags that can read
                          false while the extension runs, e.g. armed via a cap
                          goal rather than the usage picker), not the real
                          "can it run" signal, which is Family Controls
                          authorization. So:
                            ladderOn        → fully live, "counting since".
                            armed + authed  → still tracking, but the detailed
                                              display flag is off. Ambiguous
                                              whether the number is advancing,
                                              so claim NEITHER live nor stopped.
                            not authorized  → it truly cannot run: honest
                                              "not tracking, where it stopped". */}
                      {ladderOn ? (
                        <div style={{fontSize:11, color:T.textMuted, marginTop:4}}>
                          {usage.asOfLabel}
                          {armedLate ? ` · counting since ${new Date(usageArmedAt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}, earlier today not counted` : ""}
                        </div>
                      ) : (stStatus.authorization === "approved" && usageArmedAt) ? (
                        <div style={{fontSize:11, color:T.textMuted, marginTop:4, lineHeight:1.5}}>
                          Today's usage so far.
                          <button onClick={enableUsageDisplay} style={{display:"block", marginTop:6, background:"none", border:"none", color:T.accent, fontWeight:700, fontSize:11, cursor:"pointer", padding:0, fontFamily:"inherit"}}>Turn on Screen Time →</button>
                        </div>
                      ) : (
                        <div style={{fontSize:11, color:T.textMuted, marginTop:4, lineHeight:1.5}}>
                          Not tracking right now. This is where today stood when it stopped.
                          <button onClick={enableUsageDisplay} style={{display:"block", marginTop:6, background:"none", border:"none", color:T.accent, fontWeight:700, fontSize:11, cursor:"pointer", padding:0, fontFamily:"inherit"}}>Turn tracking back on →</button>
                        </div>
                      )}
                      {/* Ambient trace: one soft dot per fired 30-min step.
                          Fired dots only — no empty remainder, so nothing
                          reads as progress toward a target. */}
                      {steps > 0 && (
                        <div style={{display:"flex", flexWrap:"wrap", gap:4, marginTop:10}} aria-hidden="true">
                          {Array.from({length: steps}).map((_, i) => (
                            <span key={i} style={{width:6, height:6, borderRadius:3, background:withAlpha(T.accent, 0.25 + Math.min(0.5, i * 0.02))}}/>
                          ))}
                        </div>
                      )}
                      {/* Why this can read lower than iOS Settings: DeviceActivity
                          counts active on-device app use, while Settings also
                          counts CarPlay and screen-off/background time like
                          navigation. A gap is expected, not a bug (device
                          2026-09-23: a 1h45m CarPlay Maps route showed in Settings
                          but not here). */}
                      <div style={{fontSize:10.5, color:T.textDim, marginTop:10, lineHeight:1.5}}>Counts time actively using apps on this iPhone, so it can read lower than Settings, which also counts CarPlay and navigation.</div>
                    </>
                  ) : ladderOn ? (
                    <div style={{fontSize:13, color:T.textMuted, lineHeight:1.6}}>
                      Watching. Your first number lands after about 30 minutes of use.
                    </div>
                  ) : (
                    <div>
                      <div style={{fontSize:15, fontWeight:800, color:T.text, marginBottom:6}}>Not tracking yet</div>
                      <div style={{fontSize:13, color:T.textMuted, lineHeight:1.6, marginBottom:12}}>
                        Track how much you actually use your phone: a running total for today,
                        counted on this iPhone in 30-minute steps. This is just the number, no goal or limit. You can add a daily limit goal separately anytime.
                      </div>
                      <button onClick={enableUsageDisplay} style={{...C.btn(T.accent),width:"100%"}}>Turn on Screen Time</button>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* ── SUPPLEMENT TRACKER ── */}
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div>
                  <h2 style={{fontSize:18,fontWeight:800}}> Supplement Tracker</h2>
                  <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>Log your daily supplements and build a streak.</div>
                </div>
                <button style={C.btn(T.accent)} onClick={()=>setShowAddSupp(true)}>+ Add Supplement</button>
              </div>

              {/* Today's summary bar */}
              <div style={{background:T.bg,border:"none",borderRadius:14,padding:"14px 20px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontSize:12,color:T.accent,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,marginBottom:4}}>Today's Progress</div>
                  <div style={{fontSize:22,fontWeight:700,color:T.text}}>{supplements.filter(s=>s.takenToday).length}<span style={{fontSize:14,color:T.textMuted,fontWeight:400}}> / {supplements.length} taken</span></div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {supplements.map(s=>(
                    <div key={s.id} title={s.name} style={{width:36,height:36,borderRadius:"50%",background:s.takenToday?withAlpha(T.accent,0.2):T.overlayLight(0.08),border:`2px solid ${s.takenToday?T.accent:T.overlayLight(0.12)}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:s.takenToday?14:(s.icon&&s.icon!=="·"&&s.icon!==""?16:11),fontWeight:700,color:s.takenToday?T.accent:T.overlayLight(0.6),cursor:"pointer",transition:"all 0.2s",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>!s.takenToday&&takeSupp(s.id)}>
                      {s.takenToday?"✓":iconOrInitials(s.icon,s.name)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Supplement cards grid */}
              <div style={C.g()}>
                {supplements.map(s=>{
                  const isNew = suppJustLogged === s.id;
                  const streakColor = s.streak>=14?T.accent:s.streak>=7?T.accent:s.streak>=3?T.accent:T.textMuted;
                  const streakIcon  = s.streak>=14?"·":s.streak>=7?"·":s.streak>=3?"·":"○";
                  return(
                    <div key={s.id} className={isNew?"popped":""} style={{...GC.card, border:s.takenToday?`1px solid ${withAlpha(T.accent,0.3)}`:`1px solid ${T.overlayLight(0.08)}`, background:s.takenToday?withAlpha(T.accent,0.06):C.card.background, transition:"all 0.3s"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <div style={{width:48,height:48,borderRadius:12,background:s.takenToday?withAlpha(T.accent,0.15):withAlpha(T.accent,0.08),border:`1px solid ${s.takenToday?T.accent:withAlpha(T.accent,0.2)}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:s.takenToday?20:(s.icon&&s.icon!=="·"&&s.icon!==""?22:16),fontWeight:700,color:T.accent,fontFamily:"'DM Sans',sans-serif"}}>
                            {s.takenToday?"✓":iconOrInitials(s.icon,s.name)}
                          </div>
                          <div>
                            <div style={{fontSize:15,fontWeight:800}}>{s.name}</div>
                            <div style={{fontSize:12,color:T.textMuted}}>{s.dose && <span style={{color:T.accent,fontWeight:600}}>{s.dose}</span>}{s.dose && s.timing && " · "}{s.timing}</div>
                          </div>
                        </div>
                        <button onClick={()=>{if(window.confirm(`Remove "${s.name}"?`))removeSupp(s.id);}} aria-label={`Remove ${s.name}`} style={{background:"transparent",border:"none",color:T.textDim,cursor:"pointer",fontSize:16,padding:"12px 14px",lineHeight:1}} title="Remove">×</button>
                      </div>

                      {/* Streak */}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:16}}>{streakIcon}</span>
                          <span style={{fontSize:20,fontWeight:900,color:streakColor}}>{s.streak}</span>
                          <span style={{fontSize:11,color:T.textMuted,fontWeight:600}}>day streak</span>
                        </div>
                        {s.takenToday
                          ? <span style={{fontSize:12,color:T.accent,fontWeight:700,background:withAlpha(T.accent,0.15),padding:"3px 10px",borderRadius:99}}>✓ Done today</span>
                          : <button onClick={()=>takeSupp(s.id)} style={{...C.btn(T.accent),fontSize:12,padding:"6px 14px"}}>Take Now ✓</button>
                        }
                      </div>

                      {/* The 28-day heatmap that stood here was removed (map
                          6.5): no per-day supplement history exists in the DB,
                          so its cells were Array(28).fill(0) fabrications that
                          read as 28 missed days on every fresh load. The only
                          real signal — the current streak — is the number
                          shown above. Do not re-add a grid without a real
                          per-day log table behind it. */}
                      {isNew&&(
                        <div style={{marginTop:10,background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:8,padding:"7px 10px",fontSize:12,color:T.accent,fontWeight:700,textAlign:"center",animation:"fadeUp 0.3s ease"}}>
                           Logged! Streak updated 
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Empty state — hidden while the Medications tracker is off
                    (2026-10-17), same rule as the health empty state above:
                    the tab then shows only the trackers the user turned on. */}
                {supplements.length===0&&enrolledSet.has("medications")&&(
                  <div style={{...GC.card,gridColumn:"1/-1",textAlign:"center",padding:40}}>
                    <div style={{fontSize:40,marginBottom:12}}></div>
                    <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>No supplements yet</div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:16}}>Add your first supplement to start tracking your daily consistency.</div>
                    <button style={C.btn(T.accent)} onClick={()=>setShowAddSupp(true)}>+ Add Supplement</button>
                  </div>
                )}
              </div>
            </div>

            {/* Health & Fitness Goals — display only; goals are added from Home */}
            {goals.filter(g => (g.category === "fitness" || g.category === "health") && !g.completed).length > 0 && (
              <div style={GC.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div style={C.cTitle}> Health & Fitness Goals</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {goals.filter(g => (g.category === "fitness" || g.category === "health") && !g.completed).map(g => {
                    const auto = getGoalAutoValue(g);
                    return <GoalCard key={g.id} g={viewGoal(g)} onEdit={openGoalEditor} onUpdate={(g)=>{setShowUpdateGoal(g);setGoalUpdateVal(String(g.current_value));}} onDelete={deleteGoal} autoValue={auto?.value} autoLabel={auto?.label} autoTarget={auto?.target} autoUnit={auto?.unit} autoHasData={auto?.hasData} autoInverted={auto?.inverted} autoNoSource={auto?.noSource} autoStateLine={auto?.stateLine} autoNote={auto?.note} onConnectSource={auto?.noSource==="plaid"?connectPlaid:auto?.noSource==="screen_time"?repairScreenTime:undefined}/>;
                  })}
                </div>
                {goals.filter(g=>(g.category==="fitness"||g.category==="health")&&!g.completed).some(g=>getGoalAutoValue(g)) && (
                  <div style={{marginTop:12,padding:"10px 14px",background:withAlpha(T.success,0.05),border:`1px solid ${withAlpha(T.success,0.15)}`,borderRadius:10,fontSize:12,color:T.textMuted}}>
                     <strong style={{color:T.success}}>Auto-tracking</strong>: tracked automatically from Apple Health, your habits, and your weight log. Log workouts manually for anything Apple Health can't see.
                  </div>
                )}
              </div>
            )}
            {/* ── WORKOUT LOG ── */}
            <div style={GC.card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:17,fontWeight:700,color:T.text}}>Workouts</div>
                <button style={{...C.btn(T.accent),fontSize:11,padding:"4px 12px"}} onClick={()=>{ setNewWorkout({ type:"Lift", customType:"", name:"", duration_min:"", distance_mi:"", notes:"", date: localDateStr() }); setWorkoutError(""); setShowAddWorkout(true); }}>+ Log workout</button>
              </div>
              {workouts.length === 0 ? (
                <div style={{textAlign:"center",padding:"20px 0",color:T.textMuted,fontSize:13}}>No workouts logged yet. Tap + to add one.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {[...workouts].sort((a,b)=>String(b.date||"").localeCompare(String(a.date||""))||String(b.created_at||"").localeCompare(String(a.created_at||""))).slice(0, 7).map(w => (
                    <div key={w.id} style={{background:T.overlayLight(0.05),border:`1px solid ${T.overlayLight(0.08)}`,borderRadius:12,padding:"12px 14px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:14,fontWeight:700,color:T.text}}>{w.type}{w.name ? <span style={{fontWeight:400,color:T.textMuted}}> · {w.name}</span> : null}</div>
                          {/* "45 min · 3.1 mi · Today" — never an ISO date (workoutMeta). */}
                          <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{workoutMeta(w, localDateStr())}</div>
                          {w.notes ? <div style={{fontSize:12,color:T.textDim,marginTop:4,fontStyle:"italic"}}>{w.notes}</div> : null}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,marginLeft:10}}>
                          {workoutDeleteConfirm === w.id ? (
                            <>
                              <button onClick={()=>deleteWorkout(w.id)} style={{fontSize:11,color:T.danger,background:"none",border:`1px solid ${T.danger}`,borderRadius:6,padding:"3px 8px",cursor:"pointer"}}>Confirm</button>
                              <button onClick={()=>setWorkoutDeleteConfirm(null)} style={{fontSize:11,color:T.textMuted,background:"none",border:"none",cursor:"pointer"}}>Cancel</button>
                            </>
                          ) : (
                            <button onClick={()=>setWorkoutDeleteConfirm(w.id)} style={{fontSize:11,color:T.textDim,background:"none",border:"none",cursor:"pointer",padding:"2px 4px"}}>Delete</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {enrolledSet.has("medications") && (
            <div style={C.g()}>
              <div style={GC.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div style={C.cTitle}>Medication Tracker</div>
                  <button style={{...C.btn(T.accent),fontSize:11,padding:"4px 12px"}} onClick={()=>setShowAddMed(true)}>+ Add</button>
                </div>
                {medications.length===0?(
                  <div style={{textAlign:"center",padding:"20px 0"}}>
                    <div style={{fontSize:28,marginBottom:8}}></div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:10}}>No medications added yet.</div>
                    <button style={{...C.btn(T.accent),fontSize:12}} onClick={()=>setShowAddMed(true)}>+ Add Medication</button>
                  </div>
                ):(
                  medications.map(m=>{
                    const today = new Date(); today.setHours(0,0,0,0);
                    const todayStr = localDateStr();
                    const asNeededPat = /as.?needed|prn|when needed/i;
                    const isAsNeeded = asNeededPat.test(m.schedule || '');
                    const refill = m.refill_date ? new Date(m.refill_date) : null;
                    const daysLeft = refill ? Math.ceil((refill - today) / (1000*60*60*24)) : null;
                    const daysLeftDisplay = daysLeft !== null ? Math.max(0, daysLeft) : (m.refill_days || 30);
                    const cycleMax = m.refill_days || 30;
                    const takenToday = m.last_taken_date === todayStr;
                    const color = daysLeftDisplay<=7?T.danger:daysLeftDisplay<=14?T.accent:T.success;
                    const showRefill = !isAsNeeded && refill !== null;
                    const refillOverdue = showRefill && daysLeft !== null && daysLeft <= 0;
                    return(
                      <div key={m.id} style={{background:G.tileBg,border:`1px solid ${T.overlayLight(0.08)}`,borderRadius:12,padding:"12px 16px",marginBottom:10}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div>
                            <div style={{fontSize:14,fontWeight:700}}>{m.name}</div>
                            <div style={{fontSize:12,color:T.textMuted}}>{m.dose} · {m.schedule}</div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            {showRefill && (
                              <div style={{textAlign:"right"}}>
                                {refillOverdue
                                  ? <><div style={{fontSize:11,color:T.danger,fontWeight:700}}>Refill overdue</div></>
                                  : <><div style={{fontSize:11,color:T.textMuted}}>Refill in</div><div style={{fontSize:18,fontWeight:800,color}}>{daysLeft}d</div></>
                                }
                              </div>
                            )}
                            <button onClick={()=>{if(window.confirm(`Remove "${m.name}"?`))removeMed(m.id);}} aria-label={`Remove ${m.name}`} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:16,padding:"12px 14px"}}>×</button>
                          </div>
                        </div>
                        {showRefill && <div style={{marginTop:10}}><Bar value={refillOverdue?0:daysLeftDisplay} max={cycleMax} color={color} h={5}/></div>}
                        {!isAsNeeded && (
                        <button
                          onClick={()=>markMedTaken(m.id)}
                          disabled={takenToday}
                          style={{marginTop:10,width:"100%",background:takenToday?withAlpha(T.success,0.12):withAlpha(T.accent,0.1),border:`1px solid ${takenToday?withAlpha(T.success,0.3):withAlpha(T.accent,0.25)}`,borderRadius:8,padding:"7px 0",fontSize:12,fontWeight:700,color:takenToday?T.success:T.accent,cursor:takenToday?"default":"pointer",transition:"all 0.15s"}}>
                          {takenToday ? "Taken today" : "Log as taken"}
                        </button>
                        )}
                        {isAsNeeded && (
                          <div style={{marginTop:10,textAlign:"center",fontSize:11,color:T.textMuted,fontStyle:"italic"}}>Take as needed, no daily log required</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            )}

            {enrolledSet.has("preventive_care") && (
              <PreventiveCareChecklist user={user} isDemo={isDemoMode} demoItems={isDemoMode ? (DEMO.preventiveCare || []) : undefined} />
            )}

            {/* Add a tracker — optional Health trackers, off by default. Toggling on shows
                the section immediately; off hides it, stops collection, and keeps it out of
                Kai/the briefing. Mirrored by the Profile "What you're tracking" grid. */}
            <div style={GC.card}>
              <div style={{...C.cTitle, marginBottom: 6}}>Add a tracker</div>
              <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5,marginBottom:12}}>Optional trackers. Turn one on to start tracking it; turn it off anytime to hide it and stop collecting that data.</div>
              {OPTIONAL_HEALTH_TRACKERS.map(id=>(
                <div key={id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"10px 0",borderTop:`1px solid ${T.border}`}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:600,color:T.text}}>{OPTIONAL_TRACKER_META[id].label}</div>
                    <div style={{fontSize:12,color:T.textMuted,lineHeight:1.4}}>{OPTIONAL_TRACKER_META[id].desc}</div>
                  </div>
                  <ToggleSwitch on={enrolledSet.has(id)} onChange={()=>toggleTracker(id)} label={OPTIONAL_TRACKER_META[id].label}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── WELLNESS ── */}
        {contentTab==="wellness"&&(
          <div style={{display:"flex",flexDirection:"column",gap:18}}>

            {/* ── HERO (Phase 3, TabWellness layout · settled tokens) ──
                Small pillar ring + serif title replaces the banner. VALUES
                are the exact fields the banner's PillarHeader read:
                pillarById.wellbeing pct/band/trackedCount/totalCount/
                earned/possible via pctText (null renders as an em dash,
                never 0%). "See breakdown" toggles the SAME
                showWellnessBreakdown + BucketRows. */}
            {(()=>{
              const wp = pillarById.wellbeing;
              const C0 = 2*Math.PI*38;
              const dash = wp.pct===null ? 0 : C0*Math.min(100,Math.max(0,wp.pct))/100;
              return (
                <>
                  <div ref={heroRef} style={{display:"flex",alignItems:"center",gap:18,padding:"8px 2px 0",transformOrigin:"left top",willChange:"transform, opacity"}}>
                    <div style={{position:"relative",width:92,height:92,flexShrink:0}}>
                      <div aria-hidden="true" style={{position:"absolute",left:6,top:6,width:80,height:80,borderRadius:"50%",background:G.ringHalo}}/>
                      <svg width="92" height="92" viewBox="0 0 92 92" style={{position:"absolute",left:0,top:0}} aria-hidden="true">
                        <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringTrack} strokeWidth="10"/>
                        {dash>0 && <circle cx="46" cy="46" r="38" fill="none" stroke={G.ringColors[3]} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${C0+20}`} transform="rotate(-90 46 46)"/>}
                      </svg>
                      <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Serif Display',serif",fontSize:22,color:wp.band==='poor'?G.lowValue:T.text}}>{pctText(wp.pct, wp.placeholder)}</span>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                      <span style={{fontFamily:"'DM Serif Display',serif",fontSize:32,lineHeight:1.05,color:T.text}}>Mind</span>
                    {wp.stale && wp.computedAt && <span style={{fontSize:11,color:T.textMuted}}>Last updated {relativeTime(wp.computedAt)}{wp.pending ? " · updating…" : ""}</span>}
                      <span style={{fontSize:14,color:T.textMuted}}>{wp.placeholder ? "Updating…" : wp.pct===null ? "Not tracked yet" : `${wp.trackedCount} of ${wp.totalCount} trackers · ${wp.earned} of ${wp.possible} points`}</span>
                      <button onClick={()=>setShowWellnessBreakdown(s=>!s)} aria-expanded={showWellnessBreakdown} style={{minHeight:32,display:"flex",alignItems:"center",background:"transparent",border:"none",padding:0,cursor:"pointer",fontSize:14,fontWeight:600,color:T.accent,fontFamily:"inherit"}}>{showWellnessBreakdown?"Hide breakdown":"See breakdown"}</button>
                    </div>
                  </div>
                  {showWellnessBreakdown && (
                    <div style={GC.card}>
                      <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Trackers</div>
                      <BucketRows buckets={wp.buckets}/>
                      <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5,marginTop:10}}>Trackers shown as {"\u2014"} are not enabled. They are left out of this pillar's score entirely, so turning one off never costs you points.</div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Sub-tab segmented control (glass, mockup layout) */}
            <div style={{padding:4,borderRadius:24,border:G.hairline,background:G.chipSurface,backdropFilter:G.blurChip,WebkitBackdropFilter:G.blurChip,boxShadow:G.chipShadow,display:"flex",gap:2}}>
              {[["overview","Mood & Check-in"],["journal","Journal"]].map(([v,l])=>(
                <button key={v} onClick={()=>setWellnessSubTab(v)} aria-pressed={wellnessSubTab===v}
                  style={{flexGrow:1,minHeight:40,padding:"0 8px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:20,fontSize:13,fontWeight:600,whiteSpace:"nowrap",border:"none",cursor:"pointer",fontFamily:"inherit",
                    background: wellnessSubTab===v?T.accentFill:"transparent",
                    color: wellnessSubTab===v?"#fff":T.textMuted}}>
                  {l}
                </button>
              ))}
            </div>

            {/* ── OVERVIEW SUB-TAB ── */}
            {wellnessSubTab==="overview"&&<>

            {/* Wellness empty state — no real mood data yet */}
            {!isDemoMode && !moodHistory.some(m=>m.date) && (
              <div style={{...GC.card,textAlign:"center",padding:"40px 24px",marginBottom:0}}>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text,marginBottom:8}}>Check in on your wellbeing</div>
                <div style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.65}}>Track mood and stress to spot patterns early.</div>
                <button style={{...C.btn(T.accent),fontSize:14,padding:"12px 28px"}} onClick={()=>setCheckInStep(1)}>Log today's mood</button>
              </div>
            )}

            {/* Disclaimer — plain page line per the mockup, no box */}
            <div style={{padding:"0 6px",fontSize:13,color:T.textMuted,lineHeight:1.6}}>
              A self-reflection tool, not a clinical diagnosis. If you're struggling, reach a mental health professional or call or text <span style={{color:T.accent,fontWeight:600}}>988</span> (Suicide & Crisis Lifeline).
            </div>

            {/* TOP ROW */}
            <div style={C.g()}>

              {/* Mood, last 14 days (mockup layout; same avgMood/moodTrend/
                  days-logged expressions — avgMood renders an em dash when
                  nothing is logged, never 0) */}
              <div style={{...GC.card}}>
                <div style={{fontSize:18,fontWeight:600,marginBottom:14}}>Mood, last 14 days</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:12,marginBottom:18}}>
                  <div style={{display:"flex",flexDirection:"column",gap:2}}>
                    <span style={{fontFamily:"'DM Serif Display',serif",fontSize:30,lineHeight:1.1,color:avgMood()==="\u2014"?T.textMuted:moodColor(parseFloat(avgMood()))}}>{avgMood()}</span>
                    <span style={{fontSize:13,color:T.textMuted}}>28 day avg</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:2,borderLeft:`1px solid ${G.divider}`,paddingLeft:12}}>
                    <span style={{fontFamily:"'DM Serif Display',serif",fontSize:30,lineHeight:1.1,color:moodTrend()>0?T.success:moodTrend()<0?T.danger:T.textMuted}}>{moodTrend()>0?"▲":moodTrend()<0?"▼":"—"} {Math.abs(moodTrend())||""}</span>
                    <span style={{fontSize:13,color:T.textMuted}}>vs last week</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:2,borderLeft:`1px solid ${G.divider}`,paddingLeft:12}}>
                    <span style={{fontFamily:"'DM Serif Display',serif",fontSize:30,lineHeight:1.1,color:T.text}}>{(()=>{const ago28=new Date();ago28.setDate(ago28.getDate()-28);const ds28=localDateStr(ago28);return moodHistory.filter(d=>d.score!==null&&d.date&&d.date>=ds28).length;})()}</span>
                    <span style={{fontSize:13,color:T.textMuted}}>days logged</span>
                  </div>
                </div>
                {/* Spark line — one bar per calendar day for last 14 days.
                    Grey bar = no entry logged that day. Score bars are colored.
                    Data shape: moodHistory entries must have { date: "YYYY-MM-DD", score: number } */}
                {(()=>{
                  const chartDays = Array.from({length:14},(_,i)=>{
                    const d=new Date(); d.setDate(d.getDate()-(13-i));
                    const ds=localDateStr(d);
                    const entry=moodHistory.find(m=>m.date===ds);
                    return {ds, score: entry?.score||null};
                  });
                  return(
                    <div role="img" aria-label="Mood chart: daily mood scores, last 14 days" style={{display:"flex",alignItems:"flex-end",gap:3,height:56,marginBottom:6}}>
                      {chartDays.map((d,i)=>{
                        const h = d.score ? Math.round((d.score/10)*50) : 4;
                        const col = d.score ? moodColor(d.score) : T.overlayLight(0.08);
                        const isToday = i === 13;
                        return(
                          <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                            <div style={{width:"100%",height:h,borderRadius:"3px 3px 0 0",background:d.score?col:"transparent",opacity:d.score?1:0.6,border:isToday&&d.score?`1px solid ${col}`:(d.score?"none":`1px dashed ${T.overlayLight(0.25)}`),boxSizing:"border-box"}}/>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.textMuted}}>
                  <span>14 days ago</span><span>Today</span>
                </div>
              </div>

              {/* Daily Mood Log (mockup: serif 1-10 grid; same handlers) */}
              <div style={{...GC.card}}>
                <div style={{fontSize:18,fontWeight:600,marginBottom:14}}>How are you feeling today?</div>
                {!moodLogged ? (
                  <div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(5,minmax(0,1fr))",gap:8,marginBottom:8}}>
                      {[1,2,3,4,5,6,7,8,9,10].map(n=>(
                        <button key={n} onClick={()=>setTodayMood(n)}
                          aria-pressed={todayMood===n}
                          style={{minHeight:48,borderRadius:14,background:todayMood===n?moodColor(n):G.tileBg,border:`1px solid ${todayMood===n?moodColor(n):T.overlayLight(0.12)}`,color:todayMood===n?"#fff":T.text,fontFamily:"'DM Serif Display',serif",fontSize:20,cursor:"pointer",transition:"all 0.15s"}}>
                          {n}
                        </button>
                      ))}
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.textMuted,marginBottom:14}}><span>Rough</span><span>Great</span></div>
                    {todayMood&&(
                      <div style={{textAlign:"center",marginBottom:12}}><span style={{fontSize:14,color:moodColor(todayMood),fontWeight:700}}>{todayMood >= 8?"Feeling good!":todayMood>=5?"Doing okay":todayMood>=3?"Rough day":"Tough time"}</span></div>
                    )}
                    <textarea value={todayNote} onChange={e=>setTodayNote(e.target.value)} aria-label="Mood note"
                      placeholder="Optional: anything on your mind today? (just for you)"
                      style={{...C.inp,width:"100%",minHeight:72,resize:"none",fontSize:16,marginBottom:12}}/>
                    <div style={{display:"flex",gap:8}}>
                      <button style={{...C.btn(T.accent),flex:1,opacity:moodSaving?0.7:1}} onClick={logMood} disabled={!todayMood||moodSaving} aria-busy={moodSaving}>{moodSaving?"Saving…":"Log Mood"}</button>
                      <button style={{...C.ghost}} onClick={()=>setCheckInStep(1)}>Full Check-in →</button>
                    </div>
                  </div>
                ) : (
                  <div style={{textAlign:"center",padding:"10px 0"}}>
                    {/* The SAVED score (today's row), never the draft the grid edits. */}
                    <div style={{fontSize:28,fontWeight:900,color:moodColor(savedMood ?? todayMood)}}>{savedMood ?? todayMood}/10</div>
                    <div style={{fontSize:13,color:T.textMuted,margin:"8px 0 16px"}}>Logged today ✓</div>
                    {todayNote&&<div style={{fontSize:12,color:T.textMuted,background:T.overlayLight(0.06),borderRadius:10,padding:"8px 12px",marginBottom:16,fontStyle:"italic"}}>"{todayNote}"</div>}
                    {/* Edit REPLACES today's row (one mood per day): the grid opens on the saved score. */}
                    <button style={C.ghost} onClick={()=>{setMoodLogged(false);setTodayMood(savedMood ?? todayMood);}}>Edit</button>
                  </div>
                )}
              </div>

              {/* Weekly patterns */}
              <div style={GC.card}>
                <div style={C.cTitle}>28-Day Mood Calendar</div>
                <div style={{display:"grid",gridTemplateColumns:SEVEN_EQUAL_COLUMNS,gap:4,marginBottom:8}}>
                  {["M","T","W","T","F","S","S"].map((d,i)=><div key={i} style={{fontSize:10,color:T.textMuted,textAlign:"center",fontWeight:700}}>{d}</div>)}
                  {(()=>{
                    const calDays=Array.from({length:28},(_,i)=>{
                      const d=new Date(); d.setDate(d.getDate()-(27-i));
                      const ds=localDateStr(d);
                      const entry=moodHistory.find(m=>m.date===ds);
                      const dayStr=d.toLocaleDateString('en-US',{weekday:'short'});
                      return {ds, score: entry?.score||null, day: dayStr};
                    });
                    return calDays.map((d,i)=>(
                      <div key={i} title={d.score?`${d.day}: ${d.score}/10`:d.day}
                        style={{height:28,borderRadius:6,background:d.score?moodColor(d.score):T.overlayLight(0.07),opacity:d.score?0.9:0.3,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:d.score?(d.score<=4||d.score>=9?"#fff":"#1a1a1a"):T.textMuted,fontWeight:d.score?800:400}}>
                        {d.score||""}
                      </div>
                    ));
                  })()}
                </div>
                <div style={{display:"flex",gap:10,marginTop:6,flexWrap:"wrap"}}>
                  {[[T.danger,"Low (1–4)"],[T.warning,"Okay (5–6)"],[T.moodMid,"Good (7–8)"],[T.success,"Great (9–10)"]].map(([c,l])=>(
                    <div key={l} style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:T.textMuted}}>
                      <div style={{width:10,height:10,borderRadius:2,background:c}}/>{l}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* WEEKLY CHECK-IN MODAL FLOW */}
            {checkInStep > 0 && checkInStep < 4 && (
              <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>{}}>
                <div style={{...C.mbox,width:480,maxHeight:"90vh",overflowY:"auto"}}>
                  {/* Progress */}
                  <div style={{display:"flex",gap:6,marginBottom:20}}>
                    {[1,2,3].map(s=>(
                      <div key={s} style={{flex:1,height:4,borderRadius:99,background:checkInStep>=s?T.accent:T.overlayLight(0.07),transition:"background 0.3s"}}/>
                    ))}
                  </div>

                  {checkInStep===1&&(
                    <div>
                      <div style={{fontSize:20,marginBottom:6}}></div>
                      <div style={{fontSize:17,fontWeight:800,marginBottom:4}}>How are you feeling?</div>
                      <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>Step 1 of 3: Mood and note</div>
                      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
                        {[1,2,3,4,5,6,7,8,9,10].map(n=>(
                          <button key={n} onClick={()=>setTodayMood(n)}
                            style={{width:42,height:42,borderRadius:10,background:todayMood===n?moodColor(n):T.surfaceAlt,border:`2px solid ${todayMood===n?moodColor(n):T.overlayLight(0.08)}`,color:todayMood===n?"#fff":T.textMuted,fontWeight:900,fontSize:15,cursor:"pointer"}}>
                            {n}
                          </button>
                        ))}
                      </div>
                      <textarea value={todayNote} onChange={e=>setTodayNote(e.target.value)} aria-label="Mood note"
                        placeholder="Anything on your mind? (optional, private)"
                        style={{...C.inp,width:"100%",minHeight:80,resize:"none",fontSize:16,marginBottom:16}}/>
                      <div style={{display:"flex",gap:8}}>
                        <button style={{...C.btn(T.accent),flex:1}} onClick={()=>{if(todayMood&&!moodSaving){logMood();setCheckInStep(2);}}} disabled={!todayMood||moodSaving}>Next →</button>
                        <button style={C.ghost} onClick={()=>setCheckInStep(0)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {checkInStep===2&&(
                    <div>
                      <div style={{fontSize:20,marginBottom:6}}></div>
                      <div style={{fontSize:17,fontWeight:800,marginBottom:4}}>Low mood screen</div>
                      <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>Step 2 of 3: PHQ-2, two quick questions</div>
                      {PHQ2.map((q,qi)=>(
                        <div key={qi} style={{marginBottom:18}}>
                          <div style={{fontSize:13,fontWeight:600,marginBottom:10,lineHeight:1.5}}>{q}</div>
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {PHQ_OPTS.map((opt,oi)=>(
                              <button key={oi} onClick={()=>{const a=[...phqAnswers];a[qi]=oi;setPhqAnswers(a);}}
                                style={{textAlign:"left",background:phqAnswers[qi]===oi?withAlpha(T.accent,0.15):T.surfaceAlt,border:`1px solid ${phqAnswers[qi]===oi?T.accent:T.overlayLight(0.08)}`,borderRadius:8,padding:"8px 12px",color:phqAnswers[qi]===oi?T.accent:T.textMuted,fontSize:13,cursor:"pointer",fontWeight:phqAnswers[qi]===oi?700:400}}>
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div style={{display:"flex",gap:8,marginTop:4}}>
                        <button style={{...C.btn(T.accent),flex:1,opacity:phqAnswers.every(a=>a!==null)?1:0.4}} onClick={()=>{if(phqAnswers.every(a=>a!==null))setCheckInStep(3);}} disabled={!phqAnswers.every(a=>a!==null)}>Next →</button>
                        <button style={C.ghost} onClick={()=>setCheckInStep(1)}>← Back</button>
                      </div>
                    </div>
                  )}

                  {checkInStep===3&&(
                    <div>
                      <div style={{fontSize:17,fontWeight:800,marginBottom:4}}>Anxiety screen</div>
                      <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>Step 3 of 3: GAD-2, two quick questions</div>
                      {GAD2.map((q,qi)=>(
                        <div key={qi} style={{marginBottom:18}}>
                          <div style={{fontSize:13,fontWeight:600,marginBottom:10,lineHeight:1.5}}>{q}</div>
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {PHQ_OPTS.map((opt,oi)=>(
                              <button key={oi} onClick={()=>{const a=[...gadAnswers];a[qi]=oi;setGadAnswers(a);}}
                                style={{textAlign:"left",background:gadAnswers[qi]===oi?withAlpha(T.accent,0.15):T.surfaceAlt,border:`1px solid ${gadAnswers[qi]===oi?T.accent:T.overlayLight(0.08)}`,borderRadius:8,padding:"8px 12px",color:gadAnswers[qi]===oi?T.accent:T.textMuted,fontSize:13,cursor:"pointer",fontWeight:gadAnswers[qi]===oi?700:400}}>
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div style={{display:"flex",gap:8,marginTop:4}}>
                        <button style={{...C.btn(T.accent),flex:1,opacity:gadAnswers.every(a=>a!==null)?1:0.4}} onClick={()=>{if(gadAnswers.every(a=>a!==null))finishCheckIn();}} disabled={!gadAnswers.every(a=>a!==null)}>See Results →</button>
                        <button style={C.ghost} onClick={()=>setCheckInStep(2)}>← Back</button>
                      </div>
                    </div>
                  )}

                  {/* 988 is reachable from every step of the check-in, not
                      only after an elevated result. */}
                  <div style={{marginTop:18,paddingTop:12,borderTop:`1px solid ${T.border}`,fontSize:11,color:T.textDim,textAlign:"center",lineHeight:1.6}}>
                    Need support right now? Call or text <a href="tel:988" style={{color:T.accent,fontWeight:700,textDecoration:"none"}}>988</a>, free and available 24 hours a day.
                  </div>
                </div>
              </div>
            )}

            {/* CHECK-IN RESULTS */}
            {checkInStep===4&&checkInResults&&(
              <div role="dialog" aria-modal="true" style={C.overlay}>
                <div style={{...C.mbox,width:500,maxHeight:"90vh",overflowY:"auto"}}>
                  <div style={{fontSize:24,marginBottom:8}}></div>
                  <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>Check-in Complete</div>
                  <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>Here's a gentle summary of how you're doing.</div>

                  <div style={C.g("1fr 1fr")}>
                    {[
                      {label:"Mood Today",val:`${checkInResults.mood}/10`,sub:checkInResults.mood>=7?"Feeling good":"Could be better",color:moodColor(checkInResults.mood)},
                      {label:"Depression Screen",val:`${checkInResults.phqTotal}/6`,sub:checkInResults.phqRisk==="low"?"Low indicators":checkInResults.phqRisk==="below_threshold"?"Below screening threshold":"Elevated, consider support",color:checkInResults.phqRisk==="elevated"?T.danger:T.success},
                      {label:"Anxiety Screen",val:`${checkInResults.gadTotal}/6`,sub:checkInResults.gadRisk==="low"?"Low indicators":checkInResults.gadRisk==="below_threshold"?"Below screening threshold":"Elevated, consider support",color:checkInResults.gadRisk==="elevated"?T.danger:T.success},
                      {label:"Logged Streak",val:(()=>{const n=moodHistory.filter(d=>d.score!==null).length;return `${n} ${n===1?"day":"days"}`;})(),sub:"Keep checking in",color:T.success},
                    ].map(r=>(
                      <div key={r.label} style={{background:T.overlayLight(0.06),borderRadius:12,padding:"14px 16px",textAlign:"center"}}>
                        <div style={{fontSize:22,fontWeight:900,color:r.color}}>{r.val}</div>
                        <div style={{fontSize:12,fontWeight:700,color:T.textMuted,marginBottom:2}}>{r.label}</div>
                        <div style={{fontSize:11,color:r.color}}>{r.sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* AI Response */}
                  <div style={{marginTop:16,background:T.overlayLight(0.06),border:`1px solid ${T.overlayLight(0.08)}`,borderRadius:12,padding:"14px 16px"}}>
                    <div style={{fontSize:11,color:T.accent,fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Note from {coachName}</div>
                    {wellnessAiLoading
                      ? <div style={{fontSize:13,color:T.textMuted}}>Writing your summary...</div>
                      : <div style={{fontSize:13,lineHeight:1.7,color:T.text}}>{wellnessMsg}</div>
                    }
                  </div>

                  {(checkInResults.phqRisk==="elevated"||checkInResults.gadRisk==="elevated")&&(
                    <div style={{marginTop:12,background:withAlpha(T.danger,0.08),border:`1px solid ${withAlpha(T.danger,0.3)}`,borderRadius:10,padding:"12px 14px",fontSize:12,color:T.danger,lineHeight:1.6}}>
                      Your responses suggest you may be struggling. You are not alone. Please consider talking to someone: call or text <strong>988</strong>, free and available 24 hours a day, or reach out to a therapist or trusted person.
                    </div>
                  )}

                  <button style={{...C.btn(T.accent),width:"100%",marginTop:16}} onClick={resetCheckIn}>Done</button>
                </div>
              </div>
            )}

            {/* Personal Goals — category === 'personal' routes HERE. With
                category as the goal router (finance → Finance, fitness/health
                → Health), personal goals had no tab at all: only Home's top-3
                and Profile's full list ever showed them. Wellness is the Mind
                pillar and the right home for self-improvement goals.
                Display only — the one Add Goal entry point is on Home. Same
                GoalCard + getGoalAutoValue as the other three sections: no
                fourth renderer, so all surfaces agree about progress. */}
            <div style={GC.card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={C.cTitle}> Personal Goals</div>
              </div>
              {goals.filter(g => g.category === "personal" && !g.completed).length === 0 ? (
                <div style={{fontSize:13,color:T.textMuted,textAlign:"center",padding:"12px 0"}}>No personal goals yet. Add one from the Home tab.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {goals.filter(g => g.category === "personal" && !g.completed).map(g => {
                    const auto = getGoalAutoValue(g);
                    return <GoalCard key={g.id} g={viewGoal(g)} onEdit={openGoalEditor} onUpdate={(g)=>{setShowUpdateGoal(g);setGoalUpdateVal(String(g.current_value));}} onDelete={deleteGoal} autoValue={auto?.value} autoLabel={auto?.label} autoTarget={auto?.target} autoUnit={auto?.unit} autoHasData={auto?.hasData} autoInverted={auto?.inverted} autoNoSource={auto?.noSource} autoStateLine={auto?.stateLine} autoNote={auto?.note} onConnectSource={auto?.noSource==="plaid"?connectPlaid:auto?.noSource==="screen_time"?repairScreenTime:undefined}/>;
                  })}
                </div>
              )}
            </div>

            {/* INSIGHTS + RESOURCES ROW */}
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Insights — full width, horizontal wrap */}
              <div style={GC.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div>
                    <div style={C.cTitle}>Patterns & Insights</div>
                    <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginTop:2}}>Based on your actual logged data</div>
                  </div>
                  <span style={{fontSize:10,background:withAlpha(T.accent,0.15),color:T.accent,border:`1px solid ${withAlpha(T.accent,0.3)}`,borderRadius:99,padding:"3px 10px",fontWeight:700,letterSpacing:0.5}}>LIVE</span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)",gap:10}}>
                {computeInsights().map((ins,i)=>{
                  const bgMap = {
                    positive:withAlpha(T.success,0.04),
                    warning:"rgba(248,113,113,0.05)",
                    alert:"rgba(248,113,113,0.08)",
                    pattern:"rgba(250,204,21,0.04)",
                    neutral:"rgba(249,115,22,0.04)",
                    info:"rgba(100,116,139,0.04)"
                  };
                  return (
                    <div key={i} style={{
                      padding:"11px 12px",
                      borderRadius:10,
                      background: bgMap[ins.type]||"transparent",
                      border:`1px solid ${ins.color}22`,
                    }}>
                      <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                        <span style={{fontSize:22,flexShrink:0,marginTop:1}}>{ins.icon}</span>
                        <div style={{flex:1,minWidth:0}}>
                          {/* Tag + text */}
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:6}}>
                            {ins.tag && (
                              <span style={{fontSize:10,fontWeight:800,color:ins.color,background:`${ins.color}18`,border:`1px solid ${ins.color}33`,borderRadius:99,padding:"2px 8px",textTransform:"uppercase",letterSpacing:0.5,flexShrink:0}}>
                                {ins.tag}
                              </span>
                            )}
                          </div>
                          <div style={{fontSize:13,color:T.text,lineHeight:1.65,marginBottom:ins.recommendation?10:0}}>{ins.text}</div>
                          {/* Recommendation */}
                          {ins.recommendation && (
                            <div style={{display:"flex",alignItems:"flex-start",gap:6,background:G.tileBg,borderRadius:8,padding:"8px 10px",border:`1px solid ${T.overlayLight(0.08)}`}}>
                              <span style={{fontSize:12,flexShrink:0,marginTop:1}}></span>
                              <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}><span style={{color:T.textMuted,fontWeight:700}}>Tip: </span>{ins.recommendation}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>

              {/* Check-in + Resources side by side */}
              <div style={C.g()}>
              <div style={{...GC.card, alignSelf:"flex-start"}}>
                <div style={C.cTitle}>Weekly Check-in</div>
                <div style={{fontSize:13,color:T.textMuted,marginBottom:16,lineHeight:1.6}}>Takes 2 minutes. Includes a mood log, a 2-question depression screen (PHQ-2), and a 2-question anxiety screen (GAD-2). Results are private and never shared.</div>
                <button style={{...C.btn(T.accent),width:"100%",marginBottom:10}} onClick={()=>setCheckInStep(1)}>Start Weekly Check-in →</button>
                <div style={{fontSize:11,color:T.textMuted,lineHeight:1.6}}>Based on validated clinical screening tools. Not a diagnosis; for self-awareness only.</div>
              </div>

              <div style={{...GC.card, alignSelf:"flex-start"}}>
                <div style={C.cTitle}>Mental Health Resources</div>
                {[
                  {name:"988 Suicide & Crisis Lifeline",desc:"Call or text 988, free, 24/7",color:T.danger,urgent:true},
                  {name:"Crisis Text Line",desc:"Text HOME to 741741",color:T.accent,urgent:true},
                  {name:"NAMI Helpline",desc:"1-800-950-NAMI (6264)",color:T.accent,urgent:false},
                  {name:"BetterHelp / Talkspace",desc:"Online therapy, affordable plans",color:T.success,urgent:false},
                  {name:"Headspace / Calm",desc:"Guided meditation & sleep tools",color:T.accent,urgent:false},
                  {name:`Talk to ${coachName}`,desc:"Available anytime in the Coach tab",color:T.accent,urgent:false},
                ].map(r=>(
                  <div key={r.name} style={C.row}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:r.urgent?"#fca5a5":T.accent}}>{r.name}</div>
                      <div style={{fontSize:11,color:T.textMuted}}>{r.desc}</div>
                    </div>
                    {r.urgent&&<span style={{fontSize:10,background:"rgba(248,113,113,0.15)",color:T.danger,padding:"2px 8px",borderRadius:99,fontWeight:700,border:"1px solid rgba(248,113,113,0.3)"}}>24/7</span>}
                  </div>
                ))}
              </div>
              </div>
            </div>
            </>}

            {/* ── JOURNAL SUB-TAB ── */}
            {wellnessSubTab==="journal"&&(
              <div style={{display:"flex",flexDirection:"column",gap:18}}>

                {/* Write entry card */}
                <div style={GC.card}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                    <div>
                      <div style={C.cTitle}>Today's Entry</div>
                      <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>Private by default. Write freely. Your thoughts are just for you.</div>
                    </div>
                    <div style={{fontSize:11,color:T.textMuted,flexShrink:0,marginLeft:12}}>
                      {new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
                    </div>
                  </div>

                  {/* Mood selector */}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>Mood (optional)</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {[1,2,3,4,5,6,7,8,9,10].map(n=>(
                        <button key={n} onClick={()=>setJournalMood(journalMood===n?null:n)}
                          style={{width:34,height:34,borderRadius:8,
                            background:journalMood===n?withAlpha(moodColor(n),0.3):T.overlayLight(0.06),
                            border:`1px solid ${journalMood===n?(n>=7?T.success:n>=5?T.accent:T.danger):T.overlayLight(0.1)}`,
                            color:journalMood===n?T.text:T.textMuted,
                            fontWeight:700,fontSize:12,cursor:"pointer",transition:"all 0.15s"}}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Text area */}
                  <textarea
                    value={journalText}
                    onChange={e=>setJournalText(e.target.value)}
                    aria-label="Journal entry"
                    placeholder="What's on your mind today? How are you feeling? What happened? There are no rules here."
                    style={{...C.inp,width:"100%",minHeight:160,resize:"vertical",fontSize:16,lineHeight:1.7,marginBottom:14}}
                  />

                  {/* Save button */}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontSize:11,color:T.textMuted}}>{journalText.length > 0 ? `${journalText.length} characters` : "Start writing..."}</div>
                    <button
                      onClick={saveJournalEntry}
                      disabled={!journalText.trim() || journalSaving}
                      style={{...C.btn(T.accent),opacity:journalText.trim()?1:0.4,fontSize:13,padding:"8px 20px"}}>
                      {journalSaving ? "Saving..." : "Save Entry"}
                    </button>
                  </div>
                </div>

                {/* Coach privacy toggle */}
                <div style={{...GC.card,padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:16}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:3}}>Share with AI Coach</div>
                    <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}>Let your coach reference recent journal entries to give more personalized insights. Only the last 5 entries are shared, never stored externally.</div>
                  </div>
                  <div onClick={()=>toggleJournalCoach(!journalCoachEnabled)}
                    style={{width:46,height:26,borderRadius:99,background:journalCoachEnabled?T.accent:T.overlayLight(0.1),position:"relative",cursor:"pointer",transition:"background 0.2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:4,left:journalCoachEnabled?22:4,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.3)"}}/>
                  </div>
                </div>

                {/* Past entries */}
                {journalEntries.length > 0 && (
                  <div style={GC.card}>
                    <div style={C.cTitle}>Past Entries</div>
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      {journalEntries.map((entry,i)=>(
                        <div key={entry.id} style={{background:T.overlayLight(0.04),border:`1px solid ${T.overlayLight(0.07)}`,borderRadius:12,padding:"14px 16px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                            <div style={{display:"flex",alignItems:"center",gap:10}}>
                              <span style={{fontSize:12,color:T.textMuted,fontWeight:600}}>
                                {new Date(entry.created_at).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"})}
                              </span>
                              {entry.mood_score && (
                                <span style={{fontSize:11,fontWeight:700,
                                  color:entry.mood_score>=7?T.success:entry.mood_score>=5?T.accent:T.danger,
                                  background:entry.mood_score>=7?withAlpha(T.success,0.12):entry.mood_score>=5?withAlpha(T.accent,0.12):withAlpha(T.danger,0.12),
                                  border:`1px solid ${entry.mood_score>=7?withAlpha(T.success,0.25):entry.mood_score>=5?withAlpha(T.accent,0.25):withAlpha(T.danger,0.25)}`,
                                  borderRadius:99,padding:"2px 8px"}}>
                                  {entry.mood_score}/10
                                </span>
                              )}
                            </div>
                            <button onClick={()=>{if(window.confirm(`Delete your journal entry from ${new Date(entry.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric"})}? This cannot be undone.`))deleteJournalEntry(entry.id);}}
                              aria-label="Delete entry" style={{background:"transparent",border:"none",color:T.overlayLight(0.2),cursor:"pointer",fontSize:16,padding:"12px 14px",lineHeight:1}}
                              title="Delete entry">×</button>
                          </div>
                          <div style={{fontSize:13,color:T.overlayLight(0.75),lineHeight:1.75,whiteSpace:"pre-wrap"}}>{entry.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {journalEntries.length === 0 && (
                  <div style={{...GC.card,textAlign:"center",padding:"40px 20px"}}>
                    <div style={{fontSize:13,color:T.textMuted,lineHeight:1.7,maxWidth:360,margin:"0 auto"}}>
                      Your journal is empty. Write your first entry above — even a few sentences a day builds a powerful record of how you've grown.
                    </div>
                  </div>
                )}

              </div>
            )}

          </div>
        )}



        {/* ── PROFILE & BODY STATS ── */}
        {contentTab==="profile"&&(()=>{
          const bs = bodyStats;
          // Rebased on current_weight alone: height is no longer collected
          // (orphaned by the BMI/calorie removal), and gating the Your Goal
          // card and weight log on a question nobody answers would hide both
          // for every new user.
          const hasStats = !!bs.current_weight;
          // BMI / BMR / TDEE / calorie targets were REMOVED 2026-09-07: unverified
          // estimates with no accountability value (and the calorie block carried
          // two contradictory floors — map entry 3.8, resolved by removal).
          // Do not reintroduce derived body metrics the app cannot verify.
          const goalLbs = bs.goal_weight && bs.current_weight ? Math.abs(bs.current_weight - bs.goal_weight).toFixed(1) : null;
          const goalDir = bs.goal_weight && bs.current_weight ? (bs.goal_weight < bs.current_weight ? "lose" : "gain") : null;
          const goalTypes = { fat_loss:" Fat Loss", muscle_gain:" Muscle Gain", general_fitness:" General Fitness", maintenance:" Maintenance", custom:" Custom Goal" };
          const wLogSorted = [...weightLog].sort((a,b) => new Date(a.logged_at)-new Date(b.logged_at));
          const startWeight = wLogSorted[0]?.weight || bs.current_weight;
          const totalChange = startWeight && bs.current_weight ? (bs.current_weight - startWeight).toFixed(1) : null;

          return (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>

              {/* 1 · IDENTITY */}
              {(()=>{
                const lvl = getLevel(progress.xp||0);
                const nextLvl = getNextLevel(progress.xp||0);
                // eslint-disable-next-line no-unused-vars
                const xpToNext = nextLvl ? nextLvl.minXP - (progress.xp||0) : 0;
                // eslint-disable-next-line no-unused-vars
                const xpPct = nextLvl ? Math.round(((progress.xp||0) - getLevel(progress.xp||0).minXP) / (nextLvl.minXP - getLevel(progress.xp||0).minXP) * 100) : 100;
                return (
                  <div style={{background:G.cardSurface,border:G.hairline,boxShadow:G.shadow,backdropFilter:G.blurCard,WebkitBackdropFilter:G.blurCard,borderRadius:16,padding:"20px 28px",display:"flex",flexDirection:"column",gap:16}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
                      <div style={{display:"flex",alignItems:"center",gap:18}}>
                        <div style={{position:"relative",cursor:"pointer"}} onClick={()=>{ setProfileForm({...bs}); setShowEditProfile(true); }}>
                          {avatarUrl
                            ? <img src={avatarUrl} alt="avatar" style={{width:64,height:64,borderRadius:"50%",objectFit:"cover",border:"2px solid rgba(0,0,0,0.08)"}}/>
                            : <div style={{width:64,height:64,borderRadius:"50%",background:`linear-gradient(135deg,${T.textMuted},${T.accent})`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:22,color:"#fff"}}>
                                {(username||"Me").slice(0,2).toUpperCase()}
                              </div>
                          }
                          <div style={{position:"absolute",bottom:-2,right:-2,width:20,height:20,background:T.accentFill,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:700,border:`2px solid ${T.surface}`}}>+</div>
                        </div>
                        <div>
                          <div style={{fontSize:22,fontWeight:900}}>{username||"Set your name"}</div>
                          {/* Signed-in email, read-only — so the user can tell
                              which account they're in (real sessions only; demo
                              has no user). */}
                          {user?.email && <div style={{fontSize:12.5,color:T.textMuted,marginTop:3,wordBreak:"break-all"}}>{user.email}</div>}
                          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                            <span style={{fontSize:13,fontWeight:700,color:lvl.color}}>{lvl.name}</span>
                            <span style={{fontSize:11,color:T.textMuted}}>·</span>
                            <span style={{fontSize:13,color:T.accent,fontWeight:700}}> {progress.daily_streak||0} day streak</span>
                          </div>
                          {bs.goal_type && <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{goalTypes[bs.goal_type]||bs.goal_type}</div>}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button style={{...C.btn(T.accent)}} onClick={()=>{ setProfileForm({...bs}); setShowEditProfile(true); }}> Edit Profile</button>
                        <button style={{...C.ghost,fontSize:11,padding:"6px 12px"}} onClick={()=>{setOnboardingStep(0);setShowOnboarding(true);}}>Setup Guide</button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {timezoneWarning && (
                <div style={{...GC.card, border:`1px solid ${T.danger}`, background:withAlpha(T.danger,0.08)}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.danger,marginBottom:4}}>Time zone not saved</div>
                  <div style={{fontSize:12,color:T.textMuted,lineHeight:1.6}}>
                    We could not save your time zone, so reminders are paused: sending them
                    without it would mean notifying you at the wrong hour. Reopening the app
                    will retry automatically.
                  </div>
                </div>
              )}

              {/* 2 · ACTIVE GOALS — same resolver + display logic as the Home
                  widget and GoalCard, so a third renderer cannot disagree with
                  them about progress or the syncing state. */}
              <div style={GC.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={C.cTitle}>Active Goals</div>
                  <button onClick={()=>{ document.getElementById("profile-all-goals")?.scrollIntoView({ behavior:"smooth", block:"start" }); }}
                    style={{background:"none",border:"none",color:T.textMuted,fontSize:12,fontWeight:600,cursor:"pointer",padding:0}}>View all →</button>
                </div>
                {goals.filter(g=>!g.completed).length === 0 ? (
                  <div style={{fontSize:13,color:T.textMuted,textAlign:"center",padding:"10px 0"}}>No active goals yet.</div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {linkWorkoutGoals(goals, habits).filter(g=>!g.completed).slice(0,3).map(g=>{
                      const auto = getGoalAutoValue(g);
                      const noSource = auto?.noSource != null;
                      const isSyncing = auto?.label != null && auto?.hasData === false && !noSource;
                      const val = isSyncing ? 0 : (auto?.value ?? g.current_value);
                      const target = auto?.target ?? g.period_target ?? g.target_value;
                      const unit = auto?.unit ?? g.unit;
                      const pct = target > 0 && !isSyncing ? Math.min(100, Math.round((val/target)*100)) : 0;
                      const isRecurring = g.period === "week" || g.period === "month" || g.period === "day";
                      const periodLabel = g.period === "week" ? "this week" : g.period === "month" ? "this month" : "today";
                      const catColors = { fitness:T.success, finance:T.accent, health:T.accent, personal:T.accent };
                      const col = catColors[g.category] || T.accent;
                      return (
                        <div key={g.id}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5,gap:10}}>
                            <div style={{fontSize:13,fontWeight:700,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.title}</div>
                            <div style={{fontSize:12,color:T.textMuted,flexShrink:0}}>
                              {noSource
                                ? <>— / {Number(target||0).toLocaleString()} {unit||""} · <button onClick={auto?.noSource==="screen_time"?repairScreenTime:connectPlaid} style={{background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",padding:0,fontFamily:"inherit"}}>{auto?.noSource==="screen_time"?"Allow Screen Time →":"Connect your bank →"}</button></>
                                : isSyncing
                                ? `— / ${Number(target||0).toLocaleString()} ${unit||""} · syncing…`
                                : isRecurring
                                  ? `${Number(val).toLocaleString()} / ${Number(target).toLocaleString()} ${unit||""} ${periodLabel}`
                                  : <span style={{fontWeight:800,color:col}}>{pct}%</span>}
                            </div>
                          </div>
                          <div style={{background:T.overlayLight(0.07),borderRadius:99,height:6,overflow:"hidden"}}>
                            <div style={{width:`${pct}%`,background:`linear-gradient(90deg,${col},${col}99)`,height:"100%",borderRadius:99,transition:"width 0.8s"}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 3 · STATS — denser grid; streak + best in one tile, weight as
                  current → goal in one tile. There is no composite Life Score
                  (core model), so the pillar summary stands where one would go. */}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:8}}>
                {[
                  // Labeled per-pillar values with the Home-chip color rule
                  // (lowValue on band 'poor', otherwise text) — VALUES from
                  // the same pillars array via pctText, never re-derived.
                  ["Pillars", (
                    <span style={{display:"flex",justifyContent:"center",gap:9,flexWrap:"wrap"}}>
                      {pillars.map(p=>(
                        <span key={p.id} style={{display:"flex",flexDirection:"column",gap:1,alignItems:"center"}}>
                          <span style={{fontSize:8,fontWeight:700,color:T.textMuted,letterSpacing:"0.05em",textTransform:"uppercase"}}>{p.short}</span>
                          <span style={{fontSize:12,fontWeight:800,color:p.band==='poor'?G.lowValue:T.text}}>{pctText(p.pct, p.placeholder)}</span>
                        </span>
                      ))}
                    </span>
                  ), null, T.text],
                  ["Streak", `${progress.daily_streak||0}d`, `best ${progress.longest_streak||0}d`, T.accent],
                  ["Born", birthYear ? `${birthYear}` : "—", birthYear ? `${ageFromBirthYear(birthYear)} yrs${birthYearEstimated ? " · est." : ""}` : null, T.text],
                  ["Weight", bs.current_weight ? (bs.goal_weight ? `${bs.current_weight} → ${bs.goal_weight}` : `${bs.current_weight}`) : "—", bs.current_weight ? "lbs" : null, T.text],
                ].map(([label,val,sub,statColor])=>(
                  <div key={label} style={{background:G.cardSurface,border:G.hairline,borderRadius:RADII.stat,padding:"12px 10px",textAlign:"center",boxShadow:G.shadow,backdropFilter:G.blurCard,WebkitBackdropFilter:G.blurCard}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{label}</div>
                    <div style={{fontSize:label==="Pillars"?12:17,fontWeight:800,color:statColor,lineHeight:1.2}}>{val}</div>
                    {sub && <div style={{fontSize:10,color:T.textDim,marginTop:2}}>{sub}</div>}
                  </div>
                ))}
              </div>

              {/* 4 · BADGES EARNED */}
              {progress.badges && progress.badges.length > 0 && (
                <div style={GC.card}>
                  <div style={C.cTitle}> Badges Earned</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:10,marginTop:8}}>
                    {progress.badges.map(bid=>{
                      const badge = BADGES.find(b=>b.id===bid);
                      if (!badge) return null;
                      return(
                        <div key={bid} title={badge.desc} style={{display:"flex",alignItems:"center",gap:8,background:withAlpha(T.accent,0.08),border:`1px solid ${withAlpha(T.accent,0.22)}`,borderRadius:10,padding:"8px 14px"}}>
                          <div><div style={{fontSize:12,fontWeight:700,color:T.accent}}>{badge.label}</div><div style={{fontSize:10,color:T.textMuted}}>{badge.desc}</div></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tracker enrollment — what each pillar is scored against */}
              <div style={GC.card}>
                <div style={C.cTitle}>What you're tracking</div>
                <div style={{fontSize:12,color:T.textMuted,lineHeight:1.6,marginBottom:14}}>
                  A pillar is scored only against the trackers you turn on here. Anything
                  switched off shows as {"\u2014"} and is left out of that pillar's score entirely, so
                  it can never cost you points or hold a pillar back.
                </div>
                {PILLARS.map(pl=>{
                  const ids = TRACKER_IDS.filter(id=>TRACKERS[id].pillar===pl.id);
                  const p = pillarById[pl.id];
                  return (
                    <div key={pl.id} style={{marginBottom:16}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                        <div style={{fontSize:12,fontWeight:700,color:T.text}}>{pl.label}</div>
                        <div style={{fontSize:12,fontWeight:700,color:bandColor(p.band)}}>{pctText(p.pct, p.placeholder)}</div>
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {ids.map(id=>{
                          const on = enrolledSet.has(id);
                          return (
                            <button key={id} onClick={()=>toggleTracker(id)}
                              aria-pressed={on}
                              style={{background:on?withAlpha(T.accent,0.12):T.surfaceAlt,
                                      border:`1px solid ${on?T.accent:T.overlayLight(0.1)}`,
                                      color:on?T.accent:T.textMuted,borderRadius:20,padding:"5px 12px",
                                      fontSize:11,fontWeight:600,cursor:"pointer"}}>
                              {on ? "\u2713 " : ""}{TRACKERS[id].label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

                            {/* ── APPEARANCE (Phase 7) ── explicit choice wins, System
                  follows the OS. T/G are module constants read by every
                  style, so applying a change means a reload — cheap, honest,
                  and only on an explicit tap. */}
              <div style={GC.card}>
                <div style={C.cTitle}>Appearance</div>
                <div style={{display:"flex",gap:8}}>
                  {[["system","System"],["light","Light"],["dark","Dark"]].map(([v,l])=>{
                    const stored=(()=>{try{const x=localStorage.getItem("scorify_theme");return x==="light"||x==="dark"?x:"system";}catch{return "system";}})();
                    const active=stored===v;
                    return (
                      <button key={v} aria-pressed={active}
                        onClick={()=>{ try{ if(v==="system") localStorage.removeItem("scorify_theme"); else localStorage.setItem("scorify_theme",v);}catch{} window.location.reload(); }}
                        style={{flex:1,minHeight:44,borderRadius:22,border:active?"none":G.hairline,background:active?T.accentFill:G.tileBg,color:active?"#fff":T.text,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                        {l}
                      </button>
                    );
                  })}
                </div>
                <div style={{fontSize:11,color:T.textMuted,marginTop:10,lineHeight:1.5}}>System follows your device setting. Changing this reloads the app.</div>
              </div>

              {/* Goals section (full) — "View all →" in Active Goals lands here */}
              <div style={GC.card} id="profile-all-goals">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div style={C.cTitle}> My Goals</div>
                </div>
                {goals.length === 0 ? (
                  <div style={{textAlign:"center",padding:"24px 0"}}>
                    <div style={{fontSize:36,marginBottom:10}}></div>
                    <div style={{fontSize:14,fontWeight:700,marginBottom:6}}>No goals yet</div>
                    <div style={{fontSize:12,color:T.textMuted}}>Add a goal from the Home tab and track your progress here.</div>
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    {/* Active goals — GoalCard, NOT custom markup. This section
                        used to be a fifth goal renderer with raw
                        current_value/target_value and an unconditional Update
                        button, which is exactly the fabricated-number bug for
                        every BOUND goal: the founder's screen-time goal
                        rendered "0 / 30 min" + Update here (2026-09-14) on a
                        number the system cannot know. One renderer, one
                        derivation — same rule as the tab sections. */}
                    {goals.filter(g=>!g.completed).map(g=>{
                      const auto = getGoalAutoValue(g);
                      return <GoalCard key={g.id} g={viewGoal(g)} onEdit={openGoalEditor} onUpdate={(g)=>{setShowUpdateGoal(g);setGoalUpdateVal(String(g.current_value));}} onDelete={deleteGoal} autoValue={auto?.value} autoLabel={auto?.label} autoTarget={auto?.target} autoUnit={auto?.unit} autoHasData={auto?.hasData} autoInverted={auto?.inverted} autoNoSource={auto?.noSource} autoStateLine={auto?.stateLine} autoNote={auto?.note} onConnectSource={auto?.noSource==="plaid"?connectPlaid:auto?.noSource==="screen_time"?repairScreenTime:undefined}/>;
                    })}
                    {/* Completed goals */}
                    {goals.filter(g=>g.completed).length > 0 && (
                      <div>
                        <div style={{fontSize:11,color:T.success,fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:10,marginTop:4}}> Completed</div>
                        {goals.filter(g=>g.completed).map(g=>(
                          <div key={g.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:withAlpha(T.success,0.05),border:`1px solid ${withAlpha(T.success,0.15)}`,borderRadius:10,marginBottom:8,opacity:0.8}}>
                            <div>
                              <div style={{fontSize:13,fontWeight:700,color:T.success}}>✓ {g.title}</div>
                              <div style={{fontSize:11,color:T.textMuted}}>{g.target_value} {g.unit} achieved</div>
                            </div>
                            <button onClick={()=>{if(window.confirm(`Delete "${g.title}"?`))deleteGoal(g.id);}} aria-label={`Delete goal ${g.title}`} style={{background:"transparent",border:"none",color:T.textDim,fontSize:16,cursor:"pointer",padding:"12px 14px"}}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!hasStats && (
                <div style={{...GC.card,textAlign:"center",padding:"40px 20px"}}>
                  <div style={{fontSize:48,marginBottom:12}}></div>
                  <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>Set Up Your Body Profile</div>
                  <div style={{fontSize:13,color:T.textMuted,marginBottom:20,maxWidth:400,margin:"0 auto 20px"}}>Add your age, height, and weight to track your progress and give your coach real context.</div>
                  <button style={C.btn(T.accent)} onClick={()=>{ setProfileForm({...bs}); setShowEditProfile(true); }}>+ Add Your Stats</button>
                </div>
              )}

              {/* Goal card */}
              {hasStats && bs.goal_type && (
                <div style={{...GC.card}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                    <div>
                      <div style={C.cTitle}>Your Goal</div>
                      <div style={{fontSize:20,fontWeight:800,color:T.accent,marginTop:4}}>{goalTypes[bs.goal_type]||bs.goal_type}</div>
                      {bs.goal_label && <div style={{fontSize:13,color:T.textMuted,marginTop:4}}>"{bs.goal_label}"</div>}
                    </div>
                    {bs.goal_date && (
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:11,color:T.textMuted}}>Target date</div>
                        <div style={{fontSize:14,fontWeight:700,color:T.text}}>{new Date(bs.goal_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>
                        <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                          {Math.max(0,Math.ceil((new Date(bs.goal_date)-new Date())/(1000*60*60*24)))} days left
                        </div>
                      </div>
                    )}
                  </div>
                  {goalLbs && (
                    <>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:6}}>
                        <span style={{color:T.textMuted}}>Need to {goalDir} <strong style={{color:T.text}}>{goalLbs} lbs</strong></span>
                      </div>
                      <div style={{background:T.overlayLight(0.07),borderRadius:99,height:10,overflow:"hidden",marginBottom:8}}>
                        {(() => {
                          const pct = startWeight && bs.goal_weight && bs.current_weight ?
                            Math.min(100, Math.max(0, Math.abs((bs.current_weight - startWeight) / (bs.goal_weight - startWeight)) * 100)) : 0;
                          return <div style={{width:`${pct}%`,background:`linear-gradient(90deg,${T.accent},${withAlpha(T.accent,0.6)})`,height:"100%",borderRadius:99,transition:"width 1s"}}/>;
                        })()}
                      </div>
                      {totalChange !== null && parseFloat(totalChange) !== 0 && (
                        <div style={{fontSize:12,color:goalDir==="lose"?(parseFloat(totalChange)<0?T.success:T.danger):(parseFloat(totalChange)>0?T.success:T.danger)}}>
                          {parseFloat(totalChange) < 0 ? "▼" : "▲"} {Math.abs(totalChange)} lbs since you started
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Weight log */}
              {hasStats && (
                <div style={GC.card}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <div style={C.cTitle}> Weight Log</div>
                    <button style={{...C.btn(T.accent),fontSize:11,padding:"4px 12px"}} onClick={()=>setShowLogWeight(true)}>+ Log Weight</button>
                  </div>
                  {wLogSorted.length===0 ? (
                    <div style={{textAlign:"center",padding:"20px 0",color:T.textMuted,fontSize:13}}>No weight entries yet. Log your first weigh-in!</div>
                  ) : (
                    <>
                      {/* Mini chart */}
                      <div style={{position:"relative",height:120,marginBottom:16,paddingLeft:40}}>
                        {(() => {
                          const weights = wLogSorted.map(w=>w.weight);
                          const minW = Math.min(...weights) - 2;
                          const maxW = Math.max(...weights) + 2;
                          const range = maxW - minW || 1;
                          return (
                            <>
                              {[maxW, (maxW+minW)/2, minW].map((v,i)=>(
                                <div key={i} style={{position:"absolute",left:0,top:`${(i/2)*90}%`,fontSize:10,color:T.textMuted,transform:"translateY(-50%)"}}>{v.toFixed(0)}</div>
                              ))}
                              {weights.map((s,si)=>{
                                if(si===0) return null;
                                const x1=`${((si-1)/(weights.length-1||1))*100}%`,x2=`${(si/(weights.length-1||1))*100}%`;
                                const y1=`${((maxW-weights[si-1])/range)*90}%`,y2=`${((maxW-s)/range)*90}%`;
                                const col = goalDir==="lose"?(s<=weights[si-1]?T.success:T.danger):(s>=weights[si-1]?T.success:T.danger);
                                return(
                                  <svg key={si} style={{position:"absolute",inset:0,width:"100%",height:"100%",overflow:"visible",pointerEvents:"none"}}>
                                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={col} strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx={x2} cy={y2} r="4" fill={col}/>
                                  </svg>
                                );
                              })}
                              {bs.goal_weight && (
                                <div style={{position:"absolute",left:40,right:0,top:`${((maxW-bs.goal_weight)/range)*90}%`,height:1,background:withAlpha(T.accent,0.4),borderTop:`1px dashed ${T.accent}`}}/>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      {/* Last 5 entries */}
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {[...wLogSorted].reverse().slice(0,5).map((entry,i)=>{
                          const prev = [...wLogSorted].reverse()[i+1];
                          const delta = prev ? (entry.weight - prev.weight).toFixed(1) : null;
                          const col = delta===null?T.textMuted:goalDir==="lose"?(parseFloat(delta)<=0?T.success:T.danger):(parseFloat(delta)>=0?T.success:T.danger);
                          return(
                            <div key={entry.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:G.tileBg,borderRadius:10}}>
                              <div style={{fontSize:13,color:T.textMuted}}>{new Date(entry.logged_at).toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
                              <div style={{display:"flex",alignItems:"center",gap:10}}>
                                {delta!==null && <div style={{fontSize:12,fontWeight:700,color:col}}>{parseFloat(delta)>0?"+":""}{delta} lbs</div>}
                                <div style={{fontSize:16,fontWeight:800,color:T.text}}>{entry.weight} lbs</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── PRIVACY & ACCOUNT ACTIONS ──
                  Moved here from the bottom of the Overview tab 2026-09-08 so the
                  privacy policy's "Profile → Delete Account" / "Profile → Export My
                  Data" references are accurate. All colors from T/withAlpha — the
                  old block carried dark-theme rgba leftovers, deliberately not
                  carried over. */}
              <div style={{marginTop:24,paddingTop:16,borderTop:`1px solid ${T.border}`,display:'flex',flexDirection:'column',gap:10}}>
                <a href='https://scorify.to/privacy.html' target='_blank' rel='noreferrer' style={{fontSize:13,color:T.textMuted,textDecoration:'none',display:'block'}}>Privacy Policy →</a>
                <a href='https://scorify.to/terms.html' target='_blank' rel='noreferrer' style={{fontSize:13,color:T.textMuted,textDecoration:'none',display:'block',marginTop:8}}>Terms of Service →</a>
                <button onClick={()=>setShowAiConsentModal(true)} style={{background:'transparent',border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:'10px 16px',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:"'DM Sans',sans-serif",width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span>Privacy &amp; AI Data Sharing</span>
                  <span style={{fontSize:11,color:aiConsent?T.success:T.textMuted,fontWeight:600}}>{aiConsent?'Granted':'Not granted'}</span>
                </button>
                {!isDemo&&isLockCapable()&&(
                  <button onClick={()=>{setAppLockChangingPin(false);setShowAppLockModal(true);}} style={{background:'transparent',border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:'10px 16px',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:"'DM Sans',sans-serif",width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <span>App Lock</span>
                    <span style={{fontSize:11,color:appLockEnabled?T.success:T.textMuted,fontWeight:600}}>{appLockEnabled?'On':'Off'}</span>
                  </button>
                )}
                {!isDemo&&(
                  <button onClick={()=>setShowNotifSettings(true)} style={{background:'transparent',border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:'10px 16px',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:"'DM Sans',sans-serif",width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <span>Notifications</span>
                    <span style={{fontSize:11,color:(notifPrefs.checkinHour!=null&&notifPrefs.checkinEnabled)?T.success:T.textMuted,fontWeight:600}}>{(notifPrefs.checkinHour!=null&&notifPrefs.checkinEnabled)?`Check-in ${fmtHour12(notifPrefs.checkinHour)}`:'Check-in off'}</span>
                  </button>
                )}
                {!isDemo&&subscription?.paywallEnabled&&(
                  <button onClick={()=>{ if(native) showManageSubscriptions(); }} style={{background:'transparent',border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:'10px 16px',fontSize:13,cursor:native?'pointer':'default',textAlign:'left',fontFamily:"'DM Sans',sans-serif",display:'flex',justifyContent:'space-between',alignItems:'center',width:'100%'}}>
                    <span>Subscription</span>
                    <span style={{fontSize:11,color:subscription.entitled?T.success:T.textMuted,fontWeight:600}}>{subscriptionStatusLabel(subscription.reason)}{native?' · Manage subscription':''}</span>
                  </button>
                )}
                {!isDemo&&<button onClick={exportMyData} disabled={exportLoading} style={{background:'transparent',border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:'10px 16px',fontSize:13,cursor:exportLoading?'default':'pointer',textAlign:'left',fontFamily:"'DM Sans',sans-serif",width:'100%',opacity:exportLoading?0.6:1}}>{exportLoading?'Preparing your export…':'Export My Data'}</button>}
                {onSignOut&&<button onClick={handleSignOut} style={{background:'transparent',border:`1px solid ${withAlpha(T.danger,0.3)}`,color:T.danger,borderRadius:8,padding:'10px 16px',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:"'DM Sans',sans-serif",fontWeight:600,width:'100%'}}>Sign Out</button>}
                {!isDemo&&<button onClick={openDeleteAccount} style={{background:'transparent',border:`1px solid ${withAlpha(T.danger,0.2)}`,color:withAlpha(T.danger,0.6),borderRadius:8,padding:'10px 16px',fontSize:12,cursor:'pointer',textAlign:'left',fontFamily:"'DM Sans',sans-serif",width:'100%'}}>Delete Account</button>}
              </div>

            </div>
          );
        })()}

        {/* ── LEAGUE — hidden for v1.0, re-enable post-launch ── */}

        {/* ── COACH TAB ── */}
        {contentTab==="coach"&&(
          <div style={{maxWidth:680,margin:"0 auto",display:"flex",flexDirection:"column",height:"calc(100vh - 130px)"}}>
            {/* Header */}
            <div style={{padding:"8px 0 18px",flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:4}}>
                <div style={{width:44,height:44,borderRadius:"50%",background:`linear-gradient(135deg,${T.accent},${T.accentPressed})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.bg} strokeWidth="2" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                </div>
                <div>
                  <h2 style={{margin:0,fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text}}>{coachName || "Kai"}</h2>
                  <p style={{margin:0,fontSize:12,color:T.textMuted}}>Sees patterns across habits, mood, finance &amp; health</p>
                </div>
              </div>
            </div>
            {/* Messages — aria-live so VoiceOver announces Kai's replies (and
                error bubbles) as they append; "polite" so it never interrupts */}
            <div aria-live="polite" style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:14,paddingBottom:16}}>
              {/* Empty state has THREE cases, each explicit. This used to be one
                  unconditional "Analyzing your data..." — but for demo users no
                  fetch ever fires (no user id, so the morning-briefing cache key
                  is null), and for signed-in users without AI consent the
                  morning_briefing fetch is a background mode that the consent
                  gate silently nulls out without opening the modal. Both groups
                  sat on a permanent fake spinner claiming work was happening. */}
              {coachMsgs.length===0&&!coachLoading&&isDemoMode&&<CoachSampleConversation/>}
              {coachMsgs.length===0&&!coachLoading&&!isDemoMode&&!aiConsent&&(
                <CoachConsentPrompt onEnable={()=>setShowAiConsentModal(true)}/>
              )}
              {coachMsgs.length===0&&!coachLoading&&!isDemoMode&&aiConsent&&(
                <div style={{textAlign:"center",color:T.textMuted,fontSize:14,marginTop:40,animation:"coachFadeInUp 0.4s ease"}}>
                  Analyzing your data...
                </div>
              )}
              {coachMsgs.map((m,i)=>(
                <div key={i} style={{display:"flex",flexDirection:"column",alignItems:m.isCoach?"flex-start":"flex-end",animation:"coachFadeInUp 0.3s ease"}}>
                  {m.isCoach&&<div style={{fontSize:11,color:T.textMuted,marginBottom:5,display:"flex",alignItems:"center",gap:5}}>Coach</div>}
                  <div style={{maxWidth:"82%",background:m.isCoach?T.overlayLight(0.07):T.accent,color:m.isCoach?T.text:T.bg,borderRadius:m.isCoach?"4px 18px 18px 18px":"18px 4px 18px 18px",padding:"13px 17px",fontSize:14,lineHeight:1.6,boxShadow:m.isCoach?"none":`0 2px 12px ${T.overlayLight(0.15)}`}}>
                    {m.text.split(/\*\*(.*?)\*\*/g).map((part,j)=>j%2===1?<strong key={j} style={{fontWeight:700,color:m.isCoach?T.text:T.bg}}>{part}</strong>:part)}
                  </div>
                </div>
              ))}
              {coachLoading&&(
                <div style={{display:"flex",gap:5,padding:"12px 16px",background:T.overlayLight(0.07),borderRadius:"4px 18px 18px 18px",alignSelf:"flex-start",width:"fit-content"}}>
                  {[0,0.2,0.4].map((d,i)=>(
                    <div key={i} style={{width:8,height:8,borderRadius:"50%",background:T.textMuted,animation:`coachTypingDot 1.2s ease-in-out ${d}s infinite`}}/>
                  ))}
                </div>
              )}
              <div ref={coachBottomRef}/>
            </div>
            {/* Prompt suggestions */}
            <div style={{flexShrink:0,marginBottom:12}}>
              <div style={{display:"flex",gap:8,marginBottom:10,overflowX:"auto",paddingBottom:4}}>
                {coachTabPrompts.map((cat,i)=>(
                  <button key={cat.label} onClick={()=>setCoachTabCategory(i)}
                    style={{whiteSpace:"nowrap",padding:"5px 14px",borderRadius:20,border:"1.5px solid",borderColor:coachTabCategory===i?T.accent:T.overlayLight(0.12),background:coachTabCategory===i?T.overlayLight(0.04):"transparent",color:coachTabCategory===i?T.accent:T.textMuted,fontSize:12,cursor:"pointer",fontWeight:coachTabCategory===i?600:400,transition:"all 0.15s",flexShrink:0}}>
                    {cat.label}
                  </button>
                ))}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {coachTabPrompts[coachTabCategory].prompts.map(p=>(
                  <button key={p} onClick={()=>sendCoachPrompt(p)} disabled={coachLoading}
                    style={{textAlign:"left",background:T.overlayLight(0.06),border:`1.5px solid ${T.overlayLight(0.1)}`,borderRadius:12,padding:"10px 14px",fontSize:13,color:T.text,cursor:coachLoading?"not-allowed":"pointer",opacity:coachLoading?0.5:1,transition:"border-color 0.15s,background 0.15s",display:"flex",alignItems:"center",justifyContent:"space-between"}}
                    onMouseEnter={e=>{if(!coachLoading){e.currentTarget.style.borderColor=T.accent;e.currentTarget.style.background=T.overlayLight(0.04);}}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.overlayLight(0.1);e.currentTarget.style.background=T.overlayLight(0.06);}}>
                    {p}<span style={{color:T.accent,fontSize:14,flexShrink:0,marginLeft:8}}>→</span>
                  </button>
                ))}
              </div>
            </div>
            {/* Input — disabled offline with an inline note, rather than
                letting the user compose a message whose send can only fail.
                The tab itself stays visible: the cached transcript is real. */}
            {isOffline&&!isDemoMode&&<div style={{fontSize:11.5,color:T.textMuted,textAlign:"center",paddingBottom:8,flexShrink:0}}>Kai needs a connection.</div>}
            <div style={{display:"flex",gap:10,flexShrink:0,paddingBottom:8}}>
              <input ref={coachInputRef} value={coachInput} onChange={e=>setCoachInput(e.target.value)}
                disabled={isOffline&&!isDemoMode}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendCoachMsg();}}}
                aria-label="Message your coach" placeholder="Ask your coach anything..."
                style={{flex:1,border:`1.5px solid ${T.overlayLight(0.12)}`,borderRadius:24,padding:"12px 18px",fontSize:16,background:T.overlayLight(0.06),color:T.text,outline:"none",transition:"border-color 0.2s",fontFamily:"inherit",opacity:isOffline&&!isDemoMode?0.5:1}}
                onFocus={e=>{e.target.style.borderColor=T.accent;}}
                onBlur={e=>{e.target.style.borderColor=T.overlayLight(0.12);}}/>
              <button onClick={sendCoachMsg} aria-label="Send" disabled={!coachInput.trim()||coachLoading||(isOffline&&!isDemoMode)}
                style={{width:46,height:46,borderRadius:"50%",background:coachInput.trim()?T.accent:T.overlayLight(0.08),border:"none",cursor:coachInput.trim()?"pointer":"not-allowed",color:coachInput.trim()?"#fff":T.overlayLight(0.3),fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.2s",opacity:isOffline&&!isDemoMode?0.5:1}}>↑</button>
            </div>
          </div>
        )}

      </div>

      {/* ── LOG MODAL ── */}
      {logModal&&(()=>{
        const t=tmpl(logModal), h=hdata(logModal);
        return(
          <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setLogModal(null)}>
            <div style={{...C.mbox,position:"relative"}} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>setLogModal(null)} style={{position:"absolute",top:12,right:14,background:"transparent",border:"none",color:T.textMuted,fontSize:24,cursor:"pointer",lineHeight:1,padding:"2px 6px"}} aria-label="Close">×</button>
              <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>{t.label}</div>
              <div style={{fontSize:13,color:T.textMuted,marginBottom:6}}>Current streak: <span style={{color:T.accent,fontWeight:800}}>{h.streak} </span></div>
              <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>Progress: <span style={{color:T.text,fontWeight:700}}>{h.weekCount}/{t.target} {t.unit}</span></div>
{(()=>{
                const isDaily = t.unit === "days/week";
                const todayStr = localDateStr();
                const loggedToday = isDaily && h.lastLoggedDate === todayStr;
                return (
                  <>
                    {loggedToday ? (
                      <div style={{background:withAlpha(T.success,0.08),border:`1px solid ${withAlpha(T.success,0.25)}`,borderRadius:12,padding:"16px",textAlign:"center",marginBottom:20}}>
                        <div style={{fontSize:28,marginBottom:8}}>✓</div>
                        <div style={{fontSize:14,fontWeight:800,color:T.success,marginBottom:4}}>Already logged today!</div>
                        <div style={{fontSize:12,color:T.textMuted}}>This habit can only be logged once per day.<br/>Come back tomorrow to keep your streak going.</div>
                      </div>
                    ) : (
                      <div style={{marginBottom:20}}>
                        {isDaily ? (
                          <div style={{fontSize:12,color:T.textMuted,fontWeight:700,marginBottom:10,textAlign:"center"}}>
                            Log today's completion (once per day)
                          </div>
                        ) : (
                          <div style={{fontSize:12,color:T.textMuted,fontWeight:700,marginBottom:10}}>How many times?</div>
                        )}
                        {!isDaily && (
                          <div style={{display:"flex",alignItems:"center",gap:16}}>
                            <button onClick={()=>setLogVal(v=>Math.max(1,v-1))} aria-label="Decrease" style={{...C.ghost,fontSize:22,padding:"8px 16px"}}>−</button>
                            <span style={{fontSize:32,fontWeight:900,minWidth:48,textAlign:"center"}}>{logVal}</span>
                            <button onClick={()=>setLogVal(v=>v+1)} aria-label="Increase" style={{...C.ghost,fontSize:22,padding:"8px 16px"}}>+</button>
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{display:"flex",gap:10}}>
                      {loggedToday ? (
                        <button style={{...C.btn(T.accent),width:"100%"}} onClick={()=>setLogModal(null)}>Done</button>
                      ) : (
                        <>
                          <button style={{...C.btn(T.accent),flex:1}} onClick={()=>logHabit(logModal, isDaily ? 1 : logVal)}>✓ Log &amp; Update Score</button>
                          <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setLogModal(null)}>Cancel</button>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* ── ADD RECOVERY MODAL ── */}
      {showAddRecovery&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddRecovery(false)}>
          <div style={{...C.mbox,width:440}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}> Start a Recovery Streak</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:20}}>Every day clean is a win. No judgment — just progress.</div>

            <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>What are you tracking?</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
              {[
                {id:"alcohol",  icon:"", label:"Alcohol"},
                {id:"nicotine", icon:"", label:"Nicotine"},
                {id:"drugs",    icon:"", label:"Drugs"},
                {id:"gambling", icon:"", label:"Gambling"},
                {id:"social",   icon:"", label:"Social Media"},
                {id:"custom",   icon:"", label:"Custom"},
              ].map(s=>(
                <button key={s.id} onClick={()=>setNewRecovery(p=>({...p,substance:s.id}))}
                  style={{padding:"12px 8px",borderRadius:12,border:`2px solid ${newRecovery.substance===s.id?T.accent:T.overlayLight(0.08)}`,
                    background:newRecovery.substance===s.id?withAlpha(T.accent,0.15):T.surfaceAlt,
                    cursor:"pointer",textAlign:"center",transition:"all 0.2s"}}>
                  <div style={{fontSize:24,marginBottom:4}}>{s.icon}</div>
                  <div style={{fontSize:11,fontWeight:700,color:newRecovery.substance===s.id?T.accent:T.textMuted}}>{s.label}</div>
                </button>
              ))}
            </div>

            {newRecovery.substance==="custom"&&(
              <div style={{marginBottom:16}}>
                <div style={{fontSize:12,color:T.textMuted,marginBottom:6}}>What are you quitting?</div>
                <input value={newRecovery.custom} onChange={e=>setNewRecovery(p=>({...p,custom:e.target.value}))}
                  placeholder="e.g. Caffeine, Junk food..." style={{...C.inp,width:"100%"}}/>
              </div>
            )}

            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,color:T.textMuted,marginBottom:6}}>Start date — when did you last use?</div>
              <input type="date" value={newRecovery.start_date}
                onChange={e=>setNewRecovery(p=>({...p,start_date:e.target.value}))}
                style={{...C.inp,width:"100%"}}/>
              <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>Set to today if starting fresh, or back-date it if you're already clean.</div>
            </div>

            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={addRecovery}>Start Tracking →</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddRecovery(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD HABIT MODAL ── */}
      {showAdd&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>{setShowAdd(false);setHabitTab("templates");}}>
          <div style={{...C.mbox,width:500,maxHeight:"85vh",display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:17,fontWeight:800}}>+ Add a Habit</div>
              <button onClick={()=>{setShowAdd(false);setHabitTab("templates");}} style={{background:"transparent",border:"none",color:T.textMuted,fontSize:24,cursor:"pointer",lineHeight:1,padding:"2px 6px"}} aria-label="Close">×</button>
            </div>

            {/* Tabs */}
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              {[["templates"," Templates"],["custom"," Custom"]].map(([v,l])=>(
                <button key={v} onClick={()=>setHabitTab(v)}
                  style={{flex:1,padding:"8px",borderRadius:10,border:`1px solid ${habitTab===v?T.accent:T.overlayLight(0.08)}`,background:habitTab===v?withAlpha(T.accent,0.15):T.surfaceAlt,color:habitTab===v?T.accent:T.textMuted,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  {l}
                </button>
              ))}
            </div>

            {habitTab==="templates" && (
              <div style={{overflowY:"auto",flex:1,minHeight:0,paddingRight:4}}>
                {["health","finance","wellness"].map(cat=>{
                  const available = HABIT_TEMPLATES.filter(t=>t.category===cat && !habits.find(h=>h.id===t.id));
                  if (available.length===0) return null;
                  const catLabel = {health:" Health",finance:" Finance",wellness:" Wellness"}[cat];
                  return (
                    <div key={cat} style={{marginBottom:14}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{catLabel}</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {available.map(t=>(
                          <button key={t.id} onClick={()=>addHabit(t.id)}
                            style={{background:T.overlayLight(0.06),border:`1px solid ${T.overlayLight(0.08)}`,borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",color:T.text,textAlign:"left",transition:"border 0.2s"}}
                            onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
                            onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(0,0,0,0.08)"}>
                            <div style={{flex:1}}>
                              <div style={{fontSize:13,fontWeight:700}}>{t.label}</div>
                              <div style={{fontSize:11,color:T.textMuted}}>Target: {t.target} {t.unit}</div>
                            </div>
                            <span style={{fontSize:18,color:T.textDim}}>+</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {HABIT_TEMPLATES.every(t=>habits.find(h=>h.id===t.id)) && (
                  <div style={{textAlign:"center",padding:"20px 0",color:T.textMuted,fontSize:13}}>You've added all templates! Try creating a custom habit. </div>
                )}
              </div>
            )}

            {habitTab==="custom" && (
              <div style={{display:"flex",flexDirection:"column",gap:14,overflowY:"auto",flex:1,minHeight:0}}>
                <div>
                  <div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontWeight:600}}>Habit Name</div>
                  <input value={customHabit.label} onChange={e=>setCustomHabit(p=>({...p,label:e.target.value}))}
                    placeholder="e.g. Morning Run, No Sugar, Read Bible..." style={{...C.inp,width:"100%"}}/>
                </div>


                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div>
                    <div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontWeight:600}}>Target</div>
                    <input type="number" min="1" max="30" value={customHabit.target}
                      onChange={e=>setCustomHabit(p=>({...p,target:e.target.value}))}
                      style={{...C.inp,width:"100%"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontWeight:600}}>Unit</div>
                    <select value={customHabit.unit} onChange={e=>setCustomHabit(p=>({...p,unit:e.target.value}))}
                      style={{...C.inp,width:"100%"}}>
                      <option value="times/week">times / week</option>
                      <option value="days/week">days / week</option>
                      <option value="times/month">times / month</option>
                      <option value="per month">per month</option>
                      <option value="per quarter">per quarter</option>
                    </select>
                  </div>
                </div>

                <div style={{background:T.overlayLight(0.06),borderRadius:10,padding:"10px 14px",border:`1px solid ${T.overlayLight(0.08)}`,display:"flex",alignItems:"center",gap:10}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:customHabit.label?T.text:T.textDim}}>{customHabit.label||"Your habit name"}</div>
                    <div style={{fontSize:11,color:T.textMuted}}>Target: {customHabit.target||5} {customHabit.unit}</div>
                  </div>
                </div>

                <button style={{...C.btn(T.accent),opacity:customHabit.label.trim()?1:0.4}} onClick={customHabit.label.trim()?addCustomHabit:null}>
                  + Add Custom Habit
                </button>
              </div>
            )}

            <button style={{...C.ghost,marginTop:14,padding:"9px",width:"100%"}} onClick={()=>{setShowAdd(false);setHabitTab("templates");}}>Cancel</button>
          </div>
        </div>
      )}


      {/* ── EDIT HABIT MODAL ── */}
      {editHabit&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setEditHabit(null)}>
          <div style={{...C.mbox,width:440}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:17,fontWeight:800,marginBottom:16}}> Edit Habit</div>

            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontWeight:600}}>Habit Name</div>
              <input value={editHabit.label} onChange={e=>setEditHabit(p=>({...p,label:e.target.value}))}
                style={{...C.inp,width:"100%"}}/>
            </div>


            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
              <div>
                <div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontWeight:600}}>Target</div>
                <input type="number" min="1" max="30" value={editHabit.target}
                  onChange={e=>setEditHabit(p=>({...p,target:e.target.value}))}
                  style={{...C.inp,width:"100%"}}/>
              </div>
              <div>
                <div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontWeight:600}}>Unit</div>
                <select value={editHabit.unit} onChange={e=>setEditHabit(p=>({...p,unit:e.target.value}))}
                  style={{...C.inp,width:"100%"}}>
                  <option value="times/week">times / week</option>
                  <option value="days/week">days / week</option>
                  <option value="times/month">times / month</option>
                  <option value="per month">per month</option>
                  <option value="per quarter">per quarter</option>
                </select>
              </div>
            </div>

            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={saveEditHabit}>Save Changes</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setEditHabit(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE HABIT CONFIRMATION ── */}
      {deleteHabitId&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setDeleteHabitId(null)}>
          <div style={{...C.mbox,width:380,textAlign:"center"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:36,marginBottom:12}}></div>
            <div style={{fontSize:17,fontWeight:800,marginBottom:8}}>Delete this habit?</div>
            <div style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.6}}>
              Your streak and progress will be lost. This can't be undone.
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.danger),flex:1}} onClick={()=>confirmDeleteHabit(deleteHabitId)}>Yes, Delete</button>
              <button style={{...C.ghost,flex:1,padding:"9px"}} onClick={()=>setDeleteHabitId(null)}>Keep It</button>
            </div>
          </div>
        </div>
      )}

      {/* ── UPDATE SCORE MODAL ── */}
      {showUpdateScore&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowUpdateScore(false)}>
          <div style={{...C.mbox,width:400}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:28,marginBottom:8}}></div>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>Update Your Credit Score</div>
            <div style={{fontSize:13,color:T.textMuted,marginBottom:6,lineHeight:1.6}}>Check your score for free on <span style={{color:T.accent}}>Credit Karma</span>, <span style={{color:T.accent}}>Experian</span>, or your bank app, then enter it below.</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:20}}>Current score: <span style={{fontWeight:800,color:scColor(creditScore)}}>{creditScore}</span></div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,color:T.textMuted,fontWeight:700,marginBottom:8}}>Your new score (300–850)</div>
              <input
                type="number" min="300" max="850"
                value={newScoreInput}
                onChange={e=>setNewScoreInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&submitNewScore()}
                placeholder="e.g. 705"
                style={{...C.inp,width:"100%",fontSize:24,fontWeight:800,textAlign:"center",padding:"14px"}}
                autoFocus/>
              {newScoreInput&&(parseInt(newScoreInput)<300||parseInt(newScoreInput)>850)&&(
                <div style={{fontSize:12,color:T.danger,marginTop:6}}>Score must be between 300 and 850.</div>
              )}
              {newScoreInput&&parseInt(newScoreInput)>=300&&parseInt(newScoreInput)<=850&&(
                <div style={{fontSize:13,marginTop:8,textAlign:"center",fontWeight:700,color:scColor(parseInt(newScoreInput))}}>
                  {parseInt(newScoreInput)} — {scLabel(parseInt(newScoreInput))}
                  {parseInt(newScoreInput)>creditScore&&<span style={{color:T.success}}> ▲ +{parseInt(newScoreInput)-creditScore} pts</span>}
                  {parseInt(newScoreInput)<creditScore&&<span style={{color:T.danger}}> ▼ {parseInt(newScoreInput)-creditScore} pts</span>}
                </div>
              )}
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn("#0ea5e9"),flex:1}} onClick={submitNewScore} disabled={!newScoreInput||parseInt(newScoreInput)<300||parseInt(newScoreInput)>850}>Save Score</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowUpdateScore(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT FINANCES MODAL ── */}
      {showNotifSettings&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowNotifSettings(false)}>
          <div style={{...C.mbox,width:460,maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>Notifications</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:18,lineHeight:1.5}}>Choose when {coachName||"Kai"} checks in and which timely alerts you want. Full details open behind your app lock, never on the lock screen.</div>

            {/* Check-ins are written by the coach, which needs AI coaching
                consent — without it the cron never processes this account, so a
                chosen time would silently do nothing (review L5). Say so. */}
            {!aiConsent && (
              <div style={{background:withAlpha(T.warning,0.08),border:`1px solid ${withAlpha(T.warning,0.25)}`,borderRadius:10,padding:"10px 14px",marginBottom:14}}>
                <div style={{fontSize:12.5,color:T.text,lineHeight:1.5,marginBottom:8}}>Check-ins and the weekly recap are written by {coachName||"Kai"}, which needs AI coaching turned on. Until then, nothing will be sent.</div>
                <button onClick={()=>{setShowNotifSettings(false);setShowAiConsentModal(true);}} style={{background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:12.5,cursor:"pointer",padding:0,fontFamily:"inherit"}}>Turn on AI coaching →</button>
              </div>
            )}

            <div style={NOTIF_ROW}>
              <div style={{flex:1}}>
                <div style={NOTIF_LABEL}>Daily check-in</div>
                <div style={NOTIF_SUB}>One short note at your chosen time.</div>
              </div>
              <ToggleSwitch on={notifPrefs.checkinEnabled} onChange={v=>saveNotifPrefs({checkinEnabled:v})} label="Daily check-in"/>
            </div>
            {notifPrefs.checkinEnabled&&(
              <div style={{marginBottom:6}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Check-in time</div>
                <select value={notifPrefs.checkinHour==null?"":String(notifPrefs.checkinHour)} onChange={e=>saveNotifPrefs({checkinHour:e.target.value===""?null:Number(e.target.value)})} style={{...C.inp,width:"100%"}} aria-label="Check-in time">
                  <option value="">Choose a time…</option>
                  {Array.from({length:24}).map((_,h)=><option key={h} value={h}>{fmtHour12(h)}</option>)}
                </select>
                {notifPrefs.checkinHour==null&&<div style={{fontSize:11,color:T.textMuted,marginTop:6}}>Pick a time to start daily check-ins.</div>}
              </div>
            )}

            <div style={{...NOTIF_ROW,borderTop:`1px solid ${T.border}`,marginTop:10,paddingTop:16}}>
              <div style={{flex:1}}>
                <div style={NOTIF_LABEL}>Weekly recap</div>
                <div style={NOTIF_SUB}>A short review each Sunday evening.</div>
              </div>
              <ToggleSwitch on={notifPrefs.weeklyRecapEnabled} onChange={v=>saveNotifPrefs({weeklyRecapEnabled:v})} label="Weekly recap"/>
            </div>

            <div style={NOTIF_ROW}>
              <div style={{flex:1}}>
                <div style={NOTIF_LABEL}>Evening mood check-in</div>
                <div style={NOTIF_SUB}>A one-tap nudge around 8 PM to log today's mood, only if you haven't already.</div>
              </div>
              <ToggleSwitch on={notifPrefs.eveningMoodEnabled} onChange={v=>saveNotifPrefs({eveningMoodEnabled:v})} label="Evening mood check-in"/>
            </div>

            <div style={{borderTop:`1px solid ${T.border}`,marginTop:16,paddingTop:16}}>
              <div style={{fontSize:14,fontWeight:800,marginBottom:4}}>Timely alerts</div>
              <div style={{fontSize:12,color:T.textMuted,marginBottom:6,lineHeight:1.5}}>Turn a type off to stop its separate alerts. Those items still appear in your daily check-in.</div>
              {[["alertsMoney","Money","Bills due, spending over a limit"],["alertsGoals","Goals","Goal pacing and deadlines"],["alertsHabits","Habits","Streaks worth protecting"],["alertsHealth","Health","Sleep, activity, screen time"]].map(([key,name,desc])=>(
                <div key={key} style={NOTIF_ROW}>
                  <div style={{flex:1}}><div style={NOTIF_LABEL}>{name}</div><div style={NOTIF_SUB}>{desc}</div></div>
                  <ToggleSwitch on={notifPrefs[key]} onChange={v=>saveNotifPrefs({[key]:v})} label={`${name} alerts`}/>
                </div>
              ))}
            </div>

            <div style={{borderTop:`1px solid ${T.border}`,marginTop:16,paddingTop:16}}>
              <div style={{fontSize:14,fontWeight:800,marginBottom:4}}>Food for thought</div>
              <div style={{fontSize:12,color:T.textMuted,marginBottom:6,lineHeight:1.5}}>Gentle observations when parts of your life move together.</div>
              <div style={NOTIF_ROW}>
                <div style={{flex:1}}><div style={NOTIF_LABEL}>Patterns involving mood</div><div style={NOTIF_SUB}>Only ever shown inside your check-in and weekly recap, never as an alert.</div></div>
                <ToggleSwitch on={notifPrefs.moodPatternsEnabled} onChange={v=>saveNotifPrefs({moodPatternsEnabled:v})} label="Mood patterns"/>
              </div>
            </div>

            <div style={{borderTop:`1px solid ${T.border}`,marginTop:16,paddingTop:16}}>
              <div style={{fontSize:14,fontWeight:800,marginBottom:4}}>Names shared with AI</div>
              <div style={{fontSize:12,color:T.textMuted,marginBottom:6,lineHeight:1.5}}>Whether specific medication names and merchant names reach the AI coach.</div>
              <div style={NOTIF_ROW}>
                <div style={{flex:1}}><div style={NOTIF_LABEL}>Share medication &amp; merchant names</div><div style={NOTIF_SUB}>On: the coach sees names like "Adderall" or "Whole Foods". Off: it still sees that you took a medication and what you spent, just not the specific names.</div></div>
                <ToggleSwitch on={notifPrefs.shareSensitiveNames} onChange={v=>saveNotifPrefs({shareSensitiveNames:v})} label="Share names with AI"/>
              </div>
            </div>

            <button style={{...C.btn(T.accent),width:"100%",marginTop:20}} onClick={()=>setShowNotifSettings(false)}>Done</button>
          </div>
        </div>
      )}
      {/* ── WORKOUT DETAILS SHEET ── after a Gym tick (logHabit). Save writes a
           workouts row for that local date; Skip leaves just the tick. ── */}
      {workoutSheet&&(
        <div role="dialog" aria-modal="true" aria-label="Workout details"
          style={{position:"fixed",inset:0,zIndex:3200,background:T.overlayLight(0.6),display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={()=>!workoutSheetSaving&&setWorkoutSheet(null)}>
          <div onClick={e=>e.stopPropagation()}
            style={{width:"100%",maxWidth:520,background:T.surface,borderTopLeftRadius:20,borderTopRightRadius:20,border:`1px solid ${T.border}`,borderBottom:"none",padding:"20px 18px",paddingBottom:"calc(20px + env(safe-area-inset-bottom,0px))"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text}}>{workoutSheet.editId ? "Workout details" : "What did you do?"}</div>
              <button onClick={()=>setWorkoutSheet(null)} disabled={workoutSheetSaving} style={{background:"transparent",border:"none",color:T.textMuted,fontSize:14,cursor:"pointer",fontFamily:"inherit",minHeight:44,padding:"0 6px"}}>Skip</button>
            </div>
            <div style={{fontSize:13,color:T.textMuted,marginBottom:14}}>{workoutSheet.editId ? "Edit this day's workout." : "Logged. Add the details if you like; Skip keeps today's tick."}</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
              {WORKOUT_TYPES.map(t=>{
                const on = workoutSheet.type===t;
                return <button key={t} onClick={()=>setWorkoutSheet(x=>({...x,type:t}))} aria-pressed={on}
                  style={{minHeight:40,padding:"0 14px",borderRadius:20,border:`1px solid ${on?T.accent:T.border}`,background:on?withAlpha(T.accent,0.12):T.surfaceAlt,color:on?T.accent:T.text,fontSize:13,fontWeight:on?700:500,cursor:"pointer",fontFamily:"inherit"}}>{t}</button>;
              })}
            </div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <input type="number" inputMode="numeric" min="1" placeholder="Minutes (optional)" value={workoutSheet.duration} onChange={e=>setWorkoutSheet(x=>({...x,duration:e.target.value}))} aria-label="Duration in minutes" style={{...C.inp,flex:"0 0 150px"}}/>
              <input placeholder="Note (optional)" value={workoutSheet.note} maxLength={140} onChange={e=>setWorkoutSheet(x=>({...x,note:e.target.value}))} aria-label="Note" style={{...C.inp,flex:1,minWidth:0}}/>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1,minHeight:44}} disabled={workoutSheetSaving} onClick={saveWorkoutSheet}>{workoutSheetSaving?"Saving…":"Save"}</button>
              <button style={{...C.ghost,padding:"9px 18px",minHeight:44}} disabled={workoutSheetSaving} onClick={()=>setWorkoutSheet(null)}>Skip</button>
            </div>
          </div>
        </div>
      )}
      {/* ── ONE-TAP MOOD SHEET ── opened by the evening-mood push deep-link (or a
           quick-log button). One tap on 1-10 saves and closes; optional note is
           collapsed. Design tokens only, light + dark. ── */}
      {showMoodSheet&&(
        <div role="dialog" aria-modal="true" aria-label="Log your mood"
          style={{position:"fixed",inset:0,zIndex:3200,background:T.overlayLight(0.6),display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={()=>{setShowMoodSheet(false);setMoodSheetNoteOpen(false);}}>
          <div onClick={e=>e.stopPropagation()}
            style={{width:"100%",maxWidth:520,background:T.surface,borderTopLeftRadius:20,borderTopRightRadius:20,border:`1px solid ${T.border}`,borderBottom:"none",padding:"20px 18px",paddingBottom:"calc(20px + env(safe-area-inset-bottom,0px))",boxShadow:G.shadow}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text}}>How was today?</div>
              <button onClick={()=>{setShowMoodSheet(false);setMoodSheetNoteOpen(false);}} style={{background:"transparent",border:"none",color:T.textMuted,fontSize:14,cursor:"pointer",fontFamily:"inherit",minHeight:44,padding:"0 6px"}}>Close</button>
            </div>
            <div style={{fontSize:13,color:T.textMuted,marginBottom:16}}>One tap to log it. 1 is rough, 10 is great.</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
              {[1,2,3,4,5,6,7,8,9,10].map(n=>(
                <button key={n} onClick={()=>logMoodQuick(n)} disabled={moodSaving} aria-label={`Log mood ${n} of 10`}
                  style={{aspectRatio:"1",borderRadius:14,border:`1px solid ${withAlpha(moodColor(n),0.4)}`,background:withAlpha(moodColor(n),0.16),color:T.text,fontFamily:"'DM Serif Display',serif",fontSize:22,cursor:"pointer",minHeight:54}}>{n}</button>
              ))}
            </div>
            {!moodSheetNoteOpen?(
              <button onClick={()=>setMoodSheetNoteOpen(true)} style={{marginTop:16,background:"transparent",border:"none",color:T.accent,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",minHeight:44}}>Add a note</button>
            ):(
              <div style={{marginTop:16}}>
                <textarea value={moodSheetNote} onChange={e=>setMoodSheetNote(e.target.value)} placeholder="Anything about today? (optional)" rows={3}
                  style={{...C.inp,width:"100%",resize:"vertical",padding:"10px 12px"}}/>
                <div style={{fontSize:12,color:T.textMuted,marginTop:6}}>Then tap a number above to save it with your note.</div>
              </div>
            )}
          </div>
        </div>
      )}
      {showEditFinances&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowEditFinances(false)}>
          <div style={{...C.mbox,width:460,maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}> Your Finances</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:18}}>{plaidConnected?"Your income and spending are pulled from Plaid automatically. Update your savings balance below.":"Enter your monthly numbers. Only you can see these."}</div>
            {(plaidConnected ? [["Total Savings","savings","2000"]] : [["Monthly Income","income","4200"],["Monthly Expenses","expenses","3100"],["Total Savings","savings","2000"]]).map(([label,key,ph])=>(
              <div key={key} style={{marginBottom:14}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>{label}</div>
                <input value={financeForm[key]} onChange={e=>setFinanceForm(p=>({...p,[key]:e.target.value}))}
                  placeholder={ph} style={{...C.inp,width:"100%"}} type="number"/>
              </div>
            ))}
            <div style={{borderTop:"1px solid #1e2240",margin:"18px 0 16px",paddingTop:16}}>
              <div style={{fontSize:14,fontWeight:800,marginBottom:14,color:T.text}}> Pay Schedule</div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Paycheck Amount</div>
                <input value={financeForm.pay_amount} onChange={e=>setFinanceForm(p=>({...p,pay_amount:e.target.value}))}
                  placeholder="e.g. 2200" style={{...C.inp,width:"100%"}} type="number"/>
                <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>How much do you receive each paycheck?</div>
              </div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:8}}>How often do you get paid?</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                  {[["weekly"," Weekly"],["biweekly"," Bi-weekly"],["semimonthly"," Twice/month"],["monthly"," Monthly"]].map(([val,lbl])=>(
                    <button key={val} onClick={()=>setFinanceForm(p=>({...p,pay_freq:val}))}
                      style={{padding:"9px 12px",borderRadius:9,border:`1px solid ${financeForm.pay_freq===val?T.accent:T.overlayLight(0.08)}`,background:financeForm.pay_freq===val?"rgba(129,140,248,0.15)":T.surfaceAlt,color:financeForm.pay_freq===val?T.accent:T.textMuted,cursor:"pointer",fontSize:12,fontWeight:700,textAlign:"center"}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              {(financeForm.pay_freq==="semimonthly"||financeForm.pay_freq==="monthly") && (
                <div style={{display:"flex",gap:10,marginBottom:14}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>1st Payday (day of month)</div>
                    <input value={financeForm.pay_day1} onChange={e=>setFinanceForm(p=>({...p,pay_day1:e.target.value}))} type="number" min="1" max="31" placeholder="1" style={{...C.inp,width:"100%"}}/>
                  </div>
                  {financeForm.pay_freq==="semimonthly" && (
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>2nd Payday (day of month)</div>
                      <input value={financeForm.pay_day2} onChange={e=>setFinanceForm(p=>({...p,pay_day2:e.target.value}))} type="number" min="1" max="31" placeholder="15" style={{...C.inp,width:"100%"}}/>
                    </div>
                  )}
                </div>
              )}
              {(financeForm.pay_freq==="weekly"||financeForm.pay_freq==="biweekly") && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:8}}>What day do you get paid?</div>
                  <div style={{display:"flex",gap:6}}>
                    {[["1","Mon"],["2","Tue"],["3","Wed"],["4","Thu"],["5","Fri"]].map(([val,lbl])=>(
                      <button key={val} onClick={()=>setFinanceForm(p=>({...p,pay_weekday:val}))}
                        style={{flex:1,padding:"8px 4px",borderRadius:8,border:`1px solid ${financeForm.pay_weekday===val?T.accent:T.overlayLight(0.08)}`,background:financeForm.pay_weekday===val?"rgba(129,140,248,0.15)":T.surfaceAlt,color:financeForm.pay_weekday===val?T.accent:T.textMuted,cursor:"pointer",fontSize:12,fontWeight:700}}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Last or next payday date <span style={{color:T.textMuted,fontWeight:400}}>(to anchor the schedule)</span></div>
                    {dateField(financeForm.pay_start, v=>setFinanceForm(p=>({...p,pay_start:v})))}
                  </div>
                </div>
              )}
              {parseFloat(financeForm.pay_amount) > 0 && (()=>{
                const freq = financeForm.pay_freq;
                const paychecks = freq==="weekly"?4.33:freq==="biweekly"?2.17:freq==="semimonthly"?2:1;
                const monthlyAmt = Math.round(parseFloat(financeForm.pay_amount) * paychecks);
                return <div style={{background:withAlpha(T.success,0.08),border:`1px solid ${withAlpha(T.success,0.2)}`,borderRadius:10,padding:"10px 14px",fontSize:13,color:T.success,fontWeight:700}}> ~${monthlyAmt.toLocaleString()} per month</div>;
              })()}
            </div>
            <div style={{display:"flex",gap:10,marginTop:8}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={async()=>{await saveFinances(financeForm.income,financeForm.expenses,financeForm.savings,financeForm);setShowEditFinances(false);}}>Save →</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowEditFinances(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}




      {/* ── ADD GOAL MODAL ── */}
      {showAddGoal&&(
        /* kbInset padding + reduced maxHeight: the 85vh cap is measured
           against the LAYOUT viewport, which the keyboard does not shrink —
           the box's bottom sat behind the keyboard with only iOS's window
           auto-pan to reveal fields (the 9-14 scroll report). Centering
           inside the visible area and capping height to it keeps the whole
           box reachable; overscroll containment stops the inner scroller
           chaining to the page behind. */
        <div role="dialog" aria-modal="true" style={{...C.overlay,paddingBottom:kbInset||0}} onClick={closeGoalModal}>
          <div style={{...C.mbox,width:460,...(kbInset?{maxHeight:`calc(100vh - ${kbInset+40}px)`}:{}),overscrollBehavior:"contain"}} onClick={e=>e.stopPropagation()}
            onFocusCapture={e=>{ if (e.target && (e.target.tagName==="INPUT"||e.target.tagName==="TEXTAREA")) { try { e.target.scrollIntoView({block:"center",behavior:"smooth"}); } catch {} } }}>
          {/* Templates are the front door; custom is one tap away; edit mode
              bypasses both and renders the form directly (untouched). */}
          {!editGoalId && goalView === "gallery" ? (<>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>Set a New Goal</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:16}}>Pick a goal and set your number. Goals marked ● track themselves.</div>
            {TEMPLATE_GROUPS.map(([gid, glabel]) => {
              // Screen time needs the native plugin — the group simply is not
              // offered on web or in demo, same gate the old toggle had.
              const items = (gid === "screen" && (!native || isDemoMode)) ? [] : GOAL_TEMPLATES.filter(t => t.group === gid);
              if (items.length === 0) return null;
              return (
                <div key={gid} style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>{glabel}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {items.map(t => (
                      <button key={t.title} onClick={()=>openTemplateConfirm(t)}
                        style={{background:T.overlayLight(0.06),border:`1px solid ${T.overlayLight(0.08)}`,color:T.text,borderRadius:20,padding:"7px 13px",fontSize:12,cursor:"pointer",fontWeight:600}}>
                        {(t.source_type || t.screenTime) ? <span style={{color:T.success}}>● </span> : null}{t.title}{t.hint ? <span style={{color:T.textMuted}}> · {t.hint}</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <button onClick={()=>{
              // FULL draft reset — the confirm screen loads the whole
              // template preset into newGoal and Back does not clear it, so
              // without this the custom form rendered the previous
              // template's binding: its disclosure line, its prefilled cap,
              // and NO shape cards (the picker hides when source_type is
              // set). The 9-14 device screenshot, verbatim.
              setNewGoal({ title:"", category:"fitness", current_value:"0", target_value:"", unit:"", deadline:"", shape:"target" });
              setGoalTpl(null);
              setGoalView("custom");
              setGoalError("");
            }}
              style={{display:"block",width:"100%",background:"transparent",border:`1px dashed ${T.borderStrong}`,color:T.textMuted,borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:"pointer",marginTop:4,marginBottom:12}}>
              + Custom goal
            </button>
            <button style={{...C.ghost,padding:"9px",width:"100%"}} onClick={closeGoalModal}>Cancel</button>
          </>) : !editGoalId && goalView === "confirm" && goalTpl ? (<>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>{newGoal.title || goalTpl.title}</div>
            {(() => { const ccAuto = goalTpl.source_type === "credit_payoff" && plaidConnected && liveCreditBalance() > 0; return (
            <div style={{fontSize:11,marginBottom:16,color:(goalTpl.source_type||goalTpl.screenTime)?((goalTpl.source_type==="credit_payoff"&&!ccAuto)?T.textMuted:T.success):T.textMuted,lineHeight:1.5}}>
              {goalTpl.source_type === "credit_payoff" ? (ccAuto
                  ? `● Tracks automatically from your bank. You owe $${Math.round(liveCreditBalance()).toLocaleString()} across ${(plaidAccounts||[]).filter(a=>a.type==='credit').length} card${(plaidAccounts||[]).filter(a=>a.type==='credit').length===1?'':'s'}.`
                  : "Connect your bank to track this automatically. For now you log progress yourself.")
               : goalTpl.source_type === "savings" ? "● Tracks automatically from your bank balance, or your savings figure until a bank is connected."
               : goalTpl.source_type === "plaid_spend" ? "● Tracks automatically from your bank via Plaid."
               : goalTpl.source_type === "workout" ? "● Tracks automatically from your workouts via Apple Health."
               : goalTpl.screenTime ? "● Tracks automatically from Screen Time on this iPhone, per day. You'll pick the apps and approve access when you save."
               : "You log progress yourself with the Update button."}
            </div>
            ); })()}
            {(goalTpl.source_type === "plaid_spend" || goalTpl.source_type === "credit_payoff") && !plaidConnected && (
              <div style={{fontSize:12,color:T.textMuted,background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:10,padding:"9px 12px",marginBottom:14,lineHeight:1.5}}>
                No bank connected yet.{" "}
                <button onClick={connectPlaid} style={{background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:12,cursor:"pointer",padding:0,fontFamily:"inherit"}}>Connect your bank →</button>
                {" "}to track this automatically.
              </div>
            )}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>{
                goalTpl.screenTime ? "Daily limit (hours) *"
                : goalTpl.source_type === "credit_payoff" ? "How much to pay off ($) *"
                : goalTpl.period === "month" ? "Monthly cap ($) *"
                : goalTpl.period === "week" ? `Weekly target${goalTpl.unit ? ` (${goalTpl.unit})` : ""} *`
                : `Target${goalTpl.unit ? ` (${goalTpl.unit})` : ""} *`
              }</div>
              <input value={newGoal.period ? (newGoal.period_target ?? newGoal.target_value) : newGoal.target_value}
                onChange={e=>setNewGoal(p=>{
                  const v = e.target.value;
                  const next = p.period ? {...p, period_target: v, target_value: v} : {...p, target_value: v};
                  // Same title-follows-number rule as the form's target input.
                  if (p.titleBase && p.title === p.autoTitle) {
                    const gen = genGoalTitle(p.titleBase, { period: next.period, unit: next.unit, value: v, source_type: next.source_type });
                    next.title = gen; next.autoTitle = gen;
                  }
                  return next;
                })}
                placeholder={goalTpl.targetHint || goalTpl.target_value || "0"} type="number" style={{...C.inp,width:"100%"}}/>
              {goalTpl.targetHint && <div style={{fontSize:11,color:T.textMuted,marginTop:5}}>Enter {goalTpl.targetHint}.</div>}
            </div>
            {goalTpl.moneyStart && (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Already saved or paid something? (optional, $)</div>
                <input value={newGoal.current_value === "0" ? "" : newGoal.current_value}
                  onChange={e=>setNewGoal(p=>({...p, current_value: e.target.value === "" ? "0" : e.target.value}))}
                  placeholder="0" type="number" style={{...C.inp,width:"100%"}}/>
              </div>
            )}
            {goalError&&(
              <div style={{marginBottom:12,padding:"9px 12px",borderRadius:8,background:withAlpha(T.danger,0.1),border:`1px solid ${T.danger}`,color:T.danger,fontSize:12,fontWeight:600}}>{goalError}</div>
            )}
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={addGoal} disabled={!newGoal.title.trim()||!newGoal.target_value}>Add Goal →</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>{setGoalView("gallery");setGoalError("");}}>Back</button>
            </div>
          </>) : (<>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>{editGoalId?"Edit Goal":"Custom Goal"}</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:20}}>{editGoalId?"Change the target, title, or deadline. Progress is kept.":"Describe it, pick the kind, set the number."}</div>

            {/* Title */}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Goal title</div>
              {/* No autoFocus — it popped the keyboard on frame one, putting
                  the modal straight into the covered-by-keyboard state the
                  9-14 scroll report described. */}
              <input value={newGoal.title} onChange={e=>{setNewGoal(p=>({...p,title:e.target.value})); if(goalError) setGoalError("");}}
                placeholder='e.g. "Move 10 miles a week" or "Save $10,000"'
                style={{...C.inp,width:"100%",border:goalError?`1px solid ${T.danger}`:undefined}}/>
              {/* Tracking disclosure — reads the binding in state, so it updates
                  live as templates are tapped or reset. Same ●-prefix language
                  GoalCard uses; no new visual vocabulary. Free-text titles are
                  never inferred into bindings, so no binding = self-logged. */}
              <div style={{fontSize:11,marginTop:6,color:(newGoal.source_type||customDraftBinding(newGoal))?T.success:T.textMuted}}>
                {customDraftBinding(newGoal) ? "\u25CF " + customDraftBinding(newGoal).describe
                 : newGoal.source_type === "plaid_spend" ? "\u25CF Tracks automatically from your bank via Plaid."
                 : newGoal.source_type === "workout" ? "\u25CF Tracks automatically from your workouts via Apple Health."
                 : newGoal.source_type === "savings" ? "\u25CF Tracks automatically from your savings balance."
                 : newGoal.screen_time_bind ? "\u25CF Tracks automatically from Screen Time on this iPhone, per day."
                 : newGoal.shape === "milestone" ? "You mark it done with the Update button when it happens."
                 : newGoal.shape === "cap" ? "You log the count yourself with the Update button. Staying under the limit counts as on track."
                 : newGoal.shape === "recurring" ? "You log the count yourself with the Update button, per " + (newGoal.period === "month" ? "month" : "week") + "."
                 : "You log progress yourself with the Update button."}
              </div>
            </div>

            {/* Savings binding offer (Tier 6, P3 follow-up) — EDIT MODE ONLY,
                for reach-a-number finance goals. This is the explicit opt-in
                that replaced the deleted P3 heuristics: the old code GUESSED
                that finance+$ meant savings and hid real typed progress; here
                the user says so, or says stop. Toggling writes newGoal.
                source_type so the disclosure line above updates live; nothing
                touches the DB until Save Changes. current_value is never
                written by binding — the typed progress is kept and resurfaces
                on unbind. Works both directions: bind manual → savings, and
                unbind savings → manual (chip-created bound goals included). */}
            {editGoalId && newGoal.category === "finance" && newGoal.shape === "target" && !newGoal.period
              && (!newGoal.source_type || newGoal.source_type === "savings") && (
              <div style={{marginBottom:12,padding:"10px 12px",borderRadius:10,background:T.surfaceAlt,border:`1px solid ${T.overlayLight(0.08)}`}}>
                <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
                  <input type="checkbox" checked={newGoal.source_type === "savings"}
                    onChange={e=>setNewGoal(p=>({...p, source_type: e.target.checked ? "savings" : undefined}))}
                    style={{accentColor:T.accent,width:16,height:16,marginTop:1,flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>Track this from your savings balance?</div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:3,lineHeight:1.5}}>
                      {newGoal.source_type === "savings"
                        ? "Progress reads your live savings balance from the Money tab. Anything you logged by hand is kept and comes back if you switch this off."
                        : "Progress currently comes from the Update button. Switch on to read your live savings balance instead."}
                    </div>
                    {newGoal.source_type === "savings" && !hasFinancesRow && (
                      <div style={{fontSize:11,color:T.warning,marginTop:4,lineHeight:1.5}}>
                        No savings amount on file yet. Add one in the Money tab; until then this goal shows no number rather than $0.
                      </div>
                    )}
                  </div>
                </label>
              </div>
            )}

            {/* Category is no longer ASKED anywhere: templates carry their
                own, the custom path infers it from the picked unit at save
                (inferGoalCategory — lbs/mi/km → fitness, $ → finance, else
                personal). It must still be SET: assembleSignals maps it to
                the cron signal's pillar tag, and it routes tab placement. */}

            {/* Shape picker — custom path only (template taps carry their own
                shape via bindings) and creation only (changing an existing
                goal's shape is a re-scope, not an edit). Each shape shows only
                the fields it needs; all four save through buildGoalPayload. */}
            {!editGoalId&&!newGoal.source_type&&(
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>What kind of goal is this?</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {GOAL_SHAPES.map(s=>{
                    const active = (newGoal.shape||"target")===s.id;
                    return (
                      <button key={s.id} onClick={()=>setNewGoal(p=>({
                        ...p,
                        shape: s.id,
                        // Shape-owned fields reset on every switch so a stale
                        // period or marker can't ride under the wrong shape.
                        period: (s.id==="recurring"||s.id==="cap") ? (p.period==="month"?"month":"week") : undefined,
                        period_target: (s.id==="recurring"||s.id==="cap") ? (p.period_target ?? "") : undefined,
                        source_config: s.id==="cap" ? {manual_shape:"cap"} : s.id==="milestone" ? {manual_shape:"milestone"} : s.id==="recurring" ? {manual_shape:"recurring"} : undefined,
                        target_value: s.id==="milestone" ? "1" : (s.id==="recurring"||s.id==="cap") ? (p.period_target ?? "") : p.target_value,
                        current_value: s.id==="milestone" ? "0" : p.current_value,
                        unit: s.id==="milestone" ? "" : p.unit,
                        deadline: (s.id==="recurring"||s.id==="cap") ? "" : p.deadline,
                      }))}
                        style={{background:active?withAlpha(T.accent,0.1):T.surfaceAlt,border:`1px solid ${active?T.accent:T.border}`,color:active?T.accent:T.textMuted,borderRadius:10,padding:"9px 10px",cursor:"pointer",textAlign:"left"}}>
                        <div style={{fontSize:12,fontWeight:700}}>{s.label}</div>
                        <div style={{fontSize:10,color:active?T.accent:T.textDim,marginTop:2}}>{s.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Screen Time binding moved to the template gallery (the
                "Screen time limit" template sets screen_time_bind and the
                confirm screen saves through addGoal's native block) — the
                old in-form toggle is gone with the redesign. */}

            {/* Progress values — edit mode shows only the target: current_value
                is Update's job, and unit is the binding's semantics (or would
                re-denominate a manual goal's existing progress). Creation mode
                shows only the picked shape's fields; milestones have none. */}
            {/* Starting value is GONE from the custom path — production
                showed it invited absolute weights into delta forms ("Loose
                10 pounds": 167 start / 157 target). current_value defaults
                to 0 and the Update button owns progress; the one legitimate
                use (money head-starts) lives on the template confirm
                screen's optional line. */}
            {!(!editGoalId&&newGoal.shape==="milestone")&&<div style={{display:"grid",gridTemplateColumns:editGoalId?"1fr":(newGoal.shape==="recurring"||newGoal.shape==="cap")?((newGoal.screen_time_bind||customDraftBinding(newGoal))?"1fr":"1fr 1fr"):"1fr",gap:10,marginBottom:12}}>
              <div>
                {/* Destination depends on goal type. For period goals (caps and
                    recurring — anything a template loaded `period` into state
                    for), the number the user edits IS the period_target: the
                    resolver reads period_target ?? target_value, so writing only
                    target_value here silently ignored the edit and left the cap
                    at the template default. Both fields are written in sync so
                    no reader sees them disagree. */}
                <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>{
                  // Custom period shapes are counts, not dollars — the $ labels
                  // belong to the bound (plaid/workout) templates only.
                  // Screen time: HOURS in both create and edit — minutes are
                  // storage-layer only (stHoursToMinutes/stMinutesToHours).
                  customDraftBinding(newGoal) ? ({ plaid_spend: "Monthly limit ($) *", savings: "Target ($) *" }[customDraftBinding(newGoal).source_type]
                    || (customDraftBinding(newGoal).screen_time_bind ? "Daily limit (hours) *"
                    : `${{ weekly_distance: "Miles", weekly_steps: "Steps" }[customDraftBinding(newGoal).source_config?.metric] || "Workouts"} per week *`))
                  : !newGoal.source_type && newGoal.screen_time_bind ? "Daily limit (hours) *"
                  : newGoal.source_type === "screen_time" ? "Daily limit (hours) *"
                  : !newGoal.source_type && newGoal.shape === "cap" ? "Limit (how many) *"
                  : !newGoal.source_type && newGoal.shape === "recurring" ? "How many times? *"
                  : newGoal.period ? (newGoal.period === "month" ? "Monthly cap ($) *" : "Weekly target *") : "Target value *"
                }</div>
                {newGoal.habitLinked ? (
                  <div style={{...C.inp,width:"100%",display:"flex",alignItems:"center",color:T.textMuted,fontSize:13}}>Set in your Gym / Workout habit ({newGoal.period_target}/wk)</div>
                ) : <input value={newGoal.period ? (newGoal.period_target ?? newGoal.target_value) : newGoal.target_value}
                  onChange={e=>setNewGoal(p=>{
                    const v = e.target.value;
                    const next = p.period ? {...p, period_target: v, target_value: v} : {...p, target_value: v};
                    // The title tracks the number ONLY while it is still the
                    // untouched generated title (title === autoTitle). Once the
                    // user has typed in the title field the pair has diverged
                    // and the title is theirs — never overwritten again.
                    if (p.titleBase && p.title === p.autoTitle) {
                      const gen = genGoalTitle(p.titleBase, { period: next.period, unit: next.unit, value: v, source_type: next.source_type });
                      next.title = gen;
                      next.autoTitle = gen;
                    }
                    return next;
                  })}
                  placeholder="100" style={{...C.inp,width:"100%"}} type="number"/>}
              </div>
              {/* Hidden while Track from Screen Time is on: a screen-time cap
                  is inherently DAILY (the window runs to 23:59 local, the row
                  is keyed on one local day) — offering week/month here could
                  only contradict the binding. The save path forces period
                  'day' regardless, so this is honesty, not enforcement. */}
              {!editGoalId&&(newGoal.shape==="recurring"||newGoal.shape==="cap")&&!newGoal.source_type&&!newGoal.screen_time_bind&&!customDraftBinding(newGoal)&&<div>
                <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Per</div>
                <div style={{display:"flex",gap:6}}>
                  {/* Tap the selected one again to CLEAR it (a standing limit
                      with no period). The old handler could only set, so the
                      pre-first-tap unset state was unreachable ever after. */}
                  {[["week","week"],["month","month"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setNewGoal(p=>p.period===v ? {...p,period:undefined,period_target:undefined} : {...p,period:v})}
                      aria-pressed={newGoal.period===v}
                      style={{flex:1,padding:"9px 6px",borderRadius:10,border:`1px solid ${newGoal.period===v?T.accent:T.border}`,background:newGoal.period===v?withAlpha(T.accent,0.1):T.surfaceAlt,color:newGoal.period===v?T.accent:T.textMuted,cursor:"pointer",fontSize:12,fontWeight:newGoal.period===v?700:400}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>}
            </div>}

            {/* Unit — bounded picker, never free text again: the free-text
                field produced "", "Mi" and "%" in production and let two
                absolute weights land in a delta form. "count" stores "". */}
            {!editGoalId&&!newGoal.source_type&&!newGoal.screen_time_bind&&newGoal.shape!=="milestone"&&(
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Unit</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {CUSTOM_GOAL_UNITS.map(u=>{
                    const val = u === "count" ? "" : u;
                    const active = newGoal.unit === val;
                    return (
                      <button key={u} onClick={()=>setNewGoal(p=>({...p,unit:val}))} aria-pressed={active}
                        style={{padding:"7px 12px",borderRadius:10,border:`1px solid ${active?T.accent:T.border}`,background:active?withAlpha(T.accent,0.1):T.surfaceAlt,color:active?T.accent:T.textMuted,cursor:"pointer",fontSize:12,fontWeight:active?700:500}}>
                        {u}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Track automatically (2026-10-20) — the picked shape + unit map to an
                existing binding (customGoalBinding). On by default; off = manual.
                Spend caps: everyday spending by default, bills opt-in, optional
                category. No binding: say plainly that it is manual. */}
            {!editGoalId&&!newGoal.source_type&&!newGoal.screen_time_bind&&newGoal.shape!=="milestone"&&(()=>{
              const opt = customBindingOption(newGoal);
              if (!opt) return <div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>Manual: you update this.</div>;
              const on = newGoal.autoTrack !== false;
              return (
                <div style={{marginBottom:12,padding:"10px 12px",borderRadius:10,background:T.surfaceAlt,border:`1px solid ${T.overlayLight(0.08)}`}}>
                  <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
                    <input type="checkbox" checked={on} onChange={e=>setNewGoal(p=>({...p,autoTrack:e.target.checked}))}
                      style={{accentColor:T.accent,width:16,height:16,marginTop:1,flexShrink:0}}/>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:T.text}}>Track automatically</div>
                      <div style={{fontSize:11,color:on?T.success:T.textMuted,marginTop:3,lineHeight:1.5}}>{on ? "\u25CF " + opt.describe : "Manual: you update this."}</div>
                    </div>
                  </label>
                  {on && opt.source_type === "plaid_spend" && (
                    <div style={{marginTop:10}}>
                      <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:T.text,cursor:"pointer",marginBottom:8}}>
                        <input type="checkbox" checked={!!newGoal.includeBills} onChange={e=>setNewGoal(p=>({...p,includeBills:e.target.checked}))} style={{accentColor:T.accent,width:15,height:15}}/>
                        Include bills (rent, utilities, insurance)
                      </label>
                      <div style={{fontSize:11,color:T.textMuted,marginBottom:6}}>Only one kind of spending? (optional)</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {[{key:null,label:"All"},...SPEND_CATEGORY_FILTERS].map(f=>{
                          const active = (newGoal.spendFilter||null) === f.key;
                          return <button key={f.key||"all"} onClick={()=>setNewGoal(p=>({...p,spendFilter:f.key}))} aria-pressed={active}
                            style={{padding:"6px 10px",borderRadius:10,border:`1px solid ${active?T.accent:T.border}`,background:active?withAlpha(T.accent,0.1):T.surface,color:active?T.accent:T.textMuted,cursor:"pointer",fontSize:11.5,fontWeight:active?700:500}}>{f.label}</button>;
                        })}
                      </div>
                    </div>
                  )}
                  {on && opt.source_type === "plaid_spend" && !plaidConnected && (
                    <div style={{fontSize:11,color:T.textMuted,marginTop:8}}>No bank connected yet. <button onClick={connectPlaid} style={{background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:11,cursor:"pointer",padding:0,fontFamily:"inherit"}}>Connect your bank →</button></div>
                  )}
                </div>
              );
            })()}

            {/* Habit offer — the recurring shape's no-endpoint case. Habits own
                streaks; a goal that is only "do this consistently forever" is
                a habit wearing a goal costume. */}
            {!editGoalId&&!newGoal.source_type&&newGoal.shape==="recurring"&&(
              <div style={{fontSize:11,color:T.textDim,marginBottom:14,lineHeight:1.5}}>
                No end point, just want to stay consistent? That's a habit — habits track streaks.{" "}
                <button onClick={()=>{
                  setCustomHabit({ label: newGoal.title.trim().slice(0,40), target: String(parseInt(newGoal.period_target)||5), unit: newGoal.period==="month"?"times/month":"times/week" });
                  setHabitTab("custom");
                  closeGoalModal();
                  setShowAdd(true);
                }} style={{background:"none",border:"none",color:T.accent,fontWeight:700,fontSize:11,cursor:"pointer",padding:0,fontFamily:"inherit",textDecoration:"underline"}}>Add it as a habit instead →</button>
              </div>
            )}

            {/* Deadline — period shapes have no end date by definition. */}
            {!(newGoal.shape==="recurring"||newGoal.shape==="cap")&&<div style={{marginBottom:20}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Target date (optional)</div>
              {dateField(newGoal.deadline, v=>setNewGoal(p=>({...p,deadline:v})))}
            </div>}

            {/* The inline template-chip list moved to the gallery front
                door (GOAL_TEMPLATES via openTemplateConfirm) — templates are
                no longer an afterthought at the bottom of the custom form. */}

            {goalError&&(
              <div style={{marginBottom:12,padding:"9px 12px",borderRadius:8,background:withAlpha(T.danger,0.1),border:`1px solid ${T.danger}`,color:T.danger,fontSize:12,fontWeight:600}}>{goalError}</div>
            )}

            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={editGoalId?saveGoalEdits:addGoal} disabled={!newGoal.title.trim()||!newGoal.target_value}>{editGoalId?"Save Changes":"Add Goal →"}</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={editGoalId?closeGoalModal:()=>{setGoalView("gallery");setGoalError("");}}>{editGoalId?"Cancel":"Back"}</button>
            </div>
          </>)}
          </div>
        </div>
      )}

      {/* ── UPDATE GOAL PROGRESS MODAL ── */}
      {/* ── WEEK PLANNER ── pre-filled from last week (src/lib/weekPlan.js); tap
          days on/off, set a time; Save writes commitments (Up next, hour-ahead
          reminders, "Did it happen?"). No AI call anywhere here. */}
      {weekPlan&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>!planSaving&&setWeekPlan(null)}>
          <div style={{...C.mbox,width:400}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>Plan your week</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:16}}>
              {fmtDueDay(weekPlan.days[0])} to {fmtDueDay(weekPlan.days[weekPlan.days.length-1])}. Picked from the days you did these last week. Tap a day to move it.
            </div>
            {weekPlan.items.length===0 ? (
              <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:16}}>Nothing to plan yet. Give a habit a weekly target (like 3 times a week), or add a workouts goal, and it shows up here.</div>
            ) : weekPlan.items.map(it=>(
              <div key={it.key} style={{padding:"12px 0",borderTop:`1px solid ${T.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                  <div style={{fontSize:14,fontWeight:700,color:T.text}}>{it.label}</div>
                  <div style={{fontSize:11,color:it.days.length<it.target?T.warning:T.textMuted}}>{it.days.length} of {it.target}</div>
                </div>
                <div style={{display:"flex",gap:5,marginBottom:8}}>
                  {weekPlan.days.map(d=>{
                    const on = it.days.includes(d);
                    return <button key={d} onClick={()=>toggleplanDay(it.key,d)} aria-pressed={on} aria-label={`${it.label} on ${fmtDueDay(d)}`}
                      style={{flex:1,minWidth:0,padding:"8px 0",borderRadius:9,border:`1px solid ${on?T.accent:T.border}`,background:on?withAlpha(T.accent,0.12):T.surfaceAlt,color:on?T.accent:T.textMuted,fontSize:11,fontWeight:on?700:500,cursor:"pointer"}}>{WEEKDAY_SHORT[weekdayIdx(d)]}</button>;
                  })}
                </div>
                <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:T.textMuted}}>
                  Time (optional)
                  <input type="time" value={it.time||""} onChange={e=>setPlanTime(it.key,e.target.value)} style={{...C.inp,padding:"6px 8px",fontSize:12,width:120}}/>
                </label>
              </div>
            ))}
            {planError&&<div style={{marginTop:8,color:T.danger,fontSize:12,fontWeight:600}}>{planError}</div>}
            <div style={{display:"flex",gap:10,marginTop:14}}>
              <button style={{...C.btn(T.accent),flex:1}} disabled={planSaving||weekPlan.items.every(it=>!it.days.length)} onClick={saveWeekPlan}>{planSaving?"Saving…":"Save plan"}</button>
              <button style={{...C.ghost,padding:"9px 18px"}} disabled={planSaving} onClick={()=>setWeekPlan(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showUpdateGoal&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowUpdateGoal(null)}>
          <div style={{...C.mbox,width:360}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}> Update Progress</div>
            <div style={{fontSize:13,color:T.accent,fontWeight:700,marginBottom:4}}>{showUpdateGoal.title}</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:16}}>Target: {showUpdateGoal.target_value} {showUpdateGoal.unit}</div>
            <div style={{position:"relative",marginBottom:6}}>
              <input value={goalUpdateVal} onChange={e=>setGoalUpdateVal(e.target.value)}
                placeholder={String(showUpdateGoal.current_value)}
                style={{...C.inp,width:"100%",fontSize:28,textAlign:"center",fontWeight:800}} type="number" autoFocus/>
            </div>
            <div style={{textAlign:"center",fontSize:12,color:T.textMuted,marginBottom:16}}>{showUpdateGoal.unit}</div>
            {/* Preview progress */}
            {goalUpdateVal && (
              <div style={{marginBottom:16}}>
                <div style={{background:T.overlayLight(0.07),borderRadius:99,height:8,overflow:"hidden",marginBottom:4}}>
                  <div style={{width:`${Math.min(100,Math.round((parseFloat(goalUpdateVal)/showUpdateGoal.target_value)*100))}%`,background:`linear-gradient(90deg,${T.accent},${withAlpha(T.accent,0.6)})`,height:"100%",borderRadius:99}}/>
                </div>
                <div style={{textAlign:"center",fontSize:12,color:T.accent,fontWeight:700}}>
                  {Math.min(100,Math.round((parseFloat(goalUpdateVal)/showUpdateGoal.target_value)*100))}% complete
                  {parseFloat(goalUpdateVal)>=showUpdateGoal.target_value && <span style={{color:T.success}}>  Goal reached!</span>}
                </div>
              </div>
            )}
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={()=>updateGoalProgress(showUpdateGoal,goalUpdateVal)} disabled={!goalUpdateVal}>Save →</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowUpdateGoal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}


      {/* ── EDIT PROFILE MODAL ── */}
      {showEditProfile&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowEditProfile(false)}>
          <div style={{...C.mbox,width:460,maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>Edit Profile</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:20}}>Your stats are private — only you can see them.</div>

            {/* Avatar + Display Name */}
            <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>Identity</div>
            <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20,padding:"16px",background:T.overlayLight(0.06),borderRadius:12,border:`1px solid ${T.overlayLight(0.06)}`}}>
              <div style={{position:"relative",flexShrink:0}}>
                {avatarUrl
                  ? <img src={avatarUrl} alt="avatar" style={{width:64,height:64,borderRadius:"50%",objectFit:"cover",border:"2px solid rgba(0,0,0,0.08)"}}/>
                  : <div style={{width:64,height:64,borderRadius:"50%",background:`linear-gradient(135deg,${T.textMuted},${T.accent})`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:22,color:"#fff"}}>
                      {(username||"Me").slice(0,2).toUpperCase()}
                    </div>
                }
                <button style={{position:"absolute",bottom:-4,right:-4,width:22,height:22,background:T.accentFill,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:"2px solid #fff",fontSize:11,color:"#fff",fontWeight:700,padding:0}} onClick={async()=>{
                    try {
                      const photo = await Camera.getPhoto({
                        quality: 80,
                        allowEditing: false,
                        resultType: CameraResultType.DataUrl,
                        source: CameraSource.Prompt,
                        presentationStyle: "popover",
                      });
                      const dataUrl = photo.dataUrl;
                      if (!dataUrl) return;
                      if (user) {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session) return;
                        const { error } = await writeProfile({ avatar_url: dataUrl });
                        if (error) {
                          console.error("avatar save failed", error);
                          setProfileError("Couldn't save your photo. Please try again.");
                          return;
                        }
                        setProfileError("");
                      }
                      // Local state only after the write is confirmed, so a failed
                      // save cannot leave a photo on screen that is not in the DB.
                      setAvatarUrl(dataUrl);
                    } catch (e) {
                      // Camera cancel throws too, so only surface a real failure.
                      if (!/cancel/i.test(e?.message || "")) {
                        console.error("avatar capture failed", e);
                        setProfileError("Couldn't save your photo. Please try again.");
                      }
                    }
                  }} aria-label="Add photo">+</button>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:11,color:T.textMuted,marginBottom:6}}>Display Name</div>
                <input
                  value={username}
                  onChange={e=>setUsername(e.target.value)}
                  onBlur={async()=>{
                    if (!user || !username.trim()) return;
                    // Writes display_name, NOT username. This field is the display
                    // name, but it used to upsert into profiles.username — the
                    // UNIQUE auto-handle. That was wrong on both paths: on success
                    // the load at mount prefers display_name, so the edit appeared
                    // to vanish on the next launch; and when another account
                    // already held that handle the write failed with a uniqueness
                    // violation that was discarded, leaving the rejected name on
                    // screen. Onboarding already writes display_name for exactly
                    // this reason; this now matches it.
                    const { error } = await writeProfile({ display_name: username.trim() });
                    if (error) {
                      console.error("display name save failed", error);
                      setProfileError("Couldn't save your name. Please try again.");
                      return;
                    }
                    setProfileError("");
                  }}
                  placeholder="Your name"
                  style={{...C.inp, width:"100%", border:profileError?`1px solid ${T.danger}`:undefined}}
                />
                {profileError&&(
                  <div style={{marginTop:6,padding:"7px 10px",borderRadius:8,background:withAlpha(T.danger,0.1),border:`1px solid ${T.danger}`,color:T.danger,fontSize:11,fontWeight:600}}>{profileError}</div>
                )}
                <div style={{fontSize:10,color:T.textMuted,marginTop:5}}>Tap photo to change your avatar</div>
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>Coach Name</div>
              <input
                value={coachName}
                onChange={e=>setCoachName(e.target.value)}
                onBlur={async()=>{
                  if (!user) return;
                  // Same trap as the two writes above: upsert({id, coach_name})
                  // omits username, and PostgREST's INSERT ... ON CONFLICT runs the
                  // NOT NULL check on the proposed tuple before resolving the
                  // conflict, so it failed for every existing user too.
                  const { error } = await writeProfile({ coach_name: coachName.trim() || "Kai" });
                  if (error) {
                    console.error("coach name save failed", error);
                    setProfileError("Couldn't save your coach's name. Please try again.");
                    return;
                  }
                  setProfileError("");
                }}
                placeholder="Kai"
                style={{...C.inp, width:"100%"}}
              />
              <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>What your AI coach is called.</div>
            </div>

            {/* Basic stats */}
            <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>Body Stats</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
              {/* Height / sex / activity level removed — orphaned by the
                  BMI/BMR/calorie removal; saveBodyStats no longer writes them. */}
              {birthYearSelect(profileForm.birth_year ?? birthYear ?? "", v=>setProfileForm(p=>({...p,birth_year:v})), { label: birthYearEstimated && profileForm.birth_year === undefined ? "Birth year (estimated, please check)" : "Birth year" })}
              {[["Current Weight (lbs)","current_weight","175"],["Goal Weight (lbs)","goal_weight","160"]].map(([label,key,ph])=>(
                <div key={key}>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>{label}</div>
                  <input value={profileForm[key]||""} onChange={e=>setProfileForm(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={{...C.inp,width:"100%"}} type="number"/>
                </div>
              ))}
            </div>

            {/* Goal type */}
            <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>Goal Type</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
              {[["fat_loss"," Fat Loss"],["muscle_gain"," Muscle Gain"],["general_fitness"," General Fitness"],["maintenance"," Maintenance"],["custom"," Custom Goal"]].map(([val,label])=>(
                <button key={val} onClick={()=>setProfileForm(p=>({...p,goal_type:val}))}
                  style={{background:profileForm.goal_type===val?withAlpha(T.accent,0.2):T.surfaceAlt,border:`1px solid ${profileForm.goal_type===val?T.accent:T.overlayLight(0.08)}`,color:profileForm.goal_type===val?T.accent:T.textMuted,borderRadius:10,padding:"10px 12px",cursor:"pointer",fontSize:13,fontWeight:profileForm.goal_type===val?700:400,textAlign:"left"}}>
                  {label}
                </button>
              ))}
            </div>

            {/* Custom goal label */}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Goal description (optional)</div>
              <input value={profileForm.goal_label||""} onChange={e=>setProfileForm(p=>({...p,goal_label:e.target.value}))} placeholder='e.g. "I want to reach 175 lbs by summer"' style={{...C.inp,width:"100%"}}/>
            </div>

            {/* Target date */}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Target date (optional)</div>
              {dateField(profileForm.goal_date, v=>setProfileForm(p=>({...p,goal_date:v})))}
            </div>

            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={async()=>{ await saveBodyStats(profileForm); setShowEditProfile(false); }}>Save Profile →</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowEditProfile(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOG WEIGHT MODAL ── */}
      {showLogWeight&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowLogWeight(false)}>
          <div style={{...C.mbox,width:340}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}> Log Today's Weight</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:16}}>Weigh yourself first thing in the morning for consistency.</div>
            <input value={logWeightVal} onChange={e=>setLogWeightVal(e.target.value)} placeholder="e.g. 174.5" style={{...C.inp,width:"100%",fontSize:24,textAlign:"center",fontWeight:800}} type="number" autoFocus/>
            <div style={{fontSize:11,color:T.textMuted,textAlign:"center",marginTop:6,marginBottom:16}}>lbs</div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={logWeight} disabled={!logWeightVal}>Save</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowLogWeight(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}


      {/* ── EDIT CREDIT DETAILS MODAL ── */}
      {showEditCreditDetails&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowEditCreditDetails(false)}>
          <div style={{...C.mbox,width:480,maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}> Credit & Benefits Details</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:20}}>Used to calculate your real score breakdown and check benefit eligibility. Private — only you can see this.</div>

            <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>Credit Card</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[["CC Balance ($)","cc_balance","2400"],["Credit Limit ($)","cc_limit","5000"]].map(([label,key,ph])=>(
                <div key={key}>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>{label}</div>
                  <input value={creditDetailForm[key]||""} onChange={e=>setCreditDetailForm(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={{...C.inp,width:"100%"}} type="number"/>
                </div>
              ))}
            </div>

            <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>Credit History</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
              {[["Credit Age (yrs)","credit_age_years","4"],["# of Accounts","num_accounts","3"],["Hard Inquiries","hard_inquiries","1"]].map(([label,key,ph])=>(
                <div key={key}>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>{label}</div>
                  <input value={creditDetailForm[key]||""} onChange={e=>setCreditDetailForm(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={{...C.inp,width:"100%"}} type="number"/>
                </div>
              ))}
            </div>

            <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>Benefits Eligibility Info</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Household / family size</div>
              <input value={creditDetailForm.family_size||""} onChange={e=>setCreditDetailForm(p=>({...p,family_size:e.target.value}))} placeholder="1" style={{...C.inp,width:"100%"}} type="number"/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
              {[
                ["has_student_loans","I have student loans"],
                ["is_first_time_buyer","I am a first-time homebuyer (never owned a home)"],
                ["employer_has_401k","My employer offers a 401(k) or retirement plan"],
              ].map(([key,label])=>(
                <label key={key} style={{display:"flex",alignItems:"center",gap:12,cursor:"pointer",padding:"10px 14px",background:T.overlayLight(0.06),borderRadius:10,border:`1px solid ${creditDetailForm[key]?T.accent:T.overlayLight(0.08)}`}}>
                  <div onClick={()=>setCreditDetailForm(p=>({...p,[key]:!p[key]}))}
                    style={{width:20,height:20,borderRadius:5,border:`2px solid ${creditDetailForm[key]?T.accent:T.textDim}`,background:creditDetailForm[key]?T.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.2s"}}>
                    {creditDetailForm[key]&&<span style={{color:"#fff",fontSize:12,fontWeight:900}}>✓</span>}
                  </div>
                  <span style={{fontSize:13,color:creditDetailForm[key]?T.text:T.textMuted}}>{label}</span>
                </label>
              ))}
            </div>

            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={async()=>{ await saveCreditDetails(creditDetailForm); setShowEditCreditDetails(false); }}>Save →</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowEditCreditDetails(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD DEBT MODAL ── */}
      {showAddDebt&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddDebt(false)}>
          <div style={{...C.mbox,width:420}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:16}}> Add a Debt</div>
            {[["Name","name","Credit Card, Student Loan..."],["Balance ($)","balance","5000"],["Monthly Payment ($)","monthly_payment","150"],["APR (%)","apr","19.9%"]].map(([label,key,ph])=>(
              <div key={key} style={{marginBottom:12}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>{label}</div>
                <input value={newDebt[key]} onChange={e=>setNewDebt(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={{...C.inp,width:"100%"}}/>
              </div>
            ))}
            <div style={{display:"flex",gap:10,marginTop:8}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={addDebt}>Add Debt</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddDebt(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD OTHER ASSET MODAL ── */}
      {showAddAsset&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddAsset(false)}>
          <div style={{...C.mbox,width:420}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:16}}>Add savings or an asset</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Type</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6}}>
                {[["cash","Cash"],["gold_silver","Gold / Silver"],["bank_brokerage","Other bank / brokerage"],["other","Other"]].map(([t,lbl])=>(
                  <button key={t} onClick={()=>setNewAsset(p=>({...p,type:t}))} style={{background:newAsset.type===t?withAlpha(T.accent,0.2):T.surfaceAlt,border:`1px solid ${newAsset.type===t?T.accent:T.overlayLight(0.08)}`,color:newAsset.type===t?T.accent:T.textMuted,borderRadius:8,padding:"8px",cursor:"pointer",fontSize:12,fontWeight:700,textAlign:"center"}}>{lbl}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Label (optional)</div>
              <input value={newAsset.label} onChange={e=>setNewAsset(p=>({...p,label:e.target.value}))} placeholder="e.g. Vanguard, coin collection" style={{...C.inp,width:"100%"}}/>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Amount (optional)</div>
              <input value={newAsset.amount} onChange={e=>setNewAsset(p=>({...p,amount:e.target.value}))} placeholder="2500" inputMode="decimal" style={{...C.inp,width:"100%"}}/>
              <div style={{fontSize:11,color:T.textMuted,marginTop:6}}>Leave blank if you'd rather not say — it'll still be listed.</div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:8}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={addAsset}>Add</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddAsset(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD BILL MODAL ── */}
      {showAddBill&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddBill(false)}>
          <div style={{...C.mbox,width:440}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:16}}> Add a Bill</div>
            {[["Name","name","Rent, Netflix, Electric..."],["Amount ($)","amount","1200"],["Due Day of Month","due_day","1"]].map(([label,key,ph])=>(
              <div key={key} style={{marginBottom:12}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>{label}</div>
                <input value={newBill[key]} onChange={e=>setNewBill(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={{...C.inp,width:"100%"}}/>
              </div>
            ))}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Category</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                {[["housing"," Housing"],["transport"," Transport"],["food"," Food"],["utilities"," Utilities"],["subscriptions"," Subs"],["insurance"," Insurance"],["health"," Health"],["debt"," Debt"],["other"," Other"]].map(([cat,lbl])=>(
                  <button key={cat} onClick={()=>setNewBill(p=>({...p,category:cat}))} style={{background:newBill.category===cat?"rgba(129,140,248,0.2)":T.surfaceAlt,border:`1px solid ${newBill.category===cat?T.accent:T.overlayLight(0.08)}`,color:newBill.category===cat?T.accent:T.textMuted,borderRadius:8,padding:"6px",cursor:"pointer",fontSize:11,fontWeight:700,textAlign:"center"}}>{lbl}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Status</div>
              <div style={{display:"flex",gap:6}}>
                {["upcoming","paid","overdue"].map(s=>(
                  <button key={s} onClick={()=>setNewBill(p=>({...p,status:s}))} style={{flex:1,background:newBill.status===s?"rgba(129,140,248,0.2)":T.surfaceAlt,border:`1px solid ${newBill.status===s?T.accent:T.overlayLight(0.08)}`,color:newBill.status===s?T.accent:T.textMuted,borderRadius:8,padding:"7px",cursor:"pointer",fontSize:12,fontWeight:700,textTransform:"capitalize"}}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",background:T.overlayLight(0.06),borderRadius:10,padding:"10px 14px"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600}}>Autopay</div>
                <div style={{fontSize:11,color:T.textMuted}}>Mark as automatically paid each month</div>
              </div>
              <div onClick={()=>setNewBill(p=>({...p,autopay:!p.autopay}))} style={{width:42,height:24,borderRadius:99,background:newBill.autopay?T.success:T.overlayLight(0.07),position:"relative",cursor:"pointer",transition:"background 0.2s",flexShrink:0}}>
                <div style={{position:"absolute",top:4,left:newBill.autopay?20:4,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:8}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={()=>{addBill();setNewBill({name:"",amount:"",due_day:"",status:"upcoming",category:"other",autopay:false});}}>Add Bill</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddBill(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {editBill&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setEditBill(null)}>
          <div style={{...C.mbox,width:440}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:16}}> Edit Bill</div>
            {[["Name","name",""],["Amount ($)","amount",""],["Due Day of Month","due_day",""]].map(([label,key,ph])=>(
              <div key={key} style={{marginBottom:12}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>{label}</div>
                <input value={editBill[key]||""} onChange={e=>setEditBill(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={{...C.inp,width:"100%"}}/>
              </div>
            ))}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Category</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                {[["housing"," Housing"],["transport"," Transport"],["food"," Food"],["utilities"," Utilities"],["subscriptions"," Subs"],["insurance"," Insurance"],["health"," Health"],["debt"," Debt"],["other"," Other"]].map(([cat,lbl])=>(
                  <button key={cat} onClick={()=>setEditBill(p=>({...p,category:cat}))} style={{background:editBill.category===cat?"rgba(129,140,248,0.2)":T.surfaceAlt,border:`1px solid ${editBill.category===cat?T.accent:T.overlayLight(0.08)}`,color:editBill.category===cat?T.accent:T.textMuted,borderRadius:8,padding:"6px",cursor:"pointer",fontSize:11,fontWeight:700,textAlign:"center"}}>{lbl}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Status</div>
              <div style={{display:"flex",gap:6}}>
                {["upcoming","paid","overdue"].map(s=>(
                  <button key={s} onClick={()=>setEditBill(p=>({...p,status:s}))} style={{flex:1,background:editBill.status===s?"rgba(129,140,248,0.2)":T.surfaceAlt,border:`1px solid ${editBill.status===s?T.accent:T.overlayLight(0.08)}`,color:editBill.status===s?T.accent:T.textMuted,borderRadius:8,padding:"7px",cursor:"pointer",fontSize:12,fontWeight:700,textTransform:"capitalize"}}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",background:T.overlayLight(0.06),borderRadius:10,padding:"10px 14px"}}>
              {/* On: no due-soon reminders, marked paid when the charge posts.
                  Off: the normal reminders resume. Using it makes the choice the
                  user's (autopay_set_by_user), so nothing flips it back. */}
              <div style={{paddingRight:10}}>
                <div style={{fontSize:13,fontWeight:600}}>Autopay</div>
                <div style={{fontSize:11,color:T.textMuted,lineHeight:1.45}}>{editBill.autopay ? "On: no reminders. Marked paid when the charge posts." : "Off: you'll get a reminder before it's due."}</div>
              </div>
              <button type="button" role="switch" aria-checked={!!editBill.autopay} aria-label="Autopay" onClick={()=>setEditBill(p=>({...p,autopay:!p.autopay,autopayTouched:true}))} style={{width:42,height:24,borderRadius:99,border:"none",padding:0,background:editBill.autopay?T.success:T.overlayLight(0.07),position:"relative",cursor:"pointer",transition:"background 0.2s",flexShrink:0}}>
                <span style={{position:"absolute",top:4,left:editBill.autopay?20:4,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
              </button>
            </div>
            <div style={{display:"flex",gap:10,marginTop:8}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={()=>updateBill(editBill)}>Save Changes</button>
              {/* Autopay: the only place to mark it paid by hand (a charge on an unlinked account). */}
              {editBill.autopay===true&&!isPaidThisMonth(editBill, currentMonth())&&(
                <button style={{...C.ghost,padding:"9px 14px"}} onClick={()=>{ markBillPaid(editBill.id); setEditBill(null); }}>Mark paid</button>
              )}
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setEditBill(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD MEDICATION MODAL ── */}
      {showAddMed&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddMed(false)}>
          <div style={{...C.mbox,width:420}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:16}}> Add a Medication</div>
            {[["Name","name","Lisinopril, Metformin..."],["Dose","dose","10mg, 500mg..."]].map(([label,key,ph])=>(
              <div key={key} style={{marginBottom:12}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>{label}</div>
                <input value={newMed[key]} onChange={e=>setNewMed(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={{...C.inp,width:"100%"}}/>
              </div>
            ))}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Schedule</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["Daily","Twice daily","Weekly","As needed"].map(s=>(
                  <button key={s} onClick={()=>setNewMed(p=>({...p,schedule:s}))} style={{background:newMed.schedule===s?"rgba(129,140,248,0.2)":T.surfaceAlt,border:`1px solid ${newMed.schedule===s?T.accent:T.overlayLight(0.08)}`,color:newMed.schedule===s?T.accent:T.textMuted,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700}}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Supply length (days)</div>
              <input value={newMed.refill_days} onChange={e=>setNewMed(p=>({...p,refill_days:e.target.value}))} placeholder="30" style={{...C.inp,width:"100%"}} type="number"/>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={addMed}>Add Medication</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddMed(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD CHECKUP MODAL ── */}
      {showAddCheckup&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddCheckup(false)}>
          <div style={{...C.mbox,width:420}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:16}}>Add a Health Checkup</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Checkup Name</div>
              <input value={newCheckup.name} onChange={e=>setNewCheckup(p=>({...p,name:e.target.value}))} placeholder="Annual Physical, Dental Cleaning..." style={{...C.inp,width:"100%"}}/>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>When was your last one? <span style={{fontWeight:400,color:T.textMuted}}>(optional)</span></div>
              <input value={newCheckup.last_date} onChange={e=>setNewCheckup(p=>({...p,last_date:e.target.value}))} placeholder="e.g. 6 months ago, Jan 2024..." style={{...C.inp,width:"100%"}}/>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Next scheduled date <span style={{fontWeight:400,color:T.textMuted}}>(optional)</span></div>
              {dateField(newCheckup.next_date, v=>setNewCheckup(p=>({...p,next_date:v})))}
            </div>
            <div style={{marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
              <div onClick={()=>setNewCheckup(p=>({...p,urgent:!p.urgent}))} style={{width:40,height:22,borderRadius:99,background:newCheckup.urgent?T.danger:T.surfaceAlt,position:"relative",cursor:"pointer",transition:"background 0.2s"}}>
                <div style={{position:"absolute",top:3,left:newCheckup.urgent?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
              </div>
              <span style={{fontSize:13,color:newCheckup.urgent?T.danger:T.textMuted}}>Mark as overdue</span>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={addCheckup}>Add Checkup</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddCheckup(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD SUPPLEMENT MODAL ── */}
      {showAddSupp&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddSupp(false)}>
          <div style={{...C.mbox,width:420}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:800,marginBottom:4}}> Add a Supplement</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:18}}>Track any vitamin, mineral, herb, or nootropic.</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.textMuted,fontWeight:700,marginBottom:6}}>Supplement name *</div>
              <input value={newSupp.name} onChange={e=>setNewSupp(p=>({...p,name:e.target.value}))}
                placeholder="e.g. Vitamin C, Lion's Mane, Ashwagandha..."
                style={{...C.inp,width:"100%"}}/>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:T.textMuted,fontWeight:700,marginBottom:6}}>Dose (optional)</div>
              <input value={newSupp.dose} onChange={e=>setNewSupp(p=>({...p,dose:e.target.value}))}
                placeholder="e.g. 500mg, 1 capsule..."
                style={{...C.inp,width:"100%"}}/>
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,color:T.textMuted,fontWeight:700,marginBottom:6}}>When do you take it?</div>
              <div style={{display:"flex",gap:6}}>
                {["Morning","Afternoon","Evening","With meals"].map(t=>(
                  <button key={t} onClick={()=>setNewSupp(p=>({...p,timing:t}))}
                    style={{flex:1,background:newSupp.timing===t?withAlpha(T.accent,0.15):T.surfaceAlt,border:`1px solid ${newSupp.timing===t?T.accent:T.overlayLight(0.08)}`,color:newSupp.timing===t?T.accent:T.textMuted,borderRadius:8,padding:"7px 2px",cursor:"pointer",fontSize:11,fontWeight:700}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1,opacity:newSupp.name.trim()?1:0.5}} onClick={addSupp} disabled={!newSupp.name.trim()}>+ Add Supplement</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddSupp(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* ── ADD WORKOUT MODAL ── */}
      {showAddWorkout&&(
        <div role="dialog" aria-modal="true" style={C.overlay} onClick={()=>setShowAddWorkout(false)}>
          <div style={{...C.mbox,width:420}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,marginBottom:4}}>Log a Workout</div>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:18}}>Saved to your workouts; today's or a recent date also ticks your Gym habit.</div>

            {/* Type chips */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:8}}>Type</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {WORKOUT_TYPES.map(t=>(
                  <button key={t} onClick={()=>setNewWorkout(p=>({...p,type:t}))}
                    style={{background:newWorkout.type===t?withAlpha(T.accent,0.2):T.overlayLight(0.05),border:`1px solid ${newWorkout.type===t?T.accent:T.overlayLight(0.1)}`,color:newWorkout.type===t?T.accent:T.textMuted,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Name <span style={{fontWeight:400,color:T.textMuted}}>(optional)</span></div>
              <input value={newWorkout.name} onChange={e=>setNewWorkout(p=>({...p,name:e.target.value}))} placeholder="e.g. Upper body, Morning swim" style={{...C.inp,width:"100%"}}/>
            </div>

            {/* Duration + Date row */}
            <div style={{display:"flex",gap:10,marginBottom:14}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Duration <span style={{fontWeight:400,color:T.textMuted}}>(min)</span></div>
                <input type="number" min="1" value={newWorkout.duration_min} onChange={e=>setNewWorkout(p=>({...p,duration_min:e.target.value}))} placeholder="45" style={{...C.inp,width:"100%"}}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Distance <span style={{fontWeight:400,color:T.textMuted}}>(mi, optional)</span></div>
                <input type="number" min="0" step="0.1" value={newWorkout.distance_mi} onChange={e=>setNewWorkout(p=>({...p,distance_mi:e.target.value}))} placeholder="4" style={{...C.inp,width:"100%"}}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Date</div>
                {dateField(newWorkout.date, v=>setNewWorkout(p=>({...p,date:v})))}
              </div>
            </div>

            {/* Notes */}
            <div style={{marginBottom:18}}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6}}>Notes <span style={{fontWeight:400,color:T.textMuted}}>(optional)</span></div>
              <textarea value={newWorkout.notes} onChange={e=>setNewWorkout(p=>({...p,notes:e.target.value}))} placeholder="How'd it go? What did you do?" rows={3} style={{...C.inp,width:"100%",resize:"vertical",fontFamily:"'DM Sans',sans-serif"}}/>
            </div>

            {workoutError&&<div style={{fontSize:12,color:T.danger,marginBottom:10}}>{workoutError}</div>}

            <div style={{display:"flex",gap:10}}>
              <button style={{...C.btn(T.accent),flex:1}} onClick={addWorkout}>Save Workout</button>
              <button style={{...C.ghost,padding:"9px 18px"}} onClick={()=>setShowAddWorkout(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PRE-ONBOARDING COVER ──
          Until the profile answers whether onboarding is needed, the dashboard
          must not paint: a brand new signup used to flash the empty dashboard
          for the beat between mount and the onboarding sheet's dataLoaded and
          profileLoaded gate. The crimson brand screen covers that whole
          window, and stays under the sheet (z 290 vs the sheet's 300) for the
          rest of onboarding so the scrim blurs crimson, not dashboard. It
          drops the moment a returning user's profile reads complete, so they
          wait on nothing extra. onboardingDone.current keeps the Setup Guide
          reopen path (an existing user's dashboard) uncovered. */}
      {!isDemoMode && (!profileLoaded || (showOnboarding && !onboardingDone.current)) && (
        <PrivacyCover zIndex={290} />
      )}

      {/* ── ONBOARDING MODAL ── */}
      {showOnboarding && !isDemoMode && dataLoaded && profileLoaded && (()=>{
        // OB_STEPS lives at module scope (the greeting effect needs it too).
        const step = OB_STEPS[onboardingStep];
        const totalSteps = OB_STEPS.length;
        const pct = Math.round((onboardingStep / (totalSteps - 1)) * 100);
        // QUICK_GOALS moved to module scope (with bindings) so
        // persistOnboarding can look a tapped chip up by title at insert time.
        // eslint-disable-next-line no-unused-vars
        const STARTER_HABITS = [
          {id:"gym",       label:"Gym / Workout",   icon:""},
          {id:"water",     label:"Drink 8 Glasses", icon:""},
          {id:"sleep",     label:"8 Hours Sleep",   icon:""},
          {id:"steps",     label:"10k Steps",       icon:""},
          {id:"meditate",  label:"Meditate",        icon:""},
          {id:"noalcohol", label:"Alcohol-Free",    icon:""},
          {id:"nosmoke",   label:"Smoke-Free",      icon:""},
          {id:"budget",    label:"Review Budget",   icon:""},
          {id:"savings",   label:"Add to Savings",  icon:""},
          {id:"reading",   label:"Read 20 Min",     icon:""},
          {id:"veggies",   label:"Eat Veggies",     icon:""},
          {id:"gratitude", label:"Gratitude",       icon:""},
        ];
        return (
          /* Overlay stops at the top of the keyboard (kbInset), so the sheet is laid
             out inside the visible area instead of being translated or squashed. */
          <div ref={obOverlayRef} role="dialog" aria-modal="true" aria-label="Welcome to Scorify" style={{position:"fixed",left:0,right:0,top:0,bottom:kbInset,background:G.scrim,zIndex:300,backdropFilter:G.scrimBlur,WebkitBackdropFilter:G.scrimBlur,display:"flex",alignItems:"flex-end",justifyContent:"center",animation:"coachFadeIn 0.2s ease"}}>
            {/* Sheet is a flex column: fixed header, scrolling body. The sheet itself
                never scrolls, so its proportions hold when the keyboard opens. */}
            <div style={{width:"100%",maxWidth:560,background:G.modalSurface,backdropFilter:G.blurCard,WebkitBackdropFilter:G.blurCard,borderTop:G.hairline,borderRadius:`${RADII.card}px ${RADII.card}px 0 0`,maxHeight:"100%",display:"flex",flexDirection:"column",overflow:"hidden",animation:"coachSlideUp 0.35s cubic-bezier(0.16,1,0.3,1)",boxShadow:`${G.shadow}, 0 -16px 60px rgba(20,14,12,0.35)`}}>

              {/* Top safe-area padding: a full-height onboarding sheet reaches
                  the screen top, so without this the handle and "Setup N of N"
                  header render under the iOS status bar (device report, every
                  step). */}
              <div style={{flexShrink:0,paddingTop:"env(safe-area-inset-top, 0px)"}}>
                {/* Handle */}
                <div style={{width:36,height:4,borderRadius:2,background:T.overlayLight(0.15),margin:"14px auto 0"}}/>

                {/* Progress bar */}
                <div style={{padding:"16px 28px 0"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em"}}>Setup {onboardingStep + 1} of {totalSteps}</div>
                    {step !== "done" && <button onClick={skipOnboarding} style={{background:"transparent",border:"none",color:T.overlayLight(0.3),fontSize:12,cursor:"pointer",padding:"2px 4px"}}>Skip setup</button>}
                  </div>
                  <div style={{height:3,borderRadius:99,background:T.overlayLight(0.08)}}>
                    <div style={{height:"100%",borderRadius:99,background:T.accent,width:`${pct}%`,transition:"width 0.4s ease"}}/>
                  </div>
                </div>
                {/* Health permission sheet failed to open (SCORIFY-APP-P) —
                    App.js requests it at sign-in, i.e. during onboarding.
                    Renders nothing unless it failed; never blocks a step. */}
                <HealthAuthNotice userId={user?.id} style={{margin:"12px 28px 0"}} />
              </div>

              <div
                ref={obScrollRef}
                onFocus={e=>{
                  const el = e.target;
                  if (el.tagName!=="INPUT" && el.tagName!=="TEXTAREA") return;
                  // Deliberately NOT scrollIntoView: that scrolls every scrollable
                  // ancestor including the document, which is what pushed the sheet
                  // up behind the status bar. Scroll only this container.
                  setTimeout(()=>{
                    const box = obScrollRef.current;
                    if (window.visualViewport && window.visualViewport.offsetTop > 0) window.scrollTo(0, 0);
                    if (!box) return;
                    const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
                    box.scrollTo({ top: Math.max(0, top - 24), behavior: "smooth" });
                  }, 350);
                }}
                style={{flex:"1 1 auto",minHeight:0,overflowY:"auto",WebkitOverflowScrolling:"touch",overscrollBehavior:"contain",padding:kbInset>0?"24px 28px 28px":"24px 28px calc(28px + env(safe-area-inset-bottom,0px))"}}>

                {/* ── STEP 0: WELCOME ── */}
                {step==="welcome"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:28,color:T.text,marginBottom:8,lineHeight:1.2}}>Welcome to Scorify.</div>
                    <div style={{fontSize:14,color:T.textMuted,marginBottom:28,lineHeight:1.7}}>Let's set up your profile in about 2 minutes. This helps your AI coach give you real, personalized insights from day one.</div>

                    <div style={{marginBottom:16}}>
                      <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>What should we call you?</div>
                      <input value={obName} onChange={e=>setObName(e.target.value)}
                        placeholder="Your first name"
                        style={{...C.inp,width:"100%",fontSize:16}} autoFocus/>
                    </div>

                    {/* The old 8-option "What brings you here?" grid is gone: it
                        overloaded body_stats.goal_type with 5 values the Your
                        Goal card cannot render, and it asked (badly) what the
                        focus step now asks properly. goal_type is chosen on
                        the body step. */}
                    <button style={{...C.btn(T.accent),width:"100%",fontSize:15,padding:"14px",marginTop:12}} onClick={()=>setOnboardingStep(1)}>
                      Let's go →
                    </button>
                  </div>
                )}

                {/* ── STEP 1: COACH NAME ── */}
                {step==="coach"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:28,color:T.text,marginBottom:8,lineHeight:1.2}}>Meet your AI coach.</div>
                    <div style={{fontSize:14,color:T.textMuted,marginBottom:28,lineHeight:1.7}}>
                      Your coach tracks your habits, finances, mood, and health, then connects the dots to give you real, personalized insights. You can name them whatever you want.
                    </div>
                    <div style={{marginBottom:16}}>
                      <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>What do you want to call your coach?</div>
                      <input
                        value={obCoachName}
                        onChange={e=>setObCoachName(e.target.value)}
                        placeholder="Default: Kai"
                        style={{...C.inp,width:"100%",fontSize:16}}
                      />
                      <div style={{fontSize:11,color:T.textMuted,marginTop:8}}>Leave blank to use Kai. You can change this anytime in settings.</div>
                    </div>
                    <div style={{background:withAlpha(T.accent,0.06),border:`1px solid ${withAlpha(T.accent,0.15)}`,borderRadius:12,padding:"14px 16px",marginBottom:28}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.accent,marginBottom:6}}>{obCoachName.trim() || "Kai"} will:</div>
                      <div style={{fontSize:12,color:T.textMuted,lineHeight:1.8}}>
                        Track your habits, mood, and finances in real time<br/>
                        Spot patterns across all areas of your life<br/>
                        Give you personalized insights every day<br/>
                        Remember what you share across sessions
                      </div>
                    </div>
                    <div style={{display:"flex",gap:10}}>
                      <button style={{...C.ghost,padding:"12px 20px"}} onClick={()=>setOnboardingStep(0)}>← Back</button>
                      <button style={{...C.btn(T.accent),flex:1,fontSize:14,padding:"13px"}} onClick={()=>setOnboardingStep(2)}>Continue →</button>
                    </div>
                  </div>
                )}

                {/* ── STEP 2: FOCUS — "What are you here to work on?" ── */}
                {step==="focus"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:T.text,marginBottom:6}}>What are you here to work on?</div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:20,lineHeight:1.6}}>Pick what matters most right now. Scorify sets itself up around it. You can change everything later.</div>

                    <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                      {FOCUS_ORDER.map(key=>{
                        const active = obFocus===key;
                        return (
                          <button key={key} onClick={()=>{
                            setObFocus(key);
                            // Deliberately NO habit pre-selection (device
                            // finding 2026-09-11): every non-founder account
                            // in production had exactly the 3 preset habits
                            // and ZERO logs — a habit the user did not choose
                            // is one they feel no obligation to. The focus
                            // answer still visibly acts: the habit step
                            // orders this pillar's section and its suggested
                            // habits first, unchecked. The user picks their
                            // own.
                          }}
                            style={{padding:"13px 16px",borderRadius:12,border:`1.5px solid ${active?T.accent:T.overlayLight(0.08)}`,background:active?withAlpha(T.accent,0.1):T.overlayLight(0.03),color:active?T.accent:T.textMuted,cursor:"pointer",fontSize:14,fontWeight:active?700:500,textAlign:"left",transition:"all 0.15s",display:"flex",alignItems:"center",gap:10}}>
                            {active&&<span style={{fontSize:13}}>✓</span>}{FOCUS_PRESETS[key].label}
                          </button>
                        );
                      })}
                    </div>

                    <div style={{marginBottom:6}}>
                      <div style={{fontSize:11,color:T.textMuted,marginBottom:6,fontWeight:600}}>Anything else you want {obCoachName.trim()||"Kai"} to know? <span style={{fontWeight:400,color:T.textDim}}>(optional)</span></div>
                      <input value={obFocusNote} onChange={e=>setObFocusNote(e.target.value)}
                        placeholder='e.g. "training for a half marathon in May"'
                        style={{...C.inp,width:"100%"}}/>
                    </div>
                    <div style={{fontSize:11,color:T.textDim,marginBottom:24,lineHeight:1.5}}>Stays on your Scorify profile. Nothing here is sent to AI services unless you turn on AI coaching later.</div>

                    <div style={{display:"flex",gap:10}}>
                      <button style={{...C.ghost,padding:"12px 20px"}} onClick={()=>setOnboardingStep(1)}>← Back</button>
                      <button style={{...C.btn(T.accent),flex:1,fontSize:14,padding:"13px"}} onClick={()=>setOnboardingStep(3)}>
                        {obFocus?"Continue →":"Skip →"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP 3: BODY STATS ── */}
                {step==="body"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:T.text,marginBottom:6}}>Your body stats</div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.6}}>Tracks your progress toward your goal weight and gives your coach real context to work with. All private.</div>

                    {/* Height, sex and activity level are no longer asked:
                        they fed only the removed BMI/BMR/calorie features
                        (their body_stats columns remain, unwritten). */}
                    {/* Birth year, optional and skippable; age is computed from it. */}
                    <div style={{marginBottom:14}}>
                      {birthYearSelect(obBirthYear, setObBirthYear, { label: "What year were you born?", helper: "Optional. Helps us build Scorify for people like you." })}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                      {[["Current Weight (lbs)","obWeight",setObWeight,"175","number"],["Goal Weight (lbs)","obGoalWeight",setObGoalWeight,"160","number"]].map(([lbl,key,setter,ph,type])=>(
                        <div key={key}>
                          <div style={{fontSize:11,color:T.textMuted,marginBottom:6,fontWeight:600}}>{lbl}</div>
                          <input value={key==="obWeight"?obWeight:obGoalWeight}
                            onChange={e=>setter(e.target.value)} placeholder={ph} type={type}
                            style={{...C.inp,width:"100%"}}/>
                        </div>
                      ))}
                    </div>

                    <div style={{marginBottom:28}}>
                      <div style={{fontSize:11,color:T.textMuted,marginBottom:8,fontWeight:600}}>Body goal</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        {[["fat_loss","Fat loss"],["muscle_gain","Muscle gain"],["general_fitness","General fitness"],["maintenance","Maintenance"]].map(([v,l])=>(
                          <button key={v} onClick={()=>setObGoalType(v)}
                            style={{padding:"11px 10px",borderRadius:10,border:`1.5px solid ${obGoalType===v?T.accent:T.overlayLight(0.1)}`,background:obGoalType===v?withAlpha(T.accent,0.1):"transparent",color:obGoalType===v?T.accent:T.overlayLight(0.5),cursor:"pointer",fontSize:13,fontWeight:obGoalType===v?700:400}}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{display:"flex",gap:10}}>
                      <button style={{...C.ghost,padding:"12px 20px"}} onClick={()=>setOnboardingStep(2)}>← Back</button>
                      <button style={{...C.btn(T.accent),flex:1,fontSize:14,padding:"13px"}} onClick={()=>setOnboardingStep(4)}>Next →</button>
                    </div>
                  </div>
                )}

                {/* ── STEP 2: HABITS ── */}
                {step==="habits"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:T.text,marginBottom:6}}>Pick your first habits</div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:6,lineHeight:1.6}}>Choose up to 3 to start. You can always add more later.</div>
                    <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:16}}>{obHabits.length}/3 selected</div>

                    {(()=>{
                      // The focus answer's visible effect (replacing the
                      // removed pre-selection): the chosen pillar's section
                      // leads, and within it the pillar's suggested habits
                      // float to the front — ordered, never pre-checked.
                      const sections = [
                        {cat:"Body & Health",   focus:"health",    ids:["gym","steps","sleep","water","cook_home","no_phone_bed"]},
                        {cat:"Mind & Mood",     focus:"wellbeing", ids:["meditate","journal","gratitude","reading","therapy","wind_down"]},
                        {cat:"Money",           focus:"finance",   ids:["budget","savings","noimpulse","track_spend","no_subs","cardpay"]},
                        {cat:"Quit / Reduce",   focus:null,        ids:["noalcohol","nosmoke","nojunk","no_social","outside","coldshow"]},
                      ];
                      const suggested = obFocus && FOCUS_PRESETS[obFocus] ? FOCUS_PRESETS[obFocus].habits : [];
                      const ordered = [...sections]
                        .sort((a, b) => (b.focus === obFocus ? 1 : 0) - (a.focus === obFocus ? 1 : 0))
                        .map(sec => ({
                          ...sec,
                          ids: [...sec.ids].sort((a, b) => (suggested.includes(b) ? 1 : 0) - (suggested.includes(a) ? 1 : 0)),
                        }));
                      return ordered;
                    })().map(section=>(
                      <div key={section.cat} style={{marginBottom:16}}>
                        <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{section.cat}</div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                          {section.ids.map(id=>{
                            const h = HABIT_TEMPLATES.find(t=>t.id===id);
                            if (!h) return null;
                            const selected = obHabits.includes(id);
                            const maxed = obHabits.length >= 3 && !selected;
                            return (
                              <button key={id} onClick={()=>{
                                if (maxed) return;
                                // Derive from the previous list, not the render-time
                                // `selected` — a fast double-tap re-used the stale
                                // value and appended the same id twice.
                                setObHabits(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]);
                              }}
                                style={{padding:"10px 12px",borderRadius:10,border:`1.5px solid ${selected?T.accent:T.overlayLight(0.07)}`,background:selected?withAlpha(T.accent,0.12):T.overlayLight(0.03),color:selected?T.accent:maxed?T.overlayLight(0.2):T.overlayLight(0.65),cursor:maxed?"not-allowed":"pointer",fontSize:12,fontWeight:selected?700:400,textAlign:"left",display:"flex",alignItems:"center",gap:8,transition:"all 0.15s"}}>
                                <span style={{lineHeight:1.3}}>{h.label}</span>
                                {selected&&<span style={{marginLeft:"auto",fontSize:12,flexShrink:0}}>✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <div style={{height:8}}/>

                    <div style={{display:"flex",gap:10}}>
                      <button style={{...C.ghost,padding:"12px 20px"}} onClick={()=>setOnboardingStep(3)}>← Back</button>
                      <button style={{...C.btn(T.accent),flex:1,fontSize:14,padding:"13px"}} onClick={()=>setOnboardingStep(5)}>
                        {obHabits.length>0?`Add ${obHabits.length} habit${obHabits.length>1?"s":""} →`:"Skip →"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP 3: GOAL ── */}
                {step==="goal"&&(()=>{
                  // Reorder-safe: neighbours are resolved from OB_STEPS, not
                  // hardcoded indices, so item #4 can move the bank step ahead
                  // of this one without touching the Back/Next wiring.
                  const goalIdx = OB_STEPS.indexOf("goal");
                  // ONE plain question per template, unit built in ($ prefix or
                  // a suffix, never a free-text unit field). Keyed on the
                  // template's own unit/source so it stays correct if templates
                  // are added or reordered in goalTemplates.js.
                  const tplQuestion = (t) => {
                    if (t.unit === "$") {
                      if (t.targetHint === "your total card balance") return { label: "How much do you owe?", prefix: "$" };
                      if (t.source_type === "savings") return { label: "How much do you want to save?", prefix: "$" };
                      return { label: "Amount", prefix: "$" };
                    }
                    if (t.unit === "lbs") return { label: "How many pounds do you want to lose?", suffix: "lbs" };
                    if (t.unit === "books") return { label: "How many books?", suffix: "books" };
                    if (t.unit === "days") return { label: "How many days?", suffix: "days" };
                    if (t.unit === "km") return { label: "Distance", suffix: "km" };
                    return { label: `Target${t.unit ? ` (${t.unit})` : ""}` };
                  };
                  // Shared prefixed/suffixed number field (banking-app style).
                  const numField = (value, setter, { prefix, suffix, placeholder }) => (
                    <div style={{display:"flex",alignItems:"center",...C.inp,padding:0,overflow:"hidden"}}>
                      {prefix && <span style={{padding:"11px 2px 11px 14px",color:T.textMuted,fontSize:16}}>{prefix}</span>}
                      <input value={value} onChange={e=>setter(e.target.value)} type="number" inputMode="decimal" placeholder={placeholder}
                        style={{flex:1,minWidth:0,background:"transparent",border:"none",outline:"none",color:T.text,fontSize:16,padding:prefix?"11px 14px 11px 2px":"11px 14px"}}/>
                      {suffix && <span style={{padding:"11px 14px 11px 4px",color:T.textMuted,fontSize:14}}>{suffix}</span>}
                    </div>
                  );
                  return (
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:T.text,marginBottom:6}}>Set your first goal</div>

                    {obGoalTpl ? (() => {
                      // ── TEMPLATE CONFIRM: one question, the redesign's shape ──
                      const q = tplQuestion(obGoalTpl);
                      const isCredit = obGoalTpl.source_type === "credit_payoff";
                      const isSavings = obGoalTpl.source_type === "savings";
                      // Money templates auto-track from the bank connected on
                      // the previous step. Credit payoff needs a live credit
                      // balance to bind; without one it stays manual.
                      const creditBal = isCredit ? liveCreditBalance() : 0;
                      const cardCount = isCredit ? (plaidAccounts || []).filter(a => a.type === 'credit').length : 0;
                      const savingsBal = isSavings ? plaidBalanceSum(a => a.type === 'depository' && a.subtype === 'savings') : null;
                      const creditAuto = isCredit && plaidConnected && creditBal > 0;
                      const trackLine = creditAuto ? "● Tracks automatically from your bank."
                        : isCredit ? "Connect your bank to track this automatically. For now you log progress yourself."
                        : isSavings && savingsBal != null ? "● Tracks automatically from your bank balance."
                        : isSavings ? "● Tracks automatically from your savings figure."
                        : "You log progress yourself with the Update button.";
                      const usd = (n) => `$${Math.round(n).toLocaleString()}`;
                      const prefillNote = creditAuto ? `You owe ${usd(creditBal)} across ${cardCount} card${cardCount === 1 ? '' : 's'}.`
                        : (isSavings && savingsBal != null) ? `Current savings balance: ${usd(savingsBal)}.${balanceAsOf ? ` ${balanceAsOf}.` : ''} Pick a target above it.`
                        : null;
                      return (<>
                        <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:6}}>{obGoalTpl.title}</div>
                        <div style={{fontSize:12,marginBottom:prefillNote?6:18,color:(creditAuto||isSavings)?T.success:T.textMuted,lineHeight:1.5}}>{trackLine}</div>
                        {prefillNote && <div style={{fontSize:12,marginBottom:18,color:T.textMuted,lineHeight:1.5}}>{prefillNote}</div>}
                        <div style={{marginBottom:16}}>
                          <div style={{fontSize:12,color:T.textMuted,marginBottom:8,fontWeight:600}}>{isCredit ? (creditAuto ? "How much to pay off?" : q.label) : q.label}</div>
                          {numField(obGoalTarget, setObGoalTarget, { prefix:q.prefix, suffix:q.suffix, placeholder: obGoalTpl.targetHint || obGoalTpl.target_value || "0" })}
                          {creditAuto && <div style={{fontSize:11,color:T.textDim,marginTop:5}}>Defaults to your full balance. Lower it for a partial payoff.</div>}
                        </div>
                        {/* Head-start only for the MANUAL fallback: an auto
                            credit payoff derives progress from the balance. */}
                        {obGoalTpl.moneyStart && !creditAuto && !isSavings && (
                          <div style={{marginBottom:16}}>
                            <div style={{fontSize:12,color:T.textMuted,marginBottom:8,fontWeight:600}}>Already saved or paid something? <span style={{fontWeight:400,color:T.textDim}}>(optional)</span></div>
                            {numField(obGoalCurrent, setObGoalCurrent, { prefix:"$", placeholder:"0" })}
                          </div>
                        )}
                        <div style={{marginBottom:24}}>
                          <div style={{fontSize:12,color:T.textMuted,marginBottom:8,fontWeight:600}}>Target date <span style={{fontWeight:400,color:T.textDim}}>(optional)</span></div>
                          {dateField(obGoalDeadline, setObGoalDeadline)}
                        </div>
                        <div style={{display:"flex",gap:10}}>
                          <button style={{...C.ghost,padding:"12px 20px"}} onClick={()=>{ setObGoalTpl(null); setObGoalTitle(""); setObGoalTarget(""); setObGoalUnit(""); setObGoalCurrent(""); }}>← Back</button>
                          <button style={{...C.btn(T.accent),flex:1,fontSize:14,padding:"13px"}} disabled={!obGoalTarget} onClick={advanceFromGoal}>Set Goal →</button>
                        </div>
                      </>);
                    })() : (<>
                      {/* ── GALLERY: tap a template, or write your own ── */}
                      <div style={{fontSize:13,color:T.textMuted,marginBottom:20,lineHeight:1.6}}>Pick one to start, or write your own. You can add more later.</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:18}}>
                        {(FOCUS_PRESETS[obFocus]?.goalCats
                          ? [...QUICK_GOALS].sort((a,b)=>(FOCUS_PRESETS[obFocus].goalCats.includes(b.category)?1:0)-(FOCUS_PRESETS[obFocus].goalCats.includes(a.category)?1:0))
                          : QUICK_GOALS
                        ).map(g=>(
                          <button key={g.title} onClick={()=>{
                            setObGoalTpl(g); setObGoalTitle(g.title); setObGoalCategory(g.category); setObGoalUnit(g.unit); setObGoalCurrent("");
                            // Prefill the credit-payoff target from the live
                            // balance when a bank is connected; other templates
                            // keep their default.
                            if (g.source_type === 'credit_payoff' && plaidConnected && liveCreditBalance() > 0) {
                              setObGoalTarget(String(Math.round(liveCreditBalance())));
                            } else setObGoalTarget(g.target_value);
                          }}
                            style={{padding:"11px 12px",borderRadius:12,border:`1.5px solid ${T.overlayLight(0.08)}`,background:T.overlayLight(0.04),color:T.overlayLight(0.7),cursor:"pointer",fontSize:12,fontWeight:400,textAlign:"left",transition:"all 0.15s"}}>
                            {g.title}
                          </button>
                        ))}
                      </div>

                      <div style={{fontSize:11,color:T.textMuted,marginBottom:10,textAlign:"center"}}>or write your own</div>

                      {(()=>{
                        const shape = obGoalShape;
                        const isPeriodShape = shape==="recurring"||shape==="cap";
                        const showUnitPicker = shape==="target";
                        return (<>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:10}}>
                            {GOAL_SHAPES.map(s=>{
                              const active = obGoalShape===s.id;
                              return (
                                <button key={s.id} onClick={()=>{ setObGoalShape(s.id); if (s.id==="milestone"||isPeriodShape) { setObGoalUnit(""); } }}
                                  style={{padding:"8px 10px",borderRadius:10,border:`1.5px solid ${active?T.accent:T.overlayLight(0.08)}`,background:active?withAlpha(T.accent,0.1):T.overlayLight(0.03),color:active?T.accent:T.textMuted,cursor:"pointer",fontSize:11,fontWeight:active?700:500,textAlign:"left"}}>
                                  {s.label}
                                </button>
                              );
                            })}
                          </div>

                          <div style={{marginBottom:10}}>
                            <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Goal</div>
                            <input value={obGoalTitle} onChange={e=>setObGoalTitle(e.target.value)} placeholder={shape==="recurring"?'e.g. "Gym sessions"':shape==="cap"?'e.g. "Takeout orders"':shape==="milestone"?'e.g. "Run a 10K"':'e.g. "Save $2,000"'} style={{...C.inp,width:"100%",fontSize:16,boxSizing:"border-box"}}/>
                          </div>

                          {shape!=="milestone"&&(
                            <div style={{display:"grid",gridTemplateColumns:isPeriodShape?"1fr 1fr":"1fr",gap:8,marginBottom:10}}>
                              <div>
                                <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>{shape==="cap"?"Limit":shape==="recurring"?"Times":"Target"}</div>
                                <input value={obGoalTarget} onChange={e=>setObGoalTarget(e.target.value)} placeholder={isPeriodShape?"3":"100"} type="number" inputMode="decimal" style={{...C.inp,width:"100%",fontSize:16,boxSizing:"border-box"}}/>
                              </div>
                              {isPeriodShape&&(
                                <div>
                                  <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Per</div>
                                  <div style={{display:"flex",gap:4}}>
                                    {["week","month"].map(v=>(
                                      <button key={v} onClick={()=>setObGoalPeriod(v)}
                                        style={{flex:1,padding:"11px 4px",borderRadius:10,border:`1.5px solid ${obGoalPeriod===v?T.accent:T.overlayLight(0.08)}`,background:obGoalPeriod===v?withAlpha(T.accent,0.1):"transparent",color:obGoalPeriod===v?T.accent:T.textMuted,cursor:"pointer",fontSize:12,fontWeight:obGoalPeriod===v?700:400}}>
                                        {v==="week"?"Week":"Month"}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Bounded unit picker (Add Goal redesign) — never
                              free text. Only the "reach a number" shape has a
                              unit; counts and milestones do not. */}
                          {showUnitPicker&&(
                            <div style={{marginBottom:10}}>
                              <div style={{fontSize:11,color:T.textMuted,marginBottom:6}}>Unit</div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                {["$","lbs","mi","km","days","books","%","count"].map(u=>{
                                  const val = u==="count"?"":u;
                                  const active = obGoalUnit===val;
                                  return (
                                    <button key={u} onClick={()=>setObGoalUnit(val)} aria-pressed={active}
                                      style={{padding:"7px 12px",borderRadius:10,border:`1px solid ${active?T.accent:T.border}`,background:active?withAlpha(T.accent,0.1):T.surfaceAlt,color:active?T.accent:T.textMuted,cursor:"pointer",fontSize:12,fontWeight:active?700:500}}>
                                      {u}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {shape==="recurring"&&(
                            <div style={{fontSize:10,color:T.textDim,marginBottom:8,lineHeight:1.5}}>Tip: consistency with no end point is a habit, not a goal. The Habits step (one step back) tracks streaks for exactly that.</div>
                          )}

                          {obGoalTitle.trim()!=="" && (
                            <div style={{fontSize:11,marginBottom:14,color:T.textMuted,lineHeight:1.5}}>
                              {shape==="milestone" ? "You mark it done with the Update button when it happens."
                               : shape==="cap" ? "You log the count yourself with the Update button. Staying under the limit counts as on track."
                               : shape==="recurring" ? `You log the count yourself with the Update button, per ${obGoalPeriod}.`
                               : "You log progress yourself with the Update button."}
                            </div>
                          )}

                          <div style={{marginBottom:24}}>
                            <div style={{fontSize:11,color:T.textMuted,marginBottom:5}}>Target date <span style={{color:T.overlayLight(0.3)}}>(optional)</span></div>
                            {dateField(obGoalDeadline, setObGoalDeadline)}
                          </div>
                        </>);
                      })()}

                      <div style={{display:"flex",gap:10}}>
                        <button style={{...C.ghost,padding:"12px 20px"}} onClick={()=>setOnboardingStep(goalIdx-1)}>← Back</button>
                        <button style={{...C.btn(T.accent),flex:1,fontSize:14,padding:"13px"}} onClick={advanceFromGoal}>
                          {obGoalTitle.trim()?"Set Goal →":"Skip →"}
                        </button>
                      </div>
                    </>)}
                  </div>
                  );
                })()}

                {/* ── STEP 4: BANK ── */}
                {step==="bank"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:T.text,marginBottom:6}}>Connect your bank</div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:20,lineHeight:1.7}}>Real-time balances and spending make your Financial Health score accurate and your coach insights genuinely useful.</div>

                    {!obPlaidDone && !obSkippedPlaid && (
                      <>
                        <div style={{background:withAlpha(T.success,0.07),border:`1px solid ${withAlpha(T.success,0.2)}`,borderRadius:12,padding:"16px 18px",marginBottom:20}}>
                          {[["Bank-level encryption","Your data is never stored on our servers"],["Read-only access","We can see balances and transactions, never move money"],["Powered by Plaid","Used by 8,000+ apps including Venmo and Robinhood"]].map(([t,d])=>(
                            <div key={t} style={{display:"flex",gap:12,marginBottom:12}}>
                              <span style={{color:T.success,flexShrink:0,marginTop:1}}>✓</span>
                              <div><div style={{fontSize:13,fontWeight:600,color:T.text}}>{t}</div><div style={{fontSize:11,color:T.textMuted}}>{d}</div></div>
                            </div>
                          ))}
                        </div>

                        <button onClick={async()=>{
                          // Only a REAL connection sets obPlaidDone — cancelling
                          // Plaid Link used to set it anyway and the done screen
                          // claimed "✓ Bank connected via Plaid" for a user who
                          // connected nothing. A cancel still advances (never
                          // block onboarding on the bank), just without the claim.
                          const connected = await connectPlaid();
                          if (connected) setObPlaidDone(true);
                          setTimeout(()=>advanceFromBank(), connected ? 800 : 0);
                        }} disabled={plaidLoading}
                          style={{...C.btn(T.accent),width:"100%",fontSize:15,padding:"14px",marginBottom:12,opacity:plaidLoading?0.6:1}}>
                          {plaidLoading?"Connecting...":"Connect Bank →"}
                        </button>

                        <button onClick={()=>setObSkippedPlaid(true)}
                          style={{width:"100%",background:"transparent",border:"none",color:T.overlayLight(0.35),fontSize:13,cursor:"pointer",padding:"8px",textDecoration:"underline"}}>
                          Enter manually instead
                        </button>
                      </>
                    )}

                    {obSkippedPlaid && (
                      <>
                        <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:24}}>
                          {[["Monthly Income ($)","obIncome",setObIncome,"3500"],["Monthly Expenses ($)","obExpenses",setObExpenses,"2800"],["Current Savings ($)","obSavings",setObSavings,"1200"]].map(([lbl,key,setter,ph])=>(
                            <div key={key}>
                              <div style={{fontSize:11,color:T.textMuted,marginBottom:6,fontWeight:600}}>{lbl}</div>
                              <input value={key==="obIncome"?obIncome:key==="obExpenses"?obExpenses:obSavings}
                                onChange={e=>setter(e.target.value)} placeholder={ph} type="number"
                                style={{...C.inp,width:"100%",fontSize:16}}/>
                            </div>
                          ))}
                        </div>
                        <button onClick={()=>setObSkippedPlaid(false)}
                          style={{background:"transparent",border:"none",color:T.overlayLight(0.35),fontSize:12,cursor:"pointer",marginBottom:16,padding:0,textDecoration:"underline"}}>
                          ← Connect bank instead
                        </button>
                      </>
                    )}

                    <div style={{display:"flex",gap:10,marginTop:obSkippedPlaid?0:0}}>
                      <button style={{...C.ghost,padding:"12px 20px"}} onClick={()=>setOnboardingStep(OB_STEPS.indexOf("bank")-1)}>← Back</button>
                      {/* plaidConnected fallback: after a remount mid-Link
                          (OAuth handoff / jettison) obPlaidDone resets but the
                          connection is real — show the advance button, not the
                          connect UI, so the user is never asked to relink. */}
                      {(obSkippedPlaid || obPlaidDone || plaidConnected) && (
                        <button style={{...C.btn(T.accent),flex:1,fontSize:14,padding:"13px"}} onClick={advanceFromBank}>
                          {(obPlaidDone||plaidConnected)?"All set →":"Save & Continue →"}
                        </button>
                      )}
                      {!obSkippedPlaid && !obPlaidDone && !plaidConnected && (
                        <button style={{...C.ghost,flex:1,padding:"12px 20px",fontSize:13}} onClick={advanceFromBank}>Skip for now</button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── STEP 7: AI CONSENT — asked at the moment it is obvious
                    what it is for: everything is saved, and the next screen is
                    Kai's first message. Same writeProfile path as the Profile
                    modal (grantAiConsent), so the two can never diverge.
                    Declining writes nothing (not a revoke) and the free-text
                    note then never reaches Anthropic — every coach call is
                    consent-gated. ── */}
                {step==="consent"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:T.text,marginBottom:6}}>One thing before {obCoachName.trim()||"Kai"} says hello.</div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:16,lineHeight:1.7}}>
                      {obCoachName.trim()||"Kai"} coaches you by reading what you track and sending it to Anthropic's Claude to write each response. To power this, Scorify also keeps a derived day-by-day summary (a daily ledger) built from the data below. That means:
                    </div>
                    <div style={{background:T.overlayLight(0.04),border:`1px solid ${T.overlayLight(0.08)}`,borderRadius:12,padding:"14px 16px",marginBottom:16}}>
                      {[
                        "Your habits, goals, supplements, recovery streaks, XP and level, mood scores and wellbeing check-in results (the PHQ-2 and GAD-2 questionnaires)",
                        "Health data the app tracks: workouts, steps, sleep, screen time and weight",
                        "Your medications for adherence only (names and schedules, so the coach can acknowledge a dose, never assess the medication), plus upcoming checkups",
                        `Your finances: income, expenses, savings, debts and bills${obPlaidDone ? ", plus balances and recent transactions from your bank (never credentials)" : ""}`,
                        "Tendencies Scorify computes from your own history (for example, how your mood compares on more vs less sleep), and commitments you make to the coach so it can follow up",
                        obFocus ? `Your focus (${FOCUS_PRESETS[obFocus].label.toLowerCase()})${obFocusNote.trim() ? " and the note you wrote" : ""}` : (obFocusNote.trim() ? "The note you wrote at signup" : null),
                        "Journal entries and mood notes only if you also switch on sharing in Journal settings",
                      ].filter(Boolean).map((item,i)=>(
                        <div key={i} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
                          <span style={{color:T.accent,flexShrink:0}}>→</span>
                          <span style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}>{item}</span>
                        </div>
                      ))}
                      <div style={{fontSize:11,color:T.textDim,marginTop:10,lineHeight:1.5}}>Used only to generate your coaching, never to train AI models, never sold. Revoke anytime in Profile → Privacy &amp; AI Data Sharing.</div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <button style={{...C.btn(T.accent),width:"100%",fontSize:14,padding:"13px"}} onClick={async()=>{
                        const ok = await grantAiConsent();
                        if (!ok) { alert("Couldn't save your consent. Please try again."); return; }
                        setOnboardingStep(OB_STEPS.indexOf("consent") + 1);
                      }}>
                        Turn on AI coaching →
                      </button>
                      <button style={{...C.ghost,width:"100%",padding:"12px",fontSize:13}} onClick={()=>setOnboardingStep(OB_STEPS.indexOf("consent") + 1)}>
                        Not now, I'll decide later
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP: DAILY CHECK-IN TIME (whole hours only — the cron
                    runs hourly, so the chosen hour is when it arrives). No
                    preselected default; Skip leaves checkin_hour null so nothing
                    is sent until the user picks (Home prompt / Notifications). ── */}
                {step==="checkin"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:T.text,marginBottom:6}}>When should {obCoachName.trim()||"Kai"} check in each day?</div>
                    <div style={{fontSize:13,color:T.textMuted,marginBottom:16,lineHeight:1.7}}>One short note at the time you pick, with the single most important thing that day. Tap it to open the full briefing. Change it anytime in Profile, Notifications.</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
                      {[7,8,12,17,18,21].map(h=>(
                        <button key={h} onClick={()=>setObCheckinHour(h)} style={{
                          flex:"1 0 28%",padding:"12px 8px",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                          background:obCheckinHour===h?T.accent:T.overlayLight(0.05),
                          color:obCheckinHour===h?"#fff":T.text,
                          border:`1px solid ${obCheckinHour===h?T.accent:T.overlayLight(0.1)}`,
                        }}>{fmtHour12(h)}</button>
                      ))}
                    </div>
                    <select value={obCheckinHour==null?"":String(obCheckinHour)} onChange={e=>setObCheckinHour(e.target.value===""?null:Number(e.target.value))} style={{...C.inp,width:"100%",marginBottom:16}} aria-label="Check-in time">
                      <option value="">Another time…</option>
                      {Array.from({length:24}).map((_,h)=><option key={h} value={h}>{fmtHour12(h)}</option>)}
                    </select>
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <button disabled={obCheckinHour==null} style={{...C.btn(T.accent),width:"100%",fontSize:14,padding:"13px",opacity:obCheckinHour==null?0.5:1,cursor:obCheckinHour==null?"default":"pointer"}} onClick={async()=>{
                        if (obCheckinHour==null) return;
                        await saveNotifPrefs({ checkinHour: obCheckinHour, checkinEnabled: true });
                        setOnboardingStep(OB_STEPS.indexOf("checkin") + 1);
                      }}>Set check-in time →</button>
                      <button style={{...C.ghost,width:"100%",padding:"12px",fontSize:13}} onClick={()=>setOnboardingStep(OB_STEPS.indexOf("checkin") + 1)}>
                        Skip for now
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP: APP LOCK (native only — absent from OB_STEPS on
                    web, so this block simply never matches there). Writes only
                    to the device Keychain via LockSetup; skipping records the
                    offer was made so LockGate does not re-ask. ── */}
                {step==="lock"&&(
                  <div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:26,color:T.text,marginBottom:8,lineHeight:1.25}}>Lock Scorify when you step away.</div>
                    {appLockEnabled?(
                      /* Already on (set via LockGate's prompt before finishing
                         onboarding) — confirm instead of asking twice. */
                      <>
                        <div style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.7}}>
                          App Lock is already on for this device. Scorify asks for Face ID, Touch ID or your PIN when you come back. Change it anytime in Profile.
                        </div>
                        <button style={{...C.btn(T.accent),width:"100%",fontSize:14,padding:"13px"}} onClick={()=>setOnboardingStep(OB_STEPS.indexOf("done"))}>
                          Continue →
                        </button>
                      </>
                    ):(
                      <>
                        <div style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.7}}>
                          Your balances, health data and journal live in this app. Set a 4 digit PIN and Scorify asks for Face ID, Touch ID or the PIN whenever you come back.
                        </div>
                        <LockSetup
                          ownerId={user?.id}
                          onDone={()=>{ setAppLockEnabled(true); announceLockChanged(); setOnboardingStep(OB_STEPS.indexOf("done")); }}
                          onSkip={async()=>{ await markPrompted(user?.id); announceLockChanged(); setOnboardingStep(OB_STEPS.indexOf("done")); }}
                          skipLabel="Skip for now"
                        />
                      </>
                    )}
                  </div>
                )}

                {/* ── STEP 8: DONE — branches on consent. The static summary
                    renders in EVERY branch, so this screen can never spin with
                    nothing behind it; Kai's greeting tops it when it arrives,
                    and a slow or failed call just leaves the summary. ── */}
                {step==="done"&&(
                  <div style={{padding:"4px 0 8px"}}>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:26,color:T.text,marginBottom:14,lineHeight:1.2}}>You're all set{obName?`, ${obName}`:""}.</div>

                    {aiConsent && obGreeting && (
                      <div style={{background:withAlpha(T.accent,0.06),border:`1px solid ${withAlpha(T.accent,0.18)}`,borderRadius:14,padding:"14px 16px",marginBottom:16}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.accent,marginBottom:6}}>{coachName||"Kai"}</div>
                        <div style={{fontSize:13,color:T.text,lineHeight:1.7}}>{obGreeting}</div>
                      </div>
                    )}
                    {aiConsent && !obGreeting && obGreetingState==="loading" && (
                      <div style={{fontSize:12,color:T.textMuted,marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
                        <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:T.accent,animation:"coachPulse 1.2s ease-in-out infinite"}}/>
                        {coachName||"Kai"} is looking at what you set up…
                      </div>
                    )}
                    {!aiConsent && (
                      <div style={{fontSize:13,color:T.textMuted,marginBottom:16,lineHeight:1.6}}>
                        Here's what you're now tracking. You can turn on AI coaching anytime in Profile → Privacy &amp; AI Data Sharing.
                      </div>
                    )}

                    {/* Static summary — every branch */}
                    <div style={{background:T.overlayLight(0.04),border:`1px solid ${T.overlayLight(0.08)}`,borderRadius:14,padding:"16px 20px",marginBottom:24,textAlign:"left"}}>
                      {[
                        obFocus && `Focus: ${FOCUS_PRESETS[obFocus].label.toLowerCase()}`,
                        obHabits.length>0 && `${obHabits.length} habit${obHabits.length>1?"s":""} added`,
                        obGoalTitle && `Goal set: "${obGoalTitle}"`,
                        obFocus && (enabledTrackers||[]).length>0 && `${enabledTrackers.length} trackers switched on`,
                        obPlaidDone && "Bank connected via Plaid",
                        obSkippedPlaid && obIncome && `Income: ${parseInt(obIncome).toLocaleString()}/mo`,
                        obWeight && `Current weight: ${obWeight} lbs`,
                      ].filter(Boolean).map((item,i,arr)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:i<arr.length-1?10:0}}>
                          <span style={{color:T.success,fontSize:14}}>✓</span>
                          <span style={{fontSize:13,color:T.textMuted}}>{item}</span>
                        </div>
                      ))}
                    </div>

                    <button style={{...C.btn(T.accent),width:"100%",fontSize:15,padding:"14px"}} onClick={finishOnboarding}>
                      Open Scorify →
                    </button>
                  </div>
                )}

              </div>
            </div>
          </div>
        );
      })()}

      {/* ── AI CONSENT MODAL ── */}
      {showAiConsentModal&&(
        <div role="dialog" aria-modal="true" aria-label="AI consent" style={{position:"fixed",inset:0,background:G.scrim,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 16px"}} onClick={()=>setShowAiConsentModal(false)}>
          <div style={{background:T.surface,borderRadius:20,padding:"28px 24px",maxWidth:420,width:"100%",boxShadow:"0 16px 48px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:6}}>AI Coaching Powered by Anthropic</div>
            <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:20}}>
              Scorify's AI coach reads what you track and sends it to Anthropic's Claude API to write each response: your habits, goals, supplements, recovery streaks, XP and level, mood scores and wellbeing check-in results (PHQ-2 and GAD-2), health data such as workouts, steps, sleep, screen time and weight, your finances including income, savings, debts and bills, and when your bank is connected, account balances and recent transactions. Your messages to the coach are sent too. Your medications are included for adherence only (names and schedules, so the coach can acknowledge a dose, never to assess the medication), along with upcoming checkups. It also sends tendencies Scorify computes from your own history and the commitments you make to the coach; to power these, Scorify stores a derived day-by-day summary (a daily ledger) built from the data above. Journal entries and mood notes are included only if you also turn on sharing in Journal settings.<br/><br/>
              Your data is used only to generate your response and is not used to train AI models. No data is sold to third parties. You can revoke this consent at any time in Profile → Privacy &amp; AI Data Sharing.
            </div>
            {aiConsent&&(
              <div style={{background:withAlpha(T.success,0.12),border:`1px solid ${withAlpha(T.success,0.3)}`,borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:T.success,fontWeight:600}}>
                ✓ AI coaching consent granted
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {!aiConsent&&(
                <button style={{background:T.accentFill,color:"#fff",border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={async()=>{
                  // Shared grantAiConsent (also the onboarding consent step) —
                  // writeProfile-verified for the 0-row new-user trap; one
                  // write path so the two surfaces can never diverge.
                  if (user) {
                    const ok = await grantAiConsent();
                    if (!ok) { alert("Couldn't save your consent. Please try again."); return; }
                  } else {
                    setAiConsent(true);
                  }
                  setShowAiConsentModal(false);
                }}>Agree and Enable AI Coaching</button>
              )}
              {aiConsent&&(
                <button style={{background:"transparent",color:T.danger,border:`1px solid ${withAlpha(T.danger,0.3)}`,borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={async()=>{
                  if (user) {
                    // Same verified path as the grant — a revoke that matches
                    // 0 rows must not report success either.
                    const { error, row } = await writeProfile({ ai_consent_granted: false, ai_consent_date: null });
                    if (error || !row) {
                      console.error("consent revoke failed", error);
                      alert("Couldn't revoke consent. Please try again.");
                      return;
                    }
                  }
                  setAiConsent(false);
                  try { const ck=userKey(user?.id,'ai_consent'); if(ck) localStorage.removeItem(ck); } catch {}
                  setShowAiConsentModal(false);
                }}>Revoke Consent</button>
              )}
              <button style={{background:"transparent",color:T.textMuted,border:"none",padding:"10px",fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>setShowAiConsentModal(false)}>
                {aiConsent?"Close":"Not now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PAYWALL (src/Paywall.jsx): full screen; Not now drops to preview mode.
          The delete confirm modals below sit above it so its Delete account link works. ── */}
      {paywallVisible&&(
        <Paywall native={native} onSync={()=>fetchSubscription("POST")} onSignOut={handleSignOut} onDeleteAccount={openDeleteAccount} onNotNow={dismissPaywall} />
      )}

      {/* ── DELETE ACCOUNT STEP 0: cancel the Apple subscription first (native,
          willRenew, not beta). Deleting the account does not cancel it. ── */}
      {showCancelSubStep&&(
        <div role="dialog" aria-modal="true" aria-label={CANCEL_FIRST_TITLE} style={{position:"fixed",inset:0,background:G.scrim,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 16px"}} onClick={()=>setShowCancelSubStep(false)}>
          <div style={{background:T.surface,borderRadius:20,padding:"28px 24px",maxWidth:380,width:"100%",boxShadow:"0 16px 48px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>{CANCEL_FIRST_TITLE}</div>
            <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:24}}>{CANCEL_FIRST_BODY}</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button style={{background:T.accent,color:T.surface,border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>{ showManageSubscriptions().catch(()=>{}); }}>
                {CANCEL_FIRST_PRIMARY}
              </button>
              <button style={{background:withAlpha(T.danger,0.12),color:T.danger,border:`1px solid ${withAlpha(T.danger,0.3)}`,borderRadius:10,padding:"13px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>{ setShowCancelSubStep(false); setShowDeleteConfirm1(true); }}>
                {CANCEL_FIRST_CONTINUE}
              </button>
              <button style={{background:"transparent",color:T.textMuted,border:"none",padding:"10px",fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>{ setShowCancelSubStep(false); setShowDeleteConfirm1(true); }}>
                {CANCEL_FIRST_SKIP}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE ACCOUNT DONE: shown only when the Apple subscription is still
          active, so the Settings line is read before the redirect. ── */}
      {deleteDone&&(
        <div role="dialog" aria-modal="true" aria-label="Account deleted" style={{position:"fixed",inset:0,background:G.scrim,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 16px"}}>
          <div style={{background:T.surface,borderRadius:20,padding:"28px 24px",maxWidth:380,width:"100%",boxShadow:"0 16px 48px rgba(0,0,0,0.5)"}}>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>Your account has been deleted</div>
            <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:24}}>{SUBSCRIPTION_STILL_ACTIVE_LINE}</div>
            <button style={{width:"100%",background:T.accent,color:T.surface,border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>{ window.location.href = "/"; }}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* ── DELETE ACCOUNT CONFIRM STEP 1 ── */}
      {showDeleteConfirm1&&(
        <div role="dialog" aria-modal="true" aria-label="Delete account" style={{position:"fixed",inset:0,background:G.scrim,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 16px"}} onClick={()=>setShowDeleteConfirm1(false)}>
          <div style={{background:T.surface,borderRadius:20,padding:"28px 24px",maxWidth:380,width:"100%",boxShadow:"0 16px 48px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>Delete Account</div>
            <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:24}}>
              This will permanently delete your account and all your data — habits, finances, moods, goals, and everything else. This cannot be undone.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button style={{background:withAlpha(T.danger,0.12),color:T.danger,border:`1px solid ${withAlpha(T.danger,0.3)}`,borderRadius:10,padding:"13px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>{ setShowDeleteConfirm1(false); setShowDeleteConfirm2(true); setDeleteConfirmText(""); }}>
                Continue
              </button>
              <button style={{background:"transparent",color:T.textMuted,border:"none",padding:"10px",fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>setShowDeleteConfirm1(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE ACCOUNT CONFIRM STEP 2 ── */}
      {showDeleteConfirm2&&(
        <div role="dialog" aria-modal="true" aria-label="Confirm account deletion" style={{position:"fixed",inset:0,background:G.scrim,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 16px"}} onClick={()=>{ setShowDeleteConfirm2(false); setDeleteConfirmText(""); }}>
          <div style={{background:T.surface,borderRadius:20,padding:"28px 24px",maxWidth:380,width:"100%",boxShadow:"0 16px 48px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>Are you absolutely sure?</div>
            <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:16}}>Type <strong style={{color:T.text}}>DELETE</strong> to confirm permanent deletion of your account.</div>
            <input
              value={deleteConfirmText}
              onChange={e=>setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE here"
              style={{...C.inp,width:"100%",marginBottom:16,fontSize:16,textAlign:"center",letterSpacing:2}}
              autoFocus
            />
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button
                disabled={deleteConfirmText!=="DELETE"||deleteLoading}
                style={{background:deleteConfirmText==="DELETE"?T.danger:withAlpha(T.danger,0.12),color:deleteConfirmText==="DELETE"?"#fff":T.textMuted,border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:700,cursor:deleteConfirmText==="DELETE"?"pointer":"default",fontFamily:"'DM Sans',sans-serif",opacity:deleteConfirmText==="DELETE"&&!deleteLoading?1:0.5,transition:"all 0.2s"}}
                onClick={async()=>{
                  if (deleteConfirmText!=="DELETE"||deleteLoading) return;
                  setDeleteLoading(true);
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) { setDeleteLoading(false); return; }
                    const r = await apiFetch(apiUrl("/api/delete-account"), {
                      timeoutMs: 60000, // purges every table + Plaid items; never cut short
                      method: "POST",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                    });
                    const d = await r.json();
                    if (d.success) {
                      try { localStorage.clear(); } catch {}
                      // This account's Keychain PIN + lock config die with the
                      // account (keyed by this uid); another account's PIN on the
                      // same device is untouched.
                      try { await clearLockState('account_deletion', user?.id); } catch {}
                      if (window.Capacitor?.isNativePlatform?.()) {
                        try {
                          const OneSignal = window.plugins?.OneSignal || require('onesignal-cordova-plugin').default;
                          // Opt the device's push subscription out, then drop
                          // the alias: the account is gone (src/lib/pushIdentity.js).
                          await applyPushIdentity(OneSignal, 'account_delete');
                        } catch (e) {
                          console.log('OneSignal logout error', e);
                        }
                      }
                      // scope: 'local' — the account is already deleted
                      // server-side (delete-account.js), which invalidates every
                      // device's session; this is just local cleanup, so it must
                      // not attempt a global revoke on a now-gone account.
                      await logOutPurchases();
                      await supabase.auth.signOut({ scope: 'local' });
                      // The Apple subscription outlives the account: say so
                      // once, on screen, before leaving.
                      if (d.subscriptionStillActive === true) { setShowDeleteConfirm2(false); setDeleteDone(true); return; }
                      window.location.href = "/";
                    } else {
                      alert("Deletion failed: " + (d.error || "Unknown error"));
                      setDeleteLoading(false);
                    }
                  } catch (err) {
                    alert("Deletion failed: " + err.message);
                    setDeleteLoading(false);
                  }
                }}>
                {deleteLoading ? "Deleting…" : "Permanently Delete Account"}
              </button>
              <button style={{background:"transparent",color:T.textMuted,border:"none",padding:"10px",fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} onClick={()=>{ setShowDeleteConfirm2(false); setDeleteConfirmText(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MONEY-GOAL AUTO-TRACK UPGRADE (one-time, explicit) ── */}
      {moneyUpgrade && moneyUpgrade.length > 0 && (
        <div role="dialog" aria-modal="true" aria-label="Track goals automatically" style={{position:"fixed",inset:0,background:G.scrim,zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{width:"100%",maxWidth:440,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:"26px 24px"}}>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text,marginBottom:8}}>Track these from your bank?</div>
            <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:18}}>
              Your bank is connected. These goals are updated by hand right now. Switch any to automatic tracking and Scorify will keep them current from your real balance.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              {moneyUpgrade.map(g => {
                const kind = moneyGoalKind(g);
                const bal = kind === 'credit' ? liveCreditBalance() : plaidBalanceSum(a => a.type === 'depository' && a.subtype === 'savings');
                const asOfNote = balanceAsOf ? ` ${balanceAsOf}.` : '';
                const balNote = kind === 'credit'
                  ? `You owe $${Math.round(liveCreditBalance()).toLocaleString()} on your cards.${asOfNote}`
                  : bal != null ? `You have $${Math.round(bal).toLocaleString()} in savings.${asOfNote}` : `Tracks from your savings balance.`;
                return (
                  <div key={g.id} style={{background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px"}}>
                    <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:2}}>{g.title}</div>
                    <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5,marginBottom:10}}>{balNote}</div>
                    <button onClick={()=>convertGoalToAuto(g, kind)} style={{...C.btn(T.accent),width:"100%",padding:"10px",fontSize:13}}>
                      Switch to automatic tracking
                    </button>
                  </div>
                );
              })}
            </div>
            <button onClick={dismissMoneyUpgrade} style={{width:"100%",background:"transparent",color:T.textMuted,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px",fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
              Keep them manual
            </button>
          </div>
        </div>
      )}

      {/* ── APP LOCK MODAL (native only; device-local Keychain state) ── */}
      {showAppLockModal&&(
        <div role="dialog" aria-modal="true" aria-label="App Lock" style={{position:"fixed",inset:0,background:G.scrim,zIndex:400,backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{width:"100%",maxWidth:420,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",background:T.surface,borderRadius:20,padding:"26px 24px",boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text,marginBottom:6}}>App Lock</div>
            {appLockEnabled&&!appLockChangingPin&&(
              <>
                <div style={{fontSize:13,color:T.textMuted,lineHeight:1.65,marginBottom:20}}>
                  App Lock is on. Scorify asks for Face ID, Touch ID or your PIN on launch and when you return after 30 seconds away. The PIN lives only in this device's Keychain.
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <button style={{...C.btn(T.accent),width:"100%",padding:"12px",fontSize:14}} onClick={()=>setAppLockChangingPin(true)}>Change PIN</button>
                  <button style={{background:'transparent',border:`1px solid ${withAlpha(T.danger,0.3)}`,color:T.danger,borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",width:'100%'}} onClick={async()=>{
                    await disableLock(user?.id);
                    announceLockChanged();
                    setAppLockEnabled(false);
                  }}>Turn Off App Lock</button>
                  <button style={{...C.ghost,width:"100%",padding:"11px",fontSize:13}} onClick={()=>setShowAppLockModal(false)}>Close</button>
                </div>
              </>
            )}
            {(!appLockEnabled||appLockChangingPin)&&(
              <>
                <div style={{fontSize:13,color:T.textMuted,lineHeight:1.65,marginBottom:20}}>
                  {appLockChangingPin
                    ? "Choose a new PIN. It replaces the old one immediately."
                    : "Scorify will ask for Face ID, Touch ID or a PIN on launch and when you return after 30 seconds away."}
                </div>
                <LockSetup
                  ownerId={user?.id}
                  onDone={()=>{
                    setAppLockEnabled(true);
                    announceLockChanged();
                    setAppLockChangingPin(false);
                    setShowAppLockModal(false);
                    showToast(appLockChangingPin?"PIN updated.":"App Lock is on.");
                  }}
                  onSkip={()=>{ setAppLockChangingPin(false); if(!appLockEnabled) setShowAppLockModal(false); }}
                  skipLabel="Cancel"
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* ── CRISIS RESOURCE CARD ──
          Deterministic and universal: shown by the on-device keyword check
          (src/lib/crisisDetect.js) on check-in notes and journal entries.
          No AI call, no consent gate, works signed out of AI and in demo.
          It only offers resources; it never claims anyone read anything. */}
      {showCrisisResources&&(
        <div role="dialog" aria-modal="true" aria-label="Support resources" style={{position:"fixed",inset:0,background:G.scrim,zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{width:"100%",maxWidth:420,maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain",background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:"26px 24px"}}>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:T.text,marginBottom:10}}>Support is available</div>
            <div style={{fontSize:14,color:T.text,lineHeight:1.7,marginBottom:14}}>
              If things feel heavy right now, you do not have to carry it alone.
            </div>
            <div style={{background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px",marginBottom:10}}>
              <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>988 Suicide and Crisis Lifeline</div>
              <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6,marginBottom:10}}>Free, confidential, 24 hours a day.</div>
              <div style={{display:"flex",gap:8}}>
                <a href="tel:988" style={{flex:1,textAlign:"center",background:T.accentFill,color:"#fff",borderRadius:10,padding:"10px",fontSize:13,fontWeight:700,textDecoration:"none",fontFamily:"'DM Sans',sans-serif"}}>Call 988</a>
                <a href="sms:988" style={{flex:1,textAlign:"center",background:"transparent",color:T.accent,border:`1px solid ${withAlpha(T.accent,0.5)}`,borderRadius:10,padding:"10px",fontSize:13,fontWeight:700,textDecoration:"none",fontFamily:"'DM Sans',sans-serif"}}>Text 988</a>
              </div>
            </div>
            <div style={{background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
              <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>Crisis Text Line</div>
              <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6}}>Text HOME to 741741 to reach a trained counselor.</div>
            </div>
            <div style={{fontSize:12,color:T.textDim,lineHeight:1.6,marginBottom:16}}>
              Scorify is not a crisis service, and no person reviews what you write here. These lines connect you with people who can help.
            </div>
            <button style={{...C.btn(T.accent),width:"100%"}} onClick={()=>setShowCrisisResources(false)}>Okay</button>
          </div>
        </div>
      )}

      {/* ── FLOATING COACH BUTTON ── */}
      {/* The FIXED positioning + hide-on-scroll transform live on the WRAPPER, so
          the button keeps its own transform (hover scale) and box-shadow pulse
          without a conflict. Hidden = translated fully past the right edge (button
          width + its 24px offset + shadow) with a transition; under
          prefers-reduced-motion the global rule forces transition-duration ~0 (see
          the <style> block), so it still hides but without a visible slide. */}
      {/* "Ask Kai" glass pill (redesign Phase 1). Same behavior contract as
          the old round FAB: openCoach, fabHidden slide-away, coachPulse,
          hidden while a modal is on screen (the wrapper's condition).
          Fixed chrome → capped G.blurFixed. */}
      {tab!=="coach"&&!modalOnScreen&&(
        <div style={{position:"fixed",bottom:FAB_OFFSET,right:20,zIndex:190,transform:fabHidden?"translateX(calc(100% + 40px))":"none",transition:REDUCED_MOTION?undefined:"transform 0.25s ease",pointerEvents:fabHidden?"none":"auto"}}>
        <button onClick={openCoach} aria-label={`Talk to ${coachName||"Kai"}`}
          style={{minHeight:48,padding:"0 20px",display:"flex",alignItems:"center",gap:8,borderRadius:24,
            background:G.pillSurface,border:G.hairline,boxShadow:G.chromeShadow,
            backdropFilter:G.blurFixed,WebkitBackdropFilter:G.blurFixed,
            cursor:"pointer",color:T.accent,fontSize:15,fontWeight:700,fontFamily:"inherit",textShadow:G.chromeTextShadow,
            position:"relative"}}
          title="Open AI Coach">
          {/* Attention cue lives on the DOT, not the button: the old
              coachPulse box-shadow glow on the whole pill was the pink
              halo reported on device (crimson shadow to 30px + 8px ring,
              running by default until the coach is first opened). */}
          <span aria-hidden="true" style={{width:8,height:8,borderRadius:"50%",background:T.accentFill,animation:(coachPulse&&!REDUCED_MOTION)?"coachDotPulse 2s ease-in-out infinite":undefined}}/>
          Ask {coachName||"Kai"}
          {coachMsgs.length===0&&<span style={{position:"absolute",top:-2,right:-2,width:10,height:10,borderRadius:"50%",background:T.success,border:`2px solid ${T.surface}`}}/>}
        </button>
        </div>
      )}

      {/* ── COACH DRAWER ── */}
      {coachOpen&&(
        <>
          <div style={{position:"fixed",inset:0,background:G.scrim,zIndex:191,backdropFilter:G.scrimBlur,WebkitBackdropFilter:G.scrimBlur,animation:"coachFadeIn 0.2s ease"}} onClick={()=>setCoachOpen(false)}/>
          <div role="dialog" aria-modal="true" aria-label="Coach" style={{position:"fixed",bottom:0,left:0,right:0,zIndex:192,background:G.modalSurface,backdropFilter:G.blurCard,WebkitBackdropFilter:G.blurCard,borderTop:G.hairline,borderRadius:`${RADII.card}px ${RADII.card}px 0 0`,boxShadow:`${G.shadow}, 0 -8px 40px rgba(20,14,12,0.35)`,display:"flex",flexDirection:"column",maxHeight:"82vh",animation:"coachSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)"}}>
            <div style={{width:36,height:4,borderRadius:2,background:T.borderStrong,margin:"12px auto 0",flexShrink:0}}/>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 20px 10px",flexShrink:0,borderBottom:`1px solid ${T.overlayLight(0.08)}`}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:`linear-gradient(135deg,${T.accent},${T.accentPressed})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.bg} strokeWidth="2" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
              </div>
              <div>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:17,color:T.text,lineHeight:1.2}}>{coachName || "Kai"}</div>
                <div style={{fontSize:11,color:T.textMuted}}>Sees patterns across your whole life</div>
              </div>
              <button onClick={()=>{ if(!window.confirm(`Clear all ${coachMsgs.length} messages in this conversation? This cannot be undone.`))return; setCoachMsgs([]); try{if(user?.id){localStorage.removeItem(userKey(user.id,'coach_msgs'));localStorage.removeItem(userKey(user.id,'coach_msgs_savedAt'));}}catch{} }} style={{marginLeft:"auto",background:"none",border:"none",fontSize:11,cursor:"pointer",color:T.textMuted,padding:"14px 12px"}}>Clear</button>
              <button onClick={()=>setCoachOpen(false)} aria-label="Close coach" style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:T.textMuted,lineHeight:1,padding:"10px 12px"}}>×</button>
            </div>
            {/* Messages — aria-live matches the tab transcript */}
            <div aria-live="polite" style={{flex:1,overflowY:"auto",overscrollBehavior:"contain",padding:"16px 20px",display:"flex",flexDirection:"column",gap:12}}>
              {/* Same gap the coach tab had (map A.2): open_greeting is a
                  background mode, so a consent-less user opened an empty drawer
                  with no fetch and no prompt, and demo opened it with nothing at
                  all. Both now render the SAME components the tab renders. */}
              {coachMsgs.length===0&&!coachLoading&&isDemoMode&&<CoachSampleConversation/>}
              {coachMsgs.length===0&&!coachLoading&&!isDemoMode&&!aiConsent&&(
                <CoachConsentPrompt onEnable={()=>{ setCoachOpen(false); setShowAiConsentModal(true); }}/>
              )}
              {coachMsgs.map((m,i)=>(
                <div key={i} style={{maxWidth:"88%",alignSelf:m.isCoach?"flex-start":"flex-end",background:m.isCoach?T.overlayLight(0.07):T.accent,color:m.isCoach?T.text:T.bg,borderRadius:m.isCoach?"4px 16px 16px 16px":"16px 4px 16px 16px",padding:"11px 15px",fontSize:14,lineHeight:1.55,boxShadow:m.isCoach?"none":`0 2px 10px ${T.overlayLight(0.15)}`,animation:"coachFadeInUp 0.25s ease"}}>
                  {m.text.split(/\*\*(.*?)\*\*/g).map((part,j)=>j%2===1?<strong key={j} style={{fontWeight:700,color:m.isCoach?T.text:T.bg}}>{part}</strong>:part)}
                </div>
              ))}
              {coachLoading&&(
                <div style={{display:"flex",gap:4,padding:"12px 16px",background:T.overlayLight(0.07),borderRadius:"4px 16px 16px 16px",alignSelf:"flex-start"}}>
                  {[0,0.2,0.4].map((d,i)=>(
                    <div key={i} style={{width:7,height:7,borderRadius:"50%",background:T.textMuted,animation:`coachTypingDot 1.2s ease-in-out ${d}s infinite`}}/>
                  ))}
                </div>
              )}
              <div ref={coachBottomRef}/>
            </div>
            {/* Quick suggestions */}
            {coachMsgs.length<=2&&(
              <div style={{display:"flex",gap:8,padding:"0 20px 12px",overflowX:"auto",flexShrink:0}}>
                {coachSuggestions.map(s=>(
                  <button key={s} onClick={()=>sendCoachPrompt(s)}
                    style={{whiteSpace:"nowrap",border:`1.5px solid ${withAlpha(T.accent,0.4)}`,borderRadius:20,padding:"6px 14px",fontSize:12,color:T.accent,background:"transparent",cursor:"pointer",transition:"background 0.15s",flexShrink:0}}
                    onMouseEnter={e=>{e.currentTarget.style.background=withAlpha(T.accent,0.08);}}
                    onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            {/* Input — same offline treatment as the other composer: keep the
                surface and the cached transcript, disable only the send. */}
            {isOffline&&!isDemoMode&&<div style={{fontSize:11.5,color:T.textMuted,textAlign:"center",padding:"8px 16px 0",flexShrink:0,borderTop:`1px solid ${T.overlayLight(0.08)}`}}>Kai needs a connection.</div>}
            <div style={{display:"flex",gap:10,padding:"12px 16px",paddingBottom:"calc(12px + env(safe-area-inset-bottom,0px))",flexShrink:0,borderTop:isOffline&&!isDemoMode?"none":`1px solid ${T.overlayLight(0.08)}`}}>
              <input ref={coachInputRef} value={coachInput} onChange={e=>setCoachInput(e.target.value)}
                disabled={isOffline&&!isDemoMode}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendCoachMsg();setCoachOpen(false);}}}
                aria-label="Message your coach" placeholder="Ask your coach anything..."
                style={{flex:1,border:`1.5px solid ${T.overlayLight(0.12)}`,borderRadius:24,padding:"10px 16px",fontSize:16,background:T.overlayLight(0.06),color:T.text,outline:"none",transition:"border-color 0.2s",fontFamily:"inherit",opacity:isOffline&&!isDemoMode?0.5:1}}
                onFocus={e=>{e.target.style.borderColor=T.accent;}}
                onBlur={e=>{e.target.style.borderColor=T.overlayLight(0.12);}}/>
              <button onClick={()=>{sendCoachMsg();}} aria-label="Send" disabled={!coachInput.trim()||coachLoading||(isOffline&&!isDemoMode)}
                style={{width:42,height:42,borderRadius:"50%",background:T.accentFill,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:17,flexShrink:0,transition:"transform 0.15s,opacity 0.15s",opacity:coachInput.trim()&&!(isOffline&&!isDemoMode)?1:0.4}}>↑</button>
            </div>
          </div>
        </>
      )}

      {/* ── MOBILE BOTTOM NAV — Floating Pill ── */}
      {/* Hidden entirely while any sheet/modal is open (anyModalOpen): it must not
          paint over a sheet's footer (the Done button was unreachable), and a tab
          tap must not switch the tab underneath an open sheet or silently discard
          typed input. Hiding — not just covering — is the honest fix. */}
      {isMobile && !modalOnScreen && (
        <>
          {/* Bottom fade (2026-10-13): content legitimately scrolls beneath
              the see-through nav, and a button sitting under the blur read
              as "rendered under the nav bar" on device. This fixed gradient
              dims content out just above the nav so nothing looks clipped.
              Below the nav's z-index, no pointer events, theme background. */}
          <div aria-hidden="true" style={{position:"fixed",left:0,right:0,bottom:0,height:"calc(100px + env(safe-area-inset-bottom,0px))",zIndex:199,pointerEvents:"none",background:`linear-gradient(to bottom, ${withAlpha(T.bg,0)} 0%, ${withAlpha(T.bg,0.85)} 40%, ${withAlpha(T.bg,0.92)} 100%)`}}/>
          {/* Floating glass pill bar (redesign Phase 1). Same tabs, same
              gates (hidden while a modal is on screen — see the comment
              above), same aria/unvisited logic; container + item styling
              only. bottom includes env(safe-area-inset-bottom): the
              home-indicator strip stays untouchable (the 390px mockup frame
              ignores it). Fixed chrome over scrolling content, so the blur
              is the capped G.blurFixed, not the card blur. */}
          <div style={{
            position:"fixed", left:12, right:12,
            bottom:"calc(10px + env(safe-area-inset-bottom,0px))",
            height:60, boxSizing:"border-box", padding:"6px 8px",
            zIndex:200, display:"flex", alignItems:"center", justifyContent:"space-between",
            background:G.chromeSurface,
            border:G.hairline,
            borderRadius:RADII.tabBar,
            boxShadow:G.chromeShadow,
            backdropFilter:G.blurFixed, WebkitBackdropFilter:G.blurFixed,
            transform:"none",
          }}>
            {[
              {id:"overview", label:"Home",    svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:18,height:18}}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>},
              {id:"habits",   label:"Habits",  svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:18,height:18}}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>},
              {id:"wellness", label:"Mind",svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:18,height:18}}><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>},
              {id:"finances", label:"Money", svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:18,height:18}}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>},
              {id:"health",   label:"Health",  svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:18,height:18}}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>},
            ].map(t => {
              const active = tab === t.id;
              // Unvisited-today highlight: a tab the user has not opened since
              // local midnight (visited_<date> key) gets a SOFT accent-tinted
              // fill with an accent label — deliberately lighter than the
              // active tab's SOLID accent pill with white text, so the two
              // states never look alike. Home is marked visited on open, so it
              // never highlights; the active tab is excluded by !active.
              const unvisited = !active && !visitedTabs.has(t.id);
              return (
                // setShowMoreMenu(false) is still needed even though the More
                // button is gone: this nav is fixed at z-index 200, while the
                // avatar menu's backdrop lives inside the z-index 50 header, so
                // these buttons stay tappable with the menu open. Without the
                // reset the menu would hang around after a tab switch.
                <button key={t.id} onClick={()=>{ if(t.id!==tab){ setTab(t.id); markTabVisited(t.id); } setShowMoreMenu(false); }}
                  aria-current={tab===t.id?"page":undefined} data-nav-tab={t.id} data-nav-state={active?"active":unvisited?"unvisited":"idle"}
                  style={{
                    // Mockup pattern: the active tab IS a filled crimson pill
                    // (shape + color), inactive tabs are bare labels — the
                    // pill replaces the old underline as the non-color signal.
                    background: active ? T.accentFill : unvisited ? withAlpha(T.accent, 0.16) : "transparent",
                    border: unvisited ? `1px solid ${withAlpha(T.accent, 0.30)}` : "none", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    minHeight:48, padding: active ? "0 14px" : "0 6px",
                    position:"relative", borderRadius:24,
                    minWidth:0, flex: active ? "0 0 auto" : 1,
                    transition:REDUCED_MOTION?undefined:"color 0.15s, background 0.15s",
                    // Inactive labels are T.text, not textMuted (chrome pass):
                    // with see-through chrome the worst backdrop is a crimson
                    // button blurring to pink, where muted reads 2.7:1 and
                    // text 6.8:1. The mockup's inactive tabs are #3d3a35 dark.
                    color: active ? "#fff" : unvisited ? T.accent : T.text,
                    fontFamily:"inherit",
                  }}>
                  <span style={{fontSize:12.5,fontWeight:(active||unvisited)?600:500,whiteSpace:"nowrap",textShadow:active?undefined:G.chromeTextShadow}}>{t.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
